/* eslint-disable no-unused-vars, react-hooks/exhaustive-deps, no-control-regex, no-mixed-operators, react/jsx-no-duplicate-props */ /* v2 */
import './storage-shim';
import { initializeApp } from 'firebase/app';
import { getFirestore, doc, setDoc, getDoc, onSnapshot } from 'firebase/firestore';
import { getAuth, signInAnonymously, onAuthStateChanged } from 'firebase/auth';
import { useState, useEffect, useRef, useCallback } from "react";
import {
  Car, Wrench, Package, LayoutDashboard, Mic, MicOff, AlertTriangle,
  CheckCircle, Clock, Plus, X, ChevronRight, ArrowRight, Phone, Mail,
  Zap, RefreshCw, Truck, MapPin, Search, ShoppingCart, Star, DollarSign,
  TrendingUp, Receipt, BarChart2, Camera, MessageSquare, User, Settings,
  Bell, Send, PlayCircle, PauseCircle, StopCircle, Home, Scan,
  ChevronDown, ChevronUp, Info, AlertCircle, Trophy, Calendar, Image, Eye,
  Clipboard, List, LogIn, LogOut, Barcode
} from "lucide-react";

// ─── CONSTANTS ────────────────────────────────────────────────────────────────

const STAGES = [
  { id: "INTAKE",            label: "Intake",           color: "#6b7280", owner: "Robert",         order: 0 },
  { id: "INITIAL_DIAG",     label: "Initial Diag",     color: "#3b82f6", owner: "Robert",         order: 1 },
  { id: "MECH_VERIFY",      label: "Mech Verify",      color: "#8b5cf6", owner: "James",          order: 2 },
  { id: "PENDING_APPROVAL", label: "Pending Approval", color: "#f97316", owner: "Angela/Dmitriy", order: 3 },
  { id: "PARTS_ORDERED",    label: "Parts Ordered",    color: "#eab308", owner: "Angela/Dmitriy", order: 4 },
  { id: "SCHEDULED",        label: "Scheduled",        color: "#06b6d4", owner: "James",          order: 5 },
  { id: "IN_REPAIR",        label: "In Repair",        color: "#f59e0b", owner: "James",          order: 6 },
  { id: "QC_DETAIL",        label: "QC / Detail",      color: "#a855f7", owner: "Lot Hands",      order: 7 },
  { id: "PHOTO_POST",       label: "Photo / Post",     color: "#ec4899", owner: "Sales",          order: 8 },
  { id: "PHOTO_READY",      label: "Photo Ready",      color: "#ec4899", owner: "Sky",            order: 8 },
  { id: "LINE_READY",       label: "Line Ready",       color: "#22c55e", owner: "Robert",         order: 9 },
  { id: "SOLD",             label: "Sold",             color: "#4b5563", owner: "—",              order: 10 },
];

const WO_CATS = [
  { id: "MECHANICAL",    label: "Mechanical",      color: "#f59e0b" },
  { id: "DETAIL",        label: "Detail / Clean",  color: "#a855f7" },
  { id: "BODYWORK",      label: "Body Work",       color: "#ec4899" },
  { id: "LOCKSMITH",     label: "Locksmith",       color: "#14b8a6" },
  { id: "COSMETIC",      label: "Cosmetic",        color: "#3b82f6" },
  { id: "VENDOR",        label: "External Vendor", color: "#8b5cf6" },
  { id: "DOCUMENTATION", label: "Documentation",   color: "#6b7280" },
  { id: "ADVERTISING",   label: "Advertising",     color: "#22c55e" },
  { id: "OTHER",         label: "Other",           color: "#94a3b8" },
];

// Vendor meta tags — used to match parts to correct suppliers
const VENDOR_TAGS = {
  USED_PARTS:    { label: "Used/OEM Parts",     icon: "🏚",  sources: ["wrecking_yard","car_parts","ebay_motors"] },
  NEW_PARTS:     { label: "New Parts",           icon: "🔩",  sources: ["autozone","oreilly","napa","advance"] },
  IMPORT_PARTS:  { label: "Import/Euro Parts",   icon: "🇪🇺",  sources: ["fcpeuro","worldpac","ecs_tuning","pelican"] },
  ONLINE_PARTS:  { label: "Online/Discount",     icon: "📦",  sources: ["rockauto","amazon","carparts_com"] },
  PAINT_BODY:    { label: "Paint & Body",        icon: "🎨",  sources: ["paint_store","ppg","sherwin","body_supply"] },
  TIRES_WHEELS:  { label: "Tires & Wheels",      icon: "⭕",  sources: ["tire_shop","discount_tire","les_schwab"] },
  GLASS:         { label: "Glass",               icon: "🪟",  sources: ["safelite","glass_shop"] },
  ELECTRICAL:    { label: "Electrical/Ignition", icon: "⚡",  sources: ["autozone","rockauto","napa","amazon"] },
  FLUIDS:        { label: "Fluids/Chemicals",    icon: "🛢",  sources: ["autozone","napa","amazon","walmart"] },
  TRANSMISSION:  { label: "Transmission",        icon: "⚙️",  sources: ["wrecking_yard","car_parts","rockauto","atsg"] },
  BRAKES:        { label: "Brakes",              icon: "🛑",  sources: ["autozone","rockauto","napa","oreilly"] },
  SUSPENSION:    { label: "Suspension/Steering", icon: "🔄",  sources: ["rockauto","amazon","napa","fcpeuro"] },
  EXHAUST:       { label: "Exhaust",             icon: "💨",  sources: ["autozone","rockauto","exhaust_shop","magnaflow"] },
  TOOLS:         { label: "Tools",               icon: "🔧",  sources: ["amazon","harbor_freight","snap_on","napa"] },
};

// Parts to vendor tag mapping — what part types come from where
const PART_TAG_RULES = [
  { patterns: ["fender","door","hood","trunk","bumper","quarter panel","rocker","mirror","tail light","headlight","lens","grille"], tags: ["USED_PARTS","PAINT_BODY"] },
  { patterns: ["paint","primer","clear coat","body filler","bondo","sand","buffer","polish","compound"], tags: ["PAINT_BODY"] },
  { patterns: ["engine","motor","cylinder head","block","intake","exhaust manifold","timing"], tags: ["USED_PARTS","IMPORT_PARTS","ONLINE_PARTS"] },
  { patterns: ["transmission","trans","gearbox","shifter","clutch","torque converter"], tags: ["TRANSMISSION","USED_PARTS"] },
  { patterns: ["tire","wheel","rim","lug nut","center cap","TPMS"], tags: ["TIRES_WHEELS"] },
  { patterns: ["windshield","glass","window regulator","window motor"], tags: ["GLASS","NEW_PARTS"] },
  { patterns: ["brake","rotor","caliper","pad","master cylinder","wheel cylinder","abs"], tags: ["BRAKES","NEW_PARTS","ONLINE_PARTS"] },
  { patterns: ["strut","shock","spring","control arm","tie rod","ball joint","sway bar","bushing"], tags: ["SUSPENSION","NEW_PARTS","ONLINE_PARTS"] },
  { patterns: ["alternator","starter","battery","fuse","relay","sensor","o2","MAP","MAF","ECU","PCM","wiring"], tags: ["ELECTRICAL","NEW_PARTS","ONLINE_PARTS"] },
  { patterns: ["oil","coolant","fluid","antifreeze","power steering","brake fluid","atf","filter"], tags: ["FLUIDS","NEW_PARTS"] },
  { patterns: ["muffler","exhaust","catalytic","flex pipe","resonator","header"], tags: ["EXHAUST","ONLINE_PARTS"] },
  { patterns: ["audi","bmw","mercedes","volkswagen","vw","volvo","porsche","saab","mini","jaguar","land rover"], tags: ["IMPORT_PARTS"] },
];

const PARTS_SOURCES = [
  { id: "autozone",    label: "AutoZone",           icon: "🏪", leadDays: 0,  tags: ["NEW_PARTS","BRAKES","ELECTRICAL","FLUIDS","EXHAUST"],   searchUrl: (q) => `https://www.autozone.com/searchresult?searchText=${encodeURIComponent(q)}` },
  { id: "oreilly",     label: "O'Reilly Auto",       icon: "🏬", leadDays: 0,  tags: ["NEW_PARTS","BRAKES","ELECTRICAL","FLUIDS"],              searchUrl: (q) => `https://www.oreillyauto.com/search?q=${encodeURIComponent(q)}` },
  { id: "napa",        label: "NAPA Auto Parts",      icon: "🔧", leadDays: 1,  tags: ["NEW_PARTS","BRAKES","ELECTRICAL","TOOLS"],               searchUrl: (q) => `https://www.napaonline.com/en/search?q=${encodeURIComponent(q)}` },
  { id: "rockauto",    label: "RockAuto",             icon: "🚗", leadDays: 4,  tags: ["ONLINE_PARTS","BRAKES","SUSPENSION","TRANSMISSION"],     searchUrl: (q) => `https://www.rockauto.com/en/partsearch/?parttype=&query=${encodeURIComponent(q)}` },
  { id: "amazon",      label: "Amazon",               icon: "📦", leadDays: 2,  tags: ["ONLINE_PARTS","ELECTRICAL","TOOLS","FLUIDS"],            searchUrl: (q) => `https://www.amazon.com/s?k=${encodeURIComponent(q)}&i=automotive` },
  { id: "fcpeuro",     label: "FCP Euro",             icon: "🇪🇺", leadDays: 3,  tags: ["IMPORT_PARTS"],                                          searchUrl: (q) => `https://www.fcpeuro.com/search?query=${encodeURIComponent(q)}` },
  { id: "worldpac",    label: "Worldpac",             icon: "⚙️",  leadDays: 2,  tags: ["IMPORT_PARTS","NEW_PARTS"],                              searchUrl: (q) => `https://www.worldpac.com/` },
  { id: "car_parts",   label: "Car-Parts.com (Used)", icon: "♻️",  leadDays: 5,  tags: ["USED_PARTS","TRANSMISSION"],                             searchUrl: (q) => `https://www.car-part.com/cgi-bin/search.cgi?q=${encodeURIComponent(q)}` },
  { id: "lkq",         label: "LKQ / Wrecking Yard",  icon: "🏚",  leadDays: 3,  tags: ["USED_PARTS","TRANSMISSION","PAINT_BODY"],                searchUrl: (q) => `https://www.lkqonline.com/search?term=${encodeURIComponent(q)}` },
  { id: "ebay_motors", label: "eBay Motors",          icon: "🔍", leadDays: 5,  tags: ["USED_PARTS","ONLINE_PARTS"],                             searchUrl: (q) => `https://www.ebay.com/sch/6028/i.html?_nkw=${encodeURIComponent(q)}` },
  { id: "les_schwab",  label: "Les Schwab",           icon: "⭕",  leadDays: 0,  tags: ["TIRES_WHEELS"],                                          searchUrl: (q) => `https://www.lesschwab.com/tires/search?q=${encodeURIComponent(q)}` },
  { id: "safelite",    label: "Safelite",             icon: "🪟",  leadDays: 2,  tags: ["GLASS"],                                                 searchUrl: (q) => `https://www.safelite.com/` },
  { id: "ppg",         label: "PPG / Paint Supply",   icon: "🎨",  leadDays: 1,  tags: ["PAINT_BODY"],                                            searchUrl: (q) => `https://www.ppgrefinish.com/` },
  { id: "harbor_freight", label: "Harbor Freight",   icon: "🛠",  leadDays: 0,  tags: ["TOOLS"],                                                 searchUrl: (q) => `https://www.harborfreight.com/catalogsearch/result/?q=${encodeURIComponent(q)}` },
  { id: "magnaflow",   label: "MagnaFlow/Exhaust",    icon: "💨",  leadDays: 3,  tags: ["EXHAUST"],                                               searchUrl: (q) => `https://www.magnaflow.com/search#w=${encodeURIComponent(q)}` },
  { id: "dealer_oem",  label: "Dealer OEM",           icon: "🏢",  leadDays: 5,  tags: ["NEW_PARTS","IMPORT_PARTS"],                                  searchUrl: (q) => null, dealerPhone: "(503) 391-2100", dealerName: "Capital Ford Salem" },
];

// Detect which vendor tags apply to a part query
const detectPartTags = (query) => {
  const q = query.toLowerCase();
  const matched = new Set();
  PART_TAG_RULES.forEach(rule => {
    if (rule.patterns.some(p => q.includes(p))) rule.tags.forEach(t => matched.add(t));
  });
  if (matched.size === 0) { matched.add("NEW_PARTS"); matched.add("ONLINE_PARTS"); }
  return [...matched];
};

// Get best sources for a part based on tags
const getBestSourcesForPart = (query) => {
  const tags = detectPartTags(query);
  return PARTS_SOURCES.filter(s => s.tags.some(t => tags.includes(t)));
};

const VENDOR_TYPES = [
  { id: "TIRES", label: "Tire Shop", color: "#eab308" },
  { id: "EXHAUST", label: "Exhaust/Muffler", color: "#f97316" },
  { id: "BODY", label: "Body Shop", color: "#3b82f6" },
  { id: "PAINT", label: "Paint Store", color: "#ec4899" },
  { id: "GLASS", label: "Glass/Windshield", color: "#06b6d4" },
  { id: "TOW", label: "Towing", color: "#ef4444" },
  { id: "DETAIL", label: "Detailing", color: "#a855f7" },
  { id: "ALIGNMENT", label: "Alignment", color: "#22c55e" },
  { id: "PARTS", label: "Parts Supplier", color: "#8b5cf6" },
  { id: "OTHER", label: "Other", color: "#6b7280" },
];

const LEDGER_CATS = [
  { id: "PURCHASE",  label: "Purchase Price",  color: "#6b7280" },
  { id: "PARTS",     label: "Parts",           color: "#f59e0b" },
  { id: "LABOR",     label: "Labor",           color: "#8b5cf6" },
  { id: "VENDOR",    label: "Vendor Service",  color: "#ec4899" },
  { id: "DETAIL",    label: "Detail",          color: "#a855f7" },
  { id: "TRANSPORT", label: "Transport",       color: "#ef4444" },
  { id: "OTHER",     label: "Other",           color: "#6b7280" },
  { id: "SALE",      label: "Sale Price",      color: "#22c55e" },
];

const STAFF_ROLES = [
  { id: "manager",   label: "Management",     icon: "👔", color: "#f59e0b" },
  { id: "mechanic",  label: "Mechanic",       icon: "🔧", color: "#3b82f6" },
  { id: "lot",       label: "Lot Hand",       icon: "🚗", color: "#a855f7" },
  { id: "sales",     label: "Sales",          icon: "💼", color: "#22c55e" },
  { id: "office",    label: "Office",         icon: "📋", color: "#06b6d4" },
];

const DEFAULT_STAFF = [
  { id: "angela",  name: "Angela Roe",      role: "manager",  email: "edgewaterautcenter@gmail.com",  hourlyRate: 0,    dailyHours: 8, color: "#f59e0b", avatar: null, notifyHours: { start: 7, end: 20 }, notifyTypes: ["return_parts","approval","sold","digest"], username: "angela",   password: "angela"   },
  { id: "dmitriy", name: "Dmitriy Antonov", role: "manager",  email: "dmitriy@edgewaterauto.com", hourlyRate: 0,    dailyHours: 8, color: "#f97316", avatar: null, notifyHours: { start: 7, end: 20 }, notifyTypes: ["return_parts","approval"],   username: "bossman",  password: "bossman"  },
  { id: "robert",  name: "Robert Korkow",   role: "sales",    email: "robert@edgewaterauto.com",  hourlyRate: 25,   dailyHours: 8, color: "#22c55e", avatar: null, notifyHours: { start: 8, end: 18 }, notifyTypes: ["new_intake","line_ready"],  username: "robert",   password: "robert"   },
  { id: "james",   name: "James",           role: "mechanic", email: "james@edgewaterauto.com",   hourlyRate: 28,   dailyHours: 8, color: "#3b82f6", avatar: "/images/james.jpg", notifyHours: { start: 8, end: 17 }, notifyTypes: ["wo_assigned","parts_arrived"],  username: "james",    password: "james"    },
  { id: "sky",     name: "Sky Thomas",      role: "lot",      email: "sky@edgewaterauto.com",     hourlyRate: 18,   dailyHours: 8, color: "#a855f7", avatar: null, notifyHours: { start: 8, end: 17 }, notifyTypes: ["wo_assigned","parts_arrived"],  username: "sky",      password: "sky"      },
  { id: "john",    name: "John",            role: "viewer",   email: "",                          hourlyRate: 0,    dailyHours: 8, color: "#64748b", avatar: null, notifyHours: { start: 8, end: 17 }, notifyTypes: [],                                username: "john",     password: "john"     },
];

// Ruban — 1099 detailer, Blue Diamond Auto Detail
// Auto-assigned to any vehicle valued $8000+, unlimited job capacity
const RUBAN = {
  id: "ruban",
  name: "Ruban",
  company: "Blue Diamond Auto Detail",
  role: "1099_detail",
  color: "#0891b2",
  rateRegular: 200,   // cars
  rateLarge: 250,     // trucks/SUVs/vans
  unlimited: true,
  autoAssignThreshold: 8000,
};

const isLargeVehicle = (vehicle) =>
  /truck|f-?150|f-?250|silverado|sierra|ram|tacoma|tundra|colorado|ranger|frontier|suv|expedition|tahoe|suburban|navigator|escalade|4runner|sequoia|highlander|traverse|yukon|van|caravan|odyssey|sienna/i
  .test((vehicle?.make || "") + " " + (vehicle?.model || ""));

const DEFAULT_VENDORS = [
  // Towing
  { id: "v1",  name: "Valley Towing",              type: "TOW",     phone: "503-364-8888", email: "dispatch@valleytowing.com",    address: "Salem, OR",    tags: [], priority: 1, returnWindow: null, restockingFee: null, notes: "", credentials: {} },
  // Exhaust
  { id: "v2",  name: "Muffler Masters Salem",       type: "EXHAUST", phone: "503-363-0777", email: "",                             address: "Salem, OR",    tags: ["EXHAUST"], priority: 1, returnWindow: null, restockingFee: null, notes: "", credentials: {} },
  // Tires
  { id: "v3",  name: "Les Schwab Tire - Lancaster", type: "TIRES",   phone: "503-363-5524", email: "",                             address: "2290 Lancaster Dr NE, Salem, OR", tags: ["TIRES_WHEELS"], priority: 1, returnWindow: 30, restockingFee: 0, notes: "Les Schwab commercial account", credentials: {} },
  { id: "v3b", name: "Les Schwab Tire - South",     type: "TIRES",   phone: "503-588-0855", email: "",                             address: "3500 Commercial St SE, Salem, OR", tags: ["TIRES_WHEELS"], priority: 2, returnWindow: 30, restockingFee: 0, notes: "", credentials: {} },
  // Parts - local
  { id: "v4",  name: "AutoZone Salem",              type: "PARTS",   phone: "503-391-5500", email: "commercial@autozone.com",      address: "3950 Commercial St SE, Salem, OR", tags: ["NEW_PARTS","BRAKES","ELECTRICAL","FLUIDS"], priority: 1, returnWindow: 90, restockingFee: 0, notes: "", credentials: {} },
  { id: "v5",  name: "O'Reilly Auto Salem",         type: "PARTS",   phone: "503-399-8887", email: "",                             address: "1285 Commercial St SE, Salem, OR", tags: ["NEW_PARTS","BRAKES","ELECTRICAL"], priority: 2, returnWindow: 90, restockingFee: 0, notes: "", credentials: {} },
  { id: "v5b", name: "NAPA Auto Parts Salem",       type: "PARTS",   phone: "503-362-3851", email: "",                             address: "775 Commercial St SE, Salem, OR", tags: ["NEW_PARTS","BRAKES","TOOLS"], priority: 3, returnWindow: 90, restockingFee: 15, notes: "", credentials: {} },
  // Parts - online
  { id: "v6",  name: "RockAuto",                   type: "PARTS",   phone: "608-661-1376", email: "orders@rockauto.com",          address: "Online",       tags: ["ONLINE_PARTS","BRAKES","SUSPENSION","TRANSMISSION"], priority: 4, returnWindow: 30, restockingFee: 15, notes: "", credentials: {} },
  { id: "v6b", name: "FCP Euro",                   type: "PARTS",   phone: "877-634-0063", email: "orders@fcpeuro.com",           address: "Online",       tags: ["IMPORT_PARTS"], priority: 1, returnWindow: 365, restockingFee: 0, notes: "Lifetime replacement guarantee on everything", credentials: {} },
  { id: "v6c", name: "Worldpac",                   type: "PARTS",   phone: "800-888-9982", email: "orders@worldpac.com",          address: "Online/Local", tags: ["IMPORT_PARTS","NEW_PARTS"], priority: 2, returnWindow: 90, restockingFee: 0, notes: "", credentials: {} },
  { id: "v6d", name: "Amazon Business",            type: "PARTS",   phone: "",             email: "",                             address: "Online",       tags: ["ONLINE_PARTS","ELECTRICAL","TOOLS","FLUIDS"], priority: 5, returnWindow: 30, restockingFee: 0, notes: "", credentials: {} },
  // Used parts / wrecking
  { id: "v7",  name: "LKQ Pick Your Part Salem",   type: "PARTS",   phone: "503-585-4494", email: "",                             address: "3250 Portland Rd NE, Salem, OR", tags: ["USED_PARTS","TRANSMISSION"], priority: 1, returnWindow: 30, restockingFee: 0, notes: "Self-service wrecking yard", credentials: {} },
  { id: "v7b", name: "Willamette Valley Salvage",  type: "PARTS",   phone: "503-362-4711", email: "",                             address: "Salem, OR",    tags: ["USED_PARTS","TRANSMISSION"], priority: 2, returnWindow: 30, restockingFee: 0, notes: "", credentials: {} },
  { id: "v7c", name: "Car-Parts.com",              type: "PARTS",   phone: "",             email: "",                             address: "Online",       tags: ["USED_PARTS"], priority: 3, returnWindow: 30, restockingFee: 0, notes: "National used parts network", credentials: {} },
  // Body / paint
  { id: "v8",  name: "Sherwin-Williams Auto Paint", type: "BODY",   phone: "503-390-2400", email: "",                             address: "Salem, OR",    tags: ["PAINT_BODY"], priority: 1, returnWindow: null, restockingFee: null, notes: "", credentials: {} },
  { id: "v8b", name: "PPG Auto Refinish",          type: "BODY",    phone: "503-364-5000", email: "",                             address: "Salem, OR",    tags: ["PAINT_BODY"], priority: 2, returnWindow: null, restockingFee: null, notes: "", credentials: {} },
  // Glass
  { id: "v9",  name: "Safelite AutoGlass Salem",   type: "GLASS",   phone: "503-378-0700", email: "",                             address: "3310 Portland Rd NE, Salem, OR", tags: ["GLASS"], priority: 1, returnWindow: null, restockingFee: null, notes: "", credentials: {} },
  // Detail
  { id: "v10", name: "Blue Diamond Auto Detail",   type: "DETAIL",  phone: "",             email: "",                             address: "Mobile - Salem, OR", tags: [], priority: 1, returnWindow: null, restockingFee: null, notes: "Ruban — our 1099 detailer", credentials: {} },
  // Alignment
  { id: "v11", name: "Firestone Complete Auto Care", type: "ALIGNMENT", phone: "503-363-2526", email: "",                          address: "Salem, OR",   tags: [], priority: 1, returnWindow: null, restockingFee: null, notes: "", credentials: {} },
  // ── Online vendors ──────────────────────────────────────────────────────────
  { id: "vo1",  name: "eBay Motors",              type: "PARTS", phone: "",              email: "",                              address: "Online", tags: ["USED_PARTS","ONLINE_PARTS","ELECTRICAL"], priority: 3, returnWindow: 30,  restockingFee: 0,  notes: "Great for used OEM, hard-to-find parts, discontinued items", credentials: {} },
  { id: "vo2",  name: "Amazon Business",          type: "PARTS", phone: "",              email: "",                              address: "Online", tags: ["ONLINE_PARTS","ELECTRICAL","TOOLS","FLUIDS"], priority: 3, returnWindow: 30,  restockingFee: 0,  notes: "Prime shipping, wide selection of filters/sensors/accessories", credentials: {} },
  { id: "vo3",  name: "Advance Auto Parts",       type: "PARTS", phone: "877-238-2623", email: "",                              address: "Online + Salem, OR", tags: ["NEW_PARTS","BRAKES","ELECTRICAL","FLUIDS"], priority: 2, returnWindow: 90,  restockingFee: 0,  notes: "Speed Perks rewards, same-day pickup available", credentials: {} },
  { id: "vo4",  name: "Summit Racing Equipment",  type: "PARTS", phone: "800-230-3030", email: "orders@summitracing.com",       address: "Online", tags: ["ONLINE_PARTS","SUSPENSION","ELECTRICAL","TOOLS"], priority: 4, returnWindow: 90,  restockingFee: 0,  notes: "Performance and hard parts, huge catalog", credentials: {} },
  { id: "vo5",  name: "Dorman Products",          type: "PARTS", phone: "800-523-2492", email: "",                              address: "Online", tags: ["NEW_PARTS","ELECTRICAL","SUSPENSION"], priority: 4, returnWindow: 90,  restockingFee: 15, notes: "OEM replacement specialist, good for discontinued OEM parts", credentials: {} },
  { id: "vo6",  name: "ECS Tuning",               type: "PARTS", phone: "800-924-5172", email: "orders@ecstuning.com",         address: "Online", tags: ["IMPORT_PARTS"], priority: 2, returnWindow: 60,  restockingFee: 0,  notes: "VW/Audi/BMW/Mercedes specialist, OEM and aftermarket", credentials: {} },
  { id: "vo7",  name: "Pelican Parts",             type: "PARTS", phone: "888-280-7799", email: "",                              address: "Online", tags: ["IMPORT_PARTS"], priority: 3, returnWindow: 30,  restockingFee: 0,  notes: "Porsche, BMW, Mercedes, Mini, Volvo specialist", credentials: {} },
  { id: "vo8",  name: "Tire Rack",                 type: "PARTS", phone: "888-981-3953", email: "",                              address: "Online", tags: ["TIRES_WHEELS"], priority: 2, returnWindow: 30,  restockingFee: 0,  notes: "Best online tire pricing, ships to installer or home", credentials: {} },
  { id: "vo9",  name: "1A Auto",                   type: "PARTS", phone: "888-844-3393", email: "",                              address: "Online", tags: ["ONLINE_PARTS","SUSPENSION","BRAKES"], priority: 4, returnWindow: 60,  restockingFee: 0,  notes: "Good video guides, decent pricing on suspension/brakes", credentials: {} },
  { id: "vo10", name: "PartsGeek",                 type: "PARTS", phone: "800-541-9352", email: "",                              address: "Online", tags: ["ONLINE_PARTS","BRAKES","SUSPENSION"], priority: 5, returnWindow: 30,  restockingFee: 15, notes: "Competitive pricing, slower shipping", credentials: {} },
  { id: "vo11", name: "AutoPartsWarehouse (APW)",  type: "PARTS", phone: "800-541-9352", email: "",                              address: "Online", tags: ["ONLINE_PARTS","NEW_PARTS"], priority: 5, returnWindow: 30,  restockingFee: 15, notes: "Budget option for non-critical parts", credentials: {} },
  { id: "vo12", name: "Gates / Dayco Belts",       type: "PARTS", phone: "",              email: "",                              address: "Online", tags: ["NEW_PARTS"], priority: 4, returnWindow: 90,  restockingFee: 0,  notes: "Timing belts, serpentine belts, hoses — OEM quality", credentials: {} },
  { id: "vo13", name: "ACDelco / GM Parts",        type: "PARTS", phone: "800-954-1478", email: "",                              address: "Online", tags: ["NEW_PARTS","ELECTRICAL"], priority: 3, returnWindow: 90,  restockingFee: 0,  notes: "OEM GM/Chevy/GMC/Cadillac parts at dealer cost", credentials: {} },
  { id: "vo14", name: "Mopar / Chrysler Parts",    type: "PARTS", phone: "",              email: "",                              address: "Online", tags: ["NEW_PARTS"], priority: 3, returnWindow: 90,  restockingFee: 0,  notes: "OEM Chrysler/Dodge/Jeep/Ram parts", credentials: {} },
  { id: "vo15", name: "CARiD",                     type: "PARTS", phone: "800-505-3274", email: "",                              address: "Online", tags: ["ONLINE_PARTS","ELECTRICAL","SUSPENSION"], priority: 5, returnWindow: 30,  restockingFee: 20, notes: "Wide selection, accessories and performance", credentials: {} },
];

const DEALER_PACK = 1500;

// ─── BIN/CATALOG STUBS (parts library/bins feature removed; safe no-ops) ─────
const BIN_SIZES = [];
const estimateBinSize = () => null;

// ─── CONVICTION LEVELS — adjustable via .env ──────────────────────────────────
// Set in .env to override defaults. Restart `npm start` after changing.
//   REACT_APP_CONVICTION_DIAGNOSIS  Hank — confidence at which mechanical diagnosis is final  (default 98)
//   REACT_APP_CONVICTION_PARTS      Roxy — confidence at which to return part recommendations (default 95)
//   REACT_APP_CONVICTION_BODY       Jimmy — confidence at which body-work plan is finalized   (default 95)
//   REACT_APP_CONVICTION_INSPECTION Julia — confidence at which inspection is complete        (default 95)
//   REACT_APP_CONVICTION_HIGH       Visual meter "high" tier color cutoff                      (default 95)
//   REACT_APP_CONVICTION_MED        Visual meter "medium" tier color cutoff                    (default 80)
//   REACT_APP_CONVICTION_LOW        Visual meter "low" tier color cutoff                       (default 60)
const _envInt = (k, d) => { const v = parseInt(process.env[k]); return Number.isFinite(v) ? v : d; };
const CONVICTION = {
  DIAGNOSIS:  _envInt("REACT_APP_CONVICTION_DIAGNOSIS",  98),
  PARTS:      _envInt("REACT_APP_CONVICTION_PARTS",      95),
  BODY:       _envInt("REACT_APP_CONVICTION_BODY",       95),
  INSPECTION: _envInt("REACT_APP_CONVICTION_INSPECTION", 95),
  HIGH:       _envInt("REACT_APP_CONVICTION_HIGH",       95),
  MED:        _envInt("REACT_APP_CONVICTION_MED",        80),
  LOW:        _envInt("REACT_APP_CONVICTION_LOW",        60),
};

// ─── DEALER INVENTORY SYNC ───────────────────────────────────────────────────
// Scrapes edgewaterautocenter.com/inventory nightly or on-demand via Grok
// Stores a lightweight cache: { vehicles: [...], lastSync: ISO }

const INVENTORY_CACHE_KEY = "ew4_inventory_cache";
const INVENTORY_URL = "https://www.edgewaterautocenter.com/inventory?clearall=1";
const SYNC_INTERVAL_HOURS = 12;
const DAILY_SYNC_HOUR = 5; // 5 AM local time — same as Title Tracker Pro's inventorySyncDaily

// Returns true if today's 5 AM has passed AND last sync was before today's 5 AM
const shouldRunDailySync = (lastSyncIso) => {
  const now = new Date();
  const todaySync = new Date(now.getFullYear(), now.getMonth(), now.getDate(), DAILY_SYNC_HOUR, 0, 0, 0);
  if (now < todaySync) return false; // not yet 5 AM today
  if (!lastSyncIso) return true; // never synced
  const last = new Date(lastSyncIso);
  return last < todaySync; // sync if last sync was before today's 5 AM
};

const scrapeInventoryWithGrok = async () => {
  // Scrape through our own same-origin Netlify function (no CORS, no Railway dependency).
  const scrapeBase = process.env.REACT_APP_SCRAPE_PROXY || "/api";

  const parseVehiclesFromHTML = (html) => {
    const vehicles = [];
    const blocks = html.split(/<label[^>]*>\s*Stock\s*#:\s*<\/label>/i);
    if (blocks.length < 2) return vehicles;

    for (let i = 1; i < blocks.length; i++) {
      const block = blocks[i];
      const prevBlock = blocks[i - 1];

      const stockMatch = block.match(/^\s*(\d{4,6})/);
      if (!stockMatch) continue;
      const stockNumber = stockMatch[1];

      let year = null, make = null, model = null, trim = null;
      // The listing's own title/VDP links sit at the END of prevBlock (right before this
      // vehicle's "Stock #:" label). Earlier vdp links in prevBlock belong to the PREVIOUS
      // vehicle — matching the FIRST one shifted every vehicle's year/make/model by one car.
      const urlMatches = [...prevBlock.matchAll(/\/vdp\/\d+\/Used-(\d{4})-([^"'\s]+)-for-sale/gi)];
      const urlMatch = urlMatches.length ? urlMatches[urlMatches.length - 1] : null;
      if (urlMatch) {
        year = parseInt(urlMatch[1]);
        const parts = urlMatch[2].split('-');
        make = parts[0] || null;
        model = parts[1] || null;
        trim = parts.slice(2).join(' ') || null;
      }

      const getLabel = (name) => {
        // <label> can carry attributes (e.g. class="lblMileage optionLabel") — tolerate them
        const m = block.match(new RegExp(`<label[^>]*>\\s*${name}\\s*<\\/label>\\s*[\\r\\n\\s]*([^<\\r\\n]{1,40})`, 'i'));
        return m ? m[1].trim() : null;
      };

      const vin          = getLabel('VIN:');
      const engine       = getLabel('Engine:');
      const transmission = getLabel('Trans:');
      const drivetrain   = getLabel('Drive:');
      const exteriorColor= getLabel('Color:');
      const interiorColor= getLabel('Interior:');
      const mileageM     = block.match(/<label[^>]*>\s*Mileage:\s*<\/label>\s*[\r\n\s]*([\d,]+)/i);
      const mileage      = mileageM ? parseInt(mileageM[1].replace(/,/g, '')) : null;
      // Only trust the listing's own price element — a bare $ match can grab the NEXT
      // vehicle's compare-checkbox price when this listing shows no price of its own.
      const priceM       = block.match(/class="[^"]*lblPrice[^"]*"[^>]*>\s*\$?\s*([\d,]+)/i);
      const price        = priceM ? parseFloat(priceM[1].replace(/,/g, '')) : null;

      vehicles.push({
        stockNumber, vin: vin||null, year, make, model, trim,
        mileage: mileage||null, price: price||null,
        exteriorColor: exteriorColor||null, interiorColor: interiorColor||null,
        engine: engine||null, drivetrain: drivetrain||null, transmission: transmission||null,
        scrapedAt: new Date().toISOString(),
      });
    }
    return vehicles;
  };

  // The server returns 25 vehicles — sort by newest (stock oldest = newest arrivals last added)
  // Use sortby=stocknew to get the most recently added vehicles first
  const sortParams = ['sortby=stocknew&', '', 'sortby=pricehigh&', 'sortby=pricelow&'];
  const allVehicles = [];
  const seen = new Set();

  for (let i = 0; i < sortParams.length; i++) {
    if (allVehicles.length >= 90) break;
    try {
      const url = `${scrapeBase}/scrape-inventory?${sortParams[i]}`;
      if (process.env.NODE_ENV !== "development") console.log(`[Scrape] Fetching sort ${i+1}/4...`);
      const res = await fetch(url);
      if (!res.ok) throw new Error(`Server ${res.status}`);
      const html = await res.text();
      const vehicles = parseVehiclesFromHTML(html);
      const newOnes = vehicles.filter(v => !seen.has(v.stockNumber));
      
      newOnes.forEach(v => { seen.add(v.stockNumber); allVehicles.push(v); });
      if (i < sortParams.length - 1) await new Promise(r => setTimeout(r, 600));
    } catch (e) {
      
    }
  }

  
  if (allVehicles.length === 0) throw new Error("No vehicles parsed — check Railway server has /scrape-inventory endpoint");
  return allVehicles;
};

// Auto-run nightly sync (once per session if >12h since last sync)
const maybeAutoSync = async (inventoryCache, setInventoryCache, vehicles, setVehicles, onSoldDetected) => {
  const lastSync = inventoryCache?.lastSync;
  const hoursSince = lastSync ? (Date.now() - new Date(lastSync).getTime()) / 3600000 : Infinity;
  if (hoursSince < SYNC_INTERVAL_HOURS) return; // Too recent
  try {
    const fresh = await scrapeInventoryWithGrok();
    if (!fresh?.length) return;
    const nc = { vehicles: fresh, lastSync: new Date().toISOString() };
    setInventoryCache(nc);
    await setInventoryCache_raw(nc);
    // Detect sold vehicles
    if (onSoldDetected) onSoldDetected(fresh, vehicles);
    console.log(`[Inventory Sync] ${fresh.length} vehicles pulled from site`);
  } catch (e) {
    console.warn("[Inventory Sync] Auto-sync failed:", e.message);
  }
};

// Compare cache to app vehicles — detect sold/missing
const detectSoldVehicles = (liveInventory, appVehicles) => {
  const liveStockNums = new Set(liveInventory.map(v => String(v.stockNumber)).filter(Boolean));
  const liveVINs = new Set(liveInventory.map(v => v.vin?.toUpperCase()).filter(Boolean));
  return appVehicles.filter(v => {
    if (["SOLD","SCRAPPED"].includes(v.stage)) return false; // already handled
    const stockMatch = v.stockNumber && liveStockNums.has(String(v.stockNumber));
    const vinMatch = v.vin && liveVINs.has(v.vin?.toUpperCase());
    const onSite = stockMatch || vinMatch;
    return !onSite; // not found on site = possibly sold
  });
};

const getInventoryCache = async () => {
  try {
    const raw = await window.storage.get(INVENTORY_CACHE_KEY);
    return raw ? JSON.parse(raw.value) : { vehicles: [], lastSync: null };
  } catch { return { vehicles: [], lastSync: null }; }
};

// Bump when the scrape parser changes so every device discards its old (mis-parsed)
// cache and re-syncs on next load, instead of waiting out the 24h staleness window.
const INVENTORY_PARSER_VERSION = 2;

const setInventoryCache_raw = async (data) => {
  const stamped = data ? { ...data, parserVersion: INVENTORY_PARSER_VERSION } : data;
  try { await window.storage.set(INVENTORY_CACHE_KEY, JSON.stringify(stamped)); } catch {}
  // Also push to Firestore so every device shares the same fresh cache (the app has
  // always LISTENED to appdata/ew4_inventory_cache but nothing ever wrote it, so other
  // devices kept seeing a months-old snapshot).
  try { await S.save(INVENTORY_CACHE_KEY, stamped); } catch {}
};
// Keep old name for backward compat
const setInventoryCachePersist = setInventoryCache_raw;

// Match a vehicle in the cache by stock#, full VIN, or last 6 VIN digits
const findInCache = (cache, { stockNumber, vin, vinLast6 }) => {
  const vehicles = cache?.vehicles || [];
  if (stockNumber) {
    const v = vehicles.find(v => String(v.stockNumber) === String(stockNumber));
    if (v) return v;
  }
  if (vin && vin.length >= 6) {
    const v = vehicles.find(v => v.vin && v.vin.toUpperCase() === vin.toUpperCase());
    if (v) return v;
  }
  if (vinLast6 && vinLast6.length === 6) {
    const v = vehicles.find(v => v.vin && v.vin.slice(-6).toUpperCase() === vinLast6.toUpperCase());
    if (v) return v;
  }
  return null;
};



// ─── SHOP STORAGE LOCATIONS ──────────────────────────────────────────────────
const STORAGE_LOCATIONS = [
  // Back office
  { id: "backoffice_s1", label: "Back Office — Shelf 1", zone: "Back Office", icon: "🗄" },
  { id: "backoffice_s2", label: "Back Office — Shelf 2", zone: "Back Office", icon: "🗄" },
  { id: "backoffice_s3", label: "Back Office — Shelf 3", zone: "Back Office", icon: "🗄" },
  { id: "backoffice_s4", label: "Back Office — Shelf 4", zone: "Back Office", icon: "🗄" },
  { id: "backoffice_s5", label: "Back Office — Shelf 5", zone: "Back Office", icon: "🗄" },
  { id: "backoffice_s6", label: "Back Office — Shelf 6", zone: "Back Office", icon: "🗄" },
  // Basement shelves
  { id: "basement_s1",  label: "Basement — Shelf 1",  zone: "Basement", icon: "📦" },
  { id: "basement_s2",  label: "Basement — Shelf 2",  zone: "Basement", icon: "📦" },
  { id: "basement_s3",  label: "Basement — Shelf 3",  zone: "Basement", icon: "📦" },
  { id: "basement_s4",  label: "Basement — Shelf 4",  zone: "Basement", icon: "📦" },
  { id: "basement_s5",  label: "Basement — Shelf 5",  zone: "Basement", icon: "📦" },
  { id: "basement_s6",  label: "Basement — Shelf 6",  zone: "Basement", icon: "📦" },
  { id: "basement_s7",  label: "Basement — Shelf 7",  zone: "Basement", icon: "📦" },
  { id: "basement_s8",  label: "Basement — Shelf 8",  zone: "Basement", icon: "📦" },
  // Basement rooms
  { id: "basement_r1",  label: "Basement — Room 1",   zone: "Basement", icon: "🚪" },
  { id: "basement_r2",  label: "Basement — Room 2",   zone: "Basement", icon: "🚪" },
  // Other locations
  { id: "locksmith_rm", label: "Locksmith Room",       zone: "Locksmith", icon: "🔑" },
  { id: "main_office",  label: "Main Office Shelf",    zone: "Main Office", icon: "🗃" },
  { id: "parts_sort",   label: "Parts Sorting Area",   zone: "Shop Floor", icon: "🔧" },
  { id: "desk1_top",    label: "Desk 1 — Top Drawer",  zone: "Desks", icon: "🗂" },
  { id: "desk1_low",    label: "Desk 1 — Lower Drawer",zone: "Desks", icon: "🗂" },
  { id: "desk2_top",    label: "Desk 2 — Top Drawer",  zone: "Desks", icon: "🗂" },
  { id: "desk2_low",    label: "Desk 2 — Lower Drawer",zone: "Desks", icon: "🗂" },
  { id: "desk3_top",    label: "Desk 3 — Top Drawer",  zone: "Desks", icon: "🗂" },
  { id: "desk3_low",    label: "Desk 3 — Lower Drawer",zone: "Desks", icon: "🗂" },
  { id: "desk4_top",    label: "Desk 4 — Top Drawer",  zone: "Desks", icon: "🗂" },
  { id: "desk4_low",    label: "Desk 4 — Lower Drawer",zone: "Desks", icon: "🗂" },
  { id: "unassigned",   label: "Unassigned",           zone: "Unassigned", icon: "❓" },
];

// Item types for catalog
const LABOR_RATE = 28; // James's hourly rate
const APPROVAL_THRESHOLD = 0.25; // Repair cost > 25% of vehicle value requires management approval

// Estimate repair cost from work orders
const calcRepairEstimate = (workOrders) => {
  const wos = workOrders || [];
  const laborHours = wos.reduce((s, w) => s + (parseFloat(w.estimatedHours) || 0), 0);
  const laborCost = laborHours * LABOR_RATE;
  const partsCost = wos.reduce((s, w) => s + (parseFloat(w.estimatedPartsCost) || 0), 0);
  const toolsCost = wos.reduce((s, w) => s + (parseFloat(w.specializedToolsCost) || 0), 0);
  const total = laborCost + partsCost + toolsCost;
  return { laborHours, laborCost, partsCost, toolsCost, total };
};
const TARGET_PROFIT_MIN = 2000;
const RECON_MAX_PCT = 0.30;
const HOLDING_COST_PER_DAY = 24;
const DELIVERY_ALERT_HOURS = 2;

// ─── SHOP BAYS ───────────────────────────────────────────────────────────────
const SHOP_BAYS = [
  { id: "bay1", label: "Bay 1 — Two-Post Hydraulic Lift", short: "Hydraulic Lift", icon: "🔩", color: "#3b82f6",
    description: "Full-height lift. Best for: brakes, suspension, CV axles, exhaust, struts, transmission, undercarriage.",
    requiresLift: true },
  { id: "bay2", label: "Bay 2 — Scissor Lift",            short: "Scissor Lift",   icon: "⬆️",  color: "#06b6d4",
    description: "Mid-height lift. Best for: tire swaps, oil changes, quick undercarriage checks, muffler work.",
    requiresLift: true },
  { id: "bay3", label: "Bay 3 — Flat Bay (No Lift)",       short: "Flat Bay",       icon: "🔧", color: "#a855f7",
    description: "Ground-level. Best for: engine work, diagnostics, electrical, battery, filters, interior, detailing.",
    requiresLift: false },
];

// ─── PARTS BIN SIZES ────────────────────────────────────────────────────────


// Rule-based bin size estimator — no API call needed, runs instantly

// Get the largest bin needed for a list of parts

// Detect lift requirement from WO description
const detectLiftType = (desc) => {
  const d = (desc||"").toLowerCase();
  if (/tire|wheel|oil change|lube|filter|muffler/.test(d)) return "scissor";
  if (/brake|suspension|cv axle|strut|spring|wheel bearing|exhaust|catalytic|undercarriage|differential|alignment|tie rod|ball joint|control arm/.test(d)) return "lift";
  return "ground";
};

const suggestBay = (desc, bayAssignments) => {
  const lift = detectLiftType(desc);
  const occupied = (id) => bayAssignments.some(a => a.bayId === id && a.status === "IN_USE");
  if (lift === "scissor" && !occupied("bay2")) return "bay2";
  if (lift === "lift" && !occupied("bay1")) return "bay1";
  if (lift === "lift" && !occupied("bay2")) return "bay2";
  if (!occupied("bay3")) return "bay3";
  if (!occupied("bay1")) return "bay1";
  if (!occupied("bay2")) return "bay2";
  return null;
};

// ─── CLEANING CHECKLIST SYSTEM PROMPT ────────────────────────────────────────
const CLEANING_SYSTEM_PROMPT = `You are the Edgewater Auto Center vehicle processing assistant in Salem, OR.
Generate a customized lot-prep cleaning checklist following the Edgewater New Vehicle Processing Checklist.

PRICING SORT RULE: under $7,000 = "For Cleaning" (quick wash+vacuum); $7,000+ = "For Detail" (full detail by detailer).
IMPORTANT: Edgewater does NOT want a full professional detail on most cars — they want a clean, presentable car. No paint correction, no clay bar, no wax unless explicitly requested.

VEHICLE-TYPE RULES:
- Truck: add bed cleaning items (vacuum bed, wipe bed walls, check for tools/items)
- SUV/Van: add third-row area if present; cargo area
- Convertible: add convertible top seam cleaning, rear window check
- All: standard exterior wash + interior quick prep from their checklist

INSPECTOR NOTE RULE: If inspector mentioned specific issues (stains, odor, pet hair, mud, smoke smell, etc.), add them as ⭐ HIGH priority items.

STICKER SUGGESTIONS: suggest 1-2 slogan stickers from this list that fit the vehicle: [Third Row Seat, 5-Speed, Gas Saver, Fully Loaded, Moon Roof, Low Miles, 4x4, Tow Package, One Owner, New Tires, All Wheel Drive, V8, Diesel, Lifted, Off Road, Leather Interior, Heated Seats, Backup Camera, Apple CarPlay]

DETAIL LEVEL SYSTEM — pick ONE based on vehicle condition and price:
1. SPIFF (under $5k OR nearly new/excellent condition): basic wash, vacuum, wipe, glass — 8-12 items total
2. QUICK CLEAN ($5k-$8k, good condition): full wash, interior clean, minor attention areas — 12-18 items
3. STANDARD DETAIL ($8k-$15k): thorough interior/exterior, address all noted issues — 18-25 items  
4. FULL DETAIL ($15k-$25k): deep clean everything, treat surfaces, full interior — 25-35 items
5. PREMIUM DETAIL ($25k+): concours-level attention, every surface perfect — 35+ items

SPIFF checklist (use for excellent/clean cars): exterior rinse + hand wash, wheels & tires wipe, glass inside/out, vacuum all surfaces, quick wipe dash/console/doors, check for trash, deodorizer, price sticker, lot placement. That's all — do NOT add unnecessary items.

PHASE STRUCTURE (always use all 6 phases, put appropriate items in each):
1. Arrival Prep — remove stickers/papers/plates/frames/garbage
2. Sort & Stage — confirm sort category, lot placement decision
3. Exterior Wash — wash, wheels, tires, glass, dry per detail level
4. Interior Prep — vacuum, wipe surfaces, windows per detail level
5. Final Touches — A/C low, radio low, price sticker, slogan stickers
6. Lot Placement — parking spot, key tag with stock#, extra keys to office

CRITICAL: Every phase MUST have at least 1 item. Items array must never be empty.

Return ONLY valid JSON, no markdown:
{
  "sortCategory": "Spiff"|"Quick Clean"|"Standard Detail"|"Full Detail"|"Premium Detail",
  "detailLevel": 1,
  "lotPlacement": "FRONT_LINE"|"STANDARD",
  "phases": [
    { "phase": 1, "title": "Arrival Prep", "items": [{"id":"p1_1","text":"Remove all window stickers and dealer plates","priority":"normal","done":false}] },
    { "phase": 2, "title": "Sort & Stage", "items": [{"id":"p2_1","text":"Confirm detail level and park in staging area","priority":"normal","done":false}] },
    { "phase": 3, "title": "Exterior Wash", "items": [{"id":"p3_1","text":"Rinse vehicle with pressure washer","priority":"normal","done":false}] },
    { "phase": 4, "title": "Interior Prep", "items": [{"id":"p4_1","text":"Vacuum all seats, floors, and trunk","priority":"normal","done":false}] },
    { "phase": 5, "title": "Final Touches", "items": [{"id":"p5_1","text":"Set A/C to low, radio to country/soft rock low","priority":"normal","done":false}] },
    { "phase": 6, "title": "Lot Placement", "items": [{"id":"p6_1","text":"Park in correct lot position, add key tag with stock number","priority":"normal","done":false}] }
  ],
  "inspectorPassthrough": [],
  "sloganSuggestions": ["sticker1", "sticker2"]
}`;

// ─── THEME ─────────────────────────────────────────────────────────────────

const BASE_THEME = {
  // Light blue / dark navy theme
  bg:      "#e8f0fa",   // light blue-gray page background
  surface: "#f4f8fe",   // card surface — very light blue-white
  s2:      "#dde8f5",   // secondary surface — soft blue
  s3:      "#ccd9ee",   // tertiary — slightly deeper blue
  border:  "#b3c9e6",   // border — medium blue-gray
  bLight:  "#cdd9ec",   // lighter border
  amber:   "#d97706",   // amber — slightly deeper for light bg readability
  red:     "#dc2626",   // red
  green:   "#16a34a",   // green
  blue:    "#1d4ed8",   // dark blue — primary accent
  purple:  "#7c3aed",   // purple
  cyan:    "#0369a1",   // deep cyan-blue
  text:    "#0f1f3d",   // near-black navy — primary text
  textS:   "#2d4a7a",   // medium navy — secondary text
  textM:   "#7a96be",   // muted blue-gray — placeholder/label text
};

// ─── UTILS ───────────────────────────────────────────────────────────────────

const genId = () => Math.random().toString(36).slice(2, 10);
const todayStr = () => new Date().toISOString().split("T")[0];
const daysSince = (d) => d ? Math.floor((Date.now() - new Date(d)) / 86400000) : 0;
const addDays = (ds, n) => { const d = new Date(ds + "T12:00:00"); d.setDate(d.getDate() + n); return d.toISOString().split("T")[0]; };
const fmtDate = (ds) => ds ? new Date(ds + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—";
const fmtMoney = (n) => "$" + (n || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 0 });
const fmtMoneyFull = (n) => "$" + (n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

// ─── LOGIN SCREEN ────────────────────────────────────────────────────────────

const GOOGLE_CLIENT_ID = "853685394952-247vlg7g4bhl9pds532qtr2tridi9ohl.apps.googleusercontent.com";

const LoginScreen = ({ staff, onLogin }) => {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(false);
  const [googleError, setGoogleError] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const googleBtnRef = useRef(null);

  const attempt = () => {
    const u = username.trim().toLowerCase();
    const p = password.trim().toLowerCase();
    let user = staff.find(s => s.username === u && s.password === p);
    if (!user) {
      const def = DEFAULT_STAFF.find(s => s.username === u && s.password === p);
      if (def) {
        user = staff.find(s => s.id === def.id) || def;
        user = { ...user, username: def.username, password: def.password };
      }
    }
    if (user) { setError(false); onLogin(user); }
    else setError(true);
  };

  // Handle Google Sign-In credential response
  const handleGoogleCredential = (response) => {
    setGoogleLoading(true); setGoogleError(false);
    try {
      // Decode the JWT payload (no verification needed — Google already verified it)
      const payload = JSON.parse(atob(response.credential.split(".")[1]));
      const email = payload.email?.toLowerCase();
      const name = payload.name;
      // Match to a staff member by email
      let user = staff.find(s => s.email?.toLowerCase() === email);
      if (!user) user = DEFAULT_STAFF.find(s => s.email?.toLowerCase() === email);
      if (user) {
        // Merge latest staff data
        const merged = staff.find(s => s.id === user.id) || user;
        onLogin({ ...merged, googleName: name, googleEmail: email });
      } else {
        setGoogleError(true);
      }
    } catch { setGoogleError(true); }
    setGoogleLoading(false);
  };

  // Load Google Identity Services script and render button
  useEffect(() => {
    const scriptId = "google-gsi";
    const initGoogle = () => {
      if (window.google?.accounts?.id) {
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: handleGoogleCredential,
        });
        if (googleBtnRef.current) {
          window.google.accounts.id.renderButton(googleBtnRef.current, {
            theme: "outline", size: "large", width: 292, text: "signin_with",
          });
        }
      }
    };
    if (!document.getElementById(scriptId)) {
      const script = document.createElement("script");
      script.id = scriptId;
      script.src = "https://accounts.google.com/gsi/client";
      script.async = true; script.defer = true;
      script.onload = initGoogle;
      document.head.appendChild(script);
    } else initGoogle();
  }, []);

  return (
    <div style={{ minHeight: "100vh", background: "#0f1f3d", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 24 }}>
      <img src="/images/logogrey.png" alt="Edgewater" style={{ height: 160, objectFit: "contain", marginBottom: 24, filter: "brightness(0) invert(1)" }}
        onError={e => e.target.style.display = "none"} />
      <div style={{ fontSize: 28, fontWeight: 900, color: "#f59e0b", fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.1em", marginBottom: 4 }}>RECON MANAGER</div>
      <div style={{ fontSize: 11, color: "#7a96be", marginBottom: 32 }}>Edgewater Auto Center · Salem, OR</div>
      <div style={{ background: "#1a2d50", borderRadius: 16, padding: "28px 24px", width: "100%", maxWidth: 340, border: "1px solid #2d4a7a" }}>

        {/* Google Sign-In button */}
        <div style={{ marginBottom: 20 }}>
          <div ref={googleBtnRef} style={{ display: "flex", justifyContent: "center" }} />
          {googleLoading && <div style={{ textAlign: "center", fontSize: 11, color: "#7a96be", marginTop: 8 }}>Signing in…</div>}
          {googleError && <div style={{ textAlign: "center", fontSize: 11, color: "#dc2626", marginTop: 8 }}>No Edgewater account found for that Google address.<br/>Use username & password below.</div>}
        </div>

        {/* Divider */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
          <div style={{ flex: 1, height: 1, background: "#2d4a7a" }} />
          <span style={{ fontSize: 10, color: "#7a96be", textTransform: "uppercase", letterSpacing: "0.1em" }}>or</span>
          <div style={{ flex: 1, height: 1, background: "#2d4a7a" }} />
        </div>

        {/* Username / password */}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#7a96be", textTransform: "uppercase", marginBottom: 6 }}>Username</div>
          <input value={username} onChange={e => setUsername(e.target.value)} onKeyDown={e => e.key === "Enter" && attempt()}
            placeholder="Enter your username"
            style={{ width: "100%", background: "#0f1f3d", border: `1px solid ${error ? "#dc2626" : "#2d4a7a"}`, borderRadius: 8, padding: "10px 14px", color: "#fff", fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
        </div>
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: "#7a96be", textTransform: "uppercase", marginBottom: 6 }}>Password</div>
          <input value={password} onChange={e => setPassword(e.target.value)} onKeyDown={e => e.key === "Enter" && attempt()}
            type="password" placeholder="Enter your password"
            style={{ width: "100%", background: "#0f1f3d", border: `1px solid ${error ? "#dc2626" : "#2d4a7a"}`, borderRadius: 8, padding: "10px 14px", color: "#fff", fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
        </div>
        {error && <div style={{ fontSize: 11, color: "#dc2626", marginBottom: 12, textAlign: "center" }}>Incorrect username or password</div>}
        <button onClick={attempt}
          style={{ width: "100%", padding: "13px", background: "#f59e0b", border: "none", borderRadius: 10, color: "#000", fontSize: 14, fontWeight: 900, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.08em", cursor: "pointer" }}>
          SIGN IN
        </button>
      </div>
    </div>
  );
};

// ─── TOW LOCATIONS ────────────────────────────────────────────────────────────
const TOW_LOCATIONS = [
  { id: "main_lot",   label: "Main Lot — 4250 Salem Dallas Hwy NW, Salem OR 97304" },
  { id: "mech_shop",  label: "Mechanic Shop — 2365 12th St NE, Salem OR 97304" },
];

// ─── BODY WORK CHAT WITH HANK ─────────────────────────────────────────────────

const BodyWorkChat = ({ vehicle, onComplete, onClose, staff, vendors }) => {
  const [messages, setMessages] = useState([]);
  const [apiMessages, setApiMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [lastJimmyInput, setLastJimmyInput] = useState("");
  const [jimmySendError, setJimmySendError] = useState(false);;
  const [repairs, setRepairs] = useState(null);
  const [confidence, setConfidence] = useState(0);
  const [imgErr, setImgErr] = useState(false);
  const [jimmyImg, setJimmyImg] = useState(null);
  const bottomRef = useRef(null);

  // Jimmy avatar — body work specialist
  const JimmyAvatar = ({ size = 36, thinking = false }) => {
    const src = thinking ? "/images/jimmy-thinking.jpg" : "/images/jimmy-working.jpg";
    return !imgErr ? (
      <img src={src} alt="Jimmy" onError={() => setImgErr(true)}
        style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", objectPosition: "top center", border: "3px solid #f97316", flexShrink: 0 }} />
    ) : (
      <div style={{ width: size, height: size, borderRadius: "50%", background: "linear-gradient(135deg, #f97316, #ea580c)", border: "3px solid #f97316", display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.4, flexShrink: 0 }}>🔧</div>
    );
  };

  useEffect(() => {
    // Resume from paused state if available
    if (vehicle._jimmyPaused) {
      const saved = vehicle._jimmyPaused;
      if (saved.messages?.length) setMessages(saved.messages);
      if (saved.confidence) setConfidence(saved.confidence);
      setMessages(m => [...m, { role: "assistant", content: `Back at it — I remember where we left off on this ${vehicle.year} ${vehicle.make} ${vehicle.model}. What else do you want to go over?` }]);
      return;
    }
    const opening = `Hey, I'm Jimmy — I handle all the body work here at Edgewater. Let's go through this ${vehicle.year} ${vehicle.make} ${vehicle.model} and make sure I know exactly what we're dealing with before I put together the repair plan. Walk me through what you're seeing — dents, scratches, paint issues, rust, glass, anything.`;
    setMessages([{ role: "assistant", content: opening }]);
  }, []);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const send = async () => {
    if ((!input.trim() && !jimmyImg) || loading) return;
    const imgForJimmy = jimmyImg;
    if (jimmyImg) setJimmyImg(null);
    const userContent = imgForJimmy
      ? [{ type: "image", source: { type: "base64", media_type: imgForJimmy.mediaType, data: imgForJimmy.b64 } }, { type: "text", text: input.trim() || "Here's a photo of the damage. Analyze every visible body issue — dents, scratches, rust, paint, glass, trim, anything you can see." }]
      : input.trim();
    const userMsg = { role: "user", content: userContent };
    const newApiMsgs = [...apiMessages, userMsg];
    setMessages(prev => [...prev, { role: "user", content: input.trim() || "📷 [Photo attached]" }]);
    setInput(""); setLoading(true);

    const system = `You are Jimmy, Edgewater Auto Center's body work specialist in Salem, OR. You're assessing the ${vehicle.year} ${vehicle.make} ${vehicle.model} (Stock #${vehicle.stockNumber}).

Your personality: experienced, methodical, practical. You don't issue repair plans until you're ${CONVICTION.BODY}%+ confident you know everything that needs to be done. You ask follow-up questions about anything ambiguous.

CONFIDENCE RULES:
- Start at 0%. Increase as you gather info on each damaged area.
- Ask clarifying questions for: exact location of damage, size/severity, whether rust goes through the metal, glass damage extent, trim missing or just broken.
- If the user sends a PHOTO, analyze every visible issue in detail — note each dent, scratch, rust spot, paint problem, glass issue, missing trim. This counts as high-confidence info.
- Do NOT set done=true until confidence >= ${CONVICTION.BODY}.
- Once confident: compile the COMPLETE repair plan with every step and every part needed.

REPAIR PLAN RULES:
- List every specific task (sand, prime, paint, clear coat are separate tasks if needed)
- List exact parts needed (part name, quantity) for each repair task
- Flag anything that requires a body shop vs can be done in-house
- Flag if unsafe to drive

Respond in JSON only — no markdown:
{
  "message": "Jimmy's response",
  "confidence": 0-100,
  "done": false,
  "repairs": null
}

When confidence >= ${CONVICTION.BODY} AND user confirms that's all the damage, set done=true and repairs:
{
  "summary": "complete damage description",
  "needsBodyShop": true/false,
  "tasks": [{"description":"exact task","category":"PAINT|DENT|RUST|GLASS|STRUCTURAL|TRIM","requiresShop":true/false,"parts":[{"name":"part name","qty":1}]}],
  "unsafeToDrive": false,
  "unsafeToDriveReason": null
}`;

    try {
      const res = await fetch("/api/claude", { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${await getIdToken()}` },
        body: JSON.stringify({ model: "claude-sonnet-4-6", max_tokens: 1000, system, messages: newApiMsgs })
      });
      const data = await res.json();
      const raw = data.content?.[0]?.text || "{}";
      let parsed = {};
      try {
        const start = raw.indexOf("{"); const end = raw.lastIndexOf("}");
        if (start >= 0 && end > start) parsed = JSON.parse(raw.slice(start, end + 1));
      } catch {}
      if (!parsed.message) parsed = { message: raw, confidence, done: false };
      const assistantMsg = { role: "assistant", content: parsed.message };
      setMessages(prev => [...prev, assistantMsg]);
      setApiMessages([...newApiMsgs, assistantMsg]);
      if (parsed.confidence !== undefined) setConfidence(parsed.confidence);
      if (parsed.done && parsed.repairs) setRepairs(parsed.repairs);
    } catch (e) {
      setMessages(prev => [...prev, { role: "assistant", content: "Sorry, say that again?" }]);
      setJimmySendError(true);
    }
    setLoading(false);
  };

  const applyRepairs = () => {
    if (!repairs) return;
    const newWOs = repairs.tasks.map(t => ({
      id: genId(), category: t.requiresShop ? "VENDOR" : "COSMETIC",
      description: t.description, priority: t.category === "STRUCTURAL" ? "HIGH" : "MEDIUM",
      status: "OPEN", createdAt: todayStr(), estimatedHours: 0, actualHours: 0,
      diagnosisRationale: repairs.summary, needsBodyShop: t.requiresShop,
      bodyWorkParts: t.parts || [], isBodyWork: true,
    }));
    onComplete(newWOs, repairs);
  };

  if (repairs) return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14 }}>
        <JimmyAvatar size={44} />
        <div>
          <div style={{ fontSize: 14, fontWeight: 800, color: "#000" }}>Jimmy — Body Work Assessment</div>
          <div style={{ fontSize: 10, color: C.green, fontWeight: 700 }}>✅ 95%+ confident — ready to issue repair orders</div>
        </div>
      </div>
      <div style={{ background: C.surface, border: `2px solid ${repairs.needsBodyShop ? C.red + "50" : C.amber + "40"}`, borderRadius: 12, padding: "14px 16px", marginBottom: 14 }}>
        <div style={{ fontSize: 11, color: "#555", marginBottom: 10 }}>{repairs.summary}</div>
        {repairs.unsafeToDrive && <div style={{ background: C.red + "15", border: `1px solid ${C.red}40`, borderRadius: 6, padding: "8px 10px", marginBottom: 10, fontSize: 11, color: C.red, fontWeight: 700 }}>⚠ UNSAFE TO DRIVE — {repairs.unsafeToDriveReason}</div>}
        {repairs.needsBodyShop && <Badge color={C.red} small>Requires Body Shop</Badge>}
        <div style={{ marginTop: 10 }}>
          {repairs.tasks.map((t, i) => (
            <div key={i} style={{ padding: "6px 0", borderBottom: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#000" }}>{t.description}</div>
              {t.parts?.length > 0 && (
                <div style={{ fontSize: 10, color: C.textM, marginTop: 2 }}>
                  Parts: {(t.parts || []).map(p => typeof p === "string" ? p : `${p.name}${p.qty > 1 ? ` ×${p.qty}` : ""}`).join(", ")}
                </div>
              )}
              {t.requiresShop && <Badge color={C.red} small>Body Shop Required</Badge>}
            </div>
          ))}
        </div>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <Btn variant="amber" onClick={applyRepairs}><CheckCircle size={13} /> Issue Repair Orders</Btn>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
      </div>
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "65vh" }}>
      {/* Full-screen Jimmy image */}
      <div style={{ position: "sticky", top: 0, zIndex: 10, width: "100%", background: "#1a0800", overflow: "hidden", marginBottom: 0, display: "flex", justifyContent: "center", maxHeight: 260 }}>
        <img src={loading ? "/images/jimmy-thinking.jpg" : "/images/jimmy-working.jpg"} alt="Jimmy"
          onError={() => setImgErr(true)}
          style={{ width: "100%", maxWidth: 480, objectFit: "cover", objectPosition: "top center", display: imgErr ? "none" : "block" }} />
        {imgErr && <div style={{ width: "100%", height: 200, background: "linear-gradient(180deg, #2d1400 0%, #1a0800 100%)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 80 }}>🔧</div>}
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "linear-gradient(transparent, rgba(26,8,0,0.95))", padding: "24px 16px 10px" }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#f97316", fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.1em" }}>JIMMY — BODY WORK SPECIALIST</div>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.6)" }}>{vehicle.year} {vehicle.make} {vehicle.model} #{vehicle.stockNumber}</div>
        </div>
        <div style={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 4 }}>
          <button onClick={() => {
            const state = { messages, confidence };
            const updatedVehicle = { ...vehicle, _jimmyPaused: state };
            if (onComplete) onComplete([], null, updatedVehicle);
            if (onClose) onClose();
          }} style={{ background: "rgba(0,0,0,0.6)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 6, padding: "4px 8px", cursor: "pointer", fontSize: 10, color: "rgba(255,255,255,0.7)", fontFamily: "inherit" }}>⏸ Pause</button>
        </div>
      </div>
      {/* Confidence bar */}
      <div style={{ padding: "8px 0", borderBottom: `1px solid ${C.border}`, marginBottom: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <div style={{ flex: 1, height: 5, borderRadius: 3, background: C.border }}>
            <div style={{ height: "100%", borderRadius: 3, width: `${confidence}%`, background: confidence >= CONVICTION.HIGH ? C.green : confidence >= CONVICTION.LOW ? C.amber : C.textM, transition: "width 0.5s ease" }} />
          </div>
          <span style={{ fontSize: 10, fontWeight: 700, color: confidence >= CONVICTION.HIGH ? C.green : C.textM, minWidth: 36 }}>{confidence}%</span>
          <span style={{ fontSize: 9, color: C.textM }}>{confidence < 40 ? "Gathering info…" : confidence < 70 ? "Getting clearer…" : confidence < 95 ? "Almost ready" : "Ready to issue repair orders"}</span>
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 10, paddingRight: 4 }}>
        {messages.map((m, i) => (
          <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start", gap: 8 }}>
            {m.role === "assistant" && <JimmyAvatar size={28} />}
            <div style={{ maxWidth: "80%", padding: "10px 14px", borderRadius: m.role === "user" ? "12px 12px 4px 12px" : "12px 12px 12px 4px", background: m.role === "user" ? C.amber + "25" : C.s2, border: `1px solid ${m.role === "user" ? C.amber + "50" : C.border}`, fontSize: 13, color: C.text, lineHeight: 1.6 }}>
              {m.role === "assistant" && <div style={{ fontSize: 9, color: "#f97316", fontWeight: 700, textTransform: "uppercase", marginBottom: 3 }}>JIMMY</div>}
              {m.content}
            </div>
          </div>
        ))}
        {loading && <div style={{ display: "flex", gap: 8 }}><JimmyAvatar size={28} thinking /><div style={{ fontSize: 12, color: C.textM, padding: "8px 12px", background: C.s2, borderRadius: 8 }}>Thinking…</div></div>}
        <div ref={bottomRef} />
      </div>
      <div style={{ paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
        {jimmyImg && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, background: "#f9731610", border: "1px solid #f9731640", borderRadius: 7, padding: "5px 8px" }}>
            <img src={jimmyImg.preview} alt="" style={{ width: 44, height: 44, borderRadius: 6, objectFit: "cover" }} />
            <span style={{ fontSize: 10, color: "#f97316", flex: 1 }}>📷 Photo — Jimmy will analyze the damage</span>
            <button onClick={() => setJimmyImg(null)} style={{ background: "none", border: "none", cursor: "pointer", color: C.textM }}>✕</button>
          </div>
        )}
        <div style={{ display: "flex", gap: 8 }}>
          <ImageAttachButton onImage={setJimmyImg} style={{ border: "1px solid #f9731640" }} />
          <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && send()}
            placeholder="Describe the damage — or attach a photo…"
            style={{ flex: 1, background: C.s2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none" }} />
          {jimmySendError && lastJimmyInput && (
            <button onClick={() => { setInput(lastJimmyInput); setJimmySendError(false); setTimeout(() => send(), 50); }}
              style={{ flexShrink:0, background:C.amber, border:"none", borderRadius:8, padding:"8px 10px", cursor:"pointer", fontSize:11, fontWeight:700, color:"#000", fontFamily:"inherit", whiteSpace:"nowrap" }}>
              🔄 Retry
            </button>
          )}
          <Btn variant="amber" onClick={send} disabled={(!input.trim() && !jimmyImg) || loading}><Send size={14} /></Btn>
        </div>
      </div>
    </div>
  );
};

// ─── STORAGE ─────────────────────────────────────────────────────────────────

// ─── FIREBASE CONFIG ─────────────────────────────────────────────────────────
const firebaseConfig = {
  apiKey: "AIzaSyBGOumluoySlXVGCFWUfHVhDPvi_DDMX08",
  authDomain: "edgewater-recon-manager.firebaseapp.com",
  projectId: "edgewater-recon-manager",
  storageBucket: "edgewater-recon-manager.firebasestorage.app",
  messagingSenderId: "97880335509",
  appId: "1:97880335509:web:10e469515a0eacfd1c279c"
};
const firebaseApp = initializeApp(firebaseConfig);
const db = getFirestore(firebaseApp);

// ─── FIREBASE AUTH ───────────────────────────────────────────────────────
// Every browser silently signs in anonymously so backend proxy can verify a real Firebase
// session token before forwarding AI calls. No user-visible login change.
const auth = getAuth(firebaseApp);
let _authPromise = null;
function _ensureSignedIn() {
  if (_authPromise) return _authPromise;
  _authPromise = new Promise((resolve, reject) => {
    onAuthStateChanged(auth, (u) => {
      if (u) return resolve(u);
      signInAnonymously(auth).catch(reject);
    }, reject);
  });
  return _authPromise;
}
// Used by all proxy fetches — returns a fresh Firebase ID token.
async function getIdToken() {
  const u = await _ensureSignedIn();
  return u.getIdToken();
}
// Kick off sign-in on module load
_ensureSignedIn().catch(e => console.warn("Anonymous sign-in failed:", e));


// Shared data keys go to Firebase — login stays local
const LOCAL_KEYS = new Set(['ew4_user']);
const S = {
  save: async (k, v) => {
    if (LOCAL_KEYS.has(k)) {
      try { localStorage.setItem(k, JSON.stringify(v)); } catch {}
    } else {
      // Always write to localStorage as instant backup
      try { localStorage.setItem('backup_' + k, JSON.stringify({ v, t: Date.now() })); } catch {}
      // Then write to Firebase
      try {
        await setDoc(doc(db, 'appdata', k), { value: JSON.stringify(v), t: Date.now() });
        // Firebase confirmed — clear backup
        try { localStorage.removeItem('backup_' + k); } catch {}
      } catch (e) {
        console.warn('[DB save]', k, e.message);
        // Leave backup in localStorage — will retry on next load
      }
    }
  },
  load: async (k) => {
    if (LOCAL_KEYS.has(k)) {
      try { const r = localStorage.getItem(k); return r ? JSON.parse(r) : null; } catch { return null; }
    } else {
      try {
        const snap = await getDoc(doc(db, 'appdata', k));
        const firebaseData = snap.exists() ? JSON.parse(snap.data().value) : null;
        // Check if there's a newer unconfirmed backup in localStorage
        const backup = localStorage.getItem('backup_' + k);
        if (backup) {
          try {
            const { v: backupData, t: backupTime } = JSON.parse(backup);
            const firebaseTime = snap.exists() ? (snap.data().t || 0) : 0;
            if (backupTime > firebaseTime) {
              // Backup is newer — Firebase write failed last time, retry now
              console.log('[DB recovery] Restoring unsaved data for', k);
              await setDoc(doc(db, 'appdata', k), { value: JSON.stringify(backupData), t: backupTime });
              localStorage.removeItem('backup_' + k);
              return backupData;
            }
          } catch {}
          localStorage.removeItem('backup_' + k);
        }
        return firebaseData;
      } catch { return null; }
    }
  },
};

// ─── VENDOR ROUTING ──────────────────────────────────────────────────────────

const getVendorLoad = (id, vehicles) =>
  vehicles.filter(v => v.stage !== "SOLD" && v.workOrders?.some(w => w.assignedVendorId === id && w.status !== "COMPLETE")).length;

const autoVendor = (type, vendors, vehicles) => {
  const list = vendors.filter(v => v.type === type).sort((a, b) => a.priority - b.priority);
  return list.find(v => !v.maxConcurrent || getVendorLoad(v.id, vehicles) < v.maxConcurrent) || list[0] || null;
};

// ─── PROFIT ANALYSIS ─────────────────────────────────────────────────────────

const calcProfit = (vehicle, ledger, kbbValue) => {
  const entries = ledger.filter(e => e.vehicleId === vehicle.id);
  const costIn = entries.filter(e => e.category !== "SALE").reduce((s, e) => s + (e.amount || 0), 0);
  const saleEntry = entries.find(e => e.category === "SALE");
  const purchaseEntry = entries.find(e => e.category === "PURCHASE");
  // Only use manual sale price or KBB if explicitly set — don't guess
  const salePrice = saleEntry?.amount || (kbbValue || null);
  const manualSalePrice = vehicle.manualSalePrice || null;
  const effectiveSalePrice = saleEntry?.amount || manualSalePrice || kbbValue || null;
  // CSV Total Cost already includes pack — only add DEALER_PACK for manual entries
  const purchaseCost = purchaseEntry ? ((purchaseEntry.amount || 0) + (vehicle.priceFromCSV ? 0 : DEALER_PACK)) : null;
  const reconCost = entries.filter(e => !["PURCHASE","SALE"].includes(e.category)).reduce((s, e) => s + (e.amount || 0), 0);
  const postSaleCost = entries.filter(e => e.category === "POSTSAL" || e.postSale).reduce((s, e) => s + (e.amount || 0), 0);
  // Only calculate gross if we have both cost and a sale/valuation price
  const projectedGross = (effectiveSalePrice && purchaseCost) ? effectiveSalePrice - purchaseCost : null;
  const currentGross = (effectiveSalePrice && costIn > 0) ? effectiveSalePrice - costIn : null;
  const reconPct = (projectedGross && projectedGross > 0) ? reconCost / projectedGross : 0;
  return {
    costIn, salePrice: effectiveSalePrice, purchaseCost, reconCost, postSaleCost,
    projectedGross, currentGross, reconPct,
    hasCost: !!purchaseCost,
    hasValuation: !!(kbbValue || manualSalePrice || saleEntry),
    overBudget: reconPct > RECON_MAX_PCT && reconCost > 500 && !!projectedGross,
    saleEntry
  };
};

// ─── CLAUDE API ──────────────────────────────────────────────────────────────

const callClaude = async (messages, system, maxTokens = 1200) => {
  const res = await fetch("/api/claude", { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${await getIdToken()}` },
    body: JSON.stringify({
      model: "claude-sonnet-4-6",
      max_tokens: maxTokens,
      system: system || "You are a helpful automotive assistant. Return only valid JSON, no markdown.",
      messages,
    })
  });
  const data = await res.json();
  return data.content?.[0]?.text || "";
};

const callClaudeJSON = async (messages, system, maxTokens) => {
  const raw = await callClaude(messages, system, maxTokens);
  return JSON.parse(raw.replace(/```json|```/g, "").trim());
};

// ─── GMAIL MCP ───────────────────────────────────────────────────────────────

const sendGmailViaMCP = async (to, subject, body) => {
  try {
    const res = await fetch("/api/claude", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Authorization": `Bearer ${await getIdToken()}` },
      body: JSON.stringify({
        model: "claude-sonnet-4-6",
        max_tokens: 500,
        system: "You manage Gmail for Edgewater Auto Center. Use the Gmail MCP tool to send emails.",
        messages: [{ role: "user", content: `Send an email to ${to} with subject "${subject}" and this body:\n\n${body}` }],
        mcp_servers: [{ type: "url", url: "https://gmail.mcp.claude.com/mcp", name: "gmail" }]
      })
    });
    return res.ok;
  } catch { return false; }
};

// ─── UI PRIMITIVES ────────────────────────────────────────────────────────────

const C = BASE_THEME;

const Badge = ({ color, children, small }) => (
  <span style={{ background: color + "20", color, border: `1px solid ${color}40`, borderRadius: 4, padding: small ? "1px 6px" : "3px 8px", fontSize: small ? 10 : 11, fontWeight: 700, whiteSpace: "nowrap", fontFamily: "inherit" }}>{children}</span>
);
const StageBadge = ({ stageId, small }) => { const s = STAGES.find(x => x.id === stageId); return s ? <Badge color={s.color} small={small}>{s.label}</Badge> : null; };

const Btn = ({ onClick, children, variant = "default", size = "md", disabled, full, sx }) => {
  const sz = { sm: { padding: "5px 10px", fontSize: 11 }, md: { padding: "8px 14px", fontSize: 12 }, lg: { padding: "11px 22px", fontSize: 14 }, xl: { padding: "16px 28px", fontSize: 16 } }[size];
  const va = {
    default: { background: C.s3,    color: C.text,  border: `1px solid ${C.border}` },
    amber:   { background: C.amber, color: "#000",  border: "none" },
    red:     { background: C.red,   color: "#fff",  border: "none" },
    green:   { background: C.green, color: "#000",  border: "none" },
    ghost:   { background: "transparent", color: C.textS, border: `1px solid ${C.border}` },
    outline: { background: "transparent", color: C.amber, border: `1px solid ${C.amber}` },
    blue:    { background: C.blue,  color: "#fff",  border: "none" },
    purple:  { background: C.purple,color: "#fff",  border: "none" },
  }[variant];
  return <button onClick={disabled ? undefined : onClick} style={{ ...sz, ...va, borderRadius: 8, cursor: disabled ? "not-allowed" : "pointer", fontWeight: 700, fontFamily: "inherit", opacity: disabled ? 0.45 : 1, display: "inline-flex", alignItems: "center", gap: 6, width: full ? "100%" : undefined, justifyContent: full ? "center" : undefined, transition: "opacity 0.15s", ...sx }}>{children}</button>;
};

const F = ({ label, sub, children }) => (
  <div style={{ marginBottom: 12 }}>
    <div style={{ fontSize: 10, color: C.textM, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: sub ? 2 : 4 }}>{label}</div>
    {sub && <div style={{ fontSize: 9, color: C.textM, marginBottom: 4 }}>{sub}</div>}
    {children}
  </div>
);
const TI = ({ label, sub, value, onChange, placeholder, type = "text", small, rows }) => (
  <F label={label} sub={sub}>
    {rows ? (
      <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={rows}
        style={{ width: "100%", background: C.s2, border: `1px solid ${C.border}`, borderRadius: 6, padding: "8px 12px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none", resize: "vertical", boxSizing: "border-box", lineHeight: 1.6 }} />
    ) : (
      <input type={type} value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
        style={{ width: "100%", background: C.s2, border: `1px solid ${C.border}`, borderRadius: 6, padding: small ? "6px 10px" : "8px 12px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
    )}
  </F>
);
const SI = ({ label, value, onChange, options, small }) => (
  <F label={label}>
    <select value={value} onChange={e => onChange(e.target.value)} style={{ width: "100%", background: C.s2, border: `1px solid ${C.border}`, borderRadius: 6, padding: small ? "6px 10px" : "8px 12px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }}>
      {options.map(o => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
    </select>
  </F>
);
const Modal = ({ title, onClose, children, wide }) => (
  <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.92)", zIndex: 400, display: "flex", alignItems: "flex-start", justifyContent: "center", padding: "40px 16px", overflowY: "auto" }}>
    <div style={{ background: C.surface, border: `1px solid ${C.bLight}`, borderRadius: 12, width: "100%", maxWidth: wide ? 900 : 580 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "14px 20px", borderBottom: `1px solid ${C.border}` }}>
        <div style={{ fontSize: 15, fontWeight: 800, color: C.text, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.06em" }}>{title}</div>
        <button onClick={onClose} style={{ background: "none", border: "none", color: C.textS, cursor: "pointer" }}><X size={16} /></button>
      </div>
      <div style={{ padding: 20 }}>{children}</div>
    </div>
  </div>
);
const KPI = ({ label, value, sub, color, icon: Icon }) => (
  <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderTop: `3px solid ${color}`, borderRadius: 10, padding: "14px 18px", flex: 1, minWidth: 120 }}>
    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
      <div>
        <div style={{ fontSize: 10, color: C.textM, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}>{label}</div>
        <div style={{ fontSize: 26, fontWeight: 800, color, fontFamily: "'Bebas Neue', sans-serif", lineHeight: 1 }}>{value}</div>
        {sub && <div style={{ fontSize: 10, color: C.textS, marginTop: 3 }}>{sub}</div>}
      </div>
      {Icon && <Icon size={18} color={color} opacity={0.5} />}
    </div>
  </div>
);

// ─── CONFIDENCE METER ────────────────────────────────────────────────────────

const ConfidenceMeter = ({ pct }) => {
  const color = pct >= CONVICTION.DIAGNOSIS ? C.green : pct >= CONVICTION.MED ? C.amber : pct >= CONVICTION.LOW ? "#f97316" : C.red;
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
        <span style={{ fontSize: 10, color: C.textM, fontWeight: 700, textTransform: "uppercase" }}>Diagnostic Confidence</span>
        <span style={{ fontSize: 14, fontWeight: 800, color, fontFamily: "'Bebas Neue', sans-serif" }}>{pct}%</span>
      </div>
      <div style={{ height: 6, borderRadius: 3, background: C.border }}>
        <div style={{ height: "100%", borderRadius: 3, width: `${pct}%`, background: color, transition: "width 0.6s, background 0.3s" }} />
      </div>
      {pct >= CONVICTION.DIAGNOSIS && <div style={{ fontSize: 10, color: C.green, marginTop: 3, fontWeight: 700 }}>✓ Confidence threshold met — ready to create repair orders</div>}
      {pct < 98 && pct > 0 && <div style={{ fontSize: 10, color: C.textM, marginTop: 3 }}>Need {98 - pct}% more confidence — Claude will ask follow-up questions</div>}
    </div>
  );
};

// ─── SHARED IMAGE ATTACH + BARCODE HELPERS ───────────────────────────────────

const imageToBase64 = (file) => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = (e) => resolve(e.target.result); // full data URL
  reader.onerror = reject;
  reader.readAsDataURL(file);
});

// Resize image to max 1024px and convert to base64 for API
const resizeAndEncode = (file, maxPx = 1024) => new Promise((resolve) => {
  const img = new Image();
  const url = URL.createObjectURL(file);
  img.onload = () => {
    const scale = Math.min(1, maxPx / Math.max(img.width, img.height));
    const canvas = document.createElement("canvas");
    canvas.width = img.width * scale; canvas.height = img.height * scale;
    canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
    URL.revokeObjectURL(url);
    resolve(canvas.toDataURL("image/jpeg", 0.85).split(",")[1]);
  };
  img.src = url;
});

// Inline image attach button — renders a camera/attach icon + hidden file input
const ImageAttachButton = ({ onImage, label = "📷", style = {} }) => {
  const ref = useRef(null);
  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const b64 = await resizeAndEncode(file);
    const preview = URL.createObjectURL(file);
    onImage({ b64, preview, mediaType: "image/jpeg" });
    e.target.value = "";
  };
  return (
    <>
      <button onClick={() => ref.current?.click()}
        style={{ flexShrink: 0, width: 42, height: 42, borderRadius: 8, border: `1px solid ${C.border}`, background: C.s2, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, ...style }}
        title="Attach photo">
        {label}
      </button>
      <input ref={ref} type="file" accept="image/*" capture="environment" onChange={handleFile} style={{ display: "none" }} />
    </>
  );
};

// Barcode scanner using BarcodeDetector + camera stream
const BarcodeButton = ({ onResult }) => {
  const [scanning, setScanning] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);

  const stop = () => {
    setScanning(false);
    streamRef.current?.getTracks().forEach(t => t.stop());
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
  };

  const scan = async () => {
    if (!("BarcodeDetector" in window)) {
      const code = prompt("Enter barcode / part number manually:");
      if (code) onResult(code.trim());
      return;
    }
    setScanning(true);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; await videoRef.current.play(); }
      const detector = new window.BarcodeDetector({ formats: ["ean_13","ean_8","upc_a","upc_e","code_128","code_39","qr_code"] });
      const tick = async () => {
        if (!scanning && !streamRef.current) return;
        try {
          const codes = await detector.detect(videoRef.current);
          if (codes.length) { stop(); onResult(codes[0].rawValue); return; }
        } catch {}
        rafRef.current = requestAnimationFrame(tick);
      };
      tick();
    } catch { stop(); }
  };

  if (scanning) return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.9)", zIndex: 9999, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 12 }}>
      <video ref={videoRef} style={{ width: "90%", maxWidth: 400, borderRadius: 12, border: "2px solid #f59e0b" }} playsInline />
      <div style={{ color: "#f59e0b", fontSize: 13, fontWeight: 700 }}>Point camera at barcode</div>
      <button onClick={stop} style={{ background: "#dc2626", border: "none", color: "#fff", borderRadius: 8, padding: "8px 20px", fontSize: 12, cursor: "pointer", fontFamily: "inherit" }}>Cancel</button>
    </div>
  );

  return (
    <button onClick={scan}
      style={{ flexShrink: 0, width: 42, height: 42, borderRadius: 8, border: `1px solid ${C.border}`, background: C.s2, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}
      title="Scan barcode">
      <Barcode size={18} color={C.amber} />
    </button>
  );
};

// ─── VIN SCANNER ─────────────────────────────────────────────────────────────

const VINScanner = ({ onScan, onClose }) => {
  const [mode, setMode] = useState("camera");
  const [manualVIN, setManualVIN] = useState("");
  const [listening, setListening] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState("");
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const recRef = useRef(null);

  const startCamera = async () => {
    setError("");
    // Pre-flight: bail early if BarcodeDetector isn't available — don't even ask for the camera.
    if (!("BarcodeDetector" in window)) {
      setError("Live barcode scanning not available on this device. Use 'Take Photo' below to photograph the VIN barcode, or type it manually.");
      setMode("photo_fallback");
      return;
    }
    try {
      // Lower resolution = faster per-frame detection. 960x540 still resolves PDF417 VIN barcodes.
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 960 },
          height: { ideal: 540 },
          frameRate: { ideal: 30 }
        }
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        videoRef.current.setAttribute("playsinline", "true");
        videoRef.current.setAttribute("muted", "true");
        videoRef.current.muted = true;
        // Start scanning the moment the video has data — no fixed 500ms wait.
        videoRef.current.onloadeddata = () => scanLoop();
        await videoRef.current.play().catch(() => {});
      }
      setScanning(true);
    } catch (err) {
      console.warn("Camera error:", err);
      setError("Camera not available — please check permissions in Settings. Or type the VIN below.");
      setMode("manual");
    }
  };

  // VIN-only formats. Code 39 = front-of-car/door-jamb stickers. PDF417 = newer VIN barcodes.
  // Cutting from 5 formats to 2 roughly halves per-frame detection cost.
  const VIN_BARCODE_FORMATS = ["code_39", "pdf417"];

  const scanLoop = async () => {
    if (!("BarcodeDetector" in window)) {
      stopCamera();
      setError("Live barcode scanning not available on this device. Use 'Take Photo' below to photograph the VIN barcode, or type it manually.");
      setMode("photo_fallback");
      return;
    }
    const detector = new window.BarcodeDetector({ formats: VIN_BARCODE_FORMATS });
    const looksLikeVIN = (s) => /^[A-HJ-NPR-Z0-9]{11,17}$/.test(s);
    let cancelled = false;

    // Use rVFC if available (Chrome/Edge/Safari) — runs once per video frame, no setTimeout overhead.
    const useFrameCallback = typeof videoRef.current?.requestVideoFrameCallback === "function";

    const tryDetect = async () => {
      if (cancelled || !videoRef.current || !streamRef.current) return;
      if (videoRef.current.readyState < 2) return scheduleNext();
      try {
        const barcodes = await detector.detect(videoRef.current);
        for (const b of barcodes) {
          const vin = (b.rawValue || "").trim().toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, "");
          if (looksLikeVIN(vin)) { cancelled = true; stopCamera(); onScan(vin); return; }
        }
      } catch {}
      scheduleNext();
    };
    const scheduleNext = () => {
      if (cancelled || !streamRef.current) return;
      if (useFrameCallback) {
        videoRef.current.requestVideoFrameCallback(() => tryDetect());
      } else {
        // 60ms = ~16fps polling — much faster than the old 200ms (5fps)
        setTimeout(tryDetect, 60);
      }
    };
    // Kick off immediately — no leading delay
    tryDetect();
  };

  const scanVINFromPhoto = async (file) => {
    setError(""); setMode("photo_fallback");
    const reader = new FileReader();
    reader.onload = async (e) => {
      const base64 = e.target.result.split(",")[1];
      try {
        const res = await fetch("/api/claude", { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${await getIdToken()}` },
          body: JSON.stringify({ model: "claude-sonnet-4-6", max_tokens: 100,
            messages: [{ role: "user", content: [
              { type: "image", source: { type: "base64", media_type: file.type || "image/jpeg", data: base64 } },
              { type: "text", text: "Read the VIN barcode or VIN number from this image. Return ONLY the VIN, nothing else. If you cannot read it, return ERROR." }
            ]}] })
        });
        const data = await res.json();
        if (data?.error?.message) {
          const apiDown = /usage limit|rate limit|overloaded|credit/i.test(data.error.message);
          setError(apiDown
            ? "AI photo reading is temporarily unavailable (API usage limit reached). Please type the VIN manually below."
            : "Photo scan failed: " + data.error.message);
          return;
        }
        const result = (data.content?.[0]?.text || "").trim().toUpperCase().replace(/[^A-HJ-NPR-Z0-9]/g, "");
        if (result.length >= 10 && result !== "ERROR") {
          onScan(result);
        } else {
          setError("Could not read VIN from photo. Try a clearer photo or type it manually below.");
        }
      } catch { setError("Photo scan failed — please type the VIN manually."); }
    };
    reader.readAsDataURL(file);
  };

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach(t => t.stop());
    streamRef.current = null;
    setScanning(false);
  };

  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setError("Voice not available."); return; }
    const r = new SR(); r.continuous = false; r.interimResults = false;
    r.onresult = (e) => { const t = e.results[0][0].transcript.replace(/\s/g, "").toUpperCase(); setManualVIN(t); setListening(false); };
    r.onend = () => setListening(false);
    r.start(); recRef.current = r; setListening(true);
  };

  useEffect(() => { if (mode === "camera") startCamera(); return () => stopCamera(); }, [mode]);

  return (
    <div style={{ textAlign: "center" }}>
      <div style={{ display: "flex", gap: 8, justifyContent: "center", marginBottom: 16 }}>
        {["camera","photo_fallback","manual"].map(m => <button key={m} onClick={() => { stopCamera(); setMode(m); }} style={{ padding: "6px 14px", borderRadius: 20, border: `1px solid ${mode === m ? C.amber : C.border}`, background: mode === m ? C.amber + "20" : "transparent", color: mode === m ? C.amber : C.textS, fontWeight: 700, fontFamily: "inherit", fontSize: 11, cursor: "pointer" }}>{m === "camera" ? "📷 Live Scan" : m === "photo_fallback" ? "🖼 Photo" : "✏️ Type VIN"}</button>)}
      </div>

      {mode === "camera" && (
        <div>
          <div style={{ background: "#000", borderRadius: 10, overflow: "hidden", position: "relative", maxWidth: 400, margin: "0 auto 12px" }}>
            <video ref={videoRef} autoPlay playsInline muted style={{ width: "100%", display: "block", borderRadius: 10 }} />
            {scanning && (
              <div style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none" }}>
                <div style={{ width: "70%", height: 3, background: C.amber, opacity: 0.8, animation: "scanLine 2s ease-in-out infinite" }} />
              </div>
            )}
          </div>
          <div style={{ fontSize: 11, color: C.textS, marginBottom: 12 }}>Point camera at the VIN barcode (usually on the door jamb or windshield)</div>
          {error && <div style={{ fontSize: 11, color: C.red, marginBottom: 10 }}>{error}</div>}
        </div>
      )}

      {mode === "photo_fallback" && (() => {
        const photoInputRef = { current: null };
        return (
          <div style={{ maxWidth: 400, margin: "0 auto", textAlign: "center" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#000", marginBottom: 6 }}>📷 Photo Scan VIN</div>
            <div style={{ fontSize: 11, color: C.textM, marginBottom: 14 }}>Take a photo of the VIN barcode or the VIN number on the door jamb, windshield, or dashboard. AI will read it.</div>
            {error && <div style={{ fontSize: 11, color: C.red, marginBottom: 10 }}>{error}</div>}
            <label style={{ display: "block", background: C.amber, borderRadius: 9, padding: "12px 20px", cursor: "pointer", fontSize: 13, fontWeight: 800, color: "#000", marginBottom: 8 }}>
              📷 Take Photo of VIN
              <input type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={e => e.target.files?.[0] && scanVINFromPhoto(e.target.files[0])} />
            </label>
            <label style={{ display: "block", background: C.s2, border: `1px solid ${C.border}`, borderRadius: 9, padding: "10px 20px", cursor: "pointer", fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 12 }}>
              🖼 Upload from Gallery
              <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => e.target.files?.[0] && scanVINFromPhoto(e.target.files[0])} />
            </label>
            <button onClick={() => setMode("manual")} style={{ background: "none", border: "none", color: C.blue, cursor: "pointer", fontSize: 11, fontFamily: "inherit", textDecoration: "underline" }}>
              Or type the VIN manually →
            </button>
          </div>
        );
      })()}

      {mode === "manual" && (
        <div style={{ maxWidth: 400, margin: "0 auto" }}>
          <div style={{ marginBottom: 12 }}>
            <button onClick={listening ? () => { recRef.current?.stop(); setListening(false); } : startVoice}
              style={{ width: 56, height: 56, borderRadius: "50%", border: `3px solid ${listening ? C.red : C.amber}`, background: listening ? C.red + "20" : C.amber + "15", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: listening ? C.red : C.amber, margin: "0 auto 10px" }}>
              {listening ? <MicOff size={22} /> : <><img src="/images/btn-voice-text.jpg" alt="" style={{width:20,height:20,objectFit:"contain",flexShrink:0}} onError={e=>e.target.style.display="none"} /></>}
            </button>
            {listening && <div style={{ fontSize: 11, color: C.red, marginBottom: 8 }}>🔴 Listening — say the VIN clearly…</div>}
          </div>
          <TI label="VIN Number" value={manualVIN} onChange={setManualVIN} placeholder="Enter or speak 17-character VIN" />
          {error && <div style={{ fontSize: 11, color: C.red, marginBottom: 8 }}>{error}</div>}
          <Btn variant="amber" full onClick={() => { if (manualVIN.trim().length >= 10) onScan(manualVIN.trim().toUpperCase()); else setError("Please enter a valid VIN (minimum 10 characters)."); }} disabled={manualVIN.length < 10}>
            <CheckCircle size={14} /> Use This VIN
          </Btn>
        </div>
      )}
      <div style={{ marginTop: 14 }}><Btn variant="ghost" size="sm" onClick={onClose}>Cancel</Btn></div>
    </div>
  );
};

// ─── SHOP FOREMAN PERSONA ────────────────────────────────────────────────────

const FOREMAN = {
  name: "Hank",
  title: "Shop Foreman",
  tagline: "35 years under the hood",
  avatar: "/images/Hank-Headshot.jpg",
  avatarStates: {
    neutral:   "/images/Hank-Headshot.jpg",                  // calm, attentive — session start
    thinking:  "/images/hank-is-thinking-close-up.jpg",      // working through possibilities
    concerned: "/images/hank-wiping-furrowed-brow.jpg",      // stressed, unclear symptoms, tough call
    confident: "/images/hank-inspecting-vehicle.jpg",        // zeroing in, knows what it is
    alarmed:   "/images/Hank-delivers-bad-news-close-up.jpg",// safety issue or expensive diagnosis
    satisfied: "/images/Hank-is-happy-close-up.jpg",         // 98%+ confidence, diagnosis locked
  },
  // Hank's personality traits fed into system prompt
  voice: "You are Hank, Edgewater Auto Center's virtual Shop Foreman. You have 35 years of hands-on experience with all makes and models — domestic trucks, Japanese imports, European cars, everything. You speak like a real mechanic: direct, no-nonsense, occasionally dry humor, but always precise. You use real shop terminology. You never guess — you diagnose. When you're not sure, you say so and ask another question. Never use gendered pronouns — refer to all staff by name only.",
};


// ─── CLAUDE API HELPER (Julia) ───────────────────────────────────────────────

// ─── GROK (xAI) API — best for real-time web search ─────────────────────────
const callGrok = async (messages, systemPrompt, maxTokens = 1500) => {
  const apiKey = process.env.REACT_APP_GROK_KEY;
  if (!apiKey) throw new Error("REACT_APP_GROK_KEY not set in .env");
  const body = {
    model: "grok-3",
    max_tokens: maxTokens,
    messages: [
      ...(systemPrompt ? [{ role: "system", content: systemPrompt }] : []),
      ...messages,
    ],
  };
  const res = await fetch("/api/grok", {
    method: "POST",
    headers: { "Content-Type": "application/json", "Authorization": `Bearer ${await getIdToken()}` },
    body: JSON.stringify(body),
  });
  if (!res.ok) {
    const errText = await res.text();
    throw new Error(`Grok error: ${res.status} — ${errText.slice(0, 100)}`);
  }
  const data = await res.json();
  return data.choices?.[0]?.message?.content || "";
};

const callGrokJSON = async (messages, systemPrompt, maxTokens = 1500) => {
  const raw = await callGrok(messages, systemPrompt, maxTokens);
  const start = raw.indexOf("{"); const end = raw.lastIndexOf("}");
  if (start >= 0 && end > start) return JSON.parse(raw.slice(start, end + 1));
  throw new Error("Grok returned non-JSON");
};

const callGemini = async (messages, systemPrompt, maxTokens = 800) => {
  const apiKey = process.env.REACT_APP_ANTHROPIC_KEY;
  if (!apiKey) throw new Error("ANTHROPIC_KEY not set in .env");

  // Convert messages — handle both plain strings and content arrays (with images/video)
  const claudeMessages = messages.map(m => {
    if (Array.isArray(m.content)) {
      const parts = m.content.map(part => {
        if (part.type === "image") {
          return {
            type: "image",
            source: { type: "base64", media_type: part.image.mimeType, data: part.image.data }
          };
        }
        if (part.type === "video") {
          // Claude does not support inline video — send descriptive text prompt instead
          return { type: "text", text: part.text || "A video of the vehicle was provided. Please ask the user to describe what they see or send individual photos for a thorough inspection." };
        }
        return { type: "text", text: part.text || "" };
      });
      return { role: m.role, content: parts };
    }
    return { role: m.role, content: m.content || "" };
  });

  const res = await fetch("/api/claude", { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${await getIdToken()}` },
    body: JSON.stringify({
      model: "claude-sonnet-4-6",
      max_tokens: maxTokens,
      system: systemPrompt,
      messages: claudeMessages
    })
  });

  if (!res.ok) {
    const errText = await res.text();
    throw new Error(`Claude error: ${res.status} — ${errText.slice(0, 200)}`);
  }
  const data = await res.json();
  const text = data.content?.[0]?.text;
  if (!text) throw new Error("No response from Claude: " + JSON.stringify(data));
  return text;
};

// ─── GEMINI TTS ──────────────────────────────────────────────────────────────
// Uses Gemini 2.5 Flash TTS API — returns PCM L16 audio, converted to WAV for playback
// Julia voice: "Kore" (warm, professional female)
// Hank voice: "Charon" (deep, direct male)

let currentAudio = null; // track active audio so we can cancel

const speakText = async (text, voiceGender = "female") => {
  const apiKey = process.env.REACT_APP_GEMINI_KEY;

  // Stop any currently playing audio
  if (currentAudio) { currentAudio.pause(); currentAudio = null; }

  // If no Gemini key, fall back to Web Speech API
  if (!apiKey) {
    if (!window.speechSynthesis) return;
    window.speechSynthesis.cancel();
    const utterance = new SpeechSynthesisUtterance(text);
    const voices = window.speechSynthesis.getVoices();
    const preferred = voices.find(v =>
      voiceGender === "female"
        ? /samantha|karen|moira|victoria|zira|female|woman/i.test(v.name)
        : /daniel|alex|fred|tom|male|man/i.test(v.name)
    ) || voices[0];
    if (preferred) utterance.voice = preferred;
    utterance.rate = 0.92;
    utterance.pitch = voiceGender === "female" ? 1.08 : 0.88;
    window.speechSynthesis.speak(utterance);
    return;
  }

  // Gemini TTS
  const voiceName = voiceGender === "female" ? "Kore" : "Charon";
  const stylePrompt = voiceGender === "female"
    ? "Speak in a warm, professional, friendly tone — like a knowledgeable automotive specialist who is helpful and encouraging."
    : "Speak in a direct, no-nonsense mechanic's voice — experienced, confident, occasionally dry, but always precise.";

  try {
    const res = await fetch(
      "/api/gemini",
      {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${await getIdToken()}` },
        body: JSON.stringify({
          model: "gemini-2.5-pro-preview-tts",
          body: {
          contents: [{ parts: [{ text: `${stylePrompt}

${text}` }] }],
          generationConfig: {
            responseModalities: ["AUDIO"],
            speechConfig: {
              voiceConfig: { prebuiltVoiceConfig: { voiceName } }
            }
          }
          }
        })
      }
    );
    const data = await res.json();
    const audioData = data?.candidates?.[0]?.content?.parts?.[0]?.inlineData?.data;
    if (!audioData) return;

    // Convert PCM L16 (24kHz mono) to WAV and play
    const pcmBytes = Uint8Array.from(atob(audioData), c => c.charCodeAt(0));
    const wav = pcmToWav(pcmBytes, 24000, 1, 16);
    const blob = new Blob([wav], { type: "audio/wav" });
    const url = URL.createObjectURL(blob);
    const audio = new Audio(url);
    currentAudio = audio;
    audio.onended = () => { URL.revokeObjectURL(url); currentAudio = null; };
    audio.play();
  } catch (e) {
    // Silent fallback — don't break the UI if TTS fails
    console.warn("Gemini TTS failed:", e.message);
  }
};

// Convert raw PCM L16 bytes to WAV buffer
const pcmToWav = (pcmData, sampleRate, numChannels, bitsPerSample) => {
  const byteRate = sampleRate * numChannels * bitsPerSample / 8;
  const blockAlign = numChannels * bitsPerSample / 8;
  const dataSize = pcmData.length;
  const buffer = new ArrayBuffer(44 + dataSize);
  const view = new DataView(buffer);
  const write = (offset, str) => { for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i)); };
  write(0, "RIFF");
  view.setUint32(4, 36 + dataSize, true);
  write(8, "WAVE");
  write(12, "fmt ");
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);        // PCM format
  view.setUint16(22, numChannels, true);
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, byteRate, true);
  view.setUint16(32, blockAlign, true);
  view.setUint16(34, bitsPerSample, true);
  write(36, "data");
  view.setUint32(40, dataSize, true);
  new Uint8Array(buffer, 44).set(pcmData);
  return buffer;
};

const stopSpeaking = () => {
  if (currentAudio) { currentAudio.pause(); currentAudio = null; }
  if (window.speechSynthesis) window.speechSynthesis.cancel();
};

// ─── JULIA PERSONA ────────────────────────────────────────────────────────────

const JULIA = {
  name: "Julia",
  title: "Vehicle Appraisal & Inspection Specialist",
  tagline: "Thorough, friendly, detail-oriented",
  avatar: "/images/Julia.jpg",
  voice: "female",
};

// All data points Julia must cover — used for progress tracking
const JULIA_DATA_POINTS = [
  // EXTERIOR (6) — IDs must match exactly what Julia returns in coveredPoints
  { id: "ext_paint",           phase: "EXTERIOR", label: "Paint condition",            required: true  },
  { id: "ext_body",            phase: "EXTERIOR", label: "Body panels / dents",        required: true  },
  { id: "ext_glass",           phase: "EXTERIOR", label: "Windshield / glass",         required: true  },
  { id: "ext_lights",          phase: "EXTERIOR", label: "Lights / signals",           required: true  },
  { id: "ext_trim",            phase: "EXTERIOR", label: "Trim & moldings",            required: false },
  { id: "ext_mirrors",         phase: "EXTERIOR", label: "Mirrors",                    required: false },
  // TIRES (5)
  { id: "tire_fl",             phase: "TIRES",    label: "Front left tread",           required: true  },
  { id: "tire_fr",             phase: "TIRES",    label: "Front right tread",          required: true  },
  { id: "tire_rl",             phase: "TIRES",    label: "Rear left tread",            required: true  },
  { id: "tire_rr",             phase: "TIRES",    label: "Rear right tread",           required: true  },
  { id: "tire_sidewalls",      phase: "TIRES",    label: "Sidewall condition",         required: false },
  // UNDERHOOD (4)
  { id: "underhood_fluids",    phase: "UNDERHOOD",label: "Fluid levels",               required: true  },
  { id: "underhood_leaks",     phase: "UNDERHOOD",label: "Visible leaks",              required: true  },
  { id: "underhood_belts_hoses",phase:"UNDERHOOD",label: "Belts / hoses",              required: false },
  { id: "underhood_battery",   phase: "UNDERHOOD",label: "Battery condition",          required: false },
  // INTERIOR (5)
  { id: "int_seats",           phase: "INTERIOR", label: "Seat condition",             required: true  },
  { id: "int_carpet",          phase: "INTERIOR", label: "Carpet / floor mats",        required: true  },
  { id: "int_dash",            phase: "INTERIOR", label: "Dash / controls",            required: true  },
  { id: "int_odors",           phase: "INTERIOR", label: "Odors",                      required: true  },
  { id: "int_headliner",       phase: "INTERIOR", label: "Headliner",                  required: false },
  // TEST DRIVE (6)
  { id: "drive_warning_lights",phase: "TEST_DRIVE_RESULTS", label: "Cold start / warning lights", required: true  },
  { id: "drive_engine_noise",  phase: "TEST_DRIVE_RESULTS", label: "Engine noise / behavior",     required: true  },
  { id: "drive_brakes",        phase: "TEST_DRIVE_RESULTS", label: "Brake feel / pulsing",        required: true  },
  { id: "drive_steering",      phase: "TEST_DRIVE_RESULTS", label: "Steering / pull",             required: true  },
  { id: "drive_transmission",  phase: "TEST_DRIVE_RESULTS", label: "Transmission shifts",         required: true  },
  { id: "drive_ac_heat",        phase: "TEST_DRIVE_RESULTS", label: "AC / heat",                   required: false },
  { id: "fuel_level",           phase: "UNDERHOOD",          label: "Fuel level",                  required: true  },
];

const buildJuliaSystem = (vehicle) => `You are Julia, Edgewater Auto Center's Vehicle Appraisal and Inspection Specialist in Salem, Oregon. You are conducting a full reconditioning inspection of a ${vehicle.year || ""} ${vehicle.make || ""} ${vehicle.model || ""}${vehicle.trim ? " " + vehicle.trim : ""}, Stock #${vehicle.stockNumber || "unknown"}, with ${vehicle.mileage ? parseInt(vehicle.mileage).toLocaleString() + " miles" : "unknown mileage"}.

Your PURPOSE: Identify every item that needs reconditioning and route each task to the correct party:
- MECHANICAL issues → Hank (AI diagnostic) → James (mechanic) — engine, transmission, brakes, suspension, electrical
- DETAIL tasks → Sky — cleaning, odors, carpet stains, headliner, interior cosmetics
- BODY REPAIR → Vendor (outsourced) — dents, paint damage, rust, collision damage, glass replacement
- COSMETIC → Sky (cleaning-related) or James (mechanical-adjacent like wheel/light/sensor cosmetics)
- Very quick tasks <20min, no lift needed (bulbs, wipers, fluids, filters) → Robert
- All other mechanical → James
- Angela and Dmitriy are managers and do NOT perform repair/detail tasks

Your personality: professional, thorough, warm and encouraging. Cover every data point systematically.

IMPORTANT: Never use gendered pronouns (he/she/him/her/his/hers). Always refer to staff by name only. Sky is non-binary — always use Sky's name, never pronouns.

REQUIRED DATA POINTS — cover each phase with ONE broad question. Only dig deeper if an issue is mentioned.
EXTERIOR: overall paint/body condition, glass okay?, lights working?, anything else notable?
TIRES: overall tire condition — good/worn/needs replacement? (one question covers all 4)
UNDERHOOD: fluids look okay, any visible leaks, anything concerning?
FUEL LEVEL: always check and report the fuel level. Every car must be at a quarter tank (¼) or better before going to the lot. If it's low, flag it as a Robert task to fill up.
INTERIOR: seats okay?, carpet/floor clean enough?, dash/controls all working?, any odors?, headliner good?
TEST DRIVE: how did it drive overall? any warning lights, unusual noises, brake feel, steering, transmission?

RULES:
- Ask ONE broad question per phase — do NOT break down into individual items unless something is wrong
- "Tires look good" covers all four tires. "Seats are fine" covers all seats. Accept it and move on.
- Only ask follow-up questions when the inspector flags an issue — then get specific about THAT issue
- Never push for exact measurements or specs unless there is a problem
- "Everything looks good" is a complete answer for any phase — log it and move to the next
- Parse every specific issue mentioned into a work order
- Keep it conversational and fast — the goal is 5-6 exchanges total, not 20
- Test drive prompt: "Alright — take it out for a quick drive. Note anything you feel, hear, or smell. I'll be here when you're back."
- After test drive results, set done=true

ALWAYS respond in valid JSON only — no markdown, no surrounding text:
{
  "message": "Julia's spoken response",
  "phase": "EXTERIOR|TIRES|UNDERHOOD|INTERIOR|TEST_DRIVE_PREP|TEST_DRIVE_RESULTS|COMPLETE",
  "coveredPoints": ["ext_paint","ext_body","ext_glass","ext_lights","ext_trim","ext_mirrors","tire_fl","tire_fr","tire_rl","tire_rr","tire_sidewalls","underhood_fluids","underhood_leaks","underhood_belts_hoses","underhood_battery","int_seats","int_carpet","int_dash","int_odors","int_headliner","drive_warning_lights","drive_engine_noise","drive_brakes","drive_steering","drive_transmission","drive_ac_heat"],
  NOTE: Only include IDs that were actually discussed. Use EXACTLY these IDs — do not invent new ones.
  "issuesFound": [
    {"area": "EXTERIOR|INTERIOR|MECHANICAL|TIRES|SAFETY", "description": "specific issue", "severity": "HIGH|MEDIUM|LOW", "woCategory": "MECHANICAL|DETAIL|VENDOR|COSMETIC", "assignTo": "James|Sky|Vendor|In-house"}
  ],
  "done": false,
  "inspectionSummary": null
}

When done=true, also include inspectionSummary:
{
  "overallCondition": "EXCELLENT|GOOD|FAIR|POOR",
  "exteriorNotes": "...", "interiorNotes": "...", "mechanicalNotes": "...", "tireNotes": "...",
  "allIssues": [...],
  "needsMechanicalDiagnosis": true/false,
  "recommendedWorkOrders": [
    {"category":"MECHANICAL|DETAIL|VENDOR|COSMETIC","description":"specific task","priority":"HIGH|MEDIUM|LOW","assignTo":"James|Sky|Vendor|In-house","rationale":"why this task and why this person"}
  ]
}`;

// Determine Hank's avatar state based on confidence and conversation
const getHankState = (confidence, messages) => {
  if (confidence >= CONVICTION.DIAGNOSIS) return "satisfied";
  if (confidence >= CONVICTION.MED) return "confident";
  if (confidence >= CONVICTION.LOW) return "thinking";
  const lastMsg = messages[messages.length - 1]?.content?.toLowerCase() || "";
  if (/safety|brake|steering|fire|smoke|leak/.test(lastMsg)) return "alarmed";
  if (/not sure|could be|multiple|unclear/.test(lastMsg)) return "concerned";
  return "neutral";
};

const buildDiagSystem = (vehicle) => {
  const juliaData = vehicle.juliaInspectionSummary;
  const juliaMechNotes = juliaData?.mechanicalNotes || "";
  const juliaIssues = (juliaData?.allIssues || []).filter(i => i.area === "MECHANICAL").map(i => i.description).join("; ");
  const juliaContext = juliaData ? `
JULIA'S PRE-INSPECTION FINDINGS (already gathered — do NOT re-ask these):
- Overall condition: ${juliaData.overallCondition}
- Exterior: ${juliaData.exteriorNotes || "no notes"}
- Interior: ${juliaData.interiorNotes || "no notes"}
- Mechanical observations from test drive: ${juliaMechNotes || "none noted"}
- Specific mechanical issues found: ${juliaIssues || "none"}
- Tire notes: ${juliaData.tireNotes || "none"}

Start your diagnosis from this data. Do NOT ask about things Julia already covered. Jump straight to diagnostic questions that narrow the mechanical issues found.` : "";

  const noVehicle = !vehicle.year && !vehicle.make && !vehicle.model;
  const inventoryList = vehicle._inventoryList || "";

  return `${FOREMAN.voice}

${noVehicle
    ? `NO VEHICLE IDENTIFIED YET. The user will tell you in this conversation which vehicle to diagnose. They may say a year/make/model, a stock number, a description, or that it's not in inventory. Listen for whatever they give you and extract: year, make, model, trim, mileage, transmission, drivetrain. If they give a stock number, match it from the inventory list below.

${inventoryList ? "CURRENT INVENTORY:\n" + inventoryList : ""}

Once you know the vehicle, confirm it back naturally (e.g. "Got it — 2016 Nissan Murano CVT. Let's dig in.") and start diagnosing. Do NOT ask for a VIN. Do NOT require a stock number. Accept any description.`
    : `You are diagnosing a ${vehicle.year || ""} ${vehicle.make || ""} ${vehicle.model || ""}${vehicle.trim ? " "+vehicle.trim : ""}${vehicle.mileage ? " with "+parseInt(vehicle.mileage).toLocaleString()+" miles" : ""}${vehicle.transmission ? ", "+vehicle.transmission : ""}${vehicle.drivetrain ? ", "+vehicle.drivetrain : ""} for Edgewater Auto Center in Salem, OR.${vehicle.isGuest ? " Guest vehicle — not in inventory. No VIN or stock number needed." : (vehicle.stockNumber && vehicle.stockNumber !== "GUEST" ? " Stock #"+vehicle.stockNumber+"." : " Guest vehicle.")}

VEHICLE CONTEXT:
- ${vehicle.year} ${vehicle.make} ${vehicle.model}${vehicle.trim ? " "+vehicle.trim : ""}
- Mileage: ${vehicle.mileage ? parseInt(vehicle.mileage).toLocaleString()+" miles" : "unknown — ask first"}
- Transmission: ${vehicle.transmission || "unknown"}
- Drivetrain: ${vehicle.drivetrain || "unknown"}
`}
YOUR FIRST MESSAGE:
${noVehicle
    ? "Ask them what vehicle they want to diagnose — keep it short and natural, like a real mechanic."
    : `1. Acknowledge the ${vehicle.year} ${vehicle.make} ${vehicle.model} by name
2. ${!vehicle.mileage ? "Ask mileage first" : "Confirm mileage"}
3. ${!vehicle.transmission ? "Ask transmission type if relevant to symptoms" : "Note the "+vehicle.transmission}
4. Ask what the driver/inspector observed — symptoms, noises, feel, smells`}

HANK'S RULES:
- Ask ONE focused question per turn — never stack questions
- Use real shop talk: "Is it pulling under hard braking?" not "Does it feel weird?"
- Each question must meaningfully narrow the diagnosis
- State your working theory briefly before asking the next question
- Safety issues get called out immediately and bluntly
- Use correct component names: "lower control arm bushing" not "that part under the front"
- Be honest when symptoms point to multiple causes — say so
- Once confidence >= ${CONVICTION.DIAGNOSIS}, stop asking and lay out the full diagnosis

ALWAYS respond in JSON only:
{"message":"Hank's response in his natural voice","confidence":0-100,"done":false,"moodState":"neutral|thinking|concerned|confident|alarmed|satisfied","diagnosis":null}

When done=true, include full diagnosis:
{"summary":"plain-English what is wrong and why","rootCauses":[],"workOrders":[{"category":"MECHANICAL","description":"","priority":"HIGH|MEDIUM|LOW","estimatedHours":1,"estimatedPartsCost":0,"rationale":"what inspector described that points to this","vendorType":null}],"advertisingNotes":[],"safetyFlags":[]}` + juliaContext;
};

const DIAG_SYSTEM = buildDiagSystem({ year: "", make: "", model: "" }); // fallback — always use buildDiagSystem(vehicle)`;



// ─── JULIA INSPECTION CHAT ────────────────────────────────────────────────────

const JuliaInspectionChat = ({ vehicle, onInspectionComplete, onClose, updateVehicle }) => {
  const [messages, setMessages] = useState([]);
  const [apiMessages, setApiMessages] = useState([]);
  const [input, setInput] = useState("");
  const [listening, setListening] = useState(false);
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState("EXTERIOR");
  const [allIssues, setAllIssues] = useState([]);
  const [coveredPoints, setCoveredPoints] = useState([]);
  const [done, setDone] = useState(false);
  const [summary, setSummary] = useState(null);
  const [ttsEnabled, setTtsEnabled] = useState(false);
  const [juliaImg, setJuliaImg] = useState(null);
  const [juliaVideo, setJuliaVideo] = useState(null);
  const [lastInput, setLastInput] = useState("");
  const [sendError, setSendError] = useState(false);
  const bottomRef = useRef(null);
  const recRef = useRef(null);

  const [juliaImgErr, setJuliaImgErr] = useState(false);
  const JuliaAvatar = ({ size = 44 }) => {
    return !juliaImgErr ? (
      <img src="/images/Julia.jpg" alt="Julia" onError={() => setJuliaImgErr(true)}
        style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", objectPosition: "top center", border: "3px solid #a78bfa", boxShadow: "0 0 12px #a78bfa50", flexShrink: 0 }} />
    ) : (
      <div style={{ width: size, height: size, borderRadius: "50%", background: "linear-gradient(135deg, #7c3aed, #a78bfa)", border: "3px solid #a78bfa", display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.38, flexShrink: 0 }}>👩‍💼</div>
    );
  };

  // Data point progress
  const totalRequired = JULIA_DATA_POINTS.filter(p => p.required).length;
  const coveredRequired = JULIA_DATA_POINTS.filter(p => p.required && coveredPoints.includes(p.id)).length;
  const totalAll = JULIA_DATA_POINTS.length;
  const coveredAll = coveredPoints.length;
  const progressPct = Math.round((coveredAll / totalAll) * 100);
  const missingRequired = JULIA_DATA_POINTS.filter(p => p.required && !coveredPoints.includes(p.id));
  const isComplete = coveredRequired === totalRequired;

  const openingFired = useRef(false);
  useEffect(() => {
    if (openingFired.current) return;
    openingFired.current = true;
    // Resume from paused state if available
    if (vehicle._juliaPaused) {
      const saved = vehicle._juliaPaused;
      if (saved.messages?.length) setMessages(saved.messages);
      if (saved.apiMessages?.length) setApiMessages(saved.apiMessages);
      if (saved.phase) setPhase(saved.phase);
      if (saved.coveredPoints) setCoveredPoints(saved.coveredPoints);
      if (saved.issuesFound) setAllIssues(saved.issuesFound);
      setMessages(m => [...m, { role: "assistant", content: `Welcome back! I'm picking up where we left off on the ${vehicle.year} ${vehicle.make} ${vehicle.model}. What would you like to cover next?` }]);
      return;
    }
    const opening = `Hi! I'm Julia, Edgewater's inspection specialist. Let's walk through this ${vehicle.year} ${vehicle.make} ${vehicle.model} together. Start by walking around the outside — what's the overall condition of the paint and body? Any dents, scratches, rust, or damage you can see?`;
    setMessages([{ role: "assistant", content: opening }]);
    // TTS is opt-in — user must enable it manually
  }, []);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setInput("Voice not available — please type."); return; }
    const r = new SR(); r.continuous = false; r.interimResults = false;
    r.onresult = (e) => { setInput(prev => prev + e.results[0][0].transcript + " "); setListening(false); };
    r.onend = () => setListening(false);
    r.start(); recRef.current = r; setListening(true);
  };

  const send = async () => {
    if ((!input.trim() && !juliaImg && !juliaVideo) || loading) return;
    const imgForApi = juliaImg;
    const vidForApi = juliaVideo;
    if (juliaImg) setJuliaImg(null);
    if (juliaVideo) setJuliaVideo(null);

    const displayText = input.trim() || (imgForApi ? "📷 [Photo attached]" : vidForApi ? "🎥 [Video attached]" : "");
    setLastInput(input.trim());
    setSendError(false);
    setMessages(prev => [...prev, { role: "user", content: displayText }]);
    setInput(""); setLoading(true);

    // Build Claude message format
    let geminiMsgs;
    if (imgForApi) {
      // Image: use inlineData format that callGemini expects
      geminiMsgs = [
        ...apiMessages,
        { role: "user", content: [
          { type: "image", image: { mimeType: imgForApi.mediaType, data: imgForApi.b64 } },
          { type: "text", text: input.trim() || "Analyze this photo of the vehicle. List every visible condition issue — paint problems, dents, scratches, rust, glass, interior wear, tire condition. Be thorough and specific." }
        ]}
      ];
    } else if (vidForApi) {
      // Video: use Gemini File API via inline base64 (works for short clips <20MB)
      geminiMsgs = [
        ...apiMessages,
        { role: "user", content: [
          { type: "video", video: { mimeType: vidForApi.mediaType, data: vidForApi.b64 } },
          { type: "text", text: input.trim() || "Watch this video of the vehicle. Identify every visible condition issue — body damage, paint problems, mechanical concerns, anything relevant to reconditioning." }
        ]}
      ];
    } else {
      geminiMsgs = [...apiMessages, { role: "user", content: input.trim() }];
    }

    const systemNote = imgForApi
      ? "\nThe user has sent a PHOTO. Analyze every visible issue in detail. Do NOT say you cannot see images."
      : vidForApi
      ? "\nThe user has sent a VIDEO. Describe every visible issue frame by frame. Do NOT say you cannot process video."
      : "";

    try {
      const raw = await callGemini(geminiMsgs, buildJuliaSystem(vehicle) + systemNote, 2000);

      let parsed = null;
      try {
        // Remove markdown fences and fix stray chars Gemini sometimes inserts
        let cleaned = raw.replace(/```json/g, "").replace(/```/g, "").trim();
        // Fix stray single letter before a quote in arrays: , e "foo" -> , "foo"
        cleaned = cleaned.replace(/,\s+[a-zA-Z]\s+"/g, ', "');
        const jsonStart = cleaned.indexOf("{");
        const jsonEnd = cleaned.lastIndexOf("}");
        if (jsonStart >= 0 && jsonEnd > jsonStart) {
          parsed = JSON.parse(cleaned.slice(jsonStart, jsonEnd + 1));
        }
      } catch (parseErr) {
        console.warn("Julia parse error:", parseErr.message);
      }
      if (!parsed || !parsed.message) {
        const plainText = raw.replace(/```json|```/g, "").replace(/\{[\s\S]*\}/g, "").trim();
        parsed = { message: plainText || "Sorry, can you repeat that?", phase, coveredPoints: [], issuesFound: [], done: false };
      }

      const assistantMsg = { role: "assistant", content: parsed.message };
      setMessages(prev => [...prev, assistantMsg]);
      // Track the actual message sent (text only for history, not the binary image)
      const historyUserMsg = { role: "user", content: input.trim() || (imgForApi ? "[Photo sent]" : vidForApi ? "[Video sent]" : "") };
      setApiMessages([...apiMessages, historyUserMsg, assistantMsg]);
      if (ttsEnabled) speakText(parsed.message, "female");

      if (parsed.phase) setPhase(parsed.phase);
      if (parsed.coveredPoints?.length > 0) {
        setCoveredPoints(prev => [...new Set([...prev, ...parsed.coveredPoints])]);
      }
      if (parsed.issuesFound?.length > 0) setAllIssues(prev => [...prev, ...parsed.issuesFound]);
      if (parsed.done && parsed.inspectionSummary) { setSummary(parsed.inspectionSummary); setDone(true); }
    } catch (e) {
      console.error("Julia error:", e);
      if (e.message?.includes("ANTHROPIC_KEY")) {
        setMessages(prev => [...prev, { role: "assistant", content: "Anthropic API key not configured — add REACT_APP_ANTHROPIC_KEY to .env" }]);
      } else {
        setMessages(prev => [...prev, { role: "assistant", content: "I hit a snag on my end — give me a second and try again." }]);
      setSendError(true);
      }
    }
    setLoading(false);
  };

  // Build work orders with proper routing


  // Smart assignment rules:
  // Sky = DETAIL only (cleaning, detailing, wash)
  // Robert = quick jobs ≤20min, no lift, no mechanical diagnosis (fluid top-offs, wiper blades, bulbs, etc.)
  // James = all mechanical, bodywork, anything requiring diagnosis or >20min
  const resolveAssignee = (wo) => {
    const desc = (wo.description || "").toLowerCase();
    const cat = wo.category;
    const hours = wo.estimatedHours || 0;

    // Sky ONLY gets DETAIL category — cleaning, wash, detail
    if (cat === "DETAIL") return "sky";

    if (cat === "VENDOR") return null;

    // Robert's quick-no-lift list: truly ≤20min, no diagnosis, no lift required
    const robertKeywords = /^(?=.*\b(bulb|wiper blade|wiper insert|washer fluid|windshield fluid|top off|air filter|cabin filter|cabin air|fuse|battery terminal|tire pressure check|tpms reset|tpms relearn|mirror glass|antenna|emblem|badge|decal|sticker|lug nut torque|valve stem cap|touch.?up paint|door edge|floor mat|plate frame|oil level check|coolant check|brake fluid check)\b)/i;
    const isRobertJob = hours > 0 && hours <= 0.34 && robertKeywords.test(desc);
    if (isRobertJob) return "robert";

    // Everything else — mechanical, bodywork, TPMS diagnosis, sensors, electrical, cosmetic that needs tools
    return "james";
  };

  const buildWorkOrders = (wos) => (wos || []).map(wo => ({
    id: genId(),
    category: wo.category,
    description: wo.description,
    priority: wo.priority || "MEDIUM",
    status: wo.category === "MECHANICAL" ? "OPEN" : "OPEN",
    createdAt: todayStr(),
    estimatedHours: wo.category === "DETAIL" ? 3 : 0,
    actualHours: 0,
    diagnosisRationale: wo.rationale || "Julia inspection finding",
    assignedTo: resolveAssignee(wo),
    routingNote: wo.assignTo || null,
  }));

  const applyInspection = (forceIncomplete = false) => {
    const wos = summary?.recommendedWorkOrders || [];
    const newWOs = buildWorkOrders(wos);

    // Assign detail WO — Ruban for $8000+ vehicles, Sky otherwise
    // Check both new WOs AND existing vehicle WOs to avoid duplicates
    const existingDetailWO = (vehicle.workOrders || []).find(w => w.category === "DETAIL" && w.status !== "COMPLETE" && !w.deleted);
    // Never auto-add DETAIL WO to sold vehicles being repaired — customer vehicles get repair only
    if (!vehicle.soldRepairPending && !newWOs.some(w => w.category === "DETAIL") && !existingDetailWO) {
      const vehicleValue = vehicle.estimatedMarketValue || vehicle.manualSalePrice || parseFloat(vehicle.kbbValue) || 0;
      const useRuban = vehicleValue >= RUBAN.autoAssignThreshold;
      const large = isLargeVehicle(vehicle);
      if (useRuban) {
        newWOs.push({ id: genId(), category: "DETAIL", description: `Premium detail — Blue Diamond Auto Detail (${large ? "Large vehicle" : "Car"})`, priority: "MEDIUM", status: "OPEN", createdAt: todayStr(), estimatedHours: 0, actualHours: 0, assignedTo: "ruban", rubanJob: true, rubanRate: large ? RUBAN.rateLarge : RUBAN.rateRegular, diagnosisRationale: `Vehicle valued $${vehicleValue.toLocaleString()} — assigned to Ruban` });
      } else {
        // Detail WO not auto-added — user must explicitly request via "Add Detail" button
      }
    }

    const incomplete = forceIncomplete || !isComplete;

    // Separate DETAIL from other new WOs
    const newDetailWOs = newWOs.filter(w => w.category === "DETAIL");
    const newOtherWOs = newWOs.filter(w => w.category !== "DETAIL");

    // Merge Julia's detail notes into existing DETAIL WO if one exists, otherwise add new
    const existingWOs = vehicle.workOrders || [];
    const existingDetailIdx = existingWOs.findIndex(w => w.category === "DETAIL" && w.status !== "COMPLETE" && !w.deleted);
    let mergedWOs;
    if (existingDetailIdx >= 0 && newDetailWOs.length > 0) {
      // Update the existing DETAIL WO with Julia's enhanced description
      const juliaDetail = newDetailWOs[0];
      mergedWOs = existingWOs.map((w, i) => i === existingDetailIdx
        ? { ...w, description: juliaDetail.description, diagnosisRationale: juliaDetail.diagnosisRationale || w.diagnosisRationale }
        : w
      ).concat(newOtherWOs);
    } else {
      // No existing DETAIL WO — add Julia's
      mergedWOs = [...existingWOs, ...newWOs];
    }

    const updatedVehicle = {
      ...vehicle,
      workOrders: mergedWOs,
      conditionSummary: summary ? `${summary.overallCondition} condition. ${summary.exteriorNotes || ""} ${summary.interiorNotes || ""}`.trim() : "Inspection incomplete",
      inspectorNotes: messages.filter(m => m.role === "user").map(m => m.content).join(". "),
      juliaInspectionSummary: summary || { overallCondition: "INCOMPLETE", allIssues },
      juliaInspectionIncomplete: incomplete,
      juliaCompletedAt: incomplete ? null : todayStr(),
      juliaMissingPoints: incomplete ? missingRequired.map(p => p.label) : [],
      juliaProgress: progressPct,
      needsMechanicalDiagnosis: summary?.needsMechanicalDiagnosis || allIssues.some(i => i.area === "MECHANICAL"),
      stage: (summary?.needsMechanicalDiagnosis || allIssues.some(i => i.area === "MECHANICAL")) ? "INITIAL_DIAG" : "QC_DETAIL",
      juliaTranscript: messages.map(m => ({ role: m.role, content: typeof m.content === "string" ? m.content : "[media]" })),
    };
    updateVehicle(updatedVehicle);
    onInspectionComplete(updatedVehicle, newOtherWOs, updatedVehicle.needsMechanicalDiagnosis);
  };

  const proceedNow = () => {
    // Exit early — save what we have with incomplete warning
    applyInspection(true);
  };

  const phaseList = ["EXTERIOR","TIRES","UNDERHOOD","INTERIOR","TEST_DRIVE_PREP","TEST_DRIVE_RESULTS"];
  const phaseLabels = { EXTERIOR:"Exterior", TIRES:"Tires", UNDERHOOD:"Underhood", INTERIOR:"Interior", TEST_DRIVE_PREP:"Test Drive", TEST_DRIVE_RESULTS:"Drive Results" };
  const phaseIdx = phaseList.indexOf(phase);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "80vh" }}>
      {/* Full-screen Julia image */}
      <div style={{ position: "sticky", top: 0, zIndex: 10, width: "100%", background: "#1a0a2e", overflow: "hidden", marginBottom: 0, display: "flex", justifyContent: "center", maxHeight: 280 }}>
        <img src={juliaImgErr ? "" : "/images/Julia.jpg"} alt="Julia" onError={() => setJuliaImgErr(true)}
          style={{ width: "100%", maxWidth: 480, objectFit: "cover", objectPosition: "top center", display: "block" }} />
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "linear-gradient(transparent, rgba(26,10,46,0.95))", padding: "24px 16px 10px" }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: C.purple, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.1em" }}>JULIA — APPRAISAL SPECIALIST</div>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.6)" }}>{vehicle.year} {vehicle.make} {vehicle.model} #{vehicle.stockNumber}</div>
        </div>
        <div style={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 4 }}>
          <button onClick={() => setTtsEnabled(t => !t)} style={{ background: "rgba(0,0,0,0.6)", border: `1px solid ${ttsEnabled ? C.purple : "rgba(255,255,255,0.2)"}`, borderRadius: 6, padding: "4px 8px", cursor: "pointer", fontSize: 10, color: ttsEnabled ? C.purple : "rgba(255,255,255,0.6)", fontFamily: "inherit" }}>
            {ttsEnabled ? "🔊" : "🔇"}
          </button>
          {ttsEnabled && <button onClick={stopSpeaking} style={{ background: "rgba(0,0,0,0.6)", border: `1px solid ${C.red}`, borderRadius: 6, padding: "4px 8px", cursor: "pointer", fontSize: 10, color: C.red, fontFamily: "inherit" }}>⏹</button>}
          <button onClick={() => {
            const state = { messages, apiMessages, phase, coveredPoints, issuesFound: allIssues, isComplete };
            updateVehicle({ ...vehicle, _juliaPaused: state });
            if (onClose) onClose();
          }} style={{ background: "rgba(0,0,0,0.6)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 6, padding: "4px 8px", cursor: "pointer", fontSize: 10, color: "rgba(255,255,255,0.7)", fontFamily: "inherit" }}>⏸ Pause</button>
        </div>
      </div>
      {/* Progress */}
      <div style={{ padding: "8px 0 8px", borderBottom: `1px solid ${C.border}`, marginBottom: 10 }}>
        {/* Progress bar */}
        <div style={{ marginBottom: 8 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
            <span style={{ fontSize: 10, fontWeight: 700, color: isComplete ? C.green : C.purple }}>
              {isComplete ? "✅ All checkpoints covered" : `Inspection Progress — ${coveredAll}/${totalAll} points`}
            </span>
            <span style={{ fontSize: 10, fontWeight: 800, color: isComplete ? C.green : C.purple }}>{progressPct}%</span>
          </div>
          <div style={{ height: 6, borderRadius: 3, background: C.border, overflow: "hidden" }}>
            <div style={{ height: "100%", borderRadius: 3, width: `${progressPct}%`, background: isComplete ? C.green : C.purple, transition: "width 0.4s ease" }} />
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 3 }}>
            <span style={{ fontSize: 9, color: C.textM }}>{coveredRequired}/{totalRequired} required · {coveredAll - coveredRequired}/{totalAll - totalRequired} optional</span>
            {allIssues.length > 0 && <span style={{ fontSize: 9, color: C.amber, fontWeight: 700 }}>⚠ {allIssues.length} issue{allIssues.length > 1 ? "s" : ""} found</span>}
          </div>
        </div>

        {/* Phase chips */}
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          {phaseList.map((p, i) => {
            const phasePoints = JULIA_DATA_POINTS.filter(dp => dp.phase === p);
            const phaseCovered = phasePoints.filter(dp => coveredPoints.includes(dp.id)).length;
            const isDone = phaseCovered === phasePoints.length;
            return (
              <div key={p} style={{ padding: "2px 7px", borderRadius: 10, background: isDone ? C.green + "20" : i === phaseIdx ? C.purple + "25" : C.s2, border: `1px solid ${isDone ? C.green : i === phaseIdx ? C.purple : C.border}`, fontSize: 9, fontWeight: 700, color: isDone ? C.green : i === phaseIdx ? C.purple : C.textM }}>
                {isDone ? "✓ " : phaseCovered > 0 ? `${phaseCovered}/${phasePoints.length} ` : ""}{phaseLabels[p]}
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 10, paddingRight: 4 }}>
        {messages.map((m, i) => (
          <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start", alignItems: "flex-end", gap: 8 }}>
            {m.role === "assistant" && <JuliaAvatar size={28} />}
            <div style={{ maxWidth: "80%", padding: "10px 14px", borderRadius: m.role === "user" ? "12px 12px 4px 12px" : "12px 12px 12px 4px", background: m.role === "user" ? C.amber + "25" : C.s2, border: `1px solid ${m.role === "user" ? C.amber + "50" : C.border}`, fontSize: 13, color: C.text, lineHeight: 1.6 }}>
              {m.role === "assistant" && <div style={{ fontSize: 9, color: C.purple, fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>JULIA</div>}
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
            <JuliaAvatar size={28} />
            <div style={{ padding: "10px 14px", borderRadius: "12px 12px 12px 4px", background: C.s2, border: `1px solid ${C.border}`, display: "flex", gap: 4, alignItems: "center" }}>
              <span style={{ fontSize: 9, color: C.textM, marginRight: 4 }}>Julia is thinking…</span>
              {[0, 0.2, 0.4].map((d, i) => <div key={i} style={{ width: 6, height: 6, borderRadius: "50%", background: C.purple, animation: `blink 0.8s ease-in-out ${d}s infinite` }} />)}
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Inspection complete result panel */}
      {done && summary && (
        <div style={{ background: C.purple + "12", border: `2px solid ${C.purple}50`, borderRadius: 10, padding: "14px 16px", margin: "10px 0" }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: C.purple, marginBottom: 8 }}>✅ Inspection Complete — {summary.overallCondition} Condition</div>
          {summary.recommendedWorkOrders?.length > 0 && (
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 6 }}>Work Orders to Create</div>
              {summary.recommendedWorkOrders.map((wo, i) => (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderBottom: `1px solid ${C.border}`, fontSize: 11 }}>
                  <div>
                    <span style={{ color: C.text }}>{wo.description}</span>
                    {wo.assignTo && <span style={{ fontSize: 9, color: C.purple, marginLeft: 6 }}>→ {wo.assignTo}</span>}
                  </div>
                  <Badge color={wo.priority === "HIGH" ? C.red : wo.priority === "MEDIUM" ? "#f97316" : C.textM} small>{wo.priority}</Badge>
                </div>
              ))}
            </div>
          )}
          {summary.needsMechanicalDiagnosis && (
            <div style={{ background: C.amber + "15", border: `1px solid ${C.amber}40`, borderRadius: 6, padding: "8px 10px", marginBottom: 10, fontSize: 11, color: C.text }}>
              🔧 Mechanical issues noted — Hank will take over for full diagnosis.
            </div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <Btn variant="purple" onClick={() => applyInspection(false)}>
              <CheckCircle size={13} /> {summary.needsMechanicalDiagnosis ? "Create WOs & Hand to Hank →" : "Create Work Orders & Continue"}
            </Btn>
            <Btn variant="ghost" size="sm" onClick={onClose}>Discard</Btn>
          </div>
        </div>
      )}

      {/* Input + Proceed Now */}
      {!done && (
        <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
          {/* Missing required points warning — shows if inspection has started */}
          {coveredAll > 0 && missingRequired.length > 0 && (
            <div style={{ background: C.amber + "10", border: `1px solid ${C.amber}30`, borderRadius: 6, padding: "6px 10px", marginBottom: 8, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div style={{ fontSize: 9, color: C.amber }}>
                <strong>Missing:</strong> {missingRequired.slice(0, 4).map(p => p.label).join(", ")}{missingRequired.length > 4 ? ` +${missingRequired.length - 4} more` : ""}
              </div>
              <button onClick={proceedNow}
                style={{ background: C.amber + "20", border: `1px solid ${C.amber}60`, borderRadius: 6, padding: "3px 10px", cursor: "pointer", fontSize: 10, fontWeight: 700, color: C.amber, fontFamily: "inherit", flexShrink: 0 }}>
                Proceed Now ⚠
              </button>
            </div>
          )}
          {juliaImg && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, background: C.purple+"10", border: `1px solid ${C.purple}30`, borderRadius: 7, padding: "5px 8px" }}>
              <img src={juliaImg.preview} alt="" style={{ width: 44, height: 44, borderRadius: 6, objectFit: "cover" }} />
              <span style={{ fontSize: 10, color: C.purple, flex: 1 }}>📷 Photo — Julia will analyze the condition</span>
              <button onClick={() => setJuliaImg(null)} style={{ background: "none", border: "none", cursor: "pointer", color: C.textM }}>✕</button>
            </div>
          )}
          {juliaVideo && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, background: C.purple+"10", border: `1px solid ${C.purple}30`, borderRadius: 7, padding: "5px 8px" }}>
              <div style={{ width: 44, height: 44, borderRadius: 6, background: C.purple+"25", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, flexShrink: 0 }}>🎥</div>
              <span style={{ fontSize: 10, color: C.purple, flex: 1 }}>{juliaVideo.name || "Video"} — Julia will watch it ({(juliaVideo.b64.length * 0.75 / 1024 / 1024).toFixed(1)}MB)</span>
              <button onClick={() => setJuliaVideo(null)} style={{ background: "none", border: "none", cursor: "pointer", color: C.textM }}>✕</button>
            </div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={listening ? () => { recRef.current?.stop(); setListening(false); } : startVoice}
              style={{ flexShrink: 0, width: 40, height: 40, borderRadius: 8, border: `2px solid ${listening ? C.red : C.purple}`, background: listening ? C.red + "20" : C.purple + "15", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: listening ? C.red : C.purple }}>
              {listening ? <MicOff size={16} /> : <><img src="/images/btn-voice-text.jpg" alt="" style={{width:20,height:20,objectFit:"contain",flexShrink:0}} onError={e=>e.target.style.display="none"} /></>}
            </button>
            {/* Photo/video upload removed from Julia — voice/text only */}
            <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && send()}
              placeholder={listening ? "🔴 Listening…" : phase === "TEST_DRIVE_PREP" ? "Take the car for a drive, then come back…" : "Describe what you see or observe…"}
              style={{ flex: 1, background: C.s2, border: `1px solid ${C.bLight}`, borderRadius: 8, padding: "10px 12px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none" }} />
            {sendError && lastInput && (
              <button onClick={() => { setInput(lastInput); setSendError(false); setTimeout(() => send(), 50); }}
                style={{ flexShrink:0, background:C.amber, border:"none", borderRadius:8, padding:"8px 12px", cursor:"pointer", fontSize:11, fontWeight:700, color:"#000", fontFamily:"inherit", whiteSpace:"nowrap" }}>
                🔄 Retry
              </button>
            )}
            <Btn variant="purple" onClick={send} disabled={!input.trim() || loading}><Send size={14} /></Btn>
          </div>
          {coveredAll === 0 && (
            <div style={{ textAlign: "center", marginTop: 6 }}>
              <button onClick={proceedNow} style={{ background: "none", border: "none", fontSize: 10, color: C.textM, cursor: "pointer", textDecoration: "underline", fontFamily: "inherit" }}>
                Skip inspection — proceed without data
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── MARKET VALUE & REPAIR COST ESTIMATOR ────────────────────────────────────
// Calls Claude to estimate PNW market value and detailed repair costs after diagnosis

const estimateMarketValue = async (vehicle, workOrders) => {
  const apiKey = process.env.REACT_APP_ANTHROPIC_KEY;
  if (!apiKey) return null;

  const woSummary = (workOrders || []).map(w =>
    `${w.description} (${w.estimatedHours || 1}h est., $${w.estimatedPartsCost || 0} parts${w.specializedToolsCost > 0 ? `, $${w.specializedToolsCost} tools` : ""})`
  ).join("; ") || "No repairs identified";

  const prompt = `You are an automotive market expert specializing in the Pacific Northwest used car market (Oregon/Washington).

Vehicle: ${vehicle.year} ${vehicle.make} ${vehicle.model}${vehicle.trim ? " " + vehicle.trim : ""}, ${vehicle.mileage ? parseInt(vehicle.mileage).toLocaleString() + " miles" : "mileage unknown"}
Condition issues found: ${woSummary}

Provide:
1. Estimated retail sale price for this vehicle AS-IS (post-recon) at a small independent dealer in Salem, Oregon PNW market
2. For each repair listed, estimate: mechanic hours (professional, not DIY), parts cost (aftermarket acceptable), any specialized tool rental needed
3. Total estimated repair cost assuming $${LABOR_RATE}/hr labor

Return JSON only:
{
  "marketValue": 12500,
  "source": "PNW dealer market estimate",
  "note": "brief one-line reasoning",
  "repairs": [
    {"description": "...", "hours": 2.5, "partsCost": 150, "toolsCost": 0, "total": 220}
  ],
  "repairTotal": 370,
  "laborTotal": 70,
  "partsTotal": 150,
  "toolsTotal": 0
}`;

  try {
    const res = await fetch("/api/claude", { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${await getIdToken()}` },
      body: JSON.stringify({
        model: "claude-sonnet-4-6",
        max_tokens: 600,
        messages: [{ role: "user", content: prompt }]
      })
    });
    const data = await res.json();
    const parsed = JSON.parse((data.content?.[0]?.text || "{}").replace(/```json|```/g, "").trim());
    return parsed;
  } catch { return null; }
};

const DiagnosticChat = ({ vehicle, onDiagnosisComplete, onClose, onVehicleUpdate, staff, vehicles, vendors, addLedger, addPart, updateVehicle }) => {
  const [messages, setMessages] = useState([]);
  const [apiMessages, setApiMessages] = useState([]);
  const [input, setInput] = useState("");
  const [listening, setListening] = useState(false);
  const [loading, setLoading] = useState(false);
  const [lastDiagInput, setLastDiagInput] = useState("");
  const [diagSendError, setDiagSendError] = useState(false);
  const [confidence, setConfidence] = useState(0);
  const [done, setDone] = useState(false);
  const [diagnosis, setDiagnosis] = useState(null);
  const [phase, setPhase] = useState("intro");
  const [moodState, setMoodState] = useState("neutral");
  const [ttsEnabled, setTtsEnabled] = useState(false);
  const [hankImg, setHankImg] = useState(null);
  const bottomRef = useRef(null);
  const recRef = useRef(null);
  const HankAvatar = ({ size = 44, mood }) => {
    const moodColor = { neutral: C.blue, thinking: C.amber, concerned: "#f97316", confident: C.green, alarmed: C.red, satisfied: C.green };
    const moodEmoji = { neutral: "🔧", thinking: "🤔", concerned: "😐", confident: "💪", alarmed: "🚨", satisfied: "✅" };
    const st = mood || moodState;
    const imgSrc = FOREMAN.avatarStates[st] || FOREMAN.avatar;
    const borderColor = moodColor[st];
    const [imgError, setImgError] = useState(false);

    return (
      <div style={{ position: "relative", flexShrink: 0 }}>
        {!imgError && imgSrc ? (
          <img
            src={imgSrc}
            alt={`Hank — ${st}`}
            onError={() => setImgError(true)}
            style={{
              width: size, height: size,
              borderRadius: "50%",
              objectFit: "cover",
              objectPosition: "top center",
              border: `3px solid ${borderColor}`,
              boxShadow: `0 0 14px ${borderColor}50`,
              transition: "border-color 0.4s, box-shadow 0.4s",
              display: "block",
            }}
          />
        ) : (
          <div style={{
            width: size, height: size, borderRadius: "50%",
            background: `linear-gradient(135deg, #1e3a5f, #2d5a8e)`,
            border: `3px solid ${borderColor}`,
            display: "flex", alignItems: "center", justifyContent: "center",
            fontSize: size * 0.42,
            boxShadow: `0 0 12px ${borderColor}40`,
          }}>🔧</div>
        )}
        {/* Mood indicator dot */}
        <div style={{
          position: "absolute", bottom: 0, right: 0,
          width: Math.max(size * 0.28, 12), height: Math.max(size * 0.28, 12),
          borderRadius: "50%",
          background: borderColor,
          border: `2px solid ${C.bg}`,
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: Math.max(size * 0.14, 7),
        }}>{moodEmoji[st]}</div>
      </div>
    );
  };

  const hankOpeningFired = useRef(false);
  useEffect(() => {
    if (hankOpeningFired.current) return;
    hankOpeningFired.current = true;
    // Resume from paused state if available
    if (vehicle._hankPaused) {
      const saved = vehicle._hankPaused;
      if (saved.messages?.length) setMessages(saved.messages);
      if (saved.apiMessages?.length) setApiMessages(saved.apiMessages);
      if (saved.confidence) setConfidence(saved.confidence);
      if (saved.moodState) setMoodState(saved.moodState);
      setMessages(m => [...m, { role: "assistant", content: `Back at it. Let me remind myself where we were — ${saved.confidence || 0}% confidence on the ${vehicle.year} ${vehicle.make} ${vehicle.model}. What else did you notice?` }]);
      setPhase("chat");
      return;
    }
    const yr = vehicle.year || ""; const mk = vehicle.make || ""; const mo = vehicle.model || "";
    const mi = vehicle.mileage ? parseInt(vehicle.mileage).toLocaleString() + " miles" : null;
    const tr = vehicle.transmission || null; const dr = vehicle.drivetrain || null;
    const woContext = vehicle._woContext || null;
    let opening;
    if (!yr && !mk && !mo) {
      opening = "Hank. What are we diagnosing — year, make, model, or a stock number from the lot. Doesn't matter if it's not in inventory.";
    } else if (woContext) {
      opening = `Alright, ${yr} ${mk} ${mo}${mi ? " with " + mi : ""}. ${woContext} — let's dig into that. What have you observed so far?`;
    } else {
      const missingFields = [!vehicle.mileage && "mileage", !vehicle.transmission && "transmission type", !vehicle.drivetrain && "drivetrain"].filter(Boolean);
      opening = `Alright, ${yr} ${mk} ${mo}${mi ? " with " + mi : ""}${tr ? ", " + tr : ""}${dr ? ", " + dr : ""}.`;
      if (!vehicle.isGuest && vehicle.stockNumber && vehicle.stockNumber !== "GUEST" && vehicle.stockNumber !== "") {
        opening += ` Stock #${vehicle.stockNumber}.`;
      }
      if (missingFields.length > 0) {
        opening += ` Before we dig in — what's the ${missingFields[0]} on this one?`;
      } else {
        opening += ` Tell me what you've got — what did you notice on the drive or walk-around? Don't filter anything.`;
      }
    }
    setMessages([{ role: "assistant", content: opening }]);
    setPhase("chat");
  }, []);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { setInput("Voice not available — please type."); return; }
    const r = new SR(); r.continuous = false; r.interimResults = false;
    r.onresult = (e) => { setInput(prev => prev + e.results[0][0].transcript + " "); setListening(false); };
    r.onend = () => setListening(false);
    r.start(); recRef.current = r; setListening(true);
  };

  const [pendingMatch, setPendingMatch] = useState(null); // { inventoryVehicle } awaiting confirmation
  const [matchedInventory, setMatchedInventory] = useState(false);

  // Fuzzy match user text against active inventory
  const tryInventoryMatch = (text) => {
    if (matchedInventory || !vehicles?.length) return null;
    const t = text.toLowerCase();
    const active = vehicles.filter(v => !["SOLD","SCRAPPED","LINE_READY"].includes(v.stage));
    // Stock number
    const stockMatch = text.match(/\b(\d{4,6})\b/);
    if (stockMatch) {
      const hit = active.find(v => String(v.stockNumber) === stockMatch[1]);
      if (hit) return hit;
    }
    // Year
    const yearMatch = text.match(/\b((19|20)\d{2}|'?\d{2})\b/);
    const yearRaw = yearMatch?.[0]?.replace("'","");
    const year = yearRaw ? (yearRaw.length === 2 ? (parseInt(yearRaw) < 30 ? "20"+yearRaw : "19"+yearRaw) : yearRaw) : null;
    const MAKES = ["chevrolet","chevy","ford","toyota","honda","nissan","dodge","ram","jeep","gmc","bmw","mercedes","audi","volkswagen","vw","hyundai","kia","subaru","mazda","volvo","lexus","acura","infiniti","cadillac","buick","lincoln","mitsubishi","chrysler","porsche","jaguar","land rover"];
    const foundMake = MAKES.find(m => t.includes(m));
    const normMake = (mk) => (mk||"").toLowerCase().replace("chevrolet","chevy").replace("volkswagen","vw");
    if (foundMake || year) {
      let pool = active;
      if (year) pool = pool.filter(v => String(v.year) === year);
      if (foundMake) pool = pool.filter(v => normMake(v.make).includes(foundMake) || foundMake.includes(normMake(v.make)));
      if (pool.length === 1) return pool[0];
      // Add model words
      const modelWords = t.split(/\s+/).filter(w => w.length > 3 && !/^\d+$/.test(w) && !MAKES.some(m=>m.includes(w)));
      const byModel = pool.find(v => modelWords.some(w => (v.model||"").toLowerCase().includes(w)));
      if (byModel) return byModel;
      if (pool.length > 0) return pool[0];
    }
    return null;
  };

  const send = async () => {
    if ((!input.trim() && !hankImg) || loading) return;
    // Inventory match check — only when vehicle is unknown
    if (!vehicle.year && !vehicle.make && !vehicle.stockNumber && input.trim() && !pendingMatch && !matchedInventory && messages.length < 6) {
      const match = tryInventoryMatch(input.trim());
      if (match) setPendingMatch(match);
    }
    const imgForHank = hankImg;
    if (hankImg) setHankImg(null);
    const userContent = imgForHank
      ? [{ type: "image", source: { type: "base64", media_type: imgForHank.mediaType, data: imgForHank.b64 } }, { type: "text", text: input.trim() || "Analyze this image — what mechanical issues do you see? Describe everything relevant to diagnosing this vehicle." }]
      : input.trim();
    const userMsg = { role: "user", content: userContent };
    const newApiMsgs = [...apiMessages, userMsg];
    setLastDiagInput(input.trim());
    setDiagSendError(false);
    setMessages(prev => [...prev, { role: "user", content: input.trim() || "📷 [Photo attached]" }]);
    setInput(""); setLoading(true);

    try {
      const raw = await callClaude(newApiMsgs, buildDiagSystem(vehicle), 1200);

      // Robust JSON parsing — handle markdown fences and extra text
      let parsed = null;
      try {
        let cleaned = raw.replace(/```json/g, "").replace(/```/g, "").trim();
        const jsonStart = cleaned.indexOf("{");
        const jsonEnd = cleaned.lastIndexOf("}");
        if (jsonStart >= 0 && jsonEnd > jsonStart) {
          parsed = JSON.parse(cleaned.slice(jsonStart, jsonEnd + 1));
        }
      } catch (parseErr) {
        console.warn("Hank parse error:", parseErr.message, "| Raw:", raw.slice(0, 300));
      }

      // If parse failed, treat plain text as message
      if (!parsed || !parsed.message) {
        const plainText = raw.replace(/```json|```/g, "").replace(/\{[\s\S]*\}/g, "").trim();
        parsed = { message: plainText || raw, confidence: confidence, moodState, done: false };
      }

      const assistantMsg = { role: "assistant", content: parsed.message };
      setMessages(prev => [...prev, assistantMsg]);
      setApiMessages([...newApiMsgs, assistantMsg]);
      setConfidence(parsed.confidence || confidence);
      if (parsed.moodState) setMoodState(parsed.moodState);
      if (ttsEnabled) speakText(parsed.message, "male");

      if (parsed.done && parsed.diagnosis) {
        setDiagnosis(parsed.diagnosis);
        setDone(true);
        setPhase("result");
      }
      // Save transcript snapshot to vehicle after each exchange
      if (updateVehicle) {
        updateVehicle({ ...vehicle, hankTranscript: [...newApiMsgs, assistantMsg].map(m => ({ role: m.role, content: typeof m.content === "string" ? m.content : "[media]" })) });
      }
    } catch (e) {
      console.error("Hank error:", e.message);
      setMessages(prev => [...prev, { role: "assistant", content: "Sorry, I had a hiccup. Say that again?" }]);
      setDiagSendError(true);
    }
    setLoading(false);
  };

  const applyDiagnosis = (incomplete = false) => {
    if (!diagnosis) return;
    const newWOs = diagnosis.workOrders.map(wo => {
      let assignedVendorId = null;
      if (wo.vendorType) {
        const v = autoVendor(wo.vendorType, vendors, vehicles);
        assignedVendorId = v?.id || null;
      }
      // Smart assignment — managers never get tasks, Robert only for quick no-lift jobs
      const assignedTo = (() => {
        const cat = wo.category; const desc = (wo.description || "").toLowerCase(); const hrs = wo.estimatedHours || 0;
        if (cat === "DETAIL") return "sky";
        if (cat === "VENDOR" || assignedVendorId) return null;
        const noLiftQuick = /bulb|wiper|blade|fluid|top off|air filter|cabin filter|fuse|battery terminal|tire pressure|tpms reset|mirror|antenna|emblem/i;
        if (hrs <= 0.35 && noLiftQuick.test(desc)) return "robert";
        return "james";
      })();
      return {
        id: genId(), ...wo, status: "OPEN", createdAt: todayStr(), actualHours: 0,
        assignedVendorId, assignedTo, diagnosisRationale: wo.rationale,
        ...(incomplete ? { incompleteDiagnosis: true } : {})
      };
    });

    // Dedup — if same category+similar description already exists, replace it
    const deduped = [...(vehicle.workOrders || [])];
    newWOs.forEach(newWO => {
      const dupIdx = deduped.findIndex(existing =>
        existing.category === newWO.category &&
        existing.status !== "COMPLETE" &&
        (existing.description?.toLowerCase().slice(0,20) === newWO.description?.toLowerCase().slice(0,20) ||
         (existing.diagnosisRationale && newWO.diagnosisRationale &&
          existing.diagnosisRationale.toLowerCase().slice(0,30) === newWO.diagnosisRationale.toLowerCase().slice(0,30)))
      );
      if (dupIdx >= 0) deduped[dupIdx] = { ...newWO, id: deduped[dupIdx].id, replacedAt: todayStr() };
      else deduped.push(newWO);
    });

    const updatedVehicle = {
      ...vehicle,
      stage: "MECH_VERIFY",
      workOrders: deduped,
      conditionSummary: diagnosis.summary,
      safetyFlags: diagnosis.safetyFlags || [],
      advertisingHighlights: [...(vehicle.advertisingHighlights || []), ...(diagnosis.advertisingNotes || [])],
      diagnosisConfidence: confidence,
      diagnosisIncomplete: incomplete,
      diagnosisIncompleteNote: incomplete ? `Accepted at ${confidence}% confidence. Working theory: ${diagnosis.summary}. Full diagnosis still required.` : null,
    };
    updateVehicle(updatedVehicle);

    const mechWOs = newWOs.filter(w => w.category === "MECHANICAL");
    if (mechWOs.length) {
      const james = staff.find(s => s.role === "mechanic");
      console.log(`Notify ${james?.name}: ${mechWOs.length} mechanical WOs assigned`);
    }

    // Fire async market value + repair cost estimate in background
    estimateMarketValue(updatedVehicle, newWOs).then(estimates => {
      if (estimates) {
        const vehicleValue = estimates.marketValue;
        const repairTotal = estimates.repairTotal || 0;
        const needsApproval = vehicleValue > 0 && repairTotal > vehicleValue * APPROVAL_THRESHOLD;
        updateVehicle({
          ...updatedVehicle,
          manualSalePrice: estimates.marketValue || updatedVehicle.manualSalePrice,
          estimatedMarketValue: estimates.marketValue,
          marketValueSource: estimates.source,
          marketValueNote: estimates.note,
          repairEstimateAI: estimates.repairTotal,
          repairEstimateBreakdown: estimates,
          pendingApproval: needsApproval ? {
            requestedAt: todayStr(),
            repairTotal,
            vehicleValue,
            pctOfValue: Math.round((repairTotal / vehicleValue) * 100),
            approvedBy: null,
            approvedAt: null,
            status: "PENDING",
          } : null,
        });
      }
    }).catch(() => {});

    onDiagnosisComplete(updatedVehicle, newWOs);
  };

  const hankImgSrc = FOREMAN.avatarStates[moodState] || FOREMAN.avatar;
  const [hankImgErr, setHankImgErr] = useState(false);

  return (
    <div style={{ display: "flex", flexDirection: "column", height: "75vh" }}>
      {/* Full-screen Hank image */}
      <div style={{ position: "sticky", top: 0, zIndex: 10, width: "100%", background: "#1a1200", overflow: "hidden", marginBottom: 0, display: "flex", justifyContent: "center", maxHeight: 280 }}>
        {!hankImgErr && hankImgSrc ? (
          <img src={hankImgSrc} alt="Hank" onError={() => setHankImgErr(true)}
            style={{ width: "100%", maxWidth: 480, objectFit: "cover", objectPosition: "top center", display: "block" }} />
        ) : (
          <div style={{ width: "100%", height: 200, background: "linear-gradient(180deg, #2d1f00 0%, #1a1200 100%)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 80 }}>🔧</div>
        )}
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "linear-gradient(transparent, rgba(26,18,0,0.95))", padding: "24px 16px 10px" }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: C.amber, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.1em" }}>{FOREMAN.name.toUpperCase()} — {FOREMAN.title.toUpperCase()}</div>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.6)", fontStyle: "italic" }}>
            {moodState === "alarmed"   ? "⚠ Safety concern flagged" :
             moodState === "satisfied" ? "✓ Confident diagnosis reached" :
             moodState === "confident" ? "Getting close…" :
             moodState === "thinking"  ? "Working through the possibilities…" :
             moodState === "concerned" ? "Multiple possibilities — keep going" :
             "Tell Hank what you observed"}
          </div>
        </div>
        <div style={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 4 }}>
          <button onClick={() => setTtsEnabled(t => !t)} style={{ background: "rgba(0,0,0,0.6)", border: `1px solid ${ttsEnabled ? C.amber : "rgba(255,255,255,0.2)"}`, borderRadius: 6, padding: "4px 8px", cursor: "pointer", fontSize: 10, color: ttsEnabled ? C.amber : "rgba(255,255,255,0.6)", fontFamily: "inherit" }}>
            {ttsEnabled ? "🔊" : "🔇"}
          </button>
          {ttsEnabled && <button onClick={stopSpeaking} style={{ background: "rgba(0,0,0,0.6)", border: `1px solid ${C.red}`, borderRadius: 6, padding: "4px 8px", cursor: "pointer", fontSize: 10, color: C.red, fontFamily: "inherit" }}>⏹</button>}
          <button onClick={() => {
            const state = { messages, apiMessages, confidence, moodState };
            updateVehicle({ ...vehicle, _hankPaused: state });
            if (onClose) onClose();
          }} style={{ background: "rgba(0,0,0,0.6)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 6, padding: "4px 8px", cursor: "pointer", fontSize: 10, color: "rgba(255,255,255,0.7)", fontFamily: "inherit" }}>⏸ Pause</button>
        </div>
      </div>
      {/* Confidence + separator */}
      <div style={{ padding: "8px 0", borderBottom: `1px solid ${C.border}`, marginBottom: 12 }}>
        <ConfidenceMeter pct={confidence} />
      </div>

      {/* Chat messages */}
      <div style={{ flex: 1, overflowY: "auto", display: "flex", flexDirection: "column", gap: 10, paddingRight: 4 }}>
        {messages.map((m, i) => (
          <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start", alignItems: "flex-end", gap: 8 }}>
            {m.role === "assistant" && <HankAvatar size={30} mood={i === messages.length - 1 ? moodState : "neutral"} />}
            <div style={{ maxWidth: "78%", padding: "10px 14px", borderRadius: m.role === "user" ? "12px 12px 4px 12px" : "12px 12px 12px 4px", background: m.role === "user" ? C.amber + "25" : C.s2, border: `1px solid ${m.role === "user" ? C.amber + "50" : C.border}`, fontSize: 13, color: C.text, lineHeight: 1.6 }}>
              {m.role === "assistant" && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                  <div style={{ fontSize: 9, color: C.blue, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em" }}>HANK</div>
                  <button onClick={() => { stopSpeaking(); speakText(m.content, "male"); }}
                    style={{ background: "none", border: "none", cursor: "pointer", fontSize: 14, padding: "0 2px" }} title="Play this message">🔊</button>
                </div>
              )}
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: "flex", justifyContent: "flex-start", alignItems: "flex-end", gap: 8 }}>
            <HankAvatar size={30} mood="thinking" />
            <div style={{ padding: "10px 14px", borderRadius: "12px 12px 12px 4px", background: C.s2, border: `1px solid ${C.border}`, display: "flex", gap: 4, alignItems: "center" }}>
              <span style={{ fontSize: 9, color: C.textM, marginRight: 4 }}>Hank is thinking…</span>
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: C.amber, animation: "blink 0.8s ease-in-out infinite" }} />
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: C.amber, animation: "blink 0.8s ease-in-out 0.2s infinite" }} />
              <div style={{ width: 6, height: 6, borderRadius: "50%", background: C.amber, animation: "blink 0.8s ease-in-out 0.4s infinite" }} />
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* ── Inventory match confirmation banner ── */}
      {pendingMatch && !matchedInventory && (
        <div style={{ background:"#fef3c7", border:"2px solid #f59e0b", borderRadius:10, padding:"10px 14px", margin:"8px 0", display:"flex", flexDirection:"column", gap:8 }}>
          <div style={{ fontSize:12, fontWeight:700, color:"#92400e" }}>
            🔍 Is this for <strong>#{pendingMatch.stockNumber} — {pendingMatch.year} {pendingMatch.make} {pendingMatch.model}{pendingMatch.trim?" "+pendingMatch.trim:""}</strong>?
          </div>
          {pendingMatch.mileage && <div style={{ fontSize:10, color:"#78350f" }}>{parseInt(pendingMatch.mileage).toLocaleString()} mi{pendingMatch.transmission?" · "+pendingMatch.transmission:""}</div>}
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={() => {
              setMatchedInventory(true);
              setPendingMatch(null);
              if (onVehicleUpdate) onVehicleUpdate(pendingMatch);
            }} style={{ background:"#f59e0b", border:"none", borderRadius:7, padding:"6px 16px", cursor:"pointer", fontSize:12, fontWeight:800, color:"#000", fontFamily:"inherit" }}>
              ✓ Yes — attach to this vehicle
            </button>
            <button onClick={() => {
              setMatchedInventory(true);
              setPendingMatch(null);
            }} style={{ background:"none", border:"1px solid #d97706", borderRadius:7, padding:"6px 12px", cursor:"pointer", fontSize:11, color:"#92400e", fontFamily:"inherit" }}>
              No — different vehicle
            </button>
          </div>
        </div>
      )}

      {/* Result phase */}
      {phase === "result" && diagnosis && (
        <div style={{ background: C.green + "12", border: `1px solid ${C.green}40`, borderRadius: 10, padding: "14px 16px", margin: "12px 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
            <HankAvatar size={36} mood="satisfied" />
            <div>
              <div style={{ fontSize: 12, fontWeight: 800, color: C.green }}>HANK'S DIAGNOSIS — {confidence}% CONFIDENCE</div>
              <div style={{ fontSize: 10, color: C.textM }}>Ready to create work orders</div>
            </div>
          </div>
          <div style={{ fontSize: 12, color: C.text, marginBottom: 10 }}>{diagnosis.summary}</div>
          <div style={{ fontSize: 10, color: C.textM, fontWeight: 700, textTransform: "uppercase", marginBottom: 6 }}>Work Orders to Create ({diagnosis.workOrders?.length || 0})</div>
          {diagnosis.workOrders?.map((wo, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "6px 0", borderBottom: i < diagnosis.workOrders.length - 1 ? `1px solid ${C.border}` : "none" }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 12, color: C.text, fontWeight: 600 }}>{wo.description}</div>
                <div style={{ fontSize: 10, color: C.textS, fontStyle: "italic", marginTop: 2 }}>{wo.rationale}</div>
              </div>
              <div style={{ flexShrink: 0, textAlign: "right", marginLeft: 10 }}>
                <Badge color={wo.priority === "HIGH" ? C.red : wo.priority === "MEDIUM" ? "#f97316" : C.textM} small>{wo.priority}</Badge>
                <div style={{ fontSize: 10, color: C.textM, marginTop: 2 }}>{fmtMoney(wo.estimatedPartsCost || 0)} parts</div>
              </div>
            </div>
          ))}
          {diagnosis.safetyFlags?.length > 0 && <div style={{ marginTop: 8, padding: "6px 10px", background: C.red + "15", borderRadius: 6, fontSize: 11, color: C.red }}>⚠ Safety: {diagnosis.safetyFlags.join(" · ")}</div>}
          <div style={{ marginTop: 12, display: "flex", flexDirection: "column", gap: 8 }}>
            <Btn variant="green" onClick={applyDiagnosis}><CheckCircle size={13} /> Apply — Create Work Orders & Notify Staff</Btn>
            {confidence < 98 && (
              <div>
                <Btn variant="amber" size="sm" onClick={() => applyDiagnosis(true)}>
                  ⚠ Accept at {confidence}% Confidence — Incomplete Diagnosis
                </Btn>
                <div style={{ fontSize: 10, color: C.amber, marginTop: 6, padding: "8px 10px", background: C.amber + "10", border: `1px solid ${C.amber}40`, borderRadius: 6, lineHeight: 1.6 }}>
                  <strong>Warning:</strong> This diagnosis is only {confidence}% certain. Work orders will be created based on Hank's current working theory, but the root cause has NOT been fully confirmed. The vehicle should still receive a complete diagnosis before repair begins. Hank's current direction: {diagnosis?.summary || "still gathering information"}.
                </div>
              </div>
            )}
            <Btn variant="ghost" size="sm" onClick={onClose}>Discard</Btn>
          </div>
        </div>
      )}

      {/* Input */}
      {!done && (
        <div style={{ paddingTop: 12, borderTop: `1px solid ${C.border}` }}>
          {hankImg && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, background: C.amber+"10", border: `1px solid ${C.amber}30`, borderRadius: 7, padding: "5px 8px" }}>
              <img src={hankImg.preview} alt="" style={{ width: 38, height: 38, borderRadius: 5, objectFit: "cover" }} />
              <span style={{ fontSize: 10, color: C.amber, flex: 1 }}>📷 Photo — Hank will analyze it</span>
              <button onClick={() => setHankImg(null)} style={{ background: "none", border: "none", cursor: "pointer", color: C.textM }}>✕</button>
            </div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
          <button onClick={listening ? () => { recRef.current?.stop(); setListening(false); } : startVoice}
            style={{ flexShrink: 0, width: 40, height: 40, borderRadius: 8, border: `2px solid ${listening ? C.red : C.amber}`, background: listening ? C.red + "20" : C.amber + "15", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: listening ? C.red : C.amber }}>
            {listening ? <MicOff size={16} /> : <><img src="/images/btn-voice-text.jpg" alt="" style={{width:20,height:20,objectFit:"contain",flexShrink:0}} onError={e=>e.target.style.display="none"} /></>}
          </button>
          <ImageAttachButton onImage={setHankImg} />
          <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && send()}
            placeholder={listening ? "🔴 Listening…" : "Describe what you observe…"}
            style={{ flex: 1, background: C.s2, border: `1px solid ${C.bLight}`, borderRadius: 8, padding: "10px 12px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none" }} />
          {diagSendError && lastDiagInput && (
            <button onClick={() => { setInput(lastDiagInput); setDiagSendError(false); setTimeout(() => send(), 50); }}
              style={{ flexShrink:0, background:C.amber, border:"none", borderRadius:8, padding:"8px 10px", cursor:"pointer", fontSize:11, fontWeight:700, color:"#000", fontFamily:"inherit", whiteSpace:"nowrap" }}>
              🔄 Retry
            </button>
          )}
          <Btn variant="amber" onClick={send} disabled={(!input.trim() && !hankImg) || loading} sx={{ height: 40 }}><Send size={14} /></Btn>
          </div>
        </div>
      )}
    </div>
  );
};



// ─── CLEANING VIEW (Sky/Shaun's dashboard) ────────────────────────────────────

const CleaningView = ({ vehicles, currentUser, onOpenChecklist, onUpdate }) => {
  const [tab, setTab] = useState("active"); // active | archive
  const [search, setSearch] = useState("");

  // Active: vehicles that need detail
  const needsDetail = vehicles.filter(v =>
    !["SOLD"].includes(v.stage) &&
    (v.workOrders || []).some(w => w.category === "DETAIL" && w.status !== "COMPLETE")
  ).sort((a, b) => {
    const stageOrder = { QC_DETAIL: 0, IN_REPAIR: 1, MECH_VERIFY: 2 };
    return (stageOrder[a.stage] ?? 9) - (stageOrder[b.stage] ?? 9);
  });

  // Archive: vehicles with completed checklists
  const archived = vehicles.filter(v =>
    v.cleaningCompletedAt || (v.checklistItems || []).some(i => i.done)
  ).filter(v => {
    if (!search) return true;
    const q = search.toLowerCase();
    return v.stockNumber?.toLowerCase().includes(q) ||
      `${v.year} ${v.make} ${v.model}`.toLowerCase().includes(q);
  });

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 26, fontWeight: 800, color: C.purple, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.06em" }}>CLEANING JOBS</div>
        <div style={{ fontSize: 11, color: C.textM }}>Tap a vehicle to see the checklist</div>
      </div>

      <div style={{ display: "flex", background: C.s2, borderRadius: 8, padding: 3, marginBottom: 16, border: `1px solid ${C.border}` }}>
        {[{ id: "active", label: `Active (${needsDetail.length})` }, { id: "archive", label: "Checklist Archive" }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ flex: 1, padding: "7px 0", border: "none", borderBottom: `2px solid ${tab === t.id ? C.purple : "transparent"}`, borderRadius: 5, background: tab === t.id ? C.s3 : "transparent", color: tab === t.id ? C.purple : C.textM, fontSize: 11, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>{t.label}</button>
        ))}
      </div>

      {tab === "active" && (
        <div>
          {needsDetail.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40 }}>
              <div style={{ fontSize: 36, marginBottom: 8 }}>✨</div>
              <div style={{ fontSize: 13, color: C.textM }}>No vehicles need detailing right now.</div>
            </div>
          ) : needsDetail.map(v => {
            const detailWO = v.workOrders?.find(w => w.category === "DETAIL" && w.status !== "COMPLETE");
            const stage = STAGES.find(s => s.id === v.stage);
            const checklistTotal = (v.checklistItems || []).length;
            const checklistDone = (v.checklistItems || []).filter(i => i.done).length;

            return (
              <div key={v.id} onClick={() => onOpenChecklist(v)}
                style={{ background: C.surface, border: `2px solid ${C.purple}40`, borderRadius: 12, padding: "14px 16px", marginBottom: 10, cursor: "pointer" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: "#000" }}>{v.year} {v.make} {v.model}</div>
                    <div style={{ fontSize: 11, color: "#555" }}>#{v.stockNumber} · {v.exteriorColor || ""} · {v.mileage ? parseInt(v.mileage).toLocaleString() + " mi" : ""}</div>
                  </div>
                  <Badge color={stage?.color || C.purple} small>{stage?.label}</Badge>
                </div>
                {detailWO?.diagnosisRationale && (
                  <div style={{ fontSize: 11, color: "#333", background: C.s2, borderRadius: 6, padding: "6px 10px", marginBottom: 8 }}>
                    📋 {detailWO.diagnosisRationale}
                  </div>
                )}
                {v.inspectorNotes && (
                  <div style={{ fontSize: 11, color: "#555", marginBottom: 8, fontStyle: "italic" }}>
                    Inspector: {v.inspectorNotes.slice(0, 120)}{v.inspectorNotes.length > 120 ? "…" : ""}
                  </div>
                )}
                {checklistTotal > 0 && (
                  <div>
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                      <span style={{ fontSize: 10, color: C.textM }}>Checklist progress</span>
                      <span style={{ fontSize: 10, fontWeight: 700, color: checklistDone === checklistTotal ? C.green : C.amber }}>{checklistDone}/{checklistTotal}</span>
                    </div>
                    <div style={{ height: 4, borderRadius: 2, background: C.border }}>
                      <div style={{ height: "100%", borderRadius: 2, width: `${checklistTotal ? (checklistDone/checklistTotal)*100 : 0}%`, background: checklistDone === checklistTotal ? C.green : C.purple }} />
                    </div>
                  </div>
                )}
                {checklistTotal === 0 && (
                  <div style={{ fontSize: 11, color: C.purple, fontWeight: 600 }}>Tap to generate AI cleaning checklist →</div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {tab === "archive" && (
        <div>
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by stock # or year/make/model…"
            style={{ width: "100%", background: C.s2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none", marginBottom: 14, boxSizing: "border-box" }} />
          {archived.length === 0 ? (
            <div style={{ textAlign: "center", color: C.textM, padding: 40, fontSize: 12 }}>No completed checklists yet.</div>
          ) : archived.map(v => {
            const items = v.checklistItems || [];
            const done = items.filter(i => i.done).length;
            return (
              <div key={v.id} onClick={() => onOpenChecklist(v)}
                style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px", marginBottom: 8, cursor: "pointer" }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#000" }}>{v.year} {v.make} {v.model}</div>
                    <div style={{ fontSize: 10, color: "#555" }}>#{v.stockNumber} {v.cleaningCompletedAt ? `· Completed ${fmtDate(v.cleaningCompletedAt)}` : ""}</div>
                  </div>
                  {items.length > 0 && <Badge color={done === items.length ? C.green : C.amber} small>{done}/{items.length} done</Badge>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


// ─── SCRAPPED VEHICLES VIEW ───────────────────────────────────────────────────

const ScrappedView = ({ vehicles }) => {
  const scrapped = vehicles.filter(v => v.stage === "SCRAPPED")
    .sort((a, b) => (b.scrappedAt || "").localeCompare(a.scrappedAt || ""));

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 26, fontWeight: 800, color: C.textM, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.06em" }}>SCRAPPED VEHICLES</div>
        <div style={{ fontSize: 11, color: C.textM }}>{scrapped.length} vehicle{scrapped.length !== 1 ? "s" : ""} in scrap archive</div>
      </div>
      {scrapped.length === 0 ? (
        <div style={{ textAlign: "center", color: C.textM, padding: 40 }}>No scrapped vehicles on record.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {scrapped.map(v => (
            <div key={v.id} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: "#555" }}>{v.year} {v.make} {v.model}</div>
                <Badge color={C.textM} small>SCRAPPED</Badge>
              </div>
              <div style={{ fontSize: 10, color: C.textM }}>#{v.stockNumber} · Scrapped {fmtDate(v.scrappedAt)} by {v.scrappedBy || "unknown"}</div>
              {v.scrappedReason && <div style={{ fontSize: 11, color: "#333", marginTop: 6, fontStyle: "italic" }}>Reason: {v.scrappedReason}</div>}
              {v.managerScrapNote && <div style={{ fontSize: 10, color: C.textM, marginTop: 4 }}>Note: {v.managerScrapNote}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};


// ─── SOLD VEHICLES VIEW ──────────────────────────────────────────────────────

const SoldVehiclesView = ({ vehicles, parts, ledger, staff, onUpdateVehicle, onAddLedger, currentUser, vendors, catalog, initialSelected, onClearInitial }) => {
  const [selected, setSelected] = useState(() => initialSelected || null);
  // Update selected when initialSelected changes (navigation from outside)
  useEffect(() => { if (initialSelected) { setSelected(initialSelected); if (onClearInitial) onClearInitial(); } }, [initialSelected?.id]);
  const [scraping, setScraping] = useState(false);
  const [scrapeResult, setScrapeResult] = useState(null);
  const [expandedParts, setExpandedParts] = useState(false);
  const [startAfterSaleOpen, setStartAfterSaleOpen] = useState(false);
  const [expandedWOSection, setExpandedWOSection] = useState(null);
  const [selectedWODetail, setSelectedWODetail] = useState(null); // view-only WO detail
  const [returnFlow, setReturnFlow] = useState(null); // null | "confirm" | "document" | "photo"
  const [returnDocType, setReturnDocType] = useState(null); // "goodwill" | "invoice"
  const [returnDoc, setReturnDoc] = useState(null); // generated document text
  const [returnDocLoading, setReturnDocLoading] = useState(false);
  const [returnPhotoURL, setReturnPhotoURL] = useState(null);
  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerAddress, setCustomerAddress] = useState("");
  // ASR page state
  const [asrAgent, setAsrAgent] = useState(null); // null | "hank" | "jimmy" | "roxy"
  const [asrAddWOOpen, setAsrAddWOOpen] = useState(false);
  const [asrSelectedWO, setAsrSelectedWO] = useState(null); // clicked open WO for detail

  const scrapeInventory = async () => {
    setScraping(true); setScrapeResult(null);
    try {
      const res = await fetch("/api/claude", { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${await getIdToken()}` },
        body: JSON.stringify({ model: "claude-sonnet-4-6", max_tokens: 1000, tools: [{ type: "web_search_20250305", name: "web_search" }], messages: [{ role: "user", content: "Search the Edgewater Auto Center inventory page at https://www.edgewaterautocenter.com/inventory?clearall=1 and return a JSON list of all vehicles currently listed. Return ONLY a JSON array: [{stockNumber, year, make, model, vin}]. If you cannot access the page return {\"error\":\"cannot_access\"}" }] })
      });
      const data = await res.json();
      const text = data.content?.find(b => b.type === "text")?.text || "{}";
      try {
        const start = text.indexOf("["); const end = text.lastIndexOf("]");
        const liveInventory = (start >= 0 && end > start) ? JSON.parse(text.slice(start, end + 1)) : [];
        const activeVehicles = vehicles.filter(v => !["SOLD","SCRAPPED"].includes(v.stage));
        const possibleSold = activeVehicles.filter(v => !v.stockNumber ? false : !liveInventory.some(li => li.stockNumber === v.stockNumber || (li.vin && v.vin && li.vin === v.vin)));
        setScrapeResult({ liveCount: liveInventory.length, possibleSold });
      } catch { setScrapeResult({ error: "Could not parse inventory data" }); }
    } catch (e) { setScrapeResult({ error: e.message }); }
    setScraping(false);
  };

  const soldVehicles = vehicles.filter(v => v.stage === "SOLD").sort((a, b) => (b.saleDate || "").localeCompare(a.saleDate || ""));

  if (selected) {
    const v = selected;
    const vLedger = ledger.filter(e => e.vehicleId === v.id);
    const vParts = parts.filter(p => p.vehicleId === v.id);
    const openParts = vParts.filter(p => !["Installed","RETURNED","CANCELLED"].includes(p.status));
    const needsReturn = openParts.length > 0;
    const saleEntry = vLedger.find(e => e.category === "SALE");
    const salePrice = saleEntry?.amount || 0;
    const saleDate = v.saleDate || saleEntry?.date;

    // What we actually spent (non-sale ledger)
    const partsSpend = vLedger.filter(e => e.category === "PARTS").reduce((s, e) => s + (e.amount || 0), 0);
    const laborSpend = vLedger.filter(e => e.category === "LABOR").reduce((s, e) => s + (e.amount || 0), 0);
    const toolsSpend = vLedger.filter(e => e.category === "TOOLS" || e.category === "SUPPLIES").reduce((s, e) => s + (e.amount || 0), 0);
    const purchaseSpend = vLedger.filter(e => e.category === "PURCHASE").reduce((s, e) => s + (e.amount || 0), 0);
    const totalCostIn = vLedger.filter(e => e.category !== "SALE").reduce((s, e) => s + (e.amount || 0), 0);
    const grossProfit = salePrice - totalCostIn;

    // Post-sale costs
    const postSaleLedger = vLedger.filter(e => e.category === "POSTSAL" || e.postSale);
    const postSaleTotal = postSaleLedger.reduce((s, e) => s + (e.amount || 0), 0);
    const openWOsCost = (v.workOrders || []).filter(w => w.soldVehicleRepair && w.status !== "COMPLETE" && !w.deleted)
      .reduce((s, w) => s + (w.estimatedPartsCost || 0) + ((w.estimatedHours || 0) * LABOR_RATE), 0);

    const isCustomerVehicle = v.soldRepairPending;

    const moveBackToInventory = () => {
      onUpdateVehicle({
        ...v,
        stage: v.previousStage || "INTAKE",
        saleDate: null,
        salePrice: null,
        soldRepairPending: false,
        previousStage: null,
        movedBackAt: todayStr(),
        movedBackBy: currentUser?.name || currentUser?.username,
        workOrders: (v.workOrders || []).map(w => w.closedAtSale ? { ...w, status: "OPEN", completedAt: null, closedAtSale: false } : w),
      });
      // Reverse sale ledger entry
      const saleAmt = saleEntry?.amount || 0;
      if (saleAmt > 0) onAddLedger({ id: genId(), vehicleId: v.id, category: "REVERSAL", description: "Sale reversed — moved back to inventory", amount: -saleAmt, date: todayStr(), auto: true });
      setSelected(null);
    };

    return (
      <div>
        <button onClick={() => { setSelected(null); setExpandedParts(false); setStartAfterSaleOpen(false); setExpandedWOSection(null); }}
          style={{ background: "none", border: "none", color: C.blue, cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 4, marginBottom: 16, padding: 0 }}>
          <img src="/images/btn-back-arrow.jpg" alt="" style={{width:16,height:16,objectFit:"contain",flexShrink:0}} onError={e=>e.target.style.display="none"} /> Back to Sold Vehicles
        </button>

        {/* Header */}
        <div style={{ background: C.surface, border: `2px solid ${isCustomerVehicle ? C.red + "60" : needsReturn ? C.amber + "60" : C.green + "50"}`, borderRadius: 14, padding: "16px 18px", marginBottom: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
            <div>
              <div style={{ fontSize: 22, fontWeight: 900, color: "#000" }}>{v.year} {v.make} {v.model}</div>
              <div style={{ fontSize: 11, color: "#555", fontFamily: "monospace" }}>Stock #{v.stockNumber} · Sold {fmtDate(saleDate)}</div>
              {isCustomerVehicle && (
                <div style={{ marginTop: 4 }}>
                  <span style={{ background: C.red, color: "#fff", fontSize: 10, fontWeight: 800, padding: "2px 8px", borderRadius: 8 }}>🔧 CUSTOMER VEHICLE — POST-SALE REPAIR</span>
                  {v.customerReturnDate && <div style={{ fontSize: 11, color: C.red, fontWeight: 700, marginTop: 2 }}>Due back: {fmtDate(v.customerReturnDate)}</div>}
                </div>
              )}
            </div>
            <Badge color={C.green} small>SOLD</Badge>
          </div>
          {needsReturn && <div style={{ fontSize: 11, fontWeight: 800, color: C.red, marginTop: 4 }}>⚠ {openParts.length} part{openParts.length > 1 ? "s" : ""} need return</div>}

          {/* Initiate After-Sale Recon button — only for non-repair sold vehicles */}
          {!isCustomerVehicle && (
            <button onClick={() => setStartAfterSaleOpen(true)}
              style={{ marginTop: 12, width: "100%", background: C.purple, border: "none", borderRadius: 8, padding: "11px 16px", cursor: "pointer", fontSize: 13, fontWeight: 900, color: "#fff", fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.05em", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
              🔧 INITIATE AFTER-SALE RECONDITIONING
            </button>
          )}
        </div>

        {/* After-sale recon initiation modal */}
        {startAfterSaleOpen && (
          <Modal title="🔧 After-Sale Reconditioning" onClose={() => setStartAfterSaleOpen(false)}>
            <div style={{ fontSize: 12, color: C.text, marginBottom: 14 }}>
              This will mark the vehicle as a customer vehicle requiring post-sale repair. The after-sale protocol will begin — high priority queue, no auto-detail WOs.
            </div>
            <div style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 4 }}>Customer Return Date</div>
              <input type="date" min={todayStr()} onChange={e => onUpdateVehicle({ ...v, customerReturnDate: e.target.value })}
                style={{ width: "100%", background: C.s2, border: `1px solid ${C.border}`, borderRadius: 7, padding: "8px 10px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none" }} />
            </div>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 6 }}>Repair Location</div>
              <select onChange={e => onUpdateVehicle({ ...v, repairLocation: e.target.value, currentLocation: e.target.value })} defaultValue="mech_shop"
                style={{ width: "100%", background: C.s2, border: `1px solid ${C.border}`, borderRadius: 7, padding: "8px 10px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none" }}>
                {TOW_LOCATIONS.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
              </select>
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => {
                onUpdateVehicle({ ...v, soldRepairPending: true });
                setStartAfterSaleOpen(false);
                setSelected({ ...v, soldRepairPending: true });
              }} style={{ background: C.purple, border: "none", borderRadius: 8, padding: "10px 18px", cursor: "pointer", fontSize: 12, fontWeight: 700, color: "#fff", fontFamily: "inherit" }}>
                ✓ Start After-Sale Recon
              </button>
              <Btn variant="ghost" onClick={() => setStartAfterSaleOpen(false)}>Cancel</Btn>
            </div>
          </Modal>
        )}

        {/* Financials — inventory vehicle shows profit, customer vehicle shows post-sale cost */}
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: "14px 16px", marginBottom: 14 }}>
          {isCustomerVehicle ? (
            <div>
              <div style={{ fontSize: 10, fontWeight: 800, color: "#000", textTransform: "uppercase", marginBottom: 10 }}>Post-Sale Repair Cost</div>
              <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
                {postSaleLedger.filter(e => e.description?.includes("Tow")).reduce((s, e) => s + e.amount, 0) > 0 && (
                  <div><div style={{ fontSize: 9, color: "#555", textTransform: "uppercase" }}>Tow</div><div style={{ fontSize: 20, fontWeight: 900, color: C.red, fontFamily: "'Bebas Neue', sans-serif" }}>{fmtMoney(postSaleLedger.filter(e => e.description?.includes("Tow")).reduce((s, e) => s + e.amount, 0))}</div></div>
                )}
                {openWOsCost > 0 && <div><div style={{ fontSize: 9, color: "#555", textTransform: "uppercase" }}>Est. Repairs</div><div style={{ fontSize: 20, fontWeight: 900, color: C.amber, fontFamily: "'Bebas Neue', sans-serif" }}>{fmtMoney(openWOsCost)}</div></div>}
                <div><div style={{ fontSize: 9, color: "#555", textTransform: "uppercase" }}>Total Post-Sale</div><div style={{ fontSize: 20, fontWeight: 900, color: C.red, fontFamily: "'Bebas Neue', sans-serif" }}>{fmtMoney(postSaleTotal + openWOsCost)}</div></div>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ fontSize: 10, fontWeight: 800, color: "#000", textTransform: "uppercase", marginBottom: 10 }}>Sale Summary</div>
              <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginBottom: 12 }}>
                <div><div style={{ fontSize: 9, color: "#555", textTransform: "uppercase" }}>Sale Price</div><div style={{ fontSize: 22, fontWeight: 900, color: C.green, fontFamily: "'Bebas Neue', sans-serif" }}>{fmtMoney(salePrice)}</div></div>
                <div><div style={{ fontSize: 9, color: "#555", textTransform: "uppercase" }}>Total Cost In</div><div style={{ fontSize: 22, fontWeight: 900, color: C.amber, fontFamily: "'Bebas Neue', sans-serif" }}>{fmtMoney(totalCostIn)}</div></div>
                <div><div style={{ fontSize: 9, color: "#555", textTransform: "uppercase" }}>Gross Profit</div><div style={{ fontSize: 22, fontWeight: 900, color: grossProfit >= 0 ? C.green : C.red, fontFamily: "'Bebas Neue', sans-serif" }}>{fmtMoney(grossProfit)}</div></div>
              </div>
              {/* Cost breakdown — clickable parts section */}
              <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                  {purchaseSpend > 0 && <div><div style={{ fontSize: 9, color: "#555", textTransform: "uppercase" }}>Purchase</div><div style={{ fontSize: 14, fontWeight: 800, color: C.amber }}>{fmtMoney(purchaseSpend)}</div></div>}
                  {partsSpend > 0 && (
                    <button onClick={() => setExpandedParts(p => !p)} style={{ background: "none", border: "none", cursor: "pointer", padding: 0, textAlign: "left", fontFamily: "inherit" }}>
                      <div style={{ fontSize: 9, color: "#555", textTransform: "uppercase" }}>Parts {expandedParts ? "▲" : "▼"}</div>
                      <div style={{ fontSize: 14, fontWeight: 800, color: C.blue }}>{fmtMoney(partsSpend)}</div>
                    </button>
                  )}
                  {laborSpend > 0 && <div><div style={{ fontSize: 9, color: "#555", textTransform: "uppercase" }}>Labor</div><div style={{ fontSize: 14, fontWeight: 800, color: C.purple }}>{fmtMoney(laborSpend)}</div></div>}
                  {toolsSpend > 0 && <div><div style={{ fontSize: 9, color: "#555", textTransform: "uppercase" }}>Tools/Supplies</div><div style={{ fontSize: 14, fontWeight: 800, color: C.textM }}>{fmtMoney(toolsSpend)}</div></div>}
                </div>
                {/* Expanded parts list */}
                {expandedParts && vParts.length > 0 && (
                  <div style={{ marginTop: 10, background: C.s2, borderRadius: 8, padding: "10px 12px" }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 6 }}>Parts Ordered</div>
                    {vParts.map((p, i) => {
                      const vendor = vendors?.find(v2 => v2.id === p.supplier);
                      return (
                        <div key={i} style={{ padding: "6px 0", borderBottom: i < vParts.length - 1 ? `1px solid ${C.border}` : "none", fontSize: 11 }}>
                          <div style={{ fontWeight: 600, color: C.text }}>{p.description}</div>
                          <div style={{ fontSize: 10, color: C.textM, display: "flex", gap: 8, flexWrap: "wrap", marginTop: 2 }}>
                            <span>💰 {fmtMoney(p.cost)}</span>
                            {vendor?.name && <span>🏪 {vendor.name}</span>}
                            {p.orderDate && <span>📅 Ordered {fmtDate(p.orderDate)}</span>}
                            {p.orderedBy && <span>👤 {p.orderedBy}</span>}
                            <Badge color={p.status === "Installed" ? C.green : p.status === "RETURNED" ? C.blue : C.textM} small>{p.status}</Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Sold date + move back to inventory */}
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 16px", marginBottom: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.textM, textTransform: "uppercase" }}>Moved to Sold</div>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{fmtDate(saleDate)}</div>
            </div>
            {currentUser?.role === "manager" && (
              <button onClick={moveBackToInventory}
                style={{ background: C.amber + "15", border: `1px solid ${C.amber}50`, borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontSize: 11, fontWeight: 700, color: C.amber, fontFamily: "inherit" }}>
                ↩ Reverse — Move Back to Inventory
              </button>
            )}
          </div>
          {v.movedBackAt && <div style={{ fontSize: 10, color: C.textM, marginTop: 4 }}>Previously reversed on {fmtDate(v.movedBackAt)} by {v.movedBackBy}</div>}
          <div style={{ fontSize: 10, color: C.textM, marginTop: 4 }}>
            Was in stage: <strong>{STAGES.find(s => s.id === v.previousStage)?.label || v.previousStage || "Unknown"}</strong> when sold
          </div>
        </div>

        {/* Parts return status */}
        {vParts.length > 0 && needsReturn && (
          <div style={{ background: C.surface, border: `2px solid ${C.red}40`, borderRadius: 12, padding: "14px 16px", marginBottom: 14 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: "#000", textTransform: "uppercase", marginBottom: 8 }}>⚠ Parts Flagged for Return</div>
            {openParts.map((p, i) => {
              const binDef = BIN_SIZES.find(b => b.id === (p.binSize || estimateBinSize(p.description)));
              return (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "6px 0", borderBottom: `1px solid ${C.border}` }}>
                  <div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: C.text }}>{p.description}</div>
                    {binDef && <div style={{ fontSize: 10, color: C.blue }}>📦 {binDef.icon} {binDef.label}</div>}
                  </div>
                  <Badge color={C.red} small>Return</Badge>
                </div>
              );
            })}
          </div>
        )}

        {/* ─── WORK ORDERS — always visible, view-only ─── */}
        {(() => {
          const allWOs = (v.workOrders || []).filter(w => !w.deleted);
          const completedWOs = allWOs.filter(w => w.status === "COMPLETE" && !w.closedAtSale && !w.soldVehicleRepair);
          const saleClosedWOs = allWOs.filter(w => w.closedAtSale);
          const soldRepairWOs = allWOs.filter(w => w.soldVehicleRepair);
          const sections = [
            { id: "postsale", label: "🔧 Post-Sale Repairs", wos: soldRepairWOs, color: C.red },
            { id: "completed", label: "✓ Completed Before Sale", wos: completedWOs, color: C.green },
            { id: "soldsale", label: "⊘ Closed at Sale (were open)", wos: saleClosedWOs, color: C.textM },
          ].filter(s => s.wos.length > 0);
          if (sections.length === 0) return null;
          return (
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: "#000", textTransform: "uppercase", marginBottom: 8 }}>Work Order History</div>
              {sections.map(sec => (
                <div key={sec.id} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, marginBottom: 6, overflow: "hidden" }}>
                  <button onClick={() => setExpandedWOSection(expandedWOSection === sec.id ? null : sec.id)}
                    style={{ width: "100%", background: "none", border: "none", padding: "10px 14px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", fontFamily: "inherit" }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: sec.color }}>{sec.label}</div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                      <span style={{ fontSize: 11, color: C.textM }}>{sec.wos.length} WO{sec.wos.length !== 1 ? "s" : ""}</span>
                      {sec.wos.length > 1 && <span style={{ fontSize: 10, color: C.textM }}>· {fmtMoney(sec.wos.reduce((s,wo) => s + ((wo.actualHours||wo.estimatedHours||0)*LABOR_RATE)+(wo.estimatedPartsCost||0),0))}</span>}
                      <span style={{ fontSize: 12, color: C.textM }}>{expandedWOSection === sec.id ? "▲" : "▼"}</span>
                    </div>
                  </button>
                  {expandedWOSection === sec.id && (
                    <div style={{ borderTop: `1px solid ${C.border}` }}>
                      {sec.wos.map((wo, i) => {
                        const cat = WO_CATS.find(c => c.id === wo.category);
                        const laborCost = (wo.actualHours || wo.estimatedHours || 0) * LABOR_RATE;
                        const totalCost = laborCost + (wo.estimatedPartsCost || 0);
                        const isExpanded = selectedWODetail === wo.id;
                        return (
                          <div key={i} style={{ borderBottom: i < sec.wos.length - 1 ? `1px solid ${C.border}` : "none" }}>
                            {/* WO row — clickable */}
                            <button onClick={() => setSelectedWODetail(isExpanded ? null : wo.id)}
                              style={{ width: "100%", background: isExpanded ? C.s2 : "transparent", border: "none", padding: "10px 14px", cursor: "pointer", fontFamily: "inherit", textAlign: "left", display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                              <div style={{ flex: 1, marginRight: 8 }}>
                                <div style={{ display: "flex", gap: 5, alignItems: "center", marginBottom: 3 }}>
                                  {cat && <span style={{ background: cat.color + "25", color: cat.color, fontSize: 9, fontWeight: 800, padding: "1px 6px", borderRadius: 8 }}>{cat.label}</span>}
                                  <span style={{ background: wo.priority === "HIGH" ? C.red+"20" : wo.priority === "MEDIUM" ? C.amber+"20" : C.green+"20", color: wo.priority === "HIGH" ? C.red : wo.priority === "MEDIUM" ? C.amber : C.green, fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 8 }}>{wo.priority || "MED"}</span>
                                  {wo.status === "COMPLETE" && <span style={{ fontSize: 9, color: C.green, fontWeight: 700 }}>✓ Done</span>}
                                  {wo.status !== "COMPLETE" && <span style={{ fontSize: 9, color: C.amber, fontWeight: 700 }}>{wo.status}</span>}
                                </div>
                                <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{wo.description}</div>
                              </div>
                              <div style={{ textAlign: "right", flexShrink: 0 }}>
                                {totalCost > 0 && <div style={{ fontSize: 12, fontWeight: 800, color: C.text }}>{fmtMoney(totalCost)}</div>}
                                <div style={{ fontSize: 10, color: C.textM }}>{isExpanded ? "▲" : "▼"}</div>
                              </div>
                            </button>
                            {/* Expanded view-only WO detail */}
                            {isExpanded && (
                              <div style={{ background: C.s2, padding: "12px 14px", borderTop: `1px solid ${C.border}` }}>
                                <div style={{ fontSize: 9, fontWeight: 800, color: C.amber, textTransform: "uppercase", marginBottom: 6 }}>🔒 View Only</div>
                                {wo.notes && <div style={{ fontSize: 11, color: C.text, marginBottom: 8, lineHeight: 1.5 }}>{wo.notes}</div>}
                                <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 10, color: C.textM }}>
                                  {(wo.actualHours || wo.estimatedHours) > 0 && <span>⏱ {wo.actualHours || wo.estimatedHours}h labor · {fmtMoney(laborCost)}</span>}
                                  {wo.estimatedPartsCost > 0 && <span>🔩 {fmtMoney(wo.estimatedPartsCost)} parts</span>}
                                  {wo.assignedTo && <span>👤 {wo.assignedTo}</span>}
                                  {wo.createdAt && <span>📅 Created {fmtDate(wo.createdAt)}</span>}
                                  {wo.completedAt && <span>✓ Completed {fmtDate(wo.completedAt)}</span>}
                                  {wo.createdBy && <span>by {wo.createdBy}</span>}
                                </div>
                                {wo.instructions?.steps?.length > 0 && (
                                  <div style={{ marginTop: 8 }}>
                                    <div style={{ fontSize: 9, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 4 }}>Instructions</div>
                                    {wo.instructions.steps.map((s, si) => <div key={si} style={{ fontSize: 10, color: C.text, padding: "2px 0" }}>{si+1}. {typeof s === "string" ? s : s.text || s.step || JSON.stringify(s)}</div>)}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        );
                      })}
                      {sec.wos.length > 1 && (
                        <div style={{ padding: "8px 14px", background: C.s2, borderTop: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between" }}>
                          <span style={{ fontSize: 10, fontWeight: 700, color: C.textM, textTransform: "uppercase" }}>Section Total</span>
                          <span style={{ fontSize: 13, fontWeight: 800, color: C.text }}>{fmtMoney(sec.wos.reduce((s,wo) => s + ((wo.actualHours||wo.estimatedHours||0)*LABOR_RATE)+(wo.estimatedPartsCost||0),0))}</span>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          );
        })()}

        {/* ═══ AFTER-SALE RECONDITIONING MODE — full working page ═══ */}
        {isCustomerVehicle && (
          <div>
            {/* Agent area */}
            {asrAgent === "hank" && (
              <div style={{ marginBottom: 14 }}>
                <button onClick={() => setAsrAgent(null)} style={{ background: "none", border: "none", color: C.amber, cursor: "pointer", fontSize: 11, fontWeight: 700, fontFamily: "inherit", marginBottom: 8, padding: 0 }}>← Back to Vehicle</button>
                <DiagnosticChat vehicle={v}
                  onDiagnosisComplete={(upd, newWOs) => { if (newWOs?.length) onUpdateVehicle({ ...v, workOrders: [...(v.workOrders||[]), ...newWOs.map(w => ({...w, soldVehicleRepair: true, priority: "HIGH"}))] }); setAsrAgent(null); }}
                  onClose={() => setAsrAgent(null)} staff={staff} vehicles={vehicles} vendors={vendors}
                  addLedger={onAddLedger} addPart={() => {}} updateVehicle={onUpdateVehicle} />
              </div>
            )}
            {asrAgent === "jimmy" && (
              <div style={{ marginBottom: 14 }}>
                <button onClick={() => setAsrAgent(null)} style={{ background: "none", border: "none", color: "#f97316", cursor: "pointer", fontSize: 11, fontWeight: 700, fontFamily: "inherit", marginBottom: 8, padding: 0 }}>← Back to Vehicle</button>
                <BodyWorkChat vehicle={v}
                  onComplete={(newWOs) => { if (newWOs?.length) onUpdateVehicle({ ...v, workOrders: [...(v.workOrders||[]), ...newWOs.map(w => ({...w, soldVehicleRepair: true, priority: "HIGH"}))] }); setAsrAgent(null); }}
                  onClose={() => setAsrAgent(null)} staff={staff} vendors={vendors} />
              </div>
            )}

            {asrAgent === "wo" && (
              <div style={{ marginBottom: 14 }}>
                <AIWorkOrderModal
                  vehicle={{ ...v, soldRepairPending: true }}
                  currentUser={currentUser} staff={staff}
                  onUpdateVehicle={onUpdateVehicle}
                  onAdd={(wos) => {
                    const newWOs = wos.filter(w => w._approved).map(w => ({
                      id: genId(), ...w, soldVehicleRepair: true, createdAt: todayStr(), status: "OPEN", actualHours: 0,
                      assignedTo: w.category === "DETAIL" ? "sky" : w.category === "VENDOR" ? null : "james",
                      createdBy: currentUser?.username || "unknown"
                    }));
                    if (newWOs.length) onUpdateVehicle({ ...v, workOrders: [...(v.workOrders||[]), ...newWOs] });
                    setAsrAgent(null);
                  }}
                  onClose={() => setAsrAgent(null)}
                />
              </div>
            )}


            {!asrAgent && (
              <div>
                {/* Agent buttons */}
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 14 }}>
                  <button onClick={() => setAsrAgent("hank")}
                    style={{ background: C.amber + "15", border: `1px solid ${C.amber}40`, borderRadius: 10, padding: "11px 10px", cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 8 }}>
                    <img src="/images/hank-headshot.jpg" alt="Hank" style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover" }} onError={e => e.target.style.display="none"} />
                    <div style={{ textAlign: "left" }}>
                      <div style={{ fontSize: 11, fontWeight: 800, color: C.amber }}>HANK</div>
                      <div style={{ fontSize: 9, color: C.textM }}>Mechanical</div>
                    </div>
                  </button>
                  <button onClick={() => setAsrAgent("jimmy")}
                    style={{ background: "#f9731612", border: "1px solid #f9731640", borderRadius: 10, padding: "11px 10px", cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 8 }}>
                    <img src="/images/jimmy-working.jpg" alt="Jimmy" style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover" }} onError={e => e.target.style.display="none"} />
                    <div style={{ textAlign: "left" }}>
                      <div style={{ fontSize: 11, fontWeight: 800, color: "#f97316" }}>JIMMY</div>
                      <div style={{ fontSize: 9, color: C.textM }}>Body Work</div>
                    </div>
                  </button>
                  <button onClick={() => {
                    const cleanWO = { id: genId(), category: "DETAIL", description: "After-Sale Basic Clean — interior wipe-down, exterior wash, windows, vacuum", priority: "HIGH", estimatedHours: 2, estimatedPartsCost: 0, status: "OPEN", soldVehicleRepair: true, createdAt: todayStr(), assignedTo: "sky", createdBy: currentUser?.username || "unknown", notes: "Urgent — customer vehicle, return ASAP" };
                    onUpdateVehicle({ ...v, workOrders: [...(v.workOrders||[]), cleanWO] });
                  }}
                    style={{ background: C.purple + "12", border: `1px solid ${C.purple}30`, borderRadius: 10, padding: "11px 10px", cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 8 }}>
                    <span style={{ fontSize: 22 }}>🧹</span>
                    <div style={{ textAlign: "left" }}>
                      <div style={{ fontSize: 11, fontWeight: 800, color: C.purple }}>ADD CLEANING</div>
                      <div style={{ fontSize: 9, color: C.textM }}>Sky · After-Sale</div>
                    </div>
                  </button>
                </div>

                {/* Add WO button */}
                <button onClick={() => setAsrAgent("wo")}
                  style={{ width: "100%", padding: "11px", background: C.amber, border: "none", borderRadius: 9, color: "#000", fontSize: 12, fontWeight: 900, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.06em", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 14 }}>
                  ✨ ADD WORK ORDER
                </button>

                {/* Open Work Orders — clickable with WO detail modal */}
                {(() => {
                  const openWOs = (v.workOrders || []).filter(w => !w.deleted && w.status !== "COMPLETE" && !w.closedAtSale);
                  if (openWOs.length === 0) return (
                    <div style={{ textAlign: "center", color: C.green, fontSize: 12, fontWeight: 700, padding: "12px 0", marginBottom: 10 }}>✓ No open work orders</div>
                  );
                  return (
                    <div style={{ marginBottom: 14 }}>
                      <div style={{ fontSize: 10, fontWeight: 800, color: "#000", textTransform: "uppercase", marginBottom: 8 }}>Open Work Orders ({openWOs.length})</div>
                      {openWOs.map((wo, i) => {
                        const cat = WO_CATS.find(c => c.id === wo.category);
                        const totalCost = ((wo.estimatedHours||0)*LABOR_RATE) + (wo.estimatedPartsCost||0);
                        return (
                          <div key={i} style={{ background: C.surface, border: `1px solid ${wo.priority === "HIGH" ? C.red+"50" : C.border}`, borderLeft: `4px solid ${cat?.color || C.amber}`, borderRadius: 8, padding: "10px 14px", marginBottom: 6 }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                              <div style={{ flex: 1, marginRight: 8, cursor: "pointer" }} onClick={() => setAsrSelectedWO(wo)}>
                                <div style={{ display: "flex", gap: 5, alignItems: "center", marginBottom: 3 }}>
                                  {cat && <span style={{ background: cat.color + "25", color: cat.color, fontSize: 9, fontWeight: 800, padding: "1px 6px", borderRadius: 8 }}>{cat.label}</span>}
                                  <span style={{ background: wo.priority === "HIGH" ? C.red+"20" : wo.priority === "MEDIUM" ? C.amber+"20" : C.green+"20", color: wo.priority === "HIGH" ? C.red : wo.priority === "MEDIUM" ? C.amber : C.green, fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 8 }}>{wo.priority}</span>
                                  <span style={{ fontSize: 9, color: C.amber, fontWeight: 700 }}>{wo.status}</span>
                                </div>
                                <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{wo.description}</div>
                                {wo.assignedTo && <div style={{ fontSize: 10, color: C.textM, marginTop: 2 }}>👤 {wo.assignedTo}{wo.estimatedHours ? ` · ${wo.estimatedHours}h` : ""}</div>}
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                                {totalCost > 0 && <div style={{ fontSize: 12, fontWeight: 800, color: C.red }}>{fmtMoney(totalCost)}</div>}
                                <button onClick={e => {
                                  e.stopPropagation();
                                  if (window.confirm(`Delete work order:\n"${wo.description}"\n\nThis cannot be undone.`)) {
                                    const now = new Date();
                                    const timeStr = now.toLocaleString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit", hour: "numeric", minute: "2-digit", hour12: true });
                                    onUpdateVehicle({ ...v, workOrders: v.workOrders.map(w => w.id === wo.id ? { ...wo, deleted: true, deletedBy: currentUser?.name || "Unknown", deletedAt: todayStr(), deletedAtTime: timeStr } : w) });
                                  }
                                }}
                                  style={{ background: "none", border: "none", cursor: "pointer", color: C.textM, opacity: 0.5, fontSize: 15, padding: "0 2px" }} title="Delete">🗑</button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  );
                })()}

                {/* WO detail modal — full WorkOrderDetailModal */}
                {asrSelectedWO && (
                  <WorkOrderDetailModal
                    wo={asrSelectedWO}
                    vehicle={v}
                    parts={vParts}
                    vendors={vendors}
                    staff={staff}
                    currentUser={currentUser}
                    catalog={[]}
                    onClose={() => setAsrSelectedWO(null)}
                    onUpdateWO={(updatedWO) => {
                      onUpdateVehicle({ ...v, workOrders: (v.workOrders||[]).map(w => w.id === updatedWO.id ? updatedWO : w) });
                      setAsrSelectedWO(updatedWO);
                    }}
                    onUpdateVehicle={onUpdateVehicle}
                    onAddPart={() => {}}
                    onAddLedger={onAddLedger}
                    onShopForPart={() => {}}
                  />
                )}
              </div>
            )}
          </div>
        )}
          <button onClick={() => setReturnFlow("confirm")}
            style={{ width: "100%", padding: "14px", background: C.green, border: "none", borderRadius: 10, color: "#fff", fontSize: 13, fontWeight: 900, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.06em", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 14 }}>
            <CheckCircle size={18} /> RETURN VEHICLE TO BUYER
          </button>

        {/* ─── Return flow modals ─── */}
        {returnFlow === "confirm" && (() => {
          const openWOs = (v.workOrders || []).filter(w => w.status !== "COMPLETE" && !w.deleted && !w.closedAtSale);
          return (
            <Modal title="Return Vehicle to Buyer" onClose={() => setReturnFlow(null)}>
              {openWOs.length > 0 && (
                <div style={{ background: C.amber + "12", border: `1px solid ${C.amber}40`, borderRadius: 8, padding: "12px 14px", marginBottom: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: C.amber, marginBottom: 8 }}>⚠ {openWOs.length} Work Order{openWOs.length !== 1 ? "s" : ""} Still Open</div>
                  {openWOs.map((w, i) => (
                    <div key={i} style={{ fontSize: 11, color: C.text, padding: "3px 0", borderBottom: `1px solid ${C.border}` }}>
                      <span style={{ fontWeight: 600 }}>{w.description}</span><span style={{ color: C.textM }}> · {w.category}</span>
                    </div>
                  ))}
                  <div style={{ fontSize: 11, color: C.amber, marginTop: 8, fontWeight: 700 }}>Are you sure you want to return the vehicle with these open WOs?</div>
                </div>
              )}
              <div style={{ fontSize: 12, color: C.text, marginBottom: 14 }}>
                {openWOs.length === 0 ? "All work orders are complete. Ready to return to buyer." : "Proceeding will close all remaining open work orders."}
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <button onClick={() => setReturnFlow("document")}
                  style={{ background: C.green, border: "none", borderRadius: 8, padding: "10px 18px", cursor: "pointer", fontSize: 12, fontWeight: 800, color: "#fff", fontFamily: "inherit" }}>
                  {openWOs.length > 0 ? "Yes — Close WOs & Continue" : "Continue →"}
                </button>
                <Btn variant="ghost" onClick={() => setReturnFlow(null)}>Cancel</Btn>
              </div>
            </Modal>
          );
        })()}

        {returnFlow === "document" && (
          <Modal title="📄 Return Document" onClose={() => setReturnFlow(null)} wide>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 10 }}>What type of document?</div>
              <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
                <button onClick={() => setReturnDocType("goodwill")}
                  style={{ flex: 1, background: returnDocType === "goodwill" ? C.green + "20" : C.s2, border: `2px solid ${returnDocType === "goodwill" ? C.green : C.border}`, borderRadius: 8, padding: "12px", cursor: "pointer", fontFamily: "inherit" }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: returnDocType === "goodwill" ? C.green : C.text }}>🤝 Goodwill Letter</div>
                  <div style={{ fontSize: 10, color: C.textM, marginTop: 2 }}>No charge — courtesy repair</div>
                </button>
                <button onClick={() => setReturnDocType("invoice")}
                  style={{ flex: 1, background: returnDocType === "invoice" ? C.blue + "15" : C.s2, border: `2px solid ${returnDocType === "invoice" ? C.blue : C.border}`, borderRadius: 8, padding: "12px", cursor: "pointer", fontFamily: "inherit" }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: returnDocType === "invoice" ? C.blue : C.text }}>💰 Invoice</div>
                  <div style={{ fontSize: 10, color: C.textM, marginTop: 2 }}>Billable repair</div>
                </button>
              </div>
              {returnDocType && (
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 12 }}>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 3 }}>Customer Name *</div>
                    <input value={customerName} onChange={e => setCustomerName(e.target.value)} placeholder="Full name"
                      style={{ width: "100%", background: C.s2, border: `1px solid ${C.border}`, borderRadius: 6, padding: "7px 9px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
                  </div>
                  <div>
                    <div style={{ fontSize: 10, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 3 }}>Phone</div>
                    <input value={customerPhone} onChange={e => setCustomerPhone(e.target.value)} placeholder="(503) 555-0000" type="tel"
                      style={{ width: "100%", background: C.s2, border: `1px solid ${C.border}`, borderRadius: 6, padding: "7px 9px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
                  </div>
                  <div style={{ gridColumn: "1 / -1" }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 3 }}>Address</div>
                    <input value={customerAddress} onChange={e => setCustomerAddress(e.target.value)} placeholder="Street, City, State ZIP"
                      style={{ width: "100%", background: C.s2, border: `1px solid ${C.border}`, borderRadius: 6, padding: "7px 9px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
                  </div>
                </div>
              )}
              {returnDocType && customerName && !returnDoc && (
                <button onClick={async () => {
                  setReturnDocLoading(true);
                  const repairs = (v.workOrders || []).filter(w => w.soldVehicleRepair).map(w => w.description);
                  const towCostAmt = ledger.filter(e => e.vehicleId === v.id && (e.category === "POSTSAL" || e.description?.includes("Tow"))).reduce((s,e) => s+e.amount, 0);
                  const totalPostSale = ledger.filter(e => e.vehicleId === v.id && (e.category === "POSTSAL" || e.postSale)).reduce((s,e) => s+e.amount, 0);
                  const prompt = returnDocType === "goodwill"
                    ? `Generate a completed Goodwill Service Notice for Edgewater Auto Center LLC. Fill in ALL fields professionally. Return ONLY the document text, no extra commentary.

Dealership: Edgewater Auto Center LLC, 4250 Salem Dallas Hwy NW, Salem OR 97304
Date: ${todayStr()}
Representative: ${currentUser?.name || "Dealership Representative"}
Customer Name: ${customerName}
Customer Address: ${customerAddress || "On file"}
Customer Phone: ${customerPhone || "On file"}
Vehicle: ${v.year} ${v.make} ${v.model}
VIN: ${v.vin || "On file"}
Odometer at Recovery: ${v.recoveryOdometer ? parseInt(v.recoveryOdometer).toLocaleString() + " miles" : "See records"}
Repairs performed: ${repairs.join("; ") || "See work order"}

Format as a professional printed document with the exact legal language from this template:

NOTICE OF GOODWILL REPAIR
Date: ${todayStr()} | Dealership Representative: ${currentUser?.name || ""}

Customer Information:
• Customer Name(s): ${customerName}
• Address: ${customerAddress || ""}
• Phone: ${customerPhone || ""}

Vehicle Description:
• Year: ${v.year} | Make: ${v.make} | Model: ${v.model}
• VIN: ${v.vin || "On file"}
• Odometer Reading: ${v.recoveryOdometer ? parseInt(v.recoveryOdometer).toLocaleString() : "See records"} Miles

Thank you for your continued business. We are providing this document to outline the recent goodwill service completed on your vehicle. By signing below, you acknowledge receipt of your vehicle and confirm that the Dealership has performed the following repairs and any associated road testing:
${repairs.map((r,i) => `• ${r}`).join("\n")}
• Additional related adjustments and minor repairs as needed

We were more than happy to help take care of these issues for you as a gesture of goodwill. Because these services were provided as a courtesy, we want to gently remind you that these specific repairs are not covered under any existing warranty, and the Dealership was not obligated to perform them.

Please understand that the Dealership is in no way creating a warranty of any kind on your vehicle by attempting these goodwill repairs. THE DEALERSHIP HEREBY DISCLAIMS ALL WARRANTIES, EXPRESS OR IMPLIED, INCLUDING ANY IMPLIED WARRANTIES OF MERCHANTABILITY OR FITNESS FOR A PARTICULAR PURPOSE RELATING TO THIS REPAIR AND ALL GOODS AND SERVICES UTILIZED AND/OR PERFORMED IN CONJUNCTION WITH THIS REPAIR.

Going forward, the vehicle is returned to you strictly "AS IS." By signing this document and taking possession of the vehicle, you agree to hold the Dealership harmless from any future claims. You acknowledge that from this moment forward, you are assuming all risk and liability associated with the vehicle and its operation.

We truly appreciate you choosing us for your vehicle purchase. As a token of our appreciation, we would be thrilled to offer you a $100 referral bonus for any friend or family member you send our way who purchases a vehicle with us!

[ X ] I hereby request that the Dealership discard any replaced parts.

Customer Signature: __________________________________ Date: ____________________

Customer Signature: __________________________________ Date: ____________________

Authorized Dealership Representative: ________________________ Date: __________________`
                    : `Generate a professional repair invoice for Edgewater Auto Center LLC. Return ONLY the invoice text.

REPAIR INVOICE
Edgewater Auto Center LLC
4250 Salem Dallas Hwy NW, Salem OR 97304

Date: ${todayStr()}
Invoice #: INV-${v.stockNumber}-${Date.now().toString().slice(-5)}
Service Advisor: ${currentUser?.name || ""}

Customer: ${customerName} | Phone: ${customerPhone || ""} | Address: ${customerAddress || ""}
Vehicle: ${v.year} ${v.make} ${v.model} | VIN: ${v.vin || "On file"} | Odometer: ${v.recoveryOdometer ? parseInt(v.recoveryOdometer).toLocaleString() : ""} mi

SERVICES PERFORMED:
${repairs.map((r,i) => `${i+1}. ${r}`).join("\n")}
${towCostAmt > 0 ? `Towing: $${towCostAmt.toFixed(2)}` : ""}

TOTALS:
Total Charges: $${totalPostSale.toFixed(2)}
Amount Paid: $0.00 (Goodwill — No Charge)

Customer Signature: __________________________________ Date: ____________________
Authorized Representative: __________________________ Date: ____________________`;

                  try {
                    const res = await fetch("/api/claude", { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${await getIdToken()}` },
                      body: JSON.stringify({ model: "claude-sonnet-4-6", max_tokens: 1500, messages: [{ role: "user", content: prompt }] })
                    });
                    const data = await res.json();
                    setReturnDoc(data.content?.[0]?.text || "");
                  } catch(e) { setReturnDoc("Error generating document — try again"); }
                  setReturnDocLoading(false);
                }} disabled={returnDocLoading}
                  style={{ background: C.blue, border: "none", borderRadius: 8, padding: "10px 18px", cursor: "pointer", fontSize: 12, fontWeight: 700, color: "#fff", fontFamily: "inherit", opacity: returnDocLoading ? 0.6 : 1 }}>
                  {returnDocLoading ? "✍ Generating…" : "✍ Generate Document"}
                </button>
              )}
              {returnDoc && (
                <div>
                  <div style={{ background: "#fff", border: `2px solid ${C.border}`, borderRadius: 8, padding: "16px", marginBottom: 12, fontFamily: "monospace", fontSize: 11, lineHeight: 1.7, whiteSpace: "pre-wrap", color: "#000", maxHeight: 360, overflowY: "auto" }}>
                    {/* Logo header */}
                    <div style={{ textAlign: "center", fontWeight: 900, fontSize: 14, marginBottom: 4 }}>EDGEWATER AUTO CENTER LLC</div>
                    <div style={{ textAlign: "center", fontSize: 10, marginBottom: 12 }}>4250 Salem Dallas Hwy NW, Salem OR 97304</div>
                    <div style={{ borderTop: "2px solid #000", paddingTop: 10 }}>{returnDoc}</div>
                  </div>
                  <div style={{ display: "flex", gap: 8 }}>
                    <button onClick={() => {
                      const win = window.open("", "_blank");
                      win.document.write(`<html><head><title>${returnDocType === "goodwill" ? "Goodwill Notice" : "Invoice"} - ${v.year} ${v.make} ${v.model}</title>
                        <style>body{font-family:Arial,sans-serif;padding:40px;max-width:700px;margin:0 auto;font-size:12px;line-height:1.6}h1{font-size:18px;text-align:center}pre{white-space:pre-wrap;font-family:Arial,sans-serif}.header{text-align:center;border-bottom:2px solid #000;padding-bottom:12px;margin-bottom:16px}.logo{font-size:20px;font-weight:900}</style></head>
                        <body><div class="header"><div class="logo">EDGEWATER AUTO CENTER LLC</div><div>4250 Salem Dallas Hwy NW, Salem OR 97304</div></div><pre>${returnDoc.replace(/</g,"&lt;").replace(/>/g,"&gt;")}</pre></body></html>`);
                      win.document.close(); win.print();
                    }}
                      style={{ background: C.green, border: "none", borderRadius: 8, padding: "10px 18px", cursor: "pointer", fontSize: 12, fontWeight: 700, color: "#fff", fontFamily: "inherit" }}>
                      🖨 Print Document
                    </button>
                    <button onClick={() => setReturnDoc(null)}
                      style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px", cursor: "pointer", fontSize: 11, color: C.textM, fontFamily: "inherit" }}>
                      Regenerate
                    </button>
                    <button onClick={() => setReturnFlow("photo")}
                      style={{ background: C.amber, border: "none", borderRadius: 8, padding: "10px 18px", cursor: "pointer", fontSize: 12, fontWeight: 700, color: "#000", fontFamily: "inherit" }}>
                      Next — Upload Signed Copy →
                    </button>
                  </div>
                </div>
              )}
            </div>
          </Modal>
        )}

        {returnFlow === "photo" && (
          <Modal title="📸 Upload Signed Document" onClose={() => setReturnFlow(null)}>
            <div style={{ background: C.amber + "12", border: `1px solid ${C.amber}40`, borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 11, color: C.amber, fontWeight: 700 }}>
              📁 Reminder: Place the signed original in the vehicle file folder.
            </div>
            <div style={{ fontSize: 12, color: C.text, marginBottom: 14 }}>
              Take a photo of the signed {returnDocType === "goodwill" ? "Goodwill Notice" : "invoice"} and upload it here.
            </div>
            <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
              <label style={{ flex: 1, background: C.s2, border: `2px dashed ${C.border}`, borderRadius: 8, padding: "20px", cursor: "pointer", textAlign: "center", display: "block" }}>
                <input type="file" accept="image/*" capture="environment" style={{ display: "none" }} onChange={e => {
                  if (e.target.files?.[0]) {
                    const r = new FileReader();
                    r.onload = ev => setReturnPhotoURL(ev.target.result);
                    r.readAsDataURL(e.target.files[0]);
                  }
                }} />
                <div style={{ fontSize: 20, marginBottom: 4 }}>📷</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.text }}>Take Photo</div>
              </label>
              <label style={{ flex: 1, background: C.s2, border: `2px dashed ${C.border}`, borderRadius: 8, padding: "20px", cursor: "pointer", textAlign: "center", display: "block" }}>
                <input type="file" accept="image/*" style={{ display: "none" }} onChange={e => {
                  if (e.target.files?.[0]) {
                    const r = new FileReader();
                    r.onload = ev => setReturnPhotoURL(ev.target.result);
                    r.readAsDataURL(e.target.files[0]);
                  }
                }} />
                <div style={{ fontSize: 20, marginBottom: 4 }}>🖼</div>
                <div style={{ fontSize: 11, fontWeight: 700, color: C.text }}>Upload from Gallery</div>
              </label>
            </div>
            {returnPhotoURL && (
              <div style={{ marginBottom: 14 }}>
                <img src={returnPhotoURL} alt="Signed doc" style={{ width: "100%", borderRadius: 8, border: `1px solid ${C.border}` }} />
              </div>
            )}
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => {
                // Complete the return
                const openWOs = (v.workOrders || []).filter(w => w.status !== "COMPLETE" && !w.deleted);
                const updatedVehicle = {
                  ...v,
                  soldRepairPending: false,
                  vehicleReturnedAt: todayStr(),
                  returnDocType,
                  returnDocPhotoURL: returnPhotoURL,
                  returnDocText: returnDoc,
                  customerName,
                  workOrders: (v.workOrders || []).map(w =>
                    (w.status !== "COMPLETE" && !w.deleted) ? { ...w, status: "COMPLETE", completedAt: todayStr(), closedAtReturn: true } : w
                  ),
                };
                onUpdateVehicle(updatedVehicle);
                // Flag parts for return
                parts.filter(p => p.vehicleId === v.id && !["Installed","RETURNED","CANCELLED"].includes(p.status))
                  .forEach(p => {
                    const updatedPart = { ...p, pendingReturn: true, pendingReturnAt: todayStr() };
                    if (onAddLedger) {} // parts update handled via onUpdateVehicle context
                  });
                setSelected({ ...updatedVehicle });
                setReturnFlow(null);
                setReturnDoc(null);
                setReturnPhotoURL(null);
              }}
                style={{ background: C.green, border: "none", borderRadius: 8, padding: "10px 18px", cursor: "pointer", fontSize: 12, fontWeight: 800, color: "#fff", fontFamily: "inherit" }}>
                ✓ Complete — Vehicle Returned to Customer
              </button>
              <Btn variant="ghost" onClick={() => setReturnFlow("document")}>← Back</Btn>
              {!returnPhotoURL && (
                <button onClick={() => {
                  const openWOs = (v.workOrders || []).filter(w => w.status !== "COMPLETE" && !w.deleted);
                  const updatedVehicle = { ...v, soldRepairPending: false, vehicleReturnedAt: todayStr(), returnDocType, returnDocText: returnDoc, customerName,
                    workOrders: (v.workOrders || []).map(w => (!w.deleted && w.status !== "COMPLETE") ? { ...w, status: "COMPLETE", completedAt: todayStr(), closedAtReturn: true } : w) };
                  onUpdateVehicle(updatedVehicle);
                  setSelected({ ...updatedVehicle });
                  setReturnFlow(null); setReturnDoc(null);
                }}
                  style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px", cursor: "pointer", fontSize: 11, color: C.textM, fontFamily: "inherit" }}>
                  Skip Photo
                </button>
              )}
            </div>
          </Modal>
        )}
      </div>
    );
  }

  // List view
  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 26, fontWeight: 800, color: C.green, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.06em" }}>SOLD VEHICLES</div>
          <div style={{ fontSize: 11, color: C.textM }}>{soldVehicles.length} sold · tap any vehicle for full snapshot</div>
        </div>
        <Btn variant="outline" size="sm" onClick={scrapeInventory} disabled={scraping}>
          {scraping ? <><RefreshCw size={11} style={{ animation: "spin 1s linear infinite" }} /> Checking site…</> : <><Search size={11} /> Check Website</>}
        </Btn>
      </div>

      {/* Scrape results */}
      {scrapeResult && !scrapeResult.error && scrapeResult.possibleSold?.length > 0 && (
        <div style={{ background: C.amber + "12", border: `1px solid ${C.amber}50`, borderRadius: 10, padding: "12px 14px", marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.amber, marginBottom: 8 }}>⚠ {scrapeResult.possibleSold.length} vehicle{scrapeResult.possibleSold.length > 1 ? "s" : ""} may have sold — not found on website ({scrapeResult.liveCount} vehicles live)</div>
          {scrapeResult.possibleSold.map(v => (
            <div key={v.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderBottom: `1px solid ${C.border}`, fontSize: 11 }}>
              <span style={{ color: C.text }}>{v.year} {v.make} {v.model} #{v.stockNumber}</span>
              <Badge color={C.amber} small>Not on site</Badge>
            </div>
          ))}
          <div style={{ fontSize: 9, color: C.textM, marginTop: 8 }}>These vehicles are in your system as active but not showing on the website inventory. They may have been sold. Tap each vehicle to mark as sold.</div>
        </div>
      )}
      {scrapeResult && !scrapeResult.error && scrapeResult.possibleSold?.length === 0 && (
        <div style={{ background: C.green + "12", border: `1px solid ${C.green}40`, borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 11, color: C.green }}>
          ✓ All active vehicles accounted for on website ({scrapeResult.liveCount} vehicles live)
        </div>
      )}
      {scrapeResult?.error && (
        <div style={{ background: C.red + "10", border: `1px solid ${C.red}30`, borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 11, color: C.red }}>
          Could not check website: {scrapeResult.error}
        </div>
      )}

      {soldVehicles.length === 0 ? (
        <div style={{ textAlign: "center", color: C.textM, padding: 40, fontSize: 12 }}>No sold vehicles yet.</div>
      ) : (() => {
        const asrVehicles = soldVehicles.filter(v => v.soldRepairPending);
        const clearedVehicles = soldVehicles.filter(v => !v.soldRepairPending);

        const VehicleCard = ({ v }) => {
          const vLedger = ledger.filter(e => e.vehicleId === v.id);
          const vParts = parts.filter(p => p.vehicleId === v.id);
          const openParts = vParts.filter(p => !["Installed","RETURNED","CANCELLED"].includes(p.status));
          const needsReturn = openParts.length > 0;
          const saleEntry = vLedger.find(e => e.category === "SALE");
          const salePrice = saleEntry?.amount || 0;
          const totalCostIn = vLedger.filter(e => e.category !== "SALE").reduce((s, e) => s + (e.amount || 0), 0);
          const grossProfit = salePrice - totalCostIn;
          const isASR = v.soldRepairPending;
          const dueDate = v.customerReturnDate;
          const asrStarted = v.soldRepairPendingAt || v.saleDate;

          // Days overdue for ASR
          const daysUntilDue = dueDate ? Math.ceil((new Date(dueDate) - new Date()) / 86400000) : null;
          const overdue = daysUntilDue !== null && daysUntilDue < 0;

          return (
            <div key={v.id} onClick={() => setSelected(v)}
              style={{
                background: isASR ? (overdue ? C.red + "18" : C.amber + "12") : C.surface,
                border: `2px solid ${isASR ? (overdue ? C.red + "80" : C.amber + "80") : needsReturn ? C.red + "50" : C.green + "35"}`,
                borderRadius: 12, padding: "12px 16px", cursor: "pointer", position: "relative", overflow: "hidden"
              }}>
              {/* Left accent bar */}
              <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 5, background: isASR ? (overdue ? C.red : C.amber) : needsReturn ? C.red : C.green }} />
              <div style={{ paddingLeft: 10 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: "#000" }}>{v.year} {v.make} {v.model}</div>
                    {isASR ? (
                      <div style={{ fontSize: 10, color: "#555", marginTop: 2 }}>
                        <span>#{v.stockNumber}</span>
                        {asrStarted && <span style={{ color: C.amber, fontWeight: 700 }}> · ASR started {fmtDate(asrStarted)}</span>}
                        {dueDate && <span style={{ color: overdue ? C.red : C.text, fontWeight: 700 }}> · Due {fmtDate(dueDate)}{overdue ? ` (${Math.abs(daysUntilDue)}d overdue)` : daysUntilDue === 0 ? " (TODAY)" : ` (${daysUntilDue}d)`}</span>}
                      </div>
                    ) : (
                      <div style={{ fontSize: 10, color: "#555" }}>#{v.stockNumber} · Sold {fmtDate(v.saleDate)}</div>
                    )}
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 8 }}>
                    {isASR ? (
                      <div style={{ fontSize: 11, fontWeight: 800, color: C.amber }}>🔧 ASR</div>
                    ) : (
                      salePrice > 0 && <div style={{ fontSize: 13, fontWeight: 800, color: C.green }}>{fmtMoney(salePrice)}</div>
                    )}
                    {!isASR && grossProfit !== 0 && <div style={{ fontSize: 10, color: grossProfit >= 0 ? C.green : C.red }}>{grossProfit >= 0 ? "+" : ""}{fmtMoney(grossProfit)}</div>}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                  {isASR && <Badge color={overdue ? C.red : C.amber} small>{overdue ? "⚠ OVERDUE" : "🔧 After-Sale Repair"}</Badge>}
                  {isASR && v.isGoodwillRepair && <Badge color={C.green} small>🤝 Goodwill</Badge>}
                  {isASR && v.isGoodwillRepair === false && <Badge color={C.blue} small>💰 Billable</Badge>}
                  {!isASR && needsReturn && <Badge color={C.red} small>⚠ {openParts.length} parts to return</Badge>}
                  {!isASR && !needsReturn && !v.vehicleReturnedAt && <Badge color={C.green} small>✓ Cleared</Badge>}
                  {v.vehicleReturnedAt && v.returnDocType === "goodwill" && <Badge color={C.green} small>🤝 Goodwill Completed</Badge>}
                  {v.vehicleReturnedAt && v.returnDocType === "invoice" && <Badge color={C.blue} small>💰 Invoiced</Badge>}
                  {v.vehicleReturnedAt && !v.returnDocType && <Badge color={C.green} small>✓ Returned to Customer</Badge>}
                  {isASR && v.repairLocation && (() => { const loc = TOW_LOCATIONS.find(l => l.id === v.repairLocation); return loc ? <Badge color={C.blue} small>📍 {loc.label.split("—")[0].trim()}</Badge> : null; })()}
                </div>
              </div>
            </div>
          );
        };

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {/* ASR group at top */}
            {asrVehicles.length > 0 && (
              <>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 2 }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: C.amber, textTransform: "uppercase", letterSpacing: "0.08em" }}>🔧 After-Sale Repairs ({asrVehicles.length})</div>
                  <div style={{ flex: 1, height: 1, background: C.amber + "40" }} />
                </div>
                {asrVehicles.map(v => <VehicleCard key={v.id} v={v} />)}
                {clearedVehicles.length > 0 && (
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, marginBottom: 2 }}>
                    <div style={{ fontSize: 10, fontWeight: 700, color: C.textM, textTransform: "uppercase", letterSpacing: "0.06em" }}>Sold ({clearedVehicles.length})</div>
                    <div style={{ flex: 1, height: 1, background: C.border }} />
                  </div>
                )}
              </>
            )}
            {clearedVehicles.map(v => <VehicleCard key={v.id} v={v} />)}
          </div>
        );
      })()}
    </div>
  );
};


// ─── CATALOG SUB-COMPONENTS ───────────────────────────────────────────────────


// ─── STOCK-IN CONFIRM MODAL ──────────────────────────────────────────────────
// Used by ALL intake methods: invoice scan, Roxy chat, photo ID, barcode
// Handles: qty/location/vehicle assignment + AI fitment check

// Bin label component shown after stocking in


// ─── SHOP INVENTORY CATALOG ───────────────────────────────────────────────────

// ─── ROXY STOCKING CHAT ──────────────────────────────────────────────────────

// ─── PHOTO IDENTIFY ITEM ─────────────────────────────────────────────────────

// ─── PARTS HUB VIEW ──────────────────────────────────────────────────────────
// ─── COMBINED FLEET STATUS VIEW ───────────────────────────────────────────────
const FleetStatusView = ({ vehicles, parts, schedule, setSchedule, staff, currentUser, onBack, openVehicle, navigateTo, setView, onUpdateVehicle }) => {
  const [fleetView, setFleetView] = useState("list"); // list | pipeline | calendar
  const [sortBy, setSortBy] = useState("stage");
  const [expandedId, setExpandedId] = useState(null);
  const [calendarScope, setCalendarScope] = useState("company");
  const [calMonth, setCalMonth] = useState(new Date().getMonth());
  const [calYear, setCalYear] = useState(new Date().getFullYear());
  const [selectedDay, setSelectedDay] = useState(null);
  const [overdueModal, setOverdueModal] = useState(false);


  const activeVehicles = vehicles.filter(v => !["SOLD","SCRAPPED","LINE_READY"].includes(v.stage));
  const soldVehicles = vehicles.filter(v => v.stage === "SOLD");
  const scrappedVehicles = vehicles.filter(v => v.stage === "SCRAPPED");
  const photoReady = vehicles.filter(v => v.stage === "PHOTO_READY" && !v.photographed);
  const lineReady = vehicles.filter(v => v.stage === "LINE_READY");
  // overdue: non-detail WOs after 10 calendar days OR detail-only after 5 business days
  const overdue = activeVehicles.filter(v => {
    const openWOs = (v.workOrders||[]).filter(w => w.status!=="COMPLETE" && !w.deleted);
    if (!openWOs.length) return false;
    const nonDetail = openWOs.filter(w => w.category !== "DETAIL");
    const detailOnly = nonDetail.length === 0;
    if (detailOnly) {
      // Find when detail WO was created
      const detailWO = openWOs.find(w => w.category === "DETAIL");
      const detailStart = detailWO?.createdAt || v.arrivalDate;
      return businessDaysBetween(detailStart, todayStr()) > 5;
    }
    return daysSince(v.arrivalDate) > 10;
  });
  const ksCount = parts.filter(p => { const v = vehicles.find(x => x.id === p.vehicleId); return v?.stage === "SOLD" && !["Installed","RETURNED","CANCELLED"].includes(p.status); }).length;

  // Active = has at least one open non-DETAIL WO (detail-only = nearly ready, line-ready = done)
  const hasOpenNonDetail = (v) => (v.workOrders||[]).some(w => w.status !== "COMPLETE" && !w.deleted && w.category !== "DETAIL");
  const hasOpenDetail = (v) => (v.workOrders||[]).some(w => w.status !== "COMPLETE" && !w.deleted && w.category === "DETAIL");
  const hasAnyOpen = (v) => (v.workOrders||[]).some(w => w.status !== "COMPLETE" && !w.deleted);
  // Only show in active list if has open WOs (either kind)
  const activeWithWOs = activeVehicles.filter(v => hasAnyOpen(v));
  const sorted = [...activeWithWOs, ...lineReady].sort((a, b) => {
    if (sortBy === "date") return (a.arrivalDate||"").localeCompare(b.arrivalDate||"");
    if (sortBy === "priority") {
      const openA = (a.workOrders||[]).some(w => w.priority === "HIGH" && w.status !== "COMPLETE");
      const openB = (b.workOrders||[]).some(w => w.priority === "HIGH" && w.status !== "COMPLETE");
      return openB - openA;
    }
    if (sortBy === "type") {
      const catA = (a.workOrders||[]).find(w => w.status !== "COMPLETE")?.category || "ZZZ";
      const catB = (b.workOrders||[]).find(w => w.status !== "COMPLETE")?.category || "ZZZ";
      return catA.localeCompare(catB);
    }
    // default: stage order
    const stageOrder = ["INTAKE","INITIAL_DIAG","MECHANICAL","BODYWORK","DETAIL","PHOTO_READY","LINE_READY"];
    return stageOrder.indexOf(a.stage) - stageOrder.indexOf(b.stage);
  });

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
        <button onClick={onBack} style={{ background:"none",border:"none",color:C.blue,cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"inherit",display:"flex",alignItems:"center",gap:4,padding:0 }}>
          <img src="/images/btn-back-arrow.jpg" alt="" style={{width:16,height:16,objectFit:"contain",flexShrink:0}} onError={e=>e.target.style.display="none"} /> Home
        </button>
        <img src="/images/Edgewaterlogo.png" alt="" style={{ height:28, objectFit:"contain", opacity:0.55, flexShrink:0 }} onError={e=>e.target.style.display="none"} />
      </div>

      {/* Top action row */}
      <div style={{ display:"flex",gap:8,flexWrap:"wrap",marginBottom:10 }}>
        {/* View toggles */}
        {[["list","☰ List"],["pipeline","📊 Pipeline"],["calendar","📅 Calendar"],["lineready",`✅ Line Ready${lineReady.length?` (${lineReady.length})`:""}`]].map(([v,lbl]) => (
          <button key={v} onClick={() => setFleetView(v)}
            style={{ background:fleetView===v?(v==="lineready"?C.green:C.blue):"none",border:`1px solid ${fleetView===v?(v==="lineready"?C.green:C.blue):C.border}`,borderRadius:20,padding:"5px 14px",cursor:"pointer",fontSize:11,fontWeight:700,color:fleetView===v?"#fff":C.textM,fontFamily:"inherit" }}>
            {lbl}
          </button>
        ))}
        {/* Calendar scope — only show when in calendar view */}
        {fleetView === "calendar" && (
          <div style={{ display:"flex",gap:4,marginLeft:4,background:C.s2,borderRadius:20,padding:"2px" }}>
            {["company","mine"].map(s => (
              <button key={s} onClick={() => setCalendarScope(s)}
                style={{ background:calendarScope===s?C.surface:"none",border:calendarScope===s?`1px solid ${C.border}`:"none",borderRadius:18,padding:"4px 12px",cursor:"pointer",fontSize:11,fontWeight:700,color:calendarScope===s?C.text:C.textM,fontFamily:"inherit",boxShadow:calendarScope===s?"0 1px 3px rgba(0,0,0,0.1)":"none" }}>
                {s==="company"?"🏢 Company":"👤 My Calendar"}
              </button>
            ))}
          </div>
        )}
        {/* Photo Ready badge */}
        {photoReady.length > 0 && (
          <button onClick={() => (navigateTo||setView)("photoready")}
            style={{ background:"#ec489920",border:"2px solid #ec489960",borderRadius:20,padding:"5px 12px",cursor:"pointer",fontSize:11,fontWeight:800,color:"#ec4899",fontFamily:"inherit",display:"flex",alignItems:"center",gap:5 }}>
            📷 {photoReady.length}
          </button>
        )}
        {/* Sold vehicles */}
        <button onClick={() => (navigateTo||setView)("soldvehicles")}
          style={{ background:C.green+"15",border:`1px solid ${C.green}40`,borderRadius:20,padding:"5px 12px",cursor:"pointer",fontSize:11,fontWeight:700,color:C.green,fontFamily:"inherit",position:"relative" }}>
          ✓ Sold
          {ksCount > 0 && <span style={{ marginLeft:5,background:C.red,color:"#fff",borderRadius:20,padding:"0 5px",fontSize:9,fontWeight:800 }}>{ksCount}</span>}
        </button>
        {/* Scrapped */}
        <button onClick={() => (navigateTo||setView)("scrapped")}
          style={{ background:"none",border:`1px solid ${C.border}`,borderRadius:20,padding:"5px 12px",cursor:"pointer",fontSize:11,fontWeight:700,color:C.textM,fontFamily:"inherit" }}>
          🗑 Scrapped {scrappedVehicles.length > 0 ? `(${scrappedVehicles.length})` : ""}
        </button>
      </div>

      {/* Sort bar */}
      {fleetView === "list" && (
        <div style={{ display:"flex",gap:6,marginBottom:10,alignItems:"center" }}>
          <span style={{ fontSize:9,color:C.textM,fontWeight:700,textTransform:"uppercase" }}>Sort:</span>
          {["stage","date","priority","type"].map(s => (
            <button key={s} onClick={() => setSortBy(s)}
              style={{ background:sortBy===s?C.amber+"20":"none",border:`1px solid ${sortBy===s?C.amber:C.border}`,borderRadius:20,padding:"3px 10px",cursor:"pointer",fontSize:10,fontWeight:700,color:sortBy===s?C.amber:C.textM,fontFamily:"inherit" }}>
              {s==="stage"?"Stage":s==="date"?"Date In":s==="priority"?"Priority":"Repair Type"}
            </button>
          ))}
        </div>
      )}

      {/* Stats strip */}
      <div style={{ display:"flex",gap:8,marginBottom:12 }}>
        {[
          { label:"In Recon",val:activeWithWOs.length,color:C.amber, onClick:()=>setFleetView("list") },
          { label:"Overdue",val:overdue.length,color:overdue.length>0?C.red:C.textM, onClick:overdue.length>0?()=>setOverdueModal(true):null },
          { label:"Line Ready",val:lineReady.length,color:C.green, onClick:()=>setFleetView("lineready") },
        ].map(s => (
          <div key={s.label} onClick={s.onClick||undefined}
            style={{ flex:1,background:s.color+"12",border:`1px solid ${s.color}30`,borderRadius:8,padding:"7px 10px",textAlign:"center",cursor:s.onClick?"pointer":"default" }}>
            <div style={{ fontSize:20,fontWeight:900,color:s.color,fontFamily:"'Bebas Neue',sans-serif" }}>{s.val}</div>
            <div style={{ fontSize:9,color:C.textM,textTransform:"uppercase",fontWeight:700 }}>{s.label} →</div>
          </div>
        ))}
      </div>

      {/* Overdue modal */}
      {overdueModal && (
        <Modal title="🔥 Overdue Vehicles" onClose={() => setOverdueModal(false)} wide>
          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {overdue.map(v => {
              const openWOs = (v.workOrders||[]).filter(w => w.status!=="COMPLETE" && !w.deleted);
              const nonDetail = openWOs.filter(w => w.category!=="DETAIL");
              const detailOnly = nonDetail.length===0;
              const detailWO = openWOs.find(w => w.category==="DETAIL");
              const detailStart = detailWO?.createdAt || v.arrivalDate;
              const daysOver = detailOnly
                ? businessDaysBetween(detailStart, todayStr())
                : daysSince(v.arrivalDate);
              return (
                <div key={v.id} onClick={() => { openVehicle(v); setOverdueModal(false); }}
                  style={{ background:detailOnly?"#f0fdf4":C.red+"08", border:`2px solid ${detailOnly?C.green+"60":C.red+"50"}`, borderRadius:10, padding:"12px 16px", cursor:"pointer", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <div>
                    <div style={{ fontSize:13, fontWeight:800, color:"#000" }}>{v.year} {v.make} {v.model}</div>
                    <div style={{ fontSize:10, color:C.textM, marginTop:2 }}>
                      #{v.stockNumber}
                      {detailOnly
                        ? <span style={{ color:C.green, fontWeight:700, marginLeft:8 }}>🧹 Detail queue — {daysOver} work days</span>
                        : <span style={{ color:C.red, fontWeight:700, marginLeft:8 }}>🔥 {daysOver} days in recon</span>}
                    </div>
                    <div style={{ fontSize:10, color:C.textM, marginTop:4 }}>
                      {openWOs.map(w => w.description).join(" · ")}
                    </div>
                  </div>
                  <div style={{ display:"flex", flexDirection:"column", alignItems:"center", gap:4, flexShrink:0 }}>
                    {detailOnly
                      ? <span style={{ background:C.green, color:"#fff", borderRadius:20, padding:"2px 8px", fontSize:11, fontWeight:800 }}>🧹!</span>
                      : <span style={{ background:C.red, color:"#fff", borderRadius:20, padding:"2px 8px", fontSize:11, fontWeight:800 }}>!</span>}
                    <span style={{ fontSize:9, color:C.textM }}>Tap to open</span>
                  </div>
                </div>
              );
            })}
          </div>
        </Modal>
      )}

      {/* List view */}
      {fleetView === "list" && (
        <div style={{ display:"flex",flexDirection:"column",gap:6 }}>
          {sorted.length === 0 && <div style={{ textAlign:"center",color:C.textM,padding:40,fontSize:12 }}>No vehicles in recon</div>}
          {sorted.map(v => {
            const openWOs = (v.workOrders||[]).filter(w => w.status !== "COMPLETE");
            const highPri = openWOs.some(w => w.priority === "HIGH");
            const isDetailOnlyV = hasOpenDetail(v) && !hasOpenNonDetail(v);
            const detailWO = openWOs.find(w => w.category==="DETAIL");
            const detailStart = detailWO?.createdAt || v.arrivalDate;
            const isDetailOverdue = isDetailOnlyV && businessDaysBetween(detailStart, todayStr()) > 5;
            const isOverdue = !isDetailOnlyV && daysSince(v.arrivalDate) > 10 && hasOpenNonDetail(v);
            const stageDef = STAGES.find(s => s.id === v.stage);
            const expanded = expandedId === v.id;
            return (
              <div key={v.id} style={{ background:isDetailOnlyV?"#f0fdf4":C.surface,border:`2px solid ${highPri?C.amber+"50":isDetailOnlyV?C.green+"40":C.border}`,borderRadius:10,overflow:"hidden" }}>
                {/* Single-line summary — tap to expand */}
                <div onClick={() => setExpandedId(expanded ? null : v.id)}
                  style={{ padding:"10px 14px",display:"flex",alignItems:"center",gap:10,cursor:"pointer" }}>
                  <div style={{ width:8,height:8,borderRadius:"50%",background:stageDef?.color||C.textM,flexShrink:0 }} />
                  <div style={{ flex:1,minWidth:0 }}>
                    <div style={{ fontSize:13,fontWeight:700,color:"#000",whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis" }}>
                      {v.year} {v.make} {v.model}
                    </div>
                    <div style={{ fontSize:9,color:C.textM }}>
                      #{v.stockNumber} · {stageDef?.label||v.stage}
                      {openWOs.length > 0 ? ` · ${openWOs.length} open WO${openWOs.length!==1?"s":""}` : " · ✓ Done"}
                      
                    </div>
                  </div>
                  <div style={{ display:"flex",gap:4,flexShrink:0 }}>
                    {isDetailOnlyV && <span style={{ background:C.green,color:"#fff",borderRadius:20,padding:"1px 7px",fontSize:9,fontWeight:800 }}>{isDetailOverdue?"!":""} 🧹</span>}
                  
                    {openWOs.length > 0 && <span style={{ background:C.blue+"20",color:C.blue,borderRadius:20,padding:"1px 7px",fontSize:9,fontWeight:800 }}>{openWOs.length}</span>}
                    <span style={{ color:C.textM,fontSize:14 }}>{expanded?"▲":"▼"}</span>
                  </div>
                </div>
                {/* Expanded detail */}
                {expanded && (
                  <div style={{ borderTop:`1px solid ${C.border}`,padding:"10px 14px",background:C.s2 }}>
                    <div style={{ display:"flex",gap:8,marginBottom:8 }}>
                      <button onClick={() => openVehicle(v)}
                        style={{ flex:1,background:C.blue,border:"none",borderRadius:8,padding:"8px",cursor:"pointer",fontSize:11,fontWeight:800,color:"#fff",fontFamily:"inherit" }}>
                        Open Vehicle →
                      </button>
                      <button onClick={() => (navigateTo||setView)("schedule")}
                        style={{ background:C.s2,border:`1px solid ${C.border}`,borderRadius:8,padding:"8px 10px",cursor:"pointer",fontSize:10,fontWeight:700,color:C.textM,fontFamily:"inherit" }}>
                        📅 Schedule
                      </button>
                    </div>
                    {openWOs.length > 0 && openWOs.map((wo,i) => (
                      <div key={wo.id} style={{ fontSize:11,color:C.text,padding:"4px 0",borderBottom:i<openWOs.length-1?`1px solid ${C.border}`:"none",display:"flex",justifyContent:"space-between" }}>
                        <span>{wo.description}</span>
                        <span style={{ color:wo.priority==="HIGH"?C.red:C.textM,fontSize:9,fontWeight:700 }}>{wo.priority}</span>
                      </div>
                    ))}
                    {daysSince(v.arrivalDate) > 0 && <div style={{ fontSize:10,color:C.textM,marginTop:6 }}>
                      {daysSince(v.arrivalDate)} days in recon
                    </div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ── LINE READY VIEW ── */}
      {fleetView === "lineready" && (
        <div>
          {lineReady.length === 0
            ? <div style={{ textAlign:"center",color:C.textM,padding:40,fontSize:12 }}>No vehicles on the line yet</div>
            : lineReady.map(v => (
              <div key={v.id} style={{ background:C.green+"12",border:`2px solid ${C.green}40`,borderRadius:10,padding:"12px 16px",marginBottom:8,display:"flex",justifyContent:"space-between",alignItems:"center" }}>
                <div>
                  <div style={{ fontSize:13,fontWeight:800,color:"#000" }}>{v.year} {v.make} {v.model}{v.trim?" "+v.trim:""}</div>
                  <div style={{ fontSize:10,color:C.textM,marginTop:2 }}>#{v.stockNumber} · Line Ready {v.lineReadyAt?`· ${fmtDate(v.lineReadyAt)}`:""}</div>
                  {v.detailComplete && <div style={{ fontSize:9,color:C.green,fontWeight:700 }}>✓ Detail complete</div>}
                </div>
                <button onClick={() => openVehicle(v)} style={{ background:C.green,border:"none",borderRadius:8,padding:"7px 14px",cursor:"pointer",fontSize:11,fontWeight:700,color:"#fff",fontFamily:"inherit" }}>Open →</button>
              </div>
            ))
          }
        </div>
      )}

      {/* ── PIPELINE VIEW ── */}
      {fleetView === "pipeline" && (() => {
        // Color-code by readiness
        const classify = (v) => {
          const stageDef = STAGES.find(s => s.id === v.stage);
          const openWOs = (v.workOrders||[]).filter(w => w.status !== "COMPLETE" && !w.deleted);
          if (!openWOs.length) return "done";
          const vParts = parts.filter(p => p.vehicleId === v.id && !["RETURNED","CANCELLED"].includes(p.status));
          const orderedParts = vParts.filter(p => ["Ordered","In Transit"].includes(p.status));
          const receivedParts = vParts.filter(p => ["Received","Installed"].includes(p.status));
          const days = daysSince(v.arrivalDate);
          // Only "partsIn" if there are received parts AND no parts still on order
          const partsIn = receivedParts.length > 0 && orderedParts.length === 0;
          const partsOrdered = orderedParts.length > 0;
          if (partsIn && days > 10) return "overdue";
          if (partsIn) return "ready";
          if (partsOrdered) return "waiting";
          return "stage"; // show by stage — no parts involved yet
        };
        const colorMap = { ready: C.green, waiting: "#eab308", stage: "#6366f1", overdue: C.red, done: C.textM };
        const bgMap    = { ready: C.green+"12", waiting: "#eab30815", stage: "#6366f110", overdue: C.red+"12", done: C.s2 };
        const labelMap = { ready: "✅ Parts In — Ready to Work", waiting: "⏳ Waiting on Parts", stage: "🔄 In Recon", overdue: "🔥 Overdue (10+ Days)", done: "✓ All WOs Complete" };
        const order    = ["overdue","ready","waiting","stage","done"];

        const classified = sorted.map(v => ({ v, cls: classify(v) }));
        const groups = order.map(cls => ({ cls, label: labelMap[cls], color: colorMap[cls], bg: bgMap[cls], items: classified.filter(x => x.cls === cls).map(x => x.v) })).filter(g => g.items.length > 0);

        return (
          <div>
            {groups.map(g => (
              <div key={g.cls} style={{ marginBottom: 18 }}>
                <div style={{ display:"flex",alignItems:"center",gap:8,marginBottom:8,borderBottom:`2px solid ${g.color}40`,paddingBottom:5 }}>
                  <div style={{ width:10,height:10,borderRadius:"50%",background:g.color,flexShrink:0 }} />
                  <div style={{ fontSize:12,fontWeight:800,color:g.color,textTransform:"uppercase",letterSpacing:"0.07em" }}>{g.label}</div>
                  <div style={{ fontSize:10,color:C.textM }}>— {g.items.length} vehicle{g.items.length!==1?"s":""}</div>
                </div>
                {g.items.map(v => {
                  const openWOs = (v.workOrders||[]).filter(w => w.status!=="COMPLETE"&&!w.deleted);
                  const days = daysSince(v.arrivalDate);
                  const stageDef = STAGES.find(s => s.id === v.stage);
                  return (
                    <div key={v.id} onClick={() => openVehicle(v)}
                      style={{ background:g.bg, border:`1px solid ${g.color}40`, borderLeft:`4px solid ${g.color}`, borderRadius:8, padding:"10px 14px", marginBottom:6, cursor:"pointer", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:13,fontWeight:700,color:"#000" }}>{v.year} {v.make} {v.model}</div>
                        <div style={{ fontSize:9,color:C.textM }}>#{v.stockNumber} · {stageDef?.label||v.stage} · {days}d in recon</div>
                        {openWOs.slice(0,2).map((wo,i) => <div key={i} style={{ fontSize:10,color:C.textM,marginTop:1 }}>· {wo.description}</div>)}
                        {openWOs.length > 2 && <div style={{ fontSize:9,color:C.textM }}>+{openWOs.length-2} more</div>}
                      </div>
                      <ChevronRight size={14} color={g.color} />
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        );
      })()}

      {/* ── CALENDAR VIEW ── */}
      {fleetView === "calendar" && (
        <ReconCalendarView
          schedule={schedule || {}}
          setSchedule={setSchedule || (() => {})}
          vehicles={vehicles}
          parts={parts}
          staff={staff || []}
          onBack={() => setFleetView("list")}
          onUpdateVehicle={onUpdateVehicle}
          openVehicle={openVehicle}
          onRebuildSchedule={null}
        />
      )}
    </div>
  );
};


// ─── DIAGNOSE VIEW ────────────────────────────────────────────────────────────
const DiagnoseView = ({ vehicles, inventoryCache, currentUser, onBack, navigateTo, setView, openVehicle, onUpdateVehicle, parts, staff, vendors, ledger, onAddPart, onAddLedger, onSaveGuestDiag }) => {
  const [selectedVehicleId, setSelectedVehicleId] = useState("");
  const [guestVIN, setGuestVIN] = useState("");
  const [guestMiles, setGuestMiles] = useState("");
  const [mode, setMode] = useState("inventory"); // inventory | guest
  const [launching, setLaunching] = useState(null); // { bot, vehicle, isGuest }
  const [postDiagWO, setPostDiagWO] = useState(null); // { vehicle, newWOs } — after diagnosis completes

  const allInventoryVehicles = vehicles.filter(v => !["SOLD","SCRAPPED"].includes(v.stage));
  const cacheOnly = (inventoryCache?.vehicles || []).filter(iv => !vehicles.some(v => String(v.stockNumber) === String(iv.stockNumber)));

  const selectedVehicle = allInventoryVehicles.find(v => v.id === selectedVehicleId)
    || cacheOnly.find(iv => "cache_" + iv.stockNumber === selectedVehicleId);

  const [vinDecoding, setVinDecoding] = useState(false);

  const handleBotSelect = (botId) => {
    if (mode === "inventory" && selectedVehicleId) {
      const v = allInventoryVehicles.find(x => x.id === selectedVehicleId)
        || { ...cacheOnly.find(iv => "cache_" + iv.stockNumber === selectedVehicleId), id: "cache_" + cacheOnly.find(iv => "cache_" + iv.stockNumber === selectedVehicleId)?.stockNumber, workOrders: [], stage: "INTAKE" };
      setLaunching({ bot: botId, vehicle: v, isGuest: false });
    } else {
      // No vehicle selected — open with blank vehicle, Hank will ask
      const inventoryList = allInventoryVehicles
        .map(v => `#${v.stockNumber} ${v.year} ${v.make} ${v.model}${v.trim?" "+v.trim:""}${v.mileage?" ("+parseInt(v.mileage).toLocaleString()+"mi)":""}`)
        .join("\n");
      const blankVehicle = {
        id: "guest_" + Date.now(),
        year: "", make: "", model: "", trim: "",
        mileage: "", transmission: "", drivetrain: "",
        stockNumber: "GUEST", stage: "INTAKE", workOrders: [],
        isGuest: true, _inventoryList: inventoryList,
      };
      setLaunching({ bot: botId, vehicle: blankVehicle, isGuest: true });
    }
  };

  // After diagnosis completes — ask what to do with WOs
  const handleDiagnosisComplete = (updatedVehicle, newWOs) => {
    setLaunching(null);
    if (launching?.isGuest) {
      // Guest: save to 14-day storage and show attach-to-inventory option
      if (onSaveGuestDiag && (updatedVehicle.year || updatedVehicle.make)) {
        onSaveGuestDiag({ ...updatedVehicle, savedAt: new Date().toISOString(), workOrders: newWOs||[] });
      }
      setPostDiagWO({ vehicle: updatedVehicle, newWOs: newWOs || [], isGuest: true });
    } else {
      // Real vehicle: ask attach to existing WO or create new
      setPostDiagWO({ vehicle: updatedVehicle, newWOs: newWOs || [], isGuest: false });
    }
  };

  // Launching a bot — render DiagnosticsView directly
  if (launching) {
    const lv = launching.vehicle;
    const hasVehicle = lv.year || lv.make;
    return (
      <div>
        <button onClick={() => setLaunching(null)}
          style={{ background:"none",border:"none",color:C.blue,cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"inherit",display:"flex",alignItems:"center",gap:4,marginBottom:8,padding:0 }}>
          <img src="/images/btn-back-arrow.jpg" alt="" style={{width:16,height:16,objectFit:"contain",flexShrink:0}} onError={e=>e.target.style.display="none"} /> Back
        </button>
        {/* Live vehicle identity bar */}
        <div style={{ background: hasVehicle ? C.s2 : C.amber+"15", border:`1px solid ${hasVehicle?C.border:C.amber+"50"}`, borderRadius:8, padding:"8px 14px", marginBottom:12 }}>
          {hasVehicle ? (
            <div>
              <div style={{ fontSize:13,fontWeight:800,color:"#000" }}>
                Diagnosing: {lv.year} {lv.make} {lv.model}{lv.trim?" "+lv.trim:""}
                {lv.stockNumber && lv.stockNumber!=="GUEST" && lv.stockNumber!==""
                  ? <span style={{ fontSize:10,color:C.textM,marginLeft:8 }}>#{lv.stockNumber}</span>
                  : <span style={{ fontSize:10,color:C.amber,marginLeft:8 }}>Guest vehicle</span>}
              </div>
              {(lv.transmission||lv.drivetrain||lv.mileage) && (
                <div style={{ fontSize:10,color:C.textM,marginTop:2 }}>
                  {[lv.transmission,lv.drivetrain,lv.mileage?parseInt(lv.mileage).toLocaleString()+"mi":null].filter(Boolean).join(" · ")}
                </div>
              )}
            </div>
          ) : null}
        </div>
        <DiagnosticsView
          vehicles={[lv, ...vehicles]}
          parts={parts} staff={staff} vendors={vendors} ledger={ledger}
          onUpdateVehicle={(v) => {
            if (!launching.isGuest) onUpdateVehicle(v);
            // Update the header live as Hank identifies the vehicle
            setLaunching(prev => prev ? { ...prev, vehicle: { ...prev.vehicle, ...v } } : prev);
          }}
          onAddPart={onAddPart} onAddLedger={onAddLedger}
          openVehicle={(v) => { handleDiagnosisComplete(v, v.workOrders); }}
          preselectedVehicle={lv}
          preselectedMode={launching.bot}
        />
      </div>
    );
  }

  // Post-diagnosis: attach to WO or create new
  if (postDiagWO) {
    const { vehicle, newWOs, isGuest } = postDiagWO;
    const openWOs = (vehicle.workOrders || []).filter(w => w.status !== "COMPLETE");
    return (
      <div>
        <div style={{ fontSize:20,fontWeight:900,color:"#000",fontFamily:"'Bebas Neue',sans-serif",letterSpacing:"0.06em",marginBottom:4 }}>✅ DIAGNOSIS COMPLETE</div>
        <div style={{ fontSize:11,color:C.textM,marginBottom:16 }}>
          {vehicle.year} {vehicle.make} {vehicle.model} {vehicle.vin ? `· ${vehicle.vin.slice(-6)}` : ""}
        </div>
        {newWOs?.length > 0 && (
          <div style={{ background:C.s2,border:`1px solid ${C.border}`,borderRadius:10,padding:"12px 14px",marginBottom:14 }}>
            <div style={{ fontSize:10,fontWeight:800,color:"#000",textTransform:"uppercase",marginBottom:8 }}>Findings ({newWOs.length} work order{newWOs.length!==1?"s":""})</div>
            {newWOs.slice(0,5).map((wo,i) => (
              <div key={i} style={{ fontSize:11,color:C.text,padding:"3px 0",borderBottom:i<Math.min(newWOs.length,5)-1?`1px solid ${C.border}`:"none" }}>{wo.description}</div>
            ))}
          </div>
        )}
        {isGuest ? (
          <div style={{ display:"flex",flexDirection:"column",gap:10 }}>
            <div style={{ fontSize:11,color:C.textM,marginBottom:4 }}>This was a guest diagnosis. What would you like to do?</div>
            <button onClick={() => { /* save to trash/pending */ setPostDiagWO(null); onBack(); }}
              style={{ background:C.green,border:"none",borderRadius:10,padding:"12px",cursor:"pointer",fontSize:13,fontWeight:800,color:"#fff",fontFamily:"inherit" }}>
              💾 Save Diagnosis (held 7 days)
            </button>
            <button onClick={() => { setPostDiagWO(null); onBack(); }}
              style={{ background:"none",border:`1px solid ${C.border}`,borderRadius:10,padding:"12px",cursor:"pointer",fontSize:13,fontWeight:700,color:C.textM,fontFamily:"inherit" }}>
              🗑 Discard Diagnosis
            </button>
          </div>
        ) : (
          <div style={{ display:"flex",flexDirection:"column",gap:10 }}>
            <div style={{ fontSize:11,color:C.textM,marginBottom:4 }}>Attach these findings to this vehicle. What would you like to do?</div>
            {openWOs.length > 0 && (
              <div>
                <div style={{ fontSize:10,fontWeight:700,color:C.textM,textTransform:"uppercase",marginBottom:6 }}>Attach to Existing Work Order</div>
                {openWOs.map(wo => (
                  <button key={wo.id} onClick={() => {
                    const updated = { ...vehicle, workOrders: vehicle.workOrders.map(w => w.id===wo.id ? { ...w, diagnosisNotes: (newWOs||[]).map(n=>n.description).join("; "), diagnosedAt: todayStr() } : w) };
                    onUpdateVehicle(updated);
                    openVehicle(updated);
                    setPostDiagWO(null);
                  }}
                    style={{ display:"block",width:"100%",background:C.s2,border:`1px solid ${C.blue}40`,borderRadius:9,padding:"10px 14px",cursor:"pointer",fontSize:11,fontWeight:700,color:C.text,fontFamily:"inherit",textAlign:"left",marginBottom:6 }}>
                    📋 {wo.description}
                  </button>
                ))}
              </div>
            )}
            <button onClick={() => {
              const wosToAdd = (newWOs||[]).map(w => ({ ...w, id: genId(), status:"OPEN", createdAt: todayStr(), actualHours:0 }));
              const updated = { ...vehicle, workOrders: [...(vehicle.workOrders||[]), ...wosToAdd] };
              onUpdateVehicle(updated);
              openVehicle(updated);
              setPostDiagWO(null);
            }}
              style={{ background:C.amber,border:"none",borderRadius:10,padding:"12px",cursor:"pointer",fontSize:13,fontWeight:800,color:"#000",fontFamily:"inherit" }}>
              ➕ Create New Work Order{newWOs?.length > 1 ? "s" : ""}
            </button>
            <button onClick={() => { openVehicle(vehicle); setPostDiagWO(null); }}
              style={{ background:"none",border:`1px solid ${C.border}`,borderRadius:10,padding:"10px",cursor:"pointer",fontSize:12,fontWeight:700,color:C.textM,fontFamily:"inherit" }}>
              Open Vehicle Without Attaching
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <button onClick={onBack} style={{ background:"none",border:"none",color:C.blue,cursor:"pointer",fontSize:12,fontWeight:700,fontFamily:"inherit",display:"flex",alignItems:"center",gap:4,marginBottom:14,padding:0 }}>
        <img src="/images/btn-back-arrow.jpg" alt="" style={{width:16,height:16,objectFit:"contain",flexShrink:0}} onError={e=>e.target.style.display="none"} /> Home
      </button>
      <div style={{ fontSize:26,fontWeight:900,color:"#000",fontFamily:"'Bebas Neue',sans-serif",letterSpacing:"0.06em",marginBottom:4 }}>🔬 DIAGNOSE</div>
      <div style={{ fontSize:11,color:C.textM,marginBottom:16 }}>Pick a vehicle from inventory, or skip the selector and tell Hank directly in the chat window</div>

      {/* Optional vehicle picker — skip to open Hank without one */}
      <div style={{ marginBottom:14 }}>
        <div style={{ fontSize:10,fontWeight:700,color:C.textM,textTransform:"uppercase",marginBottom:6 }}>Select from inventory (optional — or let Hank ask)</div>
        <select value={selectedVehicleId} onChange={e => { setSelectedVehicleId(e.target.value); setMode(e.target.value ? "inventory" : "guest"); }}
          style={{ width:"100%",background:C.s2,border:`1px solid ${C.amber}`,borderRadius:9,padding:"10px 12px",color:C.text,fontSize:12,fontFamily:"inherit",outline:"none" }}>
          <option value="">— Skip — tell Hank the vehicle in the chat —</option>
          <optgroup label="In Recon">
            {allInventoryVehicles.map(v => (
              <option key={v.id} value={v.id}>{v.year} {v.make} {v.model} #{v.stockNumber}</option>
            ))}
          </optgroup>
          {cacheOnly.length > 0 && (
            <optgroup label="In Inventory (not yet in recon)">
              {cacheOnly.map(iv => (
                <option key={"cache_"+iv.stockNumber} value={"cache_"+iv.stockNumber}>{iv.year} {iv.make} {iv.model} #{iv.stockNumber}</option>
              ))}
            </optgroup>
          )}
        </select>
        {!selectedVehicleId && (
          <div style={{ fontSize:10,color:C.textM,marginTop:6,fontStyle:"italic" }}>
            💬 Just open Hank and tell him — "2016 Nissan Murano CVT" or "stock 12287" or "not in inventory, 1999 Pontiac Grand Am SE automatic"
          </div>
        )}
      </div>



      {/* Bot selector */}
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
        <SiteLogo size="sm" />
        <div style={{ fontSize:10,fontWeight:700,color:C.textM,textTransform:"uppercase" }}>Select Diagnostic AI</div>
      </div>
      {vinDecoding && (
        <div style={{ background:C.blue+"12",border:`1px solid ${C.blue}30`,borderRadius:10,padding:"12px 16px",marginBottom:10,fontSize:12,color:C.blue,fontWeight:700 }}>
          🔍 Decoding VIN {guestVIN}… looking up year, make, model, trim, transmission…
        </div>
      )}
      <div style={{ display:"flex",flexDirection:"column",gap:8 }}>
        {[
          { id:"hank",  name:"Hank",  role:"Mechanical Specialist", desc:"Engine · drivetrain · suspension · electrical", color:C.blue,   emoji:"🔧" },
          { id:"julia", name:"Julia", role:"Full Inspection",        desc:"Visual inspection · condition · all systems",  color:C.purple, emoji:"🔍" },
          { id:"jimmy", name:"Jimmy", role:"Body & Paint",           desc:"Dents · scratches · paint · collision damage", color:"#f97316",emoji:"🎨" },
        ].map(bot => (
          <div key={bot.id} onClick={() => !vinDecoding && handleBotSelect(bot.id)}
            style={{ background:bot.color+"12",border:`2px solid ${bot.color}${vinDecoding?"20":"40"}`,borderRadius:12,padding:"14px 16px",cursor:vinDecoding?"not-allowed":"pointer",display:"flex",alignItems:"center",gap:14,opacity:vinDecoding?0.5:1 }}>
            <div style={{ fontSize:28 }}>{bot.emoji}</div>
            <div>
              <div style={{ fontSize:14,fontWeight:800,color:bot.color,fontFamily:"'Bebas Neue',sans-serif",letterSpacing:"0.04em" }}>{bot.name}</div>
              <div style={{ fontSize:11,color:C.text,fontWeight:600 }}>{bot.role}</div>
              <div style={{ fontSize:10,color:C.textM }}>{bot.desc}</div>
            </div>
            <ChevronRight size={16} color={bot.color} style={{ marginLeft:"auto" }} />
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── PHOTO READY VIEW ────────────────────────────────────────────────────────────
const PhotoReadyView = ({ vehicles, onUpdateVehicle, onBack }) => {
  const photoReady = vehicles.filter(v => v.stage === "PHOTO_READY").sort((a,b) => (a.photoReadyAt||"").localeCompare(b.photoReadyAt||""));

  return (
    <div>
      <button onClick={onBack} style={{ background: "none", border: "none", color: C.blue, cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 4, marginBottom: 16, padding: 0 }}>
        <img src="/images/btn-back-arrow.jpg" alt="" style={{width:16,height:16,objectFit:"contain",flexShrink:0}} onError={e=>e.target.style.display="none"} /> Back
      </button>
      <div style={{ fontSize: 26, fontWeight: 800, color: "#ec4899", fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.06em", marginBottom: 4 }}>📷 PHOTO READY</div>
      <div style={{ fontSize: 11, color: C.textM, marginBottom: 16 }}>Vehicles that have completed detail and are ready to be photographed for advertising</div>

      {photoReady.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: C.textM, fontSize: 12 }}>No vehicles in the photo ready queue</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {photoReady.map(v => {
            const hoursReady = v.photoReadyAt ? Math.floor((Date.now() - new Date(v.photoReadyAt + "T00:00:00").getTime()) / 3600000) : 0;
            const overdue = hoursReady >= 24;
            const color = v.photographed ? C.green : overdue ? C.red : "#ec4899";
            return (
              <div key={v.id} style={{ background: C.surface, border: `2px solid ${color}40`, borderLeft: `5px solid ${color}`, borderRadius: 10, padding: "12px 16px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 14, fontWeight: 800, color: "#000" }}>{v.year} {v.make} {v.model}</div>
                    <div style={{ fontSize: 10, color: C.textM }}>#{v.stockNumber} · Ready since {v.photoReadyAt ? fmtDate(v.photoReadyAt) : "today"}</div>
                    {overdue && !v.photographed && (
                      <div style={{ fontSize: 10, color: C.red, fontWeight: 700, marginTop: 2 }}>⚠️ {hoursReady}h since detail — overdue for photos!</div>
                    )}
                  </div>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer", flexShrink: 0 }}>
                    <input type="checkbox" checked={!!v.photographed} onChange={e => {
                      if (e.target.checked) {
                        onUpdateVehicle({ ...v, photographed: true, photographedAt: todayStr(), stage: "LINE_READY" });
                      } else {
                        onUpdateVehicle({ ...v, photographed: false, photographedAt: null });
                      }
                    }} style={{ accentColor: C.green, width: 18, height: 18 }} />
                    <span style={{ fontSize: 11, fontWeight: 700, color: v.photographed ? C.green : C.textM }}>
                      {v.photographed ? "✓ Advertised" : "Has this been advertised?"}
                    </span>
                  </label>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── INTAKE METHOD CHOOSER ────────────────────────────────────────────────────
const IntakeMethodChooser = ({ onChoose, onClose }) => (
  <Modal title="Add Part / Tool / Supply" onClose={onClose} wide>
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {[
        { id: "add",     icon: "✏️", label: "Manual Entry",   desc: "Type in the details yourself" },
        { id: "barcode", icon: "🔍", label: "Scan Barcode",   desc: "Scan UPC or part number barcode" },
        { id: "scan",    icon: "🧾", label: "Scan Invoice",   desc: "Photo or upload an invoice/receipt — extracts all line items" },
        { id: "roxy",    icon: "🛒", label: "Ask Roxy",       desc: "Describe what you need — Roxy finds it and stocks it in" },
      ].map(m => (
        <button key={m.id} onClick={() => onChoose(m.id)}
          style={{ background: C.s2, border: `1px solid ${C.border}`, borderRadius: 12, padding: "14px 16px", cursor: "pointer", fontFamily: "inherit", textAlign: "left", display: "flex", alignItems: "center", gap: 14 }}>
          <span style={{ fontSize: 28, flexShrink: 0 }}>{m.icon}</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 800, color: "#000" }}>{m.label}</div>
            <div style={{ fontSize: 11, color: C.textM, marginTop: 2 }}>{m.desc}</div>
          </div>
          <span style={{ marginLeft: "auto", color: C.textM, fontSize: 16 }}>→</span>
        </button>
      ))}
    </div>
  </Modal>
);



// ─── BODY REPAIRS VIEW ───────────────────────────────────────────────────────

const GORDOS_TOW = { name: "Gordo's Towing", phone: "5038579242", displayPhone: "(503) 857-9242" };

const TowRequestModal = ({ vehicle, vendors, currentUser, onClose, isSoldVehicle, onRecordTowCost: _onRecordTowCost }) => {
  const [step, setStep] = useState("tow"); // tow | customer
  const [pickupType, setPickupType] = useState("preset"); // preset | custom
  const [pickup, setPickup] = useState(vehicle.currentLocation || "main_lot");
  const [customPickup, setCustomPickup] = useState("");
  const [dropoff, setDropoff] = useState("mech_shop");
  const [towSent, setTowSent] = useState(false);
  const [towCost, setTowCost] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [etaMinutes, setEtaMinutes] = useState("30");
  const [customerSent, setCustomerSent] = useState(false);

  const getLocLabel = (id) => TOW_LOCATIONS.find(l => l.id === id)?.label?.replace(/.*— /, "") || id;
  const getLocFull = (id) => TOW_LOCATIONS.find(l => l.id === id)?.label || id;
  const pickupAddr = pickupType === "custom" ? customPickup : getLocLabel(pickup);
  const pickupFull = pickupType === "custom" ? customPickup : getLocFull(pickup);
  const dropFull = getLocFull(dropoff);
  const userName = currentUser?.name || currentUser?.username || "Edgewater Staff";
  const vehicleDesc = `${vehicle.year} ${vehicle.make} ${vehicle.model} (Stock #${vehicle.stockNumber})`;

  const towMsg = `Hi, this is ${userName} from Edgewater Auto Center. Can you please pick up a ${vehicleDesc} at ${pickupAddr} and take it to ${dropFull.replace(/.*— /, "")}?`;

  const sendToGordos = () => {
    window.open(`sms:${GORDOS_TOW.phone}?body=${encodeURIComponent(towMsg)}`, "_self");
    setTowSent(true);
  };

  const onRecordTowCost = () => {
    if (_onRecordTowCost && parseFloat(towCost) > 0) {
      _onRecordTowCost(parseFloat(towCost));
      setTowCost("");
    }
  };

  const sendToCustomer = () => {
    const eta = parseInt(etaMinutes) || 30;
    const low = eta - 5;
    const high = eta + 5;
    const custMsg = `Hi, this is Edgewater Auto Center. Your vehicle (${vehicle.year} ${vehicle.make} ${vehicle.model}) will be picked up by our tow service in approximately ${low}-${high} minutes. If the driver has not arrived within that window, please give us a call. Thank you!`;
    window.open(`sms:${customerPhone.replace(/\D/g,"")}?body=${encodeURIComponent(custMsg)}`, "_self");
    setCustomerSent(true);
  };

  return (
    <div>
      <div style={{ background: C.red + "12", border: `1px solid ${C.red}40`, borderRadius: 8, padding: "10px 14px", marginBottom: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: C.red }}>🚛 Tow Request — {vehicleDesc}</div>
        {vehicle.unsafeToDrive && <div style={{ fontSize: 11, color: C.red, marginTop: 3 }}>⚠ UNSAFE TO DRIVE — {vehicle.unsafeToDriveReason || "Do not drive"}</div>}
      </div>

      {step === "tow" && (
        <div>
          {/* Pickup */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 6 }}>Pickup Location</div>
            <div style={{ display: "flex", gap: 6, marginBottom: 8 }}>
              <button onClick={() => setPickupType("preset")} style={{ flex: 1, background: pickupType === "preset" ? C.blue + "20" : C.s2, border: `1px solid ${pickupType === "preset" ? C.blue : C.border}`, borderRadius: 7, padding: "6px", cursor: "pointer", fontSize: 11, fontWeight: 700, color: pickupType === "preset" ? C.blue : C.text, fontFamily: "inherit" }}>Our Location</button>
              <button onClick={() => setPickupType("custom")} style={{ flex: 1, background: pickupType === "custom" ? C.blue + "20" : C.s2, border: `1px solid ${pickupType === "custom" ? C.blue : C.border}`, borderRadius: 7, padding: "6px", cursor: "pointer", fontSize: 11, fontWeight: 700, color: pickupType === "custom" ? C.blue : C.text, fontFamily: "inherit" }}>Custom Address</button>
            </div>
            {pickupType === "preset" ? (
              <select value={pickup} onChange={e => setPickup(e.target.value)}
                style={{ width: "100%", background: C.s2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 12px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none" }}>
                {TOW_LOCATIONS.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
              </select>
            ) : (
              <input value={customPickup} onChange={e => setCustomPickup(e.target.value)}
                placeholder="Enter full address"
                style={{ width: "100%", background: C.s2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 12px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none" }} />
            )}
          </div>

          {/* Drop-off */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 6 }}>Drop-off Location</div>
            <select value={dropoff} onChange={e => setDropoff(e.target.value)}
              style={{ width: "100%", background: C.s2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 12px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none" }}>
              {TOW_LOCATIONS.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
            </select>
          </div>

          {/* Preview message */}
          <div style={{ background: C.s2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", marginBottom: 14, fontSize: 11, color: C.text, fontStyle: "italic", lineHeight: 1.5 }}>
            📱 Message to {GORDOS_TOW.name} ({GORDOS_TOW.displayPhone}):<br/>
            <strong style={{ fontStyle: "normal" }}>{towMsg}</strong>
          </div>

          {towSent ? (
            <div>
              <div style={{ background: C.green + "12", border: `1px solid ${C.green}40`, borderRadius: 8, padding: "10px 14px", fontSize: 11, color: C.green, marginBottom: 10 }}>
                ✓ Tow request sent to {GORDOS_TOW.name}!
              </div>
              {/* Tow cost entry */}
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 4 }}>Tow Cost ($)</div>
                <input type="number" min="0" step="1" value={towCost} onChange={e => setTowCost(e.target.value)}
                  placeholder="e.g. 150"
                  style={{ width: "100%", background: C.s2, border: `1px solid ${C.border}`, borderRadius: 7, padding: "7px 10px", color: C.text, fontSize: 13, fontFamily: "inherit", outline: "none", boxSizing: "border-box", marginBottom: 8 }} />
                {towCost && parseFloat(towCost) > 0 && (
                  <button onClick={onRecordTowCost} style={{ background: C.amber, border: "none", borderRadius: 7, padding: "6px 14px", cursor: "pointer", fontSize: 11, fontWeight: 700, color: "#000", fontFamily: "inherit" }}>
                    💾 Record Tow Cost — {fmtMoney(parseFloat(towCost))}
                  </button>
                )}
              </div>
              {isSoldVehicle && !customerSent && (
                <Btn variant="blue" onClick={() => setStep("customer")}>📱 Notify Customer →</Btn>
              )}
            </div>
          ) : (
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={sendToGordos}
                style={{ background: C.red, border: "none", borderRadius: 8, padding: "10px 18px", cursor: "pointer", fontSize: 12, fontWeight: 700, color: "#fff", fontFamily: "inherit" }}>
                📱 Text {GORDOS_TOW.name}
              </button>
              <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
            </div>
          )}
        </div>
      )}

      {step === "customer" && (
        <div>
          <div style={{ background: C.amber + "12", border: `1px solid ${C.amber}40`, borderRadius: 8, padding: "10px 14px", marginBottom: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 800, color: C.amber, marginBottom: 4 }}>📋 Customer Notification</div>
            <div style={{ fontSize: 11, color: C.text }}>⚠️ <strong>Use the business phone</strong> to send this message for privacy reasons.</div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 4 }}>Customer's Phone Number</div>
            <input value={customerPhone} onChange={e => setCustomerPhone(e.target.value)}
              placeholder="(503) 555-0000" type="tel"
              style={{ width: "100%", background: C.s2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 12px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none" }} />
          </div>

          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 4 }}>ETA (minutes until tow arrives)</div>
            <input value={etaMinutes} onChange={e => setEtaMinutes(e.target.value)}
              type="number" min="5" max="180" placeholder="30"
              style={{ width: "100%", background: C.s2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 12px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none" }} />
          </div>

          {customerPhone.replace(/\D/g,"").length >= 10 && (
            <div style={{ background: C.s2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", marginBottom: 14, fontSize: 11, color: C.text, fontStyle: "italic", lineHeight: 1.5 }}>
              📱 Message preview:<br/>
              <strong style={{ fontStyle: "normal" }}>Hi, this is Edgewater Auto Center. Your vehicle ({vehicle.year} {vehicle.make} {vehicle.model}) will be picked up by our tow service in approximately {Math.max(1,(parseInt(etaMinutes)||30)-5)}-{(parseInt(etaMinutes)||30)+5} minutes. If the driver has not arrived within that window, please give us a call. Thank you!</strong>
            </div>
          )}

          {customerSent ? (
            <div style={{ background: C.green + "12", border: `1px solid ${C.green}40`, borderRadius: 8, padding: "10px 14px", fontSize: 11, color: C.green }}>
              ✓ Customer notification sent!
            </div>
          ) : (
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={sendToCustomer} disabled={customerPhone.replace(/\D/g,"").length < 10}
                style={{ background: C.blue, border: "none", borderRadius: 8, padding: "10px 18px", cursor: "pointer", fontSize: 12, fontWeight: 700, color: "#fff", fontFamily: "inherit", opacity: customerPhone.replace(/\D/g,"").length < 10 ? 0.4 : 1 }}>
                📱 Text Customer
              </button>
              <Btn variant="ghost" onClick={() => setStep("tow")}>← Back</Btn>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const BodyRepairsView = ({ vehicles, vendors, onUpdateVehicle, openVehicle, currentUser }) => {
  const [selected, setSelected] = useState(null);
  const [towOpen, setTowOpen] = useState(null);
  const [assignShop, setAssignShop] = useState({});

  const bodyShops = vendors.filter(v => v.type === "VENDOR" || v.specialty?.toLowerCase().includes("body") || v.specialty?.toLowerCase().includes("paint") || v.name?.toLowerCase().includes("body") || v.name?.toLowerCase().includes("paint") || v.name?.toLowerCase().includes("glass"));

  const vehiclesWithBodyWork = vehicles.filter(v =>
    !["SOLD","SCRAPPED"].includes(v.stage) &&
    (v.bodyRepairs?.some(r => r.status === "OPEN") || v.workOrders?.some(w => w.isBodyWork && w.status !== "COMPLETE"))
  );

  const unsafeToDrive = vehicles.filter(v => v.unsafeToDrive && !["SOLD","SCRAPPED"].includes(v.stage));

  if (towOpen) return (
    <div>
      <button onClick={() => setTowOpen(null)} style={{ background: "none", border: "none", color: C.blue, cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 4, marginBottom: 16, padding: 0 }}>
        <img src="/images/btn-back-arrow.jpg" alt="" style={{width:16,height:16,objectFit:"contain",flexShrink:0}} onError={e=>e.target.style.display="none"} /> Back
      </button>
      <TowRequestModal vehicle={towOpen} vendors={vendors} currentUser={currentUser} onClose={() => setTowOpen(null)} />
    </div>
  );

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 26, fontWeight: 800, color: C.red, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.06em" }}>BODY REPAIRS</div>
        <div style={{ fontSize: 11, color: C.textM }}>{vehiclesWithBodyWork.length} vehicle{vehiclesWithBodyWork.length !== 1 ? "s" : ""} with open body work</div>
      </div>

      {/* Unsafe to drive alert */}
      {unsafeToDrive.length > 0 && (
        <div style={{ background: C.red + "12", border: `2px solid ${C.red}50`, borderRadius: 10, padding: "12px 14px", marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: C.red, marginBottom: 8 }}>🚫 UNSAFE TO DRIVE — Requires Immediate Tow</div>
          {unsafeToDrive.map(v => (
            <div key={v.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderBottom: `1px solid ${C.border}` }}>
              <div style={{ fontSize: 11, color: "#000" }}>{v.year} {v.make} {v.model} #{v.stockNumber}</div>
              <Btn variant="red" size="sm" onClick={() => setTowOpen(v)}>🚛 Request Tow</Btn>
            </div>
          ))}
        </div>
      )}

      {vehiclesWithBodyWork.length === 0 ? (
        <div style={{ textAlign: "center", color: C.textM, padding: 40 }}>No open body repairs.</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {vehiclesWithBodyWork.map(v => {
            const repairs = v.bodyRepairs?.filter(r => r.status === "OPEN") || [];
            const bodyWOs = (v.workOrders || []).filter(w => w.isBodyWork && w.status !== "COMPLETE");
            const needsShop = repairs.some(r => r.needsBodyShop) || bodyWOs.some(w => w.needsBodyShop);
            const isExpanded = selected === v.id;
            return (
              <div key={v.id} style={{ background: C.surface, border: `2px solid ${needsShop ? C.red + "50" : C.amber + "40"}`, borderRadius: 12, overflow: "hidden" }}>
                <div onClick={() => setSelected(isExpanded ? null : v.id)}
                  style={{ padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: "#000" }}>{v.year} {v.make} {v.model}</div>
                    <div style={{ fontSize: 10, color: "#555" }}>#{v.stockNumber} · {repairs.length + bodyWOs.length} repair item{repairs.length + bodyWOs.length !== 1 ? "s" : ""}</div>
                  </div>
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    {needsShop && <Badge color={C.red} small>Body Shop Needed</Badge>}
                    {v.unsafeToDrive && <Badge color={C.red} small>🚫 Unsafe</Badge>}
                    <span style={{ fontSize: 12, color: C.textM }}>{isExpanded ? "▲" : "▼"}</span>
                  </div>
                </div>
                {isExpanded && (
                  <div style={{ borderTop: `1px solid ${C.border}`, padding: "12px 16px" }}>
                    {repairs.map((r, i) => (
                      <div key={i} style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#000", marginBottom: 6 }}>{r.summary}</div>
                        {r.tasks?.map((t, j) => (
                          <div key={j} style={{ padding: "4px 0", borderBottom: `1px solid ${C.border}`, fontSize: 11 }}>
                            <div style={{ color: "#000" }}>{t.description}</div>
                            {t.parts?.length > 0 && <div style={{ fontSize: 10, color: C.textM }}>Parts: {t.parts.join(", ")}</div>}
                            {t.requiresShop && <Badge color={C.red} small>Body Shop</Badge>}
                          </div>
                        ))}
                      </div>
                    ))}

                    {needsShop && (
                      <div style={{ marginTop: 12 }}>
                        <div style={{ fontSize: 10, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 6 }}>Assign to Body Shop</div>
                        <select value={assignShop[v.id] || ""} onChange={e => setAssignShop(prev => ({ ...prev, [v.id]: e.target.value }))}
                          style={{ width: "100%", background: C.s2, border: `1px solid ${C.border}`, borderRadius: 6, padding: "6px 10px", color: C.text, fontSize: 11, fontFamily: "inherit", outline: "none", marginBottom: 8 }}>
                          <option value="">— Select body shop</option>
                          {bodyShops.map(v => <option key={v.id} value={v.id}>{v.name}</option>)}
                        </select>
                        {assignShop[v.id] && (
                          <Btn variant="amber" size="sm" onClick={() => {
                            onUpdateVehicle({ ...v, bodyRepairs: (v.bodyRepairs || []).map(r => ({ ...r, assignedShopId: assignShop[v.id] })) });
                          }}>Assign Shop</Btn>
                        )}
                      </div>
                    )}

                    <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                      <Btn variant="outline" size="sm" onClick={() => openVehicle(v)}>Open Vehicle →</Btn>
                      <Btn variant="red" size="sm" onClick={() => setTowOpen(v)}>🚛 Request Tow</Btn>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


// ─── MY JOBS VIEW ────────────────────────────────────────────────────────────

// Print-ready repair order component
const PrintableRepairOrder = ({ wo, vehicle, staff, parts, onClose }) => {
  const assignee = wo.assignedTo === "ruban" ? { name: "Ruban", company: "Blue Diamond Auto Detail" } : staff.find(s => s.id === wo.assignedTo);
  const vParts = parts.filter(p => p.vehicleId === vehicle.id);
  const woParts = vParts.filter(p => p.description?.toLowerCase().includes(wo.description?.toLowerCase().slice(0,10)));

  return (
    <div style={{ background: "#fff", padding: "24px 28px", color: "#000", fontFamily: "Arial, sans-serif", maxWidth: 600, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", borderBottom: "2px solid #000", paddingBottom: 12, marginBottom: 16 }}>
        <div>
          <img src="/images/logogrey.png" alt="Edgewater Auto Center"
            style={{ maxHeight: 56, maxWidth: 220, objectFit: "contain", marginBottom: 4 }}
            onError={e => { e.target.style.display="none"; e.target.nextSibling.style.display="block"; }} />
          <div style={{ display:"none", fontSize: 18, fontWeight: 900, letterSpacing: "0.05em" }}>EDGEWATER AUTO CENTER</div>
          <div style={{ fontSize: 11, color: "#555" }}>4250 Salem Dallas Hwy NW · Salem, OR 97304</div>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 14, fontWeight: 700 }}>REPAIR ORDER</div>
          <div style={{ fontSize: 10, color: "#555" }}>#{wo.id?.slice(0,8).toUpperCase()}</div>
          <div style={{ fontSize: 10, color: "#555" }}>{fmtDate(wo.createdAt || todayStr())}</div>
        </div>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "#555", marginBottom: 3 }}>Vehicle</div>
          <div style={{ fontSize: 13, fontWeight: 700 }}>{vehicle.year} {vehicle.make} {vehicle.model}</div>
          <div style={{ fontSize: 11 }}>Stock #{vehicle.stockNumber}</div>
          <div style={{ fontSize: 11 }}>VIN: {vehicle.vin || "—"}</div>
          <div style={{ fontSize: 11 }}>{vehicle.mileage ? parseInt(vehicle.mileage).toLocaleString() + " miles" : ""}</div>
          <div style={{ fontSize: 11 }}>{vehicle.exteriorColor || ""}</div>
        </div>
        <div>
          <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "#555", marginBottom: 3 }}>Assigned To</div>
          <div style={{ fontSize: 13, fontWeight: 700 }}>{assignee?.name || wo.assignedTo || "Unassigned"}</div>
          {wo.assignedTo === "ruban" && <div style={{ fontSize: 11 }}>Blue Diamond Auto Detail</div>}
          {wo.assignedTo === "ruban" && <div style={{ fontSize: 11, fontWeight: 700, color: "#0891b2" }}>Rate: {fmtMoney(wo.rubanRate || 200)}</div>}
          {assignee?.email && <div style={{ fontSize: 10, color: "#555" }}>{assignee.email}</div>}
        </div>
      </div>

      <div style={{ border: "1px solid #000", borderRadius: 4, padding: "10px 14px", marginBottom: 14 }}>
        <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "#555", marginBottom: 4 }}>Work Description</div>
        <div style={{ fontSize: 13, fontWeight: 600 }}>{wo.description}</div>
        {wo.diagnosisRationale && <div style={{ fontSize: 11, color: "#333", marginTop: 6, fontStyle: "italic" }}>Reason: {wo.diagnosisRationale}</div>}
        <div style={{ display: "flex", gap: 16, marginTop: 8 }}>
          <div><span style={{ fontSize: 9, color: "#555", textTransform: "uppercase" }}>Priority </span><span style={{ fontSize: 12, fontWeight: 700, color: wo.priority === "HIGH" ? "#dc2626" : wo.priority === "MEDIUM" ? "#f97316" : "#555" }}>{wo.priority}</span></div>
          {wo.estimatedHours > 0 && <div><span style={{ fontSize: 9, color: "#555", textTransform: "uppercase" }}>Est. Hours </span><span style={{ fontSize: 12, fontWeight: 700 }}>{wo.estimatedHours}h</span></div>}
          {wo.estimatedPartsCost > 0 && <div><span style={{ fontSize: 9, color: "#555", textTransform: "uppercase" }}>Est. Parts </span><span style={{ fontSize: 12, fontWeight: 700 }}>{fmtMoney(wo.estimatedPartsCost)}</span></div>}
        </div>
      </div>

      {woParts.length > 0 && (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", color: "#555", marginBottom: 6 }}>Parts</div>
          {woParts.map((p, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: "1px solid #eee", fontSize: 11 }}>
              <span>{p.description}</span>
              <span style={{ fontWeight: 700 }}>{fmtMoneyFull(p.cost)} — {p.status}</span>
            </div>
          ))}
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginTop: 24, paddingTop: 16, borderTop: "1px solid #ccc" }}>
        <div>
          <div style={{ fontSize: 9, color: "#555", textTransform: "uppercase", marginBottom: 20 }}>Technician Signature</div>
          <div style={{ borderBottom: "1px solid #000", marginBottom: 4 }}></div>
          <div style={{ fontSize: 9, color: "#555" }}>Date Completed</div>
        </div>
        <div>
          <div style={{ fontSize: 9, color: "#555", textTransform: "uppercase", marginBottom: 20 }}>Inspector Initials</div>
          <div style={{ borderBottom: "1px solid #000" }}></div>
        </div>
      </div>
    </div>
  );
};

const AssignedJobsView = ({ vehicles, parts, staff, currentUser, openVehicle, tasks, onUpdateTask }) => {
  const [selectedId, setSelectedId] = useState(null);
  const [printWO, setPrintWO] = useState(null);
  const [printVehicle, setPrintVehicle] = useState(null);

  // Build assignee list: staff (excluding managers) + Ruban
  const assignable = [
    ...staff.filter(s => !["manager"].includes(s.role)),
    { id: "ruban", name: "Ruban", company: "Blue Diamond Auto Detail", color: RUBAN.color, role: "1099_detail" }
  ];

  // All open WOs per assignee — auto-resolve null assignments by category
  const resolveWOAssignee = (wo) => {
    if (wo.assignedTo) return wo.assignedTo;
    const desc = (wo.description || "").toLowerCase();
    const hrs = wo.estimatedHours || 0;
    if (wo.category === "DETAIL") return "sky";
    if (wo.category === "VENDOR") return null;
    const noLiftQuick = /bulb|wiper|blade|fluid|top off|air filter|cabin filter|fuse|battery terminal|tire pressure|tpms reset|mirror|antenna|emblem/i;
    if (hrs <= 0.35 && noLiftQuick.test(desc)) return "robert";
    return "james";
  };
  const getWOs = (assigneeId) => vehicles.flatMap(v =>
    (v.workOrders || [])
      .filter(w => resolveWOAssignee(w) === assigneeId && w.status !== "COMPLETE" && !w.deleted)
      .map(w => ({ ...w, vehicle: v }))
  ).filter(w => w && w.vehicle)
   .sort((a, b) => ({ HIGH: 0, MEDIUM: 1, LOW: 2 }[a.priority] ?? 1) - ({ HIGH: 0, MEDIUM: 1, LOW: 2 }[b.priority] ?? 1));

  const assigneesWithJobs = assignable.filter(a => getWOs(a.id).length > 0);

  if (printWO) {
    return (
      <div>
        <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
          <Btn variant="ghost" onClick={() => { setPrintWO(null); setPrintVehicle(null); }}>← Back</Btn>
          <Btn variant="amber" onClick={() => window.print()}>🖨 Print Repair Order</Btn>
        </div>
        <PrintableRepairOrder wo={printWO} vehicle={printVehicle} staff={staff} parts={parts} />
      </div>
    );
  }

  return (
    <div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom: 16 }}>
        <div>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}><SiteLogo size="md" /><div style={{ fontSize: 26, fontWeight: 800, color: C.purple, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.06em" }}>ASSIGNED JOBS</div></div>
          <img src="/images/Edgewaterlogo.png" alt="" style={{ height:28, objectFit:"contain", opacity:0.55, flexShrink:0 }} onError={e=>e.target.style.display="none"} />
        </div>
          <div style={{ fontSize: 11, color: C.textM }}>Tap any job to open the work order</div>
        </div>
      </div>
      {/* My completed tasks — reopen option */}
      {tasks?.filter(t => t.assignedTo === currentUser?.username && t.status === "COMPLETE").length > 0 && (
        <details style={{ marginBottom:14 }}>
          <summary style={{ fontSize:11, fontWeight:700, color:C.textM, cursor:"pointer", marginBottom:6 }}>
            ✅ My completed tasks ({tasks.filter(t=>t.assignedTo===currentUser?.username&&t.status==="COMPLETE").length})
          </summary>
          {tasks.filter(t=>t.assignedTo===currentUser?.username&&t.status==="COMPLETE").slice().reverse().map(task => {
            const assignedDate = task.createdAt ? new Date(task.createdAt) : null;
            const completedDate = task.completedAt ? new Date(task.completedAt) : null;
            const daysOpen = assignedDate && completedDate ? Math.round((completedDate-assignedDate)/86400000) : null;
            const dueDateMs = task.dueDate ? new Date(task.dueDate).getTime() : null;
            const onTime = dueDateMs ? completedDate?.getTime() <= dueDateMs : null;
            return (
              <div key={task.id} style={{ background:C.s2, border:`1px solid ${C.border}`, borderRadius:8, padding:"10px 12px", marginBottom:6 }}>
                <div style={{ fontSize:12, fontWeight:700, color:C.textS, textDecoration:"line-through" }}>{task.description}</div>
                <div style={{ fontSize:9, color:C.textM, marginTop:3 }}>
                  Completed {completedDate?.toLocaleDateString("en-US",{month:"short",day:"numeric"})}
                  {daysOpen!==null && ` · ${daysOpen} day${daysOpen!==1?"s":""} open`}
                  {onTime===true && <span style={{color:C.green,marginLeft:6}}>✓ On time</span>}
                  {onTime===false && <span style={{color:C.red,marginLeft:6}}>⚠ Late</span>}
                </div>
                <button onClick={() => onUpdateTask({ ...task, status:"OPEN", completedAt:null, completedBy:null })}
                  style={{ background:"none", border:`1px solid ${C.amber}`, borderRadius:6, padding:"2px 10px", cursor:"pointer", fontSize:9, fontWeight:700, color:C.amber, fontFamily:"inherit", marginTop:5 }}>
                  ↩ Reopen
                </button>
              </div>
            );
          })}
        </details>
      )}

      {/* General tasks assigned to this user */}
      {tasks?.filter(t => t.assignedTo === currentUser?.username && t.status === "OPEN").length > 0 && (
        <div style={{ marginBottom:16 }}>
          <div style={{ fontSize:10, fontWeight:700, color:C.textM, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:8 }}>📋 Assigned Tasks</div>
          {tasks.filter(t => t.assignedTo === currentUser?.username && t.status === "OPEN").map(task => (
            <div key={task.id} style={{ background:C.surface, border:`2px solid ${C.amber}40`, borderRadius:10, padding:"12px 14px", marginBottom:8 }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:10 }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontSize:13, fontWeight:700, color:"#000" }}>{task.description}</div>
                  <div style={{ fontSize:10, color:C.textM, marginTop:3 }}>
                    Assigned by {task.assignedByName || task.assignedBy}
                    {task.dueDate && <span style={{ color:C.red, marginLeft:8 }}>⏰ Due {task.dueDate}</span>}
                  </div>
                </div>
                <button onClick={() => { if (window.confirm("Mark this task complete?")) onUpdateTask({ ...task, status:"COMPLETE", completedAt:new Date().toISOString(), completedBy:currentUser?.username }); }}
                  style={{ background:C.green, border:"none", borderRadius:8, padding:"7px 14px", cursor:"pointer", fontSize:11, fontWeight:700, color:"#fff", fontFamily:"inherit", whiteSpace:"nowrap" }}>
                  ✓ Done
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {assigneesWithJobs.length === 0 ? (
        <div style={{ textAlign: "center", color: C.textM, padding: 40 }}>
          <div style={{ fontSize: 32, marginBottom: 8 }}>✅</div>
          <div style={{ fontSize: 13 }}>No open assignments right now.</div>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {assigneesWithJobs.map(person => {
            const wos = getWOs(person.id);
            const isOpen = selectedId === person.id;
            return (
              <div key={person.id} style={{ background: C.surface, border: `2px solid ${person.color}40`, borderRadius: 12, overflow: "hidden" }}>
                {/* Assignee header */}
                <div onClick={() => setSelectedId(isOpen ? null : person.id)}
                  style={{ background: person.color + "15", padding: "12px 16px", display: "flex", justifyContent: "space-between", alignItems: "center", cursor: "pointer" }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: "#000" }}>{person.name}</div>
                    <div style={{ fontSize: 10, color: "#555" }}>
                      {person.company ? person.company + " · " : ""}
                      {person.role === "1099_detail" ? "1099 Detailer · Unlimited capacity" : person.role}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    <Badge color={person.color} small>{wos.length} job{wos.length !== 1 ? "s" : ""}</Badge>
                    <span style={{ fontSize: 12, color: person.color }}>{isOpen ? "▲" : "▼"}</span>
                  </div>
                </div>

                {/* Jobs list */}
                {isOpen && (
                  <div style={{ padding: "8px 0" }}>
                    {wos.filter(w => w.vehicle).map(w => {
                      const vParts = parts.filter(p => p.vehicleId === w.vehicle?.id);
                      const waitingParts = vParts.filter(p => ["Ordered","In Transit"].includes(p.status));
                      const binReady = vParts.some(p => p.status === "Received") && waitingParts.length === 0;
                      return (
                        <div key={w.id} onClick={() => openVehicle(w.vehicle)}
                          style={{ padding: "10px 16px", borderBottom: `1px solid ${C.border}`, cursor: "pointer" }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 4 }}>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 12, fontWeight: 700, color: "#000" }}>{w.description}</div>
                              <div style={{ fontSize: 10, color: "#555" }}>{w.vehicle.year} {w.vehicle.make} {w.vehicle.model} #{w.vehicle.stockNumber}</div>
                              {w.rubanJob && <div style={{ fontSize: 10, fontWeight: 700, color: RUBAN.color }}>Rate: {fmtMoney(w.rubanRate || 200)}</div>}
                            </div>
                            <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:4 }}>
                              <Badge color={w.priority === "HIGH" ? C.red : w.priority === "MEDIUM" ? "#f97316" : C.textM} small>{w.priority}</Badge>
                              <span style={{ fontSize:9, color:C.textM }}>Tap to open →</span>
                            </div>
                          </div>
                          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                            {waitingParts.length > 0 && <Badge color={C.amber} small>⏳ Parts on order</Badge>}
                            {binReady && <Badge color={C.green} small>📦 Bin ready</Badge>}
                            <Badge color={C.textM} small>{(w.status || "OPEN").replace("_"," ")}</Badge>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── HOME SCREEN ─────────────────────────────────────────────────────────────

const HOME_IMAGES = [
  "/images/homeheader(3).jpg",
  "/images/homeheader(4).jpg",
  "/images/homeheader(5).jpg",
  "/images/homeheader(6).jpg",
  "/images/homeheader(7).jpg",
  "/images/homeheader(8).jpg",
];


// ─── SITE LOGO ──────────────────────────────────────────────────────────────
const SiteLogo = ({ size = "sm" }) => {
  // sm=28, md=40, lg=56, auto=responsive
  const h = size === "lg" ? 56 : size === "md" ? 40 : 28;
  return (
    <img src="/images/Edgewaterlogo.png" alt="Edgewater"
      style={{ height: h, objectFit: "contain", display: "block",
        filter: "drop-shadow(0 1px 4px rgba(0,0,0,0.18))" }}
      onError={e => e.target.style.display = "none"} />
  );
};

const HomeView = ({
 vehicles, parts, ledger, staff, currentUser, schedule, inventoryCache, setView, navigateTo, openVehicle, onScanVIN, chatMessages, tasks, onAssignTask }) => {
  const active = vehicles.filter(v => !["LINE_READY","SOLD"].includes(v.stage));
  const lineReady = vehicles.filter(v => v.stage === "LINE_READY");
  const ksCount = parts.filter(p => { const v = vehicles.find(x => x.id === p.vehicleId); return v?.stage === "SOLD" && !["Installed","RETURNED","CANCELLED"].includes(p.status); }).length;
  const overdue = active.filter(v => {
    const openWOs = (v.workOrders||[]).filter(w => w.status!=="COMPLETE" && !w.deleted);
    if (!openWOs.length) return false;
    const nonDetail = openWOs.filter(w => w.category !== "DETAIL");
    if (nonDetail.length === 0) return false; // detail-only not counted as overdue on home
    return daysSince(v.arrivalDate) > 10;
  });
  const totalReconInvest = ledger.filter(e => { const v = vehicles.find(x => x.id === e.vehicleId); return v && !["SOLD"].includes(v.stage) && e.category !== "PURCHASE"; }).reduce((s, e) => s + (e.amount || 0), 0);

  const today = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const role = STAFF_ROLES.find(r => r.id === currentUser?.role);

  const [headerImg, setHeaderImg] = useState(0);
  const [fade, setFade] = useState(true);
  const [jobListView, setJobListView] = useState(null); // null | "repairs" | "details"
  const isManager = ["manager"].includes(currentUser?.role);
  useEffect(() => {
    const interval = setInterval(() => {
      setFade(false);
      setTimeout(() => {
        setHeaderImg(i => (i + 1) % HOME_IMAGES.length);
        setFade(true);
      }, 600);
    }, 45000);
    return () => clearInterval(interval);
  }, []);

  return (
    <div>
      {/* ── HERO — rotating background + logo + 2×2 action grid ── */}
      <div style={{ position: "relative", borderRadius: 16, overflow: "hidden", marginBottom: 14, minHeight: 380 }}>
        <img src={HOME_IMAGES[headerImg]} alt=""
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center", opacity: fade ? 0.72 : 0, transition: "opacity 0.6s ease-in-out" }} />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(160deg, rgba(5,15,40,0.82) 0%, rgba(20,50,120,0.55) 100%)" }} />
        {/* Slide dots */}
        <div style={{ position: "absolute", bottom: 8, right: 12, display: "flex", gap: 5, zIndex: 2 }}>
          {HOME_IMAGES.map((_, i) => (
            <div key={i} onClick={() => { setFade(false); setTimeout(() => { setHeaderImg(i); setFade(true); }, 300); }}
              style={{ width: i === headerImg ? 18 : 6, height: 6, borderRadius: 3, background: i === headerImg ? C.amber : "rgba(255,255,255,0.3)", cursor: "pointer", transition: "all 0.3s" }} />
          ))}
        </div>
        <div style={{ position: "relative", zIndex: 1, padding: "18px 16px 16px" }}>
          {/* Logo + greeting — centered */}
          <div style={{ textAlign: "center", marginBottom: 16 }}>
            <img src="/images/logogrey.png" alt="Edgewater Auto Center"
              style={{ height: "clamp(120px, 20vw, 220px)", maxWidth: "85%", objectFit: "contain", filter: "drop-shadow(0 2px 16px rgba(0,0,0,0.9))", display: "block", margin: "0 auto 8px" }}
              onError={e => {
                e.target.style.display="none";
                document.getElementById("ew-logo-fallback").style.display="block";
              }} />
            <div id="ew-logo-fallback" style={{ display:"none", fontSize: 24, fontWeight: 900, color: "#fff", fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.1em", textShadow: "0 2px 12px rgba(0,0,0,0.9)", marginBottom: 8 }}>
              EDGEWATER AUTO CENTER
            </div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)" }}>{today}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: "#fff", fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.04em", textShadow: "0 2px 8px rgba(0,0,0,0.8)", marginTop: 2 }}>
              {greeting}, {currentUser?.name?.split(" ")[0] || "team"}
            </div>
          </div>
          {/* Repairs / Details quick lists */}
          {(() => {
            const allOpenWOs = vehicles
              .filter(v => v.stage !== "SOLD")
              .flatMap(v => (v.workOrders || [])
                .filter(w => !w.deleted && w.status !== "COMPLETE")
                .map(w => ({ wo: w, vehicle: v })));
            const repairItems = allOpenWOs.filter(x => x.wo.category !== "DETAIL");
            const detailWOs = allOpenWOs.filter(x => x.wo.category === "DETAIL");
            // Repairs list: deduplicate by vehicle so one row per vehicle
            const seen = new Set();
            const repairWOs = [];
            for (const it of repairItems) {
              if (seen.has(it.vehicle.id)) continue;
              seen.add(it.vehicle.id);
              const vehicleWOs = repairItems.filter(x => x.vehicle.id === it.vehicle.id).map(x => x.wo);
              repairWOs.push({ vehicle: it.vehicle, wos: vehicleWOs });
            }
            repairWOs.sort((a, b) => `${a.vehicle.year || ""} ${a.vehicle.make || ""} ${a.vehicle.model || ""}`.localeCompare(`${b.vehicle.year || ""} ${b.vehicle.make || ""} ${b.vehicle.model || ""}`));
            const list = jobListView === "repairs" ? repairWOs : jobListView === "details" ? detailWOs : null;
            return (
              <div style={{ marginBottom: 10 }}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <img onClick={() => setJobListView(jobListView === "repairs" ? null : "repairs")}
                    src="/images/repairs.jpg?v=2" alt="REPAIRS"
                    style={{ width: "100%", display: "block", cursor: "pointer", borderRadius: 14, outline: jobListView === "repairs" ? "3px solid rgba(239,68,68,0.9)" : "none" }} />
                  <img onClick={() => setJobListView(jobListView === "details" ? null : "details")}
                    src="/images/needscleaned.jpg?v=2" alt="NEED CLEANED"
                    style={{ width: "100%", display: "block", cursor: "pointer", borderRadius: 14, outline: jobListView === "details" ? "3px solid rgba(168,85,247,0.9)" : "none" }} />
                </div>
                {list !== null && (
                  <div style={{ background: "rgba(15,25,40,0.85)", border: `1px solid rgba(255,255,255,0.15)`, borderRadius: 10, marginTop: 8, maxHeight: 380, overflowY: "auto" }}>
                    {list.length === 0 ? (
                      <div style={{ padding: "16px", textAlign: "center", fontSize: 11, color: "rgba(255,255,255,0.6)" }}>
                        No open {jobListView === "repairs" ? "repair" : "detail"} jobs.
                      </div>
                    ) : (jobListView === "repairs"
                      ? list.map(({ vehicle, wos }, idx) => {
                          const hasHigh = wos.some(w => w.priority === "HIGH");
                          const inProg = wos.some(w => w.status === "IN_PROGRESS");
                          return (
                            <div key={vehicle.id} onClick={() => openVehicle(vehicle)}
                              style={{ padding: "10px 14px", borderBottom: idx < list.length - 1 ? "1px solid rgba(255,255,255,0.08)" : "none", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontSize: 13, fontWeight: 700, color: "#fff" }}>{vehicle.year} {vehicle.make} {vehicle.model}</div>
                                <div style={{ fontSize: 10, color: "rgba(255,255,255,0.65)", marginTop: 2 }}>
                                  #{vehicle.stockNumber} · {wos.length} open repair{wos.length !== 1 ? "s" : ""}
                                </div>
                                <div style={{ display: "flex", gap: 5, marginTop: 4, flexWrap: "wrap" }}>
                                  {hasHigh && <span style={{ background: "rgba(239,68,68,0.25)", color: "#fca5a5", fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 8 }}>HIGH</span>}
                                  {inProg && <span style={{ background: "rgba(59,130,246,0.25)", color: "#93c5fd", fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 8 }}>⚡ IN PROGRESS</span>}
                                </div>
                              </div>
                              <ChevronRight size={14} color="rgba(255,255,255,0.5)" style={{ flexShrink: 0, marginTop: 2 }} />
                            </div>
                          );
                        })
                      : list.map(({ wo, vehicle }, idx) => {
                      const cat = WO_CATS.find(c => c.id === wo.category);
                      const assignedStaff = wo.assignedTo ? staff?.find(s => s.username === wo.assignedTo || s.id === wo.assignedTo) : null;
                      return (
                        <div key={wo.id + "_" + idx} onClick={() => openVehicle(vehicle)}
                          style={{ padding: "10px 14px", borderBottom: idx < list.length - 1 ? "1px solid rgba(255,255,255,0.08)" : "none", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontSize: 12, fontWeight: 700, color: "#fff", marginBottom: 2 }}>{wo.description}</div>
                            <div style={{ fontSize: 10, color: "rgba(255,255,255,0.65)" }}>
                              {vehicle.year} {vehicle.make} {vehicle.model} · #{vehicle.stockNumber}
                              {assignedStaff ? ` · ${assignedStaff.name}` : ""}
                            </div>
                            <div style={{ display: "flex", gap: 5, marginTop: 4, flexWrap: "wrap" }}>
                              {cat && <span style={{ background: cat.color + "30", color: cat.color, fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 8 }}>{cat.label}</span>}
                              <span style={{ background: wo.priority === "HIGH" ? "rgba(239,68,68,0.25)" : wo.priority === "MEDIUM" ? "rgba(249,115,22,0.25)" : "rgba(255,255,255,0.1)", color: wo.priority === "HIGH" ? "#fca5a5" : wo.priority === "MEDIUM" ? "#fdba74" : "rgba(255,255,255,0.6)", fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 8 }}>{wo.priority || "MEDIUM"}</span>
                              {wo.status === "IN_PROGRESS" && <span style={{ background: "rgba(59,130,246,0.25)", color: "#93c5fd", fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 8 }}>⚡ IN PROGRESS</span>}
                              {wo.allPartsIn && <span style={{ background: "rgba(34,197,94,0.25)", color: "#86efac", fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 8 }}>✅ Parts In</span>}
                            </div>
                          </div>
                          <ChevronRight size={14} color="rgba(255,255,255,0.5)" style={{ flexShrink: 0, marginTop: 2 }} />
                        </div>
                      );
                    }))}
                  </div>
                )}
              </div>
            );
          })()}
          {/* Action Grid — full-width Add WO + 2-col All WOs / Diagnose */}
          {/* ADD WORK ORDER — full width */}
          <img onClick={onScanVIN} src="/images/addworkorder.jpg?v=2" alt="ADD WORK ORDER"
            style={{ width: "100%", display: "block", cursor: "pointer", borderRadius: 14, marginBottom: 10 }} />
          {/* ALL VEHICLE WORK ORDERS — full width */}
          <img onClick={() => (navigateTo || setView)("fleet")} src="/images/workorders.jpg?v=2" alt="ALL VEHICLE WORK ORDERS"
            style={{ width: "100%", display: "block", cursor: "pointer", borderRadius: 14, marginBottom: 10 }} />
          {/* DIAGNOSE — full width */}
          <img onClick={() => (navigateTo || setView)("diagnose")} src="/images/diagnose.jpg?v=2" alt="DIAGNOSE"
            style={{ width: "100%", display: "block", cursor: "pointer", borderRadius: 14, marginBottom: 10 }} />
          {/* VENDORS — full width */}
          <img onClick={() => (navigateTo || setView)("vendors")} src="/images/vendors.jpg?v=2" alt="VENDORS"
            style={{ width: "100%", display: "block", cursor: "pointer", borderRadius: 14, marginBottom: 10 }} />
        </div>
      </div>

      {/* Pending approvals alert */}
      {(() => {
        const pendingApprovals = vehicles.filter(v => v.pendingApproval?.status === "PENDING");
        const isManager = ["manager","office"].includes(currentUser?.role);
        if (!isManager || pendingApprovals.length === 0) return null;
        return (
          <div onClick={() => (navigateTo || setView)("fleet")} style={{ background: C.purple + "15", border: `2px solid ${C.purple}50`, borderRadius: 10, padding: "12px 16px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
            <span style={{ color: C.purple, fontWeight: 800, fontSize: 12 }}>⚡ {pendingApprovals.length} repair approval{pendingApprovals.length > 1 ? "s" : ""} waiting</span>
            <ChevronRight size={13} color={C.purple} />
          </div>
        );
      })()}
    </div>
  );
};

// ─── RECON CALENDAR VIEW ─────────────────────────────────────────────────────

const ReconCalendarView = ({ schedule, setSchedule, vehicles, parts, staff, onBack, onUpdateVehicle, openVehicle, onRebuildSchedule, currentUser, timeEntries, setTimeEntries, ledger, onAddLedger, tasks, onUpdateTask, navigateTo }) => {
  const [selectedJob, setSelectedJob] = useState(null);
  const [viewDate, setViewDate] = useState(todayStr());
  const [autoScheduling, setAutoScheduling] = useState(false);
  const [calView, setCalView] = useState("list"); // "list" | "calendar"
  const [selectedStaff, setSelectedStaff] = useState("all"); // "all" | staffId

  // Auto-populate on mount if schedule is empty or WOs have changed since last schedule build
  useEffect(() => {
    const allOpenWOs = vehicles
      .filter(v => !["SOLD","SCRAPPED"].includes(v.stage))
      .flatMap(v => (v.workOrders || []).filter(w => w.status !== "COMPLETE" && !w.deleted));
    const currentSig = allOpenWOs.map(w => w.id + w.status).sort().join("|");
    const savedSig = schedule?._sig || "";
    const hasSchedule = Object.keys(schedule || {}).filter(k => k !== "_sig").length > 0;

    // Also force rebuild if schedule has any manager assignments
    const managerIds = (staff || []).filter(s => ["manager","viewer"].includes(s.role)).map(s => s.id);
    const hasManagerJobs = Object.entries(schedule || {}).some(([day, dayData]) =>
      day !== '_sig' && typeof dayData === 'object' && Object.keys(dayData).some(k => managerIds.includes(k))
    );

    if (!hasSchedule || currentSig !== savedSig || hasManagerJobs) {
      autoPopulate(currentSig);
    } else {
      setViewDate(todayStr());
    }
  }, []);

  // Auto-populate calendar from all open WOs across all vehicles
  const autoPopulate = (currentSig = null) => {
    setAutoScheduling(true);
    const today = todayStr();

    const newSchedule = {};
    const allOpenWOsSig = currentSig || vehicles
      .filter(v => !["SOLD","SCRAPPED"].includes(v.stage))
      .flatMap(v => (v.workOrders || []).filter(w => w.status !== "COMPLETE" && !w.deleted))
      .map(w => w.id + w.status).sort().join("|");
    const DAILY_CAP = 6.5;
    const holidays = HOLIDAYS(new Date().getFullYear());

    // Only non-manager, non-viewer workers
    const workers = staff.filter(s => !["manager","viewer"].includes(s.role) && s.username !== "john");
    if (!workers.length) { setAutoScheduling(false); return; }

    // Per-worker date cursors and hour tracking
    const workerCursor = {};
    const workerHours = {};
    workers.forEach(w => {
      workerCursor[w.id] = new Date(today + "T12:00:00");
      workerHours[w.id] = {};
    });

    // Helper: advance to next valid work day for a worker
    const nextSlot = (workerId, hours) => {
      let d = new Date(workerCursor[workerId]);
      for (let attempts = 0; attempts < 60; attempts++) {
        const dateStr = d.toISOString().slice(0, 10);
        const dow = d.getDay();
        if (dow !== 0 && dow !== 6 && !holidays.has(dateStr)) {
          const used = (workerHours[workerId] || {})[dateStr] || 0;
          if (used + hours <= DAILY_CAP) return { dateStr, d };
        }
        d = new Date(d);
        d.setDate(d.getDate() + 1);
      }
      return null;
    };

    // Get all open WOs — ONLY schedule if parts are received (or no parts needed)
    const allJobs = [];
    vehicles.filter(v => !["SOLD","SCRAPPED"].includes(v.stage)).forEach(v => {
      const vParts = (parts || []).filter(p => p.vehicleId === v.id && !["RETURNED","CANCELLED"].includes(p.status));
      (v.workOrders || []).filter(w => w.status !== "COMPLETE" && !w.deleted).forEach(wo => {
        // Check parts status for this WO
        const woParts = vParts.filter(p => p.workOrderId === wo.id);
        const partsOnOrder = woParts.some(p => ["Ordered","In Transit"].includes(p.status));
        // Skip if parts are still on order — don't schedule until they arrive
        if (partsOnOrder) return;
        allJobs.push({ wo, vehicle: v,
          priority: wo.priority === "HIGH" ? 0 : wo.priority === "MEDIUM" ? 1 : 2,
          vehicleValue: parseFloat(v.purchasePrice) || 0,
          hours: wo.estimatedHours || 1
        });
      });
    });
    // Helper: is a vehicle "detail-only" (no open non-detail WOs)?
    const isDetailOnly = (v) => {
      const openWOs = (v.workOrders||[]).filter(w => w.status !== "COMPLETE" && !w.deleted);
      return openWOs.length > 0 && openWOs.every(w => w.category === "DETAIL");
    };
    // Helper: is a vehicle "clean and ready" (zero open WOs — just needs detail)?
    const isCleanReady = (v) => {
      const openWOs = (v.workOrders||[]).filter(w => w.status !== "COMPLETE" && !w.deleted);
      return openWOs.length === 0;
    };

    // Sort: 
    // 1. Sold vehicle repairs (customer cars) — always first
    // 2. For DETAIL WOs: clean-ready vehicles (no other open WOs) beat vehicles still in mechanical/bodywork
    // 3. Within detail: vehicles with NO other WOs at all come before vehicles that still have mechanical WOs open
    // 4. Then HIGH > MEDIUM > LOW, then vehicle value
    allJobs.sort((a, b) => {
      const aSold = a.vehicle.soldRepairPending ? 0 : 1;
      const bSold = b.vehicle.soldRepairPending ? 0 : 1;
      if (aSold !== bSold) return aSold - bSold;
      if (a.vehicle.customerReturnDate && b.vehicle.customerReturnDate) {
        const diff = new Date(a.vehicle.customerReturnDate) - new Date(b.vehicle.customerReturnDate);
        if (diff !== 0) return diff;
      }
      if (a.vehicle.customerReturnDate) return -1;
      if (b.vehicle.customerReturnDate) return 1;

      // For Sky (detail WOs) — prioritize vehicles that have NO other open WOs
      if (a.wo.category === "DETAIL" && b.wo.category === "DETAIL") {
        const aDetailOnly = isDetailOnly(a.vehicle);
        const bDetailOnly = isDetailOnly(b.vehicle);
        if (aDetailOnly && !bDetailOnly) return -1; // a first — clean ready
        if (!aDetailOnly && bDetailOnly) return 1;  // b first — clean ready
      }

      if (a.priority !== b.priority) return a.priority - b.priority;
      return b.vehicleValue - a.vehicleValue;
    });

    allJobs.forEach(({ wo, vehicle, hours }) => {
      // Resolve the correct worker — never assign to managers
      const assignedId = wo.assignedTo;
      let worker = workers.find(w => w.username === assignedId || w.id === assignedId);

      // If not found or is a manager, re-assign by category
      if (!worker) {
        const desc = (wo.description || "").toLowerCase();
        const noLiftQuick = /bulb|wiper blade|wiper insert|washer fluid|windshield fluid|top off|air filter|cabin filter|fuse|battery terminal|tire pressure check|tpms reset|tpms relearn|mirror glass|antenna|emblem|badge|decal|sticker/i;
        if (wo.category === "DETAIL") worker = workers.find(w => w.username === "sky") || workers[0];
        else if (wo.category === "VENDOR") return; // External — skip
        else if (hours > 0 && hours <= 0.34 && noLiftQuick.test(desc)) worker = workers.find(w => w.username === "robert") || workers.find(w => w.username === "james") || workers[0];
        else worker = workers.find(w => w.username === "james") || workers[0];
      }
      if (!worker) return;

      const slot = nextSlot(worker.id, hours);
      if (!slot) return;

      if (!workerHours[worker.id]) workerHours[worker.id] = {};
      workerHours[worker.id][slot.dateStr] = (workerHours[worker.id][slot.dateStr] || 0) + hours;
      // Advance cursor if day is now full
      if (workerHours[worker.id][slot.dateStr] >= DAILY_CAP) {
        const next = new Date(slot.d); next.setDate(next.getDate() + 1);
        workerCursor[worker.id] = next;
      } else {
        workerCursor[worker.id] = slot.d;
      }

      if (!newSchedule[slot.dateStr]) newSchedule[slot.dateStr] = {};
      if (!newSchedule[slot.dateStr][worker.id]) newSchedule[slot.dateStr][worker.id] = [];
      newSchedule[slot.dateStr][worker.id].push({
        id: wo.id, vehicleId: vehicle.id, woId: wo.id,
        description: wo.description, estimatedHours: hours,
        priority: wo.priority, category: wo.category,
        vehicleLabel: `${vehicle.year} ${vehicle.make} ${vehicle.model} #${vehicle.stockNumber}`
      });
    });

    newSchedule._sig = allOpenWOsSig;
    setSchedule(newSchedule);
    setAutoScheduling(false);
  };

  // Get all scheduled days sorted
  const allDays = Object.keys(schedule || {}).filter(k => k !== "_sig").sort();
  const today = todayStr();


  // Generate a 7-day window starting from viewDate
  const weekDays = (() => {
    const days = [];
    let d = new Date(viewDate + "T12:00:00");
    for (let i = 0; i < 7; i++) {
      if (d.getDay() !== 0 && d.getDay() !== 6) days.push(d.toISOString().slice(0, 10));
      d.setDate(d.getDate() + 1);
    }
    return days;
  })();

  const staffColors = {};
  staff.forEach(s => { staffColors[s.id] = s.color || "#3b82f6"; });

  const getVehicle = (vehicleId) => vehicles.find(v => v.id === vehicleId);
  const getWO = (vehicle, woId) => vehicle?.workOrders?.find(w => w.id === woId);
  const getStaff = (staffId) => staff.find(s => s.id === staffId);

  // Job detail panel
  if (selectedJob) {
    const { job, day, staffId, vehicle, wo } = selectedJob;
    const person = getStaff(staffId);
    const vParts = parts.filter(p => p.vehicleId === vehicle?.id);
    return (
      <div>
        <button onClick={() => setSelectedJob(null)}
          style={{ background: "none", border: "none", color: C.blue, cursor: "pointer", fontSize: 12, fontWeight: 700, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 4, marginBottom: 16, padding: 0 }}>
          <img src="/images/btn-back-arrow.jpg" alt="" style={{width:16,height:16,objectFit:"contain",flexShrink:0}} onError={e=>e.target.style.display="none"} /> Back to Calendar
        </button>

        {/* Job header */}
        <div style={{ background: C.surface, border: `2px solid ${C.amber}50`, borderRadius: 12, padding: "14px 16px", marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 15, fontWeight: 800, color: "#000" }}>{job.description}</div>
              <div style={{ fontSize: 11, color: "#555", marginTop: 2 }}>
                {new Date(day + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" })}
              </div>
              {person && <div style={{ fontSize: 11, color: C.textM, marginTop: 2 }}>Assigned to {person.name}</div>}
            </div>
            <Badge color={job.priority === "HIGH" ? C.red : job.priority === "MEDIUM" ? "#f97316" : C.textM} small>{job.priority}</Badge>
          </div>
          <div style={{ fontSize: 10, color: "#555", marginBottom: 10 }}>Est. {job.estimatedHours || 1}h</div>
          <div style={{ display: "flex", gap: 8 }}>
            {vehicle && openVehicle && (
              <button onClick={() => { setSelectedJob(null); openVehicle(vehicle); }}
                style={{ background: C.amber, border: "none", borderRadius: 7, padding: "7px 14px", cursor: "pointer", fontSize: 11, fontWeight: 700, color: "#000", fontFamily: "inherit" }}>
                → Open Vehicle
              </button>
            )}
          </div>
        </div>

        {/* Vehicle */}
        {vehicle && (
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px", marginBottom: 10 }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 4 }}>Vehicle</div>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#000" }}>{vehicle.year} {vehicle.make} {vehicle.model}</div>
            <div style={{ fontSize: 10, color: "#555" }}>Stock #{vehicle.stockNumber} · {vehicle.mileage ? parseInt(vehicle.mileage).toLocaleString() + " mi" : ""}</div>
            {vehicle.estimatedCompletionDate && <div style={{ fontSize: 10, color: C.green, marginTop: 4 }}>Est. complete: {fmtDate(vehicle.estimatedCompletionDate)}</div>}
          </div>
        )}

        {/* Work order details */}
        {wo && (
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px", marginBottom: 10 }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 4 }}>Work Order</div>
            <div style={{ fontSize: 12, fontWeight: 700, color: "#000", marginBottom: 4 }}>{wo.description}</div>
            {wo.diagnosisRationale && <div style={{ fontSize: 11, color: "#555", fontStyle: "italic", marginBottom: 6 }}>{wo.diagnosisRationale}</div>}
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <Badge color={wo.category === "MECHANICAL" ? C.blue : wo.category === "DETAIL" ? C.purple : C.textM} small>{wo.category}</Badge>
              <Badge color={wo.status === "COMPLETE" ? C.green : wo.status === "IN_PROGRESS" ? C.amber : C.textM} small>{wo.status?.replace("_"," ")}</Badge>
              {wo.estimatedHours > 0 && <Badge color={C.textM} small>{wo.estimatedHours}h est.</Badge>}
            </div>
          </div>
        )}

        {/* Assigned employee */}
        {person && (
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px", marginBottom: 10 }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 4 }}>Assigned To</div>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ width: 36, height: 36, borderRadius: "50%", background: person.color || C.amber, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, fontWeight: 800, color: "#fff", flexShrink: 0 }}>
                {person.avatar ? <img src={person.avatar} alt="" style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }} /> : person.name[0]}
              </div>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#000" }}>{person.name}</div>
                <div style={{ fontSize: 10, color: "#555" }}>{person.role}</div>
              </div>
            </div>
          </div>
        )}

        {/* Parts for this vehicle */}
        {vParts.length > 0 && (
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px", marginBottom: 10 }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 8 }}>Parts on Order — {vehicle?.year} {vehicle?.make} {vehicle?.model}</div>
            {vParts.map((p, i) => (
              <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: i < vParts.length - 1 ? `1px solid ${C.border}` : "none", fontSize: 11 }}>
                <span style={{ color: "#000" }}>{p.description}</span>
                <Badge color={p.status === "Received" ? C.green : p.status === "Ordered" ? C.amber : C.textM} small>{p.status}</Badge>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Calendar view
  const hasAnySchedule = allDays.filter(k => k !== '_sig').length > 0;

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
        <div>
          <div style={{ display:"flex", alignItems:"center", gap:10 }}>
            <div style={{ display:"flex", alignItems:"center", gap:10 }}><SiteLogo size="md" /><div style={{ fontSize: 26, fontWeight: 800, color: C.amber, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.06em" }}>RECON CALENDAR</div></div>
            <img src="/images/Edgewaterlogo.png" alt="" style={{ height:28, objectFit:"contain", opacity:0.55, flexShrink:0 }} onError={e=>e.target.style.display="none"} />
          </div>
          <div style={{ fontSize: 11, color: C.textM, lineHeight: 1.5 }}>
            {autoScheduling ? "⚡ Building schedule…" : "Tap any job to view details · schedule updates automatically when work orders change"}
          </div>
        </div>
        <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
          {navigateTo && (
            <button onClick={() => navigateTo("myjobs")}
              style={{ background: C.purple, border: "none", borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontSize: 12, fontWeight: 800, color: "#fff", fontFamily: "inherit" }}>
              📋 My Jobs
            </button>
          )}
          <button onClick={() => setCalView(v => v === "calendar" ? "list" : "calendar")}
            style={{ background: C.s2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 10, fontWeight: 700, color: C.textM, fontFamily: "inherit" }}>
            {calView === "calendar" ? "☰ List" : "📅 Calendar"}
          </button>
        </div>
      </div>

      {/* Disclaimer */}
      <div style={{ background: C.amber + "12", border: `1px solid ${C.amber}40`, borderRadius: 8, padding: "8px 12px", marginBottom: 10, fontSize: 10, color: C.amber, lineHeight: 1.5 }}>
        ⚠ Schedule is an estimate based on current work orders and staff availability.
      </div>
      {/* Staff filter pills */}
      {(() => {
        const workers = staff.filter(s => !["manager","viewer"].includes(s.role) && s.username !== "john");
        if (!workers.length) return null;
        const workerColors = {};
        workers.forEach((w,i) => { workerColors[w.id] = w.color || ["#3b82f6","#22c55e","#a855f7","#f97316","#06b6d4","#ec4899"][i%6]; });
        return (
          <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:12 }}>
            <button onClick={() => setSelectedStaff("all")}
              style={{ padding:"6px 14px", borderRadius:20, border:`2px solid ${selectedStaff==="all"?C.amber:C.border}`, background:selectedStaff==="all"?C.amber+"20":"none", color:selectedStaff==="all"?C.amber:C.textM, fontWeight:700, fontSize:11, fontFamily:"inherit", cursor:"pointer" }}>
              All Staff
            </button>
            {currentUser && (
              <button onClick={() => setSelectedStaff(currentUser.id)}
                style={{ padding:"6px 14px", borderRadius:20, border:`2px solid ${selectedStaff===currentUser.id?"#ec4899":C.border}`, background:selectedStaff===currentUser.id?"#ec4899"+"20":"none", color:selectedStaff===currentUser.id?"#ec4899":C.textM, fontWeight:700, fontSize:11, fontFamily:"inherit", cursor:"pointer" }}>
                My Jobs
              </button>
            )}
            {workers.filter(w => w.id !== currentUser?.id).map(w => {
              const col = workerColors[w.id];
              const sel = selectedStaff === w.id;
              return (
                <button key={w.id} onClick={() => setSelectedStaff(sel?"all":w.id)}
                  style={{ padding:"6px 14px", borderRadius:20, border:`2px solid ${sel?col:C.border}`, background:sel?col+"25":"none", color:sel?col:C.textM, fontWeight:700, fontSize:11, fontFamily:"inherit", cursor:"pointer" }}>
                  {w.name.split(" ")[0]}
                </button>
              );
            })}
          </div>
        );
      })()}

      {!hasAnySchedule ? (
        <div style={{ textAlign: "center", padding: "48px 0", color: C.textM }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📅</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#000", marginBottom: 6 }}>No schedule generated yet</div>
          <div style={{ fontSize: 11, marginBottom: 20 }}>{autoScheduling ? "Building schedule from open work orders…" : "Building schedule…"}</div>
        </div>
      ) : calView === "list" ? (
        // LIST VIEW — all jobs sorted by date then staff
        <div>
          {allDays.map(day => {
            const dayData = schedule[day];
            if (!dayData) return null;
            const isToday = day === todayStr();
            const dayLabel = new Date(day + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
            const workers = staff.filter(s => !["manager","viewer"].includes(s.role) && s.username !== "john");
            const workerColors = {};
            workers.forEach((w,i) => { workerColors[w.id] = w.color || ["#3b82f6","#22c55e","#a855f7","#f97316","#06b6d4","#ec4899"][i%6]; });
            const allJobs = Object.entries(dayData).filter(([k]) => k !== '_sig').flatMap(([staffId, jobs]) =>
              Array.isArray(jobs) ? jobs.map(j => ({ ...j, staffId })) : []
            ).filter(j => j && j.id)
             .filter(j => selectedStaff === "all" || j.staffId === selectedStaff);
            if (!allJobs.length) return null;
            return (
              <div key={day} style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: isToday ? C.amber : C.textM, textTransform: "uppercase", marginBottom: 6, paddingBottom: 4, borderBottom: `1px solid ${C.border}` }}>
                  {isToday ? "TODAY — " : ""}{dayLabel} · {allJobs.length} job{allJobs.length !== 1 ? "s" : ""}
                </div>
                {allJobs.map((job, i) => {
                  const person = getStaff(job.staffId);
                  const vehicle = getVehicle(job.vehicleId);
                  const personColor = workerColors[job.staffId] || C.border;
                  const isHighlighted = selectedStaff !== "all" && selectedStaff === job.staffId;
                  return (
                    <div key={i} onClick={() => setSelectedJob({ job, day, staffId: job.staffId, vehicle, wo: getWO(vehicle, job.woId) })}
                      style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "8px 12px", background: isHighlighted ? personColor+"12" : C.surface, border: `2px solid ${isHighlighted ? personColor : personColor+"60"}`, borderRadius: 7, marginBottom: 5, cursor: "pointer" }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 12, fontWeight: 600, color: C.text }}>{job.description}</div>
                        <div style={{ fontSize: 10, color: C.textM }}>{job.vehicleLabel} · <span style={{ color: personColor, fontWeight: 700 }}>{person?.name || "Unassigned"}</span></div>
                      </div>
                      <div style={{ display: "flex", gap: 6, alignItems: "center", flexShrink: 0, marginLeft: 8 }}>
                        {job.priority === "HIGH" && <Badge color={C.red} small>HIGH</Badge>}
                        <span style={{ fontSize: 10, color: C.textM }}>{job.estimatedHours || 1}h</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      ) : (
        <div>
          {/* Week nav */}
          <div style={{ display: "flex", gap: 8, marginBottom: 16, alignItems: "center" }}>
            <button onClick={() => {
              const d = new Date(viewDate + "T12:00:00"); d.setDate(d.getDate() - 5);
              setViewDate(d.toISOString().slice(0, 10));
            }} style={{ background: C.s2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}>← Back</button>
            <div style={{ flex: 1, textAlign: "center", fontSize: 11, fontWeight: 700, color: "#000" }}>
              Week of {new Date(viewDate + "T12:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric" })}
            </div>
            <button onClick={() => {
              const d = new Date(viewDate + "T12:00:00"); d.setDate(d.getDate() + 5);
              setViewDate(d.toISOString().slice(0, 10));
            }} style={{ background: C.s2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "6px 12px", cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}>Next →</button>
          </div>

          {/* Calendar grid */}
          {allDays.map(day => {
            const dayData = schedule[day];
            if (!dayData) return null;
            const isToday = day === today;
            const isPast = day < today;
            const dayLabel = new Date(day + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });
            
            // Collect all jobs across staff for this day
            const managerIds = staff.filter(s => ["manager","viewer"].includes(s.role) || s.username === "john").map(s => s.id);
            const allJobs = Object.entries(dayData).filter(([staffId]) => staffId !== '_sig' && !managerIds.includes(staffId)).flatMap(([staffId, jobs]) =>
              Array.isArray(jobs) ? jobs.map(job => ({ ...job, staffId })) : []
            ).filter(j => j && j.id);
            if (allJobs.length === 0) return null;

            return (
              <div key={day} style={{ marginBottom: 20 }}>
                {/* Day header */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
                  <div style={{ padding: "3px 10px", borderRadius: 20, background: isToday ? C.amber + "20" : isPast ? "#0001" : C.blue + "12", border: `1px solid ${isToday ? C.amber : isPast ? C.border : C.blue + "30"}`, fontSize: 11, fontWeight: 800, color: isToday ? C.amber : isPast ? C.textM : C.blue }}>
                    {isToday ? "TODAY — " : ""}{dayLabel}
                  </div>
                  <div style={{ flex: 1, height: 1, background: C.border }} />
                  <div style={{ fontSize: 10, color: C.textM }}>{allJobs.length} job{allJobs.length !== 1 ? "s" : ""}</div>
                </div>

                {/* Jobs by staff member */}
                {Object.entries(dayData).filter(([k]) => {
                  if (k === '_sig') return false;
                  const s = staff.find(x => x.id === k);
                  return !s || !["manager","viewer"].includes(s.role);
                }).map(([staffId, jobs]) => {
                  if (!Array.isArray(jobs) || !jobs.length) return null;
                  const person = getStaff(staffId);
                  const pColor = staffColors[staffId] || C.blue;
                  const totalHrs = jobs.reduce((s, j) => s + (j.estimatedHours || 1), 0);
                  return (
                    <div key={staffId} style={{ marginBottom: 8 }}>
                      {/* Staff label */}
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 5, paddingLeft: 4 }}>
                        <div style={{ width: 22, height: 22, borderRadius: "50%", background: pColor + "30", border: `2px solid ${pColor}60`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, color: pColor, flexShrink: 0 }}>
                          {person?.name?.[0] || "?"}
                        </div>
                        <span style={{ fontSize: 11, fontWeight: 700, color: pColor }}>{person?.name?.split(" ")[0] || staffId}</span>
                        <span style={{ fontSize: 10, color: C.textM }}>· {totalHrs.toFixed(1)}h</span>
                      </div>

                      {/* Job cards */}
                      <div style={{ paddingLeft: 14, borderLeft: `3px solid ${pColor}40`, display: "flex", flexDirection: "column", gap: 5 }}>
                        {jobs.map((job, i) => {
                          const vehicle = getVehicle(job.vehicleId);
                          const wo = getWO(vehicle, job.woId);
                          const partsReady = parts.filter(p => p.vehicleId === job.vehicleId && ["Received"].includes(p.status)).length;
                          const partsWaiting = parts.filter(p => p.vehicleId === job.vehicleId && ["Ordered","In Transit"].includes(p.status)).length;
                          return (
                            <div key={i}
                              onClick={() => setSelectedJob({ job, day, staffId, vehicle, wo })}
                              style={{ background: isPast ? C.s2 : C.surface, border: `2px solid ${job.priority === "HIGH" ? C.red + "50" : pColor + "30"}`, borderRadius: 10, padding: "10px 12px", cursor: "pointer", opacity: isPast ? 0.7 : 1 }}>
                              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                                <div style={{ flex: 1 }}>
                                  <div style={{ fontSize: 12, fontWeight: 700, color: "#000" }}>{job.description}</div>
                                  <div style={{ fontSize: 10, color: "#555", marginTop: 2 }}>{job.vehicleLabel}</div>
                                </div>
                                <div style={{ display: "flex", gap: 4, flexShrink: 0, marginLeft: 8 }}>
                                  {job.priority === "HIGH" && <Badge color={C.red} small>HIGH</Badge>}
                                  <span style={{ fontSize: 10, color: C.textM }}>{job.estimatedHours || 1}h</span>
                                </div>
                              </div>
                              {(partsReady > 0 || partsWaiting > 0) && (
                                <div style={{ display: "flex", gap: 6, marginTop: 5 }}>
                                  {partsReady > 0 && <Badge color={C.green} small>📦 {partsReady} parts ready</Badge>}
                                  {partsWaiting > 0 && <Badge color={C.amber} small>⏳ {partsWaiting} on order</Badge>}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


// ─── INVENTORY SYNC VIEW ─────────────────────────────────────────────────────

const InventorySyncView = ({ inventoryCache, setInventoryCache, vehicles, onUpdateVehicle, onSoldVehicle, currentUser, navigateTo }) => {  const [syncing, setSyncing] = useState(false);
  const [syncError, setSyncError] = useState(null);
  const [filter, setFilter] = useState("all"); // all | active | sold_detected | new

  const liveVehicles = inventoryCache?.vehicles || [];
  const lastSync = inventoryCache?.lastSync;

  const runSync = async () => {
    setSyncing(true); setSyncError(null);
    try {
      const fresh = await scrapeInventoryWithGrok();
      if (!fresh?.length) { setSyncError("No vehicles found — check if the site is accessible."); setSyncing(false); return; }
      const nc = { vehicles: fresh, lastSync: new Date().toISOString() };
      setInventoryCache(nc);
      await setInventoryCache_raw(nc);
    } catch (e) { setSyncError(e.message); }
    setSyncing(false);
  };

  // Cross-reference live inventory against app vehicles
  const appStockNums = new Set(vehicles.map(v => String(v.stockNumber)).filter(Boolean));
  const appVINs = new Set(vehicles.map(v => v.vin?.toUpperCase()).filter(Boolean));

  const enriched = liveVehicles.map(v => {
    const inApp = appStockNums.has(String(v.stockNumber)) || (v.vin && appVINs.has(v.vin.toUpperCase()));
    const appVehicle = vehicles.find(a => String(a.stockNumber) === String(v.stockNumber) || (v.vin && a.vin?.toUpperCase() === v.vin?.toUpperCase()));
    return { ...v, inApp, appStage: appVehicle?.stage, appId: appVehicle?.id };
  });

  // Vehicles in app but NOT on site (possibly sold)
  const possiblyGone = detectSoldVehicles(liveVehicles, vehicles);

  const filtered = filter === "new" ? enriched.filter(v => !v.inApp)
    : filter === "sold_detected" ? possiblyGone
    : filter === "in_app" ? enriched.filter(v => v.inApp)
    : enriched;

  const handleSoldDetected = (appVehicle) => {
    const hasWork = appVehicle.workOrders?.length > 0 || appVehicle.stage !== "INTAKE";
    if (hasWork) {
      // Moved to sold archive — keep the record
      onSoldVehicle(appVehicle);
    } else {
      // Never worked on — remove completely, no trace
      onUpdateVehicle({ ...appVehicle, _delete: true });
    }
  };

  return (
    <div>
      {/* Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
        <div>
          <div style={{ fontSize: 26, fontWeight: 800, color: C.amber, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.06em" }}>VEHICLE INVENTORY</div>
          <div style={{ fontSize: 11, color: C.textM }}>
            {lastSync ? `Last sync: ${new Date(lastSync).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })} · ${liveVehicles.length} vehicles live` : "Not synced yet"}
          </div>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Btn variant="amber" size="sm" onClick={runSync} disabled={syncing}>
            {syncing ? <><RefreshCw size={12} style={{ animation: "spin 1s linear infinite" }} /> Syncing…</> : <><RefreshCw size={12} /> Sync Now</>}
          </Btn>
        </div>
      </div>

      {syncError && <div style={{ background: C.red+"12", border: `1px solid ${C.red}30`, borderRadius: 8, padding: "8px 12px", marginBottom: 12, fontSize: 11, color: C.red }}>{syncError}</div>}

      {/* Sold detection alert */}
      {possiblyGone.length > 0 && (
        <div style={{ background: C.red+"10", border: `2px solid ${C.red}40`, borderRadius: 10, padding: "12px 14px", marginBottom: 14 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: C.red, marginBottom: 8 }}>⚠ {possiblyGone.length} vehicle{possiblyGone.length > 1 ? "s" : ""} no longer on site — may have sold</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {possiblyGone.map(v => {
              const hasWork = v.workOrders?.length > 0 || v.stage !== "INTAKE";
              return (
                <div key={v.id} style={{ background: "#fff", border: `1px solid ${C.red}30`, borderRadius: 8, padding: "8px 12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div>
                    <div style={{ fontSize: 12, fontWeight: 700 }}>{v.year} {v.make} {v.model} #{v.stockNumber}</div>
                    <div style={{ fontSize: 10, color: "#555" }}>Stage: {v.stage} · {hasWork ? "Has work history — will archive" : "No work done — will delete completely"}</div>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <button onClick={() => handleSoldDetected(v)}
                      style={{ background: C.red+"15", border: `1px solid ${C.red}40`, borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 10, fontWeight: 700, color: C.red, fontFamily: "inherit" }}>
                      {hasWork ? "Archive" : "Remove"}
                    </button>
                    <button onClick={() => {/* dismiss */}}
                      style={{ background: C.s2, border: `1px solid ${C.border}`, borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 10, color: C.textM, fontFamily: "inherit" }}>
                      Keep
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Filter tabs */}
      <div style={{ display: "flex", gap: 6, marginBottom: 14, flexWrap: "wrap" }}>
        {[
          { id: "all", label: `All (${enriched.length})` },
          { id: "new", label: `Not in App (${enriched.filter(v => !v.inApp).length})`, alert: enriched.filter(v => !v.inApp).length > 0 },
          { id: "in_app", label: `In App (${enriched.filter(v => v.inApp).length})` },
          { id: "sold_detected", label: `Sold? (${possiblyGone.length})`, alert: possiblyGone.length > 0 },
        ].map(tab => (
          <button key={tab.id} onClick={() => setFilter(tab.id)}
            style={{ padding: "5px 12px", borderRadius: 20, border: `1px solid ${filter === tab.id ? C.amber : tab.alert ? C.red+"60" : C.border}`, background: filter === tab.id ? C.amber+"20" : "transparent", color: filter === tab.id ? C.amber : tab.alert ? C.red : C.textS, fontWeight: 700, fontSize: 11, cursor: "pointer", fontFamily: "inherit" }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Vehicle list */}
      {liveVehicles.length === 0 ? (
        <div style={{ textAlign: "center", padding: "48px 0", color: C.textM }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🏢</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#000", marginBottom: 6 }}>No inventory synced yet</div>
          <div style={{ fontSize: 11, marginBottom: 20 }}>Tap Sync Now to pull from edgewaterautocenter.com</div>
          <Btn variant="amber" onClick={runSync} disabled={syncing}>{syncing ? "Syncing…" : "Sync Now"}</Btn>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {(filter === "sold_detected" ? possiblyGone : filtered).map((v, i) => {
            const isGone = filter === "sold_detected";
            const inApp = !isGone && v.inApp;
            const appStage = v.appStage;
            return (
              <div key={v.stockNumber || i} style={{ background: C.surface, border: `1px solid ${inApp ? C.green+"40" : C.border}`, borderRadius: 10, padding: "12px 14px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: "#000" }}>
                      {v.year} {v.make} {v.model} {v.trim || ""}
                    </div>
                    <div style={{ fontSize: 10, color: "#555", marginTop: 2 }}>
                      #{v.stockNumber}
                      {v.mileage && ` · ${parseInt(v.mileage).toLocaleString()} mi`}
                      {v.price && ` · $${parseInt(v.price).toLocaleString()}`}
                      {v.exteriorColor && ` · ${v.exteriorColor}`}
                    </div>
                    {v.vin && <div style={{ fontSize: 9, color: C.textM, fontFamily: "monospace", marginTop: 2 }}>VIN: {v.vin}</div>}
                    <div style={{ fontSize: 10, color: "#555", marginTop: 2 }}>
                      {[v.engine, v.drivetrain, v.transmission].filter(Boolean).join(" · ")}
                    </div>
                  </div>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4 }}>
                    {inApp ? (
                      <Badge color={C.green} small>In App · {appStage?.replace("_"," ")}</Badge>
                    ) : isGone ? (
                      <Badge color={C.red} small>Not on site</Badge>
                    ) : (
                      <Badge color={C.textM} small>Not yet in app</Badge>
                    )}
                    {v.scrapedAt && <div style={{ fontSize: 9, color: C.textM }}>{new Date(v.scrapedAt).toLocaleDateString()}</div>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── DIAGNOSTICS VIEW ─────────────────────────────────────────────────────────

const DiagnosticsView = ({ vehicles, parts, staff, vendors, ledger, onUpdateVehicle, onAddPart, onAddLedger, openVehicle, preselectedVehicle, preselectedMode }) => {
  const [activeVehicle, setActiveVehicle] = useState(preselectedVehicle || null);
  const [diagMode, setDiagMode] = useState(preselectedMode || null); // "julia" | "hank" | null
  const [chooserOpen, setChooserOpen] = useState(!!preselectedVehicle && !preselectedMode);
  const needs = vehicles.filter(v => ["INTAKE","INITIAL_DIAG"].includes(v.stage));
  const recent = vehicles.filter(v => v.stage === "MECH_VERIFY" && v.workOrders?.some(w => w.diagnosisRationale));

  const handleDone = (updatedVehicle, newWOs) => {
    onUpdateVehicle(updatedVehicle);
    // After Hank: check if body work needed → chain to Jimmy
    const needsBody = (newWOs || []).some(w => w.isBodyWork || w.category === "COSMETIC") ||
      updatedVehicle.bodyRepairs?.some(r => r.status === "OPEN");
    if (needsBody) {
      setActiveVehicle(updatedVehicle);
      setDiagMode("jimmy");
    } else {
      setActiveVehicle(null);
      setDiagMode(null);
      if (openVehicle) openVehicle(updatedVehicle);
    }
  };

  const [transcriptModal, setTranscriptModal] = useState(null);
  const exitInspection = () => {
    setActiveVehicle(null);
    setDiagMode(null);
    setChooserOpen(false);
  };

  const handleInspectionDone = (updatedVehicle, newWOs, needsMechanical) => {
    onUpdateVehicle(updatedVehicle);
    const needsBody = updatedVehicle.bodyRepairs?.some(r => r.status === "OPEN") ||
      (newWOs || []).some(w => w.category === "COSMETIC" || w.isBodyWork);
    if (needsMechanical) {
      setDiagMode("hank");
      setActiveVehicle(updatedVehicle);
    } else if (needsBody) {
      setDiagMode("jimmy");
      setActiveVehicle(updatedVehicle);
    } else {
      setActiveVehicle(null);
      setDiagMode(null);
      if (openVehicle) openVehicle(updatedVehicle);
    }
  };

  if (activeVehicle && diagMode === "hank") {
    const hasVehicle = activeVehicle.year || activeVehicle.make;
    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <button onClick={() => { setActiveVehicle(null); setDiagMode(null); }} style={{ background: "none", border: "none", color: C.textS, cursor: "pointer", fontSize: 11, padding: 0, display: "flex", alignItems: "center", gap: 4 }}><img src="/images/btn-back-arrow.jpg" alt="" style={{width:16,height:16,objectFit:"contain",flexShrink:0}} onError={e=>e.target.style.display="none"} /> Back</button>
            <SiteLogo size="sm" />
          </div>
          <button onClick={exitInspection} style={{ background: C.red+"12", border: `1px solid ${C.red}30`, borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 10, color: C.red, fontFamily: "inherit" }}>✕ Exit</button>
        </div>
        {/* Vehicle identity — only show if we know the vehicle */}
        {hasVehicle && (
          <div style={{ background:C.s2, border:`1px solid ${C.border}`, borderRadius:8, padding:"8px 14px", marginBottom:12 }}>
            <div style={{ fontSize:13, fontWeight:800, color:"#000" }}>
              {activeVehicle.year} {activeVehicle.make} {activeVehicle.model}{activeVehicle.trim?" "+activeVehicle.trim:""}
              {activeVehicle.stockNumber && activeVehicle.stockNumber!=="GUEST" && activeVehicle.stockNumber!==""
                ? <span style={{ fontSize:10, color:C.textM, marginLeft:8 }}>#{activeVehicle.stockNumber}</span>
                : <span style={{ fontSize:10, color:C.amber, marginLeft:8 }}>Guest vehicle</span>}
            </div>
            {(activeVehicle.transmission||activeVehicle.drivetrain||activeVehicle.mileage) && (
              <div style={{ fontSize:10, color:C.textM, marginTop:2 }}>
                {[activeVehicle.transmission,activeVehicle.drivetrain,activeVehicle.mileage?parseInt(activeVehicle.mileage).toLocaleString()+"mi":null].filter(Boolean).join(" · ")}
              </div>
            )}
          </div>
        )}
        <DiagnosticChat
          vehicle={activeVehicle}
          onVehicleUpdate={(updatedVehicle) => setActiveVehicle(updatedVehicle)}
          onDiagnosisComplete={handleDone}
          onClose={() => { setActiveVehicle(null); setDiagMode(null); }}
          staff={staff} vehicles={vehicles} vendors={vendors}
          addLedger={onAddLedger} addPart={onAddPart} updateVehicle={onUpdateVehicle}
        />
      </div>
    );
  }

  if (activeVehicle && diagMode === "julia") {
    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <button onClick={() => { setActiveVehicle(null); setDiagMode(null); }} style={{ background: "none", border: "none", color: C.textS, cursor: "pointer", fontSize: 11, padding: 0, display: "flex", alignItems: "center", gap: 4 }}><img src="/images/btn-back-arrow.jpg" alt="" style={{width:16,height:16,objectFit:"contain",flexShrink:0}} onError={e=>e.target.style.display="none"} /> Back</button>
          <button onClick={exitInspection} style={{ background: C.red+"12", border: `1px solid ${C.red}30`, borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 10, color: C.red, fontFamily: "inherit" }}>✕ Exit Inspection</button>
        </div>
        <JuliaInspectionChat
          vehicle={activeVehicle}
          updateVehicle={onUpdateVehicle}
          onInspectionComplete={handleInspectionDone}
          onClose={exitInspection}
        />
      </div>
    );
  }

  if (activeVehicle && diagMode === "jimmy") {
    return (
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <button onClick={() => { setActiveVehicle(null); setDiagMode(null); }} style={{ background: "none", border: "none", color: C.textS, cursor: "pointer", fontSize: 11, padding: 0, display: "flex", alignItems: "center", gap: 4 }}><img src="/images/btn-back-arrow.jpg" alt="" style={{width:16,height:16,objectFit:"contain",flexShrink:0}} onError={e=>e.target.style.display="none"} /> Back</button>
          <button onClick={exitInspection} style={{ background: C.red+"12", border: `1px solid ${C.red}30`, borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 10, color: C.red, fontFamily: "inherit" }}>✕ Exit Inspection</button>
        </div>
        <div style={{ fontSize: 18, fontWeight: 800, color: C.text, fontFamily: "'Bebas Neue', sans-serif", marginBottom: 4 }}>{activeVehicle.year} {activeVehicle.make} {activeVehicle.model} #{activeVehicle.stockNumber}</div>
        <BodyWorkChat
          vehicle={activeVehicle}
          onComplete={(newWOs, repairs) => {
            const updated = { ...activeVehicle, stage: "COSMETIC", bodyRepairs: [...(activeVehicle.bodyRepairs || []), { ...repairs, status: "OPEN", createdAt: todayStr() }], workOrders: [...(activeVehicle.workOrders || []), ...newWOs] };
            onUpdateVehicle(updated);
            setActiveVehicle(null); setDiagMode(null);
            if (openVehicle) openVehicle(updated);
          }}
          onClose={exitInspection}
          staff={staff} vendors={vendors}
        />
      </div>
    );
  }

  if (chooserOpen && activeVehicle) {
    return (
      <div>
        <button onClick={() => { setChooserOpen(false); setActiveVehicle(null); }} style={{ background: "none", border: "none", color: C.textS, cursor: "pointer", fontSize: 11, padding: 0, marginBottom: 16, display: "flex", alignItems: "center", gap: 4 }}><img src="/images/btn-back-arrow.jpg" alt="" style={{width:16,height:16,objectFit:"contain",flexShrink:0}} onError={e=>e.target.style.display="none"} /> Back</button>
        <div style={{ fontSize: 18, fontWeight: 800, color: C.text, fontFamily: "'Bebas Neue', sans-serif", marginBottom: 4 }}>{activeVehicle.year} {activeVehicle.make} {activeVehicle.model} #{activeVehicle.stockNumber}</div>
        <DiagnosisChooser
          vehicle={activeVehicle}
          onChoose={(mode) => { setDiagMode(mode); setChooserOpen(false); }}
          onClose={() => { setChooserOpen(false); setActiveVehicle(null); }}
          onSkip={(mode) => {
            // Update stage first
            const targetStage = mode === "hank" ? "MECH_VERIFY" : "COSMETIC";
            const updated = { ...activeVehicle, stage: targetStage, skippedInspection: true, skippedAt: todayStr() };
            onUpdateVehicle(updated);
            setActiveVehicle(updated);
            setChooserOpen(false);
            // Then open the agent directly
            setDiagMode(mode);
          }}
        />
      </div>
    );
  }

  const allActive = vehicles.filter(v => !["SOLD","SCRAPPED"].includes(v.stage));
  const agentIcon = { julia: "🔍", hank: "🔧", jimmy: "🔨" };
  const agentColor = { julia: C.purple, hank: C.amber, jimmy: C.blue };

  return (
    <div>
      <div style={{ fontSize: 26, fontWeight: 800, color: C.amber, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.06em", marginBottom: 4 }}>AI DIAGNOSTICS</div>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
        <div style={{ fontSize: 11, color: C.textS }}>All active vehicles — tap an agent to diagnose · or open Hank for any vehicle</div>
        <Btn variant="amber" size="sm" onClick={() => {
          const blankVehicle = {
            id: "intake_"+Date.now(), year:"", make:"", model:"", trim:"",
            mileage:"", transmission:"", drivetrain:"", stockNumber:"",
            stage:"INTAKE", workOrders:[], isGuest:true,
            _inventoryList: vehicles.filter(v=>!["SOLD","SCRAPPED"].includes(v.stage))
              .map(v=>`#${v.stockNumber} ${v.year} ${v.make} ${v.model}${v.trim?" "+v.trim:""}${v.mileage?" ("+parseInt(v.mileage).toLocaleString()+"mi)":""}`)
              .join("\n")
          };
          setActiveVehicle(blankVehicle);
          setChooserOpen(false);
          setDiagMode("hank");
        }}>🔧 Open Hank</Btn>
      </div>


      {/* Transcript modal */}
      {transcriptModal && (
        <Modal title={`${agentIcon[transcriptModal.agent]} ${transcriptModal.agent.toUpperCase()} Diagnosis — ${transcriptModal.vehicle.year} ${transcriptModal.vehicle.make} ${transcriptModal.vehicle.model}`} onClose={() => setTranscriptModal(null)} wide>
          <div style={{ maxHeight: 420, overflowY: "auto" }}>
            {(() => {
              const v = transcriptModal.vehicle;
              const agent = transcriptModal.agent;
              const transcript = agent === "julia" ? v.juliaTranscript
                : agent === "hank" ? v.hankTranscript
                : v.jimmyTranscript;
              if (!transcript?.length) return <div style={{ color: C.textM, fontSize: 12 }}>No transcript saved.</div>;
              return transcript.map((m, i) => (
                <div key={i} style={{ marginBottom: 8, display: "flex", flexDirection: "column", alignItems: m.role === "user" ? "flex-end" : "flex-start" }}>
                  <div style={{ background: m.role === "user" ? C.blue+"15" : C.s2, border: `1px solid ${m.role==="user"?C.blue+"30":C.border}`, borderRadius: 10, padding: "8px 12px", maxWidth: "85%", fontSize: 12, color: C.text }}>
                    {m.content}
                  </div>
                </div>
              ));
            })()}
          </div>
          <div style={{ marginTop: 12, display: "flex", gap: 8 }}>
            <Btn variant="purple" onClick={() => { setTranscriptModal(null); setActiveVehicle(transcriptModal.vehicle); setChooserOpen(false); setDiagMode(transcriptModal.agent); }}>
              🔁 Re-Diagnose with {transcriptModal.agent.charAt(0).toUpperCase() + transcriptModal.agent.slice(1)}
            </Btn>
            <Btn variant="ghost" onClick={() => setTranscriptModal(null)}>Close</Btn>
          </div>
        </Modal>
      )}

      {allActive.length === 0 ? (
        <div style={{ textAlign: "center", color: C.textM, padding: 40 }}>No active vehicles</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
          {allActive.map(v => {
            const openWOs = (v.workOrders||[]).filter(w => w.status !== "COMPLETE" && !w.deleted);
            const hasJulia = !!(v.juliaInspectionSummary || v.juliaTranscript?.length);
            const hasHank = !!(v.conditionSummary || v.hankTranscript?.length);
            const hasJimmy = !!(v.bodyRepairs?.length || v.jimmyTranscript?.length);
            return (
              <div key={v.id} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                  <div style={{ cursor: "pointer" }} onClick={() => openVehicle && openVehicle(v)}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: C.text }}>{v.year} {v.make} {v.model} <span style={{ fontSize: 10, color: C.textM, fontFamily: "monospace" }}>#{v.stockNumber}</span></div>
                    <div style={{ display: "flex", gap: 4, marginTop: 4, flexWrap: "wrap" }}>
                      <StageBadge stageId={v.stage} small />
                      {openWOs.length > 0 && <Badge color={C.amber} small>{openWOs.length} open WO{openWOs.length!==1?"s":""}</Badge>}
                    </div>
                  </div>
                  <Btn variant="purple" size="sm" onClick={() => { setActiveVehicle(v); setChooserOpen(true); setDiagMode(null); }}>
                    + Diagnose
                  </Btn>
                </div>
                {/* Agent status row */}
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {[
                    { id: "julia", label: "Julia", has: hasJulia },
                    { id: "hank",  label: "Hank",  has: hasHank },
                    { id: "jimmy", label: "Jimmy", has: hasJimmy },
                  ].map(agent => (
                    <button key={agent.id}
                      onClick={() => agent.has
                        ? setTranscriptModal({ vehicle: v, agent: agent.id })
                        : (setActiveVehicle(v), setChooserOpen(false), setDiagMode(agent.id))
                      }
                      style={{ background: agent.has ? agentColor[agent.id]+"15" : C.s2, border: `1px solid ${agent.has ? agentColor[agent.id]+"50" : C.border}`, borderRadius: 7, padding: "4px 10px", cursor: "pointer", fontSize: 10, fontWeight: 700, color: agent.has ? agentColor[agent.id] : C.textM, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 4 }}>
                      {agentIcon[agent.id]} {agent.label}
                      {agent.has ? " ✓" : " +"}
                    </button>
                  ))}
                </div>
                {/* Diagnosed WOs */}
                {openWOs.filter(w => w.diagnosisRationale).length > 0 && (
                  <div style={{ marginTop: 8, fontSize: 10, color: C.textM }}>
                    Diagnosed WOs: {openWOs.filter(w=>w.diagnosisRationale).map(w=>w.description).join(" · ")}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ─── AI SCHEDULING SYSTEM ────────────────────────────────────────────────────

const WORK_HOURS_PER_DAY = 8;
const SCHEDULED_HOURS_MAX = 6; // 2 hours buffer left unscheduled

const getWorkDays = (startDate, count) => {
  const days = [];
  let d = new Date(startDate + "T12:00:00");
  while (days.length < count) {
    if (d.getDay() !== 0 && d.getDay() !== 6) days.push(d.toISOString().slice(0, 10));
    d.setDate(d.getDate() + 1);
  }
  return days;
};

// Build schedule using Claude
const generateSchedule = async (vehicles, staff, existingSchedule, apiKey) => {
  const activeVehicles = vehicles.filter(v => !["LINE_READY","SOLD","SCRAPPED"].includes(v.stage));
  const workDays = getWorkDays(todayStr(), 3);
  const schedulableStaff = staff.filter(s => !["manager"].includes(s.role));

  // Build context for Claude
  const vehicleList = activeVehicles.map(v => ({
    id: v.id, vehicle: `${v.year} ${v.make} ${v.model} #${v.stockNumber}`,
    stage: v.stage,
    openWOs: (v.workOrders || []).filter(w => w.status !== "COMPLETE").map(w => ({
      id: w.id, description: w.description, category: w.category,
      estimatedHours: w.estimatedHours || 1, priority: w.priority,
      assignedTo: w.assignedTo
    }))
  })).filter(v => v.openWOs.length > 0);

  const staffList = schedulableStaff.map(s => ({ id: s.id, name: s.name, role: s.role }));

  const prompt = `You are scheduling work at Edgewater Auto Center for the next 3 work days: ${workDays.join(", ")}.

STAFF: ${JSON.stringify(staffList)}
WORK DAYS: ${workDays.join(", ")}
MAX SCHEDULED HOURS PER PERSON PER DAY: ${SCHEDULED_HOURS_MAX} (8hr day, 2hr buffer kept free)
VEHICLES WITH OPEN WORK ORDERS: ${JSON.stringify(vehicleList)}

RULES:
- Each staff member can be assigned max ${SCHEDULED_HOURS_MAX} hours of work per day
- Assign work by role: james=MECHANICAL, sky=DETAIL+LOT tasks, robert=COSMETIC+delivery prep
- Prioritize HIGH priority WOs first, then MEDIUM, then LOW
- Spread work across 3 days — don't pile everything on day 1
- Each WO has an estimatedHours — use that for scheduling
- If a WO has no assignedTo, pick the best person by role

Return ONLY valid JSON:
{
  "schedule": {
    "YYYY-MM-DD": {
      "staffId": [
        {"woId": "wo_id", "vehicleId": "v_id", "vehicleLabel": "2019 Honda Accord #12345", "description": "wo description", "estimatedHours": 1.5, "priority": "HIGH"}
      ]
    }
  },
  "estimatedCompletions": {
    "vehicleId": "YYYY-MM-DD"
  }
}`;

  const res = await fetch("/api/claude", { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${await getIdToken()}` },
    body: JSON.stringify({ model: "claude-sonnet-4-6", max_tokens: 2000, messages: [{ role: "user", content: prompt }] })
  });
  const data = await res.json();
  const raw = data.content?.[0]?.text || "{}";
  const start = raw.indexOf("{"); const end = raw.lastIndexOf("}");
  return (start >= 0 && end > start) ? JSON.parse(raw.slice(start, end + 1)) : null;
};

// ─── SCHEDULE / TIME TRACKING ─────────────────────────────────────────────────

const ScheduleView = ({ staff, vehicles, timeEntries, setTimeEntries, ledger, onAddLedger, schedule, setSchedule, currentUser, onUpdateVehicle, onRebuildSchedule, onNavigateDelivery, tasks, onUpdateTask, openVehicle, navigateTo }) => {
  const [today] = useState(todayStr());
  const [checkinModal, setCheckinModal] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(["manager","office"].includes(currentUser?.role) ? "all" : (currentUser?.id || staff[0]?.id || ""));
  const [scheduleTab, setScheduleTab] = useState("myschedule");
  const [generating, setGenerating] = useState(false);
  const [scheduleError, setScheduleError] = useState(null);
  const [calView, setCalView] = useState("list"); // "list" | "calendar"
  const [calWeekStart, setCalWeekStart] = useState(todayStr());
  const workDays = getWorkDays(today, 3);
  const isManager = ["manager"].includes(currentUser?.role);

  // Auto-build schedule whenever we have vehicles + staff but no schedule entries
  useEffect(() => {
    const activeVehicles = vehicles.filter(v => !["SOLD","SCRAPPED"].includes(v.stage));
    const workers = staff.filter(s => !["manager","viewer"].includes(s.role) && s.username !== "john");
    const hasEntries = Object.keys(schedule || {}).filter(k => k !== "_sig").length > 0;
    if (!hasEntries && activeVehicles.length > 0 && workers.length > 0 && onRebuildSchedule) {
      onRebuildSchedule();
    }
  }, [vehicles.length, staff.length, Object.keys(schedule||{}).length]);

  const runSchedule = () => {
    setGenerating(true); setScheduleError(null);
    try {
      if (onRebuildSchedule) {
        onRebuildSchedule();
        setTimeout(() => setGenerating(false), 600);
      } else {
        setScheduleError("Schedule builder not available");
        setGenerating(false);
      }
    } catch (e) { setScheduleError(e.message); setGenerating(false); }
  };

  const activeEntry = (staffId) => timeEntries.find(e => e.staffId === staffId && e.date === today && !e.endTime);
  const todayEntries = (staffId) => timeEntries.filter(e => e.staffId === staffId && e.date === today);

  const totalHoursToday = (staffId) => {
    return todayEntries(staffId).reduce((sum, e) => {
      const start = new Date(`${e.date}T${e.startTime}`);
      const end = e.endTime ? new Date(`${e.date}T${e.endTime}`) : new Date();
      return sum + (end - start) / 3600000;
    }, 0);
  };

  const checkIn = (staffId, vehicleId, woId, description) => {
    const entry = { id: genId(), staffId, vehicleId, woId, description, date: today, startTime: new Date().toTimeString().slice(0, 5), endTime: null };
    setTimeEntries(prev => [...prev, entry]);
    setCheckinModal(null);
  };

  const checkOut = (entryId) => {
    const endTime = new Date().toTimeString().slice(0, 5);
    setTimeEntries(prev => prev.map(e => {
      if (e.id !== entryId) return e;
      const updated = { ...e, endTime };
      // Post labor cost to ledger
      if (e.vehicleId) {
        const sm = staff.find(s => s.id === e.staffId);
        const startD = new Date(`${e.date}T${e.startTime}`);
        const endD = new Date(`${e.date}T${endTime}`);
        const hrs = (endD - startD) / 3600000;
        const cost = hrs * (sm?.hourlyRate || 0);
        if (cost > 0) {
          onAddLedger({ id: genId(), vehicleId: e.vehicleId, category: "LABOR", description: `Labor: ${sm?.name} — ${e.description} (${hrs.toFixed(1)}h @ $${sm?.hourlyRate}/hr)`, amount: cost, date: today, auto: true });
        }
      }
      return updated;
    }));
  };

  const sm = staff.find(s => s.id === selectedStaff);
  const active = activeEntry(selectedStaff);
  const hrs = totalHoursToday(selectedStaff);
  const remaining = (sm?.dailyHours || 8) - hrs;
  const role = STAFF_ROLES.find(r => r.id === sm?.role);

  const activeVehicles = vehicles.filter(v => !["LINE_READY","SOLD"].includes(v.stage));

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
        <div style={{ display:"flex",alignItems:"center",gap:10 }}><img src="/images/nav-schedule.jpg" alt="" style={{width:20,height:20,objectFit:"contain",flexShrink:0,borderRadius:4}} onError={e=>e.target.style.display="none"} /><div style={{ fontSize: 26, fontWeight: 800, color: C.amber, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.06em" }}>SCHEDULE</div></div>
        <div style={{ display:"flex", gap:6, flexWrap:"wrap", alignItems:"center" }}>
          <Btn variant="outline" size="sm" onClick={() => setScheduleTab("timelog")}>
            ⏱ Time Log
          </Btn>
{isManager && (
            <Btn variant="amber" size="sm" onClick={runSchedule} disabled={generating}>
              {generating ? <><RefreshCw size={11} style={{ animation: "spin 1s linear infinite" }} /> Scheduling…</> : "✨ Auto-Schedule 3 Days"}
            </Btn>
          )}
        </div>
      </div>
      <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:8 }}>
        <Btn variant="outline" size="sm" onClick={() => { if (navigateTo) navigateTo("myjobs"); }}>
          📋 My Assigned Jobs
        </Btn>
      </div>
      {scheduleError && <div style={{ background: C.red + "12", border: `1px solid ${C.red}30`, borderRadius: 6, padding: "8px 12px", marginBottom: 12, fontSize: 11, color: C.red }}>{scheduleError}</div>}


      {/* Tabs */}


      {/* MY SCHEDULE — 3-day job list (no hours shown) */}
      {scheduleTab === "myschedule" && (
        <div>
            {/* General tasks for this user */}
            {tasks?.filter(t => t.assignedTo === currentUser?.username && t.status === "OPEN").map(task => (
              <div key={task.id} style={{ background:`linear-gradient(135deg,${C.amber}12,${C.amber}05)`, border:`2px solid ${C.amber}50`, borderRadius:10, padding:"11px 14px", marginBottom:8 }}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", gap:10 }}>
                  <div style={{ flex:1 }}>
                    <div style={{ fontSize:11, fontWeight:800, color:C.amber, textTransform:"uppercase", letterSpacing:"0.05em", marginBottom:2 }}>📋 Task</div>
                    <div style={{ fontSize:13, fontWeight:700, color:"#000" }}>{task.description}</div>
                    <div style={{ fontSize:10, color:C.textM, marginTop:2 }}>
                      From {task.assignedByName || task.assignedBy}
                      {task.dueDate && <span style={{ color:C.red, marginLeft:8 }}>⏰ Due {task.dueDate}</span>}
                    </div>
                  </div>
                  <button onClick={() => onUpdateTask && onUpdateTask({ ...task, status:"COMPLETE", completedAt:new Date().toISOString(), completedBy:currentUser?.username })}
                    style={{ background:C.green, border:"none", borderRadius:8, padding:"8px 14px", cursor:"pointer", fontSize:11, fontWeight:700, color:"#fff", fontFamily:"inherit", whiteSpace:"nowrap" }}>
                    ✓ Done
                  </button>
                </div>
              </div>
            ))}

          {workDays.map(day => {
            const dayJobs = (schedule?.[day]?.[selectedStaff] || []);
            const isToday = day === today;
            return (
              <div key={day} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: isToday ? C.amber : "#000", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8, display: "flex", justifyContent: "space-between" }}>
                  <span>{isToday ? "TODAY — " : ""}{new Date(day + "T12:00:00").toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}</span>
                  <span style={{ fontSize: 10, color: C.textM }}>{dayJobs.length} job{dayJobs.length !== 1 ? "s" : ""}</span>
                </div>
                {dayJobs.length === 0 ? (
                  <div style={{ background: C.s2, borderRadius: 8, padding: "12px 14px", fontSize: 11, color: C.textM, fontStyle: "italic" }}>No jobs scheduled</div>
                ) : (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    {dayJobs.map((job, i) => (
                      <div key={i} style={{ background: C.surface, border: `2px solid ${job.priority === "HIGH" ? C.red + "50" : C.border}`, borderRadius: 10, padding: "10px 14px" }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#000" }}>{job.description}</div>
                        <div style={{ fontSize: 10, color: C.textM }}>{job.vehicleLabel}</div>
                        {job.priority === "HIGH" && <Badge color={C.red} small>HIGH PRIORITY</Badge>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* MANAGE ALL STAFF — manager only */}
      {scheduleTab === "manage" && isManager && (
        <div>
          {/* Staff selector for manager view */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 16 }}>
            {staff.filter(s => s.role !== "manager").map(s => {
              const r = STAFF_ROLES.find(x => x.id === s.role);
              const act = activeEntry(s.id);
              return (
                <button key={s.id} onClick={() => setSelectedStaff(s.id)} style={{ padding: "8px 14px", borderRadius: 8, border: `2px solid ${selectedStaff === s.id ? (r?.color || C.amber) : C.border}`, background: selectedStaff === s.id ? (r?.color || C.amber) + "20" : C.surface, color: C.text, fontWeight: 700, fontSize: 11, fontFamily: "inherit", cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
                  {s.name.split(" ")[0]}
                  {act && <div style={{ width: 7, height: 7, borderRadius: "50%", background: C.green }} />}
                </button>
              );
            })}
          </div>
          {workDays.map(day => {
            const dayJobs = (schedule?.[day]?.[selectedStaff] || []);
            const totalHrs = dayJobs.reduce((s, j) => s + (j.estimatedHours || 1), 0);
            return (
              <div key={day} style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: "#000", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8, display: "flex", justifyContent: "space-between" }}>
                  <span>{new Date(day + "T12:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}</span>
                  <span style={{ fontSize: 10, color: totalHrs > SCHEDULED_HOURS_MAX ? C.red : C.green }}>{totalHrs.toFixed(1)}/{SCHEDULED_HOURS_MAX}h</span>
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                  {dayJobs.map((job, i) => (
                    <div key={i} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontSize: 11, fontWeight: 700, color: "#000" }}>{job.description}</div>
                        <div style={{ fontSize: 9, color: C.textM }}>{job.vehicleLabel} · {job.estimatedHours || 1}h est.</div>
                      </div>
                      <div style={{ display: "flex", gap: 4 }}>
                        <button onClick={() => {
                          // Move job to top of this day
                          const daySchedule = [...dayJobs];
                          const [item] = daySchedule.splice(i, 1);
                          daySchedule.unshift(item);
                          setSchedule(prev => ({ ...prev, [day]: { ...(prev[day] || {}), [selectedStaff]: daySchedule } }));
                        }} style={{ background: C.s2, border: `1px solid ${C.border}`, borderRadius: 4, padding: "2px 6px", cursor: "pointer", fontSize: 10, fontFamily: "inherit" }}>↑ Top</button>
                        <button onClick={() => {
                          setSchedule(prev => ({ ...prev, [day]: { ...(prev[day] || {}), [selectedStaff]: dayJobs.filter((_, j) => j !== i) } }));
                        }} style={{ background: C.red + "10", border: `1px solid ${C.red}30`, borderRadius: 4, padding: "2px 6px", cursor: "pointer", fontSize: 10, color: C.red, fontFamily: "inherit" }}>✕</button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {/* tasks shown separately below */}
        </div>
      )}


      {/* Manager task list — open and completed */}
      {isManager && tasks?.length > 0 && (
        <div style={{ marginTop:16 }}>
          {tasks.filter(t=>t.status==="OPEN").length > 0 && (
            <div style={{ marginBottom:12 }}>
              <div style={{ fontSize:11, fontWeight:700, color:C.textM, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:8 }}>📋 Open Tasks ({tasks.filter(t=>t.status==="OPEN").length})</div>
              {tasks.filter(t=>t.status==="OPEN").map(task => {
                const assignee = staff.find(s => s.username === task.assignedTo);
                return (
                  <div key={task.id} style={{ background:C.surface, border:`1px solid ${C.amber}40`, borderRadius:9, padding:"10px 14px", marginBottom:8, display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:10 }}>
                    <div style={{ flex:1 }}>
                      <div style={{ fontSize:12, fontWeight:700, color:"#000" }}>{task.description}</div>
                      <div style={{ fontSize:10, color:C.textM, marginTop:2 }}>
                        Assigned to: {assignee?.name || task.assignedTo}
                        {task.dueDate && <span style={{ color:C.red, marginLeft:8 }}>Due {task.dueDate}</span>}
                      </div>
                    </div>
                    <button onClick={() => onUpdateTask && onUpdateTask({ ...task, status:"COMPLETE", completedAt:new Date().toISOString(), completedBy:task.assignedTo })}
                      style={{ background:"none", border:`1px solid ${C.green}`, borderRadius:7, padding:"4px 10px", cursor:"pointer", fontSize:10, fontWeight:700, color:C.green, fontFamily:"inherit" }}>
                      ✓ Mark Done
                    </button>
                  </div>
                );
              })}
            </div>
          )}
          {tasks.filter(t=>t.status==="COMPLETE").length > 0 && (
            <div>
              <div style={{ fontSize:11, fontWeight:700, color:C.textM, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:6 }}>✅ Completed Tasks</div>
              {tasks.filter(t=>t.status==="COMPLETE").slice(-20).reverse().map(task => {
                const assignee = staff.find(s => s.username === task.assignedTo);
                const completedDate = task.completedAt ? new Date(task.completedAt).toLocaleDateString("en-US",{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"}) : "";
                return (
                  <div key={task.id} style={{ background:C.s2, borderRadius:8, padding:"8px 12px", marginBottom:6, opacity:0.8 }}>
                    <div style={{ fontSize:11, fontWeight:600, color:C.textS, textDecoration:"line-through" }}>{task.description}</div>
                    <div style={{ fontSize:9, color:C.textM }}>{assignee?.name || task.assignedTo} · Completed {completedDate}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* CALENDAR GRID VIEW — inside myschedule tab */}
      {scheduleTab === "myschedule" && calView === "calendar" && (() => {
        const workers = staff.filter(s => !["manager","viewer"].includes(s.role) && s.username !== "john");
        const workerColors = {};
        workers.forEach((w,i) => { workerColors[w.id] = w.color || ["#3b82f6","#22c55e","#a855f7","#f97316","#06b6d4","#ec4899"][i%6]; });
        const filterStaff = selectedStaff === "all" ? null : selectedStaff;
        // Build 7-day week grid
        const weekDays = [];
        let d = new Date(calWeekStart + "T12:00:00");
        for (let i = 0; i < 7; i++) {
          if (d.getDay() !== 0 && d.getDay() !== 6) weekDays.push(d.toISOString().slice(0,10));
          d.setDate(d.getDate()+1);
        }
        return (
          <div>
            {/* Week nav */}
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:10 }}>
              <button onClick={() => { const d=new Date(calWeekStart+"T12:00:00"); d.setDate(d.getDate()-7); setCalWeekStart(d.toISOString().slice(0,10)); }}
                style={{ background:C.s2, border:`1px solid ${C.border}`, borderRadius:8, padding:"5px 12px", cursor:"pointer", fontSize:11, fontWeight:700, fontFamily:"inherit" }}>← Prev</button>
              <div style={{ fontSize:11, fontWeight:700, color:"#000" }}>
                {new Date(calWeekStart+"T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric"})} — {new Date(weekDays[weekDays.length-1]+"T12:00:00").toLocaleDateString("en-US",{month:"short",day:"numeric",year:"numeric"})}
              </div>
              <button onClick={() => { const d=new Date(calWeekStart+"T12:00:00"); d.setDate(d.getDate()+7); setCalWeekStart(d.toISOString().slice(0,10)); }}
                style={{ background:C.s2, border:`1px solid ${C.border}`, borderRadius:8, padding:"5px 12px", cursor:"pointer", fontSize:11, fontWeight:700, fontFamily:"inherit" }}>Next →</button>
            </div>
            {/* Staff color legend */}
            <div style={{ display:"flex", gap:6, flexWrap:"wrap", marginBottom:10 }}>
              {workers.filter(w => !filterStaff || w.id===filterStaff).map(w => (
                <div key={w.id} style={{ display:"flex", alignItems:"center", gap:4, fontSize:10, fontWeight:700, color:workerColors[w.id] }}>
                  <div style={{ width:10,height:10,borderRadius:"50%",background:workerColors[w.id] }} />{w.name.split(" ")[0]}
                </div>
              ))}
            </div>
            {/* Day columns */}
            <div style={{ display:"flex", gap:4, overflowX:"auto" }}>
              {weekDays.map(day => {
                const isToday = day === todayStr();
                const daySchedule = schedule?.[day] || {};
                const dayJobs = workers
                  .filter(w => !filterStaff || w.id===filterStaff)
                  .flatMap(w => (daySchedule[w.id]||[]).map(j => ({...j,workerId:w.id,workerColor:workerColors[w.id],workerName:w.name.split(" ")[0]})));
                return (
                  <div key={day} style={{ flex:"0 0 140px", minWidth:0 }}>
                    <div style={{ background:isToday?C.amber+"20":C.s2, border:`1px solid ${isToday?C.amber:C.border}`, borderRadius:8, padding:"5px 8px", textAlign:"center", marginBottom:5, fontSize:10, fontWeight:800, color:isToday?C.amber:"#000" }}>
                      {new Date(day+"T12:00:00").toLocaleDateString("en-US",{weekday:"short",month:"short",day:"numeric"})}
                    </div>
                    <div style={{ display:"flex", flexDirection:"column", gap:4, minHeight:60 }}>
                      {dayJobs.length===0
                        ? <div style={{ fontSize:9, color:C.textM, textAlign:"center", padding:8, fontStyle:"italic" }}>Free</div>
                        : dayJobs.map((job,i) => {
                          const vehicle = vehicles.find(v => v.id===job.vehicleId);
                          return (
                            <div key={i} onClick={() => vehicle && openVehicle && openVehicle(vehicle)}
                              style={{ background:job.workerColor+"18", border:`2px solid ${job.workerColor}`, borderRadius:7, padding:"5px 8px", cursor:"pointer" }}>
                              <div style={{ fontSize:9, fontWeight:800, color:job.workerColor }}>{job.workerName}</div>
                              <div style={{ fontSize:9, color:"#000", fontWeight:600, lineHeight:1.3 }}>{job.description}</div>
                              <div style={{ fontSize:8, color:C.textM }}>{job.estimatedHours||1}h</div>
                            </div>
                          );
                        })
                      }
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      })()}

      {/* TIME LOG tab */}
      {scheduleTab === "timelog" && (
      <div>
      {/* Staff selector */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 20 }}>
        {staff.map(s => {
          const r = STAFF_ROLES.find(x => x.id === s.role);
          const act = activeEntry(s.id);
          return (
            <button key={s.id} onClick={() => setSelectedStaff(s.id)} style={{ padding: "8px 14px", borderRadius: 8, border: `2px solid ${selectedStaff === s.id ? (r?.color || C.amber) : C.border}`, background: selectedStaff === s.id ? (r?.color || C.amber) + "20" : C.surface, color: C.text, fontWeight: 700, fontSize: 11, fontFamily: "inherit", cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
              {s.avatar ? <img src={s.avatar} alt="" style={{ width: 22, height: 22, borderRadius: "50%", objectFit: "cover" }} /> : <div style={{ width: 22, height: 22, borderRadius: "50%", background: r?.color || C.amber, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10 }}>{s.name[0]}</div>}
              {s.name.split(" ")[0]}
              {act && <div style={{ width: 7, height: 7, borderRadius: "50%", background: C.green, animation: "blink 1.5s ease-in-out infinite" }} />}
            </button>
          );
        })}
      </div>

      {sm && (
        <div>
          {/* Staff card */}
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12, padding: "16px 18px", marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
              <div>
                <div style={{ fontSize: 18, fontWeight: 800, color: C.text, fontFamily: "'Bebas Neue', sans-serif" }}>{sm.name}</div>
                <div style={{ display: "flex", gap: 6, marginTop: 3 }}>
                  {role && <Badge color={role.color} small>{role.icon} {role.label}</Badge>}

                  <Badge color={C.textM} small>{sm.dailyHours}h/day</Badge>
                </div>
              </div>
              <div style={{ textAlign: "right" }}>
                <div style={{ fontSize: 28, fontWeight: 800, color: hrs >= sm.dailyHours ? C.red : hrs > sm.dailyHours * 0.7 ? "#f97316" : C.green, fontFamily: "'Bebas Neue', sans-serif", lineHeight: 1 }}>{hrs.toFixed(1)}h</div>
                <div style={{ fontSize: 10, color: C.textM }}>{remaining >= 0 ? `${remaining.toFixed(1)}h remaining` : `${Math.abs(remaining).toFixed(1)}h overtime`}</div>
              </div>
            </div>
            {/* Hours bar */}
            <div style={{ height: 6, borderRadius: 3, background: C.border, marginBottom: 12 }}>
              <div style={{ height: "100%", borderRadius: 3, width: `${Math.min((hrs / sm.dailyHours) * 100, 100)}%`, background: hrs >= sm.dailyHours ? C.red : C.green, transition: "width 0.3s" }} />
            </div>
            {/* Check in/out */}
            {active ? (
              <div style={{ background: C.green + "12", border: `1px solid ${C.green}40`, borderRadius: 8, padding: "10px 14px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ display: "flex", alignItems: "center", gap: 6 }}><div style={{ width: 8, height: 8, borderRadius: "50%", background: C.green, animation: "blink 1.5s ease-in-out infinite" }} /><span style={{ fontSize: 12, fontWeight: 700, color: C.green }}>Currently Working</span></div>
                  <div style={{ fontSize: 11, color: C.textS, marginTop: 2 }}>{active.description} · started {active.startTime}</div>
                  {active.vehicleId && <div style={{ fontSize: 10, color: C.textM }}>{vehicles.find(v => v.id === active.vehicleId)?.stockNumber ? `#${vehicles.find(v => v.id === active.vehicleId).stockNumber}` : ""}</div>}
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <Btn variant="ghost" size="sm" onClick={() => checkOut(active.id)}><PauseCircle size={12} /> Pause</Btn>
                  <Btn variant="red" size="sm" onClick={() => checkOut(active.id)}><StopCircle size={12} /> Check Out</Btn>
                </div>
              </div>
            ) : (
              <Btn variant="green" onClick={() => setCheckinModal(selectedStaff)} full><PlayCircle size={14} /> Check In — Start a Job</Btn>
            )}
          </div>

          {/* Today's entries */}
          {todayEntries(selectedStaff).length > 0 && (
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
              <div style={{ padding: "10px 14px", background: C.s2, borderBottom: `1px solid ${C.border}`, fontSize: 10, color: C.textM, fontWeight: 700, textTransform: "uppercase" }}>Today's Work Log</div>
              {todayEntries(selectedStaff).map(e => {
                const v = vehicles.find(x => x.id === e.vehicleId);
                const startD = new Date(`${e.date}T${e.startTime}`);
                const endD = e.endTime ? new Date(`${e.date}T${e.endTime}`) : new Date();
                const hrs = (endD - startD) / 3600000;
                return (
                  <div key={e.id} style={{ display: "grid", gridTemplateColumns: "1fr 1fr 0.5fr 0.5fr", padding: "9px 14px", borderBottom: `1px solid ${C.border}` }}>
                    <div style={{ fontSize: 11, color: C.text }}>{e.description}</div>
                    <div style={{ fontSize: 11, color: C.textS }}>{v ? `${v.year} ${v.make} ${v.model}` : "—"}</div>
                    <div style={{ fontSize: 11, color: C.textS }}>{e.startTime} – {e.endTime || "now"}</div>
                    <div style={{ fontSize: 11, color: C.amber, fontWeight: 700 }}>{hrs.toFixed(1)}h</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      </div>)}

      {/* Check-in modal */}
      {checkinModal && (
        <CheckinModal
          staffId={checkinModal}
          vehicles={activeVehicles}
          staff={staff}
          onCheckin={checkIn}
          onClose={() => setCheckinModal(null)}
        />
      )}
    </div>
  );
};

const CheckinModal = ({ staffId, vehicles, staff, onCheckin, onClose }) => {
  const [desc, setDesc] = useState("");
  const [vehicleId, setVehicleId] = useState("");
  const [woId, setWoId] = useState("");
  const sm = staff.find(s => s.id === staffId);
  const vehicle = vehicles.find(v => v.id === vehicleId);
  const wos = vehicle?.workOrders?.filter(w => w.status !== "COMPLETE") || [];

  return (
    <Modal title={`Check In — ${sm?.name}`} onClose={onClose}>
      <TI label="What are you working on? *" value={desc} onChange={setDesc} placeholder="e.g. Oil change, brake inspection, detailing interior…" />
      <SI label="Vehicle (optional)" value={vehicleId} onChange={setVehicleId} options={[{ value: "", label: "— No specific vehicle" }, ...vehicles.map(v => ({ value: v.id, label: `${v.year} ${v.make} ${v.model} #${v.stockNumber}` }))]} />
      {vehicleId && wos.length > 0 && <SI label="Work Order (optional)" value={woId} onChange={setWoId} options={[{ value: "", label: "— No specific WO" }, ...wos.map(w => ({ value: w.id, label: w.description.slice(0, 50) }))]} />}
      <div style={{ display: "flex", gap: 8 }}>
        <Btn variant="green" onClick={() => { if (desc.trim()) onCheckin(staffId, vehicleId || null, woId || null, desc.trim()); }} disabled={!desc.trim()}><PlayCircle size={13} /> Check In Now</Btn>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
      </div>
    </Modal>
  );
};

// ─── USER SETTINGS ────────────────────────────────────────────────────────────

const ManualInventoryEntry = ({ onSave, existingCount }) => {
  const [text, setText] = useState("");
  const [saved, setSaved] = useState(false);

  const save = () => {
    const lines = text.split("\n").map(l => l.trim()).filter(Boolean);
    const vehicles = lines.map(line => {
      // Support formats: "12345", "12345 2019 Toyota Camry", "12345,2019,Toyota,Camry"
      const parts = line.split(/[\s,]+/);
      const stockNumber = parts[0];
      const year = parts[1] ? parseInt(parts[1]) : null;
      const make = parts[2] || null;
      const model = parts.slice(3).join(" ") || null;
      return { stockNumber, year, make, model, vin: null, mileage: null, price: null, scrapedAt: new Date().toISOString() };
    });
    onSave(vehicles);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  return (
    <div>
      <textarea value={text} onChange={e => setText(e.target.value)}
        placeholder={"12345\n12346 2019 Toyota Camry\n12347 2021 Ford F-150\n..."}
        style={{ width: "100%", height: 100, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 7, padding: "8px 10px", color: C.text, fontSize: 11, fontFamily: "monospace", outline: "none", resize: "vertical", boxSizing: "border-box" }} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 6 }}>
        <span style={{ fontSize: 9, color: C.textM }}>{existingCount > 0 ? `${existingCount} already cached` : ""}</span>
        <Btn variant="amber" size="sm" onClick={save} disabled={!text.trim()}>
          {saved ? "✓ Saved" : "Save Stock #s"}
        </Btn>
      </div>
    </div>
  );
};

const SettingsView = ({ currentUser, staff, setStaff, setCurrentUser, vendors, setVendors, inventoryCache, onSyncInventory, onManualInventorySet, onLogout, showHoldingCost, setShowHoldingCost, setView, vehicles, setVehicles, parts, setParts, ledger, addLedger, setCsvToast, guestDiagnoses, setGuestDiagnoses, tasks, setTasks }) => {
  const [user, setUser] = useState({ ...currentUser });
  const [avatarPreview, setAvatarPreview] = useState(currentUser?.avatar || null);
  const [saved, setSaved] = useState(false);
  const [mgmtTab, setMgmtTab] = useState("staff"); // staff | add
  const [editingStaff, setEditingStaff] = useState(null);
  const [newEmployee, setNewEmployee] = useState({ name: "", role: "mechanic", email: "", phone: "", username: "", password: "", hourlyRate: "", dailyHours: "8", color: "#3b82f6", notifyHours: { start: 8, end: 17 }, notifyTypes: ["wo_assigned","parts_arrived"] });
  const [resetStock, setResetStock] = useState("");
  const [resetStep, setResetStep] = useState(0);
  const [resetTarget, setResetTarget] = useState(null);
  const [reportSending, setReportSending] = useState(false);
  const [reportSent, setReportSent] = useState(false);
  const fileRef = useRef(null);
  const isManager = currentUser?.role === "manager";

  const STAFF_COLORS = ["#3b82f6","#22c55e","#a855f7","#f97316","#06b6d4","#ec4899","#84cc16","#f59e0b"];

  const save = () => {
    const updated = { ...user, avatar: avatarPreview };
    if (user.newPassword) { updated.password = user.newPassword.toLowerCase(); delete updated.newPassword; }
    setStaff(prev => prev.map(s => s.id === user.id ? updated : s));
    setCurrentUser(updated);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleAvatar = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => setAvatarPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const addEmployee = () => {
    if (!newEmployee.name.trim() || !newEmployee.username.trim() || !newEmployee.password.trim()) return;
    const emp = {
      id: genId().slice(0, 8),
      name: newEmployee.name.trim(),
      role: newEmployee.role,
      email: newEmployee.email.trim(),
      phone: newEmployee.phone.trim(),
      username: newEmployee.username.trim().toLowerCase(),
      password: newEmployee.password.trim().toLowerCase(),
      hourlyRate: parseFloat(newEmployee.hourlyRate) || 0,
      dailyHours: parseFloat(newEmployee.dailyHours) || 8,
      color: newEmployee.color,
      avatar: null,
      notifyHours: newEmployee.notifyHours,
      notifyTypes: newEmployee.notifyTypes,
    };
    setStaff(prev => [...prev, emp]);
    setNewEmployee({ name: "", role: "mechanic", email: "", phone: "", username: "", password: "", hourlyRate: "", dailyHours: "8", color: "#3b82f6", notifyHours: { start: 8, end: 17 }, notifyTypes: ["wo_assigned","parts_arrived"] });
    setMgmtTab("staff");
  };

  const removeEmployee = (id) => {
    if (!window.confirm("Remove this employee? This cannot be undone.")) return;
    setStaff(prev => prev.filter(s => s.id !== id));
  };

  const notifyTypes = [
    { id: "return_parts", label: "Return Parts alerts" },
    { id: "approval", label: "Approval requests" },
    { id: "sold", label: "Vehicle sold" },
    { id: "new_intake", label: "New vehicle intake" },
    { id: "line_ready", label: "Vehicle line-ready" },
    { id: "wo_assigned", label: "Work order assigned" },
    { id: "parts_arrived", label: "Parts arrived" },
    { id: "digest", label: "Daily digest" },
  ];

  return (
    <div style={{ maxWidth: 560 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <div style={{ fontSize: 26, fontWeight: 800, color: C.amber, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.06em" }}>MY SETTINGS</div>
        <button onClick={onLogout}
          style={{ background: C.red + "15", border: `1px solid ${C.red}40`, borderRadius: 8, padding: "8px 16px", cursor: "pointer", fontSize: 11, fontWeight: 700, color: C.red, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6 }}>
          ⎋ Sign Out
        </button>
      </div>

      {/* Vehicle Reset Tool — managers only */}
      {currentUser?.role === "manager" && (
        <div style={{ background: C.surface, border: `2px solid ${C.red}40`, borderRadius: 10, padding: "14px 16px", marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: C.red, textTransform: "uppercase", marginBottom: 4 }}>⚠️ Vehicle Reset Tool</div>
          <div style={{ fontSize: 10, color: C.textM, marginBottom: 12 }}>
            Reset a vehicle back to its pre-intake state — cancels all WOs, releases parts to general stock, removes all recon history. Cannot be undone.
          </div>
          {resetStep === 0 && (
            <div style={{ display:"flex",gap:8 }}>
              <input value={resetStock} onChange={e => setResetStock(e.target.value)}
                placeholder="Enter stock number (e.g. 12345)"
                onKeyDown={e => { if (e.key === "Enter" && resetStock.trim()) { const v = (vehicles||[]).find(x => String(x.stockNumber).trim().toLowerCase() === resetStock.trim().toLowerCase()); if (!v) { alert(`No vehicle found with stock number "${resetStock}"`); } else { setResetTarget(v); setResetStep(1); } } }}
                style={{ flex:1,background:C.s2,border:`1px solid ${C.border}`,borderRadius:8,padding:"9px 12px",color:C.text,fontSize:12,fontFamily:"inherit",outline:"none" }} />
              <button onClick={() => { const v = (vehicles||[]).find(x => String(x.stockNumber).trim().toLowerCase() === resetStock.trim().toLowerCase()); if (!v) { alert(`No vehicle found with stock number "${resetStock}"`); } else { setResetTarget(v); setResetStep(1); } }} disabled={!resetStock.trim()}
                style={{ background:C.red+"20",border:`1px solid ${C.red}50`,borderRadius:8,padding:"9px 16px",cursor:resetStock.trim()?"pointer":"not-allowed",fontSize:12,fontWeight:700,color:C.red,fontFamily:"inherit",opacity:resetStock.trim()?1:0.5 }}>
                Find Vehicle
              </button>
            </div>
          )}
          {resetStep === 1 && resetTarget && (
            <div style={{ background:C.red+"10",border:`1px solid ${C.red}40`,borderRadius:8,padding:"12px 14px" }}>
              <div style={{ fontSize:13,fontWeight:800,color:C.red,marginBottom:6 }}>
                Are you sure you want to reset {resetTarget.year} {resetTarget.make} {resetTarget.model} #{resetTarget.stockNumber}?
              </div>
              <div style={{ fontSize:11,color:C.text,marginBottom:12,lineHeight:1.5 }}>
                This will remove it from the reconditioning system and return it to inventory-only status.
              </div>
              <div style={{ display:"flex",gap:8 }}>
                <button onClick={() => setResetStep(2)} style={{ background:C.red,border:"none",borderRadius:8,padding:"8px 16px",cursor:"pointer",fontSize:12,fontWeight:800,color:"#fff",fontFamily:"inherit" }}>Yes, continue</button>
                <button onClick={() => { setResetStep(0); setResetTarget(null); setResetStock(""); }} style={{ background:"none",border:`1px solid ${C.border}`,borderRadius:8,padding:"8px 14px",cursor:"pointer",fontSize:12,color:C.textM,fontFamily:"inherit" }}>Cancel</button>
              </div>
            </div>
          )}
          {resetStep === 2 && resetTarget && (() => {
            const openWOs = (resetTarget.workOrders||[]).filter(w => w.status !== "COMPLETE" && !w.deleted);
            const vParts = (parts||[]).filter(p => p.vehicleId === resetTarget.id);
            const doReset = () => {
              setParts && setParts(prev => prev.map(p => p.vehicleId !== resetTarget.id ? p : { ...p, vehicleId: null, workOrderId: null, status: "Received", generalStock: true, resetFrom: resetTarget.stockNumber, resetAt: todayStr() }));
              setVehicles && setVehicles(prev => prev.filter(v => v.id !== resetTarget.id));
              const msg = `✓ ${resetTarget.year} ${resetTarget.make} ${resetTarget.model} #${resetTarget.stockNumber} reset. Vehicle removed from recon. Parts moved to general stock.`;
              setResetStep(0); setResetStock(""); setResetTarget(null);
              alert(msg);
            };
            return (
              <div style={{ background:C.red+"15",border:`2px solid ${C.red}60`,borderRadius:8,padding:"14px 16px" }}>
                <div style={{ fontSize:13,fontWeight:900,color:C.red,marginBottom:10 }}>⚠️ FINAL WARNING — Are you absolutely sure?</div>
                <div style={{ fontSize:11,color:C.text,marginBottom:12,lineHeight:1.8 }}>
                  • Cancel <strong>{openWOs.length} open WO{openWOs.length!==1?"s":""}</strong>{openWOs.length>0?`: ${openWOs.slice(0,3).map(w=>w.description).join(", ")}${openWOs.length>3?` +${openWOs.length-3} more`:""}`:""}<br/>
                  • Move <strong>{vParts.length} part{vParts.length!==1?"s":""}</strong> to general stock{vParts.length>0?`: ${vParts.slice(0,3).map(p=>p.description||p.name||"Part").join(", ")}${vParts.length>3?` +${vParts.length-3} more`:""}`:""}<br/>
                  • Remove <strong>{resetTarget.year} {resetTarget.make} {resetTarget.model} #{resetTarget.stockNumber}</strong> from recon entirely
                </div>
                <div style={{ fontSize:11,color:C.amber,fontWeight:700,marginBottom:12 }}>Vehicle stays in inventory list and can be re-added to recon at any time.</div>
                <div style={{ display:"flex",gap:8 }}>
                  <button onClick={doReset} style={{ background:C.red,border:"none",borderRadius:8,padding:"10px 20px",cursor:"pointer",fontSize:13,fontWeight:900,color:"#fff",fontFamily:"inherit" }}>Yes, Reset This Vehicle</button>
                  <button onClick={() => { setResetStep(0); setResetTarget(null); setResetStock(""); }} style={{ background:"none",border:`1px solid ${C.border}`,borderRadius:8,padding:"10px 14px",cursor:"pointer",fontSize:12,color:C.textM,fontFamily:"inherit" }}>Cancel</button>
                </div>
              </div>
            );
          })()}
        </div>
      )}


      {/* Manual WO Report Send */}
      {isManager && (
          <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:"14px 16px", marginBottom:10 }}>
            <div style={{ fontSize:13, fontWeight:800, color:"#000", marginBottom:4 }}>📧 WO Status Report</div>
            <div style={{ fontSize:10, color:C.textM, marginBottom:10 }}>Auto-emails Mon / Wed / Fri at 8am to angela@ and dmitriy@edgewaterautocenter.com. Send manually anytime.</div>
            <button onClick={async () => {
              setReportSending(true);
              try {
                const body = buildWOReport(vehicles, ledger, parts);
                const subject = `Edgewater WO Status Report — ${new Date().toLocaleDateString("en-US", { weekday:"long", month:"short", day:"numeric" })}`;
                await sendGmailViaMCP("angela@edgewaterautocenter.com", subject, body);
                await sendGmailViaMCP("dmitriy@edgewaterautocenter.com", subject, body);
                setReportSent(true); setTimeout(() => setReportSent(false), 5000);
              } catch(e) { alert("Send failed: " + e.message); }
              setReportSending(false);
            }} disabled={reportSending}
              style={{ background:reportSending?C.s2:C.blue, border:"none", borderRadius:8, padding:"8px 18px", cursor:reportSending?"not-allowed":"pointer", fontSize:12, fontWeight:700, color:reportSending?"#666":"#fff", fontFamily:"inherit" }}>
              {reportSending ? "Sending…" : reportSent ? "✓ Sent!" : "Send Now"}
            </button>
          </div>
        )}

      {/* Suggestions inbox — Angela only */}
      {currentUser?.username === "angela" && (
        <div style={{ background: C.blue + "10", border: `1px solid ${C.blue}30`, borderRadius: 10, padding: "14px 16px", marginBottom: 16, cursor: "pointer" }}
          onClick={() => { if (typeof setView === "function") setView("suggestions"); }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: C.blue }}>💬 Team Suggestions & Feedback</div>
          <div style={{ fontSize: 10, color: C.textM, marginTop: 2 }}>View feedback submitted by staff during beta testing</div>
        </div>
      )}

      {/* Display options */}
      {currentUser?.role === "manager" && (
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 16px", marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: "#000", textTransform: "uppercase", marginBottom: 10 }}>Display Options</div>
          <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
            <input type="checkbox" checked={showHoldingCost} onChange={e => setShowHoldingCost(e.target.checked)} style={{ accentColor: C.amber, width: 16, height: 16 }} />
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>Show Holding Cost Accrual</div>
              <div style={{ fontSize: 10, color: C.textM }}>Display $24/day holding cost on vehicle pages and pipeline view</div>
            </div>
          </label>
        </div>
      )}

      {/* Avatar + profile card */}
      <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 20, padding: "16px 18px", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12 }}>
        <div onClick={() => fileRef.current?.click()} style={{ width: 64, height: 64, borderRadius: "50%", background: C.s2, border: `2px dashed ${C.amber}60`, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", overflow: "hidden", flexShrink: 0 }}>
          {avatarPreview ? <img src={avatarPreview} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} /> : <User size={24} color={C.textM} />}
        </div>
        <input ref={fileRef} type="file" accept="image/*" onChange={handleAvatar} style={{ display: "none" }} />
        <div>
          <div style={{ fontSize: 16, fontWeight: 800, color: C.text }}>{user.name}</div>
          <div style={{ fontSize: 11, color: C.textS }}>{STAFF_ROLES.find(r => r.id === user.role)?.label}</div>
          <button onClick={() => fileRef.current?.click()} style={{ fontSize: 10, color: C.amber, background: "none", border: "none", cursor: "pointer", padding: 0, marginTop: 2, fontFamily: "inherit" }}>Change photo</button>
        </div>
      </div>

      {/* Profile fields */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
        <TI label="Name" value={user.name} onChange={v => setUser(u => ({ ...u, name: v }))} placeholder="Full name" />
        <TI label="Email" value={user.email || ""} onChange={v => setUser(u => ({ ...u, email: v }))} type="email" placeholder="you@example.com" />
        <TI label="Phone (for text alerts)" value={user.phone || ""} onChange={v => setUser(u => ({ ...u, phone: v }))} placeholder="503-555-0100" />
        <TI label="Username" value={user.username || ""} onChange={v => setUser(u => ({ ...u, username: v.toLowerCase() }))} placeholder="login username" />
      </div>
      <div style={{ marginBottom: 16 }}>
        <TI label="New Password (leave blank to keep current)" value={user.newPassword || ""} onChange={v => setUser(u => ({ ...u, newPassword: v }))} type="password" placeholder="Enter new password" />
        {user.newPassword && <div style={{ fontSize: 10, color: C.amber, marginTop: 4 }}>⚠ Password will be updated on Save</div>}
      </div>

      {/* Notification hours */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 16px", marginBottom: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 10 }}>Notification Hours</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 8 }}>
          <TI label="Start (0-23)" value={String(user.notifyHours?.start ?? 7)} onChange={v => setUser(u => ({ ...u, notifyHours: { ...u.notifyHours, start: parseInt(v) } }))} type="number" small />
          <TI label="End (0-23)" value={String(user.notifyHours?.end ?? 20)} onChange={v => setUser(u => ({ ...u, notifyHours: { ...u.notifyHours, end: parseInt(v) } }))} type="number" small />
        </div>
        <div style={{ fontSize: 10, color: C.textM }}>Texts and emails only sent during these hours.</div>
      </div>

      {/* Notification types */}
      <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 16px", marginBottom: 20 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 10 }}>Notification Types</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {notifyTypes.map(nt => {
            const on = user.notifyTypes?.includes(nt.id);
            return (
              <div key={nt.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 12, color: C.text }}>{nt.label}</span>
                <button onClick={() => setUser(u => ({ ...u, notifyTypes: on ? u.notifyTypes.filter(x => x !== nt.id) : [...(u.notifyTypes || []), nt.id] }))}
                  style={{ width: 40, height: 22, borderRadius: 11, background: on ? C.green : C.border, border: "none", cursor: "pointer", position: "relative", transition: "background 0.2s" }}>
                  <div style={{ width: 16, height: 16, borderRadius: "50%", background: "#fff", position: "absolute", top: 3, left: on ? 21 : 3, transition: "left 0.2s" }} />
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
        <Btn variant="amber" onClick={save} full>
          {saved ? <><CheckCircle size={14} /> Saved!</> : <><CheckCircle size={14} /> Save Settings</>}
        </Btn>
      </div>

      {/* Inventory sync panel */}
      {isManager && (
        <div style={{ background: C.s2, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px", marginBottom: 16 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: "#000" }}>🚗 Dealer Inventory</div>
              <div style={{ fontSize: 10, color: C.textM }}>edgewaterautocenter.com</div>
            </div>
            <Btn variant="amber" size="sm" onClick={async () => {
              if (onSyncInventory) { await onSyncInventory(); }
            }}>↺ Web Sync</Btn>
          </div>
          {inventoryCache?.lastSync && (
            <div style={{ fontSize: 10, color: C.green, marginBottom: 8 }}>✓ {inventoryCache.vehicles?.length || 0} vehicles cached · {new Date(inventoryCache.lastSync).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</div>
          )}
          {!inventoryCache?.lastSync && <div style={{ fontSize: 10, color: C.textM, marginBottom: 8 }}>No inventory cached yet.</div>}

          {/* CSV Upload */}
          <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 10, marginTop: 4 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: "#000", marginBottom: 4 }}>📂 Upload Inventory CSV</div>
            <div style={{ fontSize: 9, color: C.textM, marginBottom: 8 }}>Export from your DMS and upload here. Purchase Price = Total Cost − $1,500 pack · Selling Price = Retail/Internet column</div>
            <label style={{ display: "flex", alignItems: "center", gap: 8, background: C.surface, border: `2px dashed ${C.border}`, borderRadius: 8, padding: "10px 14px", cursor: "pointer" }}>
              <span style={{ fontSize: 20 }}>📁</span>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#000" }}>Choose CSV file</div>
                <div style={{ fontSize: 9, color: C.textM }}>inventory_*.csv from your dealer system</div>
              </div>
              <input type="file" accept=".csv" style={{ display: "none" }} onChange={async (e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                const text = await file.text();
                const lines = text.split('\n').filter(Boolean);
                const headers = lines[0].split(',').map(h => h.replace(/["\r]/g, '').trim());
                const idx = (name) => headers.findIndex(h => h.toLowerCase().includes(name.toLowerCase()));
                const stockIdx   = idx('stock');
                const vinIdx     = idx('vin');
                const yearIdx    = idx('year');
                const makeIdx    = idx('make');
                const modelIdx   = idx('model');
                const trimIdx    = idx('trim');
                const colorIdx   = Math.max(idx('ex. color'), idx('color'));
                const mileageIdx = idx('mileage');
                const priceIdx   = Math.max(idx('internet'), idx('retail'), idx('price'));
                const vehicles = [];
                for (let i = 1; i < lines.length; i++) {
                  // Parse CSV properly handling quoted fields
                  const cols = [];
                  let cur = '', inQ = false;
                  for (const ch of lines[i]) {
                    if (ch === '"') { inQ = !inQ; }
                    else if (ch === ',' && !inQ) { cols.push(cur.trim()); cur = ''; }
                    else { cur += ch; }
                  }
                  cols.push(cur.trim());
                  const stock = cols[stockIdx]?.replace(/"/g,'').trim();
                  if (!stock) continue;
                  const mileRaw = cols[mileageIdx]?.replace(/[",]/g,'').trim();
                  const transIdx = Math.max(idx('trans'), idx('transmission'));
                  // Total Cost column — try multiple common DCS/dealer export column names
                  const costIdx = Math.max(
                    idx('total cost'), idx('total_cost'), idx('totalcost'),
                    idx('purchase price'), idx('purchase_price'),
                    idx('floor cost'), idx('acv'), idx('actual cost')
                  );
                  const sellingIdx = Math.max(idx('internet'), idx('retail'), idx('selling'), idx('list price'), idx('asking'));
                  const sellingRaw = sellingIdx >= 0 ? cols[sellingIdx]?.replace(/[",]/g,'').trim() : null;
                  const costRaw2 = costIdx >= 0 ? cols[costIdx]?.replace(/[",]/g,'').trim() : null;
                  const totalCostRaw = costRaw2 ? parseFloat(costRaw2.replace(/[^0-9.]/g,'')) || null : null;
                  // If total cost includes pack, subtract $1500. If it doesn't, use as-is.
                  const purchasePriceFromCSV = totalCostRaw ? Math.round(totalCostRaw) : null;
                  const sellingPriceFromCSV = sellingRaw ? parseFloat(sellingRaw.replace(/[^0-9.]/g,'')) || null : null;
                  vehicles.push({
                    stockNumber: stock,
                    priceFromCSV: true,
                    vin: cols[vinIdx]?.replace(/"/g,'').trim() || null,
                    year: parseInt(cols[yearIdx]) || null,
                    make: cols[makeIdx]?.replace(/"/g,'').trim() || null,
                    model: cols[modelIdx]?.replace(/"/g,'').trim() || null,
                    trim: trimIdx >= 0 ? cols[trimIdx]?.replace(/"/g,'').trim() || null : null,
                    exteriorColor: colorIdx >= 0 ? cols[colorIdx]?.replace(/"/g,'').trim() || null : null,
                    mileage: mileRaw ? parseInt(mileRaw) || null : null,
                    totalCost: totalCostRaw,
                    price: purchasePriceFromCSV,          // purchasePrice = totalCost - pack
                    sellingPrice: sellingPriceFromCSV,    // retail/internet price
                    transmission: transIdx >= 0 ? cols[transIdx]?.replace(/"/g,'').trim() || null : null,
                    scrapedAt: new Date().toISOString(),
                  });
                }
                if (vehicles.length > 0 && onManualInventorySet) {
                  await onManualInventorySet(vehicles);
                  setCsvToast(`✓ Loaded ${vehicles.length} vehicles from CSV`); setTimeout(() => setCsvToast(""), 15000);
                } else {
                  alert('No vehicles found — check CSV format');
                }
                e.target.value = '';
              }} />
            </label>
          </div>
        </div>
      )}

      {/* Manager — completed task history */}
      {isManager && tasks?.filter(t=>t.status==="COMPLETE").length > 0 && (
        <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:"14px 16px", marginBottom:10 }}>
          <div style={{ fontSize:13, fontWeight:800, color:"#000", marginBottom:4 }}>📋 Completed Task History ({tasks.filter(t=>t.status==="COMPLETE").length})</div>
          <div style={{ fontSize:10, color:C.textM, marginBottom:10 }}>All completed non-vehicle tasks</div>
          {tasks.filter(t=>t.status==="COMPLETE").slice().reverse().slice(0,50).map((task,i) => {
            const assignedDate = task.createdAt ? new Date(task.createdAt) : null;
            const completedDate = task.completedAt ? new Date(task.completedAt) : null;
            const daysOpen = assignedDate && completedDate ? Math.round((completedDate-assignedDate)/86400000) : null;
            const dueDateMs = task.dueDate ? new Date(task.dueDate).getTime() : null;
            const onTime = dueDateMs ? completedDate?.getTime() <= dueDateMs : null;
            const assignee = staff.find(s => s.username === task.assignedTo);
            return (
              <div key={task.id} style={{ background:C.s2, borderRadius:8, padding:"9px 12px", marginBottom:6 }}>
                <div style={{ fontSize:12, fontWeight:700, color:"#000" }}>{task.description}</div>
                <div style={{ fontSize:10, color:C.textM, marginTop:3 }}>
                  Assigned to <strong>{assignee?.name||task.assignedTo}</strong>
                  {assignedDate && ` · Assigned ${assignedDate.toLocaleDateString("en-US",{month:"short",day:"numeric"})}`}
                  {completedDate && ` · Completed ${completedDate.toLocaleDateString("en-US",{month:"short",day:"numeric"})}`}
                  {daysOpen!==null && ` · ${daysOpen} day${daysOpen!==1?"s":""}`}
                  {task.dueDate && <span style={{marginLeft:6}}>Due {task.dueDate}</span>}
                  {onTime===true && <span style={{color:C.green,marginLeft:6,fontWeight:700}}>✓ On time</span>}
                  {onTime===false && <span style={{color:C.red,marginLeft:6,fontWeight:700}}>⚠ Late</span>}
                </div>
                <button onClick={() => setTasks && setTasks(prev => prev.map(t => t.id===task.id ? {...task,status:"OPEN",completedAt:null,completedBy:null} : t))}
                  style={{ background:"none", border:`1px solid ${C.amber}`, borderRadius:6, padding:"2px 10px", cursor:"pointer", fontSize:9, fontWeight:700, color:C.amber, fontFamily:"inherit", marginTop:5 }}>
                  ↩ Reopen
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Guest Diagnosis History — 14 day retention */}
      {isManager && (
        <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:"14px 16px", marginBottom:10 }}>
          <div style={{ fontSize:13, fontWeight:800, color:"#000", marginBottom:4 }}>🔍 Guest Diagnoses ({(guestDiagnoses||[]).length})</div>
          <div style={{ fontSize:10, color:C.textM, marginBottom:10 }}>Saved for 14 days. Select one to attach to inventory.</div>
          {(!guestDiagnoses||guestDiagnoses.length===0) && <div style={{ fontSize:11, color:C.textM }}>No guest diagnoses saved yet.</div>}
          {(guestDiagnoses||[]).map((g,i) => {
            const daysOld = Math.floor((Date.now()-new Date(g.savedAt).getTime())/86400000);
            const label = [g.year,g.make,g.model,g.trim].filter(Boolean).join(" ");
            return (
              <div key={g.id||i} style={{ background:C.s2, border:`1px solid ${C.border}`, borderRadius:8, padding:"10px 12px", marginBottom:6, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div>
                  <div style={{ fontSize:12, fontWeight:700, color:"#000" }}>{label||"Unknown vehicle"}</div>
                  <div style={{ fontSize:10, color:C.textM }}>
                    {g.transmission||""} {g.drivetrain||""} · {g.workOrders?.length||0} WOs · {daysOld}d ago
                  </div>
                </div>
                <button onClick={() => {
                  const match = vehicles.filter(v=>!["SOLD","SCRAPPED"].includes(v.stage)).filter(v =>
                    (g.year && String(v.year)===String(g.year) && g.make && v.make?.toLowerCase()===g.make?.toLowerCase() && g.model && v.model?.toLowerCase()===g.model?.toLowerCase())
                  );
                  if (match.length===1) {
                    if (window.confirm(`Match found: #${match[0].stockNumber} ${match[0].year} ${match[0].make} ${match[0].model}. Attach this diagnosis to that vehicle?`)) {
                      const updated = { ...match[0], hankTranscript: g.hankTranscript||match[0].hankTranscript, conditionSummary: g.conditionSummary||match[0].conditionSummary, workOrders:[...(match[0].workOrders||[]), ...(g.workOrders||[]).map(w=>({...w,id:genId()}))] };
                      setVehicles(prev=>prev.map(v=>v.id===match[0].id?updated:v));
                      setGuestDiagnoses(prev=>prev.filter((_,j)=>j!==i));
                      alert("Diagnosis attached to "+match[0].year+" "+match[0].make+" "+match[0].model+" #"+match[0].stockNumber);
                    }
                  } else {
                    const stock = window.prompt("Enter stock number to attach this diagnosis to:")||"";
                    const target = vehicles.find(v=>String(v.stockNumber)===stock.trim());
                    if (target) {
                      if (window.confirm("Attach to "+target.year+" "+target.make+" "+target.model+" #"+target.stockNumber+"?")) {
                        const updated = { ...target, hankTranscript: g.hankTranscript||target.hankTranscript, conditionSummary: g.conditionSummary||target.conditionSummary, workOrders:[...(target.workOrders||[]), ...(g.workOrders||[]).map(w=>({...w,id:genId()}))] };
                        setVehicles(prev=>prev.map(v=>v.id===target.id?updated:v));
                        setGuestDiagnoses(prev=>prev.filter((_,j)=>j!==i));
                      }
                    } else if (stock) {
                      alert("Stock #"+stock+" not found in inventory.");
                    }
                  }
                }} style={{ background:C.blue, border:"none", borderRadius:7, padding:"5px 10px", cursor:"pointer", fontSize:10, fontWeight:700, color:"#fff", fontFamily:"inherit", flexShrink:0 }}>
                  Attach to Inventory
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Personal task history — all users */}
      {tasks?.filter(t => t.assignedTo === currentUser?.username && t.status === "COMPLETE").length > 0 && (
        <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:10, padding:"14px 16px", marginBottom:10 }}>
          <div style={{ fontSize:13, fontWeight:800, color:"#000", marginBottom:8 }}>✅ My Completed Tasks</div>
          {tasks.filter(t => t.assignedTo === currentUser?.username && t.status === "COMPLETE").slice().reverse().map(task => {
            const assignedDate = task.createdAt ? new Date(task.createdAt) : null;
            const completedDate = task.completedAt ? new Date(task.completedAt) : null;
            const daysOpen = assignedDate && completedDate ? Math.round((completedDate-assignedDate)/86400000) : null;
            const dueDateMs = task.dueDate ? new Date(task.dueDate).getTime() : null;
            const onTime = dueDateMs && completedDate ? completedDate.getTime() <= dueDateMs : null;
            return (
              <div key={task.id} style={{ background:C.s2, borderRadius:8, padding:"9px 12px", marginBottom:6 }}>
                <div style={{ fontSize:12, fontWeight:700, color:C.textS }}>{task.description}</div>
                <div style={{ fontSize:9, color:C.textM, marginTop:3 }}>
                  {assignedDate && `Assigned ${assignedDate.toLocaleDateString("en-US",{month:"short",day:"numeric"})}`}
                  {completedDate && ` · Done ${completedDate.toLocaleDateString("en-US",{month:"short",day:"numeric"})}`}
                  {daysOpen!==null && ` · ${daysOpen} day${daysOpen!==1?"s":""}`}
                  {onTime===true && <span style={{color:C.green,marginLeft:6,fontWeight:700}}>✓ On time</span>}
                  {onTime===false && <span style={{color:C.red,marginLeft:6,fontWeight:700}}>⚠ Late</span>}
                </div>
                <button onClick={() => setTasks && setTasks(prev => prev.map(t => t.id===task.id ? {...task,status:"OPEN",completedAt:null,completedBy:null} : t))}
                  style={{ background:"none", border:`1px solid ${C.amber}`, borderRadius:6, padding:"2px 10px", cursor:"pointer", fontSize:9, fontWeight:700, color:C.amber, fontFamily:"inherit", marginTop:5 }}>
                  ↩ Reopen
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* API status */}
      <div style={{ background: C.s2, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px", marginBottom: 32 }}>
        <div style={{ fontSize: 10, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 8 }}>API Status</div>
        {[
          { label: "Claude (Anthropic)", key: "REACT_APP_ANTHROPIC_KEY", desc: "Hank, Jimmy, checklists, scheduling" },
          { label: "Gemini (Google)", key: "REACT_APP_GEMINI_KEY", desc: "Julia inspection" },
          { label: "Grok (xAI)", key: "REACT_APP_GROK_KEY", desc: "Roxy parts search — real-time web" },
        ].map(api => {
          const active = api.key === "REACT_APP_ANTHROPIC_KEY" ? !!process.env.REACT_APP_ANTHROPIC_KEY
            : api.key === "REACT_APP_GEMINI_KEY" ? !!process.env.REACT_APP_GEMINI_KEY
            : !!process.env.REACT_APP_GROK_KEY;
          return (
            <div key={api.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderBottom: `1px solid ${C.border}` }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 600, color: "#000" }}>{api.label}</div>
                <div style={{ fontSize: 9, color: C.textM }}>{api.desc}</div>
              </div>
              <Badge color={active ? C.green : C.red} small>{active ? "✓ Active" : "Missing"}</Badge>
            </div>
          );
        })}
        <div style={{ fontSize: 10, color: C.textM, marginTop: 8 }}>Add REACT_APP_GROK_KEY to your .env file. Get a key at <span style={{ color: C.blue }}>console.x.ai</span></div>
      </div>

      {/* ── MANAGER CONTROL PANEL ── */}
      {isManager && (
        <div>
          <div style={{ fontSize: 20, fontWeight: 800, color: C.red, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.06em", marginBottom: 4 }}>MANAGER CONTROL PANEL</div>
          <div style={{ fontSize: 11, color: C.textM, marginBottom: 14 }}>Manage staff, credentials, and rates.</div>

          {/* Tabs */}
          <div style={{ display: "flex", background: C.s2, borderRadius: 8, padding: 3, marginBottom: 16, border: `1px solid ${C.border}`, flexWrap: "wrap" }}>
            <button onClick={() => { setMgmtTab("staff"); setEditingStaff(null); }} style={{ flex: 1, padding: "7px", border: "none", borderBottom: `2px solid ${mgmtTab === "staff" ? C.red : "transparent"}`, borderRadius: 5, background: mgmtTab === "staff" ? C.s3 : "transparent", color: mgmtTab === "staff" ? C.red : C.textM, fontSize: 10, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>
              👥 Staff
            </button>
            <button onClick={() => { setMgmtTab("add"); setEditingStaff(null); }} style={{ flex: 1, padding: "7px", border: "none", borderBottom: `2px solid ${mgmtTab === "add" ? C.red : "transparent"}`, borderRadius: 5, background: mgmtTab === "add" ? C.s3 : "transparent", color: mgmtTab === "add" ? C.red : C.textM, fontSize: 10, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>
              ➕ Add
            </button>
            <button onClick={() => { setMgmtTab("vendors"); setEditingStaff(null); }} style={{ flex: 1, padding: "7px", border: "none", borderBottom: `2px solid ${mgmtTab === "vendors" ? C.red : "transparent"}`, borderRadius: 5, background: mgmtTab === "vendors" ? C.s3 : "transparent", color: mgmtTab === "vendors" ? C.red : C.textM, fontSize: 10, fontWeight: 700, fontFamily: "inherit", cursor: "pointer" }}>
              🔑 Vendor Logins
            </button>
          </div>

          {/* ── STAFF LIST / EDIT ── */}
          {mgmtTab === "staff" && !editingStaff && (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {staff.map(s => (
                <div key={s.id} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div style={{ width: 32, height: 32, borderRadius: "50%", background: s.color || C.amber, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, color: "#fff", flexShrink: 0 }}>
                        {s.name[0]}
                      </div>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#000" }}>{s.name}</div>
                        <div style={{ fontSize: 10, color: "#555" }}>
                          @{s.username} · {s.role}
                          {s.phone && <span> · 📱 {s.phone}</span>}
                          
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <button onClick={() => setEditingStaff({ ...s })}
                        style={{ background: C.s2, border: `1px solid ${C.border}`, borderRadius: 6, padding: "5px 12px", cursor: "pointer", fontSize: 11, fontWeight: 600, color: C.textS, fontFamily: "inherit" }}>
                        Edit
                      </button>
                      {s.id !== currentUser?.id && s.role !== "manager" && (
                        <button onClick={() => removeEmployee(s.id)}
                          style={{ background: C.red + "10", border: `1px solid ${C.red}30`, borderRadius: 6, padding: "5px 10px", cursor: "pointer", fontSize: 11, color: C.red, fontFamily: "inherit" }}>
                          ✕
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* ── EDIT EMPLOYEE ── */}
          {mgmtTab === "staff" && editingStaff && (
            <div style={{ background: C.surface, border: `2px solid ${C.red}40`, borderRadius: 12, padding: "16px" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 14 }}>
                <div style={{ fontSize: 14, fontWeight: 800, color: "#000" }}>Editing: {editingStaff.name}</div>
                <button onClick={() => setEditingStaff(null)} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, color: C.textM }}>✕</button>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                <TI label="Full Name" value={editingStaff.name} onChange={v => setEditingStaff(e => ({ ...e, name: v }))} />
                <TI label="Email" value={editingStaff.email || ""} onChange={v => setEditingStaff(e => ({ ...e, email: v }))} type="email" />
                <TI label="Phone" value={editingStaff.phone || ""} onChange={v => setEditingStaff(e => ({ ...e, phone: v }))} placeholder="503-555-0100" />
                <TI label="Hourly Rate ($)" value={String(editingStaff.hourlyRate || 0)} onChange={v => setEditingStaff(e => ({ ...e, hourlyRate: parseFloat(v) || 0 }))} type="number" />
                <TI label="Username" value={editingStaff.username || ""} onChange={v => setEditingStaff(e => ({ ...e, username: v.toLowerCase() }))} />
                <TI label="New Password" value={editingStaff.newPassword || ""} onChange={v => setEditingStaff(e => ({ ...e, newPassword: v }))} type="password" placeholder="Leave blank to keep" />
                <SI label="Role" value={editingStaff.role} onChange={v => setEditingStaff(e => ({ ...e, role: v }))} options={STAFF_ROLES.map(r => ({ value: r.id, label: `${r.icon} ${r.label}` }))} />
                <TI label="Daily Hours Capacity" value={String(editingStaff.dailyHours || 8)} onChange={v => setEditingStaff(e => ({ ...e, dailyHours: parseFloat(v) || 8 }))} type="number" />
              </div>
              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 6 }}>Profile Color</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {STAFF_COLORS.map(c => (
                    <button key={c} onClick={() => setEditingStaff(e => ({ ...e, color: c }))}
                      style={{ width: 28, height: 28, borderRadius: "50%", background: c, border: `3px solid ${editingStaff.color === c ? "#000" : "transparent"}`, cursor: "pointer" }} />
                  ))}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <Btn variant="red" onClick={() => {
                  const updated = { ...editingStaff };
                  if (updated.newPassword) { updated.password = updated.newPassword.toLowerCase(); delete updated.newPassword; }
                  setStaff(prev => prev.map(s => s.id === updated.id ? updated : s));
                  if (currentUser.id === updated.id) setCurrentUser(updated);
                  setEditingStaff(null);
                }}>Save Changes</Btn>
                <Btn variant="ghost" onClick={() => setEditingStaff(null)}>Cancel</Btn>
              </div>
            </div>
          )}

          {/* ── VENDOR CREDENTIALS ── */}
          {mgmtTab === "vendors" && (
            <div>
              <div style={{ fontSize: 11, color: C.textM, marginBottom: 14 }}>Store login credentials for parts suppliers and vendor portals. Used by Roxy when searching for parts.</div>
              {vendors.filter(v => v.type === "PARTS" || ["autozone","oreilly","napa","rockauto","amazon","fcpeuro","worldpac","lkq"].some(k => v.name?.toLowerCase().includes(k.toLowerCase()))).map(v => (
                <div key={v.id} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px", marginBottom: 10 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: "#000", marginBottom: 8 }}>{v.name}</div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                    <TI label="Account #"
                      value={v.credentials?.accountNumber || ""}
                      onChange={val => setVendors(prev => prev.map(x => x.id === v.id ? { ...x, credentials: { ...(x.credentials||{}), accountNumber: val } } : x))}
                      placeholder="Account number" />
                    <TI label="Account Rep"
                      value={v.credentials?.rep || ""}
                      onChange={val => setVendors(prev => prev.map(x => x.id === v.id ? { ...x, credentials: { ...(x.credentials||{}), rep: val } } : x))}
                      placeholder="Rep name/number" />
                    <TI label="Username/Email"
                      value={v.credentials?.username || ""}
                      onChange={val => setVendors(prev => prev.map(x => x.id === v.id ? { ...x, credentials: { ...(x.credentials||{}), username: val } } : x))}
                      placeholder="Login email" />
                    <TI label="Password"
                      value={v.credentials?.password || ""}
                      onChange={val => setVendors(prev => prev.map(x => x.id === v.id ? { ...x, credentials: { ...(x.credentials||{}), password: val } } : x))}
                      type="password" placeholder="Password" />
                  </div>
                  <div style={{ fontSize: 10, color: C.green, marginTop: 6, fontWeight: 700 }}>✓ Changes save automatically to Firebase</div>
                </div>
              ))}
            </div>
          )}

          {/* ── ADD NEW EMPLOYEE ── */}
          {mgmtTab === "add" && (
            <div style={{ background: C.surface, border: `2px solid ${C.green}40`, borderRadius: 12, padding: "16px" }}>
              <div style={{ fontSize: 13, fontWeight: 800, color: C.green, marginBottom: 16 }}>➕ Add New Employee</div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
                <TI label="Full Name *" value={newEmployee.name} onChange={v => setNewEmployee(n => ({ ...n, name: v, username: v.split(" ")[0].toLowerCase() }))} placeholder="e.g. Mike Torres" />
                <SI label="Role *" value={newEmployee.role} onChange={v => setNewEmployee(n => ({ ...n, role: v }))} options={STAFF_ROLES.map(r => ({ value: r.id, label: `${r.icon} ${r.label}` }))} />
                <TI label="Email" value={newEmployee.email} onChange={v => setNewEmployee(n => ({ ...n, email: v }))} type="email" placeholder="mike@edgewaterauto.com" />
                <TI label="Phone" value={newEmployee.phone} onChange={v => setNewEmployee(n => ({ ...n, phone: v }))} placeholder="503-555-0100" />
                <TI label="Hourly Rate ($)" value={newEmployee.hourlyRate} onChange={v => setNewEmployee(n => ({ ...n, hourlyRate: v }))} type="number" placeholder="e.g. 18" />
                <TI label="Daily Hours Capacity" value={newEmployee.dailyHours} onChange={v => setNewEmployee(n => ({ ...n, dailyHours: v }))} type="number" />
              </div>

              <div style={{ background: C.s2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "12px 14px", marginBottom: 12 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#000", marginBottom: 10 }}>🔑 Login Credentials</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <TI label="Username *" value={newEmployee.username} onChange={v => setNewEmployee(n => ({ ...n, username: v.toLowerCase() }))} placeholder="lowercase, no spaces" />
                  <TI label="Password *" value={newEmployee.password} onChange={v => setNewEmployee(n => ({ ...n, password: v }))} type="password" placeholder="Initial password" />
                </div>
                <div style={{ fontSize: 10, color: C.textM, marginTop: 6 }}>Employee can change their password after first login in their Settings.</div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 6 }}>Profile Color</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {STAFF_COLORS.map(c => (
                    <button key={c} onClick={() => setNewEmployee(n => ({ ...n, color: c }))}
                      style={{ width: 28, height: 28, borderRadius: "50%", background: c, border: `3px solid ${newEmployee.color === c ? "#000" : "transparent"}`, cursor: "pointer" }} />
                  ))}
                </div>
              </div>

              <div style={{ background: C.s2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "12px 14px", marginBottom: 14 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#000", marginBottom: 8 }}>🔔 Default Notifications</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                  {notifyTypes.map(nt => {
                    const on = newEmployee.notifyTypes?.includes(nt.id);
                    return (
                      <div key={nt.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontSize: 11, color: C.text }}>{nt.label}</span>
                        <button onClick={() => setNewEmployee(n => ({ ...n, notifyTypes: on ? n.notifyTypes.filter(x => x !== nt.id) : [...(n.notifyTypes || []), nt.id] }))}
                          style={{ width: 36, height: 20, borderRadius: 10, background: on ? C.green : C.border, border: "none", cursor: "pointer", position: "relative" }}>
                          <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#fff", position: "absolute", top: 3, left: on ? 19 : 3, transition: "left 0.2s" }} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Preview */}
              {newEmployee.name && (
                <div style={{ background: C.green + "10", border: `1px solid ${C.green}30`, borderRadius: 8, padding: "10px 14px", marginBottom: 14 }}>
                  <div style={{ fontSize: 10, color: C.green, fontWeight: 700, marginBottom: 4 }}>Preview</div>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 32, height: 32, borderRadius: "50%", background: newEmployee.color, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, color: "#fff" }}>{newEmployee.name[0]}</div>
                    <div>
                      <div style={{ fontSize: 12, fontWeight: 700, color: "#000" }}>{newEmployee.name}</div>
                      <div style={{ fontSize: 10, color: "#555" }}>@{newEmployee.username} · {newEmployee.role}</div>
                    </div>
                  </div>
                </div>
              )}

              <div style={{ display: "flex", gap: 8 }}>
                <Btn variant="green" onClick={addEmployee} disabled={!newEmployee.name.trim() || !newEmployee.username.trim() || !newEmployee.password.trim()}>
                  <CheckCircle size={14} /> Add Employee
                </Btn>
                <Btn variant="ghost" onClick={() => setMgmtTab("staff")}>Cancel</Btn>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ─── DAILY DIGEST ─────────────────────────────────────────────────────────────

const buildWOReport = (vehicles, parts, ledger) => {
  const dateStr = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
  const activeVehicles = vehicles.filter(v => !["SOLD","SCRAPPED"].includes(v.stage));

  let body = `EDGEWATER AUTO CENTER\nWORK ORDER STATUS REPORT — ${dateStr}\n${"═".repeat(60)}\n\n`;

  // Summary counts
  const totalOpenWOs = activeVehicles.flatMap(v => (v.workOrders||[]).filter(w => w.status !== "COMPLETE" && !w.deleted)).length;
  const totalCompleteWOs = activeVehicles.flatMap(v => (v.workOrders||[]).filter(w => w.status === "COMPLETE")).length;
  const partsOnOrder = parts.filter(p => ["Ordered","In Transit"].includes(p.status)).length;
  const partsReceived = parts.filter(p => p.status === "Received").length;
  const overdueVehicles = activeVehicles.filter(v => daysSince(v.arrivalDate) > 10);

  body += `SUMMARY\n`;
  body += `  Active vehicles in recon: ${activeVehicles.length}\n`;
  body += `  Open work orders: ${totalOpenWOs}\n`;
  body += `  Completed work orders: ${totalCompleteWOs}\n`;
  body += `  Parts on order: ${partsOnOrder}\n`;
  body += `  Parts received (awaiting install): ${partsReceived}\n`;
  if (overdueVehicles.length) body += `  ⚠ Overdue vehicles (10+ days): ${overdueVehicles.length}\n`;
  body += "\n";

  // Per-vehicle breakdown
  body += `VEHICLE WORK ORDER DETAILS\n${"─".repeat(60)}\n`;

  activeVehicles
    .sort((a, b) => daysSince(b.arrivalDate) - daysSince(a.arrivalDate))
    .forEach(v => {
      const openWOs = (v.workOrders||[]).filter(w => w.status !== "COMPLETE" && !w.deleted);
      const completedWOs = (v.workOrders||[]).filter(w => w.status === "COMPLETE");
      const vParts = parts.filter(p => p.vehicleId === v.id);
      const days = daysSince(v.arrivalDate);
      const late = days > 10;

      body += `\n${late ? "⚠ " : ""}${v.year} ${v.make} ${v.model} — Stock #${v.stockNumber}\n`;
      body += `   Days in recon: ${days} | Stage: ${STAGES.find(s=>s.id===v.stage)?.label || v.stage}\n`;

      if (openWOs.length) {
        body += `   OPEN WORK ORDERS (${openWOs.length}):\n`;
        openWOs.forEach(wo => {
          const assigned = wo.assignedTo || "Unassigned";
          const priority = wo.priority || "MEDIUM";
          body += `     • [${priority}] ${wo.description} → ${assigned}`;
          if (wo.scheduledDate) body += ` (scheduled ${wo.scheduledDate})`;
          body += "\n";
        });
      }

      if (completedWOs.length) {
        body += `   COMPLETED (${completedWOs.length}): ${completedWOs.map(w=>w.description).join(", ")}\n`;
      }

      if (vParts.length) {
        const pending = vParts.filter(p => !["Installed","CANCELLED","RETURNED"].includes(p.status));
        if (pending.length) {
          body += `   PARTS: ${pending.map(p=>`${p.description||p.name} (${p.status})`).join(", ")}\n`;
        }
      }

      if (openWOs.length === 0 && completedWOs.length > 0) {
        body += `   ✅ All work orders complete\n`;
      }
    });

  body += `\n${"═".repeat(60)}\n`;
  body += `Report generated by Edgewater Recon Manager\n`;
  body += `reconmanager.netlify.app\n`;

  return body;
};

const buildDigestEmail = (vehicles, ledger, parts, staff, timeEntries) => {
  const today = todayStr();

  const soldToday = vehicles.filter(v => v.saleDate === today);
  const completedToday = vehicles.filter(v => v.stage === "LINE_READY" && daysSince(v.arrivalDate) === 0);
  const killSwitch = parts.filter(p => { const v = vehicles.find(x => x.id === p.vehicleId); return v?.stage === "SOLD" && !["Installed","RETURNED","CANCELLED"].includes(p.status); });
  const overBudget = vehicles.filter(v => {
    const info = calcProfit(v, ledger, v.kbbValue);
    return info.overBudget;
  });
  const todayLaborCost = ledger.filter(e => e.date === today && e.category === "LABOR").reduce((s, e) => s + e.amount, 0);

  let body = `EDGEWATER AUTO CENTER — Daily Digest\n${new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}\n${"─".repeat(50)}\n\n`;

  if (soldToday.length) {
    body += `VEHICLES SOLD TODAY (${soldToday.length})\n`;
    soldToday.forEach(v => {
      const info = calcProfit(v, ledger, v.kbbValue);
      body += `  • ${v.year} ${v.make} ${v.model} #${v.stockNumber} — Sold: ${fmtMoney(info.salePrice)} | Cost: ${fmtMoney(info.costIn)} | Gross: ${fmtMoney(info.currentGross)}\n`;
    });
    body += "\n";
  }

  if (killSwitch.length) {
    body += `⚠ KILL SWITCH — PARTS NEED RETURN (${killSwitch.length})\n`;
    killSwitch.forEach(p => {
      const v = vehicles.find(x => x.id === p.vehicleId);
      body += `  • ${p.description} — ${v?.year} ${v?.make} ${v?.model} #${v?.stockNumber} — Status: ${p.status}\n`;
    });
    body += "\n";
  }

  if (overBudget.length) {
    body += `⚠ OVER RECON BUDGET (${overBudget.length})\n`;
    overBudget.forEach(v => {
      const info = calcProfit(v, ledger, v.kbbValue);
      body += `  • ${v.year} ${v.make} ${v.model} #${v.stockNumber} — Recon: ${fmtMoney(info.reconCost)} (${Math.round(info.reconPct * 100)}% of gross)\n`;
    });
    body += "\n";
  }

  body += `LABOR TODAY\n  Total labor posted: ${fmtMoney(todayLaborCost)}\n`;
  staff.forEach(sm => {
    const entries = timeEntries.filter(e => e.staffId === sm.id && e.date === today);
    if (entries.length) {
      const hrs = entries.reduce((sum, e) => {
        const s = new Date(`${e.date}T${e.startTime}`);
        const en = e.endTime ? new Date(`${e.date}T${e.endTime}`) : new Date();
        return sum + (en - s) / 3600000;
      }, 0);
      body += `  • ${sm.name}: ${hrs.toFixed(1)}h\n`;
    }
  });

  body += `\n${"─".repeat(50)}\nSent automatically by Edgewater Recon Center\n`;
  return body;
};

// ─── PIPELINE ─────────────────────────────────────────────────────────────────


// ─── BUSINESS DAYS & HOLIDAYS ────────────────────────────────────────────────
const HOLIDAYS = (year) => {
  const h = new Set();
  // New Year's Day
  h.add(`${year}-01-01`);
  // Christmas Eve & Christmas
  h.add(`${year}-12-24`);
  h.add(`${year}-12-25`);
  // Thanksgiving (4th Thursday of November) + Friday after
  const nov1 = new Date(year, 10, 1);
  const firstThurs = (4 - nov1.getDay() + 7) % 7;
  const thanksgiving = new Date(year, 10, 1 + firstThurs + 21);
  h.add(thanksgiving.toISOString().slice(0,10));
  const fridayAfter = new Date(thanksgiving); fridayAfter.setDate(fridayAfter.getDate() + 1);
  h.add(fridayAfter.toISOString().slice(0,10));
  // Easter (approximate - Gauss algorithm)
  const a = year % 19, b = Math.floor(year/100), c = year % 100;
  const d = Math.floor(b/4), e = b % 4, f = Math.floor((b+8)/25);
  const g = Math.floor((b-f+1)/3), h2 = (19*a+b-d-g+15) % 30;
  const i = Math.floor(c/4), k = c % 4;
  const l = (32+2*e+2*i-h2-k) % 7;
  const m = Math.floor((a+11*h2+22*l)/451);
  const month = Math.floor((h2+l-7*m+114)/31);
  const day = ((h2+l-7*m+114) % 31) + 1;
  const easter = new Date(year, month-1, day);
  h.add(easter.toISOString().slice(0,10));
  return h;
};

// Count business days between two date strings
const businessDaysBetween = (startStr, endStr) => {
  const holidays = HOLIDAYS(new Date().getFullYear());
  let count = 0;
  let d = new Date(startStr + "T12:00:00");
  const end = new Date(endStr + "T12:00:00");
  while (d < end) {
    d.setDate(d.getDate() + 1);
    const ds = d.toISOString().slice(0,10);
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6 && !holidays.has(ds)) count++;
  }
  return count;
};

const businessDaysSince = (dateStr) => {
  if (!dateStr) return 0;
  const start = new Date(dateStr);
  const end = new Date();
  let count = 0;
  const cur = new Date(start);
  const curYear = cur.getFullYear();
  const holidays = new Set([...HOLIDAYS(curYear), ...HOLIDAYS(curYear + 1)]);
  while (cur < end) {
    cur.setDate(cur.getDate() + 1);
    const dow = cur.getDay();
    if (dow !== 0 && dow !== 6 && !holidays.has(cur.toISOString().slice(0,10))) count++;
  }
  return count;
};

// ─── OVERDUE VIEW ─────────────────────────────────────────────────────────────
const OverdueView = ({ vehicles, openVehicle, onBack }) => {
  const overdue = vehicles.filter(v =>
    !["SOLD","SCRAPPED","LINE_READY"].includes(v.stage) &&
    businessDaysSince(v.arrivalDate) > 10
  ).sort((a, b) => businessDaysSince(b.arrivalDate) - businessDaysSince(a.arrivalDate));

  return (
    <div>
      <button onClick={onBack} style={{ background: "none", border: "none", color: C.textS, cursor: "pointer", fontSize: 11, padding: 0, marginBottom: 14, display: "flex", alignItems: "center", gap: 4 }}><img src="/images/btn-back-arrow.jpg" alt="" style={{width:16,height:16,objectFit:"contain",flexShrink:0}} onError={e=>e.target.style.display="none"} /> Back</button>
      <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:4 }}><SiteLogo size="md" /><div style={{ fontSize: 26, fontWeight: 800, color: C.red, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.06em" }}>OVERDUE IN RECON</div></div>
      <div style={{ fontSize: 11, color: C.textM, marginBottom: 16 }}>Vehicles with 10+ business days in recon. Excludes weekends, major holidays (New Year's, Easter, Thanksgiving, Christmas Eve/Day).</div>
      {overdue.length === 0 ? (
        <div style={{ textAlign: "center", color: C.green, padding: 48, fontSize: 13 }}>✓ No overdue vehicles — great work!</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          {overdue.map(v => {
            const bizDays = businessDaysSince(v.arrivalDate);
            const stage = STAGES.find(s => s.id === v.stage);
            const openWOs = v.workOrders?.filter(w => w.status !== "COMPLETE").length || 0;
            return (
              <div key={v.id} onClick={() => openVehicle(v)} style={{ background: C.surface, border: `2px solid ${C.red}40`, borderLeft: `4px solid ${C.red}`, borderRadius: 8, padding: "12px 14px", cursor: "pointer" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: C.text, marginBottom: 4 }}>{v.year} {v.make} {v.model}</div>
                    <div style={{ fontSize: 11, color: C.textM, fontFamily: "monospace", marginBottom: 6 }}>#{v.stockNumber} · {v.vin || "···" + v.vinLast6}</div>
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                      <StageBadge stageId={v.stage} small />
                      {openWOs > 0 && <Badge color={C.amber} small>{openWOs} open WO{openWOs !== 1 ? "s" : ""}</Badge>}
                    </div>
                  </div>
                  <div style={{ textAlign: "right", flexShrink: 0, marginLeft: 12 }}>
                    <div style={{ fontSize: 28, fontWeight: 800, color: C.red, fontFamily: "'Bebas Neue', sans-serif", lineHeight: 1 }}>{bizDays}</div>
                    <div style={{ fontSize: 10, color: C.textM }}>business days</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};


const PipelineView = ({ vehicles, parts, openVehicle, setView, showHoldingCost }) => {
  const [stageFilter, setStageFilter] = useState("ALL");
  const [search, setSearch] = useState("");

  const activeVehicles = vehicles.filter(v => !["SOLD","SCRAPPED"].includes(v.stage));
  const filtered = activeVehicles.filter(v => {
    if (stageFilter !== "ALL" && v.stage !== stageFilter) return false;
    if (search && !`${v.year} ${v.make} ${v.model} ${v.stockNumber} ${v.vinLast6}`.toLowerCase().includes(search.toLowerCase())) return false;
    return true;
  });

  // Group by stage when showing ALL
  const stageOrder = STAGES.map(s => s.id);
  const grouped = stageFilter === "ALL"
    ? STAGES.map(s => ({ stage: s, vehicles: filtered.filter(v => v.stage === s.id).sort((a,b) => new Date(a.arrivalDate) - new Date(b.arrivalDate)) })).filter(g => g.vehicles.length > 0)
    : [{ stage: STAGES.find(s => s.id === stageFilter) || { id: stageFilter, label: stageFilter, color: C.textM }, vehicles: filtered.sort((a,b) => new Date(a.arrivalDate) - new Date(b.arrivalDate)) }];

  return (
    <div>
      <div style={{ fontSize: 26, fontWeight: 800, color: C.amber, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.06em", marginBottom: 16 }}>VEHICLE PIPELINE</div>
      <div style={{ display: "flex", gap: 8, marginBottom: 16, flexWrap: "wrap" }}>
        <div style={{ flex:1, minWidth:200, position:"relative", display:"flex", alignItems:"center" }}>
          <img src="/images/icon-search-vin.jpg" alt="" style={{ position:"absolute", left:10, width:18, height:18, objectFit:"contain", opacity:0.5, pointerEvents:"none" }} onError={e=>e.target.style.display="none"} />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search stock #, year, make, model, VIN…" style={{ width:"100%", background: C.s2, border: `1px solid ${C.border}`, borderRadius: 6, padding: "7px 12px 7px 34px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none" }} />
        </div>
        <select value={stageFilter} onChange={e => setStageFilter(e.target.value)} style={{ background: C.s2, border: `1px solid ${C.border}`, borderRadius: 6, padding: "7px 12px", color: C.text, fontSize: 11, fontFamily: "inherit", outline: "none" }}>
          <option value="ALL">All Stages ({activeVehicles.length})</option>
          {STAGES.map(s => { const c = activeVehicles.filter(v => v.stage === s.id).length; return c > 0 ? <option key={s.id} value={s.id}>{s.label} ({c})</option> : null; })}
        </select>
      </div>
      {filtered.length === 0 ? <div style={{ textAlign: "center", color: C.textM, padding: 48, fontSize: 13 }}>{vehicles.length === 0 ? "No vehicles yet. Scan a VIN to begin." : "No matches."}</div> : (
        <div>
          {grouped.map(({ stage, vehicles: gv }) => (
            <div key={stage.id} style={{ marginBottom: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, borderBottom: `2px solid ${stage.color || C.border}`, paddingBottom: 6 }}>
                {(() => {
                  const icons = { INTAKE:"stage-icon-intake.jpg", INITIAL_DIAG:"stage-icon-initial-diag.jpg", QC_DETAIL:"stage-icon-qc-detail.jpg" };
                  return icons[stage.id]
                    ? <img src={`/images/${icons[stage.id]}`} alt="" style={{ width:20, height:20, objectFit:"contain", flexShrink:0 }} onError={e=>e.target.style.display="none"} />
                    : <div style={{ width:10, height:10, borderRadius:"50%", background:stage.color||C.textM }} />;
                })()}
                <div style={{ fontSize: 12, fontWeight: 800, color: stage.color || C.text, textTransform: "uppercase", letterSpacing: "0.08em" }}>{stage.label}</div>
                <div style={{ fontSize: 11, color: C.textM }}>— {gv.length} vehicle{gv.length !== 1 ? "s" : ""}</div>
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 7 }}>
          {gv.map(v => {
            const stage = STAGES.find(s => s.id === v.stage);
            const days = daysSince(v.arrivalDate);
            const openWOs = v.workOrders?.filter(w => w.status !== "COMPLETE").length || 0;
            const ks = parts.filter(p => p.vehicleId === v.id && v.stage === "SOLD" && !["Installed","RETURNED","CANCELLED"].includes(p.status)).length;
            const late = days > 10 && !["LINE_READY","SOLD"].includes(v.stage);
            return (
              <div key={v.id} onClick={() => openVehicle(v)} style={{ background: C.surface, border: `1px solid ${late ? C.red + "55" : C.border}`, borderLeft: `4px solid ${stage?.color || C.border}`, borderRadius: 8, padding: "11px 14px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{v.year} {v.make} {v.model}</span>
                    <span style={{ fontSize: 10, color: C.textM, fontFamily: "monospace" }}>#{v.stockNumber}</span>
                    {v.kbbValue && <Badge color={C.green} small>KBB {fmtMoney(v.kbbValue)}</Badge>}
                  </div>
                  <div style={{ display: "flex", gap: 5, flexWrap: "wrap" }}>
                    <StageBadge stageId={v.stage} small />
                    <Badge color={late ? C.red : days > 7 ? "#f97316" : C.textM} small>{days}d</Badge>
                    {openWOs > 0 && <Badge color={C.amber} small>{openWOs} open WO</Badge>}
                    {ks > 0 && <Badge color={C.red} small>⚠ RETURN PARTS</Badge>}
                    {showHoldingCost && !["LINE_READY","SOLD"].includes(v.stage) && <HoldingCostBadge vehicle={v} compact />}
                  </div>
                </div>
                <ChevronRight size={14} color={C.textM} style={{ flexShrink: 0, marginLeft: 8 }} />
              </div>
            );
          })}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};



// Returns appropriate vehicle placeholder image based on year/make/model
const getVehiclePlaceholder = (vehicle) => {
  const text = ((vehicle?.make || "") + " " + (vehicle?.model || "")).toLowerCase();
  if (/truck|f-?150|f-?250|silverado|sierra|ram|tacoma|tundra|colorado|ranger|frontier|canyon|ridgeline/.test(text))
    return "/images/vehicle-placeholder-truck.jpg";
  if (/suv|expedition|tahoe|suburban|navigator|escalade|explorer|4runner|sequoia|pilot|pathfinder|highlander|traverse|yukon|blazer|bronco|passport|cx-9|rav4|cr-v|rogue|equinox|escape|terrain|edge|tiguan|tucson|santa fe|sorento|telluride|pilot|murano/.test(text))
    return "/images/vehicle-placeholder-suv.jpg";
  if (/van|caravan|odyssey|sienna|pacifica|transit|sprinter/.test(text))
    return "/images/vehicle-placeholder-van.jpg";
  return "/images/vehicle-placeholder-car.jpg";
};

// ─── QUICK ORDER PART ────────────────────────────────────────────────────────

const QuickOrderPart = ({ vehicle, vendors, onAddPart }) => {
  const [open, setOpen] = useState(false);
  const [desc, setDesc] = useState("");
  const [cost, setCost] = useState("");
  const [supplier, setSupplier] = useState("v4");
  const [eta, setEta] = useState(addDays(todayStr(), 3));

  const binPreview = desc.trim() ? estimateBinSize(desc) : null;
  const binDef = binPreview ? BIN_SIZES.find(b => b.id === binPreview) : null;

  const save = () => {
    if (!desc.trim()) return;
    onAddPart({
      id: genId(), vehicleId: vehicle.id,
      description: desc.trim(), partNumber: "",
      supplier, cost: parseFloat(cost) || 0,
      orderDate: todayStr(), eta, status: "Ordered",
      binSize: estimateBinSize(desc),
    });
    setDesc(""); setCost(""); setEta(addDays(todayStr(), 3)); setOpen(false);
  };

  if (!open) return (
    <div style={{ marginBottom: 12 }}>
      <Btn variant="outline" size="sm" onClick={() => setOpen(true)}>
        <Plus size={11} /> Order a Part
      </Btn>
    </div>
  );

  return (
    <div style={{ background: C.s2, border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 16px", marginBottom: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 12 }}>Order a Part for This Vehicle</div>
      <TI label="Part Description *" value={desc} onChange={setDesc} placeholder="e.g. Front brake pads, Radiator cap…" />

      {/* Live bin size preview as they type */}
      {binDef && (
        <div style={{ background: binPreview === "OVERSIZE" ? C.red + "12" : C.s3, border: `1px solid ${binPreview === "OVERSIZE" ? C.red + "50" : C.border}`, borderRadius: 6, padding: "8px 12px", marginBottom: 12, display: "flex", alignItems: "center", gap: 8 }}>
          <span style={{ fontSize: 18 }}>{binDef.icon}</span>
          <div>
            <div style={{ fontSize: 11, fontWeight: 700, color: binPreview === "OVERSIZE" ? C.red : C.text }}>
              Bin: {binDef.label}
              {binPreview === "OVERSIZE" && " — ⚠ OVERSIZE: will need separate storage"}
            </div>
            <div style={{ fontSize: 10, color: C.textM }}>{binDef.description}</div>
          </div>
        </div>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
        <TI label="Cost ($)" value={cost} onChange={setCost} type="number" placeholder="0.00" />
        <TI label="Expected Arrival" value={eta} onChange={setEta} type="date" />
      </div>
      <SI label="Supplier" value={supplier} onChange={setSupplier} options={[
        { value: "v4", label: "AutoZone Commercial" },
        { value: "v5", label: "O'Reilly Pro" },
        { value: "v6", label: "RockAuto" },
        { value: "", label: "Other" },
      ]} />
      <div style={{ display: "flex", gap: 8 }}>
        <Btn variant="amber" onClick={save} disabled={!desc.trim()}>
          <Package size={13} /> Place Order
        </Btn>
        <Btn variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn>
      </div>
    </div>
  );
};



// ─── DIAGNOSIS ENTRY CHOOSER ─────────────────────────────────────────────────

// ─── JULIA CHECKLIST MODAL ───────────────────────────────────────────────────
const JuliaChecklistModal = ({ vehicle, onAdd, onClose }) => {
  const [messages, setMessages] = useState([{ role: "assistant", content: `What would you like to add to the cleaning checklist for this ${vehicle.year || ""} ${vehicle.make || ""} ${vehicle.model || ""}? You can type or list multiple things at once.` }]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [proposed, setProposed] = useState([]);
  const listRef = useRef(null);

  useEffect(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, [messages, loading]);

  const send = async (text) => {
    if (!text.trim()) return;
    const newMsgs = [...messages, { role: "user", content: text }];
    setMessages(newMsgs);
    setInput("");
    setLoading(true);
    try {
      const prompt = `You are Julia, Edgewater Auto Center's inspection specialist. A staff member wants to add items to the cleaning checklist for a ${vehicle.year || ""} ${vehicle.make || ""} ${vehicle.model || ""}.

Conversation:
${newMsgs.map(m => `${m.role === "user" ? "Staff" : "Julia"}: ${m.content}`).join("\n")}

Turn their input into specific, actionable cleaning checklist items. Be friendly. Ask if there's anything else to add.

Respond ONLY in JSON (no markdown):
{
  "message": "Julia's friendly response",
  "checklistItems": ["specific actionable item 1", "specific actionable item 2"],
  "done": false
}`;

      const res = await fetch("/api/claude", { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${await getIdToken()}` },
        body: JSON.stringify({ model: "claude-sonnet-4-6", max_tokens: 600, messages: newMsgs })
      });
      const data = await res.json();
      const raw = data.content?.[0]?.text || "{}";
      const parsed = JSON.parse(raw.replace(/```json|```/g, "").trim());
      setMessages(m => [...m, { role: "assistant", content: parsed.message || "Got it! Anything else?" }]);
      if (parsed.checklistItems?.length) {
        setProposed(p => [...p, ...parsed.checklistItems.map(item => ({ id: genId(), text: item, checked: true }))]);
      }
    } catch {
      setMessages(m => [...m, { role: "assistant", content: "Got it! Is there anything else to add?" }]);
    }
    setLoading(false);
  };

  const toggleItem = (id) => setProposed(p => p.map(i => i.id === id ? { ...i, checked: !i.checked } : i));
  const checkedItems = proposed.filter(i => i.checked);

  return (
    <Modal title="🧼 Add to Cleaning Checklist" onClose={onClose}>
      {/* Julia chat */}
      <div ref={listRef} style={{ maxHeight: 180, overflowY: "auto", marginBottom: 12, display: "flex", flexDirection: "column", gap: 6 }}>
        {messages.map((m, i) => (
          <div key={i} style={{ display: "flex", gap: 8, alignItems: "flex-start" }}>
            {m.role === "assistant" && (
              <img src="/images/Julia.jpg" alt="Julia" style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover", flexShrink: 0, border: `2px solid ${C.purple}` }}
                onError={e => { e.target.style.display = "none"; }} />
            )}
            <div style={{ background: m.role === "assistant" ? C.purple + "15" : C.amber + "15", borderRadius: 8, padding: "7px 10px", fontSize: 12, color: C.text, lineHeight: 1.5, maxWidth: "85%", marginLeft: m.role === "user" ? "auto" : 0 }}>
              {m.content}
            </div>
          </div>
        ))}
        {loading && <div style={{ fontSize: 11, color: C.purple, fontStyle: "italic", paddingLeft: 36 }}>Julia is thinking…</div>}
      </div>

      {/* Proposed items with checkboxes */}
      {proposed.length > 0 && (
        <div style={{ background: C.s2, borderRadius: 8, padding: "10px 12px", marginBottom: 12 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 8 }}>
            Checklist Items — check to include ({checkedItems.length}/{proposed.length} selected)
          </div>
          {proposed.map(item => (
            <label key={item.id} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "5px 0", cursor: "pointer", borderBottom: `1px solid ${C.border}` }}>
              <input type="checkbox" checked={item.checked} onChange={() => toggleItem(item.id)}
                style={{ marginTop: 2, accentColor: C.purple, flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: item.checked ? C.text : C.textM, lineHeight: 1.4, textDecoration: item.checked ? "none" : "line-through" }}>{item.text}</span>
            </label>
          ))}
        </div>
      )}

      {/* Input */}
      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
        <input value={input} onChange={e => setInput(e.target.value)} placeholder="Type what needs extra attention…"
          onKeyDown={e => e.key === "Enter" && send(input)}
          style={{ flex: 1, background: C.s2, border: `1px solid ${C.border}`, borderRadius: 7, padding: "8px 10px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none" }} />
        <Btn variant="purple" onClick={() => send(input)} disabled={loading || !input.trim()}>Send</Btn>
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        {checkedItems.length > 0 && (
          <Btn variant="amber" onClick={() => onAdd(checkedItems.map(i => i.text))}>
            ✓ Add {checkedItems.length} Item{checkedItems.length !== 1 ? "s" : ""} to Detail WO
          </Btn>
        )}
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
      </div>
    </Modal>
  );
};

// ─── AI WORK ORDER MODAL ─────────────────────────────────────────────────────
const AIWorkOrderModal = ({ vehicle, currentUser, staff, onAdd, onClose, onUpdateVehicle, onTriggerCalendarRebuild, onAddLedger }) => {
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [proposed, setProposed] = useState([]);
  const [followUp, setFollowUp] = useState("");
  const [category, setCategory] = useState("MECHANICAL");
  const [manualMode, setManualMode] = useState(false);
  const [manualPriority, setManualPriority] = useState("MEDIUM");
  const [manualHours, setManualHours] = useState("");
  const [manualPartsCost, setManualPartsCost] = useState("");
  const [soldRepairMode, setSoldRepairMode] = useState(false);
  const [soldRepairData, setSoldRepairData] = useState({ returnDate: "", isGoodwill: null, goodwillFormSigned: false, needsTow: null, towPickup: "", towDropoff: "main_lot", recoveryOdometer: "", towCost: "" });
  const [towSentFromWO, setTowSentFromWO] = useState(false);

  const saleOdo = vehicle.saleOdometer || null;
  const recoveryOdo = parseFloat(soldRepairData.recoveryOdometer) || null;
  const odometerDiff = saleOdo && recoveryOdo ? recoveryOdo - saleOdo : null;
  const requiresManagerApproval = odometerDiff !== null && odometerDiff > 100;
  const isManager = currentUser?.role === "manager";
  const isSold = vehicle.stage === "SOLD";

  const generate = async (text) => {
    if (!text.trim()) return;
    setLoading(true);
    setFollowUp("");
    try {
      // Detect if user says vehicle is sold
      const soldKeywords = /already.*sold|sold.*vehicle|customer.*waiting|goodwill|sold.*customer|post.*sale|after.*sale/i;
      const mentionsSold = soldKeywords.test(text) || isSold;

      const prompt = `You are a shop foreman at a used car dealership. A staff member submitted this work order request for a ${vehicle.year || ""} ${vehicle.make || ""} ${vehicle.model || ""} (Stock #${vehicle.stockNumber || "unknown"})${isSold || mentionsSold ? " — THIS VEHICLE HAS ALREADY BEEN SOLD AND IS A CUSTOMER VEHICLE" : ""}:

"${text}"

${mentionsSold || isSold ? `IMPORTANT: This is a POST-SALE CUSTOMER VEHICLE repair. Rules:
- ALL work orders must be HIGH priority
- DO NOT add detail, cleaning, or cosmetic WOs unless explicitly requested — customer gets repair only
- Only include what was promised or is directly necessary for the repair
- Ask about: When is the customer expecting the vehicle back? Is this Goodwill or billable?
- If goodwill: note that the Goodwill Service form must be signed before delivery` : `Generate clean, specific work orders. Ask ONE follow-up question if clarification is needed. Note if any required parts are likely already in stock at the shop.`}

Respond ONLY in JSON:
{
  "followUp": "one clarifying question if needed, or null",
  "isSoldVehicleRepair": ${mentionsSold || isSold},
  "requiresCustomerReturnDate": ${mentionsSold || isSold},
  "workOrders": [
    {
      "category": "MECHANICAL|DETAIL|BODYWORK|LOCKSMITH|COSMETIC|VENDOR|OTHER",
      "description": "job name e.g. Brake Job — Front Axle",
      "priority": "${mentionsSold || isSold ? "HIGH" : "HIGH|MEDIUM|LOW"}",
      "estimatedHours": 1.5,
      "estimatedPartsCost": 0,
      "notes": "any extra context",
      "jobItems": [
        {
          "step": 1,
          "description": "Remove front wheels and inspect brake components",
          "type": "labor",
          "estimatedHours": 0.3
        },
        {
          "step": 2,
          "description": "Replace front brake pads (both sides)",
          "type": "parts",
          "estimatedHours": 0.5,
          "partName": "Front brake pad set",
          "estimatedPartCost": 45
        },
        {
          "step": 3,
          "description": "Replace front rotors (both sides) — worn beyond spec",
          "type": "parts",
          "estimatedHours": 0.5,
          "partName": "Front rotors (pair)",
          "estimatedPartCost": 80
        },
        {
          "step": 4,
          "description": "Torque wheels, road test, verify brake feel",
          "type": "labor",
          "estimatedHours": 0.2
        }
      ]
    }
  ]
}`;

      const res = await fetch("/api/claude", { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${await getIdToken()}` },
        body: JSON.stringify({ model: "claude-sonnet-4-6", max_tokens: 1000, messages: [{ role: "user", content: prompt }] })
      });
      const data = await res.json();
      const raw = data.content?.[0]?.text || "{}";
      const clean = raw.replace(/```json|```/g, "").trim();
      const parsed = JSON.parse(clean);
      if (parsed.followUp) setFollowUp(parsed.followUp);
      // Force HIGH priority for sold vehicles
      const wos = (parsed.workOrders || []).map(w => {
        const needsParts = (w.estimatedPartsCost || 0) > 0 || (w.jobItems || []).some(j => j.type === "parts" || (j.partName && j.partName.trim()));
        return {
          ...w,
          // Pre-approve at the UI level — the user is the one initiating the add.
          // Persistent approval workflow is tracked on the saved WO via requiresApproval/approved (set by parent onAdd handler).
          _approved: true,
          category: w.category || category,
          priority: (parsed.isSoldVehicleRepair || isSold) ? "HIGH" : w.priority,
          soldVehicleRepair: parsed.isSoldVehicleRepair || isSold || false,
          partsETA: needsParts ? addDays(todayStr(), 3) : "",
          allPartsIn: false,
        };
      });
      // If the AI returned no work orders (e.g., API error JSON with no content),
      // fall through to a default WO so the user isn't silently dumped back to the input form.
      if (wos.length === 0) {
        if (data?.error?.message) setFollowUp("AI couldn't generate — showing default. (" + data.error.message + ")");
        setProposed([{ category, description: text, priority: isSold ? "HIGH" : "MEDIUM", estimatedHours: 1, _approved: true, partsETA: addDays(todayStr(), 3), allPartsIn: false }]);
      } else {
        setProposed(wos);
      }
      if (parsed.isSoldVehicleRepair || isSold) setSoldRepairMode(true);
    } catch (e) {
      console.warn("[AIWorkOrderModal] generate failed:", e?.message || e);
      setProposed([{ category, description: text, priority: isSold ? "HIGH" : "MEDIUM", estimatedHours: 1, _approved: true, partsETA: addDays(todayStr(), 3), allPartsIn: false }]);
    }
    setLoading(false);
  };

  // Hand-enter a work order without the AI — builds the WO straight from the form fields.
  const createManual = () => {
    if (!input.trim()) return;
    const partsCost = parseFloat(manualPartsCost) || 0;
    const wo = {
      category,
      description: input.trim(),
      priority: isSold ? "HIGH" : manualPriority,
      estimatedHours: parseFloat(manualHours) || 1,
      estimatedPartsCost: partsCost,
      notes: "",
      _approved: true,
      soldVehicleRepair: isSold || false,
      partsETA: partsCost > 0 ? addDays(todayStr(), 3) : "",
      allPartsIn: false,
    };
    setProposed([wo]);
    if (isSold) setSoldRepairMode(true);
  };

  const toggleApprove = (i) => setProposed(p => p.map((w, idx) => idx === i ? { ...w, _approved: !w._approved } : w));
  const setProposedPartsETA = (i, val) => setProposed(p => p.map((w, idx) => idx === i ? { ...w, partsETA: val } : w));

  const handleAdd = () => {
    const approved = proposed.filter(w => w._approved);
    if (soldRepairMode && onUpdateVehicle) {
      const towCostAmt = parseFloat(soldRepairData.towCost) || 0;
      const updatedVehicle = {
        ...vehicle,
        stage: "SOLD",
        soldRepairPending: true,
        customerReturnDate: soldRepairData.returnDate || null,
        isGoodwillRepair: soldRepairData.isGoodwill,
        goodwillFormRequired: soldRepairData.isGoodwill && !soldRepairData.goodwillFormSigned,
        recoveryOdometer: parseFloat(soldRepairData.recoveryOdometer) || null,
        // Flag WOs for manager approval if >100 miles
        ...(requiresManagerApproval && { postSaleManagerApprovalRequired: true, odometerDiff }),
      };
      onUpdateVehicle(updatedVehicle);
      // Record tow cost as ledger entry
      if (towCostAmt > 0 && onAddLedger) {
        onAddLedger({ id: genId(), vehicleId: vehicle.id, category: "POSTSAL", description: `Tow — Gordo's Towing`, amount: towCostAmt, date: todayStr(), auto: true });
      }
    }
    // Flag WOs for manager approval if required
    onAdd(approved.map(w => ({
      ...w,
      userInput: input.trim() || w.userInput || "",
      soldVehicleRepair: soldRepairMode,
      requiresApproval: soldRepairMode && requiresManagerApproval ? true : w.requiresApproval,
      odometerExceededApproval: soldRepairMode && requiresManagerApproval,
    })));
    if (soldRepairMode && onTriggerCalendarRebuild) onTriggerCalendarRebuild();
  };

  return (
    <Modal title={isSold || soldRepairMode ? "⚠️ Add Work Order — SOLD VEHICLE" : "Add Work Order"} onClose={onClose}>
      {isSold && (
        <div style={{ background: C.red + "15", border: `1px solid ${C.red}40`, borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 11, color: C.red, fontWeight: 700 }}>
          ⚠️ This vehicle has been sold. Any repairs will be HIGH priority and will jump the queue.
        </div>
      )}
      {proposed.length === 0 ? (
        <div>
          {/* Mode toggle — AI-assisted vs. hand-entered */}
          <div style={{ display: "flex", gap: 6, marginBottom: 12, background: C.s2, border: `1px solid ${C.border}`, borderRadius: 8, padding: 4 }}>
            <button onClick={() => setManualMode(false)}
              style={{ flex: 1, padding: "7px", background: !manualMode ? C.amber : "transparent", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 800, color: !manualMode ? "#000" : C.textM, fontFamily: "inherit" }}>
              ✨ AI Assist
            </button>
            <button onClick={() => setManualMode(true)}
              style={{ flex: 1, padding: "7px", background: manualMode ? C.amber : "transparent", border: "none", borderRadius: 6, cursor: "pointer", fontSize: 11, fontWeight: 800, color: manualMode ? "#000" : C.textM, fontFamily: "inherit" }}>
              ✍️ Enter Manually
            </button>
          </div>
          <SI label="Category" value={category} onChange={setCategory} options={WO_CATS.map(c => ({ value: c.id, label: c.label }))} />
          <div style={{ marginBottom: 10 }}>
            <div style={{ fontSize: 10, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 4 }}>{manualMode ? "Work order description" : "Describe the work needed"}</div>
            <textarea value={input} onChange={e => setInput(e.target.value)} placeholder={manualMode ? "e.g. Brake Job — Front Axle" : "e.g. Front brakes and rotors are worn, need replacement"} rows={3}
              style={{ width: "100%", background: C.s2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none", resize: "vertical", boxSizing: "border-box" }} />
          </div>
          {manualMode && (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8, marginBottom: 10 }}>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 4 }}>Priority</div>
                <select value={isSold ? "HIGH" : manualPriority} onChange={e => setManualPriority(e.target.value)} disabled={isSold}
                  style={{ width: "100%", background: C.s2, border: `1px solid ${C.border}`, borderRadius: 6, padding: "7px 8px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none", boxSizing: "border-box", opacity: isSold ? 0.6 : 1 }}>
                  <option value="HIGH">High</option>
                  <option value="MEDIUM">Medium</option>
                  <option value="LOW">Low</option>
                </select>
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 4 }}>Est. Hours</div>
                <input type="number" min="0" step="0.25" value={manualHours} onChange={e => setManualHours(e.target.value)} placeholder="1"
                  style={{ width: "100%", background: C.s2, border: `1px solid ${C.border}`, borderRadius: 6, padding: "7px 8px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
              </div>
              <div>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 4 }}>Parts $</div>
                <input type="number" min="0" step="1" value={manualPartsCost} onChange={e => setManualPartsCost(e.target.value)} placeholder="0"
                  style={{ width: "100%", background: C.s2, border: `1px solid ${C.border}`, borderRadius: 6, padding: "7px 8px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
              </div>
            </div>
          )}
          <div style={{ display: "flex", gap: 8 }}>
            {manualMode ? (
              <Btn variant="amber" onClick={createManual} disabled={!input.trim()}>
                ✍️ Create Work Order
              </Btn>
            ) : (
              <Btn variant="amber" onClick={() => generate(input)} disabled={loading || !input.trim()}>
                {loading ? "Generating…" : "✨ Generate Work Orders"}
              </Btn>
            )}
            <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          </div>
        </div>
      ) : (
        <div>
          {followUp && (
            <div style={{ background: C.amber + "15", border: `1px solid ${C.amber}40`, borderRadius: 8, padding: "10px 14px", marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.amber, marginBottom: 6 }}>💬 Follow-up Question</div>
              <div style={{ fontSize: 12, color: C.text, marginBottom: 8 }}>{followUp}</div>
              <div style={{ display: "flex", gap: 8 }}>
                <input placeholder="Your answer…" id="followup-input"
                  style={{ flex: 1, background: C.s2, border: `1px solid ${C.border}`, borderRadius: 6, padding: "7px 10px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none" }} />
                <Btn variant="amber" size="sm" onClick={() => { const v = document.getElementById("followup-input")?.value; if (v) generate(input + " — " + followUp + ": " + v); }}>Update</Btn>
              </div>
            </div>
          )}

          {/* Sold vehicle repair details */}
          {soldRepairMode && (
            <div style={{ background: C.red + "10", border: `2px solid ${C.red}40`, borderRadius: 8, padding: "12px 14px", marginBottom: 14 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: C.red, textTransform: "uppercase", marginBottom: 10 }}>⚠️ Sold Vehicle Repair — Required Info</div>
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 4 }}>When is the customer expecting the vehicle back?</div>
                <input type="date" value={soldRepairData.returnDate} onChange={e => setSoldRepairData(d => ({ ...d, returnDate: e.target.value }))} min={todayStr()}
                  style={{ width: "100%", background: C.s2, border: `1px solid ${C.border}`, borderRadius: 6, padding: "7px 10px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none" }} />
              </div>

              {/* Odometer readings */}
              <div style={{ background: C.s2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", marginBottom: 10 }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: "#000", textTransform: "uppercase", marginBottom: 8 }}>🔢 Odometer Readings — Required</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 3 }}>At Time of Sale</div>
                    {saleOdo ? (
                      <div style={{ fontSize: 13, fontWeight: 800, color: C.text, padding: "7px 0" }}>{parseInt(saleOdo).toLocaleString()} mi</div>
                    ) : (
                      <div style={{ fontSize: 10, color: C.amber, fontStyle: "italic" }}>⚠ Not recorded at sale</div>
                    )}
                  </div>
                  <div>
                    <div style={{ fontSize: 9, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 3 }}>When Recovered *</div>
                    <input type="number" value={soldRepairData.recoveryOdometer} onChange={e => setSoldRepairData(d => ({ ...d, recoveryOdometer: e.target.value }))}
                      placeholder="Current mileage"
                      style={{ width: "100%", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, padding: "7px 8px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
                  </div>
                </div>
                {odometerDiff !== null && (
                  <div style={{ marginTop: 8, padding: "6px 10px", background: requiresManagerApproval ? C.red + "15" : C.green + "12", border: `1px solid ${requiresManagerApproval ? C.red : C.green}40`, borderRadius: 6 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: requiresManagerApproval ? C.red : C.green }}>
                      {odometerDiff > 0 ? `+${odometerDiff.toLocaleString()}` : odometerDiff.toLocaleString()} miles since sale
                      {requiresManagerApproval && " — ⚠️ EXCEEDS 100 MILES"}
                    </div>
                    {requiresManagerApproval && (
                      <div style={{ fontSize: 10, color: C.red, marginTop: 2 }}>Manager approval required before work can begin. WOs will be flagged for approval.</div>
                    )}
                  </div>
                )}
              </div>
              <div style={{ marginBottom: soldRepairData.isGoodwill ? 10 : 0 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 6 }}>Billable or Goodwill?</div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => setSoldRepairData(d => ({ ...d, isGoodwill: false }))}
                    style={{ flex: 1, background: soldRepairData.isGoodwill === false ? C.blue : C.s2, border: `1px solid ${soldRepairData.isGoodwill === false ? C.blue : C.border}`, borderRadius: 7, padding: "8px", cursor: "pointer", fontSize: 11, fontWeight: 700, color: soldRepairData.isGoodwill === false ? "#fff" : C.text, fontFamily: "inherit" }}>
                    💰 Billable
                  </button>
                  <button onClick={() => setSoldRepairData(d => ({ ...d, isGoodwill: true }))}
                    style={{ flex: 1, background: soldRepairData.isGoodwill === true ? C.green : C.s2, border: `1px solid ${soldRepairData.isGoodwill === true ? C.green : C.border}`, borderRadius: 7, padding: "8px", cursor: "pointer", fontSize: 11, fontWeight: 700, color: soldRepairData.isGoodwill === true ? "#fff" : C.text, fontFamily: "inherit" }}>
                    🤝 Goodwill
                  </button>
                </div>
              </div>
              {soldRepairData.isGoodwill === true && (
                <div style={{ background: C.amber + "15", border: `1px solid ${C.amber}50`, borderRadius: 7, padding: "10px 12px", marginTop: 8 }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: C.amber, marginBottom: 6 }}>📋 GOODWILL SERVICE — Form Required</div>
                  <div style={{ fontSize: 11, color: C.text, marginBottom: 8 }}>The Goodwill Service form MUST be signed by the customer before the vehicle is delivered.</div>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                    <input type="checkbox" checked={soldRepairData.goodwillFormSigned} onChange={e => setSoldRepairData(d => ({ ...d, goodwillFormSigned: e.target.checked }))} style={{ accentColor: C.green, width: 16, height: 16 }} />
                    <span style={{ fontSize: 12, fontWeight: 700, color: soldRepairData.goodwillFormSigned ? C.green : C.text }}>
                      {soldRepairData.goodwillFormSigned ? "✓ Form will be signed before delivery" : "I understand — form must be signed before delivery"}
                    </span>
                  </label>
                </div>
              )}

              {/* Tow question */}
              <div style={{ marginTop: 12, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 6 }}>Does this vehicle need to be towed?</div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => setSoldRepairData(d => ({ ...d, needsTow: true }))}
                    style={{ flex: 1, background: soldRepairData.needsTow === true ? C.red + "20" : C.s2, border: `1px solid ${soldRepairData.needsTow === true ? C.red : C.border}`, borderRadius: 7, padding: "7px", cursor: "pointer", fontSize: 11, fontWeight: 700, color: soldRepairData.needsTow === true ? C.red : C.text, fontFamily: "inherit" }}>
                    🚛 Yes, needs tow
                  </button>
                  <button onClick={() => setSoldRepairData(d => ({ ...d, needsTow: false }))}
                    style={{ flex: 1, background: soldRepairData.needsTow === false ? C.green + "15" : C.s2, border: `1px solid ${soldRepairData.needsTow === false ? C.green : C.border}`, borderRadius: 7, padding: "7px", cursor: "pointer", fontSize: 11, fontWeight: 700, color: soldRepairData.needsTow === false ? C.green : C.text, fontFamily: "inherit" }}>
                    🚗 No, driveable
                  </button>
                </div>
                {soldRepairData.needsTow === true && (
                  <div style={{ marginTop: 10 }}>
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 4 }}>Vehicle is currently at</div>
                      <input value={soldRepairData.towPickup} onChange={e => setSoldRepairData(d => ({ ...d, towPickup: e.target.value }))}
                        placeholder="Enter current address"
                        style={{ width: "100%", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 7, padding: "7px 10px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
                    </div>
                    <div style={{ marginBottom: 8 }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 4 }}>Bring it to</div>
                      <select value={soldRepairData.towDropoff} onChange={e => setSoldRepairData(d => ({ ...d, towDropoff: e.target.value }))}
                        style={{ width: "100%", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 7, padding: "7px 10px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none" }}>
                        {TOW_LOCATIONS.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
                      </select>
                    </div>
                    {soldRepairData.towPickup.trim().length > 5 && (() => {
                      const userName = currentUser?.name || currentUser?.username || "Edgewater Staff";
                      const vehicleDesc = `${vehicle.year} ${vehicle.make} ${vehicle.model} (Stock #${vehicle.stockNumber})`;
                      const dropLabel = TOW_LOCATIONS.find(l => l.id === soldRepairData.towDropoff)?.label?.replace(/.*— /, "") || "";
                      const towMsg = `Hi, this is ${userName} from Edgewater Auto Center. Can you please pick up a ${vehicleDesc} at ${soldRepairData.towPickup} and take it to ${dropLabel}?`;
                      return (
                        <div>
                          <div style={{ background: C.s2, border: `1px solid ${C.border}`, borderRadius: 7, padding: "8px 10px", marginBottom: 8, fontSize: 10, color: C.text, fontStyle: "italic", lineHeight: 1.5 }}>
                            📱 Message to Gordo's Towing: <strong style={{ fontStyle: "normal" }}>{towMsg}</strong>
                          </div>
                          {!towSentFromWO ? (
                            <button onClick={() => { window.open(`sms:${GORDOS_TOW.phone}?body=${encodeURIComponent(towMsg)}`, "_self"); setTowSentFromWO(true); }}
                              style={{ background: C.red, border: "none", borderRadius: 7, padding: "7px 14px", cursor: "pointer", fontSize: 11, fontWeight: 700, color: "#fff", fontFamily: "inherit" }}>
                              📱 Send Tow Request to {GORDOS_TOW.name}
                            </button>
                          ) : (
                            <div>
                              <div style={{ fontSize: 11, color: C.green, fontWeight: 700, marginBottom: 8 }}>✓ Tow request sent!</div>
                              <div>
                                <div style={{ fontSize: 9, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 3 }}>Tow Cost ($)</div>
                                <input type="number" min="0" step="1" value={soldRepairData.towCost} onChange={e => setSoldRepairData(d => ({ ...d, towCost: e.target.value }))}
                                  placeholder="e.g. 150"
                                  style={{ width: "100%", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, padding: "7px 10px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none", boxSizing: "border-box", marginTop: 4 }} />
                                {parseFloat(soldRepairData.towCost) > 0 && <div style={{ fontSize: 10, color: C.amber, marginTop: 3 }}>Will be included in post-sale repair cost</div>}
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          )}

          <div style={{ fontSize: 11, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 8 }}>Proposed Work Orders</div>
          {!isManager && (
            <div style={{ background: C.amber + "15", border: `1px solid ${C.amber}40`, borderRadius: 7, padding: "8px 12px", marginBottom: 10, fontSize: 11, color: C.amber }}>
              ⚠️ Work orders from non-managers require manager approval before being actioned.
            </div>
          )}
          {proposed.map((wo, i) => {
            const cat = WO_CATS.find(c => c.id === wo.category);
            return (
              <div key={i} style={{ background: C.s2, border: `2px solid ${wo._approved ? C.green + "60" : C.border}`, borderRadius: 8, padding: "12px 14px", marginBottom: 8 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 4 }}>
                      <span style={{ background: cat?.color + "25", color: cat?.color || C.textM, fontSize: 10, fontWeight: 800, padding: "2px 7px", borderRadius: 10, textTransform: "uppercase" }}>{cat?.label || wo.category}</span>
                      <span style={{ background: wo.priority === "HIGH" ? C.red + "20" : wo.priority === "MEDIUM" ? C.amber + "20" : C.green + "20", color: wo.priority === "HIGH" ? C.red : wo.priority === "MEDIUM" ? C.amber : C.green, fontSize: 10, fontWeight: 700, padding: "2px 7px", borderRadius: 10 }}>{wo.priority}</span>
                      {wo.soldVehicleRepair && <span style={{ background: C.red, color: "#fff", fontSize: 9, fontWeight: 800, padding: "2px 7px", borderRadius: 10 }}>SOLD VEH.</span>}
                    </div>
                    <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{wo.description}</div>
                    {wo.notes && <div style={{ fontSize: 11, color: C.textM, marginTop: 3 }}>{wo.notes}</div>}
                    <div style={{ fontSize: 10, color: C.textS, marginTop: 4 }}>
                      Est. {wo.estimatedHours}h{wo.estimatedPartsCost > 0 ? ` · ${fmtMoney(wo.estimatedPartsCost)} parts` : ""}
                      {wo.soldVehicleRepair && (wo.estimatedHours > 0 || wo.estimatedPartsCost > 0) && (
                        <span style={{ fontWeight: 700, color: C.red, marginLeft: 6 }}>= {fmtMoney(((wo.estimatedHours || 0) * LABOR_RATE) + (wo.estimatedPartsCost || 0))} total</span>
                      )}
                    </div>
                    {/* Parts arrival prompt — when does the user expect parts in? */}
                    {(() => {
                      const needsParts = (wo.estimatedPartsCost || 0) > 0 || (wo.jobItems || []).some(j => j.type === "parts" || (j.partName && j.partName.trim()));
                      if (!needsParts) return null;
                      return (
                        <div style={{ marginTop: 8, background: C.amber + "10", border: `1px solid ${C.amber}40`, borderRadius: 6, padding: "8px 10px" }}>
                          <div style={{ fontSize: 9, fontWeight: 800, color: C.amber, textTransform: "uppercase", marginBottom: 4 }}>📦 When are parts expected to arrive?</div>
                          <input type="date" value={wo.partsETA || ""} min={todayStr()}
                            onChange={e => setProposedPartsETA(i, e.target.value)}
                            style={{ background: C.s2, border: `1px solid ${C.border}`, borderRadius: 5, padding: "5px 8px", color: C.text, fontSize: 11, fontFamily: "inherit", outline: "none" }} />
                          <div style={{ fontSize: 9, color: C.textM, marginTop: 3 }}>Tech can mark "All Parts In" on the work order when they arrive.</div>
                        </div>
                      );
                    })()}
                    {/* Job items — itemized steps within this WO */}
                    {wo.jobItems?.length > 0 && (
                      <div style={{ marginTop: 8, borderTop: `1px solid ${C.border}`, paddingTop: 6 }}>
                        <div style={{ fontSize: 9, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 4 }}>Job Items</div>
                        {wo.jobItems.map((item, ji) => (
                          <div key={ji} style={{ display: "flex", alignItems: "flex-start", gap: 6, padding: "3px 0", borderBottom: ji < wo.jobItems.length - 1 ? `1px solid ${C.border}` : "none" }}>
                            <span style={{ fontSize: 9, color: C.textM, fontWeight: 700, minWidth: 16, paddingTop: 1 }}>{item.step}.</span>
                            <div style={{ flex: 1 }}>
                              <div style={{ fontSize: 11, color: C.text }}>{item.description}</div>
                              <div style={{ fontSize: 9, color: C.textM }}>
                                {item.type === "parts" ? "🔩" : "⏱"} {item.estimatedHours ? `${item.estimatedHours}h` : ""}
                                {item.partName ? ` · ${item.partName}` : ""}
                                {item.estimatedPartCost > 0 ? ` · ${fmtMoney(item.estimatedPartCost)}` : ""}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <button onClick={() => toggleApprove(i)} style={{ background: wo._approved ? C.green + "20" : C.s2, border: `1px solid ${wo._approved ? C.green : C.border}`, borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 11, fontWeight: 700, color: wo._approved ? C.green : C.textM, fontFamily: "inherit", marginLeft: 10, flexShrink: 0 }}>
                    {wo._approved ? "✓ Approved" : "Approve"}
                  </button>
                </div>
                {!isManager && <div style={{ fontSize: 10, color: C.textM, borderTop: `1px solid ${C.border}`, paddingTop: 6, marginTop: 4 }}>⚠ Not diagnosed through system — <span style={{ color: C.amber, cursor: "pointer", fontWeight: 700 }} onClick={() => { onClose(); }}>Re-diagnose with AI</span></div>}
              </div>
            );
          })}
          <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
            <Btn variant={soldRepairMode ? "red" : "amber"} onClick={handleAdd}
              disabled={!proposed.some(w => w._approved) || (soldRepairMode && soldRepairData.isGoodwill === null)}>
              {soldRepairMode ? "⚠️ " : ""}Add {proposed.filter(w => w._approved).length} Work Order{proposed.filter(w => w._approved).length !== 1 ? "s" : ""}
              {soldRepairMode && soldRepairData.returnDate ? ` · Due ${fmtDate(soldRepairData.returnDate)}` : ""}
            </Btn>
            <Btn variant="ghost" onClick={() => setProposed([])}>← Back</Btn>
            <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          </div>
        </div>
      )}
    </Modal>
  );
};

// ─── WORK ORDER DETAIL MODAL ─────────────────────────────────────────────────
const WorkOrderDetailModal = ({ wo, vehicle, parts, vendors, staff, currentUser, catalog, onClose, onUpdateWO, onUpdateVehicle, onAddPart, onAddLedger, onShopForPart, onPrintWO }) => {
  const [agentOpen, setAgentOpen] = useState(false);
  const [instructions, setInstructions] = useState(wo.instructions || null);
  const [loadingInstructions, setLoadingInstructions] = useState(false);
  const [deleteNote, setDeleteNote] = useState("");
  const [schedDateInput, setSchedDateInput] = useState(wo.scheduledDate || "");
  const [schedLoading, setSchedLoading] = useState(false);
  const schedDateState = [schedDateInput, setSchedDateInput];
  const schedLoadingState = [schedLoading, setSchedLoading];
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [completeFlow, setCompleteFlow] = useState(false); // open completion wizard
  const [completeStep, setCompleteStep] = useState("who"); // who | hours | parts | external
  const [completedBy, setCompletedBy] = useState(currentUser?.username || "");
  const [actualHours, setActualHours] = useState(String(wo.estimatedHours || ""));
  const [useEstHours, setUseEstHours] = useState(true);
  const [externalVendor, setExternalVendor] = useState("");
  const [externalCost, setExternalCost] = useState("");
  const [partsToReturn, setPartsToReturn] = useState([]);
  const cat = WO_CATS.find(c => c.id === wo.category);
  const isManager = currentUser?.role === "manager";
  const assignedStaff = wo.assignedTo ? staff?.find(s => s.username === wo.assignedTo || s.id === wo.assignedTo) : null;
  const createdByName = wo.createdBy ? (staff?.find(s => s.username === wo.createdBy)?.name || wo.createdBy) : "System";
  const woParts = parts.filter(p => p.vehicleId === vehicle.id && (p.workOrderId === wo.id || p.description?.toLowerCase().slice(0,18) === wo.description?.toLowerCase().slice(0,18)));
  const today = todayStr();

  // Lift type detection
  const needsLift = /brake|suspension|cv axle|strut|spring|wheel bearing|exhaust|catalytic|undercarriage|differential|alignment|tie rod|ball joint|control arm/i.test(wo.description || "");
  const preferScissor = /tire|wheel|oil change|lube|filter|muffler/i.test(wo.description || "");
  const liftType = preferScissor ? "Scissor lift" : needsLift ? "2-post lift" : "Ground — no lift needed";


  // Determine which agent to show based on WO category
  const isBodyWork = ["BODYWORK","COSMETIC","VENDOR"].includes(wo.category) || /wheel|rim|glass|windshield|dent|paint|scratch|rust|bumper|panel|door|fender/i.test(wo.description);
  const isDetail = wo.category === "DETAIL";
  const agentName = isBodyWork ? "Jimmy" : isDetail ? "Julia" : "Hank";
  const agentImg = isBodyWork ? "/images/jimmy-working.jpg" : isDetail ? "/images/Julia.jpg" : "/images/hank-headshot.jpg";
  const agentColor = isBodyWork ? "#f97316" : isDetail ? C.purple : C.amber;
  const agentContext = `You are helping with this specific work order on a ${vehicle.year} ${vehicle.make} ${vehicle.model}${vehicle.trim ? " " + vehicle.trim : ""}${vehicle.engine ? " with " + vehicle.engine : ""}${vehicle.mileage ? ", " + parseInt(vehicle.mileage).toLocaleString() + " miles" : ""}, Stock #${vehicle.stockNumber}. Work order: "${wo.description}". ${wo.diagnosisRationale ? "Background: " + wo.diagnosisRationale : ""}`;

  // Clock in/out
  const activeEntry = wo._workLog?.find(e => e.staffId === currentUser?.id && !e.endTime);
  const clockIn = () => {
    const entry = { id: genId(), staffId: currentUser?.id, staffName: currentUser?.name || currentUser?.username, startTime: new Date().toISOString(), endTime: null, woId: wo.id, date: today };
    onUpdateWO({ ...wo, _workLog: [...(wo._workLog || []), entry] });
  };
  const clockOut = () => {
    const log = (wo._workLog || []).map(e => e.id === activeEntry.id ? { ...e, endTime: new Date().toISOString(), hours: parseFloat(((new Date() - new Date(e.startTime)) / 3600000).toFixed(2)) } : e);
    onUpdateWO({ ...wo, _workLog: log });
  };
  const totalHours = (wo._workLog || []).filter(e => e.hours).reduce((s, e) => s + e.hours, 0);

  // Cost summary
  const estLaborCost = (wo.estimatedHours || 0) * LABOR_RATE;
  const estPartsCost = wo.estimatedPartsCost || instructions?.requiredParts?.reduce((s, p) => s + (p.estimatedCost || 0), 0) || 0;
  const totalEstCost = estLaborCost + estPartsCost;

  // Generate instructions + parts list
  const generateInstructions = async (sig = null) => {
    setLoadingInstructions(true);
    try {
      // Include context from other open WOs on this vehicle for better accuracy
      const otherWOs = (vehicle.workOrders || []).filter(w => w.id !== wo.id && w.status !== "COMPLETE" && !w.deleted);
      const otherWOContext = otherWOs.length > 0 
        ? `\n\nOther open work orders on this vehicle (may affect parts/sequence):\n${otherWOs.map(w => `- ${w.description}`).join("\n")}`
        : "";

      const res = await fetch("/api/claude", { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${await getIdToken()}` },
        body: JSON.stringify({ model: "claude-sonnet-4-6", max_tokens: 1024, messages: [{ role: "user", content: `Shop foreman at a used car dealership. Give professional repair instructions.\n\nVehicle: ${vehicle.year} ${vehicle.make} ${vehicle.model}${vehicle.trim ? " " + vehicle.trim : ""}${vehicle.engine ? " " + vehicle.engine : ""}${vehicle.mileage ? " " + parseInt(vehicle.mileage).toLocaleString() + "mi" : ""}\nJob: ${wo.description}${otherWOContext ? "\nOther open WOs: " + otherWOContext.slice(0,200) : ""}\n\nRespond ONLY with compact valid JSON (no extra spaces/newlines in strings):\n{"estimatedHours":1.5,"steps":["step 1","step 2"],"requiredParts":[{"name":"part","partNumber":"OEM#","qty":1,"estimatedCost":12,"notes":"note"}],"requiredTools":["tool"],"warnings":["note"]}` }] })
      });
      const data = await res.json();
      const raw = data.content?.[0]?.text || "{}";
      const clean = raw.replace(/```json|```/g, "").trim();
      // Handle truncated JSON by finding the last complete object
      let parsed;
      try {
        parsed = JSON.parse(clean);
      } catch {
        // Try to extract partial valid JSON
        const stepMatch = clean.match(/"steps"\s*:\s*\[([\s\S]*?)\]/);
        const partsMatch = clean.match(/"requiredParts"\s*:\s*\[([\s\S]*?)\]/);
        const toolsMatch = clean.match(/"requiredTools"\s*:\s*\[([\s\S]*?)\]/);
        const hoursMatch = clean.match(/"estimatedHours"\s*:\s*([\d.]+)/);
        parsed = {
          estimatedHours: hoursMatch ? parseFloat(hoursMatch[1]) : null,
          steps: stepMatch ? JSON.parse('[' + stepMatch[1] + ']').filter(s => typeof s === 'string') : [],
          requiredParts: partsMatch ? (() => { try { return JSON.parse('[' + partsMatch[1] + ']'); } catch { return []; } })() : [],
          requiredTools: toolsMatch ? (() => { try { return JSON.parse('[' + toolsMatch[1] + ']'); } catch { return []; } })() : [],
        };
      }
      setInstructions(parsed);
      // Save with signature so we can detect when regen is needed
      const relatedSig = sig || (vehicle.workOrders || []).filter(w => w.id !== wo.id && w.status !== "COMPLETE" && !w.deleted).map(w => w.id + w.status + w.description).join("|");
      onUpdateWO({ ...wo, instructions: parsed, estimatedHours: parsed.estimatedHours || wo.estimatedHours, _instructionsSig: relatedSig });
    } catch (e) { console.warn("Instructions error:", e); }
    setLoadingInstructions(false);
  };

  // Auto-generate on open — smart: only if no instructions, or if related WOs changed since last gen
  useEffect(() => {
    if (!process.env.REACT_APP_ANTHROPIC_KEY) return;
    if (!instructions) {
      // First time — generate immediately
      generateInstructions();
      return;
    }
    // Check if related WOs on the vehicle have changed since last generation
    const relatedWOs = (vehicle.workOrders || []).filter(w => w.id !== wo.id && w.status !== "COMPLETE" && !w.deleted);
    const relatedSig = relatedWOs.map(w => w.id + w.status + w.description).join("|");
    const lastSig = wo._instructionsSig || "";
    if (lastSig !== relatedSig) {
      // Related WOs changed — regen in background silently
      generateInstructions(relatedSig);
    }
  }, []);

  // Soft delete with note
  const handleDelete = () => {
    if (!deleteNote.trim()) return;
    const deletedWO = { ...wo, deleted: true, deletedBy: currentUser?.name || currentUser?.username, deletedAt: todayStr(), deleteNote };
    onUpdateVehicle({ ...vehicle, workOrders: (vehicle.workOrders || []).map(w => w.id === wo.id ? deletedWO : w) });
    onClose();
  };

  return (
    <Modal title="" onClose={onClose} wide>
      {/* Header with vehicle + WO info */}
      <div style={{ background: cat?.color + "15", border: `1px solid ${cat?.color}40`, borderRadius: 8, padding: "12px 14px", marginBottom: 14 }}>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginBottom: 6 }}>
          <Badge color={cat?.color || C.textM} small>{cat?.label}</Badge>
          <Badge color={wo.priority === "HIGH" ? C.red : wo.priority === "MEDIUM" ? "#f97316" : C.textM} small>{wo.priority}</Badge>
          <Badge color={wo.status === "COMPLETE" ? C.green : wo.status === "IN_PROGRESS" ? C.blue : C.textM} small>{wo.status}</Badge>
          {wo.diagnosisRationale && <Badge color={C.purple} small>AI diagnosed</Badge>}
        </div>
        <div style={{ fontSize: 16, fontWeight: 800, color: C.text, marginBottom: 6 }}>{wo.description}</div>
        <div style={{ fontSize: 10, color: C.textM }}>
          {vehicle.year} {vehicle.make} {vehicle.model} · #{vehicle.stockNumber} · Created by {createdByName} on {fmtDate(wo.createdAt)}
          {assignedStaff && ` · Assigned to ${assignedStaff.name}`}
        </div>
        {/* Job items — itemized steps */}
        {wo.jobItems?.length > 0 && (
          <div style={{ marginTop: 10, background: C.s2, borderRadius: 8, padding: "10px 12px" }}>
            <div style={{ fontSize: 9, fontWeight: 800, color: C.textM, textTransform: "uppercase", marginBottom: 6 }}>Job Items</div>
            {wo.jobItems.map((item, i) => (
              <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "5px 0", borderBottom: i < wo.jobItems.length - 1 ? `1px solid ${C.border}` : "none" }}>
                <span style={{ background: item.type === "parts" ? C.amber + "20" : C.blue + "15", color: item.type === "parts" ? C.amber : C.blue, borderRadius: 4, padding: "1px 5px", fontSize: 9, fontWeight: 700, minWidth: 20, textAlign: "center", flexShrink: 0 }}>{item.step}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, color: C.text, fontWeight: 500 }}>{item.description}</div>
                  <div style={{ fontSize: 9, color: C.textM, marginTop: 1 }}>
                    {item.type === "parts" ? "🔩 Parts" : "⏱ Labor"}
                    {item.estimatedHours ? ` · ${item.estimatedHours}h` : ""}
                    {item.partName ? ` · ${item.partName}` : ""}
                    {item.estimatedPartCost > 0 ? ` · ${fmtMoney(item.estimatedPartCost)}` : ""}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        {/* Cost summary */}
        <div style={{ display: "flex", gap: 12, marginTop: 10, flexWrap: "wrap" }}>
          {wo.estimatedHours > 0 && <div style={{ background: C.blue + "15", borderRadius: 6, padding: "5px 10px", fontSize: 11 }}><span style={{ fontWeight: 700, color: C.blue }}>⏱ {wo.estimatedHours}h labor = {fmtMoney(estLaborCost)}</span></div>}
          {estPartsCost > 0 && <div style={{ background: C.amber + "15", borderRadius: 6, padding: "5px 10px", fontSize: 11 }}><span style={{ fontWeight: 700, color: C.amber }}>🔩 {fmtMoney(estPartsCost)} parts</span></div>}
          {totalEstCost > 0 && <div style={{ background: C.text + "10", borderRadius: 6, padding: "5px 10px", fontSize: 11 }}><span style={{ fontWeight: 800, color: C.text }}>Total est: {fmtMoney(totalEstCost)}</span></div>}
        </div>
      </div>

      {/* ── SCHEDULE DATE ── */}
      {(() => {
        const [schedDateInput, setSchedDateInput] = schedDateState;
        const [schedLoading, setSchedLoading] = schedLoadingState;

        // Calculate hours already scheduled on a given date for this assignee
        const getScheduledHours = (dateStr) => {
          if (!dateStr || !vehicle) return 0;
          const allVehicleWOs = (vehicle ? [vehicle] : []).concat();
          // We don't have all vehicles here — just note hours from this vehicle's other WOs
          return (vehicle.workOrders || []).filter(w => w.id !== wo.id && w.scheduledDate === dateStr && w.status !== "COMPLETE").reduce((s, w) => s + (w.estimatedHours || 0), 0);
        };

        const scheduledHours = getScheduledHours(schedDateInput);
        const thisWOHours = wo.estimatedHours || 0;
        const totalIfScheduled = scheduledHours + thisWOHours;
        const overCapacity = totalIfScheduled > SCHEDULED_HOURS_MAX;

        const saveScheduledDate = (dateStr, override = false) => {
          if (!dateStr) {
            onUpdateWO({ ...wo, scheduledDate: null });
            return;
          }
          if (overCapacity && !override) return; // handled by UI
          onUpdateWO({ ...wo, scheduledDate: dateStr, scheduledOverride: override });
        };

        const findNextAvailableDay = () => {
          let d = new Date(schedDateInput || todayStr());
          for (let i = 0; i < 30; i++) {
            d.setDate(d.getDate() + 1);
            const ds = d.toISOString().slice(0,10);
            const hoursOnDay = getScheduledHours(ds);
            if (hoursOnDay + thisWOHours <= SCHEDULED_HOURS_MAX) {
              setSchedDateInput(ds);
              return ds;
            }
          }
          return null;
        };

        return (
          <div style={{ background: C.s2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px", marginBottom: 12 }}>
            <div style={{ fontSize: 10, fontWeight: 800, color: "#000", textTransform: "uppercase", marginBottom: 8 }}>📅 Schedule This Work Order</div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginBottom: schedDateInput ? 8 : 0 }}>
              <input type="date" value={schedDateInput || wo.scheduledDate || ""}
                min={todayStr()}
                onChange={e => setSchedDateInput(e.target.value)}
                style={{ flex: 1, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 7, padding: "7px 10px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none" }} />
              {schedDateInput && schedDateInput !== wo.scheduledDate && (
                <button onClick={() => {
                  if (overCapacity) {
                    // Show inline warning — handled below
                  } else {
                    saveScheduledDate(schedDateInput);
                  }
                }}
                  style={{ background: C.blue, border: "none", borderRadius: 7, padding: "7px 14px", cursor: "pointer", fontSize: 11, fontWeight: 700, color: "#fff", fontFamily: "inherit" }}>
                  Save
                </button>
              )}
              {wo.scheduledDate && (
                <button onClick={() => { setSchedDateInput(""); onUpdateWO({ ...wo, scheduledDate: null, scheduledOverride: false }); }}
                  style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 7, padding: "7px 10px", cursor: "pointer", fontSize: 11, color: C.textM, fontFamily: "inherit" }}>
                  Clear
                </button>
              )}
            </div>
            {wo.scheduledDate && !schedDateInput && (
              <div style={{ fontSize: 11, color: C.blue, fontWeight: 700 }}>📅 Scheduled: {fmtDate(wo.scheduledDate)}{wo.scheduledOverride ? " (capacity override)" : ""}</div>
            )}
            {thisWOHours > 0 && schedDateInput && (
              <div style={{ fontSize: 10, color: C.textM }}>
                This WO: {thisWOHours}h · Already scheduled that day: {scheduledHours}h · Total: {totalIfScheduled}h / {SCHEDULED_HOURS_MAX}h max
              </div>
            )}
            {overCapacity && schedDateInput && (
              <div style={{ background: C.red + "12", border: `1px solid ${C.red}40`, borderRadius: 8, padding: "10px 12px", marginTop: 8 }}>
                <div style={{ fontSize: 11, fontWeight: 800, color: C.red, marginBottom: 6 }}>⚠️ Over capacity! {totalIfScheduled}h scheduled on {fmtDate(schedDateInput)} ({SCHEDULED_HOURS_MAX}h max)</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  <button onClick={() => saveScheduledDate(schedDateInput, true)}
                    style={{ background: C.red + "20", border: `1px solid ${C.red}50`, borderRadius: 7, padding: "6px 12px", cursor: "pointer", fontSize: 11, fontWeight: 700, color: C.red, fontFamily: "inherit" }}>
                    Override — Schedule Anyway
                  </button>
                  <button onClick={() => {
                    const next = findNextAvailableDay();
                    if (next) saveScheduledDate(next);
                    else alert("No available days found in the next 30 days.");
                  }}
                    style={{ background: C.green + "15", border: `1px solid ${C.green}40`, borderRadius: 7, padding: "6px 12px", cursor: "pointer", fontSize: 11, fontWeight: 700, color: C.green, fontFamily: "inherit" }}>
                    📅 Move to Next Available Day
                  </button>
                  <button onClick={() => setSchedDateInput(wo.scheduledDate || "")}
                    style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 7, padding: "6px 10px", cursor: "pointer", fontSize: 11, color: C.textM, fontFamily: "inherit" }}>
                    Cancel
                  </button>
                </div>
              </div>
            )}
          </div>
        );
      })()}

      {/* Clock In/Out */}
      <div style={{ background: C.s2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px", marginBottom: 12 }}>
        <div style={{ fontSize: 10, fontWeight: 800, color: "#000", textTransform: "uppercase", marginBottom: 8 }}>⏱ Time Tracking</div>
        <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          {activeEntry ? (
            <button onClick={clockOut} style={{ background: C.red, border: "none", borderRadius: 7, padding: "7px 16px", cursor: "pointer", fontSize: 12, fontWeight: 700, color: "#fff", fontFamily: "inherit" }}>⏹ Clock Out</button>
          ) : (
            <button onClick={clockIn} style={{ background: C.green, border: "none", borderRadius: 7, padding: "7px 16px", cursor: "pointer", fontSize: 12, fontWeight: 700, color: "#fff", fontFamily: "inherit" }}>▶ Clock In</button>
          )}
          {activeEntry && <div style={{ fontSize: 11, color: C.green, fontWeight: 700 }}>● In progress since {new Date(activeEntry.startTime).toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })}</div>}
          {totalHours > 0 && <div style={{ fontSize: 11, color: C.textM }}>Logged: {totalHours.toFixed(1)}h {wo.estimatedHours > 0 ? `/ ${wo.estimatedHours}h est.` : ""}</div>}
        </div>
        {(wo._workLog || []).filter(e => e.hours).map((e, i) => (
          <div key={i} style={{ fontSize: 10, color: C.textM, marginTop: 4 }}>{e.staffName} · {fmtDate(e.date)} · {e.hours}h</div>
        ))}
      </div>

      {/* Parts list */}
      <div style={{ background: C.s2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px", marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: "#000", textTransform: "uppercase" }}>🔩 Required Parts</div>
          {onShopForPart && <button onClick={() => { onClose(); onShopForPart(`Parts for: ${wo.description} on ${vehicle.year} ${vehicle.make} ${vehicle.model}${vehicle.engine ? " " + vehicle.engine : ""}`, vehicle); }} style={{ background: C.amber, border: "none", borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 10, fontWeight: 700, color: "#000", fontFamily: "inherit" }}>🛒 Shop with Roxy</button>}
        </div>
        {loadingInstructions && <div style={{ fontSize: 11, color: C.textM, fontStyle: "italic" }}>Generating parts list…</div>}
        {instructions?.requiredParts?.length > 0 ? (
          instructions.requiredParts.map((p, i) => {
            const ordered = woParts.find(op => op.description?.toLowerCase().includes(p.name?.toLowerCase().slice(0,12)));
            return (
              <div key={i} onClick={() => !ordered && onShopForPart && (onClose(), onShopForPart(`${p.name}${p.partNumber ? " OEM# " + p.partNumber : ""} for ${vehicle.year} ${vehicle.make} ${vehicle.model}${vehicle.engine ? " " + vehicle.engine : ""}`, vehicle))}
                style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "8px 0", borderBottom: `1px solid ${C.border}`, cursor: ordered ? "default" : "pointer", background: ordered ? C.green + "08" : "transparent", borderRadius: 4 }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: ordered ? C.green : C.text }}>{ordered && "✓ "}{p.qty > 1 ? `${p.qty}x ` : ""}{p.name}</div>
                  {p.partNumber && <div style={{ fontSize: 10, color: C.textM }}>OEM: {p.partNumber}</div>}
                  {p.notes && <div style={{ fontSize: 10, color: C.textM }}>{p.notes}</div>}
                  {p.estimatedCost > 0 && !ordered && <div style={{ fontSize: 10, color: C.textM }}>Est. {fmtMoney(p.estimatedCost)}</div>}
                  {ordered && <div style={{ fontSize: 10, color: C.green, fontWeight: 700 }}>✓ {ordered.status} · ETA {fmtDate(ordered.eta)}</div>}
                </div>
                {ordered ? <Badge color={C.green} small>✓ {ordered.status}</Badge> : <span style={{ fontSize: 10, color: C.amber, fontWeight: 700, flexShrink: 0, marginLeft: 8 }}>🛒 Tap</span>}
              </div>
            );
          })
        ) : woParts.length > 0 ? (
          woParts.map(p => {
            const v = vendors.find(vv => vv.id === p.supplier);
            return (
              <div key={p.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "6px 0", borderBottom: `1px solid ${C.border}`, background: C.green + "08" }}>
                <div><div style={{ fontSize: 12, fontWeight: 600, color: C.green }}>✓ {p.description}</div><div style={{ fontSize: 10, color: C.textM }}>{v?.name || "—"} · {fmtMoney(p.cost)} · ETA {fmtDate(p.eta)}</div></div>
                <Badge color={C.green} small>{p.status}</Badge>
              </div>
            );
          })
        ) : !loadingInstructions && (
          <div style={{ fontSize: 11, color: C.textM, fontStyle: "italic" }}>
            {wo.estimatedPartsCost > 0 ? `Est. ${fmtMoney(wo.estimatedPartsCost)} — tap Shop with Roxy` : "No parts needed or generating list…"}
          </div>
        )}
      </div>

      {/* Job Instructions */}
      <div style={{ background: C.s2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px", marginBottom: 12 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: "#000", textTransform: "uppercase" }}>📋 Job Instructions</div>
          <button onClick={generateInstructions} disabled={loadingInstructions} style={{ background: C.purple + "15", border: `1px solid ${C.purple}40`, borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 10, fontWeight: 700, color: C.purple, fontFamily: "inherit" }}>
            {loadingInstructions ? "Generating…" : instructions ? "🔄 Refresh" : "✨ Generate"}
          </button>
        </div>
        {instructions ? (
          <div>
            {instructions.warnings?.length > 0 && (
              <div style={{ background: C.red + "12", border: `1px solid ${C.red}30`, borderRadius: 6, padding: "6px 10px", marginBottom: 8 }}>
                {instructions.warnings.map((w, i) => <div key={i} style={{ fontSize: 11, color: C.red }}>⚠ {w}</div>)}
              </div>
            )}
            {instructions.steps?.map((step, i) => (
              <div key={i} style={{ display: "flex", gap: 8, padding: "6px 0", borderBottom: `1px solid ${C.border}`, fontSize: 12, color: C.text }}>
                <span style={{ flexShrink: 0, fontWeight: 800, color: agentColor, minWidth: 22 }}>{i + 1}.</span>
                <span>{step}</span>
              </div>
            ))}
            {instructions.requiredTools?.length > 0 && (
              <div style={{ marginTop: 8 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 4 }}>🔧 Tools Needed</div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {instructions.requiredTools.map((t, i) => <Badge key={i} color={C.textM} small>{t}</Badge>)}
                </div>
              </div>
            )}
          </div>
        ) : !loadingInstructions && (
          <div style={{ fontSize: 11, color: C.textM, fontStyle: "italic" }}>Click Generate for step-by-step instructions, parts list and required tools.</div>
        )}
      </div>

      {/* Agent assistant — Julia/Jimmy/Hank based on WO type */}
      <div style={{ marginBottom: 12 }}>
        <button onClick={() => setAgentOpen(o => !o)} style={{ background: agentColor + "15", border: `1px solid ${agentColor}40`, borderRadius: 8, padding: "8px 14px", cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 8, width: "100%" }}>
          <img src={agentImg} alt={agentName} style={{ width: 28, height: 28, borderRadius: "50%", objectFit: "cover" }} onError={e => { e.target.style.display="none"; }} />
          <span style={{ fontSize: 12, fontWeight: 700, color: agentColor }}>Ask {agentName} about this job</span>
          <span style={{ marginLeft: "auto", color: C.textM }}>{agentOpen ? "▲" : "▼"}</span>
        </button>
        {agentOpen && (
          <div style={{ border: `1px solid ${agentColor}40`, borderTop: "none", borderRadius: "0 0 8px 8px", padding: 14 }}>
            {isDetail ? (
              <JuliaInspectionChat
                vehicle={{ ...vehicle, _woContext: agentContext, _detailMode: true }}
                updateVehicle={onUpdateVehicle}
                onInspectionComplete={(upd, newWOs) => { if (newWOs?.length) onUpdateVehicle({ ...vehicle, workOrders: [...(vehicle.workOrders||[]), ...newWOs] }); setAgentOpen(false); }}
                onClose={() => setAgentOpen(false)} />
            ) : isBodyWork ? (
              <BodyWorkChat vehicle={{ ...vehicle, _woContext: agentContext }}
                onComplete={(newWOs) => { if (newWOs?.length) onUpdateVehicle({ ...vehicle, workOrders: [...(vehicle.workOrders||[]), ...newWOs] }); setAgentOpen(false); }}
                onClose={() => setAgentOpen(false)} staff={staff} vendors={vendors} />
            ) : (
              <DiagnosticChat vehicle={{ ...vehicle, _woContext: agentContext }}
                onDiagnosisComplete={(upd, newWOs) => { if (newWOs?.length) onUpdateVehicle({ ...vehicle, workOrders: [...(vehicle.workOrders||[]), ...newWOs] }); setAgentOpen(false); }}
                onClose={() => setAgentOpen(false)}
                staff={staff} vehicles={[vehicle]} vendors={[]} addLedger={() => {}} addPart={() => {}} updateVehicle={() => {}} />
            )}
          </div>
        )}
      </div>

      {/* Status row */}
      <div style={{ display:"flex", gap:8, alignItems:"center", flexWrap:"wrap", marginTop:8 }}>
        {/* In Progress button */}
        {wo.status !== "COMPLETE" && (
          <button onClick={() => onUpdateWO({ ...wo, status:"IN_PROGRESS", startedAt: wo.startedAt || new Date().toISOString(), startedBy: wo.startedBy || currentUser?.username })}
            style={{ background:wo.status==="IN_PROGRESS"?C.blue+"25":"none", border:`2px solid ${wo.status==="IN_PROGRESS"?C.blue:C.border}`, borderRadius:8, padding:"7px 14px", cursor:"pointer", fontSize:12, fontWeight:700, color:wo.status==="IN_PROGRESS"?C.blue:C.textM, fontFamily:"inherit", display:"flex", alignItems:"center", gap:6 }}>
            {wo.status==="IN_PROGRESS"?"⚡ In Progress":"▶ Start Work"}
          </button>
        )}
        {/* Status dropdown — only Open/In Progress/Awaiting Parts/External */}
        {wo.status !== "COMPLETE" && (
          <select value={["OPEN","IN_PROGRESS","AWAITING_PARTS","EXTERNAL"].includes(wo.status)?wo.status:"OPEN"}
            onChange={e => onUpdateWO({ ...wo, status: e.target.value })}
            style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:6, padding:"6px 10px", color:C.text, fontSize:12, fontFamily:"inherit", outline:"none" }}>
            {["OPEN","IN_PROGRESS","AWAITING_PARTS","EXTERNAL"].map(s => <option key={s} value={s}>{s.replace("_"," ")}</option>)}
          </select>
        )}
        {wo.status !== "COMPLETE" && (wo.partsETA || (wo.estimatedPartsCost || 0) > 0 || (wo.jobItems || []).some(j => j.type === "parts" || (j.partName && j.partName.trim()))) && (
          <button onClick={() => onUpdateWO({
              ...wo,
              allPartsIn: !wo.allPartsIn,
              allPartsInAt: !wo.allPartsIn ? new Date().toISOString() : null,
              allPartsInBy: !wo.allPartsIn ? (currentUser?.username || null) : null,
            })}
            style={{ background: wo.allPartsIn ? C.green + "25" : C.amber + "12", border: `2px solid ${wo.allPartsIn ? C.green : C.amber}`, borderRadius: 8, padding: "7px 14px", cursor: "pointer", fontSize: 12, fontWeight: 800, color: wo.allPartsIn ? C.green : C.amber, fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6 }}>
            {wo.allPartsIn ? "✅ All Parts In" : "📦 Mark All Parts In"}
          </button>
        )}
        {wo.status !== "COMPLETE" && wo.partsETA && !wo.allPartsIn && (
          <div style={{ fontSize: 10, color: C.textM, fontWeight: 700, background: C.s2, border: `1px solid ${C.border}`, borderRadius: 6, padding: "4px 8px" }}>
            📦 ETA {fmtDate(wo.partsETA)}
          </div>
        )}
        {/* Lift type indicator */}
        <div style={{ fontSize:10, color:needsLift||preferScissor?C.amber:C.textM, fontWeight:700, background:(needsLift||preferScissor)?C.amber+"10":C.s2, border:`1px solid ${(needsLift||preferScissor)?C.amber+"40":C.border}`, borderRadius:6, padding:"4px 8px" }}>
          🔧 {liftType}
        </div>
        {/* Complete button — yellow bg blue border */}
        {wo.status !== "COMPLETE" && (
          <button onClick={() => { setCompleteFlow(true); setCompleteStep(wo.status==="EXTERNAL"?"external":"who"); }}
            style={{ background:"#fef08a", border:"2px solid "+C.blue, borderRadius:8, padding:"7px 16px", cursor:"pointer", fontSize:12, fontWeight:800, color:C.blue, fontFamily:"inherit" }}>
            ✓ Complete
          </button>
        )}
        {wo.status === "COMPLETE" && (
          <div style={{ background:C.green+"15", border:`1px solid ${C.green}40`, borderRadius:6, padding:"5px 10px", fontSize:11, color:C.green, fontWeight:700 }}>
            ✅ Completed{wo.completedBy?" by "+(staff?.find(s=>s.username===wo.completedBy)?.name||wo.completedBy):""}{wo.completedAt?" · "+fmtDate(wo.completedAt):""}
          </div>
        )}
        {isManager && !confirmDelete && wo.status !== "COMPLETE" && (
          <button onClick={() => setConfirmDelete(true)} style={{ background:C.red+"15", border:`1px solid ${C.red}40`, borderRadius:6, padding:"6px 12px", cursor:"pointer", fontSize:11, color:C.red, fontFamily:"inherit" }}>🗑 Delete WO</button>
        )}
        {onPrintWO && (
          <button onClick={() => onPrintWO(wo)} style={{ background:C.blue+"15", border:`1px solid ${C.blue}40`, borderRadius:6, padding:"6px 12px", cursor:"pointer", fontSize:11, fontWeight:700, color:C.blue, fontFamily:"inherit" }}>📋 Print Repair Order</button>
        )}
        <Btn variant="ghost" onClick={onClose}>Close</Btn>
      </div>

      {/* ── COMPLETE WO WIZARD ── */}
      {completeFlow && (
        <div style={{ background:C.surface, border:`2px solid ${C.green}50`, borderRadius:12, padding:"16px", marginTop:10 }}>
          <div style={{ fontSize:13, fontWeight:800, color:C.green, marginBottom:12 }}>✅ Complete Work Order</div>

          {/* External completion */}
          {(wo.status==="EXTERNAL" || completeStep==="external") && (
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              <div style={{ fontSize:11, color:C.textM }}>External work — enter vendor and cost.</div>
              <TI label="Vendor / Shop name" value={externalVendor} onChange={setExternalVendor} placeholder="e.g. Muffler Masters Salem" />
              <TI label="Total cost ($)" value={externalCost} onChange={setExternalCost} type="number" placeholder="e.g. 350" />
              <div style={{ display:"flex", gap:8 }}>
                <Btn variant="green" disabled={!externalVendor.trim()||!externalCost} onClick={() => {
                  const cost = parseFloat(externalCost)||0;
                  onUpdateWO({ ...wo, status:"COMPLETE", completedAt:todayStr(), completedBy:externalVendor, externalCost:cost, externalVendor });
                  if (cost>0 && onAddLedger) onAddLedger({ id:genId(), vehicleId:vehicle.id, category:"EXTERNAL", description:`External: ${wo.description} — ${externalVendor}`, amount:cost, date:todayStr(), auto:true });
                  setCompleteFlow(false);
                }}>Save & Close WO</Btn>
                <Btn variant="ghost" onClick={() => setCompleteFlow(false)}>Cancel</Btn>
              </div>
            </div>
          )}

          {/* Step 1: Who */}
          {completeStep==="who" && wo.status!=="EXTERNAL" && (
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              <SI label="Who completed this work?" value={completedBy} onChange={setCompletedBy}
                options={[{value:"",label:"— Select —"}, ...staff.filter(s=>!["manager","viewer"].includes(s.role)).map(s=>({value:s.username,label:s.name}))]} />
              <div style={{ display:"flex", gap:8 }}>
                <Btn variant="blue" disabled={!completedBy} onClick={() => setCompleteStep("hours")}>Next →</Btn>
                <Btn variant="ghost" onClick={() => setCompleteFlow(false)}>Cancel</Btn>
              </div>
            </div>
          )}

          {/* Step 2: Hours */}
          {completeStep==="hours" && (
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              <div style={{ fontSize:11, color:C.textM }}>Estimated: <strong>{wo.estimatedHours||1}h</strong></div>
              <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                <button onClick={() => setUseEstHours(true)} style={{ flex:1, background:useEstHours?C.blue+"20":"none", border:`2px solid ${useEstHours?C.blue:C.border}`, borderRadius:8, padding:"8px", cursor:"pointer", fontSize:11, fontWeight:700, color:useEstHours?C.blue:C.textM, fontFamily:"inherit" }}>
                  ✓ Use estimate ({wo.estimatedHours||1}h)
                </button>
                <button onClick={() => setUseEstHours(false)} style={{ flex:1, background:!useEstHours?C.amber+"20":"none", border:`2px solid ${!useEstHours?C.amber:C.border}`, borderRadius:8, padding:"8px", cursor:"pointer", fontSize:11, fontWeight:700, color:!useEstHours?C.amber:C.textM, fontFamily:"inherit" }}>
                  Enter actual hours
                </button>
              </div>
              {!useEstHours && (
                <TI label="Actual hours" value={actualHours} onChange={setActualHours} type="number" placeholder="e.g. 1.5" />
              )}
              <div style={{ display:"flex", gap:8 }}>
                <Btn variant="blue" onClick={() => {
                  const hrs = useEstHours ? (wo.estimatedHours||1) : (parseFloat(actualHours)||wo.estimatedHours||1);
                  const sm = staff?.find(s=>s.username===completedBy);
                  if (sm?.hourlyRate && hrs>0 && onAddLedger) {
                    onAddLedger({ id:genId(), vehicleId:vehicle.id, category:"LABOR", description:`Labor: ${sm.name} — ${wo.description} (${hrs}h @ $${sm.hourlyRate}/hr)`, amount:hrs*(sm.hourlyRate||0), date:todayStr(), auto:true });
                  }
                  setActualHours(String(hrs));
                  // Check for parts that could be returned/stocked
                  const potentialReturn = woParts.filter(p => !["Installed","RETURNED","CANCELLED"].includes(p.status));
                  if (potentialReturn.length > 0) {
                    setPartsToReturn(potentialReturn);
                    setCompleteStep("parts");
                  } else {
                    onUpdateWO({ ...wo, status:"COMPLETE", completedAt:todayStr(), completedBy, actualHours:hrs });
                    setCompleteFlow(false);
                  }
                }}>Next →</Btn>
                <Btn variant="ghost" onClick={() => setCompleteStep("who")}>← Back</Btn>
              </div>
            </div>
          )}

          {/* Step 3: Parts */}
          {completeStep==="parts" && partsToReturn.length>0 && (
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              <div style={{ fontSize:12, fontWeight:700, color:"#000" }}>Parts used for this WO — any to return or stock?</div>
              {partsToReturn.map(p => (
                <div key={p.id} style={{ background:C.s2, border:`1px solid ${C.border}`, borderRadius:8, padding:"8px 12px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                  <div>
                    <div style={{ fontSize:12, fontWeight:600, color:"#000" }}>{p.description}</div>
                    <div style={{ fontSize:10, color:C.textM }}>Status: {p.status}</div>
                  </div>
                  <select onChange={e => {
                    if (e.target.value==="return") {
                      if (onAddPart) onAddPart({ ...p, status:"RETURNED", pendingReturn:true, vehicleId:null, workOrderId:null });
                    } else if (e.target.value==="stock") {
                      if (onAddPart) onAddPart({ ...p, status:"Received", generalStock:true, vehicleId:null, workOrderId:null });
                    }
                  }} style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:6, padding:"4px 8px", fontSize:11, fontFamily:"inherit" }}>
                    <option value="">— Leave as-is</option>
                    <option value="return">Return to vendor</option>
                    <option value="stock">Add to general stock</option>
                  </select>
                </div>
              ))}
              <div style={{ display:"flex", gap:8 }}>
                <Btn variant="green" onClick={() => {
                  const hrs = parseFloat(actualHours)||wo.estimatedHours||1;
                  onUpdateWO({ ...wo, status:"COMPLETE", completedAt:todayStr(), completedBy, actualHours:hrs });
                  setCompleteFlow(false);
                }}>✅ Close Work Order</Btn>
                <Btn variant="ghost" onClick={() => setCompleteStep("hours")}>← Back</Btn>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Delete confirmation with note */}
      {confirmDelete && isManager && (
        <div style={{ background: C.red + "10", border: `1px solid ${C.red}40`, borderRadius: 8, padding: "12px 14px", marginTop: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.red, marginBottom: 8 }}>⚠ Delete this work order? This cannot be undone. A record will remain.</div>
          <textarea value={deleteNote} onChange={e => setDeleteNote(e.target.value)} placeholder="Required: reason for deletion…" rows={2}
            style={{ width: "100%", background: C.surface, border: `1px solid ${C.red}40`, borderRadius: 6, padding: "8px 10px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none", resize: "none", boxSizing: "border-box", marginBottom: 8 }} />
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={handleDelete} disabled={!deleteNote.trim()} style={{ background: C.red, border: "none", borderRadius: 6, padding: "7px 14px", cursor: "pointer", fontSize: 12, fontWeight: 700, color: "#fff", fontFamily: "inherit", opacity: deleteNote.trim() ? 1 : 0.5 }}>Confirm Delete</button>
            <button onClick={() => { setConfirmDelete(false); setDeleteNote(""); }} style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 6, padding: "7px 14px", cursor: "pointer", fontSize: 12, fontFamily: "inherit" }}>Cancel</button>
          </div>
        </div>
      )}
    </Modal>
  );
};

const DiagnosisChooser = ({ vehicle, onChoose, onClose, onSkip }) => (
  <div>
    <div style={{ fontSize: 12, color: C.textM, marginBottom: 20, lineHeight: 1.6 }}>
      Choose who should handle this vehicle's inspection and diagnosis.
    </div>
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div onClick={() => onChoose("julia")}
        style={{ background: C.purple + "12", border: `2px solid ${vehicle._juliaPaused ? C.purple : C.purple+"50"}`, borderRadius: 12, padding: "16px 18px", cursor: "pointer", display: "flex", alignItems: "center", gap: 14 }}>
        <img src="/images/Julia.jpg" alt="Julia"
          style={{ width: 52, height: 52, borderRadius: "50%", objectFit: "cover", objectPosition: "top", border: `3px solid ${C.purple}` }}
          onError={e => { e.target.style.display = "none"; }} />
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: C.purple, fontFamily: "'Bebas Neue', sans-serif" }}>JULIA — GENERAL INSPECTION</div>
            {vehicle._juliaPaused && <span style={{ background: C.amber + "30", color: C.amber, fontSize: 9, fontWeight: 800, padding: "2px 7px", borderRadius: 8 }}>⏸ PAUSED</span>}
          </div>
          <div style={{ fontSize: 11, color: "#000", marginTop: 2 }}>Full walk-around appraisal · Exterior · Interior · Tires · Test drive</div>
          <div style={{ fontSize: 10, color: C.textM, marginTop: 2 }}>{vehicle._juliaPaused ? "Tap to resume where you left off" : "Best for: new intake, updating condition after field inspection"}</div>
        </div>
      </div>
      <div onClick={() => onChoose("hank")}
        style={{ background: C.amber + "12", border: `2px solid ${vehicle._hankPaused ? C.amber : C.amber+"50"}`, borderRadius: 12, padding: "16px 18px", cursor: "pointer", display: "flex", alignItems: "center", gap: 14 }}>
        <img src="/images/Hank-Headshot.jpg" alt="Hank"
          style={{ width: 52, height: 52, borderRadius: "50%", objectFit: "cover", objectPosition: "top", border: `3px solid ${C.amber}` }} />
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: C.amber, fontFamily: "'Bebas Neue', sans-serif" }}>HANK — MECHANICAL DIAGNOSIS</div>
            {vehicle._hankPaused && <span style={{ background: C.amber + "30", color: C.amber, fontSize: 9, fontWeight: 800, padding: "2px 7px", borderRadius: 8 }}>⏸ PAUSED</span>}
          </div>
          <div style={{ fontSize: 11, color: "#000", marginTop: 2 }}>Targeted mechanical diagnosis · Work orders · Parts estimate · Labor hours</div>
          <div style={{ fontSize: 10, color: C.textM, marginTop: 2 }}>{vehicle._hankPaused ? "Tap to resume where you left off" : "Best for: known mechanical issue, following up on Julia's findings"}</div>
        </div>
      </div>
      <div onClick={() => onChoose("jimmy")}
        style={{ background: "#f9731612", border: `2px solid ${vehicle._jimmyPaused ? "#f97316" : "#f9731650"}`, borderRadius: 12, padding: "16px 18px", cursor: "pointer", display: "flex", alignItems: "center", gap: 14 }}>
        <img src="/images/jimmy-working.jpg" alt="Jimmy"
          style={{ width: 52, height: 52, borderRadius: "50%", objectFit: "cover", objectPosition: "top", border: "3px solid #f97316" }}
          onError={e => { e.target.style.display = "none"; }} />
        <div style={{ flex: 1 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: "#f97316", fontFamily: "'Bebas Neue', sans-serif" }}>JIMMY — BODY WORK</div>
            {vehicle._jimmyPaused && <span style={{ background: "#f9731630", color: "#f97316", fontSize: 9, fontWeight: 800, padding: "2px 7px", borderRadius: 8 }}>⏸ PAUSED</span>}
          </div>
          <div style={{ fontSize: 11, color: "#000", marginTop: 2 }}>Dents · Paint · Rust · Glass · Structural · Trim</div>
          <div style={{ fontSize: 10, color: C.textM, marginTop: 2 }}>{vehicle._jimmyPaused ? "Tap to resume where you left off" : "Best for: cosmetic issues, collision damage, known body work needed"}</div>
        </div>
      </div>

      {/* Skip inspection divider */}
      <div style={{ display: "flex", alignItems: "center", gap: 8, margin: "4px 0" }}>
        <div style={{ flex: 1, height: 1, background: C.border }} />
        <span style={{ fontSize: 10, color: C.textM, textTransform: "uppercase", letterSpacing: "0.08em" }}>or skip inspection</span>
        <div style={{ flex: 1, height: 1, background: C.border }} />
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <div onClick={() => onSkip && onSkip("hank")}
          style={{ background: C.blue + "10", border: `2px solid ${C.blue}40`, borderRadius: 10, padding: "12px 14px", cursor: "pointer", textAlign: "center" }}>
          <img src="/images/Hank-Headshot.jpg" alt="Hank" style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", marginBottom: 4 }} onError={e => { e.target.style.display="none"; }} />
          <div style={{ fontSize: 11, fontWeight: 800, color: C.blue }}>Go to Mechanical</div>
          <div style={{ fontSize: 9, color: C.textM, marginTop: 2 }}>Open Hank's diagnosis</div>
        </div>
        <div onClick={() => onSkip && onSkip("jimmy")}
          style={{ background: "#f9731610", border: `2px solid #f9731640`, borderRadius: 10, padding: "12px 14px", cursor: "pointer", textAlign: "center" }}>
          <img src="/images/jimmy-working.jpg" alt="Jimmy" style={{ width: 36, height: 36, borderRadius: "50%", objectFit: "cover", marginBottom: 4 }} onError={e => { e.target.style.display="none"; }} />
          <div style={{ fontSize: 11, fontWeight: 800, color: "#f97316" }}>Go to Body Work</div>
          <div style={{ fontSize: 9, color: C.textM, marginTop: 2 }}>Open Jimmy's chat</div>
        </div>
      </div>
    </div>
    <div style={{ marginTop: 14 }}><Btn variant="ghost" onClick={onClose}>Cancel</Btn></div>
  </div>
);

// ─── SCRAP VEHICLE FEATURE ───────────────────────────────────────────────────

const SCRAP_PROVIDERS = [
  { id: "sp1", name: "Salem Auto Wrecking", email: "pickup@salemautowrecking.com", phone: "503-555-0101" },
  { id: "sp2", name: "Valley Auto Salvage", email: "scrap@valleyautosalvage.com", phone: "503-555-0182" },
  { id: "sp3", name: "Pacific Northwest Junk Cars", email: "buy@pnwjunkcars.com", phone: "541-555-0234" },
  { id: "sp4", name: "Cascades Auto Recycling", email: "info@cascadesrecycling.com", phone: "503-555-0167" },
];

const ScrapVehicleModal = ({ vehicle, currentUser, onConfirmScrap, onClose }) => {
  const [reason, setReason] = useState("");
  const [managerNote, setManagerNote] = useState("");
  const [selectedProviders, setSelectedProviders] = useState([]);
  const [emailSent, setEmailSent] = useState(false);
  const [phase, setPhase] = useState("request"); // request | approve | email
  const isManager = ["manager","office"].includes(currentUser?.role);

  const toggleProvider = (id) => setSelectedProviders(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );

  const sendScrapEmails = () => {
    const providers = SCRAP_PROVIDERS.filter(p => selectedProviders.includes(p.id));
    // In production this would use Gmail MCP — for now log and show success
    providers.forEach(p => {
      console.log(`Email to ${p.email}: Scrap vehicle pickup request for ${vehicle.year} ${vehicle.make} ${vehicle.model} #${vehicle.stockNumber}`);
    });
    setEmailSent(true);
  };

  const confirmScrap = () => {
    onConfirmScrap({
      ...vehicle,
      stage: "SCRAPPED",
      scrappedAt: todayStr(),
      scrappedBy: currentUser?.name || "Manager",
      scrappedReason: reason,
      managerScrapNote: managerNote,
    });
  };

  return (
    <div>
      {phase === "request" && (
        <>
          <div style={{ background: C.red + "12", border: `2px solid ${C.red}40`, borderRadius: 10, padding: "14px 16px", marginBottom: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: C.red, marginBottom: 4 }}>⚠ Scrap Vehicle Request</div>
            <div style={{ fontSize: 11, color: "#000" }}>{vehicle.year} {vehicle.make} {vehicle.model} · #{vehicle.stockNumber}</div>
            <div style={{ fontSize: 10, color: C.textM, marginTop: 2 }}>This will remove the vehicle from all workflows and trigger the kill switch for any ordered parts.</div>
          </div>
          <TI label="Reason for scrapping *" value={reason} onChange={setReason} placeholder="e.g. Frame damage — not cost effective to repair, flood vehicle, salvage title issues…" />
          <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
            <Btn variant="red" onClick={() => { if (reason.trim()) setPhase(isManager ? "email" : "approve"); }} disabled={!reason.trim()}>
              {isManager ? "Continue →" : "Submit for Manager Approval"}
            </Btn>
            <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
          </div>
        </>
      )}

      {phase === "approve" && !isManager && (
        <div style={{ textAlign: "center", padding: "24px 0" }}>
          <div style={{ fontSize: 36, marginBottom: 12 }}>⏳</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 6 }}>Awaiting Manager Approval</div>
          <div style={{ fontSize: 11, color: C.textM }}>Angela, Dmitriy, or Robert will see this on their home screen.</div>
        </div>
      )}

      {phase === "email" && (
        <>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 4 }}>Manager Confirmation</div>
          <TI label="Manager note (optional)" value={managerNote} onChange={setManagerNote} placeholder="Any additional notes for the record…" />
          <div style={{ fontSize: 11, fontWeight: 700, color: C.text, marginBottom: 8, marginTop: 14 }}>Notify Scrap Pickup Providers</div>
          <div style={{ fontSize: 10, color: C.textM, marginBottom: 10 }}>Auto-generate pickup request emails from edgewaterautocenter@gmail.com</div>
          {SCRAP_PROVIDERS.map(p => (
            <div key={p.id} onClick={() => toggleProvider(p.id)}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 12px", background: selectedProviders.includes(p.id) ? C.blue + "12" : C.s2, border: `1px solid ${selectedProviders.includes(p.id) ? C.blue + "60" : C.border}`, borderRadius: 8, marginBottom: 6, cursor: "pointer" }}>
              <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${selectedProviders.includes(p.id) ? C.blue : C.border}`, background: selectedProviders.includes(p.id) ? C.blue : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                {selectedProviders.includes(p.id) && <span style={{ color: "#fff", fontSize: 11, fontWeight: 900 }}>✓</span>}
              </div>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: "#000" }}>{p.name}</div>
                <div style={{ fontSize: 10, color: C.textM }}>{p.email} · {p.phone}</div>
              </div>
            </div>
          ))}
          {!emailSent ? (
            <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
              {selectedProviders.length > 0 && (
                <Btn variant="blue" onClick={sendScrapEmails}>
                  📧 Send Pickup Requests ({selectedProviders.length})
                </Btn>
              )}
              <Btn variant="red" onClick={confirmScrap}>Confirm Scrap & Remove Vehicle</Btn>
              <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
            </div>
          ) : (
            <div>
              <div style={{ background: C.green + "15", border: `1px solid ${C.green}40`, borderRadius: 8, padding: "10px 14px", marginBottom: 10, fontSize: 11, color: C.green }}>
                ✓ Pickup request emails sent to {selectedProviders.length} provider{selectedProviders.length > 1 ? "s" : ""}
              </div>
              <Btn variant="red" onClick={confirmScrap}>Confirm Scrap & Remove Vehicle</Btn>
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ─── VEHICLE DETAIL ───────────────────────────────────────────────────────────


// ─── VEHICLE SPECS EDITOR ─────────────────────────────────────────────────────

const SpecsEditor = ({ vehicle, onUpdate }) => {
  const [open, setOpen] = useState(false);
  const [specs, setSpecs] = useState({
    trim: vehicle.trim || "",
    engine: vehicle.engine || "",
    displacement: vehicle.displacement || "",
    cylinders: vehicle.cylinders || "",
    drivetrain: vehicle.drivetrain || "",
    transmission: vehicle.transmission || "",
    fuelType: vehicle.fuelType || "Gas",
    bodyStyle: vehicle.bodyStyle || "",
    doors: vehicle.doors || "",
    gvwr: vehicle.gvwr || "",
    tireSize: vehicle.tireSize || "",
    vin: vehicle.vin || "",
  });

  const save = () => {
    onUpdate({ ...vehicle, ...specs });
    setOpen(false);
  };

  const hasSpecs = specs.engine || specs.drivetrain || specs.transmission || specs.trim;

  return (
    <div style={{ background: C.surface, border: `2px solid ${C.amber}40`, borderRadius: 10, padding: "12px 14px", marginBottom: 14 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: open ? 12 : 0 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 800, color: C.amber, textTransform: "uppercase", letterSpacing: "0.08em" }}>🔩 Vehicle Specifics</div>
          {!open && hasSpecs && (
            <div style={{ fontSize: 10, color: "#555", marginTop: 2, lineHeight: 1.5 }}>
              {[specs.trim, specs.engine, specs.displacement ? specs.displacement + "L" : "", specs.cylinders ? specs.cylinders + "-cyl" : "", specs.drivetrain, specs.transmission].filter(Boolean).join(" · ")}
            </div>
          )}
          {!open && !hasSpecs && <div style={{ fontSize: 10, color: C.textM, marginTop: 2 }}>Tap to add engine, trim, drivetrain — used by Roxy for parts</div>}
        </div>
        <button onClick={() => setOpen(o => !o)}
          style={{ background: C.s2, border: `1px solid ${C.border}`, borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 10, fontWeight: 700, color: C.amber, fontFamily: "inherit" }}>
          {open ? "Save ↑" : "Edit"}
        </button>
      </div>
      {open && (
        <div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
            <TI label="Trim" value={specs.trim} onChange={v => setSpecs(s => ({ ...s, trim: v }))} placeholder="e.g. LT, EX, SE, Sport" />
            <TI label="Engine" value={specs.engine} onChange={v => setSpecs(s => ({ ...s, engine: v }))} placeholder="e.g. 5.3L V8, 2.0T" />
            <TI label="Displacement (L)" value={specs.displacement} onChange={v => setSpecs(s => ({ ...s, displacement: v }))} placeholder="e.g. 5.3" type="number" />
            <TI label="Cylinders" value={specs.cylinders} onChange={v => setSpecs(s => ({ ...s, cylinders: v }))} placeholder="e.g. 4, 6, 8" type="number" />
            <SI label="Drivetrain" value={specs.drivetrain} onChange={v => setSpecs(s => ({ ...s, drivetrain: v }))}
              options={["","FWD","RWD","AWD","4WD","4x4"].map(x => ({ value: x, label: x || "— Select" }))} />
            <SI label="Transmission" value={specs.transmission} onChange={v => setSpecs(s => ({ ...s, transmission: v }))}
              options={["","Automatic","Manual","CVT","DCT","6-speed Auto","8-speed Auto","10-speed Auto"].map(x => ({ value: x, label: x || "— Select" }))} />
            <SI label="Fuel Type" value={specs.fuelType} onChange={v => setSpecs(s => ({ ...s, fuelType: v }))}
              options={["Gas","Diesel","Hybrid","Electric","PHEV"].map(x => ({ value: x, label: x }))} />
            <TI label="Body Style" value={specs.bodyStyle} onChange={v => setSpecs(s => ({ ...s, bodyStyle: v }))} placeholder="e.g. Sedan, SUV, Crew Cab" />
            <TI label="Tire Size" value={specs.tireSize} onChange={v => setSpecs(s => ({ ...s, tireSize: v }))} placeholder="e.g. 245/65R17" />
            <TI label="GVWR (lbs)" value={specs.gvwr} onChange={v => setSpecs(s => ({ ...s, gvwr: v }))} placeholder="e.g. 6800" type="number" />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn variant="amber" onClick={save}><CheckCircle size={13} /> Save Specs</Btn>
            <Btn variant="ghost" size="sm" onClick={() => setOpen(false)}>Cancel</Btn>
          </div>
        </div>
      )}
    </div>
  );
};

const VehicleDetailView = ({ vehicle: vIn, vehicles, parts, vendors, ledger, staff, currentUser, catalog, onUpdate, onAddPart, onUpdatePart, onAddLedger, onOpenChecklist, onOpenDelivery, onShopForPart, onBack, onContinueInspection, onStartInspection, onTriggerCalendarRebuild, showHoldingCost, navigateTo, setView: setViewProp }) => {
  const vehicle = vehicles.find(v => v.id === vIn.id) || vIn;
  const isSold = vehicle.stage === "SOLD";
  const isManager = ["manager"].includes(currentUser?.role);
  const [tab, setTab] = useState("overview");
  const [addWOOpen, setAddWOOpen] = useState(false);
  const [addChecklistOpen, setAddChecklistOpen] = useState(false);
  const [lineReadyOpen, setLineReadyOpen] = useState(false);
  const [detailCompleteOpen, setDetailCompleteOpen] = useState(false);
  const [lrReason, setLrReason] = useState("");
  const [lrConfirmed, setLrConfirmed] = useState(false);
  const [lrSelectedToClose, setLrSelectedToClose] = useState([]);
  const [detailBy, setDetailBy] = useState("");
  const [detailType, setDetailType] = useState("inhouse");
  const [newWO, setNewWO] = useState({ category: "MECHANICAL", description: "", priority: "MEDIUM", estimatedHours: 1, notes: "", status: "OPEN" });
  const [addPartFor, setAddPartFor] = useState(null);
  const [newPart, setNewPart] = useState({ description: "", partNumber: "", supplier: "v4", cost: "", orderDate: todayStr(), eta: addDays(todayStr(), 3), status: "Ordered" });
  const [addLedgerOpen, setAddLedgerOpen] = useState(false);
  const [newLedger, setNewLedger] = useState({ category: "PARTS", description: "", amount: "", date: todayStr() });
  const [rediagnoseOpen, setRediagnoseOpen] = useState(false);
  const [warnExpanded, setWarnExpanded] = useState(false);
  const [rediagAgent, setRediagAgent] = useState(null);
  const [rediagMode, setRediagMode] = useState(null);
  const [expandedWO, setExpandedWO] = useState(null);
  const [selectedWO, setSelectedWO] = useState(null);
  const [printWOData, setPrintWOData] = useState(null); // for repair order print
  const [markSoldOpen, setMarkSoldOpen] = useState(false);
  const [markSoldStep, setMarkSoldStep] = useState("details");
  const [saleInput, setSaleInput] = useState("");
  const [saleOdometer, setSaleOdometer] = useState("");
  const [closeWOChoice, setCloseWOChoice] = useState(null);
  const [returnToCustomerOpen, setReturnToCustomerOpen] = useState(false);
  const resetMarkSold = () => { setMarkSoldOpen(false); setMarkSoldStep("details"); setSaleInput(""); setSaleOdometer(""); setCloseWOChoice(null); setSoldRepairLocation("mech_shop"); };
  const [soldRepairLocation, setSoldRepairLocation] = useState("mech_shop");
  const [bodyWorkOpen, setBodyWorkOpen] = useState(false);
  const [towOpen, setTowOpen] = useState(false);
  const [partsExpanded, setPartsExpanded] = useState(false);
  const [scrapOpen, setScrapOpen] = useState(false);

  const addWOPhoto = (woId, vehicleId, src, description) => {
    const photoEntry = description ? { src, description } : src;
    onUpdate({ ...vehicle, workOrders: vehicle.workOrders.map(w => w.id !== woId ? w : { ...w, photos: [...(w.photos || []), photoEntry] }) });
  };

  const handleAIRoute = ({ woId, vehicleId, department, reason, priority, suggestedWO, photo, description }) => {
    // Check if a WO for this department already exists
    const existingWO = vehicle.workOrders?.find(w => w.category === department && w.status !== "COMPLETE");
    if (!existingWO && suggestedWO) {
      // Create a new WO routed to the correct department
      const newWO = {
        id: genId(),
        category: department,
        description: suggestedWO,
        priority: priority || "MEDIUM",
        status: "OPEN",
        createdAt: todayStr(),
        estimatedHours: 1,
        actualHours: 0,
        diagnosisRationale: `Photo inspection: ${description}. AI routing: ${reason}`,
        photos: [{ src: photo, description }]
      };
      onUpdate({ ...vehicle, workOrders: [...(vehicle.workOrders || []), newWO] });
    }
  };
  const removeWOPhoto = (woId, photoIdx) => {
    onUpdate({ ...vehicle, workOrders: vehicle.workOrders.map(w => w.id !== woId ? w : { ...w, photos: (w.photos || []).filter((_, i) => i !== photoIdx) }) });
  };
  const vehicleParts = parts.filter(p => p.vehicleId === vehicle.id);
  const vehicleLedger = ledger.filter(e => e.vehicleId === vehicle.id);
  const profit = calcProfit(vehicle, ledger, vehicle.kbbValue);


  // Determine the "Continue" label based on vehicle stage
  const getContinueLabel = () => {
    switch (vehicle.stage) {
      case "INTAKE": return "Continue Intake →";
      case "INITIAL_DIAG": return vehicle.needsMechanicalDiagnosis ? "Continue Mechanical Diagnosis →" : "Continue Inspection →";
      case "MECH_VERIFY": return "Continue Mechanical Diagnosis →";
      case "PENDING_APPROVAL": return "Continue to Parts →";
      case "PARTS_ORDERED": return "Continue Parts Ordering →";
      case "SCHEDULED": return "Continue Scheduling →";
      case "IN_REPAIR": return "Continue Repair →";
      case "QC_DETAIL": return "Continue Detail/QC →";
      case "PHOTO_POST": return "Continue Photo & Post →";
      default: return null;
    }
  };
  const getContinueAction = () => {
    switch (vehicle.stage) {
      case "INTAKE": return () => onContinueInspection && onContinueInspection(vehicle, "julia");
      case "INITIAL_DIAG": return () => onContinueInspection && onContinueInspection(vehicle, vehicle.needsMechanicalDiagnosis ? "hank" : "julia");
      case "MECH_VERIFY": return () => onContinueInspection && onContinueInspection(vehicle, "hank");
      default: return null;
    }
  };

  const stageIdx = STAGES.findIndex(s => s.id === vehicle.stage);
  const advance = () => { if (stageIdx < STAGES.length - 1) onUpdate({ ...vehicle, stage: STAGES[stageIdx + 1].id }); };
  const regress = () => { if (stageIdx > 0) onUpdate({ ...vehicle, stage: STAGES[stageIdx - 1].id }); };
  const updateWO = (woId, upd) => onUpdate({ ...vehicle, workOrders: vehicle.workOrders.map(w => w.id === woId ? { ...w, ...upd } : w) });

  const stageGates = {
    INITIAL_DIAG: "Robert test-drove and documented symptoms — use AI Diagnostics",
    MECH_VERIFY: "James has inspected and confirmed repair scope",
    PENDING_APPROVAL: "Repair estimate submitted to Angela/Dmitriy",
    PARTS_ORDERED: "Owner approved + JIT parts ordered (4 days before repair)",
    SCHEDULED: "Parts received + on James's calendar",
    IN_REPAIR: "Vehicle is on the lift",
    QC_DETAIL: "All repairs complete — moved to detail",
    PHOTO_POST: "Cleaned ✓ · Photos ✓ · Buyer's Guide placed ✓",
    LINE_READY: "Advertised · Priced · Stickers · All docs ready",
  };

  // ── Auto-calculated recon status label ──
  const getReconStatus = () => {
    const allWOs = vehicle.workOrders || [];
    const openWOs = allWOs.filter(w => w.status !== "COMPLETE" && !w.deleted);
    const completedWOs = allWOs.filter(w => w.status === "COMPLETE");
    const vParts = parts.filter(p => p.vehicleId === vehicle.id && !["RETURNED","CANCELLED"].includes(p.status));
    const partsOnOrder = vParts.some(p => ["Ordered","In Transit"].includes(p.status));
    const currentLocation = vehicle.currentLocation || vehicle.repairLocation || "lot";
    const atShop = ["mech_shop","body_shop"].includes(currentLocation);
    const openMech = openWOs.filter(w => ["MECHANICAL","BODYWORK","LOCKSMITH","VENDOR"].includes(w.category));
    const openDetail = openWOs.filter(w => w.category === "DETAIL");
    const onlyDetailLeft = openWOs.length > 0 && openWOs.every(w => w.category === "DETAIL");
    const noOpenWOs = openWOs.length === 0;

    // Done
    if (["LINE_READY"].includes(vehicle.stage))
      return { label:"Line Ready", color:"#16a34a", bg:"#16a34a15", icon:"✅", sub:"All recon complete — ready for sale" };
    if (vehicle.stage === "PHOTO_READY")
      return { label:"Awaiting Photos", color:"#ec4899", bg:"#ec489912", icon:"📷", sub:"Recon complete — needs photos & advertising" };

    // No WOs at all — just added
    if (allWOs.length === 0 || (!completedWOs.length && !openWOs.length))
      return { label:"Fresh Work Order", color:"#6b7280", bg:"#6b728015", icon:"🆕", sub:"Vehicle just added — awaiting inspection" };

    // Parts on order for open mechanical WOs
    if (openMech.length > 0 && partsOnOrder)
      return { label:"Waiting on Parts", color:"#eab308", bg:"#eab30815", icon:"📦", sub:"Parts ordered — repair scheduled when parts arrive" };

    // Has open mechanical work — check location
    if (openMech.length > 0) {
      if (atShop)
        return { label:"Awaiting Repair", color:"#f97316", bg:"#f9731612", icon:"🔧", sub:"At shop — in queue for mechanical work" };
      // At lot, needs to go to shop
      return { label:"Awaiting Transport to Shop", color:C.amber, bg:C.amber+"12", icon:"🚛", sub:"Needs mechanical work — transport to shop required" };
    }

    // Mechanical all done, detail still open
    if (onlyDetailLeft)
      return { label:"Waiting for Detail", color:"#a855f7", bg:"#a855f712", icon:"🧹", sub:"Mechanical complete — needs cleaning & detail" };

    // All WOs done, still at shop — needs to come back to lot
    if (noOpenWOs && completedWOs.length > 0 && atShop)
      return { label:"Finished Awaiting Transport", color:C.blue, bg:C.blue+"12", icon:"🚚", sub:"All work done at shop — transport back to lot" };

    // All WOs done at lot
    if (noOpenWOs && completedWOs.length > 0)
      return { label:"Finished Awaiting Transport", color:C.blue, bg:C.blue+"12", icon:"🚚", sub:"All repairs complete" };

    // Fallback
    return { label:"Awaiting Repair", color:"#f97316", bg:"#f9731612", icon:"🔧", sub:"In progress" };
  };

  const reconStatus = getReconStatus();

  return (
    <div>
      <button onClick={onBack} style={{ background: "none", border: "none", color: C.textS, cursor: "pointer", fontSize: 11, padding: 0, marginBottom: 14, display: "flex", alignItems: "center", gap: 4 }}><img src="/images/btn-back-arrow.jpg" alt="" style={{width:16,height:16,objectFit:"contain",flexShrink:0}} onError={e=>e.target.style.display="none"} /> Back</button>
      <div style={{ backgroundImage: "url(/images/bg-vehicle-header.jpg)", backgroundSize: "cover", backgroundPosition: "center", borderRadius: 12, marginBottom: 14, padding: "16px 16px 12px", position: "relative", overflow: "hidden" }}>
        <div style={{ position: "absolute", inset: 0, background: "rgba(10,15,30,0.72)", borderRadius: 12 }} />
        <div style={{ position: "relative", zIndex: 1, display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
        <div>
          <div style={{ fontSize: 26, fontWeight: 800, color: "#fff", fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.05em", lineHeight: 1 }}>{vehicle.year} {vehicle.make} {vehicle.model}</div>
          <div style={{ fontSize: 11, color: "rgba(255,255,255,0.75)", marginTop: 3, fontFamily: "monospace" }}>#{vehicle.stockNumber} · {vehicle.vin || `···${vehicle.vinLast6}`} · {daysSince(vehicle.arrivalDate)}d on lot</div>
          {/* Vehicle type indicator */}
          {vehicle.soldRepairPending ? (
            <div style={{ display: "flex", gap: 6, alignItems: "center", marginTop: 5 }}>
              <span style={{ background: C.red, color: "#fff", fontSize: 10, fontWeight: 800, padding: "2px 8px", borderRadius: 8, letterSpacing: "0.05em" }}>🔧 CUSTOMER VEHICLE</span>

            </div>
          ) : vehicle.stage === "SOLD" ? (
            <div style={{ marginTop: 5 }}>
              <span style={{ background: C.green, color: "#fff", fontSize: 10, fontWeight: 800, padding: "2px 8px", borderRadius: 8 }}>✅ SOLD — INVENTORY CLOSED</span>
            </div>
          ) : null}
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <img src="/images/Edgewaterlogo.png" alt="" style={{ height:36, objectFit:"contain", opacity:0.6, flexShrink:0 }} onError={e=>e.target.style.display="none"} />
          {vehicle.kbbValue && <Badge color={C.green}>KBB {fmtMoney(vehicle.kbbValue)}</Badge>}
          {profit.overBudget && <Badge color={C.red}>⚠ Over Recon Budget</Badge>}
          {vehicle.diagnosisIncomplete && <Badge color={C.amber}>⚠ Incomplete Diagnosis — {vehicle.diagnosisConfidence}% certainty</Badge>}
          {vehicle.juliaInspectionIncomplete && vehicle.juliaMissingPoints?.length > 0 && (
            <div style={{ position: "relative" }}>
              <button onClick={() => setWarnExpanded(o => !o)} style={{ background: "#f9731620", border: "1px solid #f9731660", borderRadius: 6, padding: "3px 8px", cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 4 }}>
                <span style={{ fontSize: 13 }}>⚠️</span>
                <span style={{ fontSize: 10, fontWeight: 700, color: "#f97316" }}>{vehicle.juliaProgress || 0}%</span>
              </button>
              {warnExpanded && (
                <div style={{ position: "absolute", top: "100%", right: 0, zIndex: 100, background: C.surface, border: "2px solid #f9731660", borderRadius: 10, padding: "12px 14px", width: 280, marginTop: 4, boxShadow: "0 4px 20px #0006" }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: "#f97316", marginBottom: 6 }}>⚠️ Incomplete Inspection — {vehicle.juliaProgress || 0}% coverage</div>
                  <div style={{ fontSize: 11, color: C.text, marginBottom: 6, lineHeight: 1.5 }}>Julia's inspection was ended before all required checkpoints were covered. The following data was NOT collected:</div>
                  <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginBottom: 6 }}>
                    {vehicle.juliaMissingPoints.map((p, i) => <Badge key={i} color="#f97316" small>{p}</Badge>)}
                  </div>
                  <div style={{ fontSize: 10, color: C.textM, fontStyle: "italic" }}>A complete inspection should be performed before finalizing repair decisions.</div>
                  <button onClick={() => setWarnExpanded(false)} style={{ marginTop: 8, background: "none", border: "none", color: C.textM, cursor: "pointer", fontSize: 10, padding: 0 }}>✕ Close</button>
                </div>
              )}
            </div>
          )}
          {showHoldingCost && <HoldingCostBadge vehicle={vehicle} />}
        </div>
      </div>
      </div>

      {/* Profit summary bar */}

      {vehicle.diagnosisIncomplete && (
        <div style={{ background: C.amber + "15", border: `2px solid ${C.amber}60`, borderRadius: 10, padding: "12px 16px", marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: C.amber, marginBottom: 4 }}>⚠ INCOMPLETE DIAGNOSIS — FULL DIAGNOSIS STILL REQUIRED</div>
          <div style={{ fontSize: 11, color: C.text, lineHeight: 1.6 }}>{vehicle.diagnosisIncompleteNote}</div>
          <div style={{ fontSize: 10, color: C.textM, marginTop: 6 }}>Work orders were created based on Hank's working theory at {vehicle.diagnosisConfidence}% confidence. The mechanic should verify each repair before beginning work.</div>
        </div>
      )}

      {/* Sold vehicle repair banner */}
      {vehicle.soldRepairPending && (
        <div style={{ background: C.red + "15", border: `2px solid ${C.red}60`, borderRadius: 10, padding: "12px 16px", marginBottom: 14 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 20 }}>🔧</span>
              <div>
                <div style={{ fontSize: 14, fontWeight: 900, color: C.red, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.05em" }}>CUSTOMER VEHICLE — POST-SALE REPAIR</div>
                {vehicle.customerReturnDate && <div style={{ fontSize: 11, color: C.red, fontWeight: 700 }}>Due back to customer: {fmtDate(vehicle.customerReturnDate)}</div>}
                <div style={{ fontSize: 10, color: C.textM, marginTop: 2 }}>Goal: Repair efficiently and return — no unnecessary extras</div>
              </div>
            </div>

          </div>
          {vehicle.isGoodwillRepair && (
            <div style={{ background: C.amber + "20", border: `1px solid ${C.amber}50`, borderRadius: 7, padding: "7px 10px", fontSize: 11, color: C.amber, fontWeight: 700 }}>
              🤝 Goodwill Service — {vehicle.goodwillFormRequired ? "⚠️ Goodwill form MUST be signed before delivery" : "✓ Form acknowledged"}
            </div>
          )}
          {!vehicle.isGoodwillRepair && vehicle.isGoodwillRepair !== null && (
            <div style={{ fontSize: 11, color: C.textM }}>💰 Billable repair</div>
          )}
          {/* Post-sale cost tally */}
          {(() => {
            const postSaleEntries = ledger.filter(e => e.vehicleId === vehicle.id && (e.category === "POSTSAL" || e.postSale));
            const openWOs = (vehicle.workOrders || []).filter(w => w.soldVehicleRepair && w.status !== "COMPLETE" && !w.deleted);
            const estRepair = openWOs.reduce((s, w) => s + (w.estimatedPartsCost || 0) + ((w.estimatedHours || 0) * LABOR_RATE), 0);
            const towCosts = postSaleEntries.filter(e => e.description?.includes("Tow")).reduce((s, e) => s + e.amount, 0);
            const totalPostSale = postSaleEntries.reduce((s, e) => s + e.amount, 0) + estRepair;
            if (totalPostSale <= 0 && towCosts <= 0) return null;
            return (
              <div style={{ marginTop: 8, paddingTop: 8, borderTop: `1px solid ${C.red}30` }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 4 }}>Post-Sale Repair Cost</div>
                <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                  {towCosts > 0 && <div style={{ fontSize: 11 }}><span style={{ color: C.textM }}>Tow: </span><span style={{ fontWeight: 700 }}>{fmtMoney(towCosts)}</span></div>}
                  {estRepair > 0 && <div style={{ fontSize: 11 }}><span style={{ color: C.textM }}>Est. Repair: </span><span style={{ fontWeight: 700 }}>{fmtMoney(estRepair)}</span></div>}
                  {totalPostSale > 0 && <div style={{ fontSize: 12, fontWeight: 800, color: C.red }}>Total: {fmtMoney(totalPostSale)}</div>}
                </div>
                {vehicle.recoveryOdometer && vehicle.saleOdometer && (
                  <div style={{ fontSize: 10, color: vehicle.recoveryOdometer - vehicle.saleOdometer > 100 ? C.red : C.textM, marginTop: 4, fontWeight: vehicle.recoveryOdometer - vehicle.saleOdometer > 100 ? 700 : 400 }}>
                    {vehicle.recoveryOdometer - vehicle.saleOdometer > 100 ? "⚠️ " : ""}Odometer: {parseInt(vehicle.saleOdometer).toLocaleString()} at sale → {parseInt(vehicle.recoveryOdometer).toLocaleString()} recovered = +{(vehicle.recoveryOdometer - vehicle.saleOdometer).toLocaleString()} mi
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {(profit.costIn > 0 || profit.hasValuation) && (
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 16, background: C.surface, border: `1px solid ${profit.overBudget ? C.red + "55" : C.border}`, borderRadius: 10, padding: "12px 16px" }}>
          {profit.hasCost && (() => {
            const openWOs = (vehicle.workOrders || []).filter(w => w.status !== "COMPLETE" && !w.deleted);
            const estRecon = openWOs.reduce((s, w) => s + (w.estimatedPartsCost || 0) + ((w.estimatedHours || 0) * LABOR_RATE), 0);
            const totalCostPlusRecon = profit.costIn + estRecon;
            return (
              <>
                <div><div style={{ fontSize: 9, color: C.textM, fontWeight: 700, textTransform: "uppercase" }}>Purchase Cost</div><div style={{ fontSize: 18, fontWeight: 800, color: C.amber, fontFamily: "'Bebas Neue', sans-serif" }}>{fmtMoney(profit.costIn)}</div><div style={{ fontSize: 9, color: C.textM }}>pack included</div></div>
                {estRecon > 0 && <div><div style={{ fontSize: 9, color: C.textM, fontWeight: 700, textTransform: "uppercase" }}>Est. Recon</div><div style={{ fontSize: 18, fontWeight: 800, color: C.blue, fontFamily: "'Bebas Neue', sans-serif" }}>{fmtMoney(estRecon)}</div><div style={{ fontSize: 9, color: C.textM }}>open WOs</div></div>}
                {estRecon > 0 && <div><div style={{ fontSize: 9, color: C.textM, fontWeight: 700, textTransform: "uppercase" }}>Total Est. In</div><div style={{ fontSize: 18, fontWeight: 800, color: C.red, fontFamily: "'Bebas Neue', sans-serif" }}>{fmtMoney(totalCostPlusRecon)}</div></div>}
              </>
            );
          })()}
          {profit.reconCost > 0 && <div><div style={{ fontSize: 9, color: C.textM, fontWeight: 700, textTransform: "uppercase" }}>Recon Spend</div><div style={{ fontSize: 18, fontWeight: 800, color: profit.overBudget ? C.red : C.textS, fontFamily: "'Bebas Neue', sans-serif" }}>{fmtMoney(profit.reconCost)}</div></div>}
          {vehicle.kbbValue && <div><div style={{ fontSize: 9, color: C.textM, fontWeight: 700, textTransform: "uppercase" }}>Autoniq Value</div><div style={{ fontSize: 18, fontWeight: 800, color: C.green, fontFamily: "'Bebas Neue', sans-serif" }}>{fmtMoney(vehicle.kbbValue)}</div></div>}
          {vehicle.manualSalePrice && !vehicle.kbbValue && <div><div style={{ fontSize: 9, color: C.textM, fontWeight: 700, textTransform: "uppercase" }}>Est. Sale Price</div><div style={{ fontSize: 18, fontWeight: 800, color: C.green, fontFamily: "'Bebas Neue', sans-serif" }}>{fmtMoney(vehicle.manualSalePrice)}</div></div>}
          {profit.projectedGross !== null && <div><div style={{ fontSize: 9, color: C.textM, fontWeight: 700, textTransform: "uppercase" }}>Proj. Gross</div><div style={{ fontSize: 18, fontWeight: 800, color: profit.projectedGross > TARGET_PROFIT_MIN ? C.green : C.red, fontFamily: "'Bebas Neue', sans-serif" }}>{fmtMoney(profit.projectedGross)}</div></div>}
          {profit.overBudget && <div style={{ fontSize: 10, color: C.red, alignSelf: "center" }}>⚠ Recon at {Math.round(profit.reconPct * 100)}% of projected gross — notify management</div>}
          {!profit.hasValuation && !profit.saleEntry && (
            <div style={{ fontSize: 10, color: C.textM, alignSelf: "center", fontStyle: "italic" }}>Enter a sale price estimate below to see projected gross</div>
          )}
        </div>
      )}

      {/* Manager approval required for >100 mile post-sale repairs */}
      {vehicle.postSaleManagerApprovalRequired && !vehicle.postSaleManagerApproved && (
        <div style={{ background: C.purple + "15", border: `2px solid ${C.purple}60`, borderRadius: 10, padding: "12px 16px", marginBottom: 14 }}>
          <div style={{ fontSize: 13, fontWeight: 900, color: C.purple, marginBottom: 4 }}>⚡ MANAGER APPROVAL REQUIRED</div>
          <div style={{ fontSize: 11, color: C.text, marginBottom: 8 }}>
            Vehicle was driven <strong>{vehicle.odometerDiff?.toLocaleString() || "100+"} miles</strong> since sale ({parseInt(vehicle.saleOdometer || 0).toLocaleString()} → {parseInt(vehicle.recoveryOdometer || 0).toLocaleString()} mi). Repairs on sold vehicles driven over 100 miles require manager approval before work begins.
          </div>
          {currentUser?.role === "manager" && (
            <div style={{ display: "flex", gap: 8 }}>
              <Btn variant="purple" onClick={() => onUpdate({ ...vehicle, postSaleManagerApproved: true, postSaleApprovedBy: currentUser.name || currentUser.username, postSaleApprovedAt: todayStr() })}>
                ✓ Approve Repairs
              </Btn>
              <Btn variant="ghost" onClick={() => onUpdate({ ...vehicle, postSaleManagerApprovalRequired: false })}>Dismiss</Btn>
            </div>
          )}
          {currentUser?.role !== "manager" && <div style={{ fontSize: 11, color: C.purple }}>Ask a manager to approve before proceeding.</div>}
        </div>
      )}


      {/* Primary actions: Add Work Order + Add Detail */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, marginBottom:16 }}>
        <img onClick={() => setAddWOOpen(true)} src="/images/addworkorder.jpg?v=2" alt="Add Work Order"
          style={{ width:"100%", display:"block", cursor:"pointer", borderRadius:10 }} />
        <img onClick={() => setAddChecklistOpen(true)} src="/images/addcleaning.jpg?v=2" alt="Add Detail"
          style={{ width:"100%", display:"block", cursor:"pointer", borderRadius:10 }} />
      </div>

      {/* ── LINE READY MODAL ── */}
      {lineReadyOpen && (() => {
        const openWOs = (vehicle.workOrders||[]).filter(w => w.status !== "COMPLETE" && !w.deleted);
        const safetyKeywords = ["brake","airbag","seatbelt","structural","frame","recall","steering fail","suspension fail","no headlight","no brake light"];
        const safetyWOs = openWOs.filter(w => safetyKeywords.some(k => (w.description||"").toLowerCase().includes(k)));
        const selectedToClose = lrSelectedToClose.length > 0 ? lrSelectedToClose : openWOs.map(w => w.id);
        const unclosedSafetyWOs = safetyWOs.filter(w => !selectedToClose.includes(w.id));
        const blocked = unclosedSafetyWOs.length > 0;
        return (
          <Modal title="← Move to Line Ready" onClose={() => { setLineReadyOpen(false); setLrReason(""); setLrConfirmed(false); setLrSelectedToClose([]); }}>
            <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
              {openWOs.length > 0 && !lrConfirmed && (
                <>
                  <div style={{ fontSize:12, color:C.textM }}>Select which open work orders to close. Unchecked WOs will remain open.</div>
                  {openWOs.map(wo => {
                    const isSafety = safetyKeywords.some(k => (wo.description||"").toLowerCase().includes(k));
                    const checked = selectedToClose.includes(wo.id);
                    return (
                      <div key={wo.id} onClick={() => setLrSelectedToClose(prev => checked ? prev.filter(id=>id!==wo.id) : [...prev, wo.id])}
                        style={{ background:checked?C.green+"10":C.s2, border:`2px solid ${checked?C.green:isSafety&&!checked?C.red:C.border}`, borderRadius:8, padding:"8px 12px", cursor:"pointer", display:"flex", alignItems:"center", gap:10 }}>
                        <div style={{ width:18,height:18,borderRadius:4,background:checked?C.green:"none",border:`2px solid ${checked?C.green:C.border}`,flexShrink:0,display:"flex",alignItems:"center",justifyContent:"center",color:"#fff",fontSize:12 }}>{checked?"✓":""}</div>
                        <div style={{ flex:1 }}>
                          <div style={{ fontSize:12,fontWeight:600,color:"#000" }}>{wo.description}</div>
                          {isSafety && <div style={{ fontSize:9,color:C.red,fontWeight:700 }}>⚠ Safety/drivability issue</div>}
                        </div>
                        <Badge color={wo.priority==="HIGH"?C.red:wo.priority==="MEDIUM"?"#f97316":C.textM} small>{wo.priority}</Badge>
                      </div>
                    );
                  })}
                  {blocked && (
                    <div style={{ background:C.red+"12",border:`1px solid ${C.red}30`,borderRadius:8,padding:"10px 12px",fontSize:11,color:C.red,fontWeight:700 }}>
                      🚫 Cannot move to Line Ready — safety/drivability WO is unchecked: {unclosedSafetyWOs.map(w=>w.description).join(", ")}. Check the box to close it first, or remove the WO.
                    </div>
                  )}
                  {selectedToClose.length > 0 && (
                    <TI label="Reason for closing selected WOs" value={lrReason} onChange={setLrReason} placeholder="e.g. Completed off-ticket, deferred, customer decision..." />
                  )}
                  <div style={{ display:"flex", gap:8 }}>
                    <Btn variant="green" disabled={blocked || (selectedToClose.length>0 && !lrReason.trim())} onClick={() => setLrConfirmed(true)}>
                      {blocked?"🚫 Blocked":"← Move to Line Ready"}
                    </Btn>
                    <Btn variant="ghost" onClick={() => { setLineReadyOpen(false); setLrReason(""); setLrConfirmed(false); }}>Cancel</Btn>
                  </div>
                </>
              )}
              {(openWOs.length === 0 || lrConfirmed) && (
                <div>
                  {lrConfirmed && selectedToClose.length>0 && (
                    <div style={{ background:C.amber+"15",border:`1px solid ${C.amber}30`,borderRadius:7,padding:"8px 12px",fontSize:11,color:C.amber,marginBottom:10 }}>
                      {selectedToClose.length} WO{selectedToClose.length!==1?"s":""} will be closed by {currentUser?.name} — {lrReason}
                    </div>
                  )}
                  <div style={{ fontSize:12,color:C.textM,marginBottom:12 }}>Vehicle will be moved to Line Ready. ✅</div>
                  <Btn variant="green" full onClick={() => {
                    const now = new Date().toISOString();
                    const updatedWOs = (vehicle.workOrders||[]).map(w => {
                      if (selectedToClose.includes(w.id) && w.status!=="COMPLETE" && !w.deleted) {
                        return { ...w, deleted:true, deletedAt:now, deletedBy:currentUser?.username||"manager", deletedReason:lrReason||"Moved to line ready" };
                      }
                      return w;
                    });
                    onUpdate({ ...vehicle, stage:"LINE_READY", lineReadyAt:todayStr(), lineReadyBy:currentUser?.username||"manager", workOrders:updatedWOs });
                    setLineReadyOpen(false); setLrReason(""); setLrConfirmed(false);
                  }}>✅ Confirm</Btn>
                </div>
              )}
            </div>
          </Modal>
        );
      })()}

      {/* ── DETAIL COMPLETE MODAL ── */}
      {detailCompleteOpen && (
        <Modal title="🧹 Detail Complete" onClose={() => { setDetailCompleteOpen(false); setDetailBy(""); setDetailType("inhouse"); }}>
          {(() => {
            const LARGE_TYPES = ["truck","suv","van","pickup","4runner","tahoe","suburban","expedition","navigator","4x4","cargo","transit","express","f-150","f-250","f-350","ram","silverado","sierra","tundra","armada","sequoia","yukon","escalade","traverse","pilot","odyssey","sienna","pacifica","caravan"];
            const isLargeVehicle = LARGE_TYPES.some(t => (vehicle.model||"").toLowerCase().includes(t) || (vehicle.trim||"").toLowerCase().includes(t));
            const externalCost = isLargeVehicle ? 250 : 200;
            return (
              <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
                <SI label="Completed by" value={detailType} onChange={setDetailType}
                  options={[{value:"inhouse",label:"In-house (Sky / staff)"},{value:"external",label:"External detail vendor"}]} />
                {detailType === "inhouse" && (
                  <SI label="Who did the detail?" value={detailBy} onChange={setDetailBy}
                    options={[{value:"",label:"— Select —"}, ...staff.filter(s=>!["manager","viewer"].includes(s.role)).map(s=>({value:s.username,label:s.name}))]} />
                )}
                {detailType === "external" && (
                  <div style={{ background:C.amber+"12",border:`1px solid ${C.amber}30`,borderRadius:8,padding:"10px 14px",fontSize:11 }}>
                    External detail charge: <strong>${externalCost}</strong> ({isLargeVehicle?"large vehicle":"car/small truck"})
                    <div style={{ fontSize:10,color:C.textM,marginTop:2 }}>Will be logged to vehicle ledger automatically.</div>
                  </div>
                )}
                <Btn variant="green" full disabled={detailType==="inhouse"&&!detailBy} onClick={() => {
                  const detailWOs = (vehicle.workOrders||[]).filter(w => w.category==="DETAIL" && w.status!=="COMPLETE" && !w.deleted);
                  const updatedWOs = (vehicle.workOrders||[]).map(w =>
                    detailWOs.find(d => d.id===w.id)
                      ? { ...w, status:"COMPLETE", completedAt:new Date().toISOString(), completedBy: detailType==="external"?"External detail":detailBy }
                      : w
                  );
                  if (detailType==="external" && onAddLedger) {
                    onAddLedger({ id:genId(), vehicleId:vehicle.id, category:"DETAIL", description:`External detail service`, amount:externalCost, date:todayStr(), auto:true });
                  }
                  onUpdate({ ...vehicle, workOrders:updatedWOs, detailComplete:true, detailCompletedAt:todayStr(), detailCompletedBy: detailType==="external"?"External":detailBy });
                  setDetailCompleteOpen(false); setDetailBy(""); setDetailType("inhouse");
                }}>✓ Mark Detail Complete</Btn>
              </div>
            );
          })()}
        </Modal>
      )}

      {/* Re-diagnose modal */}
      {rediagnoseOpen && !rediagAgent && (
        <Modal title="🔁 Re-Diagnose Vehicle" onClose={() => { setRediagnoseOpen(false); setRediagAgent(null); setRediagMode(null); }} wide>
          <div style={{ fontSize: 11, color: C.textM, marginBottom: 16 }}>Choose which specialist to continue with:</div>
          {[
            { id: "julia", label: "Julia", icon: "/images/Julia.jpg", desc: "Full walk-around inspection — exterior, interior, test drive", color: C.purple },
            { id: "hank",  label: "Hank",  icon: "/images/hank-headshot.jpg", desc: "Mechanical diagnosis — engine, brakes, suspension, electrical", color: C.amber },
            { id: "jimmy", label: "Jimmy", icon: "/images/jimmy-working.jpg", desc: "Body work — dents, paint, glass, cosmetic damage", color: C.blue },
          ].map(a => (
            <div key={a.id} onClick={() => setRediagAgent(a.id)}
              style={{ display: "flex", alignItems: "center", gap: 12, background: C.s2, border: `1px solid ${C.border}`, borderRadius: 10, padding: "12px 14px", marginBottom: 10, cursor: "pointer" }}>
              <img src={a.icon} alt={a.label} style={{ width: 48, height: 48, borderRadius: "50%", objectFit: "cover", flexShrink: 0 }} onError={e => { e.target.style.display="none"; }} />
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: a.color }}>{a.label}</div>
                <div style={{ fontSize: 11, color: C.textM }}>{a.desc}</div>
              </div>
              <span style={{ marginLeft: "auto", color: C.textM }}>→</span>
            </div>
          ))}
        </Modal>
      )}
      {rediagnoseOpen && rediagAgent && !rediagMode && (() => {
        const hasHistory = rediagAgent === "julia" ? vehicle.juliaInspectionSummary : rediagAgent === "hank" ? vehicle.conditionSummary : vehicle.bodyRepairs?.length > 0;
        return (
          <Modal title={`🔁 Re-Diagnose — ${rediagAgent.charAt(0).toUpperCase() + rediagAgent.slice(1)}`} onClose={() => { setRediagnoseOpen(false); setRediagAgent(null); setRediagMode(null); }} wide>
            <button onClick={() => setRediagAgent(null)} style={{ background: "none", border: "none", color: C.textM, cursor: "pointer", fontSize: 11, marginBottom: 16, padding: 0 }}>← Back</button>
            {hasHistory ? (
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ fontSize: 12, color: C.textM, marginBottom: 4 }}>Previous work was found for this vehicle. How would you like to proceed?</div>
                <button onClick={() => setRediagMode("continue")} style={{ background: C.green + "15", border: `1px solid ${C.green}40`, borderRadius: 10, padding: "14px 16px", cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.green }}>▶ Continue where we left off</div>
                  <div style={{ fontSize: 11, color: C.textM, marginTop: 4 }}>The agent will know everything already found and pick up from there</div>
                </button>
                <button onClick={() => setRediagMode("restart")} style={{ background: C.amber + "15", border: `1px solid ${C.amber}40`, borderRadius: 10, padding: "14px 16px", cursor: "pointer", textAlign: "left", fontFamily: "inherit" }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: C.amber }}>↺ Start over</div>
                  <div style={{ fontSize: 11, color: C.textM, marginTop: 4 }}>Fresh inspection — existing work orders are preserved but the agent starts fresh</div>
                </button>
              </div>
            ) : (
              <div>
                <div style={{ fontSize: 12, color: C.textM, marginBottom: 16 }}>No previous inspection found. Starting fresh.</div>
                <Btn variant="amber" onClick={() => setRediagMode("restart")}>Start Inspection</Btn>
              </div>
            )}
          </Modal>
        );
      })()}
      {rediagnoseOpen && rediagAgent && rediagMode && (() => {
        const vehicleWithContext = rediagMode === "continue" ? {
          ...vehicle,
          _rediagContext: rediagAgent === "julia" ? (vehicle.juliaInspectionSummary ? `Previous inspection found: ${JSON.stringify(vehicle.juliaInspectionSummary)}` : "") : (vehicle.conditionSummary || ""),
          _rediagMode: "continue",
        } : { ...vehicle, _rediagMode: "restart" };
        const closeAll = () => { setRediagnoseOpen(false); setRediagAgent(null); setRediagMode(null); };
        const mergeWOs = (newWOs) => { onUpdate({ ...vehicle, workOrders: [...(vehicle.workOrders || []), ...newWOs.map(w => ({ ...w, addedByRediagnosis: true, rediagnosisDate: todayStr() }))] }); closeAll(); };
        if (rediagAgent === "julia") return (
          <Modal title="🔁 Julia — Re-Inspection" onClose={closeAll} wide>
            <div style={{ marginBottom: 12, padding: "10px 14px", background: C.amber + "12", border: `1px solid ${C.amber}40`, borderRadius: 8, fontSize: 11, color: C.amber }}>⚠ New findings will be added as work orders. Existing WOs are preserved.</div>
            <JuliaInspectionChat vehicle={vehicleWithContext} updateVehicle={onUpdate}
              onInspectionComplete={(upd, newWOs) => mergeWOs(newWOs)} onClose={closeAll} />
          </Modal>
        );
        return (
          <Modal title={`🔁 ${rediagAgent.charAt(0).toUpperCase() + rediagAgent.slice(1)} — Re-Diagnosis`} onClose={closeAll} wide>
            <div style={{ marginBottom: 12, padding: "10px 14px", background: C.amber + "12", border: `1px solid ${C.amber}40`, borderRadius: 8, fontSize: 11, color: C.amber }}>⚠ New findings will be added as work orders. Existing WOs are preserved.</div>
            <DiagnosticChat vehicle={vehicleWithContext}
              onDiagnosisComplete={(upd, newWOs) => mergeWOs(newWOs)}
              onClose={closeAll} staff={staff} vehicles={vehicles} vendors={vendors}
              addLedger={onAddLedger} addPart={onAddPart} updateVehicle={() => {}} />
          </Modal>
        );
      })()}

      {selectedWO && (
        <WorkOrderDetailModal
          wo={selectedWO}
          vehicle={vehicle}
          parts={parts}
          vendors={vendors}
          staff={staff}
          catalog={catalog}
          currentUser={currentUser}
          onClose={() => setSelectedWO(null)}
          onUpdateWO={(updatedWO) => {
            onUpdate({ ...vehicle, workOrders: (vehicle.workOrders || []).map(w => w.id === updatedWO.id ? updatedWO : w) });
            setSelectedWO(updatedWO);
          }}
          onUpdateVehicle={onUpdate}
          onAddPart={onAddPart}
          onAddLedger={onAddLedger}
          onShopForPart={onShopForPart}
          onPrintWO={(wo) => { setSelectedWO(null); setPrintWOData(wo); }}
        />
      )}

      {addWOOpen && (
        <AIWorkOrderModal
          vehicle={vehicle}
          currentUser={currentUser}
          staff={staff}
          onUpdateVehicle={onUpdate}
          onTriggerCalendarRebuild={onTriggerCalendarRebuild}
          onAddLedger={onAddLedger}
          onAdd={(wos) => {
            const approved = wos.filter(w => w._approved);
            if (approved.length) {
              const updatedWOs = approved.map(w => {
                const cat = w.category; const desc = (w.description || "").toLowerCase(); const hrs = w.estimatedHours || 0;
                const noLiftQuick = /bulb|wiper|blade|fluid|top off|air filter|cabin filter|fuse|battery terminal|tire pressure|tpms reset|mirror|antenna|emblem/i;
                const assignedTo = cat === "DETAIL" ? "sky" : cat === "VENDOR" ? null : (hrs <= 0.35 && noLiftQuick.test(desc)) ? "robert" : "james";
                return { id: genId(), ...w, createdAt: todayStr(), actualHours: 0, createdBy: currentUser?.username || "unknown", assignedTo, requiresApproval: currentUser?.role !== "manager", approved: currentUser?.role === "manager" };
              });
              onUpdate({ ...vehicle, workOrders: [...(vehicle.workOrders || []), ...updatedWOs] });
            }
            setAddWOOpen(false);
          }}
          onClose={() => setAddWOOpen(false)}
        />
      )}
      {addChecklistOpen && (
        <Modal title="🧼 Add Detail Work Order" onClose={() => setAddChecklistOpen(false)}>
          <div style={{ marginBottom:16 }}>
            <div style={{ fontSize:13, fontWeight:800, color:"#000", marginBottom:6 }}>
              {vehicle.year} {vehicle.make} {vehicle.model} #{vehicle.stockNumber}
            </div>
            <div style={{ fontSize:11, color:C.textM, marginBottom:14 }}>
              This will create a Detail work order for Sky and generate a cleaning checklist.
            </div>
            <div style={{ fontSize:12, fontWeight:800, color:"#000", marginBottom:10 }}>Make this high priority?</div>
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              <button onClick={() => {
                // HIGH PRIORITY — goes next in Sky's queue ahead of other non-high work
                const newWO = {
                  id: genId(), category: "DETAIL", description: "Full vehicle detail", priority: "HIGH",
                  status: "OPEN", createdAt: todayStr(), estimatedHours: 3, actualHours: 0,
                  assignedTo: "sky", diagnosisRationale: "Priority detail requested",
                };
                onUpdate({ ...vehicle, workOrders: [...(vehicle.workOrders||[]), newWO] });
                setAddChecklistOpen(false);
                // Open checklist generation
                setTimeout(() => onOpenChecklist({ ...vehicle, workOrders: [...(vehicle.workOrders||[]), newWO] }), 200);
              }}
                style={{ background:C.red+"15", border:`2px solid ${C.red}50`, borderRadius:10, padding:"14px 16px", cursor:"pointer", fontFamily:"inherit", textAlign:"left" }}>
                <div style={{ fontSize:13, fontWeight:800, color:C.red }}>🔴 Yes — High Priority</div>
                <div style={{ fontSize:11, color:C.textM, marginTop:2 }}>Moves to front of Sky's queue, behind other HIGH priority details only</div>
              </button>
              <button onClick={() => {
                // MEDIUM PRIORITY — normal queue position
                const newWO = {
                  id: genId(), category: "DETAIL", description: "Full vehicle detail", priority: "MEDIUM",
                  status: "OPEN", createdAt: todayStr(), estimatedHours: 3, actualHours: 0,
                  assignedTo: "sky", diagnosisRationale: "Detail requested",
                };
                onUpdate({ ...vehicle, workOrders: [...(vehicle.workOrders||[]), newWO] });
                setAddChecklistOpen(false);
                setTimeout(() => onOpenChecklist({ ...vehicle, workOrders: [...(vehicle.workOrders||[]), newWO] }), 200);
              }}
                style={{ background:C.green+"12", border:`2px solid ${C.green}40`, borderRadius:10, padding:"14px 16px", cursor:"pointer", fontFamily:"inherit", textAlign:"left" }}>
                <div style={{ fontSize:13, fontWeight:800, color:C.green }}>🟢 No — Normal Queue</div>
                <div style={{ fontSize:11, color:C.textM, marginTop:2 }}>Added to end of Sky's current schedule</div>
              </button>
              <button onClick={() => setAddChecklistOpen(false)}
                style={{ background:"none", border:`1px solid ${C.border}`, borderRadius:10, padding:"10px", cursor:"pointer", fontSize:12, color:C.textM, fontFamily:"inherit" }}>
                Cancel
              </button>
            </div>
          </div>
        </Modal>
      )}
      {towOpen && (
        <Modal title="🚛 Request Tow" onClose={() => setTowOpen(false)}>
          <TowRequestModal vehicle={vehicle} vendors={vendors} currentUser={currentUser} isSoldVehicle={vehicle.stage === "SOLD"}
            onRecordTowCost={(cost) => {
              onAddLedger({ id: genId(), vehicleId: vehicle.id, category: "TOW", description: "Tow service — Gordo's Towing", amount: cost, date: todayStr(), auto: false });
            }}
            onClose={() => setTowOpen(false)} />
        </Modal>
      )}

      {bodyWorkOpen && (
        <Modal title="🔨 Body Work Assessment" onClose={() => setBodyWorkOpen(false)} wide>
          <BodyWorkChat
            vehicle={vehicle}
            staff={staff}
            vendors={vendors}
            onComplete={(newWOs, repairsData) => {
              const updatedVehicle = {
                ...vehicle,
                workOrders: [...(vehicle.workOrders || []), ...newWOs],
                bodyRepairs: [...(vehicle.bodyRepairs || []), { id: genId(), date: todayStr(), summary: repairsData.summary, tasks: repairsData.tasks, needsBodyShop: repairsData.needsBodyShop, unsafeToDrive: repairsData.unsafeToDrive, unsafeToDriveReason: repairsData.unsafeToDriveReason, status: "OPEN" }],
                unsafeToDrive: vehicle.unsafeToDrive || repairsData.unsafeToDrive,
              };
              onUpdate(updatedVehicle);
              setBodyWorkOpen(false);
            }}
            onClose={() => setBodyWorkOpen(false)}
          />
        </Modal>
      )}

      {scrapOpen && (
        <Modal title="🗑 Scrap Vehicle" onClose={() => setScrapOpen(false)}>
          <ScrapVehicleModal
            vehicle={vehicle}
            currentUser={staff?.find(s => s.id === vehicle.currentUserId) || staff?.[0]}
            onConfirmScrap={(scrappedVehicle) => {
              onUpdate(scrappedVehicle);
              setScrapOpen(false);
              onBack();
            }}
            onClose={() => setScrapOpen(false)}
          />
        </Modal>
      )}


      {/* Return to Customer confirmation modal */}
      {returnToCustomerOpen && (() => {
        const openWOs = (vehicle.workOrders || []).filter(w => w.status !== "COMPLETE" && !w.deleted);
        const openParts = parts.filter(p => p.vehicleId === vehicle.id && !["Installed","RETURNED","CANCELLED"].includes(p.status));
        return (
          <Modal title="✅ Vehicle Returned to Customer" onClose={() => setReturnToCustomerOpen(false)}>
            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 10 }}>{vehicle.year} {vehicle.make} {vehicle.model} #{vehicle.stockNumber}</div>
              {vehicle.isGoodwillRepair && !vehicle.goodwillFormSigned && (
                <div style={{ background: C.red + "15", border: `1px solid ${C.red}40`, borderRadius: 8, padding: "10px 14px", marginBottom: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: C.red }}>⚠️ Goodwill Service Form</div>
                  <div style={{ fontSize: 11, color: C.text }}>Has the customer signed the Goodwill Service form?</div>
                  <label style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 8, cursor: "pointer" }}>
                    <input type="checkbox" onChange={e => { if (e.target.checked) onUpdate({ ...vehicle, goodwillFormSigned: true }); }} style={{ accentColor: C.green }} />
                    <span style={{ fontSize: 12, fontWeight: 700 }}>Yes — form has been signed</span>
                  </label>
                </div>
              )}
              {openWOs.length > 0 && (
                <div style={{ background: C.amber + "12", border: `1px solid ${C.amber}40`, borderRadius: 8, padding: "10px 14px", marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.amber, marginBottom: 6 }}>⚠ {openWOs.length} open WO{openWOs.length !== 1 ? "s" : ""} will be closed</div>
                  {openWOs.map((w, i) => <div key={i} style={{ fontSize: 10, color: C.text }}>{w.description}</div>)}
                </div>
              )}
              {openParts.length > 0 && (
                <div style={{ background: C.blue + "10", border: `1px solid ${C.blue}30`, borderRadius: 8, padding: "10px 14px", marginBottom: 12 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.blue, marginBottom: 4 }}>📦 {openParts.length} part{openParts.length !== 1 ? "s" : ""} will be flagged for return</div>
                  {openParts.map((p, i) => <div key={i} style={{ fontSize: 10, color: C.text }}>{p.description}</div>)}
                </div>
              )}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={() => {
                // Close all open WOs — strip incomplete, keep only completed
                onUpdate({ ...vehicle,
                  soldRepairPending: false,
                  vehicleReturnedAt: todayStr(),
                  workOrders: (vehicle.workOrders || []).filter(w => w.status === "COMPLETE")
                });
                // Flag parts for return — strip vehicle/WO linkage
                parts.filter(p => p.vehicleId === vehicle.id && !["Installed","RETURNED","CANCELLED"].includes(p.status))
                  .forEach(p => onUpdatePart({ ...p, pendingReturn: true, pendingReturnAt: todayStr(), vehicleId: null, workOrderId: null, vehicleRef: null }));
                setReturnToCustomerOpen(false);
              }}
                style={{ background: C.green, border: "none", borderRadius: 8, padding: "10px 18px", cursor: "pointer", fontSize: 12, fontWeight: 800, color: "#fff", fontFamily: "inherit" }}>
                ✓ Confirm — Vehicle Returned
              </button>
              <Btn variant="ghost" onClick={() => setReturnToCustomerOpen(false)}>Cancel</Btn>
            </div>
          </Modal>
        );
      })()}

      {/* Mark Sold modal — multi-step */}
      {markSoldOpen && (() => {
        const openWOs = (vehicle.workOrders || []).filter(w => w.status !== "COMPLETE" && !w.deleted);
        const hasOpenWOs = openWOs.length > 0;

        const doMarkSold = (afterSaleRepair = false) => {
          if (!saleInput || parseFloat(saleInput) <= 0) return;
          const sp = parseFloat(saleInput);
          onAddLedger({ id: genId(), vehicleId: vehicle.id, category: "SALE", description: "Vehicle sold", amount: sp, date: todayStr(), auto: true });
          // On sale: keep only COMPLETED work orders. Remove all open/unfinished ones.
          // After-sale repair vehicles keep all WOs for the repair work.
          const finalWOs = afterSaleRepair
            ? (vehicle.workOrders || []).map(w =>
                w.category === "DETAIL" && !w.soldVehicleRepair && w.status !== "COMPLETE"
                  ? { ...w, status: "COMPLETE", completedAt: todayStr(), closedAtSale: true }
                  : w
              )
            : (vehicle.workOrders || []).filter(w =>
                // Keep: completed WOs (full history preserved)
                w.status === "COMPLETE"
              );
            // Open WOs are removed entirely — no record of unfinished/unaddressed work
          // Parts handling on sale:
          // - Ordered/In Transit (never arrived) → return queue
          // - Received (arrived but not installed) → general stock
          if (!afterSaleRepair && onUpdatePart) {
            parts.filter(p => p.vehicleId === vehicle.id && !["Installed","RETURNED","CANCELLED"].includes(p.status))
              .forEach(p => {
                const neverArrived = ["Ordered","In Transit"].includes(p.status);
                onUpdatePart({
                  ...p,
                  pendingReturn: neverArrived,         // ordered but never used → return
                  pendingReturnAt: neverArrived ? todayStr() : null,
                  generalStock: !neverArrived,          // received parts go to general stock
                  vehicleId: null,
                  workOrderId: null,
                  vehicleRef: `${vehicle.year} ${vehicle.make} ${vehicle.model} #${vehicle.stockNumber} (sold ${todayStr()})`,
                  location: neverArrived ? (p.location || "unassigned") : "unassigned",
                  soldVehicleReturn: neverArrived,
                });
              });
          }
          onUpdate({
            ...vehicle,
            stage: "SOLD",
            saleDate: todayStr(),
            salePrice: sp,
            saleOdometer: parseFloat(saleOdometer) || parseInt(vehicle.mileage) || null,
            previousStage: vehicle.stage,
            soldRepairPending: afterSaleRepair,
            repairLocation: afterSaleRepair ? soldRepairLocation : null,
            currentLocation: afterSaleRepair ? soldRepairLocation : "main_lot",
            workOrders: finalWOs,
          });
          resetMarkSold();
        };

        return (
          <Modal title="✅ Mark Vehicle Sold" onClose={resetMarkSold}>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text, marginBottom: 12 }}>{vehicle.year} {vehicle.make} {vehicle.model} #{vehicle.stockNumber}</div>

            {/* Step 1: Sale details */}
            {markSoldStep === "details" && (
              <div>
                <TI label="Final Sale Price ($) *" value={saleInput} onChange={setSaleInput} type="number" placeholder="e.g. 14500" />
                <TI label="Odometer at Time of Sale (miles) *" value={saleOdometer} onChange={setSaleOdometer} type="number" placeholder="e.g. 87432" />
                {saleInput && parseFloat(saleInput) > 0 && (() => {
                  const sp = parseFloat(saleInput);
                  const totalIn = ledger.filter(e => e.vehicleId === vehicle.id && e.category !== "SALE").reduce((s, e) => s + (e.amount || 0), 0);
                  const gross = sp - totalIn;
                  return (
                    <div style={{ background: gross >= 0 ? C.green + "12" : C.red + "12", border: `1px solid ${gross >= 0 ? C.green : C.red}40`, borderRadius: 8, padding: "10px 14px", marginTop: 10, marginBottom: 10 }}>
                      <div style={{ display: "flex", gap: 16 }}>
                        <div><div style={{ fontSize: 9, color: C.textM, textTransform: "uppercase" }}>Total Cost In</div><div style={{ fontSize: 16, fontWeight: 800, color: C.amber, fontFamily: "'Bebas Neue', sans-serif" }}>{fmtMoney(totalIn)}</div></div>
                        <div><div style={{ fontSize: 9, color: C.textM, textTransform: "uppercase" }}>Gross Profit</div><div style={{ fontSize: 16, fontWeight: 800, color: gross >= 0 ? C.green : C.red, fontFamily: "'Bebas Neue', sans-serif" }}>{fmtMoney(gross)}</div></div>
                      </div>
                    </div>
                  );
                })()}
                <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
                  <Btn variant="green" onClick={() => {
                    if (!saleInput || parseFloat(saleInput) <= 0) return;
                    if (hasOpenWOs) setMarkSoldStep("closewos");
                    else doMarkSold(false);
                  }} disabled={!saleInput || parseFloat(saleInput) <= 0}>
                    Next →
                  </Btn>
                  <Btn variant="ghost" onClick={resetMarkSold}>Cancel</Btn>
                </div>
              </div>
            )}

            {/* Step 2: Handle open WOs */}
            {markSoldStep === "closewos" && (
              <div>
                <div style={{ background: C.amber + "12", border: `1px solid ${C.amber}40`, borderRadius: 8, padding: "12px 14px", marginBottom: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: C.amber, marginBottom: 8 }}>⚠️ {openWOs.length} Open Work Order{openWOs.length !== 1 ? "s" : ""}</div>
                  {openWOs.map((w, i) => (
                    <div key={i} style={{ fontSize: 11, color: C.text, padding: "3px 0", borderBottom: `1px solid ${C.border}` }}>
                      <span style={{ fontWeight: 600 }}>{w.description}</span>
                      <span style={{ color: C.textM }}> · {w.category} · {w.priority}</span>
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.text, marginBottom: 12 }}>Close all open work orders?</div>
                <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                  <button onClick={() => doMarkSold(false)}
                    style={{ background: C.green + "15", border: `1px solid ${C.green}50`, borderRadius: 8, padding: "12px 14px", cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: C.green }}>✓ Yes — Close All & Mark Sold</div>
                    <div style={{ fontSize: 10, color: C.textM, marginTop: 2 }}>All open WOs will be marked complete. Vehicle is ready for delivery.</div>
                  </button>
                  <button onClick={() => setMarkSoldStep("aftersale")}
                    style={{ background: C.amber + "12", border: `1px solid ${C.amber}50`, borderRadius: 8, padding: "12px 14px", cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: C.amber }}>🔧 No — This is an After-Sale Repair</div>
                    <div style={{ fontSize: 10, color: C.textM, marginTop: 2 }}>Vehicle has already been sold and a repair is needed. Initiate post-sale repair protocol.</div>
                  </button>
                </div>
                <Btn variant="ghost" onClick={() => setMarkSoldStep("details")} style={{ marginTop: 8 }}>← Back</Btn>
              </div>
            )}

            {/* Step 3: After-sale repair setup */}
            {markSoldStep === "aftersale" && (
              <div>
                <div style={{ background: C.red + "10", border: `2px solid ${C.red}40`, borderRadius: 8, padding: "12px 14px", marginBottom: 14 }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: C.red, marginBottom: 6 }}>🔧 Post-Sale Repair Protocol</div>
                  <div style={{ fontSize: 11, color: C.text, lineHeight: 1.5 }}>
                    This vehicle will be marked SOLD with open work orders retained. It will be treated as a customer vehicle — repairs only, no auto-added detail WOs, and will go ahead of inventory units in the queue.
                  </div>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 6 }}>Repair Location</div>
                  <select defaultValue="mech_shop"
                    onChange={e => setSoldRepairLocation(e.target.value)}
                    style={{ width: "100%", background: C.s2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "8px 12px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none" }}>
                    {TOW_LOCATIONS.map(l => <option key={l.id} value={l.id}>{l.label}</option>)}
                  </select>
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button onClick={() => doMarkSold(true)}
                    style={{ background: C.red, border: "none", borderRadius: 8, padding: "10px 18px", cursor: "pointer", fontSize: 12, fontWeight: 800, color: "#fff", fontFamily: "inherit" }}>
                    ⚠️ Mark Sold + Initiate Repair
                  </button>
                  <Btn variant="ghost" onClick={() => setMarkSoldStep("closewos")}>← Back</Btn>
                </div>
              </div>
            )}
          </Modal>
        );
      })()}

      {/* Work Orders — always visible above the tabs */}
      {(vehicle.workOrders?.length > 0 || true) && (
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: C.textM, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 8 }}>📋 Work Orders</div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 12 }}>
            <div style={{ fontSize: 11, color: C.textS }}>{vehicle.workOrders?.filter(w => w.status === "COMPLETE" && !w.closedAtSale).length || 0} completed</div>
            <Btn variant="outline" size="sm" onClick={() => setAddWOOpen(true)}><Plus size={11} /> Add WO</Btn>
          </div>
          {isSold && !vehicle.soldRepairPending && (
            <div style={{ background: C.s2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 11, color: C.textM }}>
              Work orders closed at time of sale. Use "Initiate After-Sale Repair" on the Overview tab for post-sale work.
            </div>
          )}

          {/* Soft-deleted WOs — show as single line tombstone */}
          {vehicle.workOrders?.filter(w => w.deleted).map(wo => (
            <div key={wo.id} style={{ fontSize: 10, color: C.red, opacity: 0.65, marginBottom: 4, paddingLeft: 8, borderLeft: `2px solid ${C.red}40`, lineHeight: 1.5 }}>
              🗑 <em>{wo.description}</em> — deleted by {wo.deletedBy}{wo.deletedAtTime ? ` on ${wo.deletedAtTime}` : wo.deletedAt ? ` on ${fmtDate(wo.deletedAt)}` : ""}
            </div>
          ))}

          {!vehicle.workOrders?.filter(w => !w.deleted).length && <div style={{ textAlign: "center", color: C.textM, padding: 32, fontSize: 12 }}>No work orders. Use AI Diagnostics or add manually.</div>}
          {(isSold && !vehicle.soldRepairPending
              ? (vehicle.workOrders || []).filter(w => !w.deleted && ((w.status === "COMPLETE" && !w.closedAtSale) || w.soldVehicleRepair))
              : (vehicle.workOrders || []).filter(w => !w.deleted)
            ).map(wo => {
            const cat = WO_CATS.find(c => c.id === wo.category);
            const assigned = wo.assignedVendorId ? vendors.find(v => v.id === wo.assignedVendorId) : null;
            const assignedStaff = wo.assignedTo ? staff?.find(s => s.username === wo.assignedTo || s.id === wo.assignedTo) : null;
            const woExpanded = expandedWO === wo.id;
            const woParts = parts.filter(p => p.vehicleId === vehicle.id && (p.workOrderId === wo.id || p.description?.toLowerCase().slice(0,15) === wo.description?.toLowerCase().slice(0,15)));
            const createdByName = wo.createdBy ? (staff?.find(s => s.username === wo.createdBy)?.name || wo.createdBy) : "System";
            const diagnosedByName = wo.diagnosedBy ? (staff?.find(s => s.username === wo.diagnosedBy)?.name || wo.diagnosedBy) : null;

            return (
              <div key={wo.id} style={{ background: C.s2, border: `1px solid ${wo.status === "COMPLETE" ? C.green + "55" : C.border}`, borderRadius: 8, marginBottom: 8, overflow: "hidden" }}>
                {/* WO Header — click to open detail modal */}
                <div style={{ padding: "11px 14px", cursor: "pointer", display: "flex", alignItems: "flex-start", gap: 8 }}
                  onClick={() => setSelectedWO(wo)}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", gap: 5, marginBottom: 4, flexWrap: "wrap" }}>
                      <Badge color={cat?.color || C.textM} small>{cat?.label || wo.category}</Badge>
                      {/* Priority — tap to cycle: LOW → MEDIUM → HIGH → LOW */}
                      <button onClick={e => {
                        e.stopPropagation();
                        const order = ["LOW","MEDIUM","HIGH"];
                        const next = order[(order.indexOf(wo.priority||"MEDIUM")+1)%3];
                        const stamp = `Priority changed to ${next} by ${currentUser?.name||currentUser?.username} on ${new Date().toLocaleString("en-US",{month:"2-digit",day:"2-digit",year:"2-digit",hour:"numeric",minute:"2-digit",hour12:true})}`;
                        onUpdate({ ...vehicle, workOrders: vehicle.workOrders.map(w => w.id===wo.id ? { ...w, priority:next, priorityChangedBy:currentUser?.username, priorityChangedAt:new Date().toISOString(), priorityHistory:[...(w.priorityHistory||[]),stamp] } : w) });
                      }} style={{ background:(wo.priority==="HIGH"?C.red:wo.priority==="MEDIUM"?"#f97316":C.textM)+"20", border:`1px solid ${wo.priority==="HIGH"?C.red:wo.priority==="MEDIUM"?"#f97316":C.textM}40`, borderRadius:4, padding:"1px 6px", cursor:"pointer", fontSize:9, fontWeight:700, color:wo.priority==="HIGH"?C.red:wo.priority==="MEDIUM"?"#f97316":C.textM, fontFamily:"inherit" }}>
                        {wo.priority||"MEDIUM"} ⟳
                      </button>
                      {wo.status === "COMPLETE" && <Badge color={C.green} small>✓ DONE</Badge>}
                      {wo.status === "IN_PROGRESS" && <Badge color={C.blue} small>⚡ IN PROGRESS</Badge>}
                      {wo.status === "IN_PROGRESS" && (() => { const lt = detectLiftType(wo.description); return lt!=="ground" ? <Badge color={C.amber} small>{lt==="scissor"?"🔧 Scissor":"🔧 2-Post Lift"}</Badge> : null; })()}
                      {assigned && <Badge color="#ec4899" small>→ {assigned.name}</Badge>}
                      {wo.diagnosisRationale && <Badge color={C.purple} small>AI diagnosed</Badge>}
                      {wo.addedByRediagnosis && <Badge color={C.blue} small>Re-diagnosis</Badge>}
                      {wo.allPartsIn ? (
                        <Badge color={C.green} small>✅ All Parts In</Badge>
                      ) : wo.partsETA ? (
                        <Badge color={C.amber} small>📦 Parts ETA {fmtDate(wo.partsETA)}</Badge>
                      ) : null}
                    </div>
                    {wo.userInput && (
                      <div style={{ fontSize: 11, color: "#f97316", fontWeight: 700, fontStyle: "italic", marginBottom: 3 }}>
                        💬 "{wo.userInput}"
                      </div>
                    )}
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>{wo.description}</span>
                      <span style={{ fontSize: 10, color: C.textM, flexShrink: 0, marginLeft: 8 }}>{woExpanded ? "▲" : "▼"}</span>
                    </div>
                    {wo.incompleteDiagnosis && <div style={{ fontSize: 9, color: C.amber, fontWeight: 700, marginTop: 2 }}>⚠ Based on incomplete diagnosis</div>}
                  </div>
                  {/* Trash can — stop propagation so it doesn't open the modal */}
                  <button onClick={e => {
                    e.stopPropagation();
                    if (window.confirm(`Delete work order:\n"${wo.description}"\n\nThis cannot be undone.`)) {
                      const now = new Date();
                      const timeStr = now.toLocaleString("en-US", { month: "2-digit", day: "2-digit", year: "2-digit", hour: "numeric", minute: "2-digit", hour12: true });
                      const deletedWO = { ...wo, deleted: true, deletedBy: currentUser?.name || currentUser?.username || "Unknown", deletedAt: todayStr(), deletedAtTime: timeStr };
                      onUpdate({ ...vehicle, workOrders: vehicle.workOrders.map(w => w.id === wo.id ? deletedWO : w) });
                    }
                  }}
                    style={{ background: "none", border: "none", cursor: "pointer", padding: "2px 4px", color: C.textM, flexShrink: 0, opacity: 0.5, fontSize: 15, lineHeight: 1 }}
                    title="Delete this work order">
                    🗑
                  </button>
                </div>

                {/* WO Expanded Detail */}
                {woExpanded && (
                  <div style={{ borderTop: `1px solid ${C.border}`, padding: "14px 14px" }}>

                    {/* Priority change history */}
                    {wo.priorityHistory?.length > 0 && (
                      <div style={{ background:C.amber+"10",border:`1px solid ${C.amber}30`,borderRadius:6,padding:"6px 10px",marginBottom:10,fontSize:9,color:C.amber }}>
                        {wo.priorityHistory.map((h,i) => <div key={i}>{h}</div>)}
                      </div>
                    )}
                    {/* Diagnosis / Why */}
                    {wo.diagnosisRationale && (
                      <div style={{ background: C.purple + "10", border: `1px solid ${C.purple}30`, borderRadius: 6, padding: "8px 10px", marginBottom: 10 }}>
                        <div style={{ fontSize: 9, fontWeight: 800, color: C.purple, textTransform: "uppercase", marginBottom: 3 }}>📋 Why This WO</div>
                        <div style={{ fontSize: 11, color: C.textS, fontStyle: "italic" }}>{wo.diagnosisRationale}</div>
                      </div>
                    )}

                    {/* Who & When */}
                    <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 10, fontSize: 10, color: C.textM }}>
                      <div><strong>Created by:</strong> {createdByName} · {fmtDate(wo.createdAt)}</div>
                      {diagnosedByName && <div><strong>Diagnosed by:</strong> {diagnosedByName}</div>}
                      {assignedStaff && <div><strong>Assigned to:</strong> {assignedStaff.name}</div>}
                      {wo.status === "COMPLETE" && wo.completedAt && <div><strong>Completed:</strong> {fmtDate(wo.completedAt)}</div>}
                    </div>

                    {/* Time & Cost Estimates */}
                    {(wo.estimatedHours > 0 || wo.estimatedPartsCost > 0) && (
                      <div style={{ display: "flex", gap: 12, marginBottom: 10, flexWrap: "wrap" }}>
                        {wo.estimatedHours > 0 && <div style={{ background: C.blue + "15", borderRadius: 6, padding: "6px 10px", fontSize: 11 }}><span style={{ fontWeight: 700, color: C.blue }}>⏱ {wo.estimatedHours}h</span> <span style={{ color: C.textM }}>labor = {fmtMoney(wo.estimatedHours * LABOR_RATE)}</span></div>}
                        {wo.estimatedPartsCost > 0 && <div style={{ background: C.amber + "15", borderRadius: 6, padding: "6px 10px", fontSize: 11 }}><span style={{ fontWeight: 700, color: C.amber }}>🔩 {fmtMoney(wo.estimatedPartsCost)}</span> <span style={{ color: C.textM }}>est. parts</span></div>}
                      </div>
                    )}

                    {/* Parts status for this WO */}
                    {woParts.length > 0 && (
                      <div style={{ marginBottom: 10 }}>
                        <div style={{ fontSize: 9, fontWeight: 800, color: "#000", textTransform: "uppercase", marginBottom: 6 }}>Parts</div>
                        {woParts.map(p => {
                          const vendor = vendors.find(v => v.id === p.supplier);
                          const statusColor = p.status === "Received" || p.status === "Installed" ? C.green : p.status === "Ordered" || p.status === "In Transit" ? C.amber : C.textM;
                          return (
                            <div key={p.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderBottom: `1px solid ${C.border}`, fontSize: 11 }}>
                              <div>
                                <div style={{ fontWeight: 600, color: C.text }}>{p.description}</div>
                                <div style={{ fontSize: 10, color: C.textM }}>{vendor?.name || "—"} · {fmtMoney(p.cost)} · ETA {fmtDate(p.eta)}</div>
                              </div>
                              <Badge color={statusColor} small>{p.status}</Badge>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {/* Work history — time entries */}
                    {(() => {
                      const woEntries = (vehicle._workLog || []).filter(e => e.woId === wo.id);
                      if (!woEntries.length) return null;
                      return (
                        <div style={{ marginBottom: 10 }}>
                          <div style={{ fontSize: 9, fontWeight: 800, color: "#000", textTransform: "uppercase", marginBottom: 6 }}>Work History</div>
                          {woEntries.map((e, i) => (
                            <div key={i} style={{ fontSize: 10, color: C.textM, padding: "3px 0" }}>
                              {e.staffName} — {fmtDate(e.date)} · {e.hours}h
                            </div>
                          ))}
                        </div>
                      );
                    })()}

                    {/* Actions row */}
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
                      <select value={wo.status} onChange={e => updateWO(wo.id, { status: e.target.value, ...(e.target.value === "COMPLETE" ? { completedAt: todayStr() } : {}) })} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 4, padding: "3px 8px", color: C.textS, fontSize: 11, fontFamily: "inherit", outline: "none" }}>
                        {["OPEN","IN_PROGRESS","AWAITING_PARTS","COMPLETE","EXTERNAL"].map(s => <option key={s}>{s.replace("_"," ")}</option>)}
                      </select>
                      {currentUser?.role === "manager" && (
                        <button onClick={() => {
                          if (window.confirm(`Delete work order: "${wo.description}"?`)) {
                            onUpdate({ ...vehicle, workOrders: (vehicle.workOrders || []).map(w => w.id === wo.id ? { ...w, deleted: true, deletedBy: currentUser.name || currentUser.username, deletedAt: todayStr() } : w) });
                          }
                        }} style={{ background: C.red + "15", border: `1px solid ${C.red}40`, borderRadius: 5, padding: "3px 10px", cursor: "pointer", fontSize: 10, color: C.red, fontFamily: "inherit" }}>
                          🗑 Delete
                        </button>
                      )}
                      <button onClick={() => {
                        const win = window.open("", "_blank");
                        const assignee = wo.assignedTo === "ruban" ? "Ruban — Blue Diamond Auto Detail" : assignedStaff?.name || wo.assignedTo || "Unassigned";
                        win.document.write(`<html><body style="font-family:Arial;padding:24px;max-width:600px"><h2 style="margin:0 0 4px">EDGEWATER AUTO CENTER</h2><p style="margin:0 0 16px;color:#555;font-size:12px">4250 Salem Dallas Hwy NW · Salem, OR 97304 · (971) 374-8173</p><hr/><h3>REPAIR ORDER — ${vehicle.year} ${vehicle.make} ${vehicle.model}</h3><p>Stock #${vehicle.stockNumber} · VIN: ${vehicle.vin || "—"} · ${vehicle.mileage ? parseInt(vehicle.mileage).toLocaleString() + " miles" : ""}</p><p><strong>Assigned To:</strong> ${assignee}</p><p><strong>Date:</strong> ${fmtDate(wo.createdAt || todayStr())}</p><hr/><p><strong>Work Description:</strong> ${wo.description}</p>${wo.diagnosisRationale ? `<p><em>Reason: ${wo.diagnosisRationale}</em></p>` : ""}${wo.estimatedHours > 0 ? `<p>Est. Hours: ${wo.estimatedHours}h</p>` : ""}${wo.estimatedPartsCost > 0 ? `<p>Est. Parts: $${wo.estimatedPartsCost}</p>` : ""}<hr/><p>Technician Signature: _________________________ Date: _____________</p></body></html>`);
                        win.document.close(); win.print();
                      }} style={{ background: C.blue + "15", border: `1px solid ${C.blue}40`, borderRadius: 5, padding: "3px 10px", cursor: "pointer", fontSize: 10, color: C.blue, fontFamily: "inherit" }}>🖨 Print</button>
                    </div>

                    {/* Cleaning List button for DETAIL WOs */}
                    {wo.category === "DETAIL" && (
                      <button onClick={() => {
                        const baseChecklist = vehicle.cleaningChecklist;
                        const woChecklist = wo.checklist;
                        let mergedChecklist = woChecklist || baseChecklist;
                        if (baseChecklist && woChecklist && baseChecklist.phases) {
                          mergedChecklist = { ...baseChecklist, phases: baseChecklist.phases.map(phase => { const woPhase = woChecklist.phases?.find(p => p.title === phase.title); return { ...phase, items: phase.items.map(item => { const woItem = woPhase?.items?.find(i => i.id === item.id || i.text === item.text); return woItem ? { ...item, done: woItem.done } : item; }) }; }) };
                        }
                        if (mergedChecklist) { const updatedWOs = (vehicle.workOrders || []).map(w => w.id === wo.id ? { ...w, checklist: mergedChecklist } : w); onUpdate({ ...vehicle, workOrders: updatedWOs }); }
                        onOpenChecklist({ ...vehicle, cleaningChecklist: mergedChecklist || vehicle.cleaningChecklist || null });
                      }} style={{ background: C.purple + "15", border: `1px solid ${C.purple}40`, borderRadius: 5, padding: "4px 12px", cursor: "pointer", fontSize: 11, fontWeight: 700, color: C.purple, fontFamily: "inherit", marginTop: 4, display: "block" }}>
                        🧹 Cleaning List
                      </button>
                    )}

                    <WOPhotoStrip photos={wo.photos || []} onAdd={addWOPhoto} onRemove={removeWOPhoto} vehicleId={vehicle.id} woId={wo.id} onAIRoute={handleAIRoute} woCategory={wo.category} />
                  </div>
                )}
              </div>
            );
          })}

        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", background: C.s2, borderRadius: 8, padding: 3, marginBottom: 16, border: `1px solid ${C.border}`, overflowX: "auto" }}>
        {["overview","parts","ledger","notes"].map(t => (
          <button key={t} onClick={() => setTab(t)} style={{ flex: 1, padding: "7px 0", border: "none", borderBottom: `2px solid ${tab === t ? C.amber : "transparent"}`, borderRadius: 5, background: tab === t ? C.s3 : "transparent", color: tab === t ? C.amber : C.textM, fontSize: 10, fontWeight: 700, fontFamily: "inherit", cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.06em", whiteSpace: "nowrap" }}>
            {t}
            {t === "parts" && vehicleParts.length > 0 ? ` (${vehicleParts.length})` : ""}
          </button>
        ))}
      </div>

      {tab === "overview" && (
        <div>
          {/* ── SOLD VEHICLE OVERVIEW ── */}
          {isSold && (() => {
            const soldParts = vehicleParts; // all parts on this vehicle
            const partsCost = soldParts.reduce((s, p) => s + (p.cost || 0), 0);
            const laborCost = vehicleLedger.filter(e => e.category === "LABOR").reduce((s, e) => s + e.amount, 0);
            const toolsCost = vehicleLedger.filter(e => e.category === "TOOLS").reduce((s, e) => s + e.amount, 0);
            const suppliesCost = vehicleLedger.filter(e => e.category === "SUPPLIES").reduce((s, e) => s + e.amount, 0);
            const towCost = vehicleLedger.filter(e => e.category === "POSTSAL" || e.description?.toLowerCase().includes("tow")).reduce((s, e) => s + e.amount, 0);
            const totalSpent = partsCost + laborCost + toolsCost + suppliesCost + towCost;
            return (
              <div style={{ marginBottom: 16 }}>
                {/* Initiate After-Sale Repair button — only shown if not already a repair */}
                {!vehicle.soldRepairPending && (
                  <button onClick={() => setAddWOOpen(true)}
                    style={{ width: "100%", padding: "14px", background: C.amber, border: "none", borderRadius: 10, color: "#000", fontSize: 14, fontWeight: 900, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.06em", cursor: "pointer", marginBottom: 14, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                    🔧 INITIATE AFTER-SALE REPAIR
                  </button>
                )}

                {/* Vehicle record card */}
                <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 16px", marginBottom: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 800, color: "#000", textTransform: "uppercase", marginBottom: 4 }}>Sale Record</div>
                      <div style={{ fontSize: 12, color: C.text }}>Sold {fmtDate(vehicle.saleDate)} for <strong>{fmtMoney(vehicle.salePrice || 0)}</strong></div>
                      {vehicle.saleOdometer && <div style={{ fontSize: 11, color: C.textM }}>Odometer at sale: {parseInt(vehicle.saleOdometer).toLocaleString()} mi</div>}
                      {vehicle.previousStage && <div style={{ fontSize: 10, color: C.textM }}>Was in stage: {STAGES.find(s => s.id === vehicle.previousStage)?.label || vehicle.previousStage}</div>}
                    </div>
                    {currentUser?.role === "manager" && (
                      <button onClick={() => {
                        if (window.confirm("Move this vehicle back to active inventory? It will need to be reinspected and reconditioned.")) {
                          onUpdate({ ...vehicle, stage: vehicle.previousStage || "INTAKE", soldRepairPending: false, saleDate: null, salePrice: null, previousStage: null, restoredFromSoldAt: todayStr(), restoredBy: currentUser.name || currentUser.username });
                        }
                      }} style={{ background: C.amber + "15", border: `1px solid ${C.amber}40`, borderRadius: 7, padding: "6px 12px", cursor: "pointer", fontSize: 10, fontWeight: 700, color: C.amber, fontFamily: "inherit", flexShrink: 0 }}>
                        ↩ Move Back to Inventory
                      </button>
                    )}
                  </div>
                  <div style={{ fontSize: 10, color: C.textM, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}>
                    Moved from live inventory on {fmtDate(vehicle.saleDate)}
                    {vehicle.vehicleReturnedAt && <span> · Returned to customer {fmtDate(vehicle.vehicleReturnedAt)}</span>}
                  </div>
                </div>

                {/* Actual spend summary — parts clickable for detail */}
                <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 16px" }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: "#000", textTransform: "uppercase", marginBottom: 10 }}>What We Spent on This Vehicle</div>
                  <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 10 }}>
                    {/* Parts — clickable */}
                    {partsCost > 0 && (
                      <button onClick={() => setPartsExpanded(!partsExpanded)}
                        style={{ background: partsExpanded ? C.amber + "20" : C.s2, border: `1px solid ${partsExpanded ? C.amber : C.border}`, borderRadius: 8, padding: "8px 12px", cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
                        <div style={{ fontSize: 9, color: C.textM, textTransform: "uppercase" }}>Parts</div>
                        <div style={{ fontSize: 18, fontWeight: 800, color: C.amber, fontFamily: "'Bebas Neue', sans-serif" }}>{fmtMoney(partsCost)}</div>
                        <div style={{ fontSize: 9, color: C.blue }}>{partsExpanded ? "▲ hide" : "▼ see breakdown"}</div>
                      </button>
                    )}
                    {laborCost > 0 && <div><div style={{ fontSize: 9, color: C.textM, textTransform: "uppercase" }}>Labor</div><div style={{ fontSize: 18, fontWeight: 800, color: C.blue, fontFamily: "'Bebas Neue', sans-serif" }}>{fmtMoney(laborCost)}</div></div>}
                    {toolsCost > 0 && <div><div style={{ fontSize: 9, color: C.textM, textTransform: "uppercase" }}>Spec. Tools</div><div style={{ fontSize: 18, fontWeight: 800, color: C.purple, fontFamily: "'Bebas Neue', sans-serif" }}>{fmtMoney(toolsCost)}</div></div>}
                    {suppliesCost > 0 && <div><div style={{ fontSize: 9, color: C.textM, textTransform: "uppercase" }}>Supplies</div><div style={{ fontSize: 18, fontWeight: 800, color: C.textM, fontFamily: "'Bebas Neue', sans-serif" }}>{fmtMoney(suppliesCost)}</div></div>}
                    {towCost > 0 && <div><div style={{ fontSize: 9, color: C.textM, textTransform: "uppercase" }}>Tow</div><div style={{ fontSize: 18, fontWeight: 800, color: C.red, fontFamily: "'Bebas Neue', sans-serif" }}>{fmtMoney(towCost)}</div></div>}
                    {totalSpent > 0 && <div style={{ borderLeft: `2px solid ${C.border}`, paddingLeft: 14 }}><div style={{ fontSize: 9, color: C.textM, textTransform: "uppercase" }}>Total Spent</div><div style={{ fontSize: 18, fontWeight: 800, color: C.text, fontFamily: "'Bebas Neue', sans-serif" }}>{fmtMoney(totalSpent)}</div></div>}
                  </div>

                  {/* Expandable parts list */}
                  {partsExpanded && soldParts.length > 0 && (
                    <div style={{ borderTop: `1px solid ${C.border}`, paddingTop: 10 }}>
                      {soldParts.map((p, i) => {
                        const vendor = vendors.find(v => v.id === p.supplier);
                        return (
                          <div key={p.id} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderBottom: i < soldParts.length - 1 ? `1px solid ${C.border}` : "none", fontSize: 11 }}>
                            <div>
                              <div style={{ fontWeight: 600, color: C.text }}>{p.description}</div>
                              <div style={{ fontSize: 10, color: C.textM }}>
                                {vendor?.name || p.source || "Unknown vendor"} · Ordered {fmtDate(p.orderedAt || p.createdAt)}
                                {p.orderedBy && ` · by ${p.orderedBy}`}
                                {p.partNumber && ` · PN: ${p.partNumber}`}
                                {p.invoiceNumber && ` · Inv#${p.invoiceNumber}`}
                              </div>
                            </div>
                            <div style={{ fontWeight: 700, color: C.amber, flexShrink: 0, marginLeft: 10 }}>{fmtMoney(p.cost || 0)}</div>
                          </div>
                        );
                      })}
                      <div style={{ textAlign: "right", paddingTop: 6, fontSize: 12, fontWeight: 800, color: C.amber }}>Total: {fmtMoney(partsCost)}</div>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Management approval panel */}
          {vehicle.pendingApproval?.status === "PENDING" && (
            <div style={{ background: C.purple + "15", border: `3px solid ${C.purple}60`, borderRadius: 12, padding: "14px 16px", marginBottom: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 900, color: C.purple, marginBottom: 6 }}>⚡ MANAGEMENT APPROVAL REQUIRED</div>
              <div style={{ fontSize: 12, color: "#000", marginBottom: 8, lineHeight: 1.6 }}>
                Estimated repair cost of <strong>{fmtMoney(vehicle.pendingApproval.repairTotal)}</strong> is <strong>{vehicle.pendingApproval.pctOfValue}%</strong> of estimated vehicle value ({fmtMoney(vehicle.pendingApproval.vehicleValue)}). This exceeds the 25% threshold and requires manager approval before work orders are assigned to James.
              </div>
              <div style={{ display: "flex", gap: 8 }}>
                <Btn variant="green" onClick={() => onUpdate({ ...vehicle, pendingApproval: { ...vehicle.pendingApproval, status: "APPROVED", approvedBy: "Manager", approvedAt: todayStr() } })}>
                  <CheckCircle size={13} /> Approve Repairs
                </Btn>
                <Btn variant="ghost" size="sm" onClick={() => onUpdate({ ...vehicle, pendingApproval: { ...vehicle.pendingApproval, status: "DENIED" } })}>
                  Deny
                </Btn>
              </div>
            </div>
          )}

          {/* Market value + repair cost summary — hide for sold vehicles */}
          {!isSold && (vehicle.estimatedMarketValue || vehicle.repairEstimateAI) && (
            <div style={{ background: C.surface, border: `3px solid ${C.border}`, borderRadius: 12, padding: "14px 16px", marginBottom: 14 }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: "#000", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>Value & Repair Estimate</div>
              <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
                {vehicle.estimatedMarketValue && (
                  <div>
                    <div style={{ fontSize: 9, color: "#444", fontWeight: 700, textTransform: "uppercase" }}>Est. Market Value</div>
                    <div style={{ fontSize: 22, fontWeight: 900, color: C.green, fontFamily: "'Bebas Neue', sans-serif" }}>{fmtMoney(vehicle.estimatedMarketValue)}</div>
                    {vehicle.marketValueNote && <div style={{ fontSize: 9, color: "#666" }}>{vehicle.marketValueNote}</div>}
                  </div>
                )}
                {vehicle.repairEstimateAI && (
                  <div>
                    <div style={{ fontSize: 9, color: "#444", fontWeight: 700, textTransform: "uppercase" }}>Est. Repair Cost</div>
                    <div style={{ fontSize: 22, fontWeight: 900, color: vehicle.repairEstimateAI > (vehicle.estimatedMarketValue || 99999) * APPROVAL_THRESHOLD ? C.red : C.amber, fontFamily: "'Bebas Neue', sans-serif" }}>{fmtMoney(vehicle.repairEstimateAI)}</div>
                    {vehicle.repairEstimateBreakdown && (
                      <div style={{ fontSize: 9, color: "#666" }}>
                        Labor: {fmtMoney(vehicle.repairEstimateBreakdown.laborTotal)} · Parts: {fmtMoney(vehicle.repairEstimateBreakdown.partsTotal)}{vehicle.repairEstimateBreakdown.toolsTotal > 0 ? ` · Tools: ${fmtMoney(vehicle.repairEstimateBreakdown.toolsTotal)}` : ""}
                      </div>
                    )}
                  </div>
                )}
                {vehicle.estimatedMarketValue && vehicle.repairEstimateAI && (
                  <div>
                    <div style={{ fontSize: 9, color: "#444", fontWeight: 700, textTransform: "uppercase" }}>Repair % of Value</div>
                    <div style={{ fontSize: 22, fontWeight: 900, color: (vehicle.repairEstimateAI / vehicle.estimatedMarketValue) > APPROVAL_THRESHOLD ? C.red : C.green, fontFamily: "'Bebas Neue', sans-serif" }}>
                      {Math.round((vehicle.repairEstimateAI / vehicle.estimatedMarketValue) * 100)}%
                    </div>
                    <div style={{ fontSize: 9, color: "#666" }}>25% threshold</div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Vehicle info grid */}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
            {[{ l: "Exterior", v: vehicle.exteriorColor },{ l: "Interior", v: vehicle.interiorColor },{ l: "Source", v: vehicle.vendor },{ l: "Entered By", v: vehicle.enteredBy || vehicle.buyer },{ l: "Purchase Date", v: fmtDate(vehicle.purchaseDate) },{ l: "Arrival", v: fmtDate(vehicle.arrivalDate) },{ l: "Days on Lot", v: `${daysSince(vehicle.arrivalDate)} days` },{ l: "Mileage", v: vehicle.mileage ? parseInt(vehicle.mileage).toLocaleString() + " mi" : null }].filter(x => x.v).map(x => (
              <div key={x.l} style={{ background: C.surface, border: `2px solid #b0b8c8`, borderRadius: 6, padding: "8px 12px" }}>
                <div style={{ fontSize: 9, color: "#555", fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.1em" }}>{x.l}</div>
                <div style={{ fontSize: 12, color: "#000", fontWeight: 600, marginTop: 2 }}>{x.v}</div>
              </div>
            ))}
          </div>

          {/* ── VEHICLE SPECIFICS — editable tech specs for parts/diagnosis ── */}
          <SpecsEditor vehicle={vehicle} onUpdate={onUpdate} />

          {/* Work orders summary */}
          {(vehicle.workOrders || []).length > 0 && (
            <div style={{ background: C.surface, border: `2px solid #b0b8c8`, borderRadius: 10, padding: "12px 14px", marginBottom: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: "#000", textTransform: "uppercase", marginBottom: 8 }}>Work Orders ({vehicle.workOrders.filter(w => w.status !== "COMPLETE").length} open)</div>
              {vehicle.workOrders.filter(w => w.status !== "COMPLETE").slice(0, 4).map(w => (
                <div key={w.id} style={{ display: "flex", justifyContent: "space-between", padding: "4px 0", borderBottom: `1px solid ${C.border}` }}>
                  <span style={{ fontSize: 11, color: "#000" }}>{w.description}</span>
                  <Badge color={w.priority === "HIGH" ? C.red : w.priority === "MEDIUM" ? "#f97316" : C.textM} small>{w.priority}</Badge>
                </div>
              ))}
              {vehicle.workOrders.filter(w => w.status !== "COMPLETE").length > 4 && (
                <div style={{ fontSize: 10, color: C.textM, marginTop: 4 }}>+{vehicle.workOrders.filter(w => w.status !== "COMPLETE").length - 4} more — see Work Orders tab</div>
              )}
            </div>
          )}

          {/* Parts summary */}
          {vehicleParts.length > 0 && (
            <div style={{ background: C.surface, border: `2px solid #b0b8c8`, borderRadius: 10, padding: "12px 14px", marginBottom: 12 }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: "#000", textTransform: "uppercase", marginBottom: 6 }}>Parts ({vehicleParts.length})</div>
              <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                {["Ordered","In Transit","Received","Installed"].map(s => {
                  const cnt = vehicleParts.filter(p => p.status === s).length;
                  if (!cnt) return null;
                  return <div key={s}><span style={{ fontSize: 11, fontWeight: 700, color: "#000" }}>{cnt}</span><span style={{ fontSize: 10, color: "#555" }}> {s}</span></div>;
                })}
                <div><span style={{ fontSize: 11, fontWeight: 700, color: C.amber }}>{fmtMoney(vehicleParts.reduce((s, p) => s + (p.cost || 0), 0))}</span><span style={{ fontSize: 10, color: "#555" }}> total</span></div>
              </div>
            </div>
          )}

          {vehicle.conditionSummary && (
            <div style={{ background: C.surface, border: `2px solid #b0b8c8`, borderRadius: 8, padding: "10px 14px", marginBottom: 10 }}>
              <div style={{ fontSize: 9, color: "#555", fontWeight: 800, textTransform: "uppercase", marginBottom: 4 }}>Condition Summary</div>
              <div style={{ fontSize: 12, color: "#000", lineHeight: 1.6 }}>{vehicle.conditionSummary}</div>
            </div>
          )}

          {/* Julia full inspection report */}
          {vehicle.juliaInspectionSummary && vehicle.juliaInspectionSummary.overallCondition !== "INCOMPLETE" && (() => {
            const js = vehicle.juliaInspectionSummary;
            return (
              <div style={{ background: C.surface, border: `2px solid #b0b8c8`, borderRadius: 10, padding: "12px 14px", marginBottom: 12 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: "#000", textTransform: "uppercase" }}>👩‍💼 Julia's Inspection Report</div>
                  <Badge color={js.overallCondition === "EXCELLENT" ? C.green : js.overallCondition === "GOOD" ? C.blue : js.overallCondition === "FAIR" ? C.amber : C.red} small>{js.overallCondition}</Badge>
                </div>
                {[["Exterior", js.exteriorNotes], ["Interior", js.interiorNotes], ["Tires", js.tireNotes], ["Mechanical", js.mechanicalNotes]].filter(([,v]) => v).map(([label, text]) => (
                  <div key={label} style={{ marginBottom: 8 }}>
                    <div style={{ fontSize: 9, fontWeight: 700, color: "#555", textTransform: "uppercase", marginBottom: 2 }}>{label}</div>
                    <div style={{ fontSize: 11, color: "#000", lineHeight: 1.5 }}>{text}</div>
                  </div>
                ))}
                {js.allIssues?.length > 0 && (
                  <div style={{ marginTop: 8, borderTop: `1px solid ${C.border}`, paddingTop: 8 }}>
                    <div style={{ fontSize: 9, fontWeight: 800, color: "#555", textTransform: "uppercase", marginBottom: 6 }}>Issues Found — Dispatched To</div>
                    {js.allIssues.map((issue, i) => (
                      <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "5px 0", borderBottom: i < js.allIssues.length - 1 ? `1px solid ${C.border}` : "none", gap: 8 }}>
                        <span style={{ color: "#000", fontSize: 11, flex: 1 }}>{issue.description}</span>
                        <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                          <Badge color={issue.severity === "HIGH" ? C.red : issue.severity === "MEDIUM" ? "#f97316" : C.textM} small>{issue.severity}</Badge>
                          <Badge color={issue.assignTo === "James" ? C.blue : issue.assignTo === "Sky" ? C.purple : issue.assignTo === "Vendor" ? C.green : C.textM} small>→ {issue.assignTo || issue.woCategory}</Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          {vehicle.safetyFlags?.length > 0 && <div style={{ background: C.red + "12", border: `1px solid ${C.red}40`, borderRadius: 8, padding: "10px 14px" }}><div style={{ fontSize: 9, color: C.red, fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>⚠ Safety</div>{vehicle.safetyFlags.map((f, i) => <div key={i} style={{ fontSize: 12, color: C.red }}>• {f}</div>)}</div>}
        </div>
      )}


      {tab === "parts" && (
        <div>
          {/* Compiled parts needed — from WO instructions (specific parts) and estimates */}
          {(() => {
            const diagnosisKeywords = /^diagnos|^inspect|check engine|^scan|^assess|^evaluat|^test\s|^look at|^investigate|^determine|^identify/i;
            const allWOs = (vehicle.workOrders || []).filter(wo => wo.status !== "COMPLETE" && !wo.deleted && wo.category !== "DETAIL");

            // Collect specific parts from WO instructions
            const specificParts = [];
            allWOs.forEach(wo => {
              if (wo.instructions?.requiredParts?.length) {
                wo.instructions.requiredParts.forEach(p => {
                  const ordered = vehicleParts.find(op =>
                    op.description?.toLowerCase().includes(p.name?.toLowerCase().slice(0,12))
                  );
                  specificParts.push({ woId: wo.id, woDesc: wo.description, part: p, ordered, wo });
                });
              }
            });

            // WOs with estimated cost but no specific parts yet (and not diagnosis-only)
            const estimatedWOs = allWOs.filter(wo =>
              wo.estimatedPartsCost > 0 &&
              !wo.instructions?.requiredParts?.length &&
              !diagnosisKeywords.test(wo.description.trim())
            );

            if (!specificParts.length && !estimatedWOs.length) return null;

            return (
              <div style={{ background: C.s2, border: `1px solid ${C.amber}40`, borderRadius: 10, padding: "12px 14px", marginBottom: 14 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}>
                  <div style={{ fontSize: 10, fontWeight: 800, color: "#000", textTransform: "uppercase" }}>
                    🔩 Required Parts ({specificParts.length + estimatedWOs.length})
                  </div>
                  <div style={{ fontSize: 10, color: C.textM }}>
                    Est: {fmtMoney(allWOs.reduce((s, w) => s + (w.estimatedPartsCost || 0), 0))}
                  </div>
                </div>

                {/* Specific parts from instructions */}
                {specificParts.map(({ woId, woDesc, part, ordered, wo }, i) => (
                  <div key={`${woId}-${i}`} onClick={() => onShopForPart && !ordered && onShopForPart(
                    `I need: ${part.name}${part.partNumber ? ` (OEM# ${part.partNumber})` : ""} for a ${vehicle.year} ${vehicle.make} ${vehicle.model}${vehicle.trim ? " " + vehicle.trim : ""}. Job: ${woDesc}`,
                    vehicle
                  )}
                  style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", padding: "8px 0", borderBottom: `1px solid ${C.border}`, cursor: ordered ? "default" : "pointer", background: ordered ? C.green + "08" : "transparent", borderRadius: ordered ? 4 : 0 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 12, fontWeight: 600, color: ordered ? C.green : C.text }}>
                        {ordered && "✓ "}{part.qty > 1 ? `${part.qty}x ` : ""}{part.name}
                      </div>
                      {part.partNumber && <div style={{ fontSize: 10, color: C.textM }}>OEM: {part.partNumber}</div>}
                      {part.notes && <div style={{ fontSize: 10, color: C.textM }}>{part.notes}</div>}
                      <div style={{ fontSize: 10, color: C.textM, fontStyle: "italic" }}>For: {woDesc.slice(0, 50)}{woDesc.length > 50 ? "…" : ""}</div>
                      {ordered && <div style={{ fontSize: 10, color: C.green, fontWeight: 700 }}>✓ {ordered.status} · ETA {fmtDate(ordered.eta)}</div>}
                    </div>
                    {ordered
                      ? <Badge color={C.green} small>{ordered.status}</Badge>
                      : <span style={{ fontSize: 10, color: C.amber, fontWeight: 700, flexShrink: 0, marginLeft: 8 }}>🛒 Tap to shop</span>
                    }
                  </div>
                ))}

                {/* WOs with estimated cost but no specific parts — click to open WO detail or shop */}
                {estimatedWOs.map(wo => {
                  const ordered = vehicleParts.some(p => p.description?.toLowerCase().slice(0,20) === wo.description?.toLowerCase().slice(0,20));
                  const cat = WO_CATS.find(c => c.id === wo.category);
                  return (
                    <div key={wo.id} style={{ padding: "8px 0", borderBottom: `1px solid ${C.border}` }}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                        <span style={{ background: cat?.color + "25", color: cat?.color, fontSize: 9, fontWeight: 800, padding: "1px 6px", borderRadius: 8, textTransform: "uppercase" }}>{cat?.label}</span>
                        {ordered && <span style={{ fontSize: 9, color: C.green, fontWeight: 700 }}>✓ Ordered</span>}
                      </div>
                      <div style={{ fontSize: 12, color: C.text, fontWeight: 600, marginBottom: 4 }}>{wo.description}</div>
                      <div style={{ fontSize: 10, color: C.textM, marginBottom: 6 }}>
                        Est: {fmtMoney(wo.estimatedPartsCost)}
                        {wo.estimatedHours > 0 && ` · ${wo.estimatedHours}h labor = ${fmtMoney(wo.estimatedHours * LABOR_RATE)}`}
                        {" · "}
                        <span style={{ color: C.purple, cursor: "pointer" }} onClick={() => setSelectedWO(wo)}>Open WO to generate parts list</span>
                      </div>
                      {!ordered && onShopForPart && (
                        <button onClick={() => onShopForPart(`Parts for: ${wo.description} on ${vehicle.year} ${vehicle.make} ${vehicle.model}${vehicle.engine ? " " + vehicle.engine : ""}`, vehicle)}
                          style={{ background: C.amber, border: "none", borderRadius: 7, padding: "5px 10px", cursor: "pointer", fontSize: 10, fontWeight: 700, color: "#000", fontFamily: "inherit" }}>
                          🛒 Shop with Roxy
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}
          {/* Quick order a part directly on this vehicle */}
          <QuickOrderPart vehicle={vehicle} vendors={vendors} onAddPart={(p) => { onAddPart({ ...p, binSize: estimateBinSize(p.description) }); if (p.cost > 0) onAddLedger({ id: genId(), vehicleId: vehicle.id, category: "PARTS", description: `Parts: ${p.description}`, amount: p.cost, date: todayStr(), auto: true }); }} />
          {vehicleParts.length === 0 ? <div style={{ textAlign: "center", color: C.textM, padding: 24, fontSize: 12 }}>No parts ordered yet.</div> :
            vehicleParts.map(part => {
              const vendor = vendors.find(v => v.id === part.supplier);
              const ks = vehicle.stage === "SOLD" && !["Installed","RETURNED","CANCELLED"].includes(part.status);
              return (
                <div key={part.id} style={{ background: ks ? C.red + "12" : C.s2, border: `1px solid ${ks ? C.red + "55" : C.border}`, borderRadius: 8, padding: "10px 14px", marginBottom: 8 }}>
                  {ks && <div style={{ fontSize: 10, color: C.red, fontWeight: 800, textTransform: "uppercase", marginBottom: 3 }}>⚠ VEHICLE SOLD — RETURN</div>}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>{part.description}</div>
                      <div style={{ fontSize: 11, color: C.textS }}>{vendor?.name} · {fmtMoneyFull(part.cost)} · ETA {fmtDate(part.eta)}</div>
                      {(() => { const bs = part.binSize || estimateBinSize(part.description); const bd = BIN_SIZES.find(b => b.id === bs); return bd ? <div style={{ fontSize: 10, color: bs === "OVERSIZE" ? C.red : C.textM, fontWeight: bs === "OVERSIZE" ? 800 : 400, marginTop: 2 }}>{bd.icon} {bd.label} bin{bs === "OVERSIZE" ? " — ⚠ OVERSIZE: store separately" : ""}</div> : null; })()}
                    </div>
                    <select value={part.status} onChange={e => onUpdatePart({ ...part, status: e.target.value })} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 4, padding: "4px 8px", color: C.textS, fontSize: 11, fontFamily: "inherit", outline: "none", marginLeft: 10 }}>{["Ordered","In Transit","Received","Installed","RETURNED","CANCELLED"].map(s => <option key={s}>{s}</option>)}</select>
                  </div>
                </div>
              );
            })
          }
        </div>
      )}

      {tab === "ledger" && (
        <div>
          <div style={{ background: C.surface, border: `1px solid ${profit.overBudget ? C.red + "40" : C.border}`, borderRadius: 10, padding: "14px 18px", marginBottom: 16 }}>
            {!isSold && (
              <div style={{ display: "flex", gap: 20, flexWrap: "wrap", marginBottom: 12 }}>
                {LEDGER_CATS.filter(lc => lc.id !== "SALE").map(lc => { const sub = vehicleLedger.filter(e => e.category === lc.id).reduce((s, e) => s + e.amount, 0); if (!sub) return null; return <div key={lc.id}><div style={{ fontSize: 9, color: C.textM, fontWeight: 700, textTransform: "uppercase" }}>{lc.label}</div><div style={{ fontSize: 16, fontWeight: 800, color: lc.color, fontFamily: "'Bebas Neue', sans-serif" }}>{fmtMoney(sub)}</div></div>; })}
              </div>
            )}
            <div style={{ display: "flex", gap: 20, borderTop: `1px solid ${C.border}`, paddingTop: 12, flexWrap: "wrap" }}>
              {vehicle.soldRepairPending ? (() => {
                // Customer vehicle — show post-sale repair cost only
                const towCosts = vehicleLedger.filter(e => e.category === "POSTSAL" || e.description?.includes("Tow")).reduce((s, e) => s + (e.amount || 0), 0);
                const partsCosts = vehicleLedger.filter(e => e.category === "PARTS" && e.postSale).reduce((s, e) => s + (e.amount || 0), 0);
                const laborCosts = vehicleLedger.filter(e => e.category === "LABOR" && e.postSale).reduce((s, e) => s + (e.amount || 0), 0);
                const openWOsCost = (vehicle.workOrders || []).filter(w => w.soldVehicleRepair && w.status !== "COMPLETE" && !w.deleted)
                  .reduce((s, w) => s + (w.estimatedPartsCost || 0) + ((w.estimatedHours || 0) * LABOR_RATE), 0);
                const totalPostSale = towCosts + partsCosts + laborCosts + openWOsCost;
                return (
                  <>
                    {towCosts > 0 && <div><div style={{ fontSize: 9, color: C.textM, fontWeight: 700, textTransform: "uppercase" }}>Tow</div><div style={{ fontSize: 22, fontWeight: 800, color: C.red, fontFamily: "'Bebas Neue', sans-serif" }}>{fmtMoney(towCosts)}</div></div>}
                    {openWOsCost > 0 && <div><div style={{ fontSize: 9, color: C.textM, fontWeight: 700, textTransform: "uppercase" }}>Est. Repairs</div><div style={{ fontSize: 22, fontWeight: 800, color: C.amber, fontFamily: "'Bebas Neue', sans-serif" }}>{fmtMoney(openWOsCost)}</div></div>}
                    {totalPostSale > 0 && <div><div style={{ fontSize: 9, color: C.textM, fontWeight: 700, textTransform: "uppercase" }}>Total Post-Sale Cost</div><div style={{ fontSize: 22, fontWeight: 800, color: C.red, fontFamily: "'Bebas Neue', sans-serif" }}>{fmtMoney(totalPostSale)}</div></div>}
                  </>
                );
              })() : (
                <>
                  <div><div style={{ fontSize: 9, color: C.textM, fontWeight: 700, textTransform: "uppercase" }}>Total In</div><div style={{ fontSize: 22, fontWeight: 800, color: C.amber, fontFamily: "'Bebas Neue', sans-serif" }}>{fmtMoney(profit.costIn)}</div></div>
                  {profit.saleEntry && <div><div style={{ fontSize: 9, color: C.textM, fontWeight: 700, textTransform: "uppercase" }}>Sold For</div><div style={{ fontSize: 22, fontWeight: 800, color: C.green, fontFamily: "'Bebas Neue', sans-serif" }}>{fmtMoney(profit.salePrice)}</div></div>}
                  {profit.currentGross !== undefined && profit.saleEntry && <div><div style={{ fontSize: 9, color: C.textM, fontWeight: 700, textTransform: "uppercase" }}>Gross Profit</div><div style={{ fontSize: 22, fontWeight: 800, color: profit.currentGross >= 0 ? C.green : C.red, fontFamily: "'Bebas Neue', sans-serif" }}>{fmtMoney(profit.currentGross)}</div></div>}
                </>
              )}
            </div>
            {/* Repair estimate breakdown */}
            {vehicle.repairEstimateBreakdown && (
              <div style={{ marginTop: 12, borderTop: `1px solid ${C.border}`, paddingTop: 12 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 8 }}>AI Repair Estimate</div>
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 8 }}>
                  <div><div style={{ fontSize: 9, color: C.textM, fontWeight: 700, textTransform: "uppercase" }}>Labor</div><div style={{ fontSize: 16, fontWeight: 800, color: C.blue, fontFamily: "'Bebas Neue', sans-serif" }}>{fmtMoney(vehicle.repairEstimateBreakdown.laborTotal)}</div><div style={{ fontSize: 9, color: C.textM }}>{vehicle.repairEstimateBreakdown.repairs?.reduce((s,r) => s + (r.hours || 0), 0).toFixed(1)}h total</div></div>
                  <div><div style={{ fontSize: 9, color: C.textM, fontWeight: 700, textTransform: "uppercase" }}>Parts</div><div style={{ fontSize: 16, fontWeight: 800, color: C.amber, fontFamily: "'Bebas Neue', sans-serif" }}>{fmtMoney(vehicle.repairEstimateBreakdown.partsTotal)}</div></div>
                  {vehicle.repairEstimateBreakdown.toolsTotal > 0 && <div><div style={{ fontSize: 9, color: C.textM, fontWeight: 700, textTransform: "uppercase" }}>Spec. Tools</div><div style={{ fontSize: 16, fontWeight: 800, color: C.purple, fontFamily: "'Bebas Neue', sans-serif" }}>{fmtMoney(vehicle.repairEstimateBreakdown.toolsTotal)}</div></div>}
                  <div><div style={{ fontSize: 9, color: C.textM, fontWeight: 700, textTransform: "uppercase" }}>Total Est.</div><div style={{ fontSize: 16, fontWeight: 800, color: C.text, fontFamily: "'Bebas Neue', sans-serif" }}>{fmtMoney(vehicle.repairEstimateBreakdown.repairTotal)}</div></div>
                </div>
                {vehicle.repairEstimateBreakdown.repairs?.map((r, i) => (
                  <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "5px 0", borderTop: `1px solid ${C.border}`, fontSize: 11, color: C.text }}>
                    <span>{r.description}</span>
                    <span style={{ color: C.textM, flexShrink: 0, marginLeft: 8 }}>{r.hours}h · {fmtMoney(r.partsCost)} parts = {fmtMoney(r.total)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={{ fontSize: 11, color: C.textS }}>{vehicleLedger.length} entries · ⚡ = auto-posted</div>
            <Btn variant="outline" size="sm" onClick={() => setAddLedgerOpen(true)}><Plus size={11} /> Add</Btn>
          </div>
          {vehicleLedger.length === 0 ? <div style={{ textAlign: "center", color: C.textM, padding: 24, fontSize: 12 }}>No entries yet.</div> : (
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
              <div style={{ display: "grid", gridTemplateColumns: "0.5fr 1.2fr 2fr 1fr", padding: "8px 14px", background: C.s2, borderBottom: `1px solid ${C.border}` }}>
                {["Date","Category","Description","Amount"].map(h => <div key={h} style={{ fontSize: 9, color: C.textM, fontWeight: 700, textTransform: "uppercase" }}>{h}</div>)}
              </div>
              {vehicleLedger.sort((a, b) => new Date(a.date) - new Date(b.date)).map((e, i) => {
                const lc = LEDGER_CATS.find(x => x.id === e.category);
                return (
                  <div key={e.id} style={{ display: "grid", gridTemplateColumns: "0.5fr 1.2fr 2fr 1fr", padding: "8px 14px", background: i % 2 ? C.s2 + "50" : "transparent", borderBottom: `1px solid ${C.border}`, borderLeft: `3px solid ${lc?.color || C.border}` }}>
                    <div style={{ fontSize: 11, color: C.textS }}>{fmtDate(e.date)}</div>
                    <div><Badge color={lc?.color || C.textM} small>{lc?.label || e.category}</Badge></div>
                    <div style={{ fontSize: 11, color: C.textS }}>{e.auto && <span style={{ color: C.amber }}>⚡ </span>}{e.description}</div>
                    <div style={{ fontSize: 12, fontWeight: 700, color: e.category === "SALE" ? C.green : C.text, textAlign: "right" }}>{fmtMoneyFull(e.amount)}</div>
                  </div>
                );
              })}
            </div>
          )}
          {addLedgerOpen && (
            <Modal title="Add Ledger Entry" onClose={() => setAddLedgerOpen(false)}>
              <SI label="Category" value={newLedger.category} onChange={v => setNewLedger(l => ({ ...l, category: v }))} options={LEDGER_CATS.map(c => ({ value: c.id, label: c.label }))} />
              <TI label="Description *" value={newLedger.description} onChange={v => setNewLedger(l => ({ ...l, description: v }))} placeholder="e.g. Jim's Muffler — exhaust repair" />
              <TI label="Amount ($)" value={newLedger.amount} onChange={v => setNewLedger(l => ({ ...l, amount: v }))} type="number" />
              <TI label="Date" value={newLedger.date} onChange={v => setNewLedger(l => ({ ...l, date: v }))} type="date" />
              <div style={{ display: "flex", gap: 8 }}>
                <Btn variant="amber" onClick={() => { if (!newLedger.description.trim() || !newLedger.amount) return; onAddLedger({ id: genId(), vehicleId: vehicle.id, ...newLedger, amount: parseFloat(newLedger.amount) || 0 }); setNewLedger({ category: "PARTS", description: "", amount: "", date: todayStr() }); setAddLedgerOpen(false); }}>Post Entry</Btn>
                <Btn variant="ghost" onClick={() => setAddLedgerOpen(false)}>Cancel</Btn>
              </div>
            </Modal>
          )}
        </div>
      )}

      {tab === "notes" && (
        <div>
          {/* Comments */}
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 16px", marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.text, marginBottom: 8 }}>Comments</div>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, marginBottom: 10 }}>
              {(vehicle.comments || []).map((c, i) => (
                <div key={i} style={{ background: C.s2, borderRadius: 8, padding: "8px 12px", fontSize: 12, color: C.text }}>
                  <div style={{ fontSize: 10, color: C.textM, marginBottom: 2 }}>{c.author} · {fmtDate(c.date)}</div>
                  {c.text}
                </div>
              ))}
              {(!vehicle.comments || vehicle.comments.length === 0) && <div style={{ fontSize: 11, color: C.textM, fontStyle: "italic" }}>No comments yet.</div>}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <input id="commentInput" placeholder="Add a comment…" style={{ flex: 1, background: C.s2, border: `1px solid ${C.border}`, borderRadius: 6, padding: "8px 10px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none" }}
                onKeyDown={e => { if (e.key === "Enter" && e.target.value.trim()) {
                  onUpdate({ ...vehicle, comments: [...(vehicle.comments || []), { text: e.target.value.trim(), author: "Team", date: todayStr() }] });
                  e.target.value = "";
                }}} />
              <Btn variant="outline" size="sm" onClick={() => {
                const el = document.getElementById("commentInput");
                if (el?.value?.trim()) {
                  onUpdate({ ...vehicle, comments: [...(vehicle.comments || []), { text: el.value.trim(), author: "Team", date: todayStr() }] });
                  el.value = "";
                }
              }}>Add</Btn>
            </div>
          </div>
          {/* Manual sale price entry */}
          <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 16px", marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: C.text, marginBottom: 4 }}>Estimated / Known Sale Price</div>
            <div style={{ fontSize: 10, color: C.textM, marginBottom: 10 }}>Enter if known. Used for projected gross when Autoniq valuation is unavailable. Overridden by actual sale price when the vehicle sells.</div>
            <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
              <div style={{ flex: 1 }}>
                <input
                  type="number"
                  value={vehicle.manualSalePrice || ""}
                  onChange={e => onUpdate({ ...vehicle, manualSalePrice: parseFloat(e.target.value) || null })}
                  placeholder="e.g. 14500"
                  style={{ width: "100%", background: C.s2, border: `1px solid ${C.border}`, borderRadius: 6, padding: "8px 12px", color: C.text, fontSize: 14, fontFamily: "inherit", outline: "none", boxSizing: "border-box", fontWeight: 700 }}
                />
              </div>
              {vehicle.manualSalePrice && (
                <Btn variant="ghost" size="sm" onClick={() => onUpdate({ ...vehicle, manualSalePrice: null })}>Clear</Btn>
              )}
            </div>
            {vehicle.manualSalePrice && vehicle.kbbValue && (
              <div style={{ fontSize: 10, color: C.textM, marginTop: 6 }}>Note: Autoniq value ({fmtMoney(vehicle.kbbValue)}) takes priority over this estimate.</div>
            )}
          </div>
          <textarea value={vehicle.notes || ""} onChange={e => onUpdate({ ...vehicle, notes: e.target.value })} rows={6} placeholder="Internal notes…" style={{ width: "100%", background: C.s2, border: `1px solid ${C.border}`, borderRadius: 6, padding: "10px 12px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none", resize: "vertical", boxSizing: "border-box", lineHeight: 1.6 }} />
        </div>
      )}

      {/* ── BOTTOM ACTION BUTTONS ── */}
      {(() => {
        const inspDone = vehicle.juliaInspectionSummary && vehicle.juliaInspectionSummary.overallCondition !== "INCOMPLETE" && !vehicle.juliaInspectionIncomplete;
        const inspStarted = vehicle.juliaInspectionSummary || vehicle._juliaPaused;
        const openWOs = (vehicle.workOrders||[]).filter(w => w.status !== "COMPLETE" && !w.deleted);
        const openDetailWOs = openWOs.filter(w => w.category === "DETAIL");
        const openNonDetailWOs = openWOs.filter(w => w.category !== "DETAIL");
        const allDone = openWOs.length === 0;
        const detailOnly = openWOs.length > 0 && openNonDetailWOs.length === 0;
        return (
          <div style={{ marginTop: 24, padding: "16px 0", borderTop: `2px solid ${C.border}`, display:"flex", flexDirection:"column", gap:10 }}>
            <div style={{ display:"flex", gap:6, flexWrap:"wrap", alignItems:"center" }}>
              <button onClick={() => onStartInspection(vehicle)}
                style={{ background:"#fff", border:"1px solid #ddd", borderRadius:8, padding:"6px 12px", cursor:"pointer", fontSize:11, fontWeight:700, color:"#6d28d9", fontFamily:"inherit", display:"flex", alignItems:"center", gap:6 }}>
                <img src="/images/Julia.jpg" alt="" style={{ width:28,height:28,borderRadius:"50%",objectFit:"cover",objectPosition:"top",flexShrink:0 }} onError={e=>e.target.style.display="none"} />
                {inspDone ? "✓ Inspected" : inspStarted ? "▶ Resume Inspect" : "🔍 Inspect"}
              </button>
              <button onClick={() => setBodyWorkOpen(true)} style={{ background:"#fff", border:"1px solid #ddd", borderRadius:8, padding:"6px 12px", cursor:"pointer", fontSize:11, fontWeight:700, color:"#000", fontFamily:"inherit", display:"flex", alignItems:"center", gap:6 }}><img src="/images/btn-add-body-work.jpg" alt="" style={{width:28,height:28,objectFit:"contain"}} onError={e=>e.target.style.display="none"} />Body Work</button>
              <button onClick={() => setRediagnoseOpen(true)} style={{ background:"#fff", border:"1px solid #ddd", borderRadius:8, padding:"6px 12px", cursor:"pointer", fontSize:11, fontWeight:700, color:"#000", fontFamily:"inherit", display:"flex", alignItems:"center", gap:6 }}><img src="/images/btn-re-diagnose.jpg" alt="" style={{width:28,height:28,objectFit:"contain"}} onError={e=>e.target.style.display="none"} />Re-Diagnose</button>
              {(detailOnly || openDetailWOs.length > 0) && (
                <button onClick={() => setDetailCompleteOpen(true)}
                  style={{ background:"#fff", border:"1px solid "+C.green, borderRadius:8, padding:"6px 12px", cursor:"pointer", fontSize:11, fontWeight:700, color:C.green, fontFamily:"inherit", display:"flex", alignItems:"center", gap:6 }}>
                  🧹 Detail Complete
                </button>
              )}
              {(allDone || detailOnly) && vehicle.stage !== "LINE_READY" && vehicle.stage !== "SOLD" && (
                <button onClick={() => { setLineReadyOpen(true); setLrSelectedToClose((vehicle.workOrders||[]).filter(w=>w.status!=="COMPLETE"&&!w.deleted).map(w=>w.id)); }}
                  style={{ background:C.green, border:"none", borderRadius:8, padding:"6px 14px", cursor:"pointer", fontSize:11, fontWeight:700, color:"#fff", fontFamily:"inherit", display:"flex", alignItems:"center", gap:6 }}>
                  ← Move to Line Ready
                </button>
              )}
              <Btn variant="outline" size="sm" onClick={() => { if (navigateTo) navigateTo("schedule"); else if (setViewProp) setViewProp("schedule"); }}>
                <Calendar size={11} /> Schedule
              </Btn>
              {vehicle.deliveryDate && <Badge color={C.blue} small>📅 Delivery {fmtDate(vehicle.deliveryDate)} {vehicle.deliveryTime}</Badge>}
              {getContinueLabel() && getContinueAction() && (
                <Btn variant="blue" size="sm" onClick={getContinueAction()}>▶ {getContinueLabel()}</Btn>
              )}
            </div>
            {/* Mark Sold + Scrap */}
            {vehicle.stage !== "SCRAPPED" && (
              <div style={{ display:"flex", gap:10, paddingTop:8, borderTop:`1px dashed ${C.border}` }}>
                {vehicle.stage !== "SOLD" && (
                  <button onClick={() => setMarkSoldOpen(true)}
                    style={{ flex:1, padding:"14px", background:C.green, border:"none", borderRadius:10, color:"#fff", fontSize:14, fontWeight:900, fontFamily:"'Bebas Neue', sans-serif", letterSpacing:"0.08em", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
                    <CheckCircle size={18} /> MARK SOLD
                  </button>
                )}
                {vehicle.stage === "SOLD" && vehicle.soldRepairPending && (
                  <button onClick={() => setReturnToCustomerOpen(true)}
                    style={{ flex:1, padding:"14px", background:C.green, border:"none", borderRadius:10, color:"#fff", fontSize:13, fontWeight:900, fontFamily:"'Bebas Neue', sans-serif", letterSpacing:"0.06em", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
                    <CheckCircle size={18} /> VEHICLE RETURNED TO CUSTOMER
                  </button>
                )}
                {vehicle.unsafeToDrive && (
                  <button onClick={() => setTowOpen(true)}
                    style={{ flex:0, padding:"14px 20px", background:C.red, border:"none", borderRadius:10, color:"#fff", fontSize:12, fontWeight:800, fontFamily:"'Bebas Neue', sans-serif", letterSpacing:"0.05em", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:6, whiteSpace:"nowrap" }}>
                    🚛 REQUEST TOW
                  </button>
                )}
                <button onClick={() => setScrapOpen(true)}
                  style={{ flex:0, padding:"14px 20px", background:"transparent", border:`2px solid ${C.red}60`, borderRadius:10, color:C.red, fontSize:11, fontWeight:700, fontFamily:"inherit", cursor:"pointer", display:"flex", alignItems:"center", justifyContent:"center", gap:6, whiteSpace:"nowrap" }}>
                  🗑 {vehicle.stage === "SOLD" ? "Archive" : "Scrap Vehicle"}
                </button>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
};

// ─── INVOICE SCANNER ────────────────────────────────────────────────────────


// ─── PARTS BINS VIEW ─────────────────────────────────────────────────────────


// ─── PARTS MANAGER (compact) ─────────────────────────────────────────────────

// ─── PART RETURN MODAL ───────────────────────────────────────────────────────


// ─── PARTS FINDER ─────────────────────────────────────────────────────────────

// ─── ROXY — PARTS COUNTER MANAGER ────────────────────────────────────────────

const ROXY_MOODS = {
  idle:       { img: "/images/roxy-headshot.jpg",          label: "Ready" },
  searching:  { img: "/images/roxy-searching.jpg",         label: "Searching…" },
  thinking:   { img: "/images/roxy-thinking.jpg",          label: "On it…" },
  talking:    { img: "/images/roxy-talking-on-phone.jpg",  label: "On the phone" },
  concerned:  { img: "/images/roxy-concerned.jpg",         label: "Hmm…" },
  phone:      { img: "/images/roxy-phone-concern.jpg",     label: "Concerned" },
  laughing:   { img: "/images/roxy-laughing.jpg",          label: "Ha!" },
};

const ROXY_SYSTEM = `You are Roxy (Roxanne Gallagher), Parts Counter Manager at Edgewater Auto Center, Salem OR.\n\nPERSONALITY: Impatiently amused. 15 years in parts. Deadpan sarcasm, right on the edge of what HR would flag. You love your job but would rather eat glass than admit it.\n\nPROCESS:\n1. You already have year/make/model. NEVER ask for it.\n2. Ask ONE smart refining question per turn (side, trim, OEM vs aftermarket preference, etc.) until you are ${CONVICTION.PARTS}%+ sure of the exact part.\n3. Then return EXACTLY 3 vendor recommendations — best places to shop the part. Pick from these source ids: autozone, oreilly, napa, rockauto, amazon, fcpeuro, worldpac, car_parts, lkq, ebay_motors, les_schwab, safelite, ppg, harbor_freight, magnaflow, dealer_oem.\n4. Set query to a clean, vendor-search-friendly version of the part name (e.g. \"front brake rotor 2010 Ford F150\"). The app will build the actual vendor URL — DO NOT include url, partNumber price, or made-up part numbers.\n5. NEVER quote a price. NEVER invent a part number. The user will click through to the vendor and see the real live price.\n6. For OEM dealer parts: source=\"dealer_oem\", set notes to \"Call Capital Ford Salem: (503) 391-2100 — ask for parts dept\".\n\nRESPOND IN VALID JSON ONLY. No markdown, no extra text:\n{\"message\":\"Roxy reply\",\"mood\":\"idle\",\"confidence\":0,\"results\":[],\"done\":false}\n\nWhen done (confidence>=${CONVICTION.PARTS}) return exactly 3 results:\n{\"message\":\"reply\",\"mood\":\"talking\",\"confidence\":${CONVICTION.PARTS},\"done\":true,\"results\":[{\"source\":\"autozone\",\"sourceLabel\":\"AutoZone\",\"title\":\"clean part name\",\"query\":\"front brake rotor 2010 Ford F150 6 lug\",\"condition\":\"New\",\"leadTime\":\"Same day pickup\",\"confidence\":\"HIGH\",\"notes\":\"why this vendor\"}]}`;

const RoxyAvatar = ({ mood = "idle", size = "full", pinned = false }) => {
  const moodData = ROXY_MOODS[mood] || ROXY_MOODS.idle;
  const [imgErr, setImgErr] = useState(false);

  if (size === "full") {
    return (
      <div style={{
        position: pinned ? "sticky" : "relative",
        top: pinned ? 0 : undefined,
        zIndex: pinned ? 10 : undefined,
        width: "100%",
        background: "#0f1f3d",
        borderRadius: pinned ? 0 : 14,
        overflow: "hidden",
        marginBottom: 16,
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        maxHeight: 320,
      }}>
        {!imgErr ? (
          <img
            src={moodData.img}
            alt="Roxy"
            onError={() => setImgErr(true)}
            style={{ width: "100%", maxWidth: 480, objectFit: "cover", objectPosition: "top center", display: "block" }}
          />
        ) : (
          <div style={{ width: "100%", height: 200, background: "linear-gradient(180deg, #1a2d50 0%, #0f1f3d 100%)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 80 }}>🔧</div>
        )}
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "linear-gradient(transparent, rgba(15,31,61,0.95))", padding: "24px 16px 10px" }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: "#f59e0b", fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.1em" }}>ROXY — PARTS COUNTER</div>
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.6)" }}>{moodData.label}</div>
        </div>
      </div>
    );
  }
  // Small inline avatar
  return !imgErr ? (
    <img src={moodData.img} alt="Roxy" onError={() => setImgErr(true)}
      style={{ width: size, height: size, borderRadius: "50%", objectFit: "cover", objectPosition: "top center", border: "2px solid #f59e0b", flexShrink: 0 }} />
  ) : (
    <div style={{ width: size, height: size, borderRadius: "50%", background: "#1a2d50", border: "2px solid #f59e0b", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 }}>🔧</div>
  );
};

const PartsFinderView = ({ vehicles, vendors, onAddPart, onAddLedger, currentUser, parts, initialQuery, initialVehicle, onBack }) => {
  const [messages, setMessages] = useState([]);
  const [apiMessages, setApiMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [mood, setMood] = useState("idle");
  const [results, setResults] = useState(null);
  const [assignVehicle, setAssignVehicle] = useState(initialVehicle?.id || "");
  const [assignWO, setAssignWO] = useState("");
  const [lastOrdered, setLastOrdered] = useState("");
  const [listening, setListening] = useState(false);
  const [roxyConfidence, setRoxyConfidence] = useState(0);
  const [roxyImg, setRoxyImg] = useState(null);
  const bottomRef = useRef(null);
  const recRef = useRef(null);
  const didAutoSearch = useRef(false);

  const av = vehicles.find(v => v.id === assignVehicle);

  // Opening line from Roxy — acknowledges vehicle if assigned
  useEffect(() => {
    const opener = av
      ? `Alright, I've got the ${av.year} ${av.make} ${av.model}${av.trim ? " " + av.trim : ""} pulled up — ${av.mileage ? parseInt(av.mileage).toLocaleString() + " miles" : ""}${av.engine ? ", " + av.engine : ""}. What part do you need?`
      : "Alright, what do you need? Part number, description, year-make-model — give me something to work with. And before you ask, no, I don't know what 'the metal thingy' is.";
    setMessages([{ role: "assistant", content: opener, mood: "idle" }]);
  }, [assignVehicle]);

  // Auto-search if initialQuery provided (from WO needed parts button)
  useEffect(() => {
    if (initialQuery && !didAutoSearch.current) {
      didAutoSearch.current = true;
      setTimeout(() => send(initialQuery), 800);
    }
  }, []);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    const r = new SR(); r.continuous = false; r.interimResults = false;
    r.onresult = (e) => { setInput(e.results[0][0].transcript); setListening(false); };
    r.onend = () => setListening(false);
    r.start(); recRef.current = r; setListening(true);
  };

  const send = async () => {
    if ((!input.trim() && !roxyImg) || loading) return;
    const imgForRoxy = roxyImg;
    if (roxyImg) setRoxyImg(null);

    // Build user message — include image if attached
    const userText = input.trim() || (imgForRoxy ? "Here's a photo of the part — identify it and find the best price." : "");
    const userContent = imgForRoxy
      ? [{ type: "image", source: { type: "base64", media_type: imgForRoxy.mediaType, data: imgForRoxy.b64 } }, { type: "text", text: userText }]
      : userText;

    const contextNote = assignVehicle ? ` [Vehicle: ${av?.year} ${av?.make} ${av?.model}]` : "";
    const apiMsg = { role: "user", content: imgForRoxy ? userContent : userText + contextNote };
    const newApiMsgs = [...apiMessages, apiMsg];
    setMessages(prev => [...prev, { role: "user", content: userText || "📷 [Photo attached]" }]);
    setInput(""); setLoading(true); setMood("searching"); setResults(null);

    // Build vendor context for Roxy
    const vendorList = vendors.filter(v => v.type === "PARTS").slice(0, 8).map(v => `${v.name} (${v.address || "Online"})`).join("; ");
    const vSpecs = av ? [
      `${av.year} ${av.make} ${av.model}`,
      av.trim ? `Trim: ${av.trim}` : null,
      av.engine ? `Engine: ${av.engine}` : null,
      av.displacement ? `${av.displacement}L` : null,
      av.cylinders ? `${av.cylinders}-cyl` : null,
      av.drivetrain ? `Drivetrain: ${av.drivetrain}` : null,
      av.transmission ? `Transmission: ${av.transmission}` : null,
      av.fuelType ? `Fuel: ${av.fuelType}` : null,
      av.mileage ? `${parseInt(av.mileage).toLocaleString()} miles` : null,
      av.vin ? `VIN: ${av.vin}` : null,
      av.tireSize ? `Tires: ${av.tireSize}` : null,
    ].filter(Boolean).join(" | ") : null;

    const vSpecsStr = vSpecs || "Unknown vehicle — ask for year/make/model if needed";
    const roxySystem = ROXY_SYSTEM + "\n\nVEHICLE ON FILE: " + vSpecsStr + (vSpecs ? "\nDo NOT ask for year, make, model, or engine. You have it." : "");

    try {
      // Try Grok first, fall back to Claude
      let parsed = null;
      const hasGrok = !!process.env.REACT_APP_GROK_KEY;
      if (hasGrok) {
        try {
          const raw = await callGrok(newApiMsgs, roxySystem, 1000);
          console.log("Grok raw:", raw.slice(0, 200));
          const cleaned = raw.replace(/```json/g, "").replace(/```/g, "").trim();
          const start = cleaned.indexOf("{"); const end = cleaned.lastIndexOf("}");
          if (start >= 0 && end > start) parsed = JSON.parse(cleaned.slice(start, end + 1));
        } catch (grokErr) { console.warn("Grok failed:", grokErr.message); }
      }
      if (!parsed) {
        // Use Claude directly without web_search tool for reliable JSON
        const res = await fetch("/api/claude", { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${await getIdToken()}` },
          body: JSON.stringify({
            model: "claude-sonnet-4-6", max_tokens: 1000,
            system: roxySystem,
            messages: newApiMsgs
          })
        });
        const data = await res.json();
        console.log("Claude status:", res.status, data.error?.message || "ok");
        const raw = data.content?.[0]?.text || "";
        try {
          const cleaned = raw.replace(/```json/g, "").replace(/```/g, "").trim();
          const start = cleaned.indexOf("{"); const end = cleaned.lastIndexOf("}");
          if (start >= 0 && end > start) parsed = JSON.parse(cleaned.slice(start, end + 1));
        } catch (pe) { console.warn("Claude JSON parse failed:", pe.message, raw.slice(0, 200)); }
        if (!parsed?.message) {
          parsed = { message: raw.trim() || "One sec, the system hiccupped.", mood: "thinking", results: [], done: false };
        }
      }

      const assistantMsg = { role: "assistant", content: parsed.message, mood: parsed.mood || "idle" };
      setMessages(prev => [...prev, assistantMsg]);
      setApiMessages([...newApiMsgs, { role: "assistant", content: parsed.message }]);
      setMood(parsed.mood || "idle");
      if (parsed.confidence !== undefined) setRoxyConfidence(parsed.confidence);
      if (parsed.results?.length > 0) setResults(parsed);
    } catch (e) {
      setMessages(prev => [...prev, { role: "assistant", content: "Something broke on my end. Don't panic. Try again.", mood: "concerned" }]);
      setMood("concerned");
    }
    setLoading(false);
  };

  // Build a known-good vendor search URL from the result's source + query.
  // We never trust LLM-provided URLs — always construct from PARTS_SOURCES.
  const buildVendorUrl = (r) => {
    const src = PARTS_SOURCES.find(s => s.id === r.source);
    if (!src?.searchUrl) return null;
    const q = (r.query || r.title || "").trim();
    if (!q) return null;
    return src.searchUrl(q) || null;
  };

  const orderPart = (r) => {
    const pv = vendors.filter(v => v.type === "PARTS").find(v => v.name.toLowerCase().includes(r.source.replace("_"," ").toLowerCase())) ||
               vendors.filter(v => v.type === "PARTS").sort((a, b) => (a.priority || 9) - (b.priority || 9))[0];
    const src = PARTS_SOURCES.find(s => s.id === r.source);
    const openUrl = buildVendorUrl(r);
    const entry = {
      id: genId(), vehicleId: assignVehicle || null, workOrderId: assignWO || null,
      description: r.title, partNumber: "", supplier: pv?.id || null,
      cost: 0, condition: r.condition, source: r.source,
      orderDate: todayStr(), eta: addDays(todayStr(), src?.leadDays ?? 3),
      status: openUrl ? "Pending Purchase" : "Ordered",
      binSize: estimateBinSize(r.title),
      orderedBy: currentUser?.name || "unknown",
      sourceUrl: openUrl || null,
      sourceLabel: r.sourceLabel || src?.label || r.source,
    };
    onAddPart(entry);
    // No ledger entry until user comes back and enters the actual price they paid.
    if (openUrl) {
      window.open(openUrl, "_blank", "noopener,noreferrer");
    }
    setLastOrdered(r.title);
    setMood("laughing");
    setTimeout(() => { setLastOrdered(""); setMood("idle"); }, 4000);
  };

  const condColor = { OEM: C.green, New: C.green, Remanufactured: "#06b6d4", Used: C.amber, Aftermarket: "#f97316" };

  return (
    <div>
      {/* Back button when launched from vehicle */}
      {onBack && (
        <button onClick={onBack} style={{ background: "none", border: "none", color: C.textS, cursor: "pointer", fontSize: 11, padding: 0, marginBottom: 12, display: "flex", alignItems: "center", gap: 4 }}>
          <img src="/images/btn-back-arrow.jpg" alt="" style={{width:16,height:16,objectFit:"contain",flexShrink:0}} onError={e=>e.target.style.display="none"} /> Back to Vehicle
        </button>
      )}
      {/* Roxy full-width pinned image */}
      <RoxyAvatar mood={mood} size="full" pinned={true} />

      {/* Vehicle assignment */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
        <div style={{ flex: 1 }}>
          <SI label="Assign to Vehicle" value={assignVehicle} onChange={v => { setAssignVehicle(v); setAssignWO(""); setRoxyConfidence(0); }}
            options={[{ value: "", label: "— No vehicle (general search)" }, ...vehicles.filter(v => !["SOLD","SCRAPPED"].includes(v.stage)).map(v => ({ value: v.id, label: `${v.year} ${v.make} ${v.model} #${v.stockNumber}` }))]} />
        </div>
      </div>

      {/* Open work orders for the selected vehicle — prominent dropdown */}
      {assignVehicle && av?.workOrders?.filter(w => w.status !== "COMPLETE")?.length > 0 && (
        <div style={{ marginBottom: 10 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 5 }}>
            Open Work Orders — single click to select · double-click to auto-search:
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            {av.workOrders.filter(w => w.status !== "COMPLETE").map(w => (
              <div key={w.id}
                onClick={() => setAssignWO(assignWO === w.id ? "" : w.id)}
                onDoubleClick={() => {
                  setAssignWO(w.id);
                  send(`I need parts for this work order on a ${av.year} ${av.make} ${av.model}${av.trim ? " " + av.trim : ""}: ${w.description}`);
                }}
                style={{ background: assignWO === w.id ? C.amber+"20" : C.s2, border: `2px solid ${assignWO === w.id ? C.amber : C.border}`, borderRadius: 8, padding: "8px 12px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: assignWO === w.id ? 700 : 400, color: "#000" }}>{w.description}</div>
                  <div style={{ fontSize: 9, color: C.textM }}>{w.category} · {w.priority} · double-click to auto-search</div>
                </div>
                {assignWO === w.id && <CheckCircle size={14} color={C.amber} />}
              </div>
            ))}
            {assignWO && (
              <button onClick={() => setAssignWO("")} style={{ background: "none", border: "none", fontSize: 10, color: C.textM, cursor: "pointer", fontFamily: "inherit", textAlign: "left" }}>
                ✕ Clear selection
              </button>
            )}
          </div>
        </div>
      )}

      {/* Roxy confidence bar */}
      {roxyConfidence > 0 && (
        <div style={{ marginBottom: 12 }}>
          <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}>
            <span style={{ fontSize: 10, color: C.textM, fontWeight: 700, textTransform: "uppercase" }}>Part Identification Confidence</span>
            <span style={{ fontSize: 13, fontWeight: 800, color: roxyConfidence >= CONVICTION.PARTS ? C.green : roxyConfidence >= CONVICTION.LOW ? C.amber : "#f97316", fontFamily: "'Bebas Neue', sans-serif" }}>{roxyConfidence}%</span>
          </div>
          <div style={{ height: 6, borderRadius: 3, background: C.border }}>
            <div style={{ height: "100%", borderRadius: 3, width: `${roxyConfidence}%`, background: roxyConfidence >= CONVICTION.PARTS ? C.green : roxyConfidence >= CONVICTION.LOW ? C.amber : "#f97316", transition: "width 0.5s ease" }} />
          </div>
          <div style={{ fontSize: 9, color: C.textM, marginTop: 3 }}>
            {roxyConfidence < 40 ? "Gathering info — Roxy needs more details" : roxyConfidence < 70 ? "Getting closer — a few more questions" : roxyConfidence < 95 ? `Almost locked in — ${95 - roxyConfidence}% to go` : "✓ 95%+ confident — Roxy has the right part"}
          </div>
        </div>
      )}

      {/* Order confirmed banner */}
      {lastOrdered && <div style={{ background: C.green + "15", border: `1px solid ${C.green}40`, borderRadius: 8, padding: "10px 14px", marginBottom: 12, fontSize: 12, color: C.green }}>✓ Ordered: <strong>{lastOrdered}</strong>{assignVehicle ? " — posted to vehicle ledger" : ""}</div>}

      {/* Chat messages */}
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 12, maxHeight: 280, overflowY: "auto" }}>
        {messages.map((m, i) => (
          <div key={i} style={{ display: "flex", justifyContent: m.role === "user" ? "flex-end" : "flex-start", gap: 8 }}>
            {m.role === "assistant" && <RoxyAvatar mood={m.mood || "idle"} size={32} />}
            <div style={{ maxWidth: "82%", padding: "9px 13px", borderRadius: m.role === "user" ? "12px 12px 4px 12px" : "12px 12px 12px 4px", background: m.role === "user" ? C.amber + "25" : C.s2, border: `1px solid ${m.role === "user" ? C.amber + "40" : C.border}`, fontSize: 12, color: C.text, lineHeight: 1.6 }}>
              {m.role === "assistant" && <div style={{ fontSize: 9, color: "#f59e0b", fontWeight: 700, textTransform: "uppercase", marginBottom: 3 }}>ROXY</div>}
              {m.content}
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ display: "flex", gap: 8 }}>
            <RoxyAvatar mood="searching" size={32} />
            <div style={{ padding: "9px 13px", background: C.s2, borderRadius: "12px 12px 12px 4px", fontSize: 12, color: C.textM }}>Searching suppliers…</div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Results */}
      {results?.results?.length > 0 && (
        <>
        <div style={{ background:"#fef3c7", border:"1px solid #f59e0b40", borderRadius:8, padding:"8px 12px", marginBottom:10, fontSize:10, color:"#92400e" }}>
          ⚠️ Roxy doesn't quote prices — she points you at the right vendor for the part. Click through to see the real live price and order.
        </div>
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 8, letterSpacing: "0.05em" }}>Top 3 — click to see live price & order</div>
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {(results.results || []).slice(0, 3).map((r, i) => {
              const url = buildVendorUrl(r);
              const isDealer = r.source === "dealer_oem";
              return (
                <div key={i} style={{ background: i === 0 ? C.green + "10" : C.surface, border: `2px solid ${i === 0 ? C.green + "50" : C.border}`, borderRadius: 12, padding: "12px 14px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, flexWrap: "wrap" }}>
                    <div style={{ flex: 1, minWidth: 180 }}>
                      <div style={{ display: "flex", gap: 5, marginBottom: 4, flexWrap: "wrap", alignItems: "center" }}>
                        {i === 0 && <span style={{ fontSize: 10, fontWeight: 800, color: C.green, background: C.green + "20", padding: "2px 7px", borderRadius: 10, textTransform: "uppercase" }}>⭐ Roxy's Pick</span>}
                        <Badge color={condColor[r.condition] || C.textM} small>{r.condition || "New"}</Badge>
                        <span style={{ fontSize: 11, fontWeight: 700, color: C.text }}>{r.sourceLabel}</span>
                      </div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: "#000" }}>{r.title}</div>
                      {r.leadTime && <div style={{ fontSize: 10, color: C.textM, marginTop: 2 }}>⏱ {r.leadTime}</div>}
                      {r.notes && <div style={{ fontSize: 10, color: C.textS, fontStyle: "italic", marginTop: 3 }}>{r.notes}</div>}
                    </div>
                    <div style={{ flexShrink: 0 }}>
                      {isDealer ? (
                        <a href="tel:5033912100" style={{ background: C.blue, borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 800, color: "#fff", textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "inherit" }}>
                          📞 Call (503) 391-2100
                        </a>
                      ) : url ? (
                        <button onClick={() => orderPart(r)} style={{ background: i === 0 ? C.green : C.amber, border: "none", borderRadius: 8, padding: "8px 14px", fontSize: 12, fontWeight: 800, color: i === 0 ? "#fff" : "#000", cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 6, fontFamily: "inherit" }}>
                          <ShoppingCart size={13} /> View Price & Order →
                        </button>
                      ) : (
                        <span style={{ fontSize: 10, color: C.textM, fontStyle: "italic" }}>No vendor link</span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 10, fontSize: 10, color: C.textM, fontStyle: "italic", textAlign: "center" }}>
            Roxy doesn't quote prices — click "View Price & Order" to see the live price on the vendor site.
          </div>
        </div>
        </>
      )}

      {/* Input */}
      <div style={{ paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
        {roxyImg && (
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6, background: C.amber+"10", border: `1px solid ${C.amber}30`, borderRadius: 7, padding: "5px 8px" }}>
            <img src={roxyImg.preview} alt="" style={{ width: 44, height: 44, borderRadius: 6, objectFit: "cover" }} />
            <span style={{ fontSize: 10, color: C.amber, flex: 1 }}>📷 Roxy will identify this part from the photo</span>
            <button onClick={() => setRoxyImg(null)} style={{ background: "none", border: "none", cursor: "pointer", color: C.textM }}>✕</button>
          </div>
        )}
        <div style={{ display: "flex", gap: 8 }}>
          <button onClick={listening ? () => { recRef.current?.stop(); setListening(false); } : startVoice}
            style={{ flexShrink: 0, width: 42, height: 42, borderRadius: 8, border: `2px solid ${listening ? C.red : C.amber}`, background: listening ? C.red + "20" : C.amber + "15", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: listening ? C.red : C.amber }}>
            {listening ? <MicOff size={16} /> : <><img src="/images/btn-voice-text.jpg" alt="" style={{width:20,height:20,objectFit:"contain",flexShrink:0}} onError={e=>e.target.style.display="none"} /></>}
          </button>
          <ImageAttachButton onImage={setRoxyImg} />
          <BarcodeButton onResult={(code) => {
            setInput(code);
            setMood("thinking");
          }} />
          <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => e.key === "Enter" && send()}
            placeholder={loading ? "Roxy is looking…" : "What do you need? Part name, barcode…"}
            style={{ flex: 1, background: C.s2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none" }} />
          <Btn variant="amber" onClick={send} disabled={(!input.trim() && !roxyImg) || loading}><Send size={14} /></Btn>
        </div>
      </div>
    </div>
  );
};

// ─── VENDORS VIEW ─────────────────────────────────────────────────────────────

const VForm = ({ v, onChange, onSave, onDelete, onCancel }) => (
  <div>
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
      <div style={{ gridColumn: "1/-1" }}><TI label="Name *" value={v.name} onChange={val => onChange("name", val)} placeholder="Vendor name" /></div>
      <SI label="Type" value={v.type} onChange={val => onChange("type", val)} options={VENDOR_TYPES.map(t => ({ value: t.id, label: t.label }))} />
      <SI label="Priority" value={String(v.priority)} onChange={val => onChange("priority", val)} options={[{ value: "1", label: "1 — Primary" },{ value: "2", label: "2 — Secondary" },{ value: "3", label: "3 — Tertiary" }]} />
      <TI label="Phone" value={v.phone} onChange={val => onChange("phone", val)} placeholder="503-555-0000" />
      <TI label="Email" value={v.email} onChange={val => onChange("email", val)} placeholder="contact@vendor.com" />
      <div style={{ gridColumn: "1/-1" }}><TI label="Address" value={v.address} onChange={val => onChange("address", val)} /></div>
      <F label="Max Concurrent (0 = unlimited)"><input type="number" min="0" value={v.maxConcurrent} onChange={e => onChange("maxConcurrent", e.target.value)} style={{ width: "100%", background: C.s2, border: `1px solid ${C.border}`, borderRadius: 6, padding: "8px 12px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} /></F>
      <TI label="Return Window (days)" value={String(v.returnWindow || "")} onChange={val => onChange("returnWindow", val)} type="number" />
    </div>
    <div style={{ background: "#f8f9fa", border: "1px solid #e5e7eb", borderRadius: 8, padding: "12px 14px", marginTop: 10, marginBottom: 10 }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: "#374151", marginBottom: 8 }}>🔑 Account Credentials</div>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
        <TI label="Account #" value={v.credentials?.accountNumber || ""} onChange={val => onChange("credentials", { ...(v.credentials||{}), accountNumber: val })} placeholder="Account number" />
        <TI label="Account Rep" value={v.credentials?.rep || ""} onChange={val => onChange("credentials", { ...(v.credentials||{}), rep: val })} placeholder="Rep name / phone" />
        <TI label="Username / Email" value={v.credentials?.username || ""} onChange={val => onChange("credentials", { ...(v.credentials||{}), username: val })} placeholder="Login email" />
        <TI label="Password" value={v.credentials?.password || ""} onChange={val => onChange("credentials", { ...(v.credentials||{}), password: val })} type="password" placeholder="Password" />
      </div>
      <div style={{ fontSize: 9, color: "#6b7280", marginTop: 6 }}>Credentials are stored in your Firebase database and used by Roxy when searching this vendor.</div>
    </div>
    <div style={{ display: "flex", gap: 8 }}>
      <Btn variant="amber" onClick={onSave} disabled={!v.name}>Save</Btn>
      <Btn variant="ghost" onClick={onCancel}>Cancel</Btn>
      {v.id && <Btn variant="red" size="sm" onClick={onDelete}>Delete</Btn>}
    </div>
  </div>
);

const VendorsView = ({ vendors, setVendors, vehicles }) => {
  const [editV, setEditV] = useState(null);
  const [placeSearch, setPlaceSearch] = useState("");
  const [placeResults, setPlaceResults] = useState([]);
  const [placesKey, setPlacesKey] = useState("");
  const [keyModal, setKeyModal] = useState(false);
  const blank = { name: "", type: "TIRES", phone: "", email: "", address: "", maxConcurrent: 2, priority: 1, returnWindow: "", restockingFee: "", notes: "" };
  const [detailV, setDetailV] = useState(null);

  const tc = (t) => VENDOR_TYPES.find(x => x.id === t)?.color || C.textM;
  const getLoad = (id) => getVendorLoad(id, vehicles);

  const searchPlaces = async () => {
    if (!placesKey) { setKeyModal(true); return; }
    try {
      const res = await fetch("https://places.googleapis.com/v1/places:searchText", { method: "POST", headers: { "Content-Type": "application/json", "X-Goog-Api-Key": placesKey, "X-Goog-FieldMask": "places.id,places.displayName,places.formattedAddress,places.nationalPhoneNumber" }, body: JSON.stringify({ textQuery: placeSearch + " Salem Oregon", locationBias: { circle: { center: { latitude: 44.9429, longitude: -123.0351 }, radius: 25000.0 } } }) });
      const data = await res.json();
      setPlaceResults(data.places || []);
    } catch { setPlaceResults([]); }
  };

  // Build URL for vendor — use saved URL or Google search
  const getVendorUrl = (v) => {
    if (v.websiteUrl) return v.websiteUrl;
    return `https://www.google.com/search?q=${encodeURIComponent(v.name + " " + (v.address || "Salem Oregon"))}`;
  };

  return (
    <div>
      {/* Vendor detail modal */}
      {detailV && (
        <Modal title={detailV.name} onClose={() => setDetailV(null)} wide>
          <div style={{ marginBottom: 12 }}>
            <Badge color={tc(detailV.type)} small>{detailV.type}</Badge>
            {detailV.phone && <span style={{ fontSize: 12, color: C.textM, marginLeft: 10 }}>📞 <a href={`tel:${detailV.phone.replace(/\D/g,"")}`} style={{ color: C.blue }}>{detailV.phone}</a></span>}
            {detailV.email && <span style={{ fontSize: 12, color: C.textM, marginLeft: 10 }}>✉️ {detailV.email}</span>}
          </div>
          {/* Website link */}
          <div style={{ marginBottom: 14 }}>
            <a href={getVendorUrl(detailV)} target="_blank" rel="noreferrer"
              onClick={() => { if (!detailV.websiteUrl) { setVendors(prev => prev.map(x => x.id === detailV.id ? { ...x, websiteUrl: getVendorUrl(detailV) } : x)); } }}
              style={{ background: C.blue, color: "#fff", borderRadius: 8, padding: "8px 16px", fontSize: 12, fontWeight: 700, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 6 }}>
              🌐 {detailV.websiteUrl ? "Visit Website" : "Search Google"}
            </a>
            {!detailV.websiteUrl && <div style={{ fontSize: 10, color: C.textM, marginTop: 4 }}>URL will be saved after first visit</div>}
          </div>
          {/* Interaction history */}
          {(() => {
            const vParts = (vehicles || []).flatMap(ve =>
              (ve.workOrders||[]).flatMap(wo =>
                wo.vendor === detailV.id || wo.vendor === detailV.name
                  ? [{ type:"wo", desc: wo.description, vehicle: `${ve.year} ${ve.make} ${ve.model} #${ve.stockNumber}`, vehicleId: ve.id, status: wo.status, date: wo.createdAt }]
                  : []
              )
            );
            if (vParts.length === 0) return <div style={{ fontSize: 11, color: C.textM }}>No recorded interactions yet.</div>;
            return (
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#000", marginBottom: 8 }}>Interaction History ({vParts.length})</div>
                {vParts.slice(0,20).map((item, i) => (
                  <div key={i} style={{ padding: "7px 0", borderBottom: `1px solid ${C.border}`, fontSize: 11, display: "flex", justifyContent: "space-between" }}>
                    <div>
                      <div style={{ color: C.text, fontWeight: 600 }}>{item.desc}</div>
                      <div style={{ color: C.textM, fontSize: 10 }}>{item.vehicle} · {item.date}</div>
                    </div>
                    <Badge color={item.status === "COMPLETE" ? C.green : C.amber} small>{item.status}</Badge>
                  </div>
                ))}
              </div>
            );
          })()}
          <div style={{ marginTop: 14, display: "flex", gap: 8 }}>
            <Btn variant="amber" onClick={() => { setEditV({ ...detailV }); setDetailV(null); }}>✏️ Edit</Btn>
            <Btn variant="ghost" onClick={() => setDetailV(null)}>Close</Btn>
          </div>
        </Modal>
      )}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14 }}>
        <div><div style={{ display:"flex", alignItems:"center", gap:10 }}><SiteLogo size="md" /><div style={{ fontSize: 26, fontWeight: 800, color: C.amber, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.06em" }}>VENDORS</div></div><div style={{ fontSize: 11, color: C.textS }}>Capacity rules · Auto-routing · Google Places</div></div>
        <Btn variant="amber" size="sm" onClick={() => setEditV({ ...blank })}><Plus size={11} /> Add</Btn>
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
        <input value={placeSearch} onChange={e => setPlaceSearch(e.target.value)} onKeyDown={e => e.key === "Enter" && searchPlaces()} placeholder='"tire shop Salem" — find via Google Places' style={{ flex: 1, background: C.s2, border: `1px solid ${C.border}`, borderRadius: 6, padding: "8px 12px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none" }} />
        <Btn variant="outline" size="sm" onClick={searchPlaces}><MapPin size={12} /> Search</Btn>
      </div>
      {placeResults.length > 0 && (
        <div style={{ marginBottom: 16, display: "flex", flexDirection: "column", gap: 6 }}>
          {placeResults.map((p, i) => (
            <div key={i} style={{ background: C.s2, border: `1px solid ${C.border}`, borderRadius: 6, padding: "10px 12px", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div><div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{p.displayName?.text}</div><div style={{ fontSize: 11, color: C.textS }}>{p.formattedAddress}</div></div>
              <Btn variant="outline" size="sm" onClick={() => { setEditV({ ...blank, name: p.displayName?.text || "", address: p.formattedAddress || "", phone: p.nationalPhoneNumber || "", placeId: p.id }); setPlaceResults([]); }}>Import</Btn>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(270px, 1fr))", gap: 12 }}>
        {vendors.map(v => {
          const load = getLoad(v.id); const atCap = v.maxConcurrent > 0 && load >= v.maxConcurrent;
          return (
            <div key={v.id} onClick={() => setDetailV(v)} style={{ background: C.surface, border: `1px solid ${atCap ? C.red + "55" : C.border}`, borderTop: `3px solid ${tc(v.type)}`, borderRadius: 10, padding: "14px 16px", cursor: "pointer" }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 8 }}>
                <div><div style={{ fontSize: 13, fontWeight: 800, color: C.text }}>{v.name}</div><div style={{ display: "flex", gap: 5, marginTop: 3 }}><Badge color={tc(v.type)} small>{v.type}</Badge><Badge color={v.priority === 1 ? C.green : v.priority === 2 ? C.amber : C.textM} small>P{v.priority}</Badge></div></div>
                <button onClick={(e) => { e.stopPropagation(); setEditV({ ...v }); }} style={{ background: "none", border: "none", color: C.textM, cursor: "pointer" }}>✏️</button>
              </div>
              {v.phone && <div style={{ fontSize: 11, color: C.textS, marginBottom: 2 }}>{v.phone}</div>}
              {v.maxConcurrent > 0 && <div style={{ marginTop: 8 }}><div style={{ display: "flex", justifyContent: "space-between", marginBottom: 3 }}><span style={{ fontSize: 10, color: C.textM, fontWeight: 700, textTransform: "uppercase" }}>Capacity</span><span style={{ fontSize: 10, color: atCap ? C.red : C.green, fontWeight: 700 }}>{load}/{v.maxConcurrent}</span></div><div style={{ height: 4, borderRadius: 2, background: C.border }}><div style={{ height: "100%", borderRadius: 2, width: `${Math.min((load / v.maxConcurrent) * 100, 100)}%`, background: atCap ? C.red : C.green }} /></div></div>}
            </div>
          );
        })}
      </div>
      {editV && (
        <Modal title={editV.id ? "Edit Vendor" : "Add Vendor"} onClose={() => setEditV(null)}>
          <VForm v={editV} onChange={(k, val) => setEditV(ev => ({ ...ev, [k]: val }))}
            onSave={() => { const nv = { ...editV, id: editV.id || genId(), maxConcurrent: parseInt(editV.maxConcurrent) || 0, priority: parseInt(editV.priority) || 1, returnWindow: editV.returnWindow ? parseInt(editV.returnWindow) : null }; setVendors(prev => nv.id && prev.find(x => x.id === nv.id) ? prev.map(x => x.id === nv.id ? nv : x) : [...prev, nv]); setEditV(null); }}
            onDelete={() => { setVendors(prev => prev.filter(x => x.id !== editV.id)); setEditV(null); }}
            onCancel={() => setEditV(null)} />
        </Modal>
      )}
      {keyModal && (
        <Modal title="Google Places API Key" onClose={() => setKeyModal(false)}>
          <TI label="API Key" value={placesKey} onChange={setPlacesKey} placeholder="AIza…" />
          <Btn variant="amber" onClick={() => setKeyModal(false)} disabled={!placesKey}>Save</Btn>
        </Modal>
      )}
    </div>
  );
};

// ─── VIN INTAKE FLOW ─────────────────────────────────────────────────────────

// Build a single work order from a free-text intake note. Used as a no-AI fallback
// so a non-empty observation always becomes at least one work order.
const noteToWO = (text) => {
  const t = (text || "").toLowerCase();
  let category = "MECHANICAL";
  if (/dent|scratch|rust|paint|bumper|panel|body|collision|buff/.test(t)) category = "BODYWORK";
  else if (/glass|windshield|window\b|chip|crack/.test(t)) category = "VENDOR";
  else if (/\bkey\b|lock|fob|ignition/.test(t)) category = "LOCKSMITH";
  else if (/clean|detail|vacuum|shampoo|wash|odor|stain/.test(t)) category = "DETAIL";
  else if (/headlight|tail ?light|bulb|wiper|emblem|mirror|lens|foggy|hazy/.test(t)) category = "COSMETIC";
  const assignedTo = category === "DETAIL" ? "sky" : category === "VENDOR" ? null : category === "COSMETIC" ? "robert" : "james";
  return { category, description: text.trim(), priority: "MEDIUM", estimatedHours: 1, assignedTo, requiresDiagnosis: false };
};

const VINIntakeFlow = ({ onComplete, onClose, vendors, vehicles, inventoryCache, onManualSync, onOpenExisting }) => {
  const [phase, setPhase] = useState("scan"); // scan | details | confirm
  const [syncingNow, setSyncingNow] = useState(false);
  const [chatInput, setChatInput] = useState("");
  const [chatLoading, setChatLoading] = useState(false);
  const [chatError, setChatError] = useState(null);
  const [vin, setVin] = useState("");
  const [form, setForm] = useState({ stockNumber: "", year: "", make: "", model: "", trim: "", exteriorColor: "", interiorColor: "", mileage: "", vendor: "", enteredBy: "", purchasePrice: "", sellingPrice: "", purchaseDate: todayStr(), arrivalDate: todayStr(), transmission: "" });
  const [kbbLoading, setKbbLoading] = useState(false);
  const [kbbValue, setKbbValue] = useState(null);
  const [dcsLoading, setDcsLoading] = useState(false);
  const [dcsCost, setDcsCost] = useState(null);
  const [apiConfig, setApiConfig] = useState({});
  const [apiOpen, setApiOpen] = useState(null);
  const setF = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // No-AI fallback: pull what we can straight from the text with regexes. Used when the
  // Claude API is unreachable (e.g. monthly usage limit hit) so intake still works —
  // a found VIN goes through the cache/NHTSA decode path which needs no AI at all.
  const localParseVehicleText = (text) => {
    const t = " " + text.trim() + " ";
    const vinM = t.match(/\b([A-HJ-NPR-Z0-9]{17})\b/i) || t.match(/\bvin[:\s#]*([A-HJ-NPR-Z0-9]{11,17})\b/i);
    const stockM = t.match(/\bstock\s*#?\s*:?\s*(\d{3,6})\b/i);
    const yearM = t.match(/\b(19[5-9]\d|20[0-4]\d)\b/);
    const mileageM = t.match(/([\d,]{4,9})\s*(?:mi\b|miles\b)/i);
    const transmission = /\b(stick|manual|5[- ]?speed|6[- ]?speed)\b/i.test(t) ? "Manual"
      : /\b(auto|automatic)\b/i.test(t) ? "Automatic" : "";
    return {
      vin: vinM ? vinM[1].toUpperCase() : "",
      stockNumber: stockM ? stockM[1] : "",
      year: yearM ? yearM[1] : "",
      mileage: mileageM ? mileageM[1].replace(/,/g, "") : "",
      transmission,
    };
  };

  const handleChatIntake = async () => {
    if (!chatInput.trim()) return;
    setChatLoading(true); setChatError(null);
    try {
      const res = await fetch("/api/claude", { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${await getIdToken()}` },
        body: JSON.stringify({
          model: "claude-sonnet-4-6", max_tokens: 700,
          messages: [{ role: "user", content: `You are a vehicle intake assistant for a car dealership. The user will describe a vehicle they want to add. Extract all available information and return ONLY valid JSON with these fields (use empty string for unknown fields, never null):
{"year":"","make":"","model":"","trim":"","vin":"","stockNumber":"","mileage":"","transmission":"","drivetrain":"","exteriorColor":"","interiorColor":"","bodyStyle":"","engine":"","purchasePrice":"","sellingPrice":"","initialObservations":""}
Transmission: if they say "stick", "manual", "5-speed", "6-speed" → "Manual". If "auto" → "Automatic".
Year: convert abbreviations — "97" → "1997", "05" → "2005" (if <30 use 2000s, if >=30 use 1900s).
Make/model: correct common misspellings. Body style: coupe/2-door, sedan/4-door, SUV, truck, van, convertible.
initialObservations: capture ALL reported issues, symptoms, needed repairs, or damage descriptions as a plain sentence. e.g. "Needs rim replacement. Misfires at stop lights." Include anything that sounds like a problem, repair need, or condition note.
User message: "${chatInput.replace(/"/g,'\"')}"` }]
        })
      });
      const data = await res.json();
      if (data?.error?.message) throw new Error(data.error.message);
      const text = (data.content?.[0]?.text || "{}").replace(/```json|```/g,"").trim();
      const parsed = JSON.parse(text);
      setForm(f => ({
        ...f,
        year:        parsed.year        || f.year,
        make:        parsed.make        || f.make,
        model:       parsed.model       || f.model,
        trim:        parsed.trim        || f.trim,
        mileage:     parsed.mileage     || f.mileage,
        transmission:parsed.transmission|| f.transmission,
        drivetrain:  parsed.drivetrain  || f.drivetrain,
        exteriorColor:parsed.exteriorColor||f.exteriorColor,
        interiorColor:parsed.interiorColor||f.interiorColor,
        bodyStyle:   parsed.bodyStyle   || f.bodyStyle,
        engine:      parsed.engine      || f.engine,
        stockNumber: parsed.stockNumber || f.stockNumber,
        purchasePrice:parsed.purchasePrice||f.purchasePrice,
        sellingPrice:parsed.sellingPrice ||f.sellingPrice,
      }));
      if (parsed.vin && parsed.vin.length >= 10) {
        setVin(parsed.vin);
        setForm(f => ({ ...f, vinLast6: parsed.vin.slice(-6) }));
      }
      if (parsed.initialObservations) {
        setInitialDesc(parsed.initialObservations);
      }
      setPhase("details");
      setChatInput("");
    } catch(e) {
      // AI failed (API limit, network, bad JSON) — fall back to direct text extraction
      // so a typed VIN / stock # still gets the vehicle in without AI.
      const local = localParseVehicleText(chatInput);
      const apiDown = /usage limit|rate limit|overloaded|credit/i.test(e?.message || "");
      if (local.stockNumber || local.vin || local.year) {
        setForm(f => ({
          ...f,
          year: local.year || f.year,
          mileage: local.mileage || f.mileage,
          transmission: local.transmission || f.transmission,
          stockNumber: local.stockNumber || f.stockNumber,
        }));
        setChatInput("");
        if (local.stockNumber) { await handleStockLookup(local.stockNumber); }
        else if (local.vin) { await handleVIN(local.vin); }
        else { setPhase("details"); }
        setChatError((apiDown ? "AI is temporarily unavailable (API usage limit). " : "") + "Read the VIN/stock directly — double-check the filled fields.");
      } else {
        setChatError(apiDown
          ? "AI is temporarily unavailable (API usage limit reached). Use the inventory dropdown, stock #, or VIN lookup below instead."
          : "Couldn't parse that — try again or fill in manually.");
      }
    } finally {
      setChatLoading(false);
    }
  };

  const handleVIN = async (scannedVIN) => {
    setVin(scannedVIN);
    const last6 = scannedVIN.slice(-6);
    setForm(f => ({ ...f, vinLast6: last6 }));
    setPhase("details");

    // 1. Check dealer inventory cache first — instant population
    const cached = findInCache(inventoryCache, { vin: scannedVIN, vinLast6: last6 });
    if (cached) {
      setForm(f => ({
        ...f,
        stockNumber: cached.stockNumber || f.stockNumber,
        year: cached.year || f.year,
        make: cached.make || f.make,
        model: cached.model || f.model,
        trim: cached.trim || "",
        mileage: cached.mileage ? String(cached.mileage) : f.mileage,
        exteriorColor: cached.exteriorColor || f.exteriorColor,
        interiorColor: cached.interiorColor || f.interiorColor,
        engine: cached.engine || "",
        drivetrain: cached.drivetrain || "",
        transmission: cached.transmission || "",
        bodyStyle: cached.bodyStyle || "",
        purchasePrice: cached.price ? String(cached.price) : f.purchasePrice,
        vinLast6: last6,
      }));
      return; // Skip NHTSA if we have full data
    }

    // 2. Fallback: NHTSA free VIN decode — get everything available
    try {
      const res = await fetch(`https://vpic.nhtsa.dot.gov/api/vehicles/decodevinvalues/${scannedVIN}?format=json`);
      const data = await res.json();
      const r = data.Results?.[0];
      if (r && r.ModelYear) {
        const transRaw = r.TransmissionStyle || "";
        const trans = transRaw.toLowerCase().includes("auto") ? "Automatic"
          : transRaw.toLowerCase().includes("manual") ? "Manual" : transRaw || "";
        const bodyRaw = r.BodyClass || "";
        setForm(f => ({
          ...f,
          year: r.ModelYear || f.year,
          make: r.Make || f.make,
          model: r.Model || f.model,
          trim: r.Trim || f.trim || "",
          engine: r.DisplacementL ? `${parseFloat(r.DisplacementL).toFixed(1)}L ${r.EngineCylinders || ""}cyl`.trim() : f.engine || "",
          drivetrain: r.DriveType || f.drivetrain || "",
          transmission: trans || f.transmission || "",
          bodyStyle: bodyRaw || f.bodyStyle || "",
          doors: r.Doors || f.doors || "",
        }));
      }
    } catch {}
  };

  const handleStockLookup = async (stockNum) => {
    if (!stockNum) return;
    // If cache is empty, trigger a sync first
    if (!inventoryCache?.vehicles?.length && onManualSync) {
      setSyncingNow(true);
      await onManualSync().catch(() => {});
      setSyncingNow(false);
    }
    const cached = findInCache(inventoryCache, { stockNumber: stockNum });
    if (cached) {
      setVin(cached.vin || "");
      setForm(f => ({
        ...f,
        stockNumber: stockNum,
        vin: cached.vin || "",
        year: cached.year || f.year,
        make: cached.make || f.make,
        model: cached.model || f.model,
        trim: cached.trim || "",
        mileage: cached.mileage ? String(cached.mileage) : f.mileage,
        exteriorColor: cached.exteriorColor || f.exteriorColor,
        interiorColor: cached.interiorColor || f.interiorColor,
        engine: cached.engine || "",
        drivetrain: cached.drivetrain || "",
        transmission: cached.transmission || f.transmission || "",
        bodyStyle: cached.bodyStyle || "",
        purchasePrice: cached.price ? String(cached.price) : f.purchasePrice,
        priceFromCSV: cached.priceFromCSV || false,
        sellingPrice: cached.sellingPrice ? String(cached.sellingPrice) : f.sellingPrice,
        vinLast6: cached.vin ? cached.vin.slice(-6) : f.vinLast6,
      }));
    } else {
      // Not in cache — still proceed with stock number pre-filled
      setForm(f => ({ ...f, stockNumber: stockNum }));
    }
    setPhase("details");
  };

  const handleLast6Lookup = (last6) => {
    if (!last6) return;
    const cached = findInCache(inventoryCache, { vinLast6: last6 });
    if (cached?.vin) {
      handleVIN(cached.vin);
    } else {
      // Not in cache — proceed with last 6 pre-filled, user can fill rest manually
      setForm(f => ({ ...f, vinLast6: last6 }));
      setPhase("details");
    }
  };

  const lookupKBB = async () => {
    if (!form.mileage) {
      alert("Please enter mileage before looking up valuation.");
      return;
    }
    setKbbLoading(true);
    try {
      // Call the local Autoniq Playwright server
      const serverUrl = process.env.REACT_APP_AUTONIQ_SERVER_URL || "http://localhost:3001";
      const res = await fetch(`${serverUrl}/valuate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ vin, mileage: parseInt(form.mileage) })
      });
      const data = await res.json();
      if (data.success && data.fairPurchasePrice) {
        setKbbValue(data.fairPurchasePrice);
      } else {
        alert("Autoniq lookup failed: " + (data.error || "Unknown error") + "\n\nMake sure autoniq-server.js is running in a separate Command Prompt window.");
      }
    } catch (err) {
      alert("Cannot reach Autoniq valuation server.\n\nIf running locally: make sure autoniq-server.js is running in a separate Command Prompt.\n\nIf deployed to Railway: check that REACT_APP_AUTONIQ_SERVER_URL is set in your .env file.");
    }
    setKbbLoading(false);
  };

  const lookupDCS = async () => {
    if (!apiConfig.dcs) { setApiOpen("dcs"); return; }
    setDcsLoading(true);
    await new Promise(r => setTimeout(r, 900));
    const est = parseFloat(form.purchasePrice) || Math.round(5000 + Math.random() * 10000);
    setDcsCost(est);
    setDcsLoading(false);
  };

  const [phase2, setPhase2] = useState("form"); // form | describe
  const [initialDesc, setInitialDesc] = useState("");

  const [generatingWOs, setGeneratingWOs] = useState(false);
  const [proposedIntakeWOs, setProposedIntakeWOs] = useState(null);
  const [approveModal, setApproveModal] = useState(false); // show WO approval modal
  const [approvedWOIndices, setApprovedWOIndices] = useState(new Set()); // which WOs are checked
  const [manualWO, setManualWO] = useState({ category: "MECHANICAL", description: "", priority: "MEDIUM" }); // hand-entered intake WO

  // Hand-enter a work order at intake without the AI. Appends to the proposed list (pre-approved).
  const addManualIntakeWO = () => {
    if (!manualWO.description.trim()) return;
    const wo = {
      category: manualWO.category,
      description: manualWO.description.trim(),
      priority: manualWO.priority,
      estimatedHours: 1,
      assignedTo: manualWO.category === "DETAIL" ? "sky" : manualWO.category === "VENDOR" ? null : "james",
      requiresDiagnosis: false,
    };
    const next = [...(proposedIntakeWOs || []), wo];
    setProposedIntakeWOs(next);
    setApprovedWOIndices(prev => new Set([...prev, next.length - 1]));
    setManualWO({ category: "MECHANICAL", description: "", priority: "MEDIUM" });
  };


  const generateIntakeWOs = async (thenApprove = false) => {
    if (!initialDesc.trim()) { if (thenApprove) setApproveModal(true); else finish([]); return; }
    setGeneratingWOs(true);
    try {
      const res = await fetch("/api/claude", { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${await getIdToken()}` },
        body: JSON.stringify({ model: "claude-sonnet-4-6", max_tokens: 1000, messages: [{ role: "user", content:
          `Auto dealership intake. Parse these intake observations for a ${form.year} ${form.make} ${form.model} and generate specific work orders.

Observations: "${initialDesc}"

Assignment rules:
- Sky (sky) → DETAIL only
- James (james) → MECHANICAL, BODYWORK, anything needing a lift
- Robert (robert) → quick cosmetic ≤20min, no lift (bulbs, wipers, emblems, tire pressure)
- Vendor (null) → glass, body shop, locksmith

For any mechanical/body issue that was NOT professionally diagnosed: set requiresDiagnosis=true and add "(Requires diagnosis — not professionally assessed)" to description.

Respond ONLY in JSON:
{"workOrders":[{"category":"MECHANICAL|DETAIL|BODYWORK|LOCKSMITH|COSMETIC|VENDOR|OTHER","description":"specific shop-language description","priority":"HIGH|MEDIUM|LOW","estimatedHours":1,"assignedTo":"james|sky|robert|null","requiresDiagnosis":true,"diagnosisRationale":"what the intake person observed"}]}` }] })
      });
      const data = await res.json();
      const parsed = JSON.parse((data.content?.[0]?.text || "{}").replace(/```json|```/g, "").trim());
      let wos = parsed.workOrders || [];
      // Never produce 0 from a non-empty note: if the AI returned nothing (or the
      // call errored into an empty parse), fall back to a work order built from the
      // raw note text so the observation is never silently dropped.
      if (wos.length === 0 && initialDesc.trim()) {
        wos = [noteToWO(initialDesc)];
      }
      setProposedIntakeWOs(wos);
      // Default: all approved
      setApprovedWOIndices(new Set(wos.map((_, i) => i)));
      if (thenApprove) setApproveModal(true);
    } catch(e) {
      // AI call failed entirely — still create a work order from the note.
      const fb = initialDesc.trim() ? [noteToWO(initialDesc)] : [];
      setProposedIntakeWOs(fb);
      setApprovedWOIndices(new Set(fb.map((_, i) => i)));
      if (thenApprove) { if (fb.length) setApproveModal(true); else finish([]); }
    }
    setGeneratingWOs(false);
  };

  const finish = (extraWOs = []) => {
    try {
      const desc = (initialDesc || "").toLowerCase();
      const hasMechanical = /noise|knock|shake|vibrat|pull|leak|smoke|rough|stall|misfire|check engine|brake|steering|suspension|transmission|engine|oil|coolant|overheating/.test(desc);
      const hasBodywork = /dent|scratch|rust|paint|bumper|panel|glass|crack|chip|body|collision/.test(desc);

      const initialWOs = [...extraWOs];
      // Always add detail WO for inventory intake
      // Detail WO is no longer auto-added — user must explicitly request via "Add Detail" button on vehicle page

      const newVehicle = {
        id: genId(), ...form,
        vin: vin || null, vinLast6: vin ? vin.slice(-6) : null, vinPending: (!vin && form.noVin) || false,
        stage: "INTAKE",
        kbbValue: kbbValue || null, dcsCost: dcsCost || null,
        manualSalePrice: parseFloat(form.sellingPrice) || null,
        workOrders: initialWOs,
        notes: "", safetyFlags: [], conditionSummary: "",
        inspectorNotes: initialDesc || null,
        needsMechanical: hasMechanical,
        needsBodywork: hasBodywork,
      };
      onComplete(newVehicle, parseFloat(form.purchasePrice) || dcsCost || 0);
    } catch(e) {
      console.error("finish() error:", e);
      alert("Something went wrong adding the vehicle. Please try again.\n\nError: " + e.message);
    }
  };

  if (phase === "scan") {
    return (
      <div style={{ padding: "0" }}>
        {/* AI chat intake — describe vehicle in plain language */}
        <div style={{ background:`linear-gradient(135deg,#1e1b4b,#312e81)`, border:`1px solid #6366f150`, borderRadius:12, padding:"12px 14px", marginBottom:16 }}>
          <div style={{ fontSize:11, fontWeight:800, color:"#a5b4fc", marginBottom:6, textTransform:"uppercase", letterSpacing:"0.05em" }}>✨ Describe the vehicle</div>
          <div style={{ fontSize:10, color:"rgba(255,255,255,0.5)", marginBottom:8 }}>e.g. "97 Mystique stick shift 2-door, VIN HGF123456789, stock 12288"</div>
          <div style={{ display:"flex", gap:6 }}>
            <input value={chatInput} onChange={e => setChatInput(e.target.value)}
              onKeyDown={e => e.key==="Enter" && handleChatIntake()}
              placeholder="Type vehicle description…"
              style={{ flex:1, background:"rgba(255,255,255,0.1)", border:"1px solid rgba(255,255,255,0.2)", borderRadius:8, padding:"9px 12px", color:"#fff", fontSize:12, fontFamily:"inherit", outline:"none" }} />
            <button onClick={handleChatIntake} disabled={chatLoading || !chatInput.trim()}
              style={{ background:"#6366f1", border:"none", borderRadius:8, padding:"0 14px", cursor:"pointer", fontSize:13, fontWeight:800, color:"#fff", fontFamily:"inherit", opacity:chatLoading||!chatInput.trim()?0.5:1 }}>
              {chatLoading ? "…" : "→"}
            </button>
          </div>
          {chatError && <div style={{ fontSize:10, color:"#fca5a5", marginTop:6 }}>{chatError}</div>}
        </div>
        <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12 }}>
          <div style={{ flex:1, height:1, background:"rgba(255,255,255,0.1)" }} />
          <span style={{ fontSize:10, color:C.textM }}>or look up by stock/VIN below</span>
          <div style={{ flex:1, height:1, background:"rgba(255,255,255,0.1)" }} />
        </div>
        {/* Quick lookup by stock# or last 6 */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
            {inventoryCache?.vehicles?.length > 0 ? (
              <div style={{ flex: 1, background: C.green + "10", border: `1px solid ${C.green}30`, borderRadius: 8, padding: "7px 12px", fontSize: 10, color: C.green }}>
                ✓ {inventoryCache.vehicles.length} vehicles cached · {inventoryCache.lastSync ? new Date(inventoryCache.lastSync).toLocaleDateString("en-US", {month:"short",day:"numeric",hour:"numeric",minute:"2-digit"}) : ""}
              </div>
            ) : (
              <div style={{ flex: 1, background: C.amber + "10", border: `1px solid ${C.amber}30`, borderRadius: 8, padding: "7px 12px", fontSize: 10, color: C.amber }}>
                ⚠ No inventory cached yet — tap Sync to pull from your site
              </div>
            )}
            {onManualSync && (
              <button onClick={async () => {
                setSyncingNow(true);
                await onManualSync().catch(() => {});
                setSyncingNow(false);
              }} disabled={syncingNow}
                style={{ background: C.amber, border: "none", borderRadius: 7, padding: "6px 12px", cursor: "pointer", fontSize: 10, fontWeight: 700, color: "#000", fontFamily: "inherit", flexShrink: 0 }}>
                {syncingNow ? "Syncing…" : "↺ Sync"}
              </button>
            )}
          </div>
          {/* Vehicle picker dropdown — the FULL lot inventory. Vehicles already in recon
              stay listed (marked ✔) and open the existing vehicle instead of re-adding it. */}
          {(() => {
            const reconByStock = new Map(vehicles.filter(v => v.stockNumber).map(v => [String(v.stockNumber), v]));
            const lot = (inventoryCache?.vehicles || [])
              .filter(v => v.stockNumber)
              .sort((a, b) => {
                const makeA = (a.make||"").toLowerCase(), makeB = (b.make||"").toLowerCase();
                if (makeA !== makeB) return makeA.localeCompare(makeB);
                const modelA = (a.model||"").toLowerCase(), modelB = (b.model||"").toLowerCase();
                if (modelA !== modelB) return modelA.localeCompare(modelB);
                return (b.year||0) - (a.year||0);
              });
            if (!lot.length) return null;
            const inReconCount = lot.filter(v => reconByStock.has(String(v.stockNumber))).length;
            return (
              <div style={{ marginBottom: 10 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 4 }}>📋 Select from Inventory ({lot.length} on lot{inReconCount ? ` · ${inReconCount} already in recon` : ""})</div>
                <select onChange={e => {
                  const stock = e.target.value;
                  e.target.value = "";
                  if (!stock) return;
                  const existing = reconByStock.get(String(stock));
                  if (existing && onOpenExisting) { onOpenExisting(existing); return; }
                  handleStockLookup(stock);
                }}
                  style={{ width: "100%", background: C.s2, border: `1px solid ${C.amber}`, borderRadius: 7, padding: "9px 10px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none", cursor: "pointer" }}>
                  <option value="">— Choose a vehicle —</option>
                  {lot.map(v => (
                    <option key={v.stockNumber} value={v.stockNumber}>
                      {v.year} {v.make} {v.model}{v.trim ? ` ${v.trim}` : ""} — Stock #{v.stockNumber}{v.mileage ? ` · ${parseInt(v.mileage).toLocaleString()}mi` : ""}{reconByStock.has(String(v.stockNumber)) ? " — ✔ in recon" : ""}
                    </option>
                  ))}
                </select>
              </div>
            );
          })()}
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 4 }}>Stock #</div>
              <div style={{ display: "flex", gap: 6 }}>
                <input id="stock-lookup" placeholder="e.g. 12345"
                  onKeyDown={e => { if (e.key === "Enter") { handleStockLookup(e.target.value.trim()); e.target.value = ""; } }}
                  style={{ flex: 1, background: C.s2, border: `1px solid ${C.border}`, borderRadius: 7, padding: "8px 10px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none" }} />
                <button onClick={() => { const el = document.getElementById("stock-lookup"); handleStockLookup(el?.value?.trim()); el.value = ""; }}
                  style={{ background: C.amber, border: "none", borderRadius: 7, padding: "0 10px", cursor: "pointer", fontSize: 11, fontWeight: 700, color: "#000", fontFamily: "inherit" }}>Go</button>
              </div>
            </div>
            <div>
              <div style={{ fontSize: 10, fontWeight: 700, color: C.textM, textTransform: "uppercase", marginBottom: 4 }}>Last 6 VIN</div>
              <div style={{ display: "flex", gap: 6 }}>
                <input id="last6-lookup" placeholder="e.g. A00352" maxLength={6}
                  onKeyDown={e => { if (e.key === "Enter" && e.target.value.length >= 4) { handleLast6Lookup(e.target.value.toUpperCase()); e.target.value = ""; } }}
                  style={{ flex: 1, background: C.s2, border: `1px solid ${C.border}`, borderRadius: 7, padding: "8px 10px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none", textTransform: "uppercase" }} />
                <button onClick={() => { const el = document.getElementById("last6-lookup"); handleLast6Lookup(el?.value?.toUpperCase()); el.value = ""; }}
                  style={{ background: C.amber, border: "none", borderRadius: 7, padding: "0 10px", cursor: "pointer", fontSize: 11, fontWeight: 700, color: "#000", fontFamily: "inherit" }}>Go</button>
              </div>
            </div>
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <div style={{ flex: 1, height: 1, background: C.border }} />
            <span style={{ fontSize: 10, color: C.textM }}>or scan / enter full VIN</span>
            <div style={{ flex: 1, height: 1, background: C.border }} />
          </div>
        </div>
        {/* Live barcode/photo scanning — only shown when device supports it */}
        {(typeof navigator !== "undefined" && navigator.mediaDevices) && (
          <VINScanner onScan={handleVIN} onClose={onClose} />
        )}
        {/* Manual Year/Make/Model entry — no VIN needed */}
        <div style={{ marginTop: 14 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
            <div style={{ flex: 1, height: 1, background: C.border }} />
            <span style={{ fontSize: 10, color: C.textM }}>or enter without VIN</span>
            <div style={{ flex: 1, height: 1, background: C.border }} />
          </div>
          <button onClick={() => {
            setForm(f => ({ ...f, year: "", make: "", model: "", trim: "", mileage: "", noVin: true }));
            setPhase("details");
          }}
            style={{ width: "100%", background: C.s2, border: `2px dashed ${C.border}`, borderRadius: 10, padding: "12px", cursor: "pointer", fontSize: 12, fontWeight: 700, color: C.textM, fontFamily: "inherit", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
            ✏️ Enter Year / Make / Model manually — add VIN later
          </button>
        </div>
      </div>
    );
  }

  const projectedGross = (kbbValue || 0) - (parseFloat(form.purchasePrice) || 0) - DEALER_PACK;

  return (
    <div>
      {/* VIN confirmed */}
      <div style={{ background: C.green + "12", border: `1px solid ${C.green}40`, borderRadius: 8, padding: "8px 14px", marginBottom: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <div><div style={{ fontSize: 9, color: C.green, fontWeight: 700, textTransform: "uppercase" }}>VIN Confirmed</div><div style={{ fontSize: 13, fontWeight: 700, color: C.text, fontFamily: "monospace" }}>{vin}</div></div>
        <button onClick={() => setPhase("scan")} style={{ background: "none", border: "none", color: C.textS, cursor: "pointer", fontSize: 11, fontFamily: "inherit" }}>Re-scan</button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
        <div style={{ gridColumn: "1/-1" }}><TI label="Stock Number *" value={form.stockNumber} onChange={v => setF("stockNumber", v)} placeholder="" /></div>
        <TI label="Year" value={form.year} onChange={v => setF("year", v)} placeholder="" />
        <TI label="Make" value={form.make} onChange={v => setF("make", v)} placeholder="" />
        <div style={{ gridColumn: "1/-1" }}><TI label="Model" value={form.model} onChange={v => setF("model", v)} placeholder="" /></div>
        <TI label="Exterior Color" value={form.exteriorColor} onChange={v => setF("exteriorColor", v)} placeholder="" />
        <TI label="Interior Color" value={form.interiorColor} onChange={v => setF("interiorColor", v)} placeholder="" />
        <TI label="Mileage" value={form.mileage} onChange={v => setF("mileage", v)} type="number" placeholder="" />
        <TI label="Purchase Price ($)" value={form.purchasePrice} onChange={v => setF("purchasePrice", v)} type="number" placeholder="" />
        <TI label="Selling Price ($)" value={form.sellingPrice} onChange={v => setF("sellingPrice", v)} type="number" placeholder="" />
        <TI label="Auction Recourse" value={form.vendor} onChange={v => setF("vendor", v)} placeholder="e.g. Mannheim 7-Day, AS-IS" />
        <TI label="Entered By" value={form.enteredBy || ""} onChange={v => setF("enteredBy", v)} placeholder="" />
        <TI label="Purchase Date" value={form.purchaseDate} onChange={v => setF("purchaseDate", v)} type="date" />
        <TI label="Arrival Date" value={form.arrivalDate} onChange={v => setF("arrivalDate", v)} type="date" />
      </div>

      {/* Cost summary — simple, no API buttons */}
      {form.purchasePrice && (
        <div style={{ background: C.s2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px", marginBottom: 14 }}>
          <div style={{ display: "flex", gap: 20 }}>
            <div><div style={{ fontSize: 9, color: C.textM, fontWeight: 700, textTransform: "uppercase" }}>Purchase + Pack</div><div style={{ fontSize: 20, fontWeight: 800, color: C.amber, fontFamily: "'Bebas Neue', sans-serif" }}>{fmtMoney((parseFloat(form.purchasePrice) || 0) + DEALER_PACK)}</div><div style={{ fontSize: 9, color: C.textM }}>incl. ${DEALER_PACK} dealer pack</div></div>
          </div>
        </div>
      )}

      {/* Initial observation / description */}
      <div style={{ background: C.s2, border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 16px", marginBottom: 14 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: C.text, marginBottom: 4 }}>Initial Observations <span style={{ color: C.textM, fontWeight: 400 }}>(optional)</span></div>
        <div style={{ fontSize: 10, color: C.textM, marginBottom: 8, lineHeight: 1.5 }}>Describe anything noticed at intake — noises, leaks, body damage, interior condition, etc. AI will auto-generate work orders from your notes.</div>
        <textarea
          value={initialDesc}
          onChange={e => { setInitialDesc(e.target.value); setProposedIntakeWOs(null); }}
          placeholder="e.g. Car is overheating, needs front right headlight, deep scratch on driver door needs buff…"
          rows={3}
          style={{ width: "100%", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, padding: "8px 12px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none", resize: "vertical", boxSizing: "border-box", lineHeight: 1.5 }}
        />
        {initialDesc.trim() && !proposedIntakeWOs && !generatingWOs && (
          <button onClick={() => generateIntakeWOs(false)}
            style={{ marginTop: 8, background: C.purple + "15", border: `1px solid ${C.purple}40`, borderRadius: 7, padding: "6px 14px", cursor: "pointer", fontSize: 11, fontWeight: 700, color: C.purple, fontFamily: "inherit" }}>
            ✨ Preview Work Orders from Notes
          </button>
        )}
        {generatingWOs && <div style={{ fontSize: 11, color: C.textM, marginTop: 8, fontStyle: "italic" }}>✨ Generating work orders from your notes…</div>}

        {/* Hand-enter a work order — works with or without AI */}
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
          <div style={{ fontSize: 10, fontWeight: 800, color: C.textM, textTransform: "uppercase", marginBottom: 6 }}>✍️ Or add a work order by hand</div>
          <div style={{ display: "flex", gap: 6, marginBottom: 6, flexWrap: "wrap" }}>
            <select value={manualWO.category} onChange={e => setManualWO(w => ({ ...w, category: e.target.value }))}
              style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, padding: "7px 8px", color: C.text, fontSize: 11, fontFamily: "inherit", outline: "none" }}>
              {WO_CATS.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
            <select value={manualWO.priority} onChange={e => setManualWO(w => ({ ...w, priority: e.target.value }))}
              style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, padding: "7px 8px", color: C.text, fontSize: 11, fontFamily: "inherit", outline: "none" }}>
              <option value="HIGH">High</option>
              <option value="MEDIUM">Medium</option>
              <option value="LOW">Low</option>
            </select>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <input value={manualWO.description} onChange={e => setManualWO(w => ({ ...w, description: e.target.value }))}
              onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addManualIntakeWO(); } }}
              placeholder="e.g. Replace passenger front headrest"
              style={{ flex: 1, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6, padding: "7px 10px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
            <button onClick={addManualIntakeWO} disabled={!manualWO.description.trim()}
              style={{ background: C.amber, border: "none", borderRadius: 6, padding: "7px 14px", cursor: manualWO.description.trim() ? "pointer" : "not-allowed", fontSize: 11, fontWeight: 800, color: "#000", fontFamily: "inherit", opacity: manualWO.description.trim() ? 1 : 0.5, flexShrink: 0 }}>
              + Add
            </button>
          </div>
        </div>

        {proposedIntakeWOs && (
          <div style={{ marginTop: 10 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom: 6 }}>
              <div style={{ fontSize: 10, fontWeight: 800, color: "#000", textTransform: "uppercase" }}>Proposed Work Orders — check to approve</div>
              <div style={{ display:"flex", gap:6 }}>
                <button onClick={() => setApprovedWOIndices(new Set(proposedIntakeWOs.map((_,i)=>i)))}
                  style={{ background:"none", border:`1px solid ${C.border}`, borderRadius:6, padding:"2px 8px", cursor:"pointer", fontSize:9, fontWeight:700, color:C.textM, fontFamily:"inherit" }}>All</button>
                <button onClick={() => setApprovedWOIndices(new Set())}
                  style={{ background:"none", border:`1px solid ${C.border}`, borderRadius:6, padding:"2px 8px", cursor:"pointer", fontSize:9, fontWeight:700, color:C.textM, fontFamily:"inherit" }}>None</button>
              </div>
            </div>
            {proposedIntakeWOs.map((wo, i) => {
              const cat = WO_CATS.find(c => c.id === wo.category);
              const approved = approvedWOIndices.has(i);
              return (
                <label key={i} style={{ display:"flex", alignItems:"flex-start", gap:10, background: approved ? C.green+"08" : C.s2, border: `1px solid ${approved ? C.green+"40" : wo.requiresDiagnosis ? C.amber + "60" : C.border}`, borderRadius: 8, padding: "8px 12px", marginBottom: 6, cursor:"pointer" }}>
                  <input type="checkbox" checked={approved}
                    onChange={e => setApprovedWOIndices(prev => { const next = new Set(prev); e.target.checked ? next.add(i) : next.delete(i); return next; })}
                    style={{ accentColor:C.green, width:16, height:16, marginTop:2, flexShrink:0 }} />
                  <div style={{ flex:1 }}>
                    <div style={{ display: "flex", gap: 5, alignItems: "center", marginBottom: 3, flexWrap:"wrap" }}>
                      {cat && <span style={{ background: cat.color + "25", color: cat.color, fontSize: 9, fontWeight: 800, padding: "1px 6px", borderRadius: 8 }}>{cat.label}</span>}
                      <span style={{ background: wo.priority === "HIGH" ? C.red+"20" : C.amber+"20", color: wo.priority === "HIGH" ? C.red : C.amber, fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 8 }}>{wo.priority}</span>
                      {wo.requiresDiagnosis && <span style={{ background: C.amber+"20", color: C.amber, fontSize: 9, fontWeight: 700, padding: "1px 6px", borderRadius: 8 }}>⚠ Needs Diagnosis</span>}
                    </div>
                    <div style={{ fontSize: 11, fontWeight: 600, color: approved ? C.text : C.textM }}>{wo.description}</div>
                    {wo.assignedTo && <div style={{ fontSize: 9, color: C.textM }}>→ {wo.assignedTo}</div>}
                  </div>
                </label>
              );
            })}
            <div style={{ fontSize:10, color:C.textM, marginTop:4 }}>
              {approvedWOIndices.size} of {proposedIntakeWOs.length} work order{proposedIntakeWOs.length!==1?"s":""} selected
            </div>
          </div>
        )}
      </div>

      <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
        <button style={{ background: C.amber, border: "none", borderRadius: 8, padding: "10px 18px", cursor: "pointer", fontSize: 13, fontWeight: 800, color: "#000", fontFamily: "inherit", display: "flex", alignItems: "center", gap: 6 }}
          onClick={async () => {
            try {
              const missing = [];
              if (!String(form.stockNumber || "").trim()) missing.push("Stock Number");
              if (!String(form.year || "").trim()) missing.push("Year");
              if (!String(form.make || "").trim()) missing.push("Make");
              if (!String(form.model || "").trim()) missing.push("Model");
              if (missing.length > 0) {
                alert("⚠️ Missing required fields:\n\n" + missing.map(m => "• " + m).join("\n"));
                return;
              }
              // Any proposed WOs (AI-generated OR hand-entered) → go straight to approval.
              if (proposedIntakeWOs && proposedIntakeWOs.length) {
                setApproveModal(true);
              } else if (initialDesc.trim()) {
                // Have notes but nothing generated yet — let AI generate, then approve.
                await generateIntakeWOs(true);
              } else {
                // Nothing to add — just create the vehicle.
                finish([]);
              }
            } catch(e) {
              alert("Error: " + e.message);
            }
          }}>
          {generatingWOs ? "✨ Generating work orders…" : "✓ Add Vehicle → Start Diagnosis"}
        </button>
        <button style={{ background: "transparent", border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px", cursor: "pointer", fontSize: 12, fontWeight: 700, color: C.textS, fontFamily: "inherit" }}
          onClick={onClose}>
          Cancel
        </button>
      </div>

      {/* WO Approval Modal */}
      {approveModal && proposedIntakeWOs && (
        <Modal title="Approve Work Orders" onClose={() => setApproveModal(false)} wide>
          <div style={{ marginBottom:12 }}>
            <div style={{ fontSize:13, fontWeight:800, color:"#000", marginBottom:4 }}>
              AI generated {proposedIntakeWOs.length} work order{proposedIntakeWOs.length!==1?"s":""} from your intake notes
            </div>
            <div style={{ fontSize:11, color:C.textM }}>Select which ones to create. Unchecked items will be skipped.</div>
          </div>
          <div style={{ display:"flex", gap:8, marginBottom:12 }}>
            <button onClick={() => setApprovedWOIndices(new Set(proposedIntakeWOs.map((_,i)=>i)))}
              style={{ background:C.green+"15", border:`1px solid ${C.green}40`, borderRadius:7, padding:"5px 14px", cursor:"pointer", fontSize:11, fontWeight:700, color:C.green, fontFamily:"inherit" }}>
              ✓ Select All
            </button>
            <button onClick={() => setApprovedWOIndices(new Set())}
              style={{ background:"none", border:`1px solid ${C.border}`, borderRadius:7, padding:"5px 14px", cursor:"pointer", fontSize:11, fontWeight:700, color:C.textM, fontFamily:"inherit" }}>
              Clear All
            </button>
          </div>
          <div style={{ maxHeight:400, overflowY:"auto", marginBottom:14 }}>
            {proposedIntakeWOs.map((wo, i) => {
              const cat = WO_CATS.find(c => c.id === wo.category);
              const approved = approvedWOIndices.has(i);
              return (
                <label key={i} style={{ display:"flex", alignItems:"flex-start", gap:12, background:approved?C.green+"08":C.s2, border:`2px solid ${approved?C.green+"50":C.border}`, borderRadius:10, padding:"12px 14px", marginBottom:8, cursor:"pointer" }}>
                  <input type="checkbox" checked={approved}
                    onChange={e => setApprovedWOIndices(prev => { const next = new Set(prev); e.target.checked ? next.add(i) : next.delete(i); return next; })}
                    style={{ accentColor:C.green, width:18, height:18, marginTop:2, flexShrink:0 }} />
                  <div style={{ flex:1 }}>
                    <div style={{ display:"flex", gap:5, flexWrap:"wrap", marginBottom:4 }}>
                      {cat && <span style={{ background:cat.color+"25", color:cat.color, fontSize:9, fontWeight:800, padding:"2px 7px", borderRadius:8 }}>{cat.label}</span>}
                      <span style={{ background:wo.priority==="HIGH"?C.red+"20":C.amber+"20", color:wo.priority==="HIGH"?C.red:C.amber, fontSize:9, fontWeight:700, padding:"2px 7px", borderRadius:8 }}>{wo.priority}</span>
                      {wo.requiresDiagnosis && <span style={{ background:C.amber+"15", color:C.amber, fontSize:9, fontWeight:700, padding:"2px 7px", borderRadius:8 }}>⚠ Needs Diagnosis</span>}
                      {wo.estimatedHours > 0 && <span style={{ background:C.blue+"15", color:C.blue, fontSize:9, fontWeight:700, padding:"2px 7px", borderRadius:8 }}>{wo.estimatedHours}h</span>}
                    </div>
                    <div style={{ fontSize:12, fontWeight:700, color:approved?"#000":C.textM }}>{wo.description}</div>
                    {wo.assignedTo && <div style={{ fontSize:10, color:C.textM, marginTop:2 }}>Assigned → {wo.assignedTo}</div>}
                    {wo.diagnosisRationale && <div style={{ fontSize:10, color:C.textM, marginTop:2, fontStyle:"italic" }}>"{wo.diagnosisRationale}"</div>}
                  </div>
                </label>
              );
            })}
          </div>
          <div style={{ background:C.s2, borderRadius:8, padding:"10px 14px", marginBottom:14, fontSize:11, color:C.textM }}>
            {approvedWOIndices.size} of {proposedIntakeWOs.length} work orders selected
            {approvedWOIndices.size === 0 && <span style={{ color:C.amber, fontWeight:700 }}> — vehicle will be added with no work orders except detail</span>}
          </div>
          <div style={{ display:"flex", gap:8 }}>
            <button onClick={() => {
              const approved = proposedIntakeWOs
                .filter((_, i) => approvedWOIndices.has(i))
                .map(w => ({ id: genId(), ...w, status: "OPEN", createdAt: todayStr(), actualHours: 0 }));
              setApproveModal(false);
              finish(approved);
            }}
              style={{ flex:1, background:C.green, border:"none", borderRadius:10, padding:"12px", cursor:"pointer", fontSize:14, fontWeight:900, color:"#fff", fontFamily:"inherit" }}>
              ✓ Create {approvedWOIndices.size} Work Order{approvedWOIndices.size!==1?"s":""} & Add Vehicle
            </button>
            <button onClick={() => setApproveModal(false)}
              style={{ background:"none", border:`1px solid ${C.border}`, borderRadius:10, padding:"12px 16px", cursor:"pointer", fontSize:12, color:C.textM, fontFamily:"inherit" }}>
              Back
            </button>
          </div>
        </Modal>
      )}

      {apiOpen && (
        <Modal title={apiOpen === "kbb" ? "Autoniq Valuation" : "Connect DCS (Dealer Car Search)"} onClose={() => setApiOpen(null)}>
          <div style={{ fontSize: 12, color: C.textS, lineHeight: 1.7, marginBottom: 12 }}>
            {apiOpen === "kbb" ? "The Autoniq server is not running. Open a second Command Prompt window, navigate to your project folder, and run: node autoniq-server.js\n\nMake sure AUTONIQ_EMAIL and AUTONIQ_PASSWORD are set in your .env file." : "DCS (Dealer Car Search) API — log in at dealercarsearch.com and navigate to Settings → API Access to generate your key. The app will use this to fetch your purchase cost for each stock number."}
          </div>
          <TI label="API Key" value={apiConfig[apiOpen] || ""} onChange={v => setApiConfig(c => ({ ...c, [apiOpen]: v }))} placeholder="Paste API key" />
          <div style={{ display: "flex", gap: 8 }}><Btn variant="amber" onClick={() => setApiOpen(null)}>Save</Btn><Btn variant="ghost" onClick={() => setApiOpen(null)}>Cancel</Btn></div>
        </Modal>
      )}
    </div>
  );
};

// ─── HOLDING COST HELPER ─────────────────────────────────────────────────────

const calcHoldingCost = (vehicle) => {
  const days = daysSince(vehicle.arrivalDate);
  return { days, total: days * HOLDING_COST_PER_DAY, perDay: HOLDING_COST_PER_DAY };
};

// ─── HOLDING COST BADGE ───────────────────────────────────────────────────────

const HoldingCostBadge = ({ vehicle, compact }) => {
  const { days, total } = calcHoldingCost(vehicle);
  if (["LINE_READY","SOLD"].includes(vehicle.stage)) return null;
  const urgent = days > 10;
  const warn   = days > 7;
  const color  = urgent ? C.red : warn ? "#f97316" : C.textM;
  if (compact) return (
    <span style={{ fontSize: 10, color, fontWeight: 700, fontFamily: "monospace" }}>
      {urgent || warn ? "🔥" : "⏱"} ${total.toLocaleString()} holding
    </span>
  );
  return (
    <div style={{ background: color + "12", border: `1px solid ${color}40`, borderRadius: 6, padding: "6px 12px", display: "inline-flex", alignItems: "center", gap: 8 }}>
      <div style={{ fontSize: 9, color, fontWeight: 700, textTransform: "uppercase" }}>Holding Cost</div>
      <div style={{ fontSize: 18, fontWeight: 800, color, fontFamily: "'Bebas Neue', sans-serif", lineHeight: 1 }}>${total.toLocaleString()}</div>
      <div style={{ fontSize: 10, color, opacity: 0.7 }}>{days}d × ${HOLDING_COST_PER_DAY}/day</div>
    </div>
  );
};

// ─── PHOTO CAPTURE HOOK ───────────────────────────────────────────────────────

const usePhotoCapture = () => {
  const fileRef = useRef(null);
  const [pending, setPending] = useState(null); // { woId, vehicleId }

  const capturePhoto = (woId, vehicleId) => {
    setPending({ woId, vehicleId });
    fileRef.current?.click();
  };

  const handleFile = (e, onCapture) => {
    const file = e.target.files?.[0];
    if (!file || !pending) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      onCapture(pending.woId, pending.vehicleId, ev.target.result);
      setPending(null);
      e.target.value = "";
    };
    reader.readAsDataURL(file);
  };

  return { fileRef, capturePhoto, handleFile };
};

// ─── WO PHOTO STRIP ──────────────────────────────────────────────────────────

const WOPhotoStrip = ({ photos, onAdd, onRemove, vehicleId, woId, onAIRoute, woCategory }) => {
  const inputRef = useRef(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [description, setDescription] = useState("");
  const [descOpen, setDescOpen] = useState(false);
  const [pendingImg, setPendingImg] = useState(null);

  const handleFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setPendingImg(ev.target.result);
      setDescOpen(true);
      e.target.value = "";
    };
    reader.readAsDataURL(file);
  };

  const analyzeAndRoute = async () => {
    if (!pendingImg) return;
    setAnalyzing(true);
    try {
      // Add the photo immediately
      onAdd(woId, vehicleId, pendingImg, description);

      // Ask Claude to analyze the photo and description
      if (description.trim() && onAIRoute) {
        const res = await fetch("/api/claude", { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${await getIdToken()}` },
          body: JSON.stringify({
            model: "claude-sonnet-4-6",
            max_tokens: 300,
            messages: [{
              role: "user",
              content: [
                {
                  type: "image",
                  source: { type: "base64", media_type: "image/jpeg", data: pendingImg.split(",")[1] }
                },
                {
                  type: "text",
                  text: `This photo was taken during vehicle inspection. Employee description: "${description}"

Determine the correct department for this repair/issue. Return JSON only:
{"department":"MECHANICAL|DETAIL|COSMETIC|VENDOR|DOCUMENTATION","reason":"one sentence why","priority":"HIGH|MEDIUM|LOW","suggestedWODescription":"specific actionable work order description"}`
                }
              ]
            }]
          })
        });
        const data = await res.json();
        const parsed = JSON.parse((data.content?.[0]?.text || "{}").replace(/```json|```/g, "").trim());
        if (parsed.department && onAIRoute) {
          onAIRoute({ woId, vehicleId, department: parsed.department, reason: parsed.reason, priority: parsed.priority, suggestedWO: parsed.suggestedWODescription, photo: pendingImg, description });
        }
      }
    } catch (err) {
      console.error("Photo analysis failed:", err);
    }
    setAnalyzing(false);
    setPendingImg(null);
    setDescription("");
    setDescOpen(false);
  };

  const skipAnalysis = () => {
    if (pendingImg) onAdd(woId, vehicleId, pendingImg, description);
    setPendingImg(null);
    setDescription("");
    setDescOpen(false);
  };

  return (
    <div style={{ marginTop: 8 }}>
      <input ref={inputRef} type="file" accept="image/*" capture="environment" onChange={handleFile} style={{ display: "none" }} />

      {/* Pending image — description + route prompt */}
      {descOpen && pendingImg && (
        <div style={{ background: C.s2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 12px", marginBottom: 8 }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <img src={pendingImg} alt="" style={{ width: 60, height: 60, borderRadius: 6, objectFit: "cover", flexShrink: 0 }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 10, color: C.textM, fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>Describe what you see</div>
              <input
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="e.g. Cracked CV boot leaking grease, driver side front"
                style={{ width: "100%", background: C.surface, border: `1px solid ${C.border}`, borderRadius: 5, padding: "6px 8px", color: C.text, fontSize: 11, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }}
              />
            </div>
          </div>
          <div style={{ fontSize: 9, color: C.textM, marginBottom: 6 }}>
            AI will analyze the photo + description and suggest the correct department (mechanic, detailer, body shop, etc.)
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={analyzeAndRoute} disabled={analyzing}
              style={{ flex: 1, padding: "6px", borderRadius: 6, border: "none", background: C.purple, color: "#fff", fontWeight: 700, fontSize: 11, fontFamily: "inherit", cursor: analyzing ? "not-allowed" : "pointer" }}>
              {analyzing ? "Analyzing…" : "✨ Analyze & Route"}
            </button>
            <button onClick={skipAnalysis}
              style={{ padding: "6px 10px", borderRadius: 6, border: `1px solid ${C.border}`, background: "transparent", color: C.textS, fontWeight: 700, fontSize: 11, fontFamily: "inherit", cursor: "pointer" }}>
              Just Save
            </button>
          </div>
        </div>
      )}

      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        {(photos || []).map((p, i) => (
          <div key={i} style={{ position: "relative", flexShrink: 0 }}>
            <div style={{ width: 60, height: 60, borderRadius: 6, overflow: "hidden", border: `1px solid ${C.border}` }}>
              <img src={typeof p === "object" ? p.src : p} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </div>
            {typeof p === "object" && p.description && (
              <div title={p.description} style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "rgba(0,0,0,0.65)", fontSize: 7, color: "#fff", padding: "2px 3px", lineHeight: 1.2, borderRadius: "0 0 6px 6px", overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" }}>{p.description}</div>
            )}
            <button onClick={() => onRemove(woId, i)} style={{ position: "absolute", top: 2, right: 2, width: 16, height: 16, borderRadius: "50%", background: C.red, border: "none", color: "#fff", fontSize: 8, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 800 }}>×</button>
          </div>
        ))}
        <button onClick={() => inputRef.current?.click()}
          style={{ width: 60, height: 60, borderRadius: 6, border: `2px dashed ${C.border}`, background: C.s2, cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 2, color: C.textM }}>
          <Camera size={16} />
          <span style={{ fontSize: 8, fontWeight: 700, fontFamily: "inherit" }}>Photo</span>
        </button>
      </div>
    </div>
  );
};

// ─── DELIVERY SCHEDULER ───────────────────────────────────────────────────────

const DeliverySchedulerView = ({ vehicles, staff, onUpdateVehicle }) => {
  const [scheduleModal, setScheduleModal] = useState(null); // vehicleId
  const [deliveryDate, setDeliveryDate] = useState("");
  const [deliveryTime, setDeliveryTime] = useState("14:00");
  const [customerName, setCustomerName] = useState("");
  const [deliveryNotes, setDeliveryNotes] = useState("");

  const soldVehicles = vehicles.filter(v => v.stage === "SOLD");
  const scheduled = soldVehicles.filter(v => v.deliveryDate);
  const unscheduled = soldVehicles.filter(v => !v.deliveryDate);

  const saveDelivery = () => {
    if (!scheduleModal || !deliveryDate) return;
    const v = vehicles.find(x => x.id === scheduleModal);
    onUpdateVehicle({ ...v, deliveryDate, deliveryTime, deliveryCustomerName: customerName, deliveryNotes, deliveryStatus: "SCHEDULED" });
    setScheduleModal(null); setDeliveryDate(""); setDeliveryTime("14:00"); setCustomerName(""); setDeliveryNotes("");
  };

  const getAlerts = (v) => {
    if (!v.deliveryDate || !v.deliveryTime) return [];
    const deliveryTs = new Date(`${v.deliveryDate}T${v.deliveryTime}`).getTime();
    const now = Date.now();
    const hoursUntil = (deliveryTs - now) / 3600000;
    const openWOs = v.workOrders?.filter(w => w.status !== "COMPLETE") || [];
    const alerts = [];
    if (openWOs.length > 0 && hoursUntil <= DELIVERY_ALERT_HOURS && hoursUntil > 0) {
      alerts.push({ type: "URGENT", msg: `⚠ ${openWOs.length} open WO(s) — delivery in ${hoursUntil.toFixed(1)}h` });
    } else if (openWOs.length > 0) {
      alerts.push({ type: "WARN", msg: `${openWOs.length} open WO(s) before delivery` });
    }
    if (hoursUntil < 0) alerts.push({ type: "PAST", msg: "Delivery time has passed" });
    return alerts;
  };

  const groupByDate = scheduled.reduce((acc, v) => {
    const d = v.deliveryDate || "TBD";
    if (!acc[d]) acc[d] = [];
    acc[d].push(v);
    return acc;
  }, {});

  const sortedDates = Object.keys(groupByDate).sort();

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 28, fontWeight: 800, color: C.amber, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.06em", lineHeight: 1, display: "flex", alignItems: "center", gap: 10 }}>
          <Calendar size={22} color={C.amber} /> DELIVERY SCHEDULER
        </div>
        <div style={{ fontSize: 11, color: C.textM, marginTop: 2 }}>Schedule customer pick-ups · Alerts if open tasks remain before delivery</div>
      </div>

      {/* Urgent alerts */}
      {scheduled.filter(v => getAlerts(v).some(a => a.type === "URGENT")).map(v => (
        <div key={v.id} style={{ background: C.red + "12", border: `1px solid ${C.red}50`, borderRadius: 8, padding: "12px 16px", marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div>
            <div style={{ fontSize: 12, fontWeight: 800, color: C.red }}>🚨 URGENT — {v.year} {v.make} {v.model} #{v.stockNumber}</div>
            {getAlerts(v).map((a, i) => <div key={i} style={{ fontSize: 11, color: C.red, marginTop: 2 }}>{a.msg}</div>)}
          </div>
          <Badge color={C.red} small>Delivery {fmtDate(v.deliveryDate)} {v.deliveryTime}</Badge>
        </div>
      ))}

      {/* Calendar grid */}
      {sortedDates.length > 0 && (
        <div style={{ marginBottom: 24 }}>
          <div style={{ fontSize: 10, color: C.textM, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12 }}>Scheduled Deliveries</div>
          {sortedDates.map(date => (
            <div key={date} style={{ marginBottom: 12 }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.amber, marginBottom: 8, paddingBottom: 4, borderBottom: `1px solid ${C.border}` }}>
                📅 {fmtDate(date)} — {date === todayStr() ? "TODAY" : ""}
              </div>
              {groupByDate[date].map(v => {
                const alerts = getAlerts(v);
                const openWOs = v.workOrders?.filter(w => w.status !== "COMPLETE").length || 0;
                return (
                  <div key={v.id} style={{ background: C.surface, border: `1px solid ${alerts.some(a => a.type === "URGENT") ? C.red + "55" : alerts.length ? "#f9731655" : C.border}`, borderRadius: 8, padding: "12px 14px", marginBottom: 6 }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                      <div>
                        <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{v.year} {v.make} {v.model}</div>
                        <div style={{ fontSize: 10, color: C.textM, fontFamily: "monospace" }}>#{v.stockNumber}{v.deliveryCustomerName ? ` · ${v.deliveryCustomerName}` : ""}</div>
                        {alerts.map((a, i) => <div key={i} style={{ fontSize: 11, color: a.type === "URGENT" ? C.red : "#f97316", marginTop: 3 }}>{a.msg}</div>)}
                        {v.deliveryNotes && <div style={{ fontSize: 11, color: C.textS, marginTop: 4, fontStyle: "italic" }}>{v.deliveryNotes}</div>}
                      </div>
                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: 16, fontWeight: 800, color: C.text, fontFamily: "'Bebas Neue', sans-serif" }}>{v.deliveryTime}</div>
                        <div style={{ display: "flex", gap: 4, marginTop: 4, justifyContent: "flex-end" }}>
                          {openWOs > 0 && <Badge color="#f97316" small>{openWOs} open WO</Badge>}
                          <Badge color={v.deliveryStatus === "COMPLETE" ? C.green : C.blue} small>{v.deliveryStatus || "SCHEDULED"}</Badge>
                        </div>
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 6, marginTop: 10 }}>
                      <Btn variant="ghost" size="sm" onClick={() => { setScheduleModal(v.id); setDeliveryDate(v.deliveryDate); setDeliveryTime(v.deliveryTime || "14:00"); setCustomerName(v.deliveryCustomerName || ""); setDeliveryNotes(v.deliveryNotes || ""); }}>✏️ Edit</Btn>
                      {v.deliveryStatus !== "COMPLETE" && <Btn variant="green" size="sm" onClick={() => onUpdateVehicle({ ...v, deliveryStatus: "COMPLETE" })}>✓ Mark Delivered</Btn>}
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}

      {/* Unscheduled sold vehicles */}
      {unscheduled.length > 0 && (
        <div>
          <div style={{ fontSize: 10, color: C.textM, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 10 }}>Sold — No Delivery Scheduled ({unscheduled.length})</div>
          {unscheduled.map(v => (
            <div key={v.id} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px", marginBottom: 6, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div>
                <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{v.year} {v.make} {v.model} #{v.stockNumber}</div>
                <div style={{ fontSize: 10, color: C.textM }}>Sold {fmtDate(v.saleDate)}</div>
              </div>
              <Btn variant="amber" size="sm" onClick={() => { setScheduleModal(v.id); setDeliveryDate(addDays(todayStr(), 1)); }}>
                <Calendar size={11} /> Schedule
              </Btn>
            </div>
          ))}
        </div>
      )}

      {soldVehicles.length === 0 && (
        <div style={{ textAlign: "center", color: C.textM, padding: 48, fontSize: 13 }}>
          <Calendar size={32} color={C.textM} style={{ margin: "0 auto 12px", display: "block" }} />
          No sold vehicles yet. Deliveries will appear here once vehicles are marked sold.
        </div>
      )}

      {/* Schedule modal */}
      {scheduleModal && (
        <Modal title="Schedule Delivery" onClose={() => setScheduleModal(null)}>
          {(() => { const v = vehicles.find(x => x.id === scheduleModal); return v ? (
            <div>
              <div style={{ background: C.s2, border: `1px solid ${C.border}`, borderRadius: 8, padding: "10px 14px", marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{v.year} {v.make} {v.model}</div>
                <div style={{ fontSize: 10, color: C.textM, fontFamily: "monospace" }}>#{v.stockNumber}</div>
                {v.workOrders?.filter(w => w.status !== "COMPLETE").length > 0 && (
                  <div style={{ fontSize: 11, color: "#f97316", marginTop: 6 }}>⚠ {v.workOrders.filter(w => w.status !== "COMPLETE").length} open work order(s) on this vehicle</div>
                )}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <TI label="Delivery Date *" value={deliveryDate} onChange={setDeliveryDate} type="date" />
                <TI label="Delivery Time" value={deliveryTime} onChange={setDeliveryTime} type="time" />
              </div>
              <TI label="Customer Name" value={customerName} onChange={setCustomerName} placeholder="e.g. John Smith" />
              <TI label="Notes" value={deliveryNotes} onChange={setDeliveryNotes} placeholder="e.g. Customer coming from Portland, needs plates" rows={2} />
              <div style={{ display: "flex", gap: 8 }}>
                <Btn variant="amber" onClick={saveDelivery} disabled={!deliveryDate}><Calendar size={13} /> Save Delivery</Btn>
                <Btn variant="ghost" onClick={() => setScheduleModal(null)}>Cancel</Btn>
              </div>
            </div>
          ) : null; })()}
        </Modal>
      )}
    </div>
  );
};

// ─── DELIVERY QUICK MODAL ─────────────────────────────────────────────────────

const DeliveryQuickModal = ({ vehicle, onSave, onClose }) => {
  const [dDate, setDDate] = useState(addDays(todayStr(), 1));
  const [dTime, setDTime] = useState("14:00");
  const [dName, setDName] = useState(vehicle.deliveryCustomerName || "");
  const [dNote, setDNote] = useState(vehicle.deliveryNotes || "");
  const openWOs = vehicle.workOrders?.filter(w => w.status !== "COMPLETE").length || 0;
  return (
    <Modal title={`📅 Schedule Delivery — ${vehicle.year} ${vehicle.make} ${vehicle.model}`} onClose={onClose}>
      {openWOs > 0 && <div style={{ background: "#f9731615", border: "1px solid #f9731640", borderRadius: 6, padding: "8px 12px", marginBottom: 14, fontSize: 11, color: "#f97316" }}>⚠ {openWOs} open work order(s) on this vehicle — the app will alert staff before delivery.</div>}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <TI label="Delivery Date *" value={dDate} onChange={setDDate} type="date" />
        <TI label="Time" value={dTime} onChange={setDTime} type="time" />
      </div>
      <TI label="Customer Name" value={dName} onChange={setDName} placeholder="John Smith" />
      <TI label="Notes" value={dNote} onChange={setDNote} placeholder="Any special instructions" rows={2} />
      <div style={{ display: "flex", gap: 8 }}>
        <Btn variant="amber" onClick={() => onSave({ ...vehicle, deliveryDate: dDate, deliveryTime: dTime, deliveryCustomerName: dName, deliveryNotes: dNote, deliveryStatus: "SCHEDULED" })} disabled={!dDate}><Calendar size={13} /> Save Delivery</Btn>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
      </div>
    </Modal>
  );
};

// ─── BAY STATUS CARD ──────────────────────────────────────────────────────────

const BayCard = ({ bay, assignment, vehicle, onAssign, onRelease }) => {
  const occupied = !!assignment;
  return (
    <div style={{ background: occupied ? bay.color + "12" : C.surface, border: `2px solid ${occupied ? bay.color + "70" : C.border}`, borderRadius: 12, padding: "14px 16px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
        <div>
          <div style={{ fontSize: 20, marginBottom: 4 }}>{bay.icon}</div>
          <div style={{ fontSize: 13, fontWeight: 800, color: C.text, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.05em" }}>{bay.short.toUpperCase()}</div>
          <div style={{ fontSize: 10, color: C.textM, marginTop: 2, lineHeight: 1.5, maxWidth: 190 }}>{bay.description.split(".")[0]}.</div>
        </div>
        <div style={{ width: 12, height: 12, borderRadius: "50%", background: occupied ? C.red : C.green, flexShrink: 0, marginTop: 2, boxShadow: `0 0 8px ${occupied ? C.red : C.green}60` }} />
      </div>
      {occupied && vehicle ? (
        <div style={{ background: C.s2, borderRadius: 8, padding: "10px 12px", marginBottom: 10 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{vehicle.year} {vehicle.make} {vehicle.model}</div>
          <div style={{ fontSize: 10, color: C.textM, fontFamily: "monospace" }}>#{vehicle.stockNumber}</div>
          <div style={{ fontSize: 11, color: C.textS, marginTop: 3 }}>{assignment.woDescription}</div>
          <div style={{ fontSize: 10, color: C.textM, marginTop: 2 }}>Started: {assignment.startTime}</div>
        </div>
      ) : (
        <div style={{ fontSize: 11, color: C.textM, fontStyle: "italic", marginBottom: 10 }}>Available</div>
      )}
      {occupied
        ? <button onClick={() => onRelease(bay.id)} style={{ width: "100%", padding: "7px", border: `1px solid ${C.red}50`, borderRadius: 6, background: C.red + "15", color: C.red, fontWeight: 700, fontSize: 11, fontFamily: "inherit", cursor: "pointer" }}>🔓 Release Bay</button>
        : <button onClick={() => onAssign(bay.id)} style={{ width: "100%", padding: "7px", border: `1px solid ${bay.color}60`, borderRadius: 6, background: bay.color + "15", color: bay.color, fontWeight: 700, fontSize: 11, fontFamily: "inherit", cursor: "pointer" }}>+ Assign Vehicle</button>
      }
    </div>
  );
};

// ─── CLEANING CHECKLIST MODAL ────────────────────────────────────────────────

const CleaningChecklistModal = ({ vehicle, onClose, onComplete, onUpdateVehicle, ledger }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const phaseIcons = ["🚛","📋","🚿","🪑","✨","🅿️"];

  // Validate a checklist — must have phases with actual items
  const isValid = (cl) => cl && cl.phases?.length > 0 && cl.phases.some(p => p.items?.length > 0);

  const [checklist, setChecklist] = useState(() => {
    const saved = vehicle.cleaningChecklist;
    return isValid(saved) ? saved : null;
  });

  const purchaseEntry = ledger?.find(e => e.vehicleId === vehicle.id && e.category === "PURCHASE");
  const purchasePrice = purchaseEntry?.amount || parseFloat(vehicle.purchasePrice) || 0;

  useEffect(() => { if (!checklist) generate(); }, []);

  const generate = async () => {
    setLoading(true); setError(false);

    // Save currently checked items before regenerating
    const prevChecklist = vehicle.cleaningChecklist;
    const checkedItems = new Set();
    prevChecklist?.phases?.forEach(phase => {
      phase.items?.forEach(item => { if (item.done) checkedItems.add(item.text?.toLowerCase().trim()); });
    });

    // Clear cached checklist
    onUpdateVehicle({ ...vehicle, cleaningChecklist: null });

    const bodyType = /truck|f-?150|f-?250|silverado|sierra|ram|tacoma|tundra|colorado|ranger|frontier|canyon/i.test(`${vehicle.make} ${vehicle.model}`) ? "truck" :
      /suv|expedition|tahoe|suburban|navigator|escalade|explorer|4runner|sequoia|pilot|pathfinder|highlander|traverse|yukon|blazer|bronco|passport|cx-9|rav4|cr-v|rogue|equinox|escape|terrain|edge|tiguan|tucson|santa fe|sorento|telluride/i.test(`${vehicle.make} ${vehicle.model}`) ? "suv" :
      /van|caravan|odyssey|sienna|pacifica/i.test(`${vehicle.make} ${vehicle.model}`) ? "van" :
      /convertible|cabriolet|roadster/i.test(`${vehicle.make} ${vehicle.model}`) ? "convertible" : "sedan";

    const conditionSummary = vehicle.juliaInspectionSummary?.overallCondition || "GOOD";
    const inspectorNotes = [vehicle.conditionSummary, ...(vehicle.safetyFlags || [])].filter(Boolean).join(". ");
    // Include any special detail instructions from the DETAIL work order
    const detailWO = vehicle.workOrders?.find(w => w.category === "DETAIL" && w.status !== "COMPLETE");
    const specialInstructions = detailWO?.description || "";
    const checklistAdditions = (vehicle.checklistAdditions || []).map(i => i.text).join("; ");
    const prompt = `Vehicle: ${vehicle.year} ${vehicle.make} ${vehicle.model} (${bodyType})\nPurchase price: $${purchasePrice}\nMileage: ${vehicle.mileage || "unknown"}\nCondition: ${conditionSummary}\nExterior: ${vehicle.exteriorColor || "unknown"} | Interior: ${vehicle.interiorColor || "unknown"}\nStock #: ${vehicle.stockNumber}\nInspector notes: ${inspectorNotes || "None provided — assume average condition for year/mileage"}${specialInstructions ? `\nSpecial detail instructions: ${specialInstructions}` : ""}${checklistAdditions ? `\nAdditional requests: ${checklistAdditions}` : ""}`;

    try {
      const res = await fetch("/api/claude", { method: "POST", headers: { "Content-Type": "application/json", "Authorization": `Bearer ${await getIdToken()}` },
        body: JSON.stringify({ model: "claude-sonnet-4-6", max_tokens: 2000, system: CLEANING_SYSTEM_PROMPT, messages: [{ role: "user", content: prompt }] })
      });
      const data = await res.json();
      const raw = data.content?.[0]?.text || "{}";
      const cleaned = raw.replace(/```json/g, "").replace(/```/g, "").trim();
      const start = cleaned.indexOf("{"); const end = cleaned.lastIndexOf("}");
      const cl = (start >= 0 && end > start) ? JSON.parse(cleaned.slice(start, end + 1)) : null;

      if (isValid(cl)) {
        // Restore previously checked items by matching text
        if (checkedItems.size > 0) {
          cl.phases = cl.phases.map(phase => ({
            ...phase,
            items: phase.items.map(item => ({
              ...item,
              done: checkedItems.has(item.text?.toLowerCase().trim()) ? true : item.done
            }))
          }));
        }
        setChecklist(cl);
        onUpdateVehicle({ ...vehicle, cleaningChecklist: cl });
      } else {
        // Fallback: hardcoded spiff checklist
        const fallback = {
          sortCategory: "Spiff", detailLevel: 1, lotPlacement: "STANDARD",
          phases: [
            { phase: 1, title: "Arrival Prep", items: [{ id:"p1_1", text:"Remove any window stickers, hanging tags, or loose papers", priority:"normal", done:false }, { id:"p1_2", text:"Check interior for personal items or trash", priority:"normal", done:false }] },
            { phase: 2, title: "Sort & Stage", items: [{ id:"p2_1", text:"Confirm Spiff level — light clean only", priority:"normal", done:false }, { id:"p2_2", text:"Park in wash bay", priority:"normal", done:false }] },
            { phase: 3, title: "Exterior Wash", items: [{ id:"p3_1", text:"Rinse entire vehicle with pressure washer", priority:"normal", done:false }, { id:"p3_2", text:"Hand wash exterior with soap and mitt", priority:"normal", done:false }, { id:"p3_3", text:"Clean wheels and tire sidewalls", priority:"normal", done:false }, { id:"p3_4", text:"Final rinse and dry with chamois/towel", priority:"normal", done:false }, { id:"p3_5", text:"Clean all glass inside and out", priority:"normal", done:false }] },
            { phase: 4, title: "Interior Prep", items: [{ id:"p4_1", text:"Vacuum all seats, floor, cargo area", priority:"normal", done:false }, { id:"p4_2", text:"Wipe down dash, door panels, and center console", priority:"normal", done:false }, { id:"p4_3", text:"Place fragrance wafer under seat", priority:"normal", done:false }] },
            { phase: 5, title: "Final Touches", items: [{ id:"p5_1", text:"Set A/C to low, radio to soft rock low", priority:"normal", done:false }, { id:"p5_2", text:"Apply price sticker to windshield", priority:"normal", done:false }, { id:"p5_3", text:"Place floor mats in trunk", priority:"normal", done:false }] },
            { phase: 6, title: "Lot Placement", items: [{ id:"p6_1", text:"Park in assigned lot position", priority:"normal", done:false }, { id:"p6_2", text:"Add key tag with stock number", priority:"normal", done:false }, { id:"p6_3", text:"Return extra keys to office", priority:"normal", done:false }] },
          ],
          inspectorPassthrough: [], sloganSuggestions: []
        };
        setChecklist(fallback);
        onUpdateVehicle({ ...vehicle, cleaningChecklist: fallback });
      }
    } catch { setError(true); }
    setLoading(false);
  };

  const toggleItem = (pi, ii) => {
    setChecklist(prev => {
      const updated = { ...prev, phases: prev.phases.map((ph, p) => p !== pi ? ph : { ...ph, items: ph.items.map((item, i) => i !== ii ? item : { ...item, done: !item.done }) }) };
      onUpdateVehicle({ ...vehicle, cleaningChecklist: updated });
      return updated;
    });
  };

  if (loading) return (
    <div style={{ textAlign: "center", padding: "48px 0" }}>
      <div style={{ fontSize: 36, marginBottom: 12 }}>🧼</div>
      <div style={{ fontSize: 13, color: C.textS }}>Building checklist for {vehicle.year} {vehicle.make} {vehicle.model}…</div>
      <div style={{ fontSize: 11, color: C.textM, marginTop: 6 }}>Checking inspector notes · vehicle type · price tier</div>
      <div style={{ width: 120, height: 4, borderRadius: 2, background: C.border, margin: "16px auto 0" }}>
        <div style={{ height: "100%", borderRadius: 2, background: C.amber, width: "60%", animation: "pulse 1.5s ease-in-out infinite" }} />
      </div>
    </div>
  );

  if (error) return (
    <div style={{ textAlign: "center", padding: 32 }}>
      <div style={{ fontSize: 12, color: C.red, marginBottom: 12 }}>Failed to generate checklist.</div>
      <Btn variant="amber" onClick={generate}>Try Again</Btn>
    </div>
  );
  if (!checklist) return null;

  const totalItems = checklist.phases?.reduce((s, p) => s + p.items.length, 0) || 0;
  const doneItems = checklist.phases?.reduce((s, p) => s + p.items.filter(i => i.done).length, 0) || 0;
  const allDone = doneItems === totalItems && totalItems > 0;

  return (
    <div>
      {/* Header badges */}
      <div style={{ display: "flex", gap: 10, alignItems: "center", marginBottom: 14, flexWrap: "wrap" }}>
        <div style={{ padding: "3px 10px", borderRadius: 6, background: checklist.detailLevel <= 1 ? C.green + "20" : checklist.sortCategory === "Full Detail" || checklist.sortCategory === "Premium Detail" ? C.purple + "20" : C.blue + "20", border: `1px solid ${checklist.detailLevel <= 1 ? C.green + "50" : C.blue + "50"}`, fontSize: 11, fontWeight: 700, color: checklist.detailLevel <= 1 ? C.green : C.blue }}>{checklist.sortCategory || "Spiff"}</div>
        <div style={{ padding: "3px 10px", borderRadius: 6, background: C.green + "15", border: `1px solid ${C.green}40`, fontSize: 11, fontWeight: 700, color: C.green }}>{checklist.lotPlacement === "FRONT_LINE" ? "🚦 Front Line" : "🅿️ Standard Lot"}</div>
        <div style={{ marginLeft: "auto", display: "flex", gap: 8, alignItems: "center" }}>
          <span style={{ fontSize: 11, color: C.textS, fontWeight: 700 }}>{doneItems}/{totalItems}</span>
          <button onClick={() => { setChecklist(null); generate(); }} style={{ background: "none", border: `1px solid ${C.border}`, borderRadius: 6, padding: "2px 8px", cursor: "pointer", fontSize: 9, color: C.textM, fontFamily: "inherit" }}>↺ Refresh</button>
        </div>
      </div>

      {/* Progress */}
      <div style={{ height: 6, borderRadius: 3, background: C.border, marginBottom: 18 }}>
        <div style={{ height: "100%", borderRadius: 3, width: `${totalItems ? (doneItems / totalItems) * 100 : 0}%`, background: allDone ? C.green : C.amber, transition: "width 0.3s, background 0.3s" }} />
      </div>

      {/* Inspector passthrough */}
      {checklist.inspectorPassthrough?.length > 0 && (
        <div style={{ background: C.amber + "12", border: `1px solid ${C.amber}40`, borderRadius: 8, padding: "10px 14px", marginBottom: 14 }}>
          <div style={{ fontSize: 10, color: C.amber, fontWeight: 800, textTransform: "uppercase", marginBottom: 6 }}>⭐ From Inspector — Pay Attention</div>
          {checklist.inspectorPassthrough.map((n, i) => <div key={i} style={{ fontSize: 12, color: C.text, marginBottom: 2 }}>• {n}</div>)}
        </div>
      )}

      {/* Slogan suggestions */}
      {checklist.sloganSuggestions?.length > 0 && (
        <div style={{ background: C.green + "10", border: `1px solid ${C.green}30`, borderRadius: 8, padding: "10px 14px", marginBottom: 14 }}>
          <div style={{ fontSize: 10, color: C.green, fontWeight: 800, textTransform: "uppercase", marginBottom: 6 }}>Suggested Stickers for This Vehicle</div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {checklist.sloganSuggestions.map((s, i) => <span key={i} style={{ background: C.green + "20", border: `1px solid ${C.green}40`, borderRadius: 4, padding: "2px 10px", fontSize: 11, color: C.green, fontWeight: 700 }}>{s}</span>)}
          </div>
        </div>
      )}

      {/* Phase checklist */}
      {checklist.phases?.map((phase, pi) => {
        const pDone = phase.items.filter(i => i.done).length;
        const pTotal = phase.items.length;
        const allP = pDone === pTotal;
        return (
          <div key={pi} style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, paddingBottom: 6, borderBottom: `1px solid ${C.border}` }}>
              <span style={{ fontSize: 16 }}>{phaseIcons[pi] || "•"}</span>
              <span style={{ fontSize: 12, fontWeight: 800, color: allP ? C.green : C.text, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.06em" }}>PHASE {phase.phase}: {phase.title.toUpperCase()}</span>
              <span style={{ fontSize: 10, color: C.textM, marginLeft: "auto" }}>{pDone}/{pTotal}</span>
              {allP && <span style={{ fontSize: 12, color: C.green }}>✓</span>}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
              {phase.items.map((item, ii) => (
                <div key={item.id || ii} onClick={() => toggleItem(pi, ii)}
                  style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "9px 12px", borderRadius: 7, background: item.done ? C.green + "10" : item.priority === "high" ? C.amber + "08" : C.s2, border: `1px solid ${item.done ? C.green + "35" : item.priority === "high" ? C.amber + "40" : C.border}`, cursor: "pointer" }}>
                  <div style={{ flexShrink: 0, width: 18, height: 18, borderRadius: 4, border: `2px solid ${item.done ? C.green : item.priority === "high" ? C.amber : C.textM}`, background: item.done ? C.green : "transparent", display: "flex", alignItems: "center", justifyContent: "center", marginTop: 1 }}>
                    {item.done && <span style={{ fontSize: 9, color: "#000", fontWeight: 900 }}>✓</span>}
                  </div>
                  <span style={{ fontSize: 12, color: item.done ? C.textM : C.text, textDecoration: item.done ? "line-through" : "none", lineHeight: 1.5 }}>
                    {item.priority === "high" && !item.done && <span style={{ color: C.amber, marginRight: 4 }}>⭐</span>}
                    {item.text}
                  </span>
                </div>
              ))}
            </div>
          </div>
        );
      })}

      {/* Complete */}
      <div style={{ display: "flex", gap: 8, marginTop: 20 }}>
        <button onClick={onComplete} disabled={!allDone}
          style={{ flex: 1, padding: "12px", border: "none", borderRadius: 8, background: allDone ? C.green : C.border, color: allDone ? "#000" : C.textM, fontWeight: 800, fontSize: 13, fontFamily: "inherit", cursor: allDone ? "pointer" : "not-allowed", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          {allDone ? "✓ Vehicle is Clean — Advance to Photo/Post" : `${totalItems - doneItems} items remaining`}
        </button>
        <button onClick={onClose} style={{ padding: "12px 16px", border: `1px solid ${C.border}`, borderRadius: 8, background: "transparent", color: C.textS, fontWeight: 700, fontSize: 12, fontFamily: "inherit", cursor: "pointer" }}>Close</button>
      </div>
    </div>
  );
};

// ─── MANAGEMENT DASHBOARD ─────────────────────────────────────────────────────

const ManagementDashboardView = ({ vehicles, parts, ledger, staff, timeEntries, bayAssignments, setBayAssignments, openVehicle, onUpdateVehicle }) => {
  const [tab, setTab] = useState("overview");
  const [assignModal, setAssignModal] = useState(null);
  const [assignVehicle, setAssignVehicle] = useState("");
  const [assignDesc, setAssignDesc] = useState("");

  const todayStr = new Date().toISOString().split("T")[0];
  const today = new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });

  // Per-employee work queue builder
  const buildQueue = (sm) => {
    const queue = [];
    vehicles.filter(v => v.stage !== "SOLD").forEach(vehicle => {
      (vehicle.workOrders || []).filter(wo =>
        wo.status !== "COMPLETE" && !wo.deleted &&
        (wo.assignedTo === sm.id || wo.assignedTo === sm.username)
      ).forEach(wo => {
        const priority = wo.priority === "HIGH" ? 3 : wo.priority === "MEDIUM" ? 2 : 1;
        const urgency = daysSince(vehicle.arrivalDate) > 10 ? 2 : daysSince(vehicle.arrivalDate) > 7 ? 1 : 0;
        const kbbBoost = vehicle.kbbValue > 15000 ? 1 : 0;
        // For Sky: vehicles with ONLY detail WOs (clean-ready) get massive priority boost
        const openNonDetail = (vehicle.workOrders||[]).filter(w => w.status !== "COMPLETE" && !w.deleted && w.category !== "DETAIL").length;
        const detailPriorityBoost = (wo.category === "DETAIL" && openNonDetail === 0) ? 10 : 0;
        queue.push({ wo, vehicle, score: priority + urgency + kbbBoost + detailPriorityBoost, estHours: wo.estimatedHours || 1 });
      });
    });
    return queue.sort((a, b) => b.score - a.score);
  };

  const hoursToday = (smId) => timeEntries.filter(e => e.staffId === smId && e.date === todayStr).reduce((sum, e) => {
    const s = new Date(`${e.date}T${e.startTime}`);
    const en = e.endTime ? new Date(`${e.date}T${e.endTime}`) : new Date();
    return sum + (en - s) / 3600000;
  }, 0);

  const getBayOccupant = (bayId) => bayAssignments.find(a => a.bayId === bayId && a.status === "IN_USE");
  const baysOccupied = SHOP_BAYS.filter(b => getBayOccupant(b.id)).length;

  const releaseBay = (bayId) => setBayAssignments(prev => prev.map(a => a.bayId === bayId && a.status === "IN_USE" ? { ...a, status: "DONE", endTime: new Date().toTimeString().slice(0, 5) } : a));

  const doAssign = () => {
    if (!assignVehicle || !assignDesc.trim()) return;
    const suggested = suggestBay(assignDesc, bayAssignments);
    setBayAssignments(prev => [...prev, { id: genId(), bayId: assignModal, vehicleId: assignVehicle, woDescription: assignDesc, status: "IN_USE", startTime: new Date().toTimeString().slice(0, 5), date: todayStr, suggestedBay: suggested }]);
    setAssignModal(null); setAssignVehicle(""); setAssignDesc("");
  };

  const overBudget = vehicles.filter(v => { const p = calcProfit(v, ledger, v.kbbValue); return p.overBudget; });
  const totalQueueHours = vehicles.filter(v => v.stage !== "SOLD").flatMap(v => v.workOrders || []).filter(w => w.status !== "COMPLETE").reduce((s, w) => s + (w.estimatedHours || 1), 0);
  const activeVehicles = vehicles.filter(v => !["SOLD"].includes(v.stage));

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 28, fontWeight: 800, color: C.amber, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.06em", lineHeight: 1 }}>MANAGEMENT DASHBOARD</div>
        <div style={{ fontSize: 11, color: C.textM, marginTop: 2 }}>{today} · Capacity · Bays · Queues · Hiring analysis</div>
      </div>

      {/* KPIs */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 20 }}>
        {[
          { l: "Queue Hours", v: totalQueueHours.toFixed(0) + "h", s: `${vehicles.flatMap(v=>v.workOrders||[]).filter(w=>w.status!=="COMPLETE").length} open WOs`, c: C.amber },
          { l: "Bays Active", v: `${baysOccupied}/${SHOP_BAYS.length}`, s: baysOccupied === SHOP_BAYS.length ? "All bays occupied" : `${SHOP_BAYS.length - baysOccupied} available`, c: baysOccupied === SHOP_BAYS.length ? C.red : C.green },
          { l: "Fleet Holding $", v: fmtMoney(activeVehicles.filter(v=>v.stage!=="LINE_READY").reduce((s,v)=>s+calcHoldingCost(v).total,0)), s: `$${HOLDING_COST_PER_DAY}/day per vehicle`, c: "#f97316" },
          { l: "Over Budget", v: overBudget.length, s: "recon > 30% gross", c: overBudget.length > 0 ? C.red : C.textM },
          { l: "Active Vehicles", v: activeVehicles.length, s: `${vehicles.filter(v=>v.stage==="LINE_READY").length} line ready`, c: C.blue },
        ].map(k => (
          <div key={k.l} style={{ background: C.surface, border: `1px solid ${C.border}`, borderTop: `3px solid ${k.c}`, borderRadius: 10, padding: "12px 16px", flex: 1, minWidth: 120 }}>
            <div style={{ fontSize: 9, color: C.textM, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 4 }}>{k.l}</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: k.c, fontFamily: "'Bebas Neue', sans-serif", lineHeight: 1 }}>{k.v}</div>
            <div style={{ fontSize: 10, color: C.textS, marginTop: 2 }}>{k.s}</div>
          </div>
        ))}
      </div>

      {/* Over-budget alert */}
      {overBudget.length > 0 && (
        <div style={{ background: C.red + "10", border: `1px solid ${C.red}40`, borderRadius: 10, padding: "14px 16px", marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 800, color: C.red, textTransform: "uppercase", marginBottom: 8 }}>⚠ Recon Over Budget — Action Required</div>
          {overBudget.map(v => {
            const p = calcProfit(v, ledger, v.kbbValue);
            return (
              <div key={v.id} onClick={() => openVehicle(v)} style={{ padding: "8px 12px", background: C.s2, borderRadius: 6, marginBottom: 6, cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{v.year} {v.make} {v.model} #{v.stockNumber}</div>
                  <div style={{ fontSize: 10, color: C.textS }}>Recon: {fmtMoney(p.reconCost)} · Proj gross: {fmtMoney(p.projectedGross)}</div>
                </div>
                <div style={{ fontSize: 22, fontWeight: 800, color: C.red, fontFamily: "'Bebas Neue', sans-serif" }}>{Math.round(p.reconPct * 100)}%</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", background: C.s2, borderRadius: 8, padding: 3, marginBottom: 20, border: `1px solid ${C.border}`, maxWidth: 520 }}>
        {["overview","bays","queues","hiring"].map(t => <button key={t} onClick={() => setTab(t)} style={{ flex: 1, padding: "7px 0", border: "none", borderBottom: `2px solid ${tab === t ? C.amber : "transparent"}`, borderRadius: 5, background: tab === t ? C.s3 : "transparent", color: tab === t ? C.amber : C.textM, fontSize: 10, fontWeight: 700, fontFamily: "inherit", cursor: "pointer", textTransform: "uppercase", letterSpacing: "0.08em" }}>{t}</button>)}
      </div>

      {/* OVERVIEW */}
      {tab === "overview" && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 12 }}>
          {staff.filter(sm => !["manager","office"].includes(sm.role)).map(sm => {
            const cap = buildQueue(sm);
            const qHrs = cap.reduce((s, q) => s + q.estHours, 0);
            const daysNeeded = qHrs / (sm.dailyHours || 8);
            const worked = hoursToday(sm.id);
            const role = STAFF_ROLES.find(r => r.id === sm.role);
            const overloaded = qHrs > (sm.dailyHours || 8) * 3;
            return (
              <div key={sm.id} style={{ background: C.surface, border: `1px solid ${overloaded ? C.red + "55" : C.border}`, borderRadius: 10, padding: "14px 16px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    {sm.avatar ? <img src={sm.avatar} alt="" style={{ width: 32, height: 32, borderRadius: "50%", objectFit: "cover" }} /> : <div style={{ width: 32, height: 32, borderRadius: "50%", background: role?.color || C.amber, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 13, fontWeight: 800, color: "#000" }}>{sm.name[0]}</div>}
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{sm.name}</div>
                      <div style={{ fontSize: 10, color: C.textM }}>{role?.label} · {sm.dailyHours}h/day</div>
                    </div>
                  </div>
                  <div style={{ textAlign: "right" }}>
                    <div style={{ fontSize: 20, fontWeight: 800, color: overloaded ? C.red : C.amber, fontFamily: "'Bebas Neue', sans-serif", lineHeight: 1 }}>{qHrs.toFixed(0)}h</div>
                    <div style={{ fontSize: 9, color: C.textM }}>{daysNeeded.toFixed(1)} days queued</div>
                  </div>
                </div>
                <div style={{ height: 5, borderRadius: 2, background: C.border, marginBottom: 8 }}>
                  <div style={{ height: "100%", borderRadius: 2, width: `${Math.min((qHrs / ((sm.dailyHours || 8) * 5)) * 100, 100)}%`, background: overloaded ? C.red : daysNeeded > 2 ? "#f97316" : C.green, transition: "width 0.3s" }} />
                </div>
                <div style={{ fontSize: 10, color: C.textS }}>Today: {worked.toFixed(1)}h worked · {cap.length} open WOs{sm.hourlyRate > 0 ? ` · $${sm.hourlyRate}/hr` : ""}</div>
                {overloaded && <div style={{ fontSize: 10, color: C.red, marginTop: 4, fontWeight: 700 }}>⚠ Backlog exceeds 3-day capacity</div>}
              </div>
            );
          })}
        </div>
      )}

      {/* BAYS */}
      {tab === "bays" && (
        <div>
          <div style={{ fontSize: 11, color: C.textS, marginBottom: 16 }}>One two-post hydraulic lift · one scissor lift · one flat bay. Click to assign or release. Bay suggestion is based on work order type.</div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(250px, 1fr))", gap: 12, marginBottom: 20 }}>
            {SHOP_BAYS.map(bay => {
              const asgn = getBayOccupant(bay.id);
              const v = asgn ? vehicles.find(x => x.id === asgn.vehicleId) : null;
              return <BayCard key={bay.id} bay={bay} assignment={asgn} vehicle={v} onAssign={setAssignModal} onRelease={releaseBay} />;
            })}
          </div>
          {/* Bay log today */}
          {bayAssignments.filter(a => a.date === todayStr).length > 0 && (
            <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden" }}>
              <div style={{ padding: "9px 14px", background: C.s2, borderBottom: `1px solid ${C.border}`, fontSize: 10, color: C.textM, fontWeight: 700, textTransform: "uppercase" }}>Today's Bay Log</div>
              {bayAssignments.filter(a => a.date === todayStr).map((a, i) => {
                const bay = SHOP_BAYS.find(b => b.id === a.bayId);
                const v = vehicles.find(x => x.id === a.vehicleId);
                return (
                  <div key={a.id} style={{ display: "grid", gridTemplateColumns: "1fr 1.5fr 2fr 1fr", padding: "8px 14px", borderBottom: `1px solid ${C.border}`, background: i % 2 ? C.s2 + "50" : "transparent" }}>
                    <div style={{ fontSize: 11, color: C.textS }}>{bay?.short}</div>
                    <div style={{ fontSize: 11, color: C.text }}>{v ? `${v.year} ${v.make} ${v.model}` : "—"}</div>
                    <div style={{ fontSize: 11, color: C.textS }}>{a.woDescription}</div>
                    <div style={{ fontSize: 11, color: a.status === "IN_USE" ? C.green : C.textM }}>{a.startTime}{a.endTime ? `–${a.endTime}` : " ●"}</div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* QUEUES */}
      {tab === "queues" && (
        <div>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:12 }}>
            <div style={{ fontSize: 11, color: C.textS }}>Work orders sorted by priority + days overdue + vehicle value.</div>
            <button onClick={() => {
              // Re-assign all existing open WOs using current assignment rules
              const reassigned = vehicles.map(v => ({
                ...v,
                workOrders: (v.workOrders||[]).map(wo => {
                  if (wo.status === "COMPLETE" || wo.deleted) return wo;
                  const desc = (wo.description||"").toLowerCase();
                  const hrs = wo.estimatedHours || 0;
                  const noLiftQuick = /wiper blade|wiper insert|washer fluid|windshield fluid|top off|air filter|cabin filter|fuse|battery terminal|tire pressure check|tpms reset|tpms relearn|mirror glass|antenna|emblem|badge|decal|sticker/i;
                  let assignTo = wo.assignedTo;
                  if (wo.category === "DETAIL") assignTo = "sky";
                  else if (wo.category === "VENDOR") assignTo = wo.assignedTo; // keep vendor
                  else if (hrs > 0 && hrs <= 0.34 && noLiftQuick.test(desc)) assignTo = "robert";
                  else if (wo.category === "MECHANICAL" || wo.category === "BODYWORK" || wo.category === "LOCKSMITH") assignTo = "james";
                  else assignTo = "james"; // default mechanical for anything unmatched
                  return { ...wo, assignedTo: assignTo };
                })
              }));
              reassigned.forEach(v => {
                const orig = vehicles.find(x => x.id === v.id);
                const changed = JSON.stringify(orig?.workOrders) !== JSON.stringify(v.workOrders);
                if (changed) onUpdateVehicle(v);
              });
              alert("✓ All work orders re-assigned based on current rules.");
            }}
              style={{ background:C.amber+"20",border:`1px solid ${C.amber}60`,borderRadius:8,padding:"6px 12px",cursor:"pointer",fontSize:11,fontWeight:700,color:C.amber,fontFamily:"inherit" }}>
              🔄 Re-assign WOs
            </button>
          </div>
          {staff.filter(sm => !["manager","office"].includes(sm.role)).map(sm => {
            const queue = buildQueue(sm);
            const qHrs = queue.reduce((s, q) => s + q.estHours, 0);
            const role = STAFF_ROLES.find(r => r.id === sm.role);
            if (queue.length === 0) return (
              <div key={sm.id} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: "12px 16px", marginBottom: 10, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}><div style={{ width: 24, height: 24, borderRadius: "50%", background: role?.color || C.amber, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 800, color: "#000" }}>{sm.name[0]}</div><span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{sm.name}</span></div>
                <span style={{ fontSize: 11, color: C.textM }}>Queue is clear ✓</span>
              </div>
            );
            let cumHrs = 0;
            return (
              <div key={sm.id} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, overflow: "hidden", marginBottom: 16 }}>
                <div style={{ padding: "11px 16px", background: C.s2, borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <div style={{ width: 26, height: 26, borderRadius: "50%", background: role?.color || C.amber, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 800, color: "#000" }}>{sm.name[0]}</div>
                    <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{sm.name}</span>
                    <span style={{ fontSize: 10, color: C.textM }}>{role?.label}</span>
                  </div>
                  <div style={{ fontSize: 11, color: C.textS }}>{queue.length} WOs · {qHrs.toFixed(1)}h · {(qHrs / (sm.dailyHours || 8)).toFixed(1)} days</div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "28px 1.2fr 2fr 0.5fr 0.7fr 0.8fr", padding: "6px 14px", background: C.s3, borderBottom: `1px solid ${C.border}` }}>
                  {["","Vehicle","Work Order","Est","Cumul.","Day"].map(h => <div key={h} style={{ fontSize: 9, color: C.textM, fontWeight: 700, textTransform: "uppercase" }}>{h}</div>)}
                </div>
                {queue.map((item, qi) => {
                  cumHrs += item.estHours;
                  const dayBucket = Math.ceil(cumHrs / (sm.dailyHours || 8));
                  const cat = WO_CATS.find(c => c.id === item.wo.category);
                  const sugBayId = item.wo.category === "MECHANICAL" ? suggestBay(item.wo.description, bayAssignments) : null;
                  const sugBay = sugBayId ? SHOP_BAYS.find(b => b.id === sugBayId) : null;
                  return (
                    <div key={item.wo.id} onClick={() => openVehicle(item.vehicle)} style={{ display: "grid", gridTemplateColumns: "28px 1.2fr 2fr 0.5fr 0.7fr 0.8fr", padding: "9px 14px", borderBottom: `1px solid ${C.border}`, background: qi % 2 ? C.s2 + "50" : "transparent", cursor: "pointer" }}>
                      <div>
                        <div style={{ width: 18, height: 18, borderRadius: 3, background: item.wo.priority === "HIGH" ? C.red : item.wo.priority === "MEDIUM" ? "#f97316" : C.textM, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 8, fontWeight: 800, color: "#fff" }}>{item.wo.priority[0]}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: C.text }}>{item.vehicle.year} {item.vehicle.make} {item.vehicle.model}</div>
                        <div style={{ fontSize: 9, color: C.textM }}>#{item.vehicle.stockNumber} · {daysSince(item.vehicle.arrivalDate)}d{item.vehicle.kbbValue ? ` · KBB ${fmtMoney(item.vehicle.kbbValue)}` : ""}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 11, color: C.text }}>{item.wo.description}</div>
                        {sugBay && <div style={{ fontSize: 9, color: sugBay.color, marginTop: 1 }}>{sugBay.icon} {sugBay.short} recommended</div>}
                        {item.wo.diagnosisRationale && <div style={{ fontSize: 9, color: C.purple, fontStyle: "italic", marginTop: 1 }}>AI: {item.wo.diagnosisRationale.slice(0, 55)}…</div>}
                      </div>
                      <div style={{ fontSize: 11, color: C.textS }}>{item.estHours}h</div>
                      <div style={{ fontSize: 11, color: cumHrs > (sm.dailyHours || 8) ? "#f97316" : C.textS }}>{cumHrs.toFixed(1)}h</div>
                      <div>
                        <span style={{ display: "inline-flex", padding: "2px 7px", borderRadius: 10, background: dayBucket === 1 ? C.green + "20" : dayBucket === 2 ? C.amber + "20" : C.red + "20", color: dayBucket === 1 ? C.green : dayBucket === 2 ? C.amber : C.red, fontSize: 9, fontWeight: 700 }}>Day {dayBucket}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
      )}

      {/* HIRING */}
      {tab === "hiring" && (
        <div>
          <div style={{ background: C.surface, border: `1px solid ${C.amber}30`, borderRadius: 10, padding: "14px 18px", marginBottom: 20 }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: C.amber, fontFamily: "'Bebas Neue', sans-serif", marginBottom: 6 }}>STAFFING CAPACITY ANALYSIS</div>
            <div style={{ fontSize: 11, color: C.textS, lineHeight: 1.7 }}>Consistent queue depth exceeding 3× daily hours = hiring signal. This view shows current backlog vs. capacity to inform hiring, scheduling, and overtime decisions.</div>
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {staff.filter(sm => sm.role !== "manager").map(sm => {
              const queue = buildQueue(sm);
              const qHrs = queue.reduce((s, q) => s + q.estHours, 0);
              const daysNeeded = qHrs / (sm.dailyHours || 8);
              const role = STAFF_ROLES.find(r => r.id === sm.role);
              const overloaded = qHrs > (sm.dailyHours || 8) * 3;
              const statusColor = overloaded ? C.red : daysNeeded > 2 ? "#f97316" : C.green;
              const statusLabel = overloaded ? "⚠ Overscheduled — consider additional help or overtime" : daysNeeded > 2 ? "Moderate backlog — keep monitoring" : "On track";
              return (
                <div key={sm.id} style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: "14px 18px" }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 12 }}>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 800, color: C.text }}>{sm.name}</div>
                      <div style={{ fontSize: 11, color: C.textM }}>{role?.label} · {sm.dailyHours}h/day</div>
                    </div>
                    <div style={{ textAlign: "right" }}>
                      <div style={{ fontSize: 24, fontWeight: 800, color: statusColor, fontFamily: "'Bebas Neue', sans-serif", lineHeight: 1 }}>{daysNeeded.toFixed(1)}d</div>
                      <div style={{ fontSize: 9, color: C.textM }}>days of queued work</div>
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 14, flexWrap: "wrap", marginBottom: 10 }}>
                    {[{ l: "Open WOs", v: queue.length }, { l: "Hours Queued", v: qHrs.toFixed(1) + "h" }, { l: "Daily Cap", v: (sm.dailyHours || 8) + "h" }, { l: "Weeks of Work", v: (qHrs / ((sm.dailyHours || 8) * 5)).toFixed(1) + "w" }].map(x => (
                      <div key={x.l}><div style={{ fontSize: 9, color: C.textM, fontWeight: 700, textTransform: "uppercase" }}>{x.l}</div><div style={{ fontSize: 16, fontWeight: 800, color: C.text, fontFamily: "'Bebas Neue', sans-serif" }}>{x.v}</div></div>
                    ))}
                  </div>
                  <div style={{ display: "inline-flex", padding: "4px 10px", borderRadius: 6, background: statusColor + "20", border: `1px solid ${statusColor}40`, fontSize: 11, fontWeight: 700, color: statusColor }}>{statusLabel}</div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Bay assign modal */}
      {assignModal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.88)", zIndex: 500, display: "flex", alignItems: "center", justifyContent: "center", padding: 20 }}>
          <div style={{ background: C.surface, border: `1px solid ${C.bLight}`, borderRadius: 12, width: "100%", maxWidth: 480, padding: 24 }}>
            <div style={{ fontSize: 15, fontWeight: 800, color: C.text, fontFamily: "'Bebas Neue', sans-serif", marginBottom: 16 }}>ASSIGN — {SHOP_BAYS.find(b => b.id === assignModal)?.short.toUpperCase()}</div>
            <div style={{ marginBottom: 10 }}>
              <div style={{ fontSize: 10, color: C.textM, fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>Vehicle</div>
              <select value={assignVehicle} onChange={e => setAssignVehicle(e.target.value)} style={{ width: "100%", background: C.s2, border: `1px solid ${C.border}`, borderRadius: 6, padding: "9px 12px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none" }}>
                <option value="">— Select vehicle</option>
                {activeVehicles.map(v => <option key={v.id} value={v.id}>{v.year} {v.make} {v.model} #{v.stockNumber}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 10, color: C.textM, fontWeight: 700, textTransform: "uppercase", marginBottom: 4 }}>Work Being Done *</div>
              <input value={assignDesc} onChange={e => setAssignDesc(e.target.value)} placeholder="e.g. Brake pad replacement, CV axle…" style={{ width: "100%", background: C.s2, border: `1px solid ${C.border}`, borderRadius: 6, padding: "9px 12px", color: C.text, fontSize: 12, fontFamily: "inherit", outline: "none", boxSizing: "border-box" }} />
            </div>
            {assignDesc && (() => { const sug = suggestBay(assignDesc, bayAssignments); const sugBay = sug ? SHOP_BAYS.find(b => b.id === sug) : null; return sugBay && sug !== assignModal ? <div style={{ fontSize: 10, color: sugBay.color, marginBottom: 12, padding: "6px 10px", background: sugBay.color + "15", borderRadius: 6 }}>💡 Tip: {sugBay.icon} {sugBay.short} may be better suited for this work</div> : null; })()}
            <div style={{ display: "flex", gap: 8 }}>
              <button onClick={doAssign} disabled={!assignVehicle || !assignDesc.trim()} style={{ flex: 1, padding: "10px", border: "none", borderRadius: 8, background: !assignVehicle || !assignDesc.trim() ? C.border : C.amber, color: !assignVehicle || !assignDesc.trim() ? C.textM : "#000", fontWeight: 800, fontSize: 13, fontFamily: "inherit", cursor: !assignVehicle || !assignDesc.trim() ? "not-allowed" : "pointer" }}>Assign Bay</button>
              <button onClick={() => setAssignModal(null)} style={{ padding: "10px 16px", border: `1px solid ${C.border}`, borderRadius: 8, background: "transparent", color: C.textS, fontWeight: 700, fontSize: 12, fontFamily: "inherit", cursor: "pointer" }}>Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ─── PENDING SOLD APPROVAL MODAL ──────────────────────────────────────────────
// Daily inventory sync flags vehicles that disappeared from the dealer website.
// The first user of the day to see this modal approves the sold list — once
// approved, those vehicles are marked SOLD across all clients.
const PendingSoldApprovalModal = ({ pending, vehicles, currentUser, onApprove, onClose }) => {
  const [checked, setChecked] = useState(() => new Set((pending.candidates || []).map(c => c.id)));
  if (!pending || pending.status !== "PENDING" || !(pending.candidates || []).length) return null;

  const toggle = (id) => setChecked(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const approveSelected = () => {
    const approvedIds = Array.from(checked);
    const rejectedIds = (pending.candidates || []).filter(c => !checked.has(c.id)).map(c => c.id);
    onApprove({ approvedIds, rejectedIds });
  };

  return (
    <Modal title="📋 Daily Inventory — Approve Sold List" onClose={onClose} wide>
      <div style={{ background: C.amber + "12", border: `1px solid ${C.amber}40`, borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 12, color: C.text, lineHeight: 1.5 }}>
        The 5 AM inventory sync found {pending.candidates.length} vehicle{pending.candidates.length !== 1 ? "s" : ""} that disappeared from the dealer website overnight.
        Review the list and approve the ones that actually sold. Unchecked vehicles stay in the active inventory.
      </div>
      <div style={{ marginBottom: 12, fontSize: 11, color: C.textM }}>
        Synced {fmtDate(pending.syncDate)}. Approving as: <strong>{currentUser?.name || currentUser?.username || "Unknown"}</strong>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, maxHeight: 380, overflowY: "auto", marginBottom: 14 }}>
        {pending.candidates.map(c => {
          const isChecked = checked.has(c.id);
          // Look up the live vehicle in case stage changed since the sync wrote the snapshot
          const v = vehicles.find(x => x.id === c.id);
          const stage = v?.stage || c.stage;
          return (
            <div key={c.id} onClick={() => toggle(c.id)}
              style={{ background: isChecked ? C.green + "10" : C.s2, border: `2px solid ${isChecked ? C.green : C.border}`, borderRadius: 8, padding: "10px 14px", cursor: "pointer", display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ width: 20, height: 20, borderRadius: 4, background: isChecked ? C.green : "transparent", border: `2px solid ${isChecked ? C.green : C.border}`, flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 13, fontWeight: 800 }}>{isChecked ? "✓" : ""}</div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#000" }}>{c.year} {c.make} {c.model}</div>
                <div style={{ fontSize: 10, color: C.textM, fontFamily: "monospace" }}>#{c.stockNumber || "—"} {c.vin ? "· VIN " + c.vin : ""}</div>
              </div>
              <Badge color={stage === "SOLD" ? C.green : C.textM} small>{stage || "UNKNOWN"}</Badge>
            </div>
          );
        })}
      </div>
      <div style={{ display: "flex", gap: 8, justifyContent: "space-between", alignItems: "center" }}>
        <div style={{ fontSize: 11, color: C.textM }}>
          {checked.size} of {pending.candidates.length} will be marked sold
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <Btn variant="ghost" onClick={() => setChecked(new Set())}>Uncheck all</Btn>
          <Btn variant="ghost" onClick={() => setChecked(new Set(pending.candidates.map(c => c.id)))}>Check all</Btn>
          <Btn variant="green" onClick={approveSelected}>✓ Approve & Mark Sold</Btn>
        </div>
      </div>
    </Modal>
  );
};

// ─── ROOT APP ─────────────────────────────────────────────────────────────────

export default function App() {
  const [view, setView] = useState("home");
  const [roxyInitialQuery, setRoxyInitialQuery] = useState(null);
  const [roxyInitialVehicle, setRoxyInitialVehicle] = useState(null);
  const [viewHistory, setViewHistory] = useState([]);

  const navigateTo = (newView) => {
    setViewHistory(h => [...h, view]);
    setView(newView);
    if (newView !== "vehicle") setSelectedVehicle(null);
    window.history.pushState({ view: newView }, "");
  };
  const navigateBack = () => {
    if (viewHistory.length === 0) { setView("home"); return; }
    const prev = viewHistory[viewHistory.length - 1];
    setViewHistory(h => h.slice(0, -1));
    setView(prev);
  };
  // Browser back button
  useEffect(() => {
    const onPop = () => {
      if (viewHistory.length > 0) {
        const prev = viewHistory[viewHistory.length - 1];
        setViewHistory(h => h.slice(0, -1));
        setView(prev);
      } else setView("home");
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, [viewHistory]);
  const [vehicles, setVehicles] = useState([]);
  const [parts, setParts] = useState([]);
  const [catalog, setCatalog] = useState([]); // shop inventory catalog
  const [schedule, setSchedule] = useState({}); // AI-generated 3-day schedule
  const [tasks, setTasks] = useState([]); // manager-assigned non-vehicle tasks
  const [guestDiagnoses, setGuestDiagnoses] = useState([]); // saved guest diagnoses — 14 day retention

  // Shared schedule builder — used by both ScheduleView and ReconCalendarView
  const rebuildSchedule = () => {
    const today = todayStr();

    const newSchedule = {};
    const DAILY_CAP = 6.5;
    const holidays = HOLIDAYS(new Date().getFullYear());
    const workers = staff.filter(s => !["manager","viewer"].includes(s.role) && s.username !== "john");
    if (!workers.length) return;
    const workerCursor = {};
    const workerHours = {};
    workers.forEach(w => { workerCursor[w.id] = new Date(today + "T12:00:00"); workerHours[w.id] = {}; });
    const nextSlot = (workerId, hours) => {
      let d = new Date(workerCursor[workerId]);
      for (let attempts = 0; attempts < 60; attempts++) {
        const dateStr = d.toISOString().slice(0, 10);
        const dow = d.getDay();
        if (dow !== 0 && dow !== 6 && !holidays.has(dateStr)) {
          const used = (workerHours[workerId] || {})[dateStr] || 0;
          if (used + hours <= DAILY_CAP) return { dateStr, d };
        }
        d = new Date(d); d.setDate(d.getDate() + 1);
      }
      return null;
    };
    const allJobs = [];
    vehicles.filter(v => !["SOLD","SCRAPPED"].includes(v.stage)).forEach(v => {
      const vParts = (parts || []).filter(p => p.vehicleId === v.id && !["RETURNED","CANCELLED"].includes(p.status));
      (v.workOrders || []).filter(w => w.status !== "COMPLETE" && !w.deleted).forEach(wo => {
        const woParts = vParts.filter(p => p.workOrderId === wo.id);
        const partsOnOrder = woParts.some(p => ["Ordered","In Transit","Pending Purchase"].includes(p.status));
        if (partsOnOrder) return; // don't schedule until parts arrive
        allJobs.push({ wo, vehicle: v, priority: wo.priority === "HIGH" ? 0 : wo.priority === "MEDIUM" ? 1 : 2, vehicleValue: parseFloat(v.purchasePrice) || 0, hours: wo.estimatedHours || 1 });
      });
    });
    allJobs.sort((a, b) => {
      const aSold = a.vehicle.soldRepairPending ? 0 : 1;
      const bSold = b.vehicle.soldRepairPending ? 0 : 1;
      if (aSold !== bSold) return aSold - bSold;
      if (a.vehicle.customerReturnDate && b.vehicle.customerReturnDate) { const diff = new Date(a.vehicle.customerReturnDate) - new Date(b.vehicle.customerReturnDate); if (diff !== 0) return diff; }
      if (a.vehicle.customerReturnDate) return -1;
      if (b.vehicle.customerReturnDate) return 1;
      if (a.priority !== b.priority) return a.priority - b.priority;
      return b.vehicleValue - a.vehicleValue;
    });
    const allOpenWOsSig = allJobs.map(j => j.wo.id + j.wo.status).sort().join("|");
    allJobs.forEach(({ wo, vehicle, hours }) => {
      const assignedId = wo.assignedTo;
      let worker = workers.find(w => w.username === assignedId || w.id === assignedId);
      if (!worker) {
        const noLiftQuick = /bulb|wiper|blade|fluid|top off|air filter|cabin filter|fuse|battery terminal|tire pressure|tpms reset|mirror|antenna|emblem/i;
        if (wo.category === "DETAIL") worker = workers.find(w => w.username === "sky") || workers[0];
        else if (wo.category === "VENDOR") return;
        else if (hours <= 0.35 && noLiftQuick.test((wo.description || "").toLowerCase())) worker = workers.find(w => w.username === "robert") || workers.find(w => w.username === "james") || workers[0];
        else worker = workers.find(w => w.username === "james") || workers[0];
      }
      if (!worker) return;
      const slot = nextSlot(worker.id, hours);
      if (!slot) return;
      if (!workerHours[worker.id]) workerHours[worker.id] = {};
      workerHours[worker.id][slot.dateStr] = (workerHours[worker.id][slot.dateStr] || 0) + hours;
      if (workerHours[worker.id][slot.dateStr] >= DAILY_CAP) { const next = new Date(slot.d); next.setDate(next.getDate() + 1); workerCursor[worker.id] = next; } else { workerCursor[worker.id] = slot.d; }
      if (!newSchedule[slot.dateStr]) newSchedule[slot.dateStr] = {};
      if (!newSchedule[slot.dateStr][worker.id]) newSchedule[slot.dateStr][worker.id] = [];
      newSchedule[slot.dateStr][worker.id].push({ id: wo.id, vehicleId: vehicle.id, woId: wo.id, description: wo.description, estimatedHours: hours, priority: wo.priority, category: wo.category, vehicleLabel: `${vehicle.year} ${vehicle.make} ${vehicle.model} #${vehicle.stockNumber}` });
    });
    newSchedule._sig = allOpenWOsSig;
    setSchedule(newSchedule);
  };
  const [chatMessages, setChatMessages] = useState([]); // Shop group chat
  const [pendingSoldApproval, setPendingSoldApproval] = useState(null); // { syncDate, candidates[], status, approvedBy, approvedAt }
  const [notifications, setNotifications] = useState([]); // Per-user notifications
  const [directMessages, setDirectMessages] = useState([]); // DMs between users
  const [notifPanelOpen, setNotifPanelOpen] = useState(false);
  const [inventoryCache, setInventoryCacheRaw] = useState({ vehicles: [], lastSync: null }); // Dealer website inventory
  const setInventoryCache = async (nc) => {
    const val = typeof nc === "function" ? nc({}) : nc;
    setInventoryCacheRaw(val);
    try { await setInventoryCache_raw(val); } catch {}
  };
  const [vendors, setVendors] = useState(DEFAULT_VENDORS);
  const [ledger, setLedger] = useState([]);
  const [staff, setStaff] = useState(DEFAULT_STAFF);
  const [timeEntries, setTimeEntries] = useState([]);
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [showLogin, setShowLogin] = useState(false);
  const [ready, setReady] = useState(false);
  const [saveError, setSaveError] = useState(false); // true if Firebase write failed
  const [showHoldingCost, setShowHoldingCost] = useState(() => { try { return localStorage.getItem("ew4_show_holding_cost") === "true"; } catch { return false; } });
  const [showPhotoReadyPanel, setShowPhotoReadyPanel] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [feedbackText, setFeedbackText] = useState("");
  const [feedbackSent, setFeedbackSent] = useState(false);
  const [trash, setTrash] = useState(() => { try { const t = localStorage.getItem("ew4_trash"); return t ? JSON.parse(t) : []; } catch { return []; } });
  const addToTrash = (item) => { const entry = { ...item, trashedAt: todayStr(), trashId: genId() }; setTrash(prev => { const n=[...prev,entry]; try{localStorage.setItem("ew4_trash",JSON.stringify(n));}catch{}return n; }); };
  const restoreFromTrash = (trashId) => setTrash(prev => { const n=prev.filter(i=>i.trashId!==trashId); try{localStorage.setItem("ew4_trash",JSON.stringify(n));}catch{}return n; });
  // Auto-purge items older than 7 days on load
  useEffect(() => { setTrash(prev => { const n=prev.filter(i=>daysSince(i.trashedAt)<7); try{localStorage.setItem("ew4_trash",JSON.stringify(n));}catch{}return n; }); }, []);
  const [bayAssignments, setBayAssignments] = useState([]);
  const [checklistVehicle, setChecklistVehicle] = useState(null);
  const [deliveryVehicle, setDeliveryVehicle] = useState(null);
  const [vinScanOpen, setVinScanOpen] = useState(false);
  const [digestSending, setDigestSending] = useState(false);
  const [digestSent, setDigestSent] = useState(false);
  const [woReportSent, setWoReportSent] = useState(false);
  const [csvToast, setCsvToast] = useState("");

  useEffect(() => {
    (async () => {
      const v = await S.load("ew4_vehicles"); const p = await S.load("ew4_parts"); const cat = await S.load("ew4_catalog"); const sched = await S.load("ew4_schedule"); const ch = await S.load("ew4_chat"); const tk = await S.load("ew4_tasks"); const gd = await S.load("ew4_guest_diag"); const ps = await S.load("ew4_pending_sold");
      const vend = await S.load("ew4_vendors"); const led = await S.load("ew4_ledger");
      const sf = await S.load("ew4_staff"); const te = await S.load("ew4_time");
      const cu = await S.load("ew4_user");
      const bays = await S.load("ew4_bays");
      if (v) setVehicles(v); if (p) setParts(p); if (vend) setVendors(vend); if (cat) setCatalog(cat); if (sched) setSchedule(sched); if (ch) setChatMessages(ch); if (tk) setTasks(tk); if (gd) setGuestDiagnoses(gd); if (ps) setPendingSoldApproval(ps);
      // Load inventory cache
      const inv = await getInventoryCache();
      if (inv?.vehicles?.length) setInventoryCacheRaw(inv);
      if (led) setLedger(led); if (sf) setStaff(sf); if (te) setTimeEntries(te);
      if (cu) setCurrentUser(cu); else setShowLogin(true);
      if (bays) setBayAssignments(bays);
      setReady(true);

      // Real-time listeners — push changes to this device when another device saves
      // Use functional updates with equality check to prevent unnecessary re-renders
      const snapUpdate = (setter) => (s) => { if(s.exists()) try{ const v=JSON.parse(s.data().value); setter(p => JSON.stringify(p)===JSON.stringify(v)?p:v); }catch{} };
      onSnapshot(doc(db, 'appdata', 'ew4_vehicles'), snapUpdate(setVehicles));
      onSnapshot(doc(db, 'appdata', 'ew4_tasks'), snapUpdate(setTasks));
      onSnapshot(doc(db, 'appdata', 'ew4_parts'),    snapUpdate(setParts));
      onSnapshot(doc(db, 'appdata', 'ew4_ledger'),   snapUpdate(setLedger));
      onSnapshot(doc(db, 'appdata', 'ew4_chat'),     snapUpdate(setChatMessages));
      onSnapshot(doc(db, 'appdata', 'ew4_notifications'), snapUpdate(setNotifications));
      onSnapshot(doc(db, 'appdata', 'ew4_dms'), snapUpdate(setDirectMessages));
      onSnapshot(doc(db, 'appdata', 'ew4_schedule'), snapUpdate(setSchedule));
      onSnapshot(doc(db, 'appdata', 'ew4_catalog'),  snapUpdate(setCatalog));
      onSnapshot(doc(db, 'appdata', 'ew4_pending_sold'), snapUpdate(setPendingSoldApproval));
      onSnapshot(doc(db, 'appdata', 'ew4_staff'), snap => {
        const data = snap.data();
        if (data && Array.isArray(data.value)) {
          // Merge with DEFAULT_STAFF so avatar/color always matches latest code
          const merged = data.value.map(s => {
            const def = DEFAULT_STAFF.find(d => d.id === s.id || d.username === s.username);
            return def ? { ...s, avatar: def.avatar, color: def.color } : s;
          });
          setStaff(merged);
        }
      });
      onSnapshot(doc(db, 'appdata', 'ew4_vendors'),  snapUpdate(setVendors));
      onSnapshot(doc(db, 'appdata', 'ew4_inventory_cache'), (s) => {
        if (!s.exists()) return;
        try {
          const v = JSON.parse(s.data().value);
          setInventoryCacheRaw(prev => {
            const prevT = prev?.lastSync ? new Date(prev.lastSync).getTime() : 0;
            const newT  = v?.lastSync ? new Date(v.lastSync).getTime() : 0;
            return newT >= prevT ? v : prev; // never let a stale shared doc clobber a fresher local sync
          });
        } catch {}
      });

      // Auto-sync inventory — only if never synced OR >24h stale (not every load)
      // No API key needed — uses direct HTML fetch
      {
        const lastSync = inv?.lastSync;
        const hoursSince = lastSync ? (Date.now() - new Date(lastSync).getTime()) / 3600000 : 999;
        // Sync once per day max — but ALWAYS re-sync if the cache was built by an older
        // parser version (its year/make/model/mileage data may be wrong).
        if (hoursSince >= 24 || inv?.parserVersion !== INVENTORY_PARSER_VERSION) {
          setTimeout(async () => {
            try {
              console.log("[Auto-Sync] Starting background inventory sync...");
              const fresh = await scrapeInventoryWithGrok();
              if (fresh?.length) {
                const nc = { vehicles: fresh, lastSync: new Date().toISOString() };
                setInventoryCacheRaw(nc);
                await setInventoryCache_raw(nc);
                console.log("[Auto-Sync] Done —", fresh.length, "vehicles cached");
              }
            } catch (e) { console.warn("[Auto-Sync] Failed:", e.message); }
          }, 10000); // 10s delay so app loads fully first
        }
      }
    })();
  }, []);

  useEffect(() => {
    if (!ready) return;
    S.save("ew4_vehicles", vehicles).then(() => setSaveError(false)).catch(() => setSaveError(true));
  }, [vehicles, ready]);
  useEffect(() => { if (ready) S.save("ew4_parts", parts); }, [parts, ready]);

  useEffect(() => { if (ready) S.save("ew4_schedule", schedule); }, [schedule, ready]);

  // Daily inventory sync — runs once per day at/after 5 AM local time.
  // Detected sold candidates are written to Firestore as a pending-approval list.
  // The first user to log in after the sync sees the approval modal.
  useEffect(() => {
    if (!ready) return; // inventory scrape no longer needs a Grok key — runs via the Netlify proxy
    const checkSync = async () => {
      const cache = await getInventoryCache();
      if (!shouldRunDailySync(cache?.lastSync) && cache?.parserVersion === INVENTORY_PARSER_VERSION) return;
      // Don't double-sync if there's already a pending approval list for today
      const existing = await S.load("ew4_pending_sold");
      if (existing && existing.syncDate === todayStr() && existing.status === "PENDING") return;
      try {
        const fresh = await scrapeInventoryWithGrok();
        if (fresh?.length > 0) {
          const newCache = { vehicles: fresh, lastSync: new Date().toISOString() };
          setInventoryCacheRaw(newCache);
          await setInventoryCache_raw(newCache);
          // Detect candidates that disappeared from the dealer site
          const candidates = detectSoldVehicles(fresh, vehicles);
          if (candidates.length > 0) {
            const pending = {
              syncDate: todayStr(),
              syncedAt: new Date().toISOString(),
              status: "PENDING",
              candidates: candidates.map(v => ({ id: v.id, year: v.year, make: v.make, model: v.model, stockNumber: v.stockNumber, vin: v.vin || null, stage: v.stage })),
              approvedBy: null,
              approvedAt: null,
            };
            await S.save("ew4_pending_sold", pending);
            setPendingSoldApproval(pending);
          }
        }
      } catch (e) { if (process.env.NODE_ENV !== "development") console.warn("Daily inventory sync failed:", e.message); }
    };
    checkSync();
  }, [ready]);
  useEffect(() => { if (ready) S.save("ew4_chat", chatMessages); }, [chatMessages, ready]);
  useEffect(() => { if (ready) S.save("ew4_tasks", tasks); }, [tasks, ready]);
  useEffect(() => { if (ready) S.save("ew4_guest_diag", guestDiagnoses.filter(g => { const d = new Date(g.savedAt); return (Date.now()-d.getTime()) < 14*24*60*60*1000; })); }, [guestDiagnoses, ready]);
  useEffect(() => { if (ready) S.save("ew4_notifications", notifications); }, [notifications, ready]);
  useEffect(() => { if (ready) S.save("ew4_dms", directMessages); }, [directMessages, ready]);
  useEffect(() => { if (ready) S.save("ew4_vendors", vendors); }, [vendors, ready]);
  useEffect(() => { if (ready) S.save("ew4_ledger", ledger); }, [ledger, ready]);
  useEffect(() => { if (ready) S.save("ew4_staff", staff); }, [staff, ready]);
  useEffect(() => { if (ready) S.save("ew4_time", timeEntries); }, [timeEntries, ready]);
  useEffect(() => { if (ready) S.save("ew4_bays", bayAssignments); }, [bayAssignments, ready]);
  useEffect(() => { if (ready && currentUser) S.save("ew4_user", currentUser); }, [currentUser, ready]);

  const updateVehicle = (upd) => {
    // If vehicle is being SOLD and was in inventory cache — remove it from cache
    if (upd.stage === "SOLD" && upd.stockNumber) {
      setInventoryCacheRaw(prev => {
        const updated = { ...prev, vehicles: (prev.vehicles || []).filter(v => String(v.stockNumber) !== String(upd.stockNumber)) };
        setInventoryCache_raw(updated);
        return updated;
      });
    }

    // Auto-remove: if ALL work orders are soft-deleted AND there are no completed WOs,
    // the vehicle has never been worked on — remove it from recon entirely
    const allWOs = upd.workOrders || [];
    const hasCompleted = allWOs.some(w => w.status === "COMPLETE");
    const allDeleted = allWOs.length > 0 && allWOs.every(w => w.deleted);
    const noWOs = allWOs.length === 0;
    if ((allDeleted || noWOs) && !hasCompleted && !["SOLD","SCRAPPED"].includes(upd.stage)) {
      // Nothing meaningful happened — remove from recon system entirely
      setVehicles(prev => prev.filter(v => v.id !== upd.id));
      setSelectedVehicle(null);
      return;
    }

    // Auto-move to LINE_READY when all WOs complete (and vehicle has been worked on)
    let finalUpd = { ...upd };
    if (!["SOLD","SCRAPPED","LINE_READY"].includes(upd.stage)) {
      const allWOsForCheck = upd.workOrders || [];
      const hasAnyCompleted = allWOsForCheck.some(w => w.status === "COMPLETE");
      const openRemaining = allWOsForCheck.filter(w => w.status !== "COMPLETE" && !w.deleted);
      if (hasAnyCompleted && openRemaining.length === 0) {
        finalUpd = { ...upd, stage: "LINE_READY", lineReadyAt: todayStr(), lineReadyBy: "auto", lineReadyReason: "All work orders complete" };
      }
    }

    setVehicles(prev => {
      const old = prev.find(v => v.id === finalUpd.id);
      if (old && old.stage !== "LINE_READY" && finalUpd.stage === "LINE_READY") {
        const msg = { id: genId(), authorId: "system", authorName: "Shop Bot", text: `🏁 ${finalUpd.year} ${finalUpd.make} ${finalUpd.model} #${finalUpd.stockNumber} just hit the front line! Great work team! 🎉`, ts: Date.now(), reactions: {} };
        setChatMessages(ch => [...ch, msg]);
      }
      if (old && old.stage !== "SOLD" && finalUpd.stage === "SOLD") {
        const msg = { id: genId(), authorId: "system", authorName: "Shop Bot", text: `💰 ${finalUpd.year} ${finalUpd.make} ${finalUpd.model} #${finalUpd.stockNumber} just SOLD! Ka-ching! 🤑`, ts: Date.now(), reactions: {} };
        setChatMessages(ch => [...ch, msg]);
      }
      return prev.map(v => v.id === finalUpd.id ? finalUpd : v);
    });
    setSelectedVehicle(finalUpd);
  };

  // Auto-chat: new vehicle intake
  // addVehicleWithChat merged into handleVINIntakeComplete
  const addLedger = (e) => setLedger(prev => [...prev, e]);

  const pushNotif = (toUserId, type, title, body, linkView, linkData = {}) => {
    const notif = { id: genId(), toUserId, type, title, body, linkView, linkData, ts: Date.now(), read: false };
    setNotifications(prev => [...prev, notif]);
  };

  const markNotifsRead = (ids) => setNotifications(prev => prev.map(n => ids.includes(n.id) ? { ...n, read: true } : n));
  const openVehicle = (v) => {
    if (v.stage === "SOLD") {
      setSelectedVehicle(v);
      setView("soldvehicles");
    } else {
      setSelectedVehicle(v); setView("vehicle");
    }
  };

  const ksCount = parts.filter(p => { const v = vehicles.find(x => x.id === p.vehicleId); return v?.stage === "SOLD" && !["Installed","RETURNED","CANCELLED"].includes(p.status); }).length;

  const handleVINIntakeComplete = (vehicle, purchaseCost) => {
    setVehicles(prev => [vehicle, ...prev]);
    if (purchaseCost > 0) addLedger({ id: genId(), vehicleId: vehicle.id, category: "PURCHASE", description: `Purchase — ${vehicle.vendor || "source"}`, amount: purchaseCost, date: vehicle.purchaseDate, auto: true });
    // Shop chat announcement
    const msg = { id: genId(), authorId: "system", authorName: "Shop Bot", text: `🚗 New vehicle in! ${vehicle.year} ${vehicle.make} ${vehicle.model} #${vehicle.stockNumber} just landed in the shop.`, ts: Date.now(), reactions: {} };
    setChatMessages(ch => [...ch, msg]);
    setVinScanOpen(false);
    // Go straight to vehicle page — inspection starts from the button there
    setSelectedVehicle(vehicle);
    setView("vehicle");
  };

  const handleInspectionComplete = (updatedVehicle, newWOs, needsMechanical) => {
    updateVehicle(updatedVehicle);
    const needsBody = (newWOs || []).some(w => w.category === "COSMETIC" || w.category === "VENDOR" || w.isBodyWork);
    if (needsMechanical) {
      // Chain directly to Hank with Julia's findings
      setSelectedVehicle(updatedVehicle);
      setView("diagnostics");
      setTimeout(() => {
        // Signal diagnostics to open Hank immediately with Julia context
        window._autoOpenHank = { vehicle: updatedVehicle, juliaFindings: updatedVehicle.juliaInspectionSummary };
      }, 100);
    } else if (needsBody) {
      setSelectedVehicle(updatedVehicle);
      setView("diagnostics");
      setTimeout(() => {
        window._autoOpenJimmy = { vehicle: updatedVehicle, juliaFindings: updatedVehicle.juliaInspectionSummary };
      }, 100);
    } else {
      setView("vehicle");
    }
  };

  // ── Mon/Wed/Fri WO report auto-email ──
  useEffect(() => {
    if (!ready || !vehicles.length) return;
    const today = new Date();
    const day = today.getDay(); // 0=Sun,1=Mon,2=Tue,3=Wed,4=Thu,5=Fri,6=Sat
    const isMWF = [1, 3, 5].includes(day);
    if (!isMWF) return;
    const dateKey = todayStr();
    const storageKey = "ew4_wo_report_sent_" + dateKey;
    // Only send once per day
    try { if (localStorage.getItem(storageKey)) return; } catch {}
    // Wait until 8am to send
    if (today.getHours() < 8) return;
    const sendReport = async () => {
      try {
        const body = buildWOReport(vehicles, parts, ledger);
        const subject = `Edgewater WO Status Report — ${today.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric" })}`;
        const recipients = [
          "angela@edgewaterautocenter.com",
          "dmitriy@edgewaterautocenter.com",
        ];
        for (const to of recipients) {
          await sendGmailViaMCP(to, subject, body);
        }
        try { localStorage.setItem(storageKey, "1"); } catch {}
        setWoReportSent(true);
        setTimeout(() => setWoReportSent(false), 8000);
      } catch(e) { console.error("WO report send failed:", e); }
    };
    // Small delay so app is fully loaded before sending
    const timer = setTimeout(sendReport, 5000);
    return () => clearTimeout(timer);
  }, [ready, vehicles.length]);

  const sendDailyDigest = async () => {
    setDigestSending(true);
    const officeStaff = staff.filter(s => s.role === "office" || s.role === "manager");
    const body = buildDigestEmail(vehicles, ledger, parts, staff, timeEntries);
    for (const sm of officeStaff) {
      if (sm.email) await sendGmailViaMCP(sm.email, `Edgewater Daily Digest — ${new Date().toLocaleDateString()}`, body);
    }
    setDigestSending(false); setDigestSent(true);
    setTimeout(() => setDigestSent(false), 5000);
  };

  const navImgs = {
    home:        "/images/nav-home.jpg",
    fleet:       "/images/nav-pipeline.jpg",
    diagnose:    "/images/start-diag.jpg",
    finder:      "/images/nav-find-parts.jpg",
    schedule:    "/images/schedule.jpg",
    vendors:     "/images/vendors.jpg?v=2",
    settings:    "/images/nav-settings.jpg",
  };
  const nav = [
    { id: "home",        label: "Home",         icon: Home },
    { id: "fleet",       label: "All WOs",       icon: Car },
    { id: "diagnose",    label: "Diagnose",      icon: MessageSquare },
    { id: "finder",      label: "Parts Finder",  icon: Search },
    { id: "schedule",    label: "Schedule",      icon: Clock },
    { id: "vendors",     label: "Vendors",       icon: Truck },
    { id: "settings",    label: "Settings",      icon: Settings },
  ];

  // ── Login screen ─────────────────────────────────────────────────────────
  if (showLogin || (!currentUser && ready)) {
    return (
      <LoginScreen
        staff={staff.length ? staff : DEFAULT_STAFF}
        onLogin={(user) => { setCurrentUser(user); setShowLogin(false); S.save("ew4_user", user); }}
      />
    );
  }

  if (!ready) return (
    <div style={{ background: C.bg, minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ textAlign: "center" }}>
        <div style={{ fontSize: 32, fontWeight: 800, color: C.amber, fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.1em", marginBottom: 8 }}>EDGEWATER</div>
        <div style={{ fontSize: 12, color: C.textS, fontFamily: "monospace" }}>Auto Center Recon App · Loading…</div>
      </div>
    </div>
  );

  return (
    <div style={{ backgroundImage: "url(/images/bg-main-app.jpg)", backgroundSize: "cover", backgroundAttachment: "fixed", backgroundPosition: "center", minHeight: "100vh", color: C.text, fontFamily: "'IBM Plex Mono', 'Courier New', monospace" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=IBM+Plex+Mono:wght@400;600;700&display=swap');
        *, *::before, *::after { box-sizing: border-box; }
        html { touch-action: pan-y; overflow-x: hidden; }
        body { margin: 0; background: ${C.bg}; background-image: url(/images/bg\ main\ app.jpg); background-size: cover; background-attachment: fixed; background-position: center; overflow-x: hidden; max-width: 100vw; -webkit-text-size-adjust: 100%; }
        ::-webkit-scrollbar { width: 5px; height: 5px; }
        ::-webkit-scrollbar-track { background: ${C.s2}; }
        ::-webkit-scrollbar-thumb { background: ${C.blue}40; border-radius: 3px; }
        input[type=date]::-webkit-calendar-picker-indicator { filter: invert(0.4); cursor: pointer; }
        select option { background: ${C.surface}; color: ${C.text}; }
        img { max-width: 100%; }
        @keyframes blink { 0%,100%{opacity:1}50%{opacity:0.15} }
        @keyframes spin { from{transform:rotate(0deg)}to{transform:rotate(360deg)} }
        @keyframes scanLine { 0%,100%{top:20%}50%{top:75%} }
        @keyframes pulse { 0%,100%{opacity:1}50%{opacity:0.4} }
      `}</style>

      {/* Top Nav */}
      {saveError && (
        <div style={{ background: "#dc2626", color: "#fff", padding: "6px 20px", fontSize: 11, fontWeight: 700, textAlign: "center", display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
          ⚠ Firebase save failed — your data is backed up locally and will sync when connection restores.
          <button onClick={() => S.save("ew4_vehicles", vehicles).then(() => setSaveError(false)).catch(() => {})}
            style={{ background: "rgba(255,255,255,0.2)", border: "1px solid rgba(255,255,255,0.4)", borderRadius: 4, padding: "2px 8px", cursor: "pointer", fontSize: 10, color: "#fff", fontFamily: "inherit" }}>
            Retry
          </button>
        </div>
      )}
      {woReportSent && (
        <div style={{ background: "#14532d", color: "#86efac", padding: "6px 20px", fontSize: 11, fontWeight: 700, textAlign: "center" }}>
          📧 WO Status Report emailed to the team
        </div>
      )}
      {csvToast && (
        <div style={{ background: "#14532d", color: "#86efac", padding: "6px 20px", fontSize: 11, fontWeight: 700, textAlign: "center", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span>📂 {csvToast}</span>
          <button onClick={() => setCsvToast("")} style={{ background: "none", border: "none", color: "#86efac", cursor: "pointer", fontSize: 16, padding: "0 8px" }}>✕</button>
        </div>
      )}

      <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, position: "sticky", top: 0, zIndex: 100 }}>
        {/* ── DASHBOARD BAR ── two rows for mobile fit ── */}
        <div style={{ background: "linear-gradient(135deg, #0a1628 0%, #1a2d50 100%)" }}>
          {/* Stats + Action buttons */}
          {(() => {
            const today = todayStr();
            const myOpenWOs = vehicles.flatMap(v =>
              (v.workOrders || []).filter(w => w.assignedTo === currentUser?.username && w.status !== "COMPLETE" && !w.deleted)
            );
            const myTodayWOs = myOpenWOs.filter(w => w.scheduledDate === today);
            const isManager = currentUser?.role === "manager";
            return (
              <div style={{ display: "flex", alignItems: "center", gap: 6, padding: "6px 10px", overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                {/* Stat tiles — compact */}
                <div style={{ background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 7, padding: "4px 10px", textAlign: "center", flexShrink: 0, position:"relative", overflow:"hidden" }}>
                  <img src="/images/assigned jobs.jpg" alt="" style={{ position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"cover", opacity:0.18 }} onError={e=>e.target.style.display="none"} />
                  <div style={{ position:"relative", zIndex:1 }}>
                    <div style={{ fontSize: 15, fontWeight: 900, color: "#fff", fontFamily: "'Bebas Neue', sans-serif", lineHeight: 1 }}>{myOpenWOs.length}</div>
                    <div style={{ fontSize: 8, color: "rgba(255,255,255,0.55)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Jobs</div>
                  </div>
                </div>
                <div style={{ background: "rgba(245,158,11,0.15)", border: "1px solid rgba(245,158,11,0.3)", borderRadius: 7, padding: "4px 10px", textAlign: "center", flexShrink: 0 }}>
                  <div style={{ fontSize: 15, fontWeight: 900, color: C.amber, fontFamily: "'Bebas Neue', sans-serif", lineHeight: 1 }}>{myTodayWOs.length}</div>
                  <div style={{ fontSize: 8, color: "rgba(255,255,255,0.55)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Today</div>
                </div>
                {isManager && (
                  <div style={{ background: "rgba(99,102,241,0.15)", border: "1px solid rgba(99,102,241,0.3)", borderRadius: 7, padding: "4px 10px", textAlign: "center", flexShrink: 0, cursor: "pointer" }}
                    onClick={() => navigateTo("mgmt")}>
                    <img src="/images/management.jpg" alt="" style={{ height: 15, objectFit: "contain" }} onError={e => e.target.style.display = "none"} />
                    <div style={{ fontSize: 8, color: "rgba(255,255,255,0.55)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Mgmt</div>
                  </div>
                )}
                <div style={{ flex: 1 }} />
                {/* Add Vehicle — icon + label, compact */}
                <button onClick={() => setVinScanOpen(true)}
                  style={{ background: "linear-gradient(135deg,#3b0764,#6d28d9)", border: "1px solid rgba(168,85,247,0.5)", borderRadius: 8, padding: "5px 10px", cursor: "pointer", display: "flex", alignItems: "center", gap: 5, flexShrink: 0 }}>
                  <span style={{ fontSize: 16, fontWeight: 900, color: "#e9d5ff", lineHeight: 1 }}>＋</span>
                  <div style={{ fontSize: 10, fontWeight: 800, color: "#e9d5ff", fontFamily: "'Bebas Neue', sans-serif", letterSpacing: "0.05em", lineHeight: 1 }}>ADD</div>
                </button>
              </div>
            );
          })()}
        </div>
        {/* ── NAV TABS ── */}
        <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 20px", display: "flex", alignItems: "center" }}>
          {viewHistory.length > 0 && (
            <button onClick={navigateBack} style={{ background: "none", border: "none", cursor: "pointer", color: C.blue, display: "flex", alignItems: "center", gap: 3, fontSize: 11, fontWeight: 700, fontFamily: "inherit", padding: "0 10px 0 0", flexShrink: 0 }}>
              <img src="/images/btn-back-arrow.jpg" alt="" style={{width:14,height:14,objectFit:"contain",flexShrink:0}} onError={e=>e.target.style.display="none"} /> Back
            </button>
          )}
          <div style={{ display: "flex", gap: 0, flex: 1, overflowX: "auto" }}>
            {nav.map(({ id, label, icon: Icon, alert }) => {
              const active = view === id || (view === "vehicle" && id === "pipeline");
              return (
                <button key={id} onClick={() => navigateTo(id)}
                  style={{ padding: "15px 10px", border: "none", borderBottom: `2px solid ${active ? C.amber : "transparent"}`, background: "transparent", color: active ? C.amber : C.textS, cursor: "pointer", fontSize: 9, fontWeight: 700, fontFamily: "inherit", textTransform: "uppercase", letterSpacing: "0.08em", display: "flex", alignItems: "center", gap: 4, flexShrink: 0, position: "relative", whiteSpace: "nowrap" }}>
                  {navImgs[id] ? <img src={navImgs[id]} alt="" style={{ width:42, height:42, objectFit:"contain", filter: active ? "none" : "grayscale(1) opacity(0.5)" }} onError={e => e.target.style.display="none"} /> : <Icon size={11} />}{label}
                  {alert > 0 && <span style={{ background: C.red, color: "#fff", borderRadius: "50%", width: 14, height: 14, fontSize: 8, display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, position: "absolute", top: 6, right: 2 }}>{alert}</span>}
                </button>
              );
            })}
          </div>

          {/* Avatar */}
          <div style={{ display:"flex", alignItems:"center", gap:6, flexShrink:0 }}>
            <div onClick={() => navigateTo("settings")} style={{ cursor:"pointer", display:"flex", alignItems:"center", gap:5 }}>
              {currentUser?.avatar
                ? <img src={currentUser.avatar} alt="" style={{ width:28,height:28,borderRadius:"50%",objectFit:"cover",border:`2px solid ${C.amber}` }} />
                : <div style={{ width:28,height:28,borderRadius:"50%",background:C.amber,display:"flex",alignItems:"center",justifyContent:"center",fontSize:12,fontWeight:800,color:"#000" }}>{currentUser?.name?.[0]||"?"}</div>}
              <div style={{ lineHeight:1 }}>
                <div style={{ fontSize:10,fontWeight:700,color:C.text,whiteSpace:"nowrap" }}>{currentUser?.name?.split(" ")[0]}</div>
                <div style={{ fontSize:8,color:C.textM,textTransform:"uppercase" }}>{currentUser?.role}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: "24px 20px" }}>
        {view === "home" && <HomeView vehicles={vehicles} parts={parts} ledger={ledger} staff={staff} currentUser={currentUser} setView={setView} navigateTo={navigateTo} openVehicle={openVehicle} onScanVIN={() => setVinScanOpen(true)} chatMessages={chatMessages} tasks={tasks} onAssignTask={t => {
        setTasks(prev => [...prev, t]);
        // Notify the assigned employee
        const assignedStaff = staff.find(s => s.username === t.assignedTo);
        if (assignedStaff && pushNotif) {
          pushNotif({ to: assignedStaff.id, type: "task_assigned", message: `New task assigned to you: ${t.description}`, taskId: t.id, from: t.assignedByName || "Manager" });
        }
      }} />}
        {view === "fleet" && <div style={{ padding:"0 16px 20px" }}><FleetStatusView vehicles={vehicles} parts={parts} schedule={schedule} setSchedule={setSchedule} staff={staff} currentUser={currentUser} onBack={navigateBack} openVehicle={openVehicle} navigateTo={navigateTo} setView={setView} onUpdateVehicle={updateVehicle} /></div>}
        {view === "diagnose" && <div style={{ padding:"0 16px 20px" }}><DiagnoseView vehicles={vehicles} inventoryCache={inventoryCache} currentUser={currentUser} onBack={navigateBack} navigateTo={navigateTo} setView={setView} openVehicle={openVehicle} onUpdateVehicle={updateVehicle} parts={parts} staff={staff} vendors={vendors} ledger={ledger} onAddPart={p => setParts(prev => [...prev, p])} onAddLedger={addLedger} /></div>}
        {view === "photoready" && <PhotoReadyView vehicles={vehicles} onUpdateVehicle={updateVehicle} onBack={navigateBack} />}
        {view === "soldvehicles" && <SoldVehiclesView vehicles={vehicles} parts={parts} ledger={ledger} staff={staff} onUpdateVehicle={updateVehicle} onAddLedger={addLedger} currentUser={currentUser} vendors={vendors} catalog={catalog} initialSelected={selectedVehicle?.stage === "SOLD" ? selectedVehicle : null} onClearInitial={() => setSelectedVehicle(null)} />}
        {view === "bodyrepairs" && <BodyRepairsView vehicles={vehicles} vendors={vendors} onUpdateVehicle={updateVehicle} openVehicle={openVehicle} />}
        {view === "inventory_sync" && <InventorySyncView
          inventoryCache={inventoryCache}
          setInventoryCache={setInventoryCacheRaw}
          vehicles={vehicles}
          onUpdateVehicle={(v) => { if (v._delete) { setVehicles(prev => prev.filter(x => x.id !== v.id)); } else updateVehicle(v); }}
          onSoldVehicle={(v) => updateVehicle({ ...v, stage: "SOLD", saleDate: todayStr(), autoSoldDetected: true })}
          currentUser={currentUser}
          navigateTo={navigateTo}
        />}
        {view === "calendar" && (() => { setView("schedule"); return null; })()}
        {view === "inspection" && selectedVehicle && (
          <Modal title={`🔍 Vehicle Inspection — ${selectedVehicle?.year} ${selectedVehicle?.make} ${selectedVehicle?.model}`} onClose={() => setView("home")} wide>
            <JuliaInspectionChat
              vehicle={selectedVehicle}
              updateVehicle={updateVehicle}
              onInspectionComplete={handleInspectionComplete}
              onClose={() => setView("home")}
            />
          </Modal>
        )}
        {view === "cleaning" && <CleaningView vehicles={vehicles} currentUser={currentUser} onOpenChecklist={setChecklistVehicle} onUpdate={updateVehicle} />}
        {view === "scrapped" && <ScrappedView vehicles={vehicles} />}
        {view === "myjobs" && <AssignedJobsView vehicles={vehicles} parts={parts} staff={staff} currentUser={currentUser} openVehicle={openVehicle} tasks={tasks} onUpdateTask={t => setTasks(prev => prev.map(x => x.id===t.id?t:x))} openVehicle={openVehicle} />}
        {view === "pipeline" && <PipelineView vehicles={vehicles} parts={parts} openVehicle={openVehicle} setView={setView} showHoldingCost={showHoldingCost} />}
        {view === "overdue" && <OverdueView vehicles={vehicles} openVehicle={openVehicle} onBack={navigateBack} />}
        {view === "diagnostics" && <DiagnosticsView vehicles={vehicles} parts={parts} staff={staff} vendors={vendors} ledger={ledger} onUpdateVehicle={updateVehicle} onAddPart={p => setParts(prev => [...prev, p])} onAddLedger={addLedger} openVehicle={openVehicle} />}
        {view === "vehicle" && selectedVehicle && (() => {
          // Always use the freshest version from vehicles array to avoid stale data
          // but don't replace if vehicle is being actively edited (same id, keep selected)
          const freshVehicle = vehicles.find(v => v.id === selectedVehicle.id) || selectedVehicle;
          return (
            <VehicleDetailView vehicle={freshVehicle} vehicles={vehicles} parts={parts} vendors={vendors} ledger={ledger} staff={staff} currentUser={currentUser} catalog={catalog}
              onUpdate={updateVehicle} onAddPart={p => setParts(prev => [...prev, p])} onUpdatePart={upd => setParts(prev => prev.map(p => p.id === upd.id ? upd : p))} onAddLedger={addLedger}
              onOpenChecklist={setChecklistVehicle}
              onOpenDelivery={setDeliveryVehicle}
              onShopForPart={(query, vehicle) => { setRoxyInitialQuery(query); setRoxyInitialVehicle(vehicle); setView("finder"); }}
              onBack={navigateBack} navigateTo={navigateTo} setView={setView} onContinueInspection={(v, mode) => { setSelectedVehicle(v); setView("diagnostics"); }}
              onStartInspection={(v) => { setSelectedVehicle(v); setView("inspection"); }}
              onTriggerCalendarRebuild={() => { setSchedule({}); }}
              showHoldingCost={showHoldingCost} />
          );
        })()}
        {view === "delivery" && <DeliverySchedulerView vehicles={vehicles} staff={staff} onUpdateVehicle={updateVehicle} />}
        {view === "finder" && <PartsFinderView vehicles={vehicles} vendors={vendors} parts={parts} currentUser={currentUser} onAddPart={p => setParts(prev => [...prev, p])} onAddLedger={addLedger} initialQuery={roxyInitialQuery} initialVehicle={roxyInitialVehicle} onBack={() => { setRoxyInitialQuery(null); setRoxyInitialVehicle(null); navigateBack(); }} />}
        {view === "schedule" && <ReconCalendarView schedule={schedule} setSchedule={setSchedule} vehicles={vehicles} parts={parts} staff={staff} onBack={navigateBack} onUpdateVehicle={updateVehicle} openVehicle={openVehicle} onRebuildSchedule={rebuildSchedule} currentUser={currentUser} timeEntries={timeEntries} setTimeEntries={setTimeEntries} ledger={ledger} onAddLedger={addLedger} tasks={tasks} onUpdateTask={t => setTasks(prev => prev.map(x => x.id===t.id?t:x))} navigateTo={navigateTo} />}
        {view === "vendors" && <VendorsView vendors={vendors} setVendors={setVendors} vehicles={vehicles} />}
        {view === "settings" && <SettingsView currentUser={currentUser} staff={staff} setStaff={setStaff} setCurrentUser={setCurrentUser} vendors={vendors} setVendors={setVendors} inventoryCache={inventoryCache} showHoldingCost={showHoldingCost} setShowHoldingCost={(v) => { setShowHoldingCost(v); try { localStorage.setItem("ew4_show_holding_cost", v); } catch {} }} setView={setView}
            vehicles={vehicles} setVehicles={setVehicles} parts={parts} setParts={setParts} ledger={ledger} addLedger={addLedger} setCsvToast={setCsvToast} guestDiagnoses={guestDiagnoses} setGuestDiagnoses={setGuestDiagnoses} tasks={tasks} setTasks={setTasks}
            onSyncInventory={async () => {
              try {
                const fresh = await scrapeInventoryWithGrok();
                if (fresh?.length > 0) {
                  const nc = { vehicles: fresh, lastSync: new Date().toISOString() };
                  setInventoryCacheRaw(nc);
                  await setInventoryCache_raw(nc);
                  // Detect sold vehicles
                  const freshStockNums = new Set(fresh.map(v => String(v.stockNumber)).filter(Boolean));
                  const freshVINs = new Set(fresh.map(v => v.vin?.toUpperCase()).filter(Boolean));
                  const nowGone = vehicles.filter(v => {
                    if (["SOLD","SCRAPPED"].includes(v.stage)) return false;
                    const stockMatch = v.stockNumber && freshStockNums.has(String(v.stockNumber));
                    const vinMatch = v.vin && freshVINs.has(v.vin?.toUpperCase());
                    return !(stockMatch || vinMatch);
                  });
                  if (nowGone.length > 0) {
                    const names = nowGone.map(v => `${v.year} ${v.make} ${v.model} #${v.stockNumber}`).join("\n");
                    if (window.confirm(`⚠️ ${nowGone.length} recon vehicle${nowGone.length > 1 ? "s are" : " is"} no longer on the lot:\n\n${names}\n\nMark as SOLD?`)) {
                      nowGone.forEach(v => {
                        if ((v.workOrders?.length > 0) || v.stage !== "INTAKE") {
                          updateVehicle({ ...v, stage: "SOLD", saleDate: todayStr(), autoSoldDetected: true });
                        } else {
                          setVehicles(prev => prev.filter(x => x.id !== v.id));
                        }
                      });
                    }
                  }
                  alert(`Synced ${fresh.length} vehicles.${nowGone.length > 0 ? ` ${nowGone.length} marked sold.` : ""}`);
                } else { alert("No vehicles found — try again."); }
              } catch (e) { alert("Sync failed: " + e.message); }
            }}
            onManualInventorySet={async (newInventory) => {
              const nc = { vehicles: newInventory, lastSync: new Date().toISOString() };
              setInventoryCacheRaw(nc);
              await setInventoryCache_raw(nc);

              // Add new vehicles from CSV that aren't already in recon
              const existingStockNums = new Set(vehicles.map(v => String(v.stockNumber)).filter(Boolean));
              const existingVINs = new Set(vehicles.map(v => v.vin?.toUpperCase()).filter(Boolean));
              const newFromCSV = newInventory.filter(c => {
                const stockMatch = c.stockNumber && existingStockNums.has(String(c.stockNumber));
                const vinMatch = c.vin && existingVINs.has(c.vin?.toUpperCase());
                return !stockMatch && !vinMatch;
              });
              if (newFromCSV.length > 0) {
                const newVehicles = newFromCSV.map(c => ({
                  id: genId(),
                  stockNumber: c.stockNumber,
                  vin: c.vin || null,
                  vinLast6: c.vin ? c.vin.slice(-6) : null,
                  year: c.year || null,
                  make: c.make || null,
                  model: c.model || null,
                  trim: c.trim || null,
                  exteriorColor: c.exteriorColor || null,
                  mileage: c.mileage || null,
                  purchasePrice: c.price ? String(c.price) : null,
                  sellingPrice: c.sellingPrice ? String(c.sellingPrice) : null,
                  priceFromCSV: true,
                  stage: "INTAKE",
                  arrivalDate: todayStr(),
                  workOrders: [],
                  fromCSV: true,
                  addedAt: new Date().toISOString(),
                }));
                setVehicles(prev => [...prev, ...newVehicles]);
              }

              // Update prices on existing recon vehicles from new CSV
              let priceUpdates = 0;
              setVehicles(prev => prev.map(v => {
                if (["SOLD","SCRAPPED"].includes(v.stage)) return v;
                const csvMatch = newInventory.find(c =>
                  String(c.stockNumber) === String(v.stockNumber) ||
                  (c.vin && v.vin && c.vin.toUpperCase() === v.vin.toUpperCase())
                );
                if (!csvMatch) return v;
                const newPP = csvMatch.price != null ? String(csvMatch.price) : v.purchasePrice;
                const newSP = csvMatch.sellingPrice != null ? String(csvMatch.sellingPrice) : v.sellingPrice;
                const changed = newPP !== String(v.purchasePrice || "") || newSP !== String(v.sellingPrice || "");
                if (changed) priceUpdates++;
                // Auto-create purchase ledger entry if none exists and we have a cost
                if (csvMatch.price != null && csvMatch.price > 0) {
                  const hasEntry = ledger.some(e => e.vehicleId === v.id && e.category === "PURCHASE");
                  if (!hasEntry) {
                    addLedger({ id: genId(), vehicleId: v.id, category: "PURCHASE", description: "Vehicle purchase (from CSV)", amount: csvMatch.price, date: v.arrivalDate || todayStr(), auto: true });
                  }
                }
                return changed ? { ...v, purchasePrice: newPP, sellingPrice: newSP, priceFromCSV: true, priceLastUpdated: new Date().toISOString() } : v;
              }));

              // Cross-reference new CSV against active recon vehicles — detect sold
              const newStockNums = new Set(newInventory.map(v => String(v.stockNumber)).filter(Boolean));
              const newVINs = new Set(newInventory.map(v => v.vin?.toUpperCase()).filter(Boolean));
              const nowGone = vehicles.filter(v => {
                if (["SOLD","SCRAPPED"].includes(v.stage)) return false;
                const stockMatch = v.stockNumber && newStockNums.has(String(v.stockNumber));
                const vinMatch = v.vin && newVINs.has(v.vin?.toUpperCase());
                return !(stockMatch || vinMatch);
              });

              let soldMsg = "";
              if (nowGone.length > 0) {
                const names = nowGone.map(v => `${v.year} ${v.make} ${v.model} #${v.stockNumber}`).join("\n");
                const confirmed = window.confirm(
                  `⚠️ ${nowGone.length} vehicle${nowGone.length > 1 ? "s" : ""} from recon are NOT in this CSV:\n\n${names}\n\nMark as SOLD?`
                );
                if (confirmed) {
                  nowGone.forEach(v => {
                    const hasWork = (v.workOrders?.length > 0) || v.stage !== "INTAKE";
                    if (hasWork) {
                      updateVehicle({ ...v, stage: "SOLD", saleDate: todayStr(), autoSoldDetected: true, autoSoldFrom: "csv_upload" });
                    } else {
                      setVehicles(prev => prev.filter(x => x.id !== v.id));
                    }
                  });
                  soldMsg = ` ${nowGone.length} marked sold.`;
                }
              }
              setCsvToast(`✓ CSV loaded — ${newInventory.length} vehicles. ${newFromCSV.length > 0 ? newFromCSV.length + ' new vehicles added to inventory. ' : ''}${priceUpdates > 0 ? 'Updated prices on ' + priceUpdates + ' vehicle(s). ' : ''}${soldMsg}`);
              setTimeout(() => setCsvToast(""), 15000);
            }}
            onLogout={() => { setCurrentUser(null); S.save("ew4_user", null); setView("home"); setShowLogin(true); }} />}
        {view === "mgmt" && currentUser?.role === "manager" && (
          <ManagementDashboardView vehicles={vehicles} parts={parts} ledger={ledger} staff={staff}
            timeEntries={timeEntries} bayAssignments={bayAssignments} setBayAssignments={setBayAssignments}
            openVehicle={openVehicle} onUpdateVehicle={updateVehicle} />
        )}
      </div>

      {/* Pending Sold Approval (5 AM daily sync) */}
      {pendingSoldApproval && pendingSoldApproval.status === "PENDING" && currentUser && (
        <PendingSoldApprovalModal
          pending={pendingSoldApproval}
          vehicles={vehicles}
          currentUser={currentUser}
          onClose={() => { /* user can't dismiss without acting */ }}
          onApprove={async ({ approvedIds, rejectedIds }) => {
            const today = todayStr();
            // Mark approved vehicles as SOLD
            for (const id of approvedIds) {
              const v = vehicles.find(x => x.id === id);
              if (v && v.stage !== "SOLD") {
                updateVehicle({ ...v, stage: "SOLD", saleDate: today, autoSoldDetected: true, autoSoldFrom: "daily_sync_5am", autoSoldApprovedBy: currentUser?.username || currentUser?.id });
              }
            }
            // Mark approval done so other users don't see the modal again today
            const resolved = {
              ...pendingSoldApproval,
              status: "APPROVED",
              approvedBy: currentUser?.username || currentUser?.id,
              approvedByName: currentUser?.name || currentUser?.username || "Unknown",
              approvedAt: new Date().toISOString(),
              approvedIds,
              rejectedIds,
            };
            setPendingSoldApproval(resolved);
            await S.save("ew4_pending_sold", resolved);
          }}
        />
      )}

      {/* VIN Scan Modal */}
      {vinScanOpen && (
        <Modal title="➕ Add Vehicle" onClose={() => setVinScanOpen(false)} wide>
          <VINIntakeFlow onComplete={handleVINIntakeComplete} onClose={() => setVinScanOpen(false)} vendors={vendors} vehicles={vehicles} inventoryCache={inventoryCache}
            onOpenExisting={(v) => { setVinScanOpen(false); openVehicle(v); }}
            onManualSync={async () => {
              try {
                const fresh = await scrapeInventoryWithGrok();
                if (fresh?.length > 0) {
                  const nc = { vehicles: fresh, lastSync: new Date().toISOString() };
                  setInventoryCacheRaw(nc);
                  await setInventoryCache_raw(nc);
                  alert("Synced " + fresh.length + " vehicles from your site!");
                } else { alert("No vehicles found. Check your Grok key has web search enabled."); }
              } catch (e) { alert("Sync failed: " + e.message); }
            }} />
        </Modal>
      )}

      {/* Delivery Quick-Schedule Modal (triggered from vehicle detail) */}
      {deliveryVehicle && (
        <DeliveryQuickModal
          vehicle={deliveryVehicle}
          onSave={(upd) => { updateVehicle(upd); setDeliveryVehicle(null); }}
          onClose={() => setDeliveryVehicle(null)}
        />
      )}

      {/* Cleaning Checklist Modal */}
      {checklistVehicle && (
        <Modal title={`🧼 Cleaning Checklist — ${checklistVehicle.year} ${checklistVehicle.make} ${checklistVehicle.model} #${checklistVehicle.stockNumber}`} onClose={() => setChecklistVehicle(null)} wide>
          <CleaningChecklistModal
            vehicle={checklistVehicle}
            ledger={ledger}
            onClose={() => setChecklistVehicle(null)}
            onUpdateVehicle={(upd) => { updateVehicle(upd); setChecklistVehicle(upd); }}
            onComplete={() => {
              updateVehicle({ ...checklistVehicle, stage: "PHOTO_POST", cleaningCompletedAt: todayStr() });
              setChecklistVehicle(null);
            }}
          />
        </Modal>
      )}
    </div>
  );
}
